// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { nbModuleTips } from './nb-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
export const nb = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Logg inn på kontoen din',
        email: 'E-post',
        password: 'Passord',
        sign_in: 'Logg inn',
        signing_in: 'Logger inn...',
        mfa_code: 'MFA-kode',
        mfa_placeholder: 'Skriv inn 6-sifret kode',
        mfa_instruction: 'Skriv inn den 6-sifrede koden fra autentiseringsappen din.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Bekreft',
        verifying: 'Bekrefter...',
        forgot_password: 'Glemt passord?',
        account_locked: 'Kontoen er låst. Vennligst prøv igjen senere.',
        account_deactivated: 'Kontoen er deaktivert. Kontakt administratoren din.',
        invalid_mfa: 'Ugyldig MFA-kode. Vennligst prøv igjen.',
        invalid_credentials: 'Ugyldig e-post eller passord.',
        login_failed: 'Innlogging mislyktes',
        session_limit_reached: 'Kontoen din har nådd grensen for aktive økter. Logg ut på en annen enhet, eller be administratoren om å tilbakekalle en eksisterende økt, og prøv igjen.',
        session_expired: 'Økten din har utløpt. Vennligst last siden på nytt og prøv igjen.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Glemt passord',
            subtitle: 'Skriv inn e-postadressen din, så sender vi deg en lenke for å tilbakestille passordet.',
            send: 'Send tilbakestillingslenke',
            sending: 'Sender...',
            success: 'Hvis den e-postadressen finnes, er en lenke for tilbakestilling av passord sendt.',
            back_to_login: 'Tilbake til innlogging',
        },
        reset_password: {
            title: 'Tilbakestill passord',
            subtitle: 'Skriv inn ditt nye passord nedenfor.',
            new_password: 'Nytt passord',
            confirm_password: 'Bekreft passord',
            reset: 'Tilbakestill passord',
            resetting: 'Tilbakestiller...',
            success: 'Passordet ditt er tilbakestilt.',
            go_to_login: 'Gå til innlogging',
            password_mismatch: 'Passordene samsvarer ikke.',
            invalid_token: 'Ugyldig eller manglende tilbakestillingstoken. Vennligst be om en ny lenke for tilbakestilling av passord.',
            requirements_title: 'Passordkrav:',
            password_requirements: 'Passordet må oppfylle følgende krav:',
            rules: {
                min_length: 'Minst 12 tegn',
                uppercase: 'Minst én stor bokstav',
                lowercase: 'Minst én liten bokstav',
                digit: 'Minst ett siffer',
                special: 'Minst ett spesialtegn',
            },
        },
        verify_email: {
            title: 'E-postbekreftelse',
            verifying: 'Bekrefter e-postadressen din...',
            success: 'E-postadressen din er bekreftet.',
            invalid_token: 'Ugyldig eller manglende bekreftelsestoken.',
            failed: 'E-postbekreftelse mislyktes. Tokenet kan være ugyldig eller utløpt.',
            go_to_login: 'Gå til innlogging',
        },
        offline: {
            title: 'Du er frakoblet',
            message: 'Det ser ut til at du har mistet internettforbindelsen. Vennligst sjekk nettverket ditt og prøv igjen.',
        },
    },
    intake: {
        title: 'Innsynsbegjæring',
        subtitle: 'Send inn en innsynsbegjæring til vår organisasjon',
        fields: {
            full_name: 'Fullt navn',
            email: 'E-postadresse',
            phone: 'Telefonnummer (valgfritt)',
            request_type: 'Type forespørsel',
            description: 'Beskriv din forespørsel',
            preferred_language: 'Foretrukket språk',
            consent: 'Jeg samtykker til behandling av denne forespørselen',
        },
        submit: 'Send inn forespørsel',
        validation: {
            name_required: 'Fullt navn er påkrevd',
            email_required: 'En gyldig e-postadresse er påkrevd',
            type_required: 'Vennligst velg en type forespørsel',
            description_required: 'Vennligst beskriv din forespørsel',
            consent_required: 'Du må samtykke til behandling for å fortsette',
        },
        error_title: 'Feil',
        config_load_error: 'Kunne ikke laste konfigurasjonen. Vennligst sjekk nettadressen.',
        organization_label: 'Organisasjon',
        customer_reference: 'Kundereferansenummer',
        country: 'Land / Jurisdiksjon',
        preferred_channel: 'Foretrukket svarskanal',
        channels: {
            email: 'E-post',
            postal: 'Post',
            phone: 'Telefon',
            portal: 'Portal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Forespørsel mottatt',
            message: 'Din forespørsel er registrert. Ditt referansenummer er:',
            next_steps: 'Vi vil behandle din forespørsel så snart som mulig i samsvar med gjeldende lovgivning. Forventet behandlingstid er 30 virkedager.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Innsyn (DSAR)',
        deletion: 'Sletting',
        correction: 'Retting / Oppdatering',
        objection: 'Innsigelse',
        portability: 'Dataportabilitet',
        restriction: 'Begrensning av behandling',
        consent_withdrawal: 'Tilbaketrekking av samtykke',
        protective_measures: 'Be om tiltak for beskyttelse av personopplysninger',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Ny',
        in_review: 'Under vurdering',
        waiting_on_requester: 'Venter på innmelder',
        waiting_on_internal_data: 'Venter på interne data',
        approved: 'Godkjent',
        closed: 'Lukket',
        rejected: 'Avvist',
    },
    admin: {
        nav: {
            rights_requests: 'Innsynsbegjæringer',
            templates: 'Maler',
            response_templates: 'Svarmaler',
            data_mapping: 'Datakartlegging',
            governance: 'Styring',
            consent: 'Samtykke',
            assessments: 'Vurderinger',
            classification: 'Klassifisering',
            monitoring: 'Overvåking',
            incidents: 'Hendelser',
            onboarding: 'Introduksjon',
            connectors: 'Koblinger',
            vendor_risk: 'Leverandørrisiko',
            deletion_orchestration: 'Sletteorkestrering',
            discovery: 'Dataoppdagelse',
            incident_ops: 'Hendelseshåndtering',
            program_governance: 'Programstyring',
            access_accountability: 'Tilgangsansvar',
            consent_properties: 'Samtykkeegenskaper',
            localization: 'Lokalisering',
            regulatory: 'Regulatorisk',
            partner_workspace: 'Partnerarbeidsområde',
            audit_trail: 'Revisjonsspor',
            forseti: 'Forseti',
            settings: 'Innstillinger',
            help_center: 'Hjelpesenter',
            dashboard: 'Kontrollpanel',
            platform: 'Plattform',
            integrations: 'Integrasjoner',
            cmp_dsar_sync: 'CMP DSAR-synkronisering',
            cmp_integration: 'CMP-integrasjon',
            purpose_mapping: 'Formålskobling',
            webhook_config: 'Webhook-konfigurasjon',
            bulk_sync: 'Massesynkronisering',
            privacy_scanner: 'Personvernskanner',
            privacy_monitoring: 'Personvernovervåking',
            real_time_scanner: 'Sanntidsskanner',
            compliance_forms: 'Samsvarsskjemaer',
            forms_dashboard: 'Skjemakontrollpanel',
            form_templates: 'Maler',
            form_catalog: 'Skjemakatalog',
            pack_catalog: 'Pakkekatalog',
            org_groups: 'Organisasjonsgrupper',
            consolidation: 'Konsolidering',
            organizations: 'Organisasjoner',
            org_list: 'Alle organisasjoner',
            intake_configs: 'Mottakskonfigurasjoner',
            task_management: 'Oppgavestyring',
            unified_audit: 'Samlet Revisjon',
            evidence_packs: 'Bevispakker',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Revisjonsperioder',
            regulatory_updates: 'Regelverksoppdateringer',
            departments: 'Avdelinger',
        },
        sidebar_category_data_privacy_rights: 'Personvern & Rettigheter',
        sidebar_category_vendor_third_party: 'Leverandør & Tredjepartsstyring',
        sidebar_category_risk_assessment: 'Risiko & Vurdering',
        sidebar_category_operations_automation: 'Drift & Automatisering',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Oppgave & Arbeidsflyt',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP og webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'RoPA Register',
            data_mapping_classification: 'Klassifisering',
            data_mapping_governance: 'Styring',
            access_live_monitoring: 'Live overvåking',
            access_reviews_investigations: 'Anmeldelser og undersøkelser',
            access_evidence_policies: 'Bevis og retningslinjer',
            classification_taxonomies: 'Taksonomier og etiketter',
            classification_workflow: 'Arbeidsflyt',
            classification_insights: 'Innsikt',
            connectors_runtime: 'Kjøretid',
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Dekning',
            cp_definitions: 'Definisjoner',
            cp_lifecycle: 'Livssyklus',
            cp_quality: 'Kvalitet og bevis',
            deletion_execution: 'Utførelse',
            deletion_vendors: 'Leverandører',
            deletion_evidence: 'Bevis og integrasjon',
            discovery_rules: 'Regler og maler',
            discovery_scanning: 'Skanning',
            discovery_review: 'Gjennomgå arbeidsflyt',
            discovery_insights: 'Innsikt',
            governance_oversight: 'Tilsyn',
            governance_reporting: 'Rapportering',
            governance_vendors: 'Leverandører og grenseoverskridende',
            governance_lifecycle: 'Livssyklus',
            incident_ops_dashboards: 'Dashboards',
            incident_ops_sla: 'SLA Konfig',
            incident_ops_playbooks: 'Playbooks',
            incident_ops_vendor_regulator: 'Leverandør og regulator',
            incident_ops_evidence: 'Bevis og gjennomgang',
            incidents_workload: 'Arbeidsmengde',
            incidents_regulatory: 'Regulatorisk',
            incidents_reporting: 'Rapportering',
            localization_content: 'Innhold',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observerbarhet',
            monitoring_metrics: 'Beregninger',
            monitoring_programs: 'Programmer',
            monitoring_tasks: 'Oppgaver',
            monitoring_controls: 'Kontroller',
            org_entities: 'Entiteter',
            org_config: 'Konfigurasjon',
            org_frameworks: 'Frameworks & Rollup',
            partner_overview: 'Oversikt',
            partner_partners: 'Partnere',
            partner_delivery: 'Levering',
            partner_support: 'Støtte',
            platform_identity: 'Identitet og roller',
            platform_access_control: 'Tilgangskontroll',
            platform_auth_session: 'Autentisering og økt',
            platform_access_reviews: 'Få tilgang til anmeldelser',
            platform_files: 'Filer',
            platform_notifications: 'Varsler',
            platform_module_config: 'Modulkonfig',
            platform_ops: 'Ops og logger',
            pg_health: 'Program Helse',
            pg_maturity: 'Modenhet',
            pg_audit: 'Revisjon og bevis',
            pg_training: 'Opplæring',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Forpliktelser',
            pg_documents: 'Dokumenter',
            vr_overview: 'Oversikt',
            vr_onboarding: 'Onboarding',
            vr_assessment: 'Vurdering',
            vr_oversight: 'Tilsyn',
        },
        tickets: {
            title: 'Innsynsbegjæringer',
            create: 'Opprett forespørsel',
            filters: 'Filtre',
            search_placeholder: 'Search tickets...',
            export_csv: 'Eksporter CSV',
            exporting: 'Eksporterer...',
            bulk_export_failed: 'Masseksport mislyktes',
            bulk_export: {
                label: 'Eksporter',
                format: {
                    pdf: 'PDF Sammendrag',
                    zip: 'ZIP (bevispakker per sak)',
                    csv: 'CSV',
                },
                warning_over_50: 'Dette vil generere omtrent {count} case-pakker – kan ta flere minutter. Fortsette?',
                blocked_over_500: 'Begrens filteret til 500 eller færre tilfeller før du eksporterer ZIP (gjeldende: {count}).',
                success: 'Masseksport lagret i filnettleseren ({count} tilfeller)',
                failed: 'Masseksport mislyktes',
                continue: 'Fortsett',
                cancel: 'Avbryt',
            },
            no_results: 'Ingen forespørsler funnet',
            ticket_id: 'Saks-ID',
            request_type: 'Type forespørsel',
            requester: 'Innmelder',
            source: 'Kilde',
            all_sources: 'Alle kilder',
            assigned_to: 'Tildelt til',
            created: 'Opprettet',
            load_error: 'Kunne ikke laste saker. Vennligst prøv igjen.',
            case_creation_failed: 'Oppretting av sak mislyktes',
            business_unit: 'Forretningsenhet',
            reference: 'Referanse',
            sla: {
                title: 'SLA-status',
                on_track: '{days} dager igjen',
                approaching: 'Nærmer seg fristen',
                overdue: '{days} dager over fristen',
                ack_overdue: '{hours}t over bekreftelsefristen',
                ack_warning: 'Bekreftelse snart forfalt',
                closed: 'Lukket',
                completed_in: 'Fullført på {days} dager',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Svarmaler',
            new: 'Ny mal',
            edit: 'Rediger mal',
            all_types: 'Alle typer',
            all_locales: 'Alle språk',
            type_access: 'Innsyn/DSAR',
            type_deletion: 'Sletting',
            type_correction: 'Retting',
            type_objection: 'Innsigelse',
            type_portability: 'Portabilitet',
            type_other: 'Annet',
            type_restriction: 'Begrensning',
            type_all: 'Alle',
            locale_en: 'Engelsk',
            locale_vi: 'Vietnamesisk',
            locale_all: 'Begge',
            name: 'Navn',
            request_type: 'Type forespørsel',
            locale: 'Språk',
            subject: 'Emne',
            body: 'Innhold',
            name_placeholder: 'Malnavn...',
            subject_placeholder: 'E-post-/brevemne...',
            body_placeholder: 'Malinnhold. Bruk {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} som plassholdere.',
            available_placeholders: 'Tilgjengelige plassholdere:',
            create_template: 'Opprett mal',
            preview: 'Forhåndsvisning',
            close_preview: 'Lukk forhåndsvisning',
            default_badge: 'Standard',
            no_templates: 'Ingen maler funnet',
            confirm_delete: 'Slette denne malen?',
            load_error: 'Kunne ikke laste maler. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette mal',
            update_error: 'Kunne ikke oppdatere mal',
            delete_error: 'Kunne ikke slette mal',
            preview_error: 'Kunne ikke forhåndsvise mal',
            response_templates_badge: 'Svarmaler',
            browse_form_templates: 'Bla gjennom Samsvarsskjemamaler',
            templates_loaded: 'Maler lastet',
            sample_organization: 'Eksempelorganisasjon',
        },
        detail: {
            requester_info: 'Innmelderinformasjon',
            status: 'Status',
            assignment: 'Tildeling',
            notes: 'Notater',
            audit_trail: 'Revisjonsspor',
            add_note: 'Legg til notat',
            case_not_found: 'Sak ikke funnet',
            load_error: 'Kunne ikke laste sak. Vennligst prøv igjen.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Kunne ikke legge til notat. Vennligst prøv igjen.',
            submitted_from: 'Sendt fra',
            via_cmp: 'via AesirX CMP',
            cmp_ticket: 'CMP-sak:',
            select_template: 'Velg en mal',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Detaljer',
                evidence: 'Dokumentasjon',
                communications: 'Kommunikasjon',
                escalations: 'Eskaleringer',
                audit_trail: 'Revisjonsspor',
                fulfillment: 'Oppfyllelse',
                response: 'Svar',
                extension: 'Forlengelse',
            },
            requester: {
                name: 'Navn',
                email: 'E-post',
                phone: 'Telefon',
                language: 'Språk',
                source: 'Kilde',
                customer_ref: 'Kundereferanse',
                country: 'Land',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Foretrukket kanal',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'I rute',
                due_soon: 'Forfaller snart',
                overdue: 'Forfalt',
                days_remaining: 'd gjenstående',
                due_date: 'Forfallsdato',
                breached: 'Brutt',
                paused: 'Pauset',
                total_paused_days: 'Totalt pausede dager',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Dager brukt',
                progress: 'Fremdrift',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identitetsverifisering',
                verified: 'Verifisert',
                not_verified: 'Ikke verifisert',
                method: 'Metode',
                notes: 'Notater',
                verified_by: 'Verifisert av',
                verified_at: 'Verifisert',
                warning: 'Identitet er ikke verifisert ennå.',
                verify_button: 'Verifiser identitet',
                select_method: '-- Velg --',
                methods: {
                    email_confirmation: 'E-postbekreftelse',
                    id_document: 'ID-dokument',
                    phone_call: 'Telefonsamtale',
                    in_person: 'Personlig oppmøte',
                    other: 'Annet',
                },
            },
            closure: {
                reason: 'Avslutningsårsak',
                summary: 'Avslutningssammendrag',
                summary_placeholder: 'Sammendrag av hvordan forespørselen ble løst...',
                reason_required: 'Avslutningssammendrag og årsak er påkrevd',
                response_not_sent: 'Et formelt svar må sendes til den registrerte før denne saken kan lukkes',
                reasons: {
                    fulfilled: 'Oppfylt',
                    partially_fulfilled: 'Delvis oppfylt',
                    unfounded: 'Ubegrunnet',
                    excessive: 'Overdreven',
                    withdrawn: 'Trukket tilbake',
                    duplicate: 'Duplikat',
                    identity_unverified: 'Identitet ikke verifisert',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Eksporter',
            export_pdf: 'Eksporter PDF',
            export_zip: 'Eksporter ZIP',
            case_file_pdf: 'Eksporter PDF (saksfil)',
            evidence_pack_zip: 'Eksporter ZIP (bevispakke)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Flagg som bruddskapende',
                modal: {
                    title: 'Flagg tilfelle som brudd-forårsaker',
                    type_label: 'Bruddtype',
                    type: {
                        misdirected_response: 'Feilrettet svar',
                        unauthorised_disclosure: 'Uautorisert avsløring',
                        identity_mismatch: 'Identitetsfeil',
                        other: 'Annet',
                    },
                    description: 'Beskrivelse (valgfritt)',
                    description_placeholder: 'Hva skjedde? (maks 1000 tegn)',
                    confirm: 'Flagg og start varslingsklokken',
                    cancel: 'Avbryt',
                    flagging: 'Flagger...',
                },
                banner: {
                    title: 'Brudd flagget: {type}',
                    detected_at: 'Oppdaget ved {timestamp}',
                    filing_deadline: 'Varslingsfrist: om {hours} timer',
                    overdue: 'Varslingsfrist: OVERSKREDET',
                },
                toasts: {
                    flagged: 'Sak flagget som brudd-forårsaker – varslingsklokke startet',
                    flag_failed: 'Kunne ikke flagge saken som bruddforårsak',
                },
            },
            export: {
                generating: 'Genererer... vennligst vent',
                success: 'Eksport lagret til Filleser',
                failed: 'Eksporten mislyktes - sjekk revisjonsloggen',
            },
            no_notes: 'Ingen notater ennå',
            assign_placeholder: 'Bruker-ID å tildele...',
            reassign: 'Tilordne på nytt',
            evidence: {
                checklist_title: 'Dokumentasjonssjekkliste',
                complete_pct: '% fullført',
                no_checklist: 'Ingen sjekkliste-elementer',
                attachments: 'Vedlegg',
                upload_attachment: 'Last opp vedlegg',
                upload_button: 'Last opp',
                uploading: 'Laster opp...',
                attachment_uploaded: 'Vedlegg lastet opp',
                upload_error: 'Opplasting feilet',
                category: 'Kategori',
                categories: {
                    identity_proof: 'Identitetsbevis',
                    correspondence: 'Korrespondanse',
                    internal_data: 'Interne data',
                    legal: 'Juridisk',
                    other: 'Annet',
                },
                no_attachments: 'Ingen vedlegg',
                references_title: 'Dokumentasjonsreferanser',
                no_references: 'Ingen referanser',
                add_reference: 'Legg til referanse',
                ref_type: 'Type',
                ref_value: 'Verdi',
                ref_desc: 'Beskrivelse',
                ref_value_placeholder: 'URL, e-post-ID eller referanse...',
                ref_desc_placeholder: 'Beskrivelse...',
                ref_types: {
                    url: 'URL',
                    email_id: 'E-post-ID',
                    crm_record: 'CRM-oppføring',
                    ticket_ref: 'Saksreferanse',
                    other: 'Annet',
                },
            },
            communications: {
                title: 'Kommunikasjon',
                add: 'Legg til melding',
                no_records: 'Ingen kommunikasjon registrert',
                direction: 'Retning',
                channel: 'Kanal',
                visibility: 'Synlighet',
                subject: 'Emne',
                body: 'Innhold',
                send: 'Send',
                inbound: 'Innkommende',
                outbound: 'Utgående',
                internal: 'Intern',
                external: 'Ekstern',
                tab_all: 'All',
                tab_internal: 'Intern',
                tab_external: 'Ekstern',
                subject_placeholder: 'Emne (valgfritt)',
                body_placeholder: 'Meldingsinnhold...',
                channels: {
                    email: 'E-post',
                    portal: 'Portal',
                    phone: 'Telefon',
                    postal: 'Post',
                    in_person: 'Personlig oppmøte',
                },
                apply_template: 'Bruk Mal',
                select_template: 'Velg en mal...',
                no_templates: 'Ingen maler tilgjengelige',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Mal brukt på svaret.',
                template_apply_error: 'Kunne ikke bruke mal.',
            },
            escalations: {
                title: 'Eskaleringshistorikk',
                no_events: 'Ingen eskaleringshendelser',
                tier: 'Nivå',
                threshold: 'Terskel',
                action: 'Handling',
                target: 'Mål',
                triggered_at: 'Utløst',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP-synkronisering',
                sync_status: 'Synkroniseringsstatus',
                last_synced: 'Sist synkronisert',
                not_synced: 'Ikke synkronisert',
                resync: 'Synkroniser nå',
                syncing: 'Synkroniserer...',
                sync_history: 'Synkroniseringshistorikk (siste 10)',
                direction: 'Retning',
                time: 'Tid',
                retries: 'Forsøk',
                error_col: 'Feil',
            },
            audit: {
                filter_event_placeholder: 'Filtrer etter hendelsestype...',
                filter_actor_placeholder: 'Filtrer etter aktør...',
            },
            fulfillment: {
                title: 'Avdelingsoppfyllelse',
                assign_departments: 'Tildel avdelinger',
                department: 'Avdeling',
                assignee: 'Tilordnet',
                deadline: 'Frist',
                evidence_count: 'Dokumentasjon',
                signoff: 'Godkjenning',
                signed_off: 'Godkjent',
                pending_signoff: 'Ventende',
                start_button: 'Start',
                complete_button: 'Merk som fullført',
                signoff_button: 'Meld deg av',
                no_tasks: 'Ingen oppfyllelsesoppgaver tildelt ennå.',
                tasks_table: 'Oppfyllelsesoppgaver',
                progress_label: 'Oppfyllelsesfremgang',
                overdue: 'forsinket',
                assigned_success: 'Avdelingsoppfyllelsesoppgave tildelt.',
                assign_error: 'Kunne ikke tildele oppfyllelsesoppgave.',
                signoff_success: 'Avdelingsgodkjenning registrert.',
                signoff_error: 'Kunne ikke registrere godkjenning.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Legg til bevis',
                evidence_uploaded: 'Bevis lastet opp og koblet',
                evidence_upload_error: 'Opplasting av bevis feilet',
                suggest_data_stores: 'Foreslå datalagre',
                suggest_error: 'Kunne ikke laste datalagringsforslag.',
                suggest_stores_title: 'Foreslåtte datalagre',
                suggest_stores_none: 'Ingen datalagre samsvarer med denne saken. Konfigurer systemer i datakartlegging for å forbedre forslag.',
                suggest_stores_add: 'Legg til sak',
                suggest_stores_added: 'Datalager lagt til sak',
                no_departments_title: 'Ingen avdelinger er konfigurert',
                no_departments_body: 'Avdelinger lar deg rute DSR-oppfyllingsoppgaver til en funksjon (Personvern, Juridisk, HR, IT Ops). Installer eksempelavdelinger eller be administratoren din om å konfigurere avdelinger i organisasjonsinnstillingene.',
                no_departments_cta: 'Installer eksempeldata',
                department_placeholder: 'Avdelingsnavn...',
                assignee_placeholder: 'Bruker-ID å tildele...',
                assign_button: 'Tildel',
                status_pending: 'Ventende',
                status_in_progress: 'Pågår',
                status_completed: 'Fullført',
                status_verified: 'Verifisert',
                confirm_approve: 'Er du sikker på at du vil godkjenne denne forlengelsen?',
                confirm_deny: 'Er du sikker på at du vil avslå denne forlengelsen?',
            },
            response: {
                title: 'Svargenerering',
                select_template: 'Svarsmal',
                choose_template: 'Velg en mal...',
                generate: 'Generer svar',
                preview: 'Svarsforhåndsvisning',
                send: 'Send svar',
                delivery_method: {
                    label: 'Leveringsmetode',
                    email: 'E-post',
                    portal: 'Portal',
                    postal: 'Postpost',
                    in_person: 'Personlig',
                },
                recipient: {
                    email: 'Mottakers e-post',
                    portal: 'Portal bruker-ID',
                    address: 'Postadresse',
                },
                auto_generate_btn: 'Generer og send svar',
                auto_generate_success: 'Respons generert og levert',
                auto_generate_missing_recipient: 'Vennligst oppgi en mottaker før du sender.',
                auto_fields: 'Automatisk utfylte felt',
                case_id: 'Saks-ID',
                requester_name: 'Anmoders navn',
                rights_type: 'Rettighetstype',
                history: 'Sendte svar',
                generated_at: 'Generert',
                confirmation: 'Bekreftelse',
                sent_label: 'Sendt',
                draft_label: 'Utkast',
                generated_success: 'Svar generert.',
                generate_error: 'Kunne ikke generere svar.',
                sent_success: 'Svar sendt.',
                send_error: 'Kunne ikke sende svar.',
                load_error: 'Kunne ikke laste svarsmaler.',
                pdf_title: 'Svarbrev til registrert person',
                download_pdf: 'Last ned PDF',
                download_all: 'Last ned alle',
                download_error: 'Kunne ikke laste ned svar',
            },
            extension: {
                title: 'Fristforlengelser',
                acknowledgment_deadline: 'Bekreftelsesfrist',
                fulfillment_deadline: 'Oppfyllelsesfrist',
                request_extension: 'Be om forlengelse',
                reason: 'Begrunnelse',
                reason_placeholder: 'Begrunnelse for å be om ekstra tid...',
                additional_days: 'Ytterligere dager',
                submit_request: 'Send forlengelsesforespørsel',
                history: 'Forlengelseshistorikk',
                requested_at: 'Forespurt',
                approve: 'Godkjenn',
                deny: 'Avslå',
                request_success: 'Forlengelsesforespørsel sendt.',
                request_error: 'Kunne ikke sende forlengelsesforespørsel.',
                approved_success: 'Forlengelse godkjent.',
                denied_success: 'Forlengelse avslått.',
                decision_error: 'Kunne ikke behandle forlengelsesvedtak.',
                status_pending: 'Ventende',
                status_approved: 'Godkjent',
                status_denied: 'Avslått',
                confirm_approve: 'Er du sikker på at du vil godkjenne denne forlengelsesforespørselen?',
                confirm_deny: 'Er du sikker på at du vil avslå denne forlengelsesforespørselen?',
                decided_by: 'Besluttet av',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Eksporter CSV',
            exporting: 'Eksporterer...',
            search_placeholder: 'Søk i revisjonsdetaljer...',
            filter_by_actor: 'Filtrer etter aktør...',
            all_event_types: 'Alle hendelsestyper',
            seq: '#',
            timestamp: 'Tidsstempel',
            event: 'Hendelse',
            case_col: 'Sak',
            actor: 'Aktør',
            summary: 'Sammendrag',
            no_events: 'Ingen revisjonshendelser funnet',
            export_success: 'Revisjonssporet ble eksportert.',
            export_error: 'Kunne ikke eksportere revisjons-CSV',
            load_error: 'Kunne ikke laste revisjonsspor. Vennligst prøv igjen.',
            announce_loaded: 'Revisjonsspor lastet',
            start_date: 'Startdato',
            end_date: 'Sluttdato',
            per_page: '{count} / side',
            unknown: 'Ukjent',
            source_filter: {
                label: 'Kilde',
                all: 'Alle',
                module: 'Modul',
                forseti: 'Forseti',
                aria_label: 'Filtrer revisjonshendelser etter kilde',
            },
            event_types: {
                case_created: 'Sak opprettet',
                status_changed: 'Status endret',
                case_assigned: 'Sak tildelt',
                note_added: 'Notat lagt til',
                attachment_added: 'Vedlegg lagt til',
                attachment_deleted: 'Vedlegg slettet',
                reference_added: 'Referanse lagt til',
                checklist_updated: 'Sjekkliste oppdatert',
                identity_verified: 'Identitet verifisert',
                closure_summary_added: 'Avslutningssammendrag',
                approval_granted: 'Godkjenning gitt',
                template_applied: 'Mal brukt',
                export_generated: 'Eksport generert',
                sla_warning: 'SLA-advarsel',
                sla_overdue: 'SLA forfalt',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Samlet Revisjonsspor',
                module_filter: 'Modul',
                framework_filter: 'Rammeverk',
                select_framework: 'Velg et rammeverk...',
                entity_type_filter: 'Entitetstype',
                action_filter: 'Handling',
                actor_filter: 'Aktør',
                select_actor: 'Velg en aktør...',
                search_filter: 'Søk',
                search_placeholder: 'Søk i revisjonshendelser...',
                filters_heading: 'Filtre',
                date_range: 'Datoperiode',
                no_events_found: 'Ingen revisjonshendelser funnet.',
                event_count: 'Antall hendelser',
                action: 'Handling',
                actor: 'Aktør',
                date_range_start: 'Startdato for intervall',
                date_range_end: 'Sluttdato for intervall',
                filtering_events: 'Filtrerer revisjonshendelser',
                framework_codes: 'Rammekoder',
                entity_type_col: 'Enhetstype',
                aria_table: 'Samlede revisjonshendelser',
                page_of: 'Side {page} av {total}',
                entity_types: {
                    user: 'Bruker',
                    case: 'Sak',
                    incident: 'Hendelse',
                    assessment: 'Vurdering',
                    vendor: 'Leverandør',
                    consent: 'Samtykke',
                    policy: 'Politikk',
                    data_map: 'Datakart',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Opprett',
                    update: 'Oppdater',
                    delete: 'Slett',
                    view: 'Visning',
                    export: 'Eksporter',
                    status_change: 'Statusendring',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Revisjonsbevis­pakker',
                assemble_pack: 'Sett sammen Bevispakke',
                framework: 'Rammeverk',
                select_framework: 'Velg et rammeverk...',
                framework_code_label: 'Rammekode',
                entity_types: 'Entitetstyper',
                select_entity_types: 'Velg entitetstyper...',
                period: 'Periode',
                period_start: 'Periodestart',
                period_end: 'Periodeslutt',
                completeness: 'Fullstendighet',
                no_packs_found: 'Ingen bevispakker funnet.',
                verify: 'Verifiser',
                export_pack: 'Eksporter',
                exporting: 'Eksporterer...',
                pack_assembled: 'Bevisningspakke sammenstilt',
                pack_exported: 'Bevisningspakke eksportert',
                aria_table: 'Revisjonsbevisingspakker',
                aria_export_pack: 'Eksporter pakke',
                export_format: 'Eksportformat',
                export_in_progress: 'Eksport pågår...',
                status_draft: 'Utkast',
                status_assembled: 'Sammensatt',
                status_verified: 'Verifisert',
                status_submitted: 'Innsendt',
                entity_type_labels: {
                    user: 'Bruker',
                    case: 'Sak',
                    incident: 'Hendelse',
                    assessment: 'Vurdering',
                    vendor: 'Leverandør',
                    consent: 'Samtykke',
                    policy: 'Politikk',
                    data_map: 'Datakart',
                },
                template_id: 'Mal-ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Modul',
                    action: 'Handling',
                    actor_id: 'Skuespiller',
                    actor_role: 'Actor Role',
                    entity_type: 'Enhetstype',
                    entity_id: 'Enhets-ID',
                    summary: 'Sammendrag',
                    timestamp: 'Tidsstempel',
                    record_type: 'Record Type',
                    framework_code: 'Rammeverk',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Evidenspakke — Rammeverk: {framework}',
                export_header_period: 'Periode: {start} til {end}',
                export_header_completeness: 'Fullstendighet: {score}%',
                export_header_status: 'Status: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Revisjonsperioder',
                create_period: 'Opprett Revisjonsperiode',
                framework_code_label: 'Rammekode',
                select_framework: 'Velg et rammeverk...',
                period_label: 'Periodeetikett',
                period_start: 'Periodestart',
                period_end: 'Periodeslutt',
                start: 'Start',
                end: 'Slutt',
                framework: 'Rammeverk',
                retention_days: 'Oppbevaringsdager',
                section_period_dates: 'Periodedatoer',
                no_periods_found: 'Ingen revisjonsperioder funnet.',
                period_created: 'Revisjonsperiode opprettet',
                aria_table: 'Revisjonsperioder',
                suggested_boundaries: 'Foreslåtte grenser',
                suggested_boundaries_info: 'Foreslåtte periodegrenser er avledet fra hvert rammeverks regulatoriske rapporteringssyklus. Juster etter behov for din organisasjon.',
                reporting_cycle: 'Rapporteringssyklus',
                suggested_start: 'Foreslått start',
                suggested_end: 'Foreslått slutt',
                suggested_next: 'Foreslått neste periode',
                cycle_monthly: 'Månedlig',
                cycle_quarterly: 'Kvartalsvis',
                cycle_semi_annual: 'Halvårlig',
                cycle_annual: 'Årlig',
                cycle_custom: 'Egendefinert',
                period_deleted: 'Revisjonsperiode slettet',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Kundereferanse',
            country: 'Land / Jurisdiksjon',
            preferred_channel: 'Foretrukket kanal',
            channels: {
                email: 'E-post',
                postal: 'Post',
                phone: 'Telefon',
                portal: 'Portal',
            },
            source: 'Kilde',
            sources: {
                admin_manual: 'Administrasjonshåndbok',
                public_form: 'Offentlig form',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefon mottatt',
                postal_received: 'Post mottatt',
                email_received: 'E-post mottatt',
                in_person: 'Personlig',
                legacy_import: 'Eldre import',
            },
            required_fields: 'Type forespørsel, navn og e-post er påkrevd',
            create_error: 'Kunne ikke opprette sak',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Innstillinger',
            request_types: 'Forespørselstyper',
            request_types_desc: 'Konfigurer hvilke forespørselstyper som er aktivert for det offentlige inntaksskjemaet.',
            notifications: 'Varsler',
            ticket_numbering: 'Saksnummerering',
            ticket_format: 'Gjeldende format',
            ticket_prefix: 'Prefiks',
            ticket_prefix_note: 'ikke konfigurerbart',
            sla_deadlines: 'SLA-frister',
            sla_desc: 'Angi maksimal svartid (i kalenderdager) for hver forespørselstype. Saker som nærmer seg fristen vil vise advarsler.',
            days: 'dager',
            warning_threshold: 'Advarselsterskel',
            escalation_label: 'Aktiver automatisk eskalering ved SLA-brudd',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Lagre SLA-konfigurasjon',
            saving: 'Lagrer...',
            saved: 'Lagret',
            announce_loaded: 'Innstillinger lastet',
            email_on_creation: 'E-postvarsling ved opprettelse av sak',
            send_confirmation: 'Send bekreftelse på e-post til innmelder',
            users: 'Brukere',
            roles: 'Roller',
            api_keys: 'API Taster',
            mfa: 'MFA',
            sessions: 'Økter',
            webhooks: 'Webhooks',
            modules: 'Moduler',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Innsyn (DSAR)',
                deletion: 'Sletting',
                correction: 'Retting',
                portability: 'Portabilitet',
                objection: 'Innsigelse',
                restriction: 'Begrensning',
                consent_withdrawal: 'Tilbaketrekking av samtykke',
                protective_measures: 'Beskyttelsestiltak',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Seksjonstildelinger',
            assign_section: 'Tildel seksjon',
            section_ref: 'Seksjonsreferanse',
            section_label: 'Seksjonsnavn',
            department_id: 'Avdeling',
            assignee_id: 'Ansvarlig',
            mandatory: 'Obligatorisk',
            assign: 'Tildel',
            no_assignments: 'Ingen seksjonstildelinger',
            status: 'Status',
            completion: 'Fullføring',
            mark_complete: 'Fullfør',
        },
        consolidation: {
            title: 'Konsolideringsfremdrift',
            add_child: 'Legg til underoppgave',
            child_task_id: 'Underoppgave-id',
            department_id: 'Avdeling',
            link: 'Lenke',
            overall_completion: 'Total fullføring',
            departments_complete: 'Avdelinger fullført',
            overdue: 'Forfalt',
            ready: 'Klar',
            not_ready: 'Ikke klar',
            master_status: 'Overordnet status',
            department_breakdown: 'Avdelingsoversikt',
            department: 'Avdeling',
            status: 'Status',
            completion: 'Fullføring',
            due_date: 'Forfallsdato',
            overdue_flag: 'Forfalt',
            child_tasks: 'Underoppgaver',
            task: 'Oppgave',
        },
        conflict_detection: {
            title: 'Konfliktdeteksjon',
            unresolved: 'uløst',
            no_conflicts: 'Ingen konflikter oppdaget',
            section: 'Seksjon',
            field: 'Felt',
            dept_a: 'Avdeling A',
            value_a: 'Verdi A',
            dept_b: 'Avdeling B',
            value_b: 'Verdi B',
            severity: 'Alvorlighet',
            status: 'Status',
            resolved: 'Løst',
            open: 'Åpen',
            resolve: 'Løs',
        },
        org_maturity: {
            title: 'Organisatorisk Modenhet',
            departments: 'Avdelinger',
            department_hierarchy: 'Avdelingshierarki',
            create_department: 'Opprett Avdeling',
            department_name: 'Avdelingsnavn',
            department_code: 'Kode',
            level: 'Nivå',
            division: 'Divisjon',
            department: 'Avdeling',
            team: 'Team',
            parent_department: 'Overordnet Avdeling',
            head_user: 'Avdelingsleder',
            no_departments: 'Ingen avdelinger konfigurert.',
            user_mappings: 'Brukertilknytninger',
            map_user: 'Tilknytt Bruker',
            user_id: 'Bruker-ID',
            role: 'Rolle',
            no_users: 'Ingen brukere tilknyttet denne avdelingen.',
            remove: 'Fjern',
            framework_assignments: 'Rammeverktildelinger',
            assign_framework: 'Tildel Rammeverk',
            entity_id: 'Enhets-ID',
            entity_type: 'Enhetstype',
            framework_id: 'Rammeverk-ID',
            framework_code: 'Rammeverkskode',
            no_assignments: 'Ingen rammeverktildelinger.',
            compliance_rollup: 'Samsvarsoppsummering',
            entity_name: 'Enhet',
            total_assessments: 'Totale Vurderinger',
            completed: 'Fullført',
            completion_pct: 'Fullføring %',
            open_gaps: 'Åpne Mangler',
            critical_gaps: 'Kritiske',
            compliance_score: 'Poengsum',
            no_rollup: 'Ingen data for samsvarsoppsummering tilgjengelig.',
            entity_summary: 'Enhetens Samsvarssammendrag',
            frameworks_assigned: 'Rammeverk',
            overall_score: 'Samlet Poengsum',
            load_departments_error: 'Kunne ikke laste avdelinger',
            load_users_error: 'Kunne ikke laste brukere',
            department_created: 'Avdeling opprettet',
            department_created_announce: 'Avdeling opprettet',
            create_department_error: 'Kunne ikke opprette avdeling',
            user_mapped: 'Bruker tilknyttet',
            user_mapped_announce: 'Bruker tilknyttet avdeling',
            map_user_error: 'Kunne ikke tilknytte bruker',
            user_removed: 'Bruker fjernet',
            load_rollup_error: 'Kunne ikke laste sammendrag',
            load_assignments_error: 'Kunne ikke laste tilordninger',
            framework_assigned: 'Rammeverk tildelt',
            framework_assigned_announce: 'Rammeverk tildelt til enhet',
            assign_framework_error: 'Kunne ikke tilordne rammeverk',
            assignment_removed: 'Tildeling fjernet',
            enter_entity_id_placeholder: 'Skriv inn enhets-ID for å se tildelinger',
            entity_type_organization: 'Organisasjon',
            entity_type_department: 'Avdeling',
        },
        onboarding_maturity: {
            title: 'Onboarding modenhet',
            quick_starts: 'Rask start',
            progress: 'Onboarding Progress',
            personas: 'Persona Paths',
            persona_onboarding: 'Persona Onboarding',
            quick_start_scenarios: 'Hurtigstartscenarier',
            start: 'Start',
            restart: 'Start på nytt',
            estimated_minutes: 'Anslått minutter',
            steps: 'Trinn',
            scenario: 'Scenario',
            no_scenarios: 'Ingen hurtigstartscenarier tilgjengelig.',
            persona_id: 'Persona-ID',
            persona_label: 'Persona',
            role_description: 'Rollebeskrivelse',
            primary_modules: 'Primære moduler',
            recommended_tours: 'Anbefalte turer',
            sample_data_modules: 'Eksempel på datamoduler',
            quick_start_scenario: 'Hurtigstartscenario',
            no_personas: 'Ingen persona-stier tilgjengelig.',
            framework_setup: 'Regulatoriske rammeverk',
            framework_code: 'Rammekode',
            framework_name: 'Rammenavn',
            related_modules: 'Relaterte moduler',
            persona_relevance: 'Relevante personas',
            no_frameworks: 'Ingen alternativer for oppsett av rammeverk tilgjengelig.',
            tier_config: 'Konfigurasjon av nivåmodul',
            tier: 'Nivå',
            available_modules: 'Tilgjengelige moduler',
            module_count: 'Antall moduler',
            overall_completion: 'Samlet fullføring',
            wizard_completed: 'Veiviseren fullført',
            tours_completed: 'Turer fullført',
            quick_starts_completed: 'Hurtigstarter fullført',
            selected_frameworks: 'Utvalgte rammer',
            no_progress: 'Ingen ombordstigningsdata tilgjengelig.',
            detected_persona: 'Oppdaget persona',
            current_tier: 'Nåværende nivå',
            completion_pct: 'Fullføring',
            progress_page: {
                loaded: 'Fremgang lastet',
                no_progress: 'Ingen onboarding-fremgangsdata tilgjengelig.',
                overall: 'Samlet fremgang',
                detected_persona: 'Oppdaget persona',
                tier: 'Nivå',
                modules: 'moduler',
                wizard: 'Veiviser',
                completed: 'Fullført',
                pending: 'Ventende',
                tours: 'Omvisninger',
                quick_starts: 'Hurtigstarter',
                frameworks: 'Valgte rammeverk',
                no_frameworks: 'Ingen rammeverk valgt.',
                go_to_wizard: 'Fullfør oppsettveiviseren',
                framework_selection: 'Rammeverkvalg',
                framework_toggle: 'Aktiver eller deaktiver rammeverk for ditt etterlevingsprogram.',
                framework_updated: 'Rammeverkvalg oppdatert.',
                view_tours: 'Se omvisninger',
                view_quick_starts: 'Se hurtigstarter',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Velg en persona for å se anbefalte moduler, omvisninger og hurtigstartscenarier.',
                no_personas: 'Ingen personastier tilgjengelig.',
                modules: 'Moduler',
                tours: 'Omvisninger',
                quick_start: 'Hurtigstart',
                details: 'Detaljer',
                primary_modules: 'Primære moduler',
                recommended_tours: 'Anbefalte omvisninger',
                framework_options: 'Regulatoriske rammeverk',
                no_frameworks: 'Ingen alternativer for rammeverkoppsett tilgjengelig.',
                no_frameworks_explain: 'Ingen regulatoriske rammeverk er installert for organisasjonen din ennå, så det er ingenting å sette opp her.',
                no_frameworks_action: 'Installer en markedspakke for å legge til rammeverkene som gjelder for deg',
                start_tour: 'Start',
                no_tours: 'Ingen omvisninger tilgjengelig.',
                help_center: 'Hjelpesenter',
                no_articles: 'Ingen artikler funnet.',
                primary: 'Primær',
                framework_code: 'Rammeverkkode',
                framework_label: 'Etikett',
                region: 'Region',
                category: 'Kategori',
                enabled: 'Aktivert',
                disabled: 'Deaktivert',
                tier_modules: 'Nivåmodulkonfigurasjon',
                tiers_loaded: 'Nivåkonfigurasjoner lastet',
                no_tier_config: 'Ingen nivåkonfigurasjon tilgjengelig.',
                view_tours: 'Se modulomvisninger',
                view_quick_start: 'Start hurtigstart',
                toggle_framework: 'Veksle rammeverk',
                framework_updated: 'Rammeverkvalg oppdatert.',
                tier_desc_light: 'Grunnleggende personvernfunksjoner — PDPL-etterlevingsgrunnlag',
                tier_desc_standard: 'Komplett etterlevingspakke — alle moduler med arbeidsflyter',
                tier_desc_enterprise: 'Avansert styring og automatisering — multi-enhet, grupperapportering',
                persona_highlight: 'Dine moduler',
                aria_go_to_module: '{name} — gå til modulen',
                aria_start_tour: 'Start {title}',
                aria_quick_start: 'Rask start: {title}',
                aria_help_article: 'Hjelpeartikkel: {title}',
                aria_view_framework: '{label}{primary} – se rammedetaljer',
                aria_primary_for_persona: '{name} (primær for denne persona)',
                primary_for_persona_suffix: ' (primær for denne persona)',
                aria_view_in_persona_paths: '{name} – se i Persona Paths',
                progress_of_total: '{completed} av {total} fullført',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Øv vanlige arbeidsflyter med guidede hurtigstartscenarier.',
                no_scenarios: 'Ingen hurtigstartscenarier tilgjengelig.',
                started: 'Hurtigstart startet',
                status_completed: 'Fullført',
                status_in_progress: 'Pågår',
                status_not_started: 'Ikke startet',
                minutes: 'min',
                steps: 'trinn',
                step: 'Trinn',
                restart: 'Start på nytt',
                start: 'Start',
                go_to_module: 'Gå til modul',
                next_step: 'Neste trinn',
                complete: 'Fullfør',
                reset: 'Tilbakestill',
                step_of: 'av',
                action_navigate: 'Naviger',
                action_form_fill: 'Fyll ut skjema',
                action_review: 'Gjennomgå',
                action_confirm: 'Bekreft',
                step_changed: 'Trinn {current} av {total}: {title}',
                completed_message: 'Hurtigstart fullført!',
                reset_message: 'Hurtigstarten er tilbakestilt.',
            },
            persona_p_vn_01_label: 'Enterprise DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Avdelingseier',
            persona_p_vn_02_description: 'Avdelingsleder ansvarlig for seksjonssamsvarsrapportering',
            persona_p_vn_03_label: 'Overholdelse av e-handel',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Leder for datastyring',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cybersikkerhet og juridiske operasjoner',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Leder for styring',
            persona_p_vn_07_description: 'Lede som overvåker AI systemstyring, risikovurderinger og klassifisering',
            persona_p_vn_08_label: 'Cross-Border & Vendor Gov',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Breach Response Lead',
            persona_p_vn_09_description: 'Lede koordinere bruddvarsling og hendelsesrespons',
            persona_p_vn_10_label: 'Leder for internrevisjon',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR- og ansattdata-ansvarlig',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Compliance-leder for multinasjonalt datterselskap',
            persona_p_vn_12_description: 'Personvernombud i vietnamesisk datterselskap av utenlandsk selskap som trenger lokalt compliance-lag',
            persona_p_vn_13_label: 'Compliance-leder for utdanning',
            persona_p_vn_13_description: 'Compliance-leder ved utenlandsk skole som håndterer databeskyttelse for elever og foreldre',
            qs_hr_consent_title: 'Registrer ansattsamtykker',
            qs_hr_consent_description: 'Gjennomgang av registrering av ansattsamtykke og generering av dekningsrapport.',
            qs_subsidiary_dpia_title: 'Opprett DPIA-vurdering for datterselskap',
            qs_subsidiary_dpia_description: 'Gjennomgang av opprettelse av DPIA for en databehandlingsaktivitet i datterselskap.',
            qs_student_consent_title: 'Sett opp innsamling av elevsamtykke',
            qs_student_consent_description: 'Gjennomgang av opprettelse av foreldresamtykkepolicyer for elevdata.',
            persona_p_vn_14_label: 'Ekstern juridisk rådgiver',
            persona_p_vn_14_description: 'Senior advokatfirmapartner som gir råd til flere klientorganisasjoner om PDPL overholdelse',
            persona_p_vn_15_label: 'Driftsleder i advokatfirmaet',
            persona_p_vn_15_description: 'Kundeengasjementskoordinator som administrerer leveringssporing og koordinering av tidsfrister',
            persona_p_vn_16_label: 'Bedriftseier / CEO',
            persona_p_vn_16_description: 'CEO eller GM som er juridisk behandlingsansvarlig under PDPL, ansvarlig for investering i samsvar',
            qs_dpia_filing_title: 'Fullfør din første DPIA innlevering',
            qs_dept_compliance_title: 'Send inn din første avdelingsseksjon for samsvar',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Sett opp datahåndtering for teleabonnenter',
            qs_data_flow_title: 'Kartlegg din første dataflyt',
            qs_incident_response_title: 'Konfigurer arbeidsflyt for respons på hendelser',
            qs_ai_risk_title: 'Kjør en AI systemrisikovurdering',
            qs_cross_border_title: 'Vurder en grenseoverskridende dataoverføring',
            qs_breach_notification_title: 'Simuler en arbeidsflyt for bruddvarsling',
            qs_audit_trail_title: 'Gjennomgå et samsvarsrevisjonsspor',
            qs_legal_advisor_portfolio_title: 'Gjennomgå porteføljen for overholdelse av flere klienter',
            qs_legal_advisor_portfolio_description: 'Gjennomgå overholdelsesstatus på tvers av flere kundeorganisasjoner.',
            qs_law_firm_ops_title: 'Spor klientleveransestatus',
            qs_law_firm_ops_description: 'Overvåk fremdrift og kundeengasjement i rådgivningsleveranser.',
            qs_ceo_dashboard_title: 'Se kontrollpanelet for overholdelse av ledere',
            qs_ceo_dashboard_description: 'Gjennomgå overholdelsesstatus på høyt nivå og investeringsberegninger.',
        },
        rights_requests: {
            response_templates: {
                title: 'Svarmaler',
                subtitle: 'Administrer DSR-svarbrevmalene som ResponseGenerator bruker til å utarbeide svar til registrerte.',
                create_btn: 'Ny mal',
                edit_btn: 'Rediger mal',
                filter_right_type: 'Filtrer etter riktig type',
                filter_locale: 'Filtrer etter lokalitet',
                filter_status: 'Filtrer etter status',
                all_right_types: 'Alle riktige typer',
                any_right_type: 'Alle (gjelder alle typer)',
                all_locales: 'Alle lokaliteter',
                all_statuses: 'Alle',
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                empty_state: 'Ingen maler samsvarer med gjeldende filtre.',
                columns: {
                    template_id: 'Mal-ID',
                    outcome: 'Utfall',
                    locale: 'Språk',
                    right_type: 'Høyre type',
                    is_active: 'Status',
                    last_updated: 'Sist oppdatert',
                    actions: 'Handlinger',
                },
                actions: {
                    edit: 'Rediger',
                    clone: 'Klone',
                    deactivate: 'Deaktiver',
                    reactivate: 'Aktiver på nytt',
                    preview: 'Forhåndsvisning',
                },
                form: {
                    template_id: 'Mal-ID',
                    template_id_help: 'Små bokstaver, f.eks. dsr_access_fulfilled_vi',
                    outcome: 'Utfall',
                    locale: 'Språk',
                    right_type: 'Høyre type',
                    subject: 'Emne',
                    body: 'Kroppsmal',
                    body_help: 'Bruk plassholdere som {case_reference}, {data_subject_name}, {fulfilled_at}. Minimum 15 linjer for flerseksjonsbokstaver.',
                    is_active: 'Aktiv',
                    official_form_id: 'Offisiell skjema-ID (valgfritt)',
                },
                outcomes: {
                    fulfilled: 'Oppfylt',
                    partially_fulfilled: 'Delvis oppfylt',
                    denied: 'Nektet',
                },
                clone_modal_title: 'Klonemal',
                clone_target_locale: 'Målsted',
                clone_help: 'En ny mal vil bli opprettet i mållokalet med denne malen som utgangspunkt.',
                preview_modal_title: 'Forhåndsvisning av mal',
                preview_note: 'Forhåndsvisning bruker demo-case DSR-DEMO-0020 plassholdere. Ekte levering erstatter live case-data.',
                preview_unavailable: 'Forhåndsvisning krever en eksisterende sak. Legg ved denne malen til en sak og bruk utkast til svarhandling på saksdetaljsiden.',
                deactivate_confirm: 'Vil du deaktivere denne malen? Den vil fortsatt gjelde for eksisterende saker som refererer til den.',
                toasts: {
                    created: 'Mal opprettet',
                    updated: 'Malen er oppdatert',
                    deactivated: 'Malen er deaktivert',
                    reactivated: 'Mal reaktivert',
                    cloned: 'Mal klonet',
                    load_error: 'Kunne ikke laste inn maler',
                    save_error: 'Kunne ikke lagre malen',
                    clone_error: 'Kunne ikke klone malen',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Avdelinger',
                subtitle: 'Administrer avdelingene som ruter DSR-saker, DPIA oppgaver og overholdelsesarbeidsflyter.',
                create_btn: 'Ny avdeling',
                edit_btn: 'Rediger avdeling',
                columns: {
                    name: 'Navn',
                    description: 'Beskrivelse',
                    active_cases: 'Aktive saker',
                    members: 'Medlemmer',
                    status: 'Status',
                    actions: 'Handlinger',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    name: 'Navn',
                    description: 'Beskrivelse',
                },
                actions: {
                    edit: 'Rediger',
                    deactivate: 'Deaktiver',
                    reactivate: 'Aktiver på nytt',
                    delete: 'Slett',
                    manage_members: 'Administrer medlemmer',
                },
                delete_modal_title: 'Slett avdeling',
                delete_warning: 'Denne avdelingen har fortsatt aktive saker. Velg en avdeling du vil tilordne dem til før sletting.',
                delete_no_cases: 'Denne avdelingen har ingen aktive saker og kan slettes trygt.',
                reassign_prompt: 'Tilordne aktive saker på nytt til:',
                reassign_none_available: 'Ingen andre aktive avdelinger tilgjengelig. Opprett en annen avdeling først.',
                member_section_title: 'Medlemmer',
                add_member: 'Legg til medlem',
                remove_member: 'Fjern',
                member_role: 'Rolle',
                no_members: 'Ingen medlemmer tilordnet denne avdelingen.',
                toasts: {
                    created: 'Avdeling opprettet',
                    updated: 'Avdeling oppdatert',
                    deactivated: 'Avdeling deaktivert',
                    reactivated: 'Avdeling reaktivert',
                    deleted: 'Avdeling slettet',
                    reassigned_count: '{count} sak(er) er tilordnet på nytt',
                    member_added: 'Medlem lagt til',
                    member_removed: 'Medlem fjernet',
                    load_error: 'Kunne ikke laste avdelinger',
                    save_error: 'Kunne ikke lagre avdelingen',
                    delete_error: 'Kunne ikke slette avdelingen',
                },
            },
            retention_policies: {
                title: 'Retningslinjer for oppbevaring',
                subtitle: 'Konfigurer oppbevaringsvinduer per måltype. Identitetsverifiseringsdokumenter bruker dette til å stemple retention_expires_at på fremtidige opplastinger.',
                create_btn: 'Ny politikk',
                edit_btn: 'Rediger policy',
                columns: {
                    target_type: 'Måltype',
                    retention_days: 'Oppbevaring (dager)',
                    owner: 'Poliseier',
                    effective_from: 'Gjelder fra',
                    last_reviewed: 'Sist anmeldt',
                    status: 'Status',
                    actions: 'Handlinger',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    target_type: 'Måltype',
                    target_type_help: 'Hvilken datakategori oppbevaringsvinduet gjelder for. For øyeblikket støttes bare DSR-identitetsverifiseringsdokumenter.',
                    retention_days: 'Oppbevaringsvindu (dager)',
                    retention_days_help: 'Mellom 1 og 3650 dager (maks. 10 år).',
                    owner: 'Poliseier',
                    owner_help: 'Vanligvis DPO. Ansvarlig for oppbevaringsbeslutningen under DG-2.',
                    effective_from: 'Gjelder fra',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR identitetsverifiseringsdokumenter',
                },
                deactivate_notice: 'Deaktivering påvirker ikke allerede stemplede vedlegg – bare fremtidige opplastinger vil ikke lenger motta et oppbevaringsvindu.',
                deactivate_confirm: 'Vil du deaktivere denne oppbevaringsretningslinjen?',
                toasts: {
                    created: 'Oppbevaringspolicy opprettet',
                    updated: 'Oppbevaringspolicy oppdatert',
                    deactivated: 'Retningslinjer for oppbevaring er deaktivert',
                    load_error: 'Kunne ikke laste inn retningslinjer for oppbevaring',
                    save_error: 'Kunne ikke lagre retningslinjene for oppbevaring',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Systemer',
            categories: 'Datakategorier',
            registry: 'Dataregister',
            purposes: 'Behandlingsformål',
            retention: 'Oppbevaring',
            flows: 'Dataflyter',
            activities: 'Behandlingsaktiviteter',
            evidence: 'Dokumentasjon',
            exports: 'Eksporter',
            attestation: 'Attestasjonssykluser',
            governance: 'Styring',
            review: 'Gjennomgangsoppgaver',
            inventory: 'Inventar',
            exceptions: 'Unntak',
            history: 'Historikk',
            ropa_completeness: 'ROPA fullstendighet',
        },
        ropa_completeness_autofix: {
            ask_button: 'Spør Forseti',
            drafting: 'Utkaster …',
            accept: 'Godta',
            reject: 'Avvis',
            accepted: 'Godtatt – søk via aktivitetsredigering.',
            rejected: 'Avvist.',
            needs_human_input: 'Forseti trenger mer aktivitetskontekst (f.eks. et dokumentert formål) før utkast til dette feltet.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA fullstendighetsrapport',
            subtitle: 'Hygienerevisjon per felt av dine registreringer av behandlingsaktiviteter, omfattet av regelverket.',
            framework_label: 'Rammeverk',
            kpi_total: 'Aktiviteter scoret',
            kpi_critical: 'Kritisk',
            kpi_high: 'Høy',
            kpi_conditional: 'Betinget',
            field_chart_heading: 'Manglende felt etter frekvens',
            drilldown_heading: 'Aktiviteter med hull',
            col_activity: 'Aktivitet',
            col_field: 'Felt',
            col_severity: 'Alvorlighetsgrad',
            col_condition: 'Tilstand',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kritisk',
            severity_high: 'Høy',
            severity_conditional: 'Betinget',
            severity_informational: 'Informasjonsmessig',
            include_informational: 'Inkluder informasjon',
            export_csv: 'Last ned CSV',
            exporting: 'Eksporterer...',
            loading: 'Laster…',
            empty_state: 'Alle aktive aktiviteter har fullstendige ROPA-poster for dette rammeverket.',
            error_fetch: 'Kunne ikke laste inn fullstendighetsrapporten.',
            error_unsupported_framework: 'Dette rammeverket støttes ikke.',
            error_framework_not_installed: 'Dette rammeverket er ikke installert for organisasjonen din.',
            no_misses_for_field: 'Ingen aktiviteter mangler dette feltet.',
            framework_PDPL: 'PDPL (lov 91/2025 + dekret 356)',
            framework_GDPR: 'GDPR Artikkel 30',
            framework_DECREE_165: 'Dekret 165 (Vietnam Data Law)',
            framework_ISO_27701: 'ISO 27701 (informasjon)',
        },
        registry: {
            // Sprint 64.72.0 — kolonneoverskrifter for dataregisterets eksport.
            export_col: {
                registry_item_id: 'Registerpost-ID',
                name: 'Navn',
                description: 'Beskrivelse',
                field_identifiers: 'Feltidentifikatorer',
                category_id: 'Kategori-ID',
                category_name: 'Kategorinavn',
                data_type: 'Datatype',
                effective_sensitivity: 'Gjeldende sensitivitet',
                sensitivity_override: 'Overstyrt sensitivitet',
                data_subjects: 'Registrerte',
                volume_band: 'Mengdeintervall',
                source_system_ids: 'Kildesystem-ID-er',
                processing_activity_ids: 'Behandlingsaktivitets-ID-er',
                retention_policy_id: 'Lagringspolicy-ID',
                effective_retention_policy_id: 'Gjeldende lagringspolicy-ID',
                purposes: 'Formål',
                collection_method: 'Innsamlingsmetode',
                legal_basis: 'Rettslig grunnlag',
                data_tier: 'Datanivå',
                data_tier_source: 'Kilde for datanivå',
                storage_locations: 'Lagringssteder',
                cross_border: 'Grensekryssende',
                status: 'Status',
                source_form_submission_id: 'Kildeskjemaets innsendings-ID',
                source_form_template_id: 'Kildeskjemaets mal-ID',
                last_reviewed_at: 'Sist gjennomgått',
                next_review_at: 'Neste gjennomgang',
                created_at: 'Opprettet',
                updated_at: 'Oppdatert',
                data_element: 'Dataelement',
                data_category: 'Datakategori',
                systems: 'Systemer',
                retention_policy: 'Lagringspolicy',
                name_of_processing: 'Behandlingens navn',
                categories_of_personal_data: 'Kategorier av personopplysninger',
                categories_of_data_subjects: 'Kategorier av registrerte',
                recipients: 'Mottakere',
                third_country_transfers: 'Overføringer til tredjeland',
                retention_period: 'Lagringstid',
                doc_title: 'Dataregister',
                doc_generated: 'Eksport generert {timestamp}',
                doc_item_count: '{count} registerpost(er)',
                doc_template_full: 'Fullstendig',
            },
            export_vocab: {
                data_type: {
                    text: 'Tekst',
                    number: 'Tall',
                    date: 'Dato',
                    datetime: 'Dato og klokkeslett',
                    boolean: 'Ja/nei',
                    binary: 'Binær',
                    structured: 'Strukturert',
                    file: 'Fil',
                    identifier: 'Identifikator',
                    geolocation: 'Geoposisjon',
                },
                collection_method: {
                    direct_from_subject: 'Direkte fra den registrerte',
                    third_party: 'Fra tredjepart',
                    derived: 'Utledet av andre data',
                    observed: 'Observert',
                    inferred: 'Sluttet seg til',
                    public_source: 'Offentlig kilde',
                },
                data_subject_type: {
                    customer: 'Kunde',
                    employee: 'Ansatt',
                    contractor: 'Oppdragstaker',
                    prospect: 'Mulig kunde',
                    minor: 'Mindreårig',
                    patient: 'Pasient',
                    student: 'Student',
                    beneficiary: 'Begunstiget',
                    vendor_contact: 'Leverandørkontakt',
                    website_visitor: 'Nettstedsbesøkende',
                    other: 'Annet',
                },
                volume_band: {
                    lt_1k: 'Færre enn 1 000 poster',
                    '1k_to_10k': '1 000 til 10 000 poster',
                    '10k_to_100k': '10 000 til 100 000 poster',
                    '100k_to_1m': '100 000 til 1 million poster',
                    gt_1m: 'Mer enn 1 million poster',
                    unknown: 'Ukjent',
                },
                tier_source: {
                    assessed_and_approved: 'Vurdert og godkjent',
                    manual_legacy: 'Overtatt fra en eldre verdi',
                    legacy_platform_field: 'Arvet plattformverdi',
                    scheme_scoped_only: 'Vurdert etter ordningsspesifikke nivåer — ingen enkelt kode dekker flere lovfestede nivåskalaer',
                },
            },
            page: {
                title: 'Dataregister',
                subtitle: 'Personlige dataelementer organisasjonen din har, organisert under Datakategorier.',
                empty_state: 'Ingen registerelementer ennå. Legg til en vare manuelt eller importer fra et regulatorisk register.',
                empty_filter_state: 'Ingen varer samsvarer med filtrene dine.',
            },
            filter: {
                category: 'Kategori',
                sensitivity: 'Følsomhet',
                data_subject: 'Datasubjekt',
                status: 'Status',
                data_tier: 'Datalag',
                cross_border: 'Grenseoverskridende',
                has_owner: 'Har eier',
                has_form_source: 'Importert fra skjema',
                search_placeholder: 'Søk etter navn, beskrivelse eller feltidentifikator …',
                clear: 'Tøm filtre',
                option_all: 'Alle',
            },
            col: {
                name: 'Navn',
                category: 'Kategori',
                sensitivity: 'Følsomhet',
                data_subjects: 'Datasubjekter',
                systems: 'Systemer',
                activities: 'Aktiviteter',
                owner: 'Eier',
                retention: 'Oppbevaring',
                data_tier: 'Datalag',
                status: 'Status',
                last_reviewed: 'Sist anmeldt',
                actions: 'Handlinger',
            },
            action: {
                add_manual: 'Legg til element',
                add_from_form: 'Legg til fra forskriftsregister',
                import_history: 'Importer tidligere innsendinger',
                export: 'Eksporter',
                archive: 'Arkiv',
                restore: 'Gjenopprett',
                edit: 'Rediger',
                assign_owner: 'Tildel eier',
                attach_evidence: 'Legg ved bevis',
                open_lineage: 'Åpen slekt',
            },
            modal_editor: {
                section_identity: 'Identitet',
                section_classification: 'Klassifisering',
                section_subjects: 'Emner og volum',
                section_location: 'Plassering og overføring',
                section_systems: 'Systemer og aktiviteter',
                section_retention: 'Oppbevaring og lov',
                section_governance: 'Styring',
                section_notes: 'Notater',
                hint_inherited: '(arvet fra kategori)',
                hint_overridden: '(overstyrt)',
                placeholder_field_identifiers: 'f.eks. kunder.primary_email, hris.employee_id',
                validation_name_required: 'Navn er påkrevd',
                validation_category_required: 'Kategori er påkrevd',
                validation_data_subjects_required: 'Det kreves minst én type registrert',
                label_data_type: 'Datatype',
                label_field_identifiers: 'Feltidentifikatorer (kommaseparert)',
                label_data_subject_types: 'Datasubjekttyper',
                label_volume_band: 'Volumbånd',
                label_storage_locations: 'Lagringsplasseringer (kommaseparert)',
                placeholder_storage_locations: 'ISO-3166 koder eller fritekst (f.eks. VN, Hetzner EU)',
                label_cross_border: 'Overføring over landegrensene',
                label_source_systems: 'Kildesystemer',
                label_processing_activities: 'Behandlingsaktiviteter',
                label_collection_method: 'Innsamlingsmetode',
                label_purposes: 'Formål',
                hint_multi_select: 'Hold Cmd/Ctrl for å velge flere.',
                option_inherit_category: '— (arve fra kategori)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Importer forhåndsvisning',
                label_to_create: 'Å skape',
                label_to_update: 'For å oppdatere',
                label_skipped_archived: 'Hoppet over (arkivert)',
                label_conflicts: 'Konflikter',
                commit: 'Importer',
                cancel: 'Avbryt',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Kategori',
                section_systems: 'Kildesystemer',
                section_activities: 'Behandlingsaktiviteter',
                section_flows: 'Dataflyter',
                section_retention: 'Effektiv oppbevaring',
                section_governance: 'Styring',
                section_evidence: 'Bevis',
                section_audit: 'Nylig revisjonsspor',
                section_source_form: 'Innsending av kildeskjema',
                badge_inherited: 'Nedarvet',
                badge_overridden: 'Overstyrt',
            },
            toast: {
                created: 'Registerelement opprettet.',
                updated: 'Registerelementet er oppdatert.',
                archived: 'Registerelement arkivert.',
                imported: '{count} elementer importert.',
                conflict_warning: '{count} felt(er) beholdt endringene dine – se konfliktliste.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Systemoversikt',
            new: 'Nytt system',
            edit: 'Rediger system',
            name: 'Navn',
            owner: 'Eier',
            criticality: 'Kritikalitet',
            dpa_status: 'DPA-status',
            location: 'Plassering',
            system_type: 'Systemtype',
            metadata: 'Metadata (JSON)',
            department: 'Avdeling',
            hosting_location: 'Driftsplassering',
            hosting_provider: 'Driftsleverandør',
            data_residency_country: 'Land for datalagring',
            hosting: 'Drift',
            no_systems: 'Ingen systemer funnet',
            confirm_archive: 'Arkiver system «{name}»?',
            count: 'systemer',
            count_one: 'system',
            type_internal: 'Intern',
            type_external: 'Ekstern',
            type_cloud_service: 'Skytjeneste',
            type_on_premise: 'Lokalt',
            type_hybrid: 'Hybrid',
            status_active: 'Aktiv',
            status_decommissioning: 'Under avvikling',
            status_decommissioned: 'Avviklet',
            status_planned: 'Planlagt',
            dpa_not_required: 'Ikke påkrevd',
            dpa_required_missing: 'Påkrevd mangler',
            dpa_in_progress: 'Pågår',
            dpa_signed: 'Signert',
            all_criticalities: 'Alle kritikaliteter',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Datakategorier',
            new: 'Ny kategori',
            edit: 'Rediger kategori',
            name: 'Navn',
            sensitivity: 'Sensitivitet',
            tags: 'Merkelapper',
            used_in: 'Brukt i',
            examples: 'Eksempler',
            examples_hint: 'Eksempler (kommaseparert)',
            default: 'Standard',
            all_sensitivities: 'Alle sensitiviteter',
            all_statuses: 'Alle statuser',
            no_categories: 'Ingen kategorier funnet',
            confirm_delete: 'Slett kategori «{name}»?',
            count: 'kategorier',
            count_one: 'kategori',
            status_draft: 'Utkast',
            status_proposed: 'Foreslått',
            status_under_review: 'Under gjennomgang',
            status_approved: 'Godkjent',
            status_deprecated: 'Utdatert',
            status_updated: 'Kategoristatus oppdatert',
            propose: 'Foreslå',
            approve: 'Godkjenn',
            deprecate: 'Utdater',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Behandlingsformål',
            new: 'Nytt formål',
            edit: 'Rediger formål',
            name: 'Navn',
            legal_basis: 'Rettslig grunnlag',
            retention_period: 'Oppbevaringsperiode (dager)',
            justification: 'Berettiget interesse-begrunnelse',
            legal_basis_detail: 'Detaljer om rettslig grunnlag',
            all_legal_bases: 'Alle rettslige grunnlag',
            no_purposes: 'Ingen formål funnet',
            confirm_delete: 'Slett «{name}»?',
            default: 'Standard',
            count: '{count} formål',
            count_one: '{count} formål',
        },
        retention: {
            title: 'Oppbevaringsregler',
            new: 'Ny regel',
            edit: 'Rediger regel',
            name: 'Navn',
            retention_days: 'Oppbevaringsdager',
            disposal_method: 'Avhendingsmetode',
            data_categories: 'Datakategorier',
            activities: 'Aktiviteter',
            review_frequency: 'Gjennomgangsfrekvens',
            review_frequency_days: 'Gjennomgangsfrekvens (dager)',
            no_policies: 'Ingen oppbevaringsregler funnet',
            confirm_delete: 'Slett «{name}»?',
            count: '{count} regler',
            count_one: '{count} regel',
            days: 'dager',
            disposal_deletion: 'Sletting',
            disposal_anonymization: 'Anonymisering',
            disposal_archival: 'Arkivering',
            review_overdue: 'Gjennomgang forfalt',
            review_status: 'Gjennomgangsstatus',
            review_ok: 'I rute',
            department: 'Avdeling',
            all_departments: 'Alle avdelinger',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Strømningsvisning',
            view_list: 'Liste',
            view_map: 'Kart',
            title: 'Dataflyter',
            new: 'Ny flyt',
            edit: 'Rediger flyt',
            name: 'Navn',
            from_system: 'Fra system',
            to_system: 'Til system',
            flow_type: 'Flyttype',
            cross_border: 'Grenseoverskridende',
            risk_level: 'Risikonivå',
            mechanism: 'Mekanisme',
            transfer_mechanism: 'Overføringsmekanisme',
            frequency: 'Frekvens',
            source_country: 'Kildeland',
            destination_country: 'Destinasjonsland',
            transfer_safeguard: 'Overføringssikkerhetstiltak',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Alle risikonivåer',
            no_flows: 'Ingen dataflyter funnet',
            confirm_archive: 'Arkiver «{name}»?',
            count: '{count} flyter',
            count_one: '{count} flyt',
            status_active: 'Aktiv',
            status_inactive: 'Inaktiv',
            status_under_review: 'Under gjennomgang',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Filoverføring',
            mechanism_database_replication: 'Databasereplikering',
            mechanism_manual: 'Manuell',
            mechanism_email: 'E-post',
            mechanism_other: 'Annet',
            frequency_real_time: 'Sanntid',
            frequency_hourly: 'Hver time',
            frequency_daily: 'Daglig',
            frequency_weekly: 'Ukentlig',
            frequency_monthly: 'Månedlig',
            frequency_on_demand: 'Ved behov',
            frequency_one_time: 'Engangs',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdiksjon',
        },
        activities: {
            title: 'Behandlingsaktiviteter',
            new: 'Ny aktivitet',
            edit: 'Rediger aktivitet',
            activity_title: 'Tittel',
            category: 'Kategori',
            legal_basis: 'Rettslig grunnlag',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RoPA generert',
            version: 'Versjon',
            generate_ropa: 'Generer RoPA',
            controller: 'Behandlingsansvarlig',
            controller_name: 'Navn på behandlingsansvarlig',
            controller_contact: 'Kontakt for behandlingsansvarlig',
            legal_basis_detail: 'Detaljer om rettslig grunnlag',
            organization_info: 'Organization Information',
            org_address: 'Organisasjonsadresse',
            tax_code: 'Skattekode',
            contact_person: 'Kontaktperson',
            contact_email: 'Kontakt-e-post',
            contact_phone: 'Kontakttelefon',
            select_entity: 'Velg...',
            no_entities: 'Ingen varer funnet',
            dpo_name: 'DPO-navn',
            dpo_contact: 'DPO-kontakt',
            processor_name: 'Databehandlernavn',
            processor_contact: 'Databehandlerkontakt',
            data_subjects: 'Registrerte',
            data_subjects_hint: 'Registrerte (kommaseparert)',
            recipients: 'Mottakere',
            recipients_hint: 'Mottakere (kommaseparert)',
            security_measures: 'Sikkerhetstiltak',
            dpia_reference: 'DPIA-referanse',
            no_activities: 'Ingen behandlingsaktiviteter funnet',
            confirm_archive: 'Arkiver «{name}»?',
            count: '{count} aktiviteter',
            count_one: '{count} aktivitet',
            ropa_success: 'RoPA-post generert. Gå til Eksporter for å laste den ned.',
            ropa_error: 'Kunne ikke generere RoPA',
            required_label: 'Påkrevd',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_under_review: 'Under gjennomgang',
            status_archived: 'Arkivert',
            decree_165_heading: 'Dekret 165 datalag (Vietnam Data Law)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Datalag',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Datalag',
            data_tier_regular: 'Vanlig',
            data_tier_important: 'Viktig',
            data_tier_core: 'Kjerne',
            transfer_mechanism_label: 'Overføringsmekanisme over landegrensene',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Sikkerhetsvurderingsreferanse',
            security_assessment_reference_placeholder: 'f.eks. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Dokumentasjonshvelv',
            new: 'Ny dokumentasjon',
            edit: 'Rediger dokumentasjon',
            name: 'Navn',
            evidence_type: 'Dokumentasjonstype',
            linked_entities: 'Tilknyttede enheter',
            uploaded: 'Opplastet',
            expiry: 'Utløpsdato',
            file_url: 'Fil-URL',
            source_mode_label: 'Beviskilde',
            source_mode_file: 'Last opp fil',
            source_mode_url: 'Lenk ekstern URL',
            attachment_label: 'Vedlegg',
            link_to: 'Knytt dokumentasjon til',
            entity_type: 'Enhetstype',
            entity_id: 'Enhets-ID',
            no_evidence: 'Ingen dokumentasjon funnet',
            manage_links: 'Administrer lenker',
            unlink: 'Fjern tilknytningen',
            confirm_delete: 'Slett dokumentasjon «{name}»?',
            count: '{count} elementer',
            count_one: '{count} element',
            type_document: 'Dokument',
            type_screenshot: 'Skjermbilde',
            type_policy: 'Retningslinje',
            type_contract: 'Kontrakt',
            type_assessment: 'Vurdering',
            type_correspondence: 'Korrespondanse',
            type_other: 'Annet',
            linked_type_system: 'System',
            linked_type_data_flow: 'Dataflyt',
            linked_type_processing_activity: 'Behandlingsaktivitet',
            linked_type_data_category: 'Datakategori',
            linked_type_purpose: 'Formål',
            linked_type_retention_policy: 'Oppbevaringspolicy',
            link_entity: 'Link til enhet',
            link_success: 'Enheten er tilkoblet',
            confirm_unlink: 'Er du sikker på at du vil fjerne tilknytningen til denne enheten?',
            unlink_success: 'Enheten ble fjernet',
            no_linked_entities: 'Ingen tilknyttede enheter',
            select_entity: 'Velg...',
            no_entities: 'Ingen varer',
        },
        exports: {
            title: 'Dataeksporter',
            recent: 'Nylige eksporter',
            export_type: 'Eksporttype',
            format: 'Format',
            requested_by: 'Forespurt av',
            requested_at: 'Forespurt',
            download: 'Last ned',
            csv_export: 'CSV-eksport',
            csv_description: 'Last ned data i CSV-format for regnearkanalyse.',
            pdf_reports: 'PDF-rapporter',
            pdf_description: 'Generer formaterte rapporter for samsvarsdokumentasjon.',
            csv_systems: 'Systemoversikt',
            csv_categories: 'Datakategorier',
            csv_flows: 'Dataflyter',
            csv_activities: 'Behandlingsaktiviteter',
            csv_cross_border: 'Grenseoverskridende flyter',
            csv_retention_policies: 'Retningslinjer for oppbevaring',
            csv_evidence: 'Beviselementer',
            csv_governance_assignments: 'Styringsoppdrag',
            pdf_ropa: 'RoPA-register',
            pdf_systems: 'Systemrapport',
            pdf_cross_border: 'Grenseoverskridende rapport',
            pdf_flow_map: 'Flytkartdata',
            export_failed: 'Eksport feilet',
            pdf_generated: 'PDF generert.',
            ropa: 'RoPA Register',
            retention_policies: 'Retningslinjer for oppbevaring',
            governance_assignments: 'Styringsoppdrag',
            col_entity: 'Eksporter enhet',
            col_description: 'Beskrivelse',
            desc_ropa: 'Fullstendig ROPA-register over alle behandlingsaktiviteter med juridisk grunnlag, DPIA status og kontroller.',
            desc_systems: 'Systeminventar med eier, datakategori-tagger og overholdelsesstatus.',
            desc_activities: 'Individuelle behandlingsaktiviteter med behandlingsansvarlig, juridisk grunnlag og DPIA klassifisering.',
            desc_categories: 'Datakategorier og sensitivitetsnivåer brukt på tvers av klassifisering og revisjoner.',
            desc_purposes: 'Behandlingsformål med det rettslige grunnlaget som er registrert for hvert formål.',
            desc_flows: 'Dataflyter mellom systemer, inkludert interne og grenseoverskridende ruter.',
            desc_cross_border: 'Bare grenseoverskridende strømmer, med overføringsmekanisme og risikovurdering.',
            desc_retention_policies: 'Retningslinjer for oppbevaring, avhendingsmetoder og gjennomgangstråkk.',
            desc_evidence: 'Bevis knyttet til behandlingsaktiviteter: sertifikater, DPA-kontrakter, DPIA rapporter.',
            desc_governance_assignments: 'Styringsoppdrag: eiere, godkjennere og ISO 27001-referanser.',
            unified_heading: 'Eksporter enheter',
            unified_description: 'Eksporter enhver datakartleggingsenhet i CSV, XLSX, PDF, eller DOCX-format. All eksport lagres i filleseren.',
            export_button_label: 'Eksporter',
            csv_download_success: 'CSV lastet ned.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Attestasjonssykluser',
            create: 'Ny syklus',
            name: 'Syklusnavn',
            year: 'År',
            deadline: 'Frist',
            scope: 'Omfang',
            status: 'Status',
            tasks: 'Avdelingsoppgaver',
            assign_tasks: 'Tildel oppgaver',
            department: 'Avdeling',
            assignee: 'Tildelt til',
            progress: 'Fremdrift',
            stale_entries: 'Utdaterte oppføringer',
            detect_stale: 'Finn utdaterte',
            export_bundle: 'Eksporter pakke',
            no_cycles: 'Ingen attestasjonssykluser ennå.',
            create_success: 'Attestasjonssyklus opprettet.',
            assign_success: 'Oppgaver tildelt.',
            complete_success: 'Oppgave fullført.',
            stale_detected: 'Utdaterte oppføringer funnet.',
            stale_systems: 'Utdaterte systemer',
            stale_flows: 'Utdaterte flyter',
            no_stale: 'Ingen utdaterte oppføringer funnet.',
            stale_name: 'Navn',
            stale_announcement: 'Fant {0} utdaterte systemer og {1} utdaterte flyter',
            last_updated: 'Sist oppdatert',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_completed: 'Fullført',
            task_status_pending: 'Venter',
            task_status_in_progress: 'Pågår',
            task_status_completed: 'Fullført',
            scope_systems: 'Systemer',
            scope_flows: 'Datastrømmer',
            scope_activities: 'Aktiviteter',
            scope_categories: 'Kategorier',
            scope_purposes: 'Formål',
            scope_retention: 'Oppbevaring',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Styringstildelinger',
            assign: 'Tildel eierskap',
            entity_type: 'Entitetstype',
            entity: 'Entitet',
            role: 'Rolle',
            assigned_by: 'Tildelt av',
            assigned_at: 'Tildelt den',
            remove: 'Fjern',
            no_assignments: 'Ingen styringstildelinger.',
            assign_success: 'Eierskap tildelt.',
            remove_success: 'Tildeling fjernet.',
            assignee: 'Tilordnet',
            department: 'Avdeling',
            all_departments: 'Alle avdelinger',
            roles: {
                data_owner: 'Dataeier',
                data_steward: 'Dataforvalter',
                data_custodian: 'Dataansvarlig',
                informed: 'Informert',
            },
            entity_types: {
                system: 'System',
                flow: 'Datastrøm',
                activity: 'Aktivitet',
                category: 'Kategori',
                purpose: 'Formål',
                retention_policy: 'Oppbevaringspolicy',
            },
        },
        risk_assessment: {
            title: 'Risikovurdering',
            create: 'Ny vurdering',
            safeguard_type: 'Beskyttelsestype',
            adequacy_status: 'Tilstrekkelighetsstatus',
            risk_factors: 'Risikofaktorer',
            mitigations: 'Tiltak',
            reviewer: 'Anmelder',
            decision: 'Beslutning',
            approve: 'Godkjenn',
            reject: 'Avvis',
            no_assessments: 'Ingen risikovurderinger.',
            create_success: 'Risikovurdering opprettet.',
            approve_success: 'Vurdering godkjent.',
            reject_success: 'Vurdering avvist.',
            one_per_line: 'Én per linje',
            adequacy_adequate: 'Adekvat',
            adequacy_inadequate: 'Utilstrekkelig',
            adequacy_under_review: 'Under gjennomgang',
            adequacy_not_assessed: 'Ikke vurdert',
            decision_approved: 'Godkjent',
            decision_rejected: 'Avvist',
        },
        review: {
            title: 'Gjennomgangsoppgaver',
            description: 'Gjennomgangsoppgaver opprettes automatisk når en datakategori foreslås. For å opprette en ny oppgave, gå til Datakategorier og klikk Foreslå på en utkast-kategori.',
            count: 'gjennomgangsoppgaver',
            summary: 'Gjennomgangsoversikt',
            pending: 'Ventende',
            resolved_today: 'Løst i dag',
            escalated: 'Eskalert',
            overdue: 'Forfalt (>5 dager)',
            all_statuses: 'Alle statuser',
            status_pending: 'Ventende',
            status_resolved: 'Løst',
            status_escalated: 'Eskalert',
            category_name: 'Kategori',
            system_name: 'System',
            owner: 'Eier',
            resolve: 'Løs',
            resolve_task: 'Løs gjennomgangsoppgave',
            resolution: 'Løsning',
            resolution_approve: 'Godkjenn',
            resolution_modify: 'Endre og godkjenn',
            resolution_reject: 'Avvis',
            resolution_escalate: 'Eskaler',
            notes: 'Løsningsnotater',
            notes_required: 'Notater påkrevd (minimum 10 tegn) for Endre, Avvis eller Eskaler',
            resolved_success: 'Gjennomgangsoppgave løst',
            bulk_approve: 'Massegodkjenning',
            bulk_reject: 'Masseavvisning',
            bulk_success: '{count} oppgaver behandlet',
            no_tasks: 'Ingen gjennomgangsoppgaver funnet',
        },
        inventory: {
            title: 'Klassifiseringsinventar',
            count: 'systemer',
            summary: 'Inventaroversikt',
            total_systems: 'Totalt systemer',
            total_labels: 'Totalt etiketter',
            system_name: 'System',
            label_count: 'Etiketter',
            approved_count: 'Godkjent',
            pending_count: 'Ventende',
            no_items: 'Ingen inventarelementer funnet',
        },
        exceptions: {
            title: 'Klassifiseringsunntak',
            count: 'unntak',
            all_severities: 'Alle alvorlighetsgrader',
            all_statuses: 'Alle statuser',
            system_name: 'System',
            severity_label: 'Alvorlighetsgrad',
            status_open: 'Åpen',
            status_resolved: 'Løst',
            status_accepted_risk: 'Akseptert risiko',
            resolve: 'Løs',
            resolve_exception: 'Løs unntak',
            resolution_type: 'Løsningstype',
            notes: 'Notater',
            justification_required: 'Begrunnelse påkrevd (minimum 10 tegn) for akseptert risiko',
            resolved_success: 'Unntak løst',
            no_exceptions: 'Ingen unntak funnet',
        },
        history: {
            title: 'Klassifiseringshistorikk',
            count: 'hendelser',
            actor: 'Aktør',
            action: 'Handling',
            entity_type: 'Entitetstype',
            changes: 'Endringer',
            view_lineage: 'Vis opprinnelse',
            lineage_title: 'Klassifiseringsopprinnelse',
            no_events: 'Ingen historikkhendelser funnet',
            no_lineage: 'Ingen opprinnelsesdata tilgjengelig',
            modified: 'Endret',
            created: 'Opprettet',
            removed: 'Fjernet',
            action_created: 'Opprettet',
            action_updated: 'Oppdatert',
            action_archived: 'Arkivert',
            action_approved: 'Godkjent',
            action_deprecated: 'Utdatert',
            entity_label: 'Etikett',
            entity_taxonomy: 'Taksonomi',
            entity_review_task: 'Gjennomgangsoppgave',
            entity_exception: 'Unntak',
            before: 'Før',
            after: 'Etter',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Revisjonsspor for styring',
        audit_trail_subtitle: 'Kryss-entitets revisjonsstrøm for styringshandlinger — godkjenninger, playbook-utførelser, bevislenker og mer.',
        audit_trail_item_count: 'revisjonshendelser',
        audit_trail_fetch_error: 'Kunne ikke hente revisjonshendelser.',
        audit_trail_no_events: 'Ingen revisjonshendelser funnet for gjeldende filtre.',
        audit_trail_entity_type: 'Entitetstype',
        audit_trail_entity_id: 'Entitets-ID',
        audit_trail_action: 'Handling',
        audit_trail_actor: 'Aktør',
        audit_trail_timestamp: 'Tidsstempel',
        audit_trail_date_from: 'Fra',
        audit_trail_date_to: 'Til',
        audit_trail_no_rows_to_export: 'Ingen rader å eksportere.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Leverandører',
            subprocessors: 'Underdatabehandlere',
            cross_border: 'Grenseoverskridende',
            drift: 'Avviksovervåking',
            scores: 'Samsvarsscore',
            playbooks: 'Rutinebøker',
            approvals: 'Godkjenninger',
            audit_trail: 'Revisjonsspor',
            board_report: 'Styrerapport',
            evidence: 'Dokumentasjon',
            onboarding: 'Onboarding-prosess',
            dpa_reviews: 'DPA-gjennomganger',
            periodic_reviews: 'Periodiske gjennomganger',
            offboarding: 'Avregistrering',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Leverandørregister',
            new: 'Ny leverandør',
            edit: 'Rediger leverandør',
            name: 'Navn',
            jurisdiction: 'Jurisdiksjon',
            dpa_status: 'DPA-status',
            risk_level: 'Risikonivå',
            onboarding_status: 'Introduksjonsstatus',
            contact_email: 'Kontakt-e-post',
            contact_name: 'Kontaktperson',
            contact_phone: 'Kontakttelefon',
            website: 'Nettsted',
            services_csv: 'Tjenester (kommaseparert)',
            country: 'Land',
            country_placeholder: 'f.eks. NO',
            last_reviewed: 'Sist gjennomgått',
            offboard: 'Avslutt',
            offboard_vendor: 'Avslutt leverandør',
            offboard_reason: 'Begrunnelse for avslutning',
            no_vendors: 'Ingen leverandører funnet',
            vendor_count: '{count} leverandør(er)',
            all_dpa_status: 'Alle DPA-statuser',
            all_risk_levels: 'Alle risikonivåer',
            all_onboarding: 'Alle introduksjonsstatuser',
            fetch_error: 'Feilet. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre leverandør',
            offboard_error: 'Kunne ikke avslutte',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Databehandler',
            sub_processor: 'Underdatabehandler',
            controller: 'Behandlingsansvarlig',
            joint_controller: 'Felles Behandlingsansvarlig',
        },
        onboarding_statuses: {
            pending_review: 'Venter på Gjennomgang',
            approved: 'Godkjent',
            active: 'Aktiv',
            suspended: 'Suspendert',
            offboarded: 'Avsluttet',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Ikke Påkrevd',
            required_missing: 'Påkrevd Mangler',
            draft: 'Utkast',
            under_review: 'Under Gjennomgang',
            signed: 'Signert',
            expired: 'Utløpt',
        },
        risk_levels: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Lav',
        },
        approval_statuses: {
            pending: 'Venter',
            approved: 'Godkjent',
            rejected: 'Avvist',
            expired: 'Utløpt',
        },
        approval_action_types: {
            vendor_onboarding: 'Leverandørtilknytning',
            vendor_offboarding: 'Leverandøravslutning',
            cross_border_acceptance: 'Grenseoverskridende Godkjenning',
            playbook_activation: 'Playbook-aktivering',
            drift_accept_risk: 'Avvik Aksepter Risiko',
            activity_activation: 'Aktivitetsaktivering',
        },
        drift_check_types: {
            dpa_expiry: 'DPA-utløp',
            dpa_missing: 'DPA Mangler',
            review_overdue: 'Gjennomgang Forfalt',
            cross_border_gap: 'Grenseoverskridende Mangel',
            sub_processor_chain: 'Underdatabehandlerkjede',
            risk_assessment_overdue: 'Risikovurdering Forfalt',
            retention_policy_missing: 'Oppbevaringspolicy Mangler',
            evidence_expiring: 'Dokumentasjon Utløper',
        },
        drift_statuses: {
            open: 'Åpen',
            acknowledged: 'Bekreftet',
            in_progress: 'Pågår',
            resolved: 'Løst',
            accepted_risk: 'Akseptert Risiko',
        },
        drift_entity_types: {
            vendor: 'Leverandør',
            system: 'System',
            data_flow: 'Dataflyt',
            processing_activity: 'Behandlingsaktivitet',
            evidence_item: 'Dokumentasjon',
        },
        playbook_statuses: {
            draft: 'Utkast',
            active: 'Aktiv',
            archived: 'Arkivert',
        },
        playbook_action_types: {
            delete_data: 'Slett Data',
            verify_deletion: 'Bekreft Sletting',
            notify_vendor: 'Varsle Leverandør',
            confirm_receipt: 'Bekreft Mottak',
            document_evidence: 'Dokumenter',
            other: 'Annet',
        },
        playbook_target_types: {
            system: 'System',
            vendor: 'Leverandør',
        },
        dpa_chain_statuses: {
            complete: 'Komplett',
            incomplete: 'Ufullstendig',
            unknown: 'Ukjent',
        },
        sub_processor_statuses: {
            active: 'Aktiv',
            terminated: 'Avsluttet',
        },
        evidence_types: {
            dpa_agreement: 'DPA-avtale',
            risk_assessment: 'Risikovurdering',
            audit_report: 'Revisjonsrapport',
            certification: 'Sertifisering',
            deletion_confirmation: 'Slettingsbekreftelse',
            training_record: 'Opplæringsjournal',
            incident_report: 'Hendelsesrapport',
            document: 'Dokument',
            screenshot: 'Skjermbilde',
            policy: 'Retningslinje',
            contract: 'Kontrakt',
            assessment: 'Vurdering',
            correspondence: 'Korrespondanse',
            other: 'Annet',
        },
        linkable_entity_types: {
            vendor: 'Leverandør',
            transfer_mechanism: 'Overføringsmekanisme',
            playbook: 'Spillebok',
            execution: 'Utførelse',
            system: 'System',
            activity: 'Aktivitet',
        },
        subprocessors: {
            title: 'Underdatabehandlerforhold',
            new: 'Ny kobling',
            vendor: 'Leverandør',
            subprocessor: 'Underdatabehandler',
            relationship_type: 'Forholdets type',
            countries: 'Land',
            countries_csv: 'Land (kommaseparert)',
            dpa_chain: 'DPA-kjede',
            dpa_chain_status: 'DPA-kjedestatus',
            no_processors: 'Ingen databehandlere funnet.',
            no_subprocessors: 'Ingen underdatabehandlere funnet.',
            processor_vendor: 'Databehandlerleverandør',
            select_processor: 'Velg databehandler',
            select_vendor: 'Velg leverandør',
            services: 'Tjenester',
            sub_processor_vendor_id: 'Underdatabehandler leverandør-ID',
            terminate: 'Avslutt',
            confirm_terminate: 'Er du sikker på at du vil avslutte dette forholdet?',
            create_error: 'Kunne ikke opprette kobling.',
            fetch_error: 'Kunne ikke hente underdatabehandlere.',
            processor: 'Databehandler',
            services_csv: 'Tjenester (kommaseparert)',
            terminate_error: 'Kunne ikke avslutte.',
            version_history_heading: 'Sub-prosessor endre versjonering',
            change_tracking_note: 'Alle endringer i forholdet til underbehandleren er versjonert. Hver modifikasjon registrerer før/etter-tilstanden for revisjonsspor.',
            history_retention_note: 'Endringshistorikk beholdes i hele dataoppbevaringsperioden. Gjennomgå endringslogger via leverandørens detaljvisning.',
            vendor_risk_cross_view_heading: 'Underleverandørregister (Vendor Risk)',
            vendor_risk_cross_view_source: 'Kilde: Vendor Risk-register',
            vendor_risk_cross_view_empty: 'Ingen oppføringer i Vendor Risk-registeret.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Grenseoverskridende styringsrapport',
            flows: 'Grenseoverskridende flyter',
            transfers: 'Grenseoverskridende overføringer',
            detected_gaps: 'Oppdagede mangler',
            safeguards: 'Sikkerhetstiltak',
            cross_border_vendors: 'Grenseoverskridende leverandører',
            destination: 'Destinasjon',
            no_cross_border_vendors: 'Ingen grenseoverskridende leverandører funnet.',
            no_safeguard: 'Ingen sikkerhetstiltak',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Behandlingslokasjoner',
            export_error: 'Kunne ikke eksportere rapport.',
            export_pdf: 'Eksporter PDF',
            export_success: 'Rapport eksportert.',
            exporting: 'Eksporterer...',
            fetch_error: 'Kunne ikke hente grenseoverskridende data.',
            source: 'Kilde',
            pdpl_p2_heading: 'PDPL-P2 Overholdelse av grenseoverskridende overføringer',
            dossier_prep_note: 'Utarbeidelse av dossier: Sett sammen konsekvensanalyse, juridisk grunnlag og ivareta dokumentasjon før innlevering.',
            filing_tracking_note: 'Filsporing: overvåk innsendingsstatus hos myndigheten (PDPC) fra første søknad til godkjenning.',
            supplement_loop_note: 'Tilleggssløyfe: svar på forespørsler om myndighetsinformasjon og send inn endrede dossierer på nytt etter behov.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Leverandørsøk',
            vendor_search_placeholder: 'Søk etter leverandørnavn…',
            jurisdiction_filter_label: 'Jurisdiksjon',
            filings_section_heading: 'Innleveringer på tvers av landegrensene',
            filings_empty: 'Ingen innleveringer ennå. Opprett en for å starte et dossier.',
            filings_create_button: 'Ny arkivering',
            filings_table_id: 'Innleverings-ID',
            filings_table_jurisdiction: 'Jurisdiksjon',
            filings_table_type: 'Skjematype',
            filings_table_status: 'Status',
            filings_table_updated: 'Oppdatert',
            filings_table_open: 'Åpne',
            filings_detail_title: 'Grenseoverskridende arkivering',
            filings_back_to_list: 'Tilbake til Cross-Border',
            filings_status_heading: 'Livssyklus',
            filings_evidence_heading: 'Bevis',
            filings_history_heading: 'Revisjonsspor',
            filings_field_filing_id: 'Innleverings-ID',
            filings_field_jurisdiction: 'Jurisdiksjon',
            filings_field_type: 'Skjematype',
            filings_field_status: 'Status',
            filings_field_reference: 'Myndighetsreferanse',
            filings_field_mps_receipt: 'MPS kvittering',
            filings_field_submitted_at: 'Innlevert kl',
            filings_field_acknowledged_at: 'Bekreftet kl',
            filings_field_archived_at: 'Arkivert på',
            filings_field_version: 'Versjon',
            filings_field_notes: 'Notater',
            filings_action_advance: 'Forhåndsstatus',
            filings_action_reject: 'Avvis',
            filings_action_archive: 'Arkiv',
            filings_action_submit: 'Merket er sendt inn',
            filings_action_acknowledge: 'Mark kvitterte',
            filings_action_resume_draft: 'Fortsett som utkast',
            filings_status_draft: 'Utkast',
            filings_status_assessed: 'Vurdert',
            filings_status_pending_review: 'Venter på vurdering',
            filings_status_ready_to_file: 'Klar til å arkivere',
            filings_status_submitted: 'Sendt inn',
            filings_status_acknowledged: 'Erkjent',
            filings_status_rejected: 'Avvist',
            filings_status_archived: 'Arkivert',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Ny grenseoverskridende arkivering',
            filings_new_form_jurisdiction: 'Jurisdiksjon',
            filings_new_form_type: 'Skjematype',
            filings_new_form_notes: 'Merknader (valgfritt)',
            filings_new_submit: 'Opprett',
            filings_new_cancel: 'Avbryt',
            filings_reason_label: 'Grunn',
            filings_reference_label: 'Myndighetsreferansenummer',
            filings_mps_receipt_label: 'MPS kvitteringsreferanse',
            filings_create_error: 'Kunne ikke opprette arkivering.',
            filings_create_success: 'Arkiv opprettet.',
            filings_update_error: 'Kunne ikke oppdatere arkiveringen.',
            filings_update_success: 'Arkiv oppdatert.',
            filings_fetch_error: 'Kunne ikke hente arkiver.',
            filings_archive_confirm: 'Vil du arkivere denne arkiveringen? Dette kan ikke angres.',
            filings_status_rejected_detour: 'Innlevering ble avvist etter gjennomgang. Fortsett som utkast for å starte flyten på nytt.',
            recent_exports_heading: 'Nylig eksport',
            recent_exports_empty: 'Ingen eksport ennå. Klikk "Eksporter PDF" for å generere en.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Konfigurasjonsavviksovervåking',
            drift_type: 'Avvikstype',
            severity: 'Alvorlighetsgrad',
            detected_at: 'Oppdaget',
            resolve: 'Løs',
            all_check_types: 'Alle sjekktyper',
            all_entity_types: 'Alle enhetstyper',
            event_count: 'Antall hendelser',
            accept_risk: 'Aksepter risiko',
            acknowledge: 'Bekreft',
            assign_error: 'Kunne ikke tildele.',
            assign_event: 'Tildel',
            assigned: 'Tildelt',
            enter_resolution_notes: 'Skriv inn løsningsnotater...',
            enter_user_id: 'Skriv inn bruker-ID...',
            entity: 'Enhet',
            fetch_error: 'Kunne ikke hente avvikshendelser.',
            no_events: 'Ingen avvikshendelser funnet.',
            resolution_notes: 'Løsningsnotater',
            update_status_error: 'Kunne ikke oppdatere status.',
        },
        scores: {
            title: 'Samsvarsscore-kontrollpanel',
            overall: 'Samlet samsvarsscore',
            breakdown: 'Scoreoversikt per modul',
            trend: 'Historisk trend',
            module: 'Modul',
            score: 'Poeng',
            grade: 'Karakter',
            calculated_at: 'Beregnet',
            generate_board_report: 'Generer styrerapport',
            lang_english: 'Engelsk',
            lang_vietnamese: 'Vietnamesisk',
            fetch_error: 'Kunne ikke hente samsvarsscorer.',
            generate_report: 'Generer rapport',
            generating: 'Genererer...',
            recommendations: 'Anbefalinger',
            report_error: 'Kunne ikke generere rapport.',
            factor_owner_assigned: 'Eier tildelt',
            factor_data_categories_linked: 'Datakategorier koblet',
            factor_purposes_linked: 'Formål koblet',
            factor_data_flows_documented: 'Dataflyter dokumentert',
            factor_processing_activity_linked: 'Behandlingsaktivitet koblet',
            factor_dpa_status: 'DPA-status',
            factor_retention_policy_linked: 'Oppbevaringspolicy koblet',
            factor_last_reviewed: 'Sist gjennomgått innen terskel',
            factor_risk_assessment_current: 'Risikovurdering oppdatert',
            factor_sub_processors_documented: 'Underdatabehandlere dokumentert',
            factor_cross_border_safeguards: 'Grenseoverskridende sikkerhetstiltak',
            factor_contact_info_complete: 'Kontaktinformasjon fullstendig',
            factor_onboarding_approved: 'Onboarding godkjent',
            factor_avg_system_score: 'Gjennomsnittlig systempoeng',
            factor_avg_vendor_score: 'Gjennomsnittlig leverandørpoeng',
            factor_processing_activity_coverage: 'Dekning av behandlingsaktiviteter',
            factor_ropa_completeness: 'RoPA-fullstendighet',
            factor_open_drift_events: 'Åpne avvikshendelser',
        },
        playbooks: {
            title: 'Sletterutinebøker',
            new: 'Ny rutinebok',
            edit: 'Rediger rutinebok',
            steps: 'Trinn',
            add_step: 'Legg til trinn',
            remove_step: 'Fjern trinn',
            data_categories_csv: 'Datakategorier (kommaseparert)',
            estimated_duration: 'Estimert varighet',
            estimated_duration_label: 'Estimert varighet',
            evidence_description: 'Dokumentasjonsbeskrivelse',
            no_playbooks: 'Ingen rutinebøker funnet.',
            playbook_count: 'Antall rutinebøker',
            requires_evidence: 'Krever dokumentasjon',
            step_label: 'Trinnetikett',
            target_name: 'Målnavn',
            target_type: 'Måltype',
            action_type: 'Handlingstype',
            archive_error: 'Kunne ikke arkivere.',
            confirm_archive: 'Er du sikker på at du vil arkivere denne rutineboken?',
            data_categories: 'Datakategorier',
            days: 'dager',
            fetch_error: 'Kunne ikke hente rutinebøker.',
            sla_days: 'SLA (dager)',
            target_id: 'Mål-ID',
            template_class_heading: 'Malklassifisering',
            official: 'Offisiell',
            internal: 'Intern',
            classification: 'Klassifisering',
            official_note: 'Offisielle: forskriftspålagte maler tilpasset PDPL, GDPR, eller sektorspesifikke krav.',
            internal_note: 'Internt: organisasjonsspesifikke prosedyrer som ikke er avledet fra regulatoriske maler.',
            active_executions_heading: 'Aktive kjøringer',
            no_active_executions: 'Ingen aktive kjøringer.',
            playbook_label: 'Playbook',
            ticket_id_label: 'Sak',
            progress_label: 'Fremdrift',
            actions_label: 'Handlinger',
            view_steps_label: 'Vis trinn',
            execution_detail_title: 'Detaljer for kjøring',
            playbook_missing: 'Playbook ikke funnet.',
            evidence_required_label: 'Bevis påkrevd',
            upload_evidence_label: 'Last opp bevis',
            mark_complete_label: 'Marker som fullført',
            evidence_required_toast: 'Bevis må legges ved før trinnet kan fullføres.',
            evidence_blocking_message: 'Legg ved en bevisfil for å fortsette.',
            step_completed_toast: 'Trinn fullført.',
            executions_fetch_error: 'Kunne ikke hente kjøringer.',
            step_evidence_default_name: 'Bevis for kjøringstrinn',
            attached_evidence_label: 'Vedlagte bevis',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Godkjenningsarbeidsflyter',
            new: 'Ny godkjenning',
            edit: 'Rediger godkjenning',
            request_type: 'Forespørselstype',
            requester: 'Innmelder',
            approve: 'Godkjenn',
            reject: 'Avvis',
            justification: 'Begrunnelse',
            all_action_types: 'Alle handlingstyper',
            decided_by: 'Besluttet av',
            no_approvals: 'Ingen godkjenninger funnet.',
            approval_count: 'Antall godkjenninger',
            approval_notes_optional: 'Notater (valgfritt)',
            approve_error: 'Kunne ikke godkjenne.',
            approve_request: 'Godkjenn forespørsel',
            decision: 'Beslutning',
            entity: 'Enhet',
            fetch_error: 'Kunne ikke hente godkjenninger.',
            reject_error: 'Kunne ikke avvise.',
            reject_request: 'Avvis forespørsel',
            rejection_notes_required: 'Avvisningsnotater påkrevd',
            rejection_notes_required_msg: 'Vennligst oppgi en begrunnelse for avvisningen.',
            requested_at: 'Forespurt',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Støttebevis (valgfritt)',
            decision_evidence_name: 'Beslutningsbevis',
        },
        evidence: {
            title: 'Styringsdokumentasjon',
            new: 'Ny dokumentasjon',
            link_to: 'Knytt til',
            entity_id_placeholder: 'Enhets-ID',
            file_path_placeholder: '/sti/til/fil',
            evidence_label: 'Dokumentasjonsetikett',
            file_path: 'Filsti',
            source_mode_label: 'Beviskilde',
            source_mode_file: 'Last opp fil',
            source_mode_url: 'Lenk ekstern URL',
            attachment_label: 'Vedlegg',
            item_count: 'Antall elementer',
            link_button: 'Knytt dokumentasjon',
            confirm_delete: 'Er du sikker på at du vil slette denne dokumentasjonen?',
            enter_entity_id: 'Skriv inn enhets-ID...',
            entity_id: 'Enhets-ID',
            entity_type: 'Enhetstype',
            expires: 'Utløper',
            expires_at: 'Utløper',
            fetch_error: 'Kunne ikke hente dokumentasjon.',
            link_error: 'Kunne ikke knytte dokumentasjon.',
            no_evidence: 'Ingen dokumentasjon funnet.',
            tags_csv: 'Merkelapper (kommaseparert)',
            uploaded: 'Opplastet',
            url: 'URL',
            framework_tag_heading: 'Rammemerket bevis',
            framework_tag_note: 'Hver bevispost fanger opp rammekode, skuespiller og tidsstempel. Filtrer etter rammeverk for å isolere revisjonsrelevante poster.',
            framework_code: 'Rammekode',
            filter_by_framework: 'Filtrer etter rammekode...',
            actor: 'Skuespiller',
        },
        certification: {
            not_applicable: 'Ikke aktuelt',
            not_certified: 'Ikke sertifisert',
            pending: 'Venter',
            certified: 'Sertifisert',
            revoked: 'Tilbakekalt',
        },
        onboarding_pipeline: {
            title: 'Onboarding-prosess',
            new_workflow: 'Ny onboarding-arbeidsflyt',
            vendor_id: 'Leverandør-ID',
            stages: 'Faser',
            current_stage: 'Nåværende fase',
            advance: 'Fremryk fase',
            advance_success: 'Fase fremrykket',
            workflow_status: 'Arbeidsflytstatus',
            initiated: 'Igangsatt',
            in_progress: 'Pågår',
            completed: 'Fullført',
            cancelled: 'Avbrutt',
            no_workflows: 'Ingen onboarding-arbeidsflyter funnet',
            stages_placeholder: 'Steg 1, Steg 2, Steg 3',
            assigned_to: 'Tildelt til',
            notes: 'Notater',
            pdpl_lifecycle_heading: 'PDPL Leverandørlivssyklus — Onboarding',
            due_diligence_note: 'Due diligence-stadiet er obligatorisk før innføring av en prosessor under PDPL. Verifiser databehandlingsomfang, juridisk grunnlag og sikkerhetstiltak.',
            contract_review_note: 'Kontraktsgjennomgang må bekrefte DPA-klausuler, datalagringsgrenser og underbehandlerbegrensninger.',
            create_dpa_review_cta: 'Opprett DPA-gjennomgang',
            dpa_review_linked: 'DPA-gjennomgang lenket',
        },
        dpa_review: {
            title: 'DPA-gjennomganger',
            new_review: 'Ny DPA-gjennomgang',
            review_type: 'Gjennomgangstype',
            findings: 'Funn',
            outcome: 'Utfall',
            complete_review: 'Fullfør gjennomgang',
            complete_success: 'Gjennomgang fullført',
            link_assessment: 'Koble sikkerhetsvurdering',
            link_success: 'Vurdering koblet',
            assessment_id: 'Vurderings-ID',
            combined_title: 'Kombinert vurdering',
            no_reviews: 'Ingen DPA-gjennomganger funnet',
            telecom_disclosure_heading: 'Telecom Disclosure Governance',
            disclosure_trigger_note: 'Avsløringsutløste anmeldelser fanger opp datadelingshendelser for teleabonnenter i henhold til VN Telecom Law.',
            telecom_t2_note: 'TELECOM-T2 nivågodkjenning kreves før abonnentdata avsløres til myndighetene.',
            quarterly_log_note: 'Bruk den kvartalsvise loggen til å dokumentere alle avsløringshendelser og opprettholde et kontinuerlig revisjonsspor.',
        },
        periodic_review: {
            title: 'Periodiske gjennomganger',
            new_review: 'Ny periodisk gjennomgang',
            review_type: 'Gjennomgangstype',
            due_date: 'Forfallsdato',
            next_review: 'Neste gjennomgangsdato',
            trigger_reassessment: 'Utløs revurdering',
            reassessment_reason: 'Årsak til revurdering',
            reassessment_success: 'Revurdering utløst',
            complete_success: 'Gjennomgang fullført',
            scheduled: 'Planlagt',
            in_progress: 'Pågår',
            completed: 'Fullført',
            overdue: 'Forfalt',
            material_change: 'Vesentlig endring',
            no_reviews: 'Ingen periodiske gjennomganger funnet',
            mark_complete: 'Merk som fullført',
            pdpl_periodic_heading: 'PDPL Leverandørlivssyklus – Periodiske vurderinger',
            scheduled_review_note: 'Periodiske gjennomganger må planlegges minst årlig for alle aktive prosessorer. Forsinkede anmeldelser utløser overholdelsesvarsler.',
            material_change_note: 'Vesentlige endringer i databehandling krever umiddelbar revurdering uavhengig av tidsplan.',
            overdue_badge: 'Forfalt',
            overdue_aria: 'Forfalt gjennomgang',
            only_overdue_filter: 'Kun forfalte',
        },
        offboarding: {
            title: 'Leverandør-avregistrering',
            initiate: 'Start avregistrering',
            initiate_success: 'Avregistrering startet',
            reason: 'Årsak til avregistrering',
            data_return_required: 'Datareturnering påkrevd',
            verify_data: 'Bekreft datareturnering',
            verify_success: 'Datareturnering bekreftet',
            sign_off: 'Godkjenn',
            sign_off_success: 'Avregistrering godkjent',
            status_initiated: 'Igangsatt',
            status_data_pending: 'Venter på datareturnering',
            status_data_verified: 'Datareturnering bekreftet',
            status_completed: 'Fullført',
            status_cancelled: 'Avbrutt',
            task_id: 'Oppgave-ID',
            no_tasks: 'Ingen avregistreringsoppgaver funnet',
            evidence_id: 'Dokumentasjons-ID',
            pdpl_offboarding_heading: 'PDPL Leverandørlivssyklus – avstigning og sletting',
            deletion_capture_note: 'Offboarding må fange opp bekreftelse på sletting av data fra leverandøren. Bekreft dataretur før avlogging.',
            evidence_attachment_note: 'Legg ved slettebevis (sertifikater, skjermbilder, leverandørbekreftelser) for å fullføre offboarding-posten.',
            verify_mode_label: 'Verifiseringsmetode',
            verify_mode_upload: 'Last opp ny fil',
            verify_mode_link: 'Lenk eksisterende bevis',
            verify_mode_none: 'Ingen data å returnere',
            upload_data_return_evidence: 'Last opp datareturbevis',
            no_data_justification_label: 'Begrunnelse',
            no_data_justification_placeholder: 'Forklar hvorfor ingen data skal returneres',
            no_data_justification_hint: 'Minst 20 tegn',
            upload_required_toast: 'Legg ved en fil for å fortsette',
            evidence_link_required_toast: 'Velg et bevis for å fortsette',
            no_data_justification_required_toast: 'Oppgi en begrunnelse på minst 20 tegn',
            data_return_evidence_name: 'Datareturbevis',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Innledende',
            annual: 'Årlig',
            renewal: 'Fornyelse',
            incident_triggered: 'Hendelsesutløst',
        },
        periodic_review_types: {
            initial: 'Innledende',
            annual: 'Årlig',
            renewal: 'Fornyelse',
            incident_triggered: 'Hendelsesutløst',
            material_change: 'Vesentlig endring',
            disclosure_triggered: 'Opplysningsutløst',
            quarterly_log: 'Kvartalslogg',
        },
        dpa_review_statuses: {
            pending: 'Venter',
            in_progress: 'Pågår',
            completed: 'Fullført',
            cancelled: 'Avbrutt',
        },
        onboarding_stage_names: {
            due_diligence: 'Due diligence',
            contract_review: 'Kontraktsgjennomgang',
            security_assessment: 'Sikkerhetsvurdering',
            data_mapping: 'Datakartlegging',
            approval: 'Godkjenning',
            integration: 'Integrasjon',
        },
        access_denied_title: 'Tilgang nektet',
        access_denied: 'Du har ikke tillatelse til å se denne siden. Kontakt administratoren din.',
    },
    consent: {
        nav: {
            policies: 'Retningslinjer',
            purposes: 'Formålskatalog',
            workflows: 'Arbeidsflyter',
            snapshots: 'CMP-øyeblikksbilder',
            change_awareness: 'Endringsbevissthet',
            audit: 'Samtykkerevisjonslogg',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Gap analyse',
            page_title: 'Analyse av samtykkegap',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Rammer',
            org_flags_label: 'Organisasjonskontekst',
            flag_withdrawal: 'Har dokumentert global uttaksmekanisme',
            flag_parental: 'Har foreldresamtykke for personer under 16 år',
            flag_cross_border: 'Har grenseoverskridende arkivering registrert',
            run_label: 'Kjør gapanalyse',
            running_label: 'Kjører …',
            requires_advisor_label: 'Krever ai_advisor:chattetillatelse.',
            results_heading: 'Det dukket opp hull ({count})',
            tokens_label: '≈ {count} tokens',
            empty_label: 'Ingen hull oppdaget for de valgte rammene.',
            remediation_label: 'Foreslått utbedring:',
            dismiss_label: 'Avvis som ikke aktuelt',
            resolve_label: 'Løs',
            dismissed_label: 'Avvist som ikke aktuelt',
            resolved_label: 'Løst',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Importer samtykkearbeidsflyter',
            nav_title: 'Masseimport',
            description: 'Last opp en CSV-fil for å masseimportere samtykkearbeidsflyter.',
            select_file: 'Velg CSV-fil',
            file_help: 'CSV med overskrifter som samsvarer med arbeidsflytfelt.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Obligatoriske kolonner: Arbeidsflyttype, Registrerte, Kanal.',
            file_too_large: 'Filen er for stor. Maksimal størrelse er 2MB.',
            preview: 'Forhåndsvisning',
            columns_detected: 'Kolonner oppdaget: {count}',
            column_mapping: 'Kolonnetilordning',
            import_button: 'Importer',
            import_complete: 'Import fullført',
            import_error: 'Import mislyktes',
            result: 'Importresultat',
            total: 'Totalt',
            imported: 'Importert',
            skipped: 'Hoppet over',
            failed: 'Feilet',
            errors: 'Feil',
            error_row: 'Rad',
            error_field: 'Felt',
            error_message: 'Melding',
            history: 'Importhistorikk',
            no_history: 'Ingen importhistorikk.',
            history_file: 'Filnavn',
            history_date: 'Dato',
            create_profiles_label: 'Opprett Registrerte-profiler for nye subjekter',
            create_profiles_help: 'Import uten en tilhørende profil oppretter en automatisk. Fjern merket for kun å importere arbeidsflyter.',
            profiles_created: 'Profiler opprettet',
            profiles_matched: 'Profiler matchet',
            subject_kind_help: 'Valgfrie kolonner: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Ugyldig e-post i subject_email-kolonnen.',
            error_subject_unknown_kind: 'Ukjent subject_kind-verdi. Forventet: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key er påkrevd når subject_kind=external_ref',
        },
        policies: {
            title: 'Samtykkeretningslinjer',
            new: 'Ny retningslinje',
            edit: 'Rediger retningslinje',
            name: 'Navn',
            version: 'Versjon',
            policy_text: 'Retningslinjetekst',
            publish: 'Publiser',
            versions: 'Versjoner',
            hide_versions: 'Skjul versjoner',
            submit_for_review: 'Send til gjennomgang',
            suspend: 'Suspender',
            retire: 'Trekk tilbake',
            jurisdictions: 'Jurisdiksjoner',
            jurisdictions_csv: 'Jurisdiksjoner (kommaseparert)',
            approval_mode: 'Godkjenningsmodus',
            reviewer_ids: 'Gjennomganger-ID-er (kommaseparert)',
            tags_csv: 'Merkelapper (kommaseparert)',
            effective_date: 'Ikrafttredelsesdato',
            review_date: 'Gjennomgangsdato',
            consent_model_statement: 'Samtykkemodelluttalelse',
            consent_model_placeholder: 'Valgfri samtykkemodelluttalelse...',
            default_settings: 'Standardinnstillinger (JSON)',
            changes: 'Endringer',
            fetch_error: 'Kunne ikke hente samtykkeretningslinjer. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre samtykkeretningslinje. Vennligst prøv igjen.',
            submit_review_error: 'Kunne ikke sende retningslinje til gjennomgang. Vennligst prøv igjen.',
            activate_error: 'Kunne ikke aktivere retningslinje. Vennligst prøv igjen.',
            suspend_error: 'Kunne ikke suspendere retningslinje. Vennligst prøv igjen.',
            retire_error: 'Kunne ikke trekke tilbake retningslinje. Vennligst prøv igjen.',
            submit_review_success: 'Retningslinjer sendt inn for vurdering.',
            activate_success: 'Policy aktivert.',
            suspend_success: 'Politikken er suspendert.',
            retire_success: 'Politikken pensjonert.',
            versions_error: 'Kunne ikke hente retningslinjeversioner. Vennligst prøv igjen.',
            invalid_json: 'Ugyldig JSON i Standardinnstillinger-feltet. Vennligst prøv igjen.',
            page_title: 'Retningslinjer for samtykke',
            version_updated: 'Oppdatert',
            version_initial: 'Opprinnelig versjon',
            json_format: 'JSON format',
            sensitive: 'Sensitive',
            flag_sensitive: 'Flagsensitiv',
            flag_sensitive_title: 'Flagg politikk som sensitiv',
            require_enhanced_review: 'Krev forbedret gjennomgang',
            enhanced_review_success: 'Krav til forbedret gjennomgang er aktivert.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Velg gjeldende PDPL sensitive datakategorier i artikkel 2:',
            no_policies: 'Ingen samtykkepolicyer funnet. Opprett din første policy for å komme i gang.',
            approval_success: 'Policy godkjent.',
            reviewers_label: 'Anmeldere',
            enhanced_review_title: 'Utvidet gjennomgang påkrevd',
            enhanced_review_badge: 'Utvidet gjennomgang påkrevd',
            enhanced_review_status_pending: 'Gjennomgang venter',
            enhanced_review_status_approved: 'Gjennomgang godkjent',
            enhanced_review_status_rejected: 'Gjennomgang avvist',
            enhanced_review_reviewed_by: 'Gjennomgått av',
            enhanced_review_notes: 'Gjennomgangsnotater',
            enhanced_review_approve: 'Godkjenn gjennomgang',
            enhanced_review_reject: 'Avvis gjennomgang',
            enhanced_review_notes_required: 'Gjennomgangsnotater er påkrevd',
            enhanced_review_approved: 'Utvidet gjennomgang godkjent',
            enhanced_review_rejected: 'Utvidet gjennomgang avvist — policy tilbakestilt til utkast',
            enhanced_review_reset_to_draft: 'Utvidet gjennomgang avvist — policy tilbakestilt til utkast',
            enhanced_review_reject_warning: 'Avvisning vil tilbakestille denne policyen til Utkast-status. DPO må sende inn på nytt for gjennomgang etter å ha løst problemene.',
            submitted_modal_title: 'Sendt inn for utvidet gjennomgang',
            submitted_modal_body: 'Denne policyen er sendt inn. En Juridisk/Personvernrevisor må godkjenne den før den kan aktiveres.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Helsedata',
            biometric: 'Biometriske data',
            political: 'Politiske meninger',
            ethnic: 'Etnisk opprinnelse',
            genetic: 'Genetiske data',
            criminal: 'Strafferegistre',
        },
        purposes: {
            title: 'Formålskatalog',
            new: 'Nytt formål',
            edit: 'Rediger formål',
            name: 'Navn',
            legal_basis: 'Rettslig grunnlag',
            retention_days: 'Oppbevaringsperiode (dager)',
            is_active: 'Aktiv',
            filter_by_category: 'Filtrer etter kategori',
            policy_ids: 'Retningslinje-ID-er (kommaseparert)',
            ropa_activity_ids: 'ROPA-aktivitets-ID-er (kommaseparert)',
            policies: 'Retningslinjer',
            deprecate: 'Avskriv',
            fetch_error: 'Kunne ikke hente samtykkeformål. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre samtykkeformål. Vennligst prøv igjen.',
            deprecate_error: 'Kunne ikke avskrive samtykkeformål. Vennligst prøv igjen.',
            page_title: 'Formålskatalog',
            no_purposes: 'Ingen samtykkeformål funnet. Opprett ditt første formål for å komme i gang.',
            category_personal_data: 'Personopplysninger',
            category_sensitive_data: 'Sensitive data',
            category_health_data: 'Helsedata',
            category_financial_data: 'Finansielle data',
            category_biometric_data: 'Biometriske data',
            category_children_data: 'Barnas data',
            category_location_data: 'Stedsdata',
            category_communications_data: 'Kommunikasjonsdata',
            category_other: 'Annet',
        },
        workflows: {
            title: 'Samtykkearbeidsflyter',
            new: 'Ny arbeidsflyt',
            edit: 'Rediger arbeidsflyt',
            workflow_type: 'Arbeidsflyttype',
            user_id: 'Bruker-ID',
            purpose_id: 'Formåls-ID',
            purpose_ids_csv: 'Formåls-ID (kommaseparert)',
            reason: 'Begrunnelse',
            process: 'Behandle',
            data_subject_ref: 'Referanse til registrert',
            data_subject: 'Registrert',
            channel: 'Kanal',
            policy_id: 'Retningslinje-ID',
            scope: 'Omfang',
            legal_grounds: 'Rettslig grunnlag',
            assessment: 'Vurdering',
            select_outcome: 'Velg utfall',
            partially_approved: 'Delvis godkjent',
            denied: 'Avslått',
            outcome_upheld: 'Opprettholdt',
            outcome_pending: 'Venter',
            outcome_denied: 'Avslått',
            status_count: 'Statusantall',
            channel_filter: 'Filtrer etter kanal',
            add_action: '+ Handling',
            by_type: 'Etter type',
            by_status: 'Etter status',
            downstream_actions: 'Oppfølgingshandlinger',
            downstream_actions_for: 'Oppfølgingshandlinger for {subject} ({type})',
            add_downstream_action: 'Legg til oppfølgingshandling',
            assigned_to: 'Tildelt til',
            fetch_error: 'Kunne ikke hente samtykkearbeidsflyter. Vennligst prøv igjen.',
            dashboard_error: 'Kunne ikke hente arbeidsflytkontrollpanel. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre samtykkearbeidsflyt. Vennligst prøv igjen.',
            status_update_error: 'Kunne ikke oppdatere arbeidsflytstatus. Vennligst prøv igjen.',
            assessment_error: 'Kunne ikke oppdatere arbeidsflytvurdering. Vennligst prøv igjen.',
            add_action_error: 'Kunne ikke legge til arbeidsflythandling. Vennligst prøv igjen.',
            update_action_error: 'Kunne ikke oppdatere arbeidsflythandling. Vennligst prøv igjen.',
            type_refusal: 'Avvisning',
            type_withdrawal: 'Tilbaketrekking',
            type_restriction: 'Begrensning',
            type_objection: 'Innsigelse',
            status_received: 'Mottatt',
            status_processing: 'Under behandling',
            status_under_assessment: 'Under vurdering',
            status_implemented: 'Implementert',
            evidence_button: 'Bevis',
            evidence_modal_title: 'Arbeidsflytbevis',
            no_evidence: 'Ingen bevis knyttet til denne arbeidsflyten.',
            upload_evidence_label: 'Last opp arbeidsflytbevis',
            completed_at: 'Fullført kl',
            effective_date: 'Ikrafttredelsesdato',
            page_title: 'Samtykke arbeidsflyter',
            no_workflows: 'Ingen samtykkearbeidsflyter funnet. Arbeidsflyter opprettes når registrerte utøver sine rettigheter.',
            type_grant: 'Tildeling',
            propagation_none: 'Ingen',
            propagation_pending: 'Venter',
            propagation_pending_warning: 'Kan ikke fullføre — tilbaketrekkingspropagering er fortsatt ventende',
            propagation_completed: 'Fullført',
            propagation_partial: 'Delvis',
            propagation_incomplete_error: 'Kan ikke fullføre arbeidsflyt mens tilbaketrekkingspropagering fortsatt venter',
            propagation_status: 'Propageringsstatus',
        },
        snapshots: {
            title: 'CMP-øyeblikksbilder',
            new: 'Nytt øyeblikksbilde',
            label: 'Etikett',
            snapshot_date: 'Dato for øyeblikksbilde',
            purposes_count: 'Antall formål',
            compare: 'Sammenlign',
            snapshot_data: 'Øyeblikksbildedata (JSON)',
            select_left: 'Velg venstre øyeblikksbilde',
            select_right: 'Velg høyre øyeblikksbilde',
            comparison_results: 'Sammenligningsresultater',
            field: 'Felt',
            left: 'Venstre',
            right: 'Høyre',
            close_comparison: 'Lukk sammenligning',
            trigger: 'Utløser',
            trigger_manual: 'Manuell',
            trigger_policy_approval: 'Retningslinjegodkjenning',
            cmp_version: 'CMP-versjon',
            policy_version_id: 'Retningslinjeversjon-ID',
            banner_config: 'Bannerkonfigurasjon (JSON)',
            active_purposes: 'Aktive formål (JSON)',
            active_legal_bases: 'Aktive rettslige grunnlag (JSON)',
            domain_scope: 'Domeneomfang',
            domain_scope_csv: 'Domeneomfang (kommaseparert)',
            raw_config: 'Råkonfigurasjon (JSON)',
            checksum: 'Sjekksum',
            created: 'Opprettet',
            fetch_error: 'Kunne ikke hente CMP-øyeblikksbilder. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre CMP-øyeblikksbilde. Vennligst prøv igjen.',
            compare_error: 'Kunne ikke sammenligne øyeblikksbilder. Vennligst prøv igjen.',
            page_title: 'Øyeblikksbilder av samtykke',
            json_format: 'JSON format',
            json_array_format: 'JSON matriseformat',
            manual_snapshot_note: 'Kun for administratorer/utviklere. I normal drift opprettes øyeblikksbilder automatisk via CMP-webhook eller ved policygodkjenning. Lim inn JSON eksportert fra din CMP-leverandør (f.eks. OneTrust, Cookiebot).',
            banner_config_hint: 'Lim inn bannerkonfigurasjonsobjektet fra din CMP-eksport.',
            active_purposes_hint: 'JSON-array av aktive formålsobjekter kopiert fra din CMP-eksport.',
            active_legal_bases_hint: 'JSON-array av aktive rettsgrunnlag-objekter kopiert fra din CMP-eksport.',
            raw_config_hint: 'Fullstendig rå konfigurasjon-JSON fra din CMP. Andre JSON-felt hentes herfra. En SHA-256-kontrollsum beregnes automatisk.',
            changed_field: 'Endret felt: ',
            no_snapshots: 'Ingen CMP-øyeblikksbilder funnet. Øyeblikksbilder opprettes når policyer godkjennes eller utløses manuelt.',
            detail_title: 'Øyeblikksbildedetalj',
            view_snapshot_detail: 'Vis øyeblikksbildedetalj',
            policy_version: 'Policyversjon',
            removed: 'Før',
            added: 'Etter',
        },
        change_awareness: {
            title: 'Samtykkeendringsbevissthet',
            change_type: 'Endringstype',
            entity_type: 'Enhetstype',
            entity_id: 'Enhets-ID',
            detected_at: 'Oppdaget',
            acknowledge: 'Bekreft',
            new_alert: 'Nytt varsel',
            new_alert_form: 'Nytt endringsbevissthetsvarsel',
            total_open: 'Totalt åpne',
            by_severity: 'Etter alvorlighetsgrad',
            snapshot_id: 'Øyeblikksbilde-ID',
            update_status: 'Oppdater status',
            resolution_notes: 'Løsningsnotater',
            resolution_required: 'Løsningsnotater (påkrevd)',
            resolution_required_msg: 'Løsningsnotater er påkrevd ved lukking av et element.',
            status_open: 'Åpen',
            status_under_review: 'Under gjennomgang',
            status_resolved: 'Løst',
            status_accepted: 'Akseptert',
            severity_informational: 'Informativ',
            severity_requires_review: 'Krever gjennomgang',
            severity_policy_violation: 'Retningslinjebrudd',
            create_reconciliation: 'Opprett avstemmingsoppgave',
            reconciliation_created: 'Avstemmingsoppgave opprettet.',
            reconciliation_error: 'Kunne ikke opprette avstemmingsoppgave. Vennligst prøv igjen.',
            fetch_error: 'Kunne ikke hente endringsbevissthetsenheter. Vennligst prøv igjen.',
            dashboard_error: 'Kunne ikke hente endringsbevissthets-kontrollpanel. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette endringsbevissthetselement. Vennligst prøv igjen.',
            status_update_error: 'Kunne ikke oppdatere endringsbevissthets-status. Vennligst prøv igjen.',
            page_title: 'Endre bevissthet',
            no_items: 'Ingen endringsbevissthetsementer funnet. Elementer opprettes når endringer i samtykkekonfigurasjonen oppdages.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Samtykkerevisjonslogg',
            event_type: 'Hendelsestype',
            user_id: 'Bruker-ID',
            timestamp: 'Tidsstempel',
            details: 'Detaljer',
            actor: 'Aktør',
            filter_by_entity_type: 'Filter etter enhet type',
            filter_by_action: 'Filter etter handling',
            fetch_error: 'Kunne ikke hente consent audit trail. Vennligst prøv igjen.',
            page_title: 'Samtykke revisjon',
            export_csv: 'Eksporter CSV',
            exporting: 'Eksporterer …',
            export_success: 'Revisjonssporet ble eksportert.',
            export_error: 'Kunne ikke eksportere revisjonssporet for samtykke. Vennligst prøv igjen.',
            no_entries: 'Ingen revisjonsoppføringer funnet. Revisjonshendelser registreres automatisk når samtykkedata endres.',
            entity_policy: 'Politikk',
            entity_purpose: 'Formål',
            entity_workflow: 'Arbeidsflyt',
            entity_consent_record: 'Samtykkeoppføring',
            entity_change_awareness: 'Endringbevissthet',
            entity_cmp_snapshot: 'CMP-øyeblikksbilde',
            action_created: 'Opprettet',
            action_updated: 'Oppdatert',
            action_status_changed: 'Status endret',
            action_drift_detected: 'Avvik oppdaget',
            action_snapshot_created: 'Øyeblikksbilde opprettet',
            action_approval_submitted: 'Godkjenning sendt inn',
            action_archived: 'Arkivert',
            action_purpose_auto_mapped: 'Formål automatisk tilknyttet',
            action_purpose_mapping_updated: 'Formålstilknytning oppdatert',
            action_webhook_configured: 'Webhook konfigurert',
            entity_propagation: 'Formering',
            entity_reconciliation: 'Forsoning',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Formering bekreftet',
            action_propagation_failed: 'Forplantning mislyktes',
            action_consent_captured: 'Samtykke fanget',
            action_reconciliation_created: 'Avstemming opprettet',
            action_reconciliation_resolved: 'Avstemming løst',
            action_submitted_for_review: 'Sendt til gjennomgang',
            action_activated: 'Aktivert',
            action_suspended: 'Suspendert',
            action_retired: 'Avviklet',
            action_enhanced_review_required: 'Forbedret gjennomgang påkrevd',
            action_enhanced_review_approved: 'Utvidet gjennomgang godkjent',
            action_enhanced_review_rejected: 'Utvidet gjennomgang avvist — policy tilbakestilt til utkast',
            action_consented: 'Samtykke',
            action_consented_customize: 'Samtykke tilpasset',
            action_revoked: 'Opphevet',
            action_rejected: 'Avvist',
        },
        web_records: {
            title: 'Nettsamtykkeposter',
            page_title: 'Nettsamtykkeposter',
            domain: 'Domene',
            visitor_id: 'Besøks-ID',
            consent_action: 'Samtykkehandling',
            received_at: 'Mottatt kl',
            admin_status: 'Admin status',
            revoke: 'Oppheve',
            reject: 'Avvis',
            reason: 'Grunn',
            reason_placeholder: 'Årsak til denne handlingen (valgfritt)',
            revoke_confirm: 'Vil du trekke tilbake denne samtykkeposten?',
            reject_confirm: 'Vil du avvise denne samtykkeposten?',
            override_success: 'Poststatus ble oppdatert.',
            override_error: 'Kunne ikke oppdatere poststatus.',
            no_records: 'Ingen nettsamtykkeposter funnet.',
            fetch_error: 'Kunne ikke laste inn nettsamtykkeposter.',
            filter_by_domain: 'Filtrer etter domene',
            filter_by_action: 'Filtrer etter handling',
            active: 'Aktiv',
        },
        legal_bases: {
            all: 'Alle retsgrundlag',
        },
        common_statuses: {
            suspended: 'Suspendert',
            retired: 'Utfaset',
            deprecated: 'Utfaset',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'AesirX CMP Integrasjon',
            back_to_consent: 'Tilbake to Samtykke',
            back_to_integration: 'Tilbage til integration',
            webhook_status: 'Webhook-status',
            events_received: 'Hendelser mottatt',
            web_consent_records: 'Websamtykkeposter',
            unmapped_purposes: 'Ikke-kartlagte formål',
            integration_management: 'Integrasjonsstyring',
            webhook_configuration: 'Webhook Konfigurasjon',
            webhook_config_desc: 'Konfigurer CMP webhook-endepunktet og administrer HMAC-hemmeligheten.',
            purpose_mapping: 'Formål Kartlegging',
            purpose_mapping_desc: 'Kartlegg CMP-formålsidentifikatorer til GRC-samtykkekatalogens formål.',
            bulk_sync: 'Masse Synkroniser',
            bulk_sync_desc: 'Anvend formålstilknytninger på nytt på alle ikke-tilknyttede samtykkeposter.',
            consent_analytics: 'Websamtykkeanalyse (siste 30 dager)',
            consent_grant_rate: 'Samtykkegodkjenningsrate',
            top_domain: 'Top Domene',
            records: 'poster',
            banner_interactions: 'Bannerinteraktioner',
            consent_actions: 'Samtykkehandlinger',
            opt_in_trend: 'Samtykke/avmelding-trend',
            records_by_domain: 'Oppføringer etter domene',
            date: 'Dato',
            grants: 'Innvilgelser',
            denials: 'Avslag',
            not_configured: 'Ikke konfigurert',
            load_error: 'Kunne ikke laste integration data',
            webhook_not_configured: 'Webhook ikke konfigurert.',
            webhook_not_configured_msg: 'Vennligst {link} først.',
            configure_webhook_link: 'konfigurer webhooken',
            setup_wizard: 'Oppsettsveiviser',
            step_configure_webhook: 'Konfigurer webhook',
            step_map_purposes: 'Map Formål',
            step_run_sync: 'Kjøring Innledende Masse Synkroniser',
            step_verify_data: 'Verifiser data i dashbord',
            configure: 'Konfigurer',
            view_dashboard: 'Vis Dashboard',
            syncing: 'Synkroniserer...',
            sync_now: 'Synkronisering Now',
            sync_completed: 'Synkronisering fullført: {imported} importert, {skipped} hoppet over, {failed} mislykket.',
            sync_failed: 'Synkronisering mislyktes',
            last_sync_result: 'Siste synkroniseringsresultat',
            imported: 'Importert',
            skipped: 'Hoppet over',
            errors: 'Feil',
            index: 'Indeks',
            error: 'Feil',
            sync_history: 'Synkronisering Historikk',
            no_sync_jobs: 'Ingen sync jobs recorded ennå.',
            load_sync_error: 'Kunne ikke laste sync data',
            purpose_mapping_title: 'CMP Formål Kartlegging',
            auto_mapping: 'Automatisk kartlegging...',
            auto_map_purposes: 'Automatisk formålskartlegging',
            unmapped_warning: '{count} ikke-tilordnede formål -- disse CMP formålene må tilordnes til GRC-formål.',
            mapping_saved: 'Kartlegging lagret.',
            mapping_deleted: 'Kartlegging slettet.',
            auto_map_complete: 'Automatisk kartlegging fullført: {mapped} kartlagt, {unmatched} ikke matchet.',
            auto_map_failed: 'Automatisk kartlegging mislyktes',
            save_mapping_error: 'Kunne ikke lagre mapping',
            delete_mapping_error: 'Kunne ikke slette mapping',
            load_data_error: 'Kunne ikke laste data',
            cmp_purpose: 'CMP Formål',
            grc_purpose: 'GRC Formål',
            select_grc_purpose: '-- Velg GRC Formål --',
            auto_mapped: 'Automatisk kartlagt',
            mapped: 'Kartlagt',
            unmapped: 'Ikke kartlagt',
            no_mappings: 'Ingen formålstilknytninger funnet. Tilknytninger opprettes automatisk når samtykkehendelser mottas.',
            total_mappings: 'Totalt: {count} kartlegging(er)',
            webhook_config_title: 'Webhook Konfigurasjon',
            current_configuration: 'Nåværende Konfigurasjon',
            setup_webhook: 'Sett opp webhook',
            setup_webhook_desc: 'Konfigurer AesirX CMP-webhook for å motta samtykkehendelser.',
            allowed_domains: 'Tilladte domæner (ét per linje)',
            webhook_secret_warning: 'Webhook-hemmelighet (lagre dette nå -- det vises ikke igjen):',
            last_event: 'Siste Hendelse',
            none_received: 'Ingen',
            update_domains: 'Oppdatering Domener',
            pause_webhook: 'Sett på pause Webhook',
            resume_webhook: 'Gjenoppta webhook',
            regenerate_secret: 'Regenerer hemmelighet',
            configuring: 'Konfigurerer...',
            create_webhook_config: 'Opprett webhook-konfigurasjon',
            webhook_endpoint: 'Webhook-endepunkt',
            webhook_endpoint_desc: 'Konfigurer din AesirX CMP til å sende samtykkehendelser til:',
            webhook_signature_note: 'Inkluder HMAC-SHA256-signaturen i {header}-headeren.',
            configured_success: 'Webhook konfigurert.',
            config_updated: 'Konfigurasjon oppdatert.',
            config_error: 'Kunne ikke konfigurere webhook',
            update_error: 'Kunne ikke oppdatere konfigurasjon',
            toggle_error: 'Kunne ikke veksle webhook',
            deactivated: 'Webhook deaktivert.',
            deactivate_error: 'Kunne ikke deaktivere webhook',
            page_title: 'CMP Integrasjon',
            webhook_config_page_title: 'CMP Webhook-konfigurasjon',
            bulk_sync_page_title: 'Kartlegg poster på nytt',
            purpose_mapping_page_title: 'Formålskartlegging',
            data_loaded: 'CMP integrasjonsdata er lastet inn',
            sync_all: 'Synkroniser alle',
            sync_selected: 'Synkroniser valgte',
            sync_status: 'Synkroniseringsstatus',
            last_sync: 'Siste synkronisering',
            no_records: 'Ingen oppføringer funnet.',
            sync_in_progress: 'Synkronisering pågår...',
            sync_complete: 'Synkronisering fullført.',
            sync_error: 'Synkronisering mislyktes. Vennligst prøv igjen.',
            map_purpose: 'Tilknytt formål',
            unmap: 'Fjern tilknytning',
            mapping_status: 'Tilknytningsstatus',
            save_mapping: 'Lagre tilknytning',
            remap_unmapped_records: 'Kartlegg ikke kartlagte poster på nytt',
            remap_records: 'Kartlegg poster på nytt',
            add_mappings_first: 'Legg til formålskartlegginger først (trinn 2)',
            remap_result: 'Omkartleggingsresultat',
            remap_result_desc: 'Behandlet alle samtykkeposter med ikke-kartlagte eller delvis kartlagte formål.',
            total_processed: 'Totalt behandlet',
            newly_mapped: 'Nylig kartlagt',
            still_unmapped_label: 'Fremdeles ikke kartlagt',
            still_unmapped_msg: '{count} post(er) har fortsatt ikke-kartlagte formål. {link} og klikk deretter på Re-map igjen.',
            add_more_mappings: 'Legg til flere kartlegginger',
            remap_complete: 'Ny kartlegging fullført: {remapped} nylig kartlagt, {still_unmapped} fortsatt ikke kartlagt.',
            add_mapping: 'Legg til kartlegging',
            cmp_purpose_id_label: 'CMP Formåls-ID *',
            cmp_purpose_id_required: 'CMP Formåls-ID kreves',
            cmp_purpose_name_label: 'CMP Formålsnavn',
            grc_purpose_label: 'GRC Formål',
        },
        propagation: {
            title: 'Tilbaketrekkingsspredning',
            systems: 'Tilknyttede systemer',
            status: 'Status',
            confirmed: 'Bekreftet',
            pending: 'Venter',
            failed: 'Mislyktes',
            confirm: 'Bekreft tilbaketrekking',
            fail: 'Merk som mislykket',
            failure_reason: 'Feilårsak',
            can_close: 'Alle systemer bekreftet — arbeidsflyten kan lukkes',
            cannot_close: 'Venter på systembekreftelser',
            no_tasks: 'Ingen spredningsoppgaver.',
            confirm_success: 'Systemtilbaketrekking bekreftet.',
            fail_success: 'System merket som mislykket.',
            propagate_success: 'Tilbaketrekking spredt til tilknyttede systemer.',
            propagate: 'Forplante seg til systemer',
            add_system: 'Legg til system',
            remove_system: 'Fjern',
            system_id: 'System-ID',
            system_name: 'Systemnavn',
            owner_id: 'Eier-ID',
            propagate_error: 'Kunne ikke spre tilbaketrekning. Vennligst prøv igjen.',
            no_systems: 'Legg til minst ett system.',
            page_title: 'Uttaksforplantning',
            workflow_id: 'Velg Arbeidsflyt for uttak',
            system_id_hint: 'f.eks. crm-001, erp-prod',
            owner_id_hint: 'Systemeier',
            elapsed: 'Forløpt',
        },
        sensitive: {
            title: 'Sensitive datapolicyer',
            flag: 'Merk som sensitiv',
            categories: 'Sensitive kategorier',
            enhanced_review: 'Krev utvidet gjennomgang',
            enhanced_review_success: 'Utvidet gjennomgang påkrevd.',
            flag_success: 'Policy merket som sensitiv.',
            no_sensitive: 'Ingen sensitive datapolicyer.',
        },
        reconciliation: {
            title: 'Avviksavstemming',
            create: 'Ny avstemmingsoppgave',
            system: 'System',
            owner: 'Eier',
            description: 'Beskrivelse',
            resolution: 'Løsning',
            resolve: 'Løs',
            update_grc: 'Oppdater GRC',
            update_cmp: 'Oppdater CMP',
            accept_difference: 'Aksepter forskjell',
            escalate: 'Eskaler',
            resolution_notes: 'Løsningsnotater',
            no_tasks: 'Ingen avstemmingsoppgaver.',
            resolve_success: 'Avstemmingsoppgave løst.',
            create_success: 'Avstemmingsoppgave opprettet.',
            open: 'Åpen',
            resolved: 'Løst',
            escalated: 'Eskalert',
            dashboard_open: 'Åpne oppgaver',
            dashboard_resolved: 'Løste oppgaver',
            dashboard_escalated: 'Eskalerte oppgaver',
            page_title: 'Mismatch avstemming',
            my_tasks: 'Mine oppgaver',
            all_tasks: 'Alle oppgaver',
            severity: 'Alvorlighetsgrad',
        },
        sync: {
            title: 'CMP-synkronisering',
            direction: 'Retning',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Konflikt',
            resolve_conflict: 'Løs konflikt',
            no_events: 'Ingen synkroniseringshendelser.',
            resolve_success: 'Synkroniseringskonflikt løst.',
            create_success: 'Synkroniseringshendelse registrert.',
            page_title: 'CMP Synkroniser',
            detect_drift: 'Oppdag konflikter',
            detecting: 'Oppdager...',
            drift_result: '{detected} konflikt(er) oppdaget av {total} formål(er) sammenlignet.',
            no_drift: 'Ingen nye konflikter oppdaget.',
            drift_error: 'Kunne ikke kjøre konfliktdeteksjon.',
            resolve_modal_title: 'Løs konflikt',
            resolve_field: 'Felt',
            resolve_grc_value: 'GRC-verdi',
            resolve_cmp_value: 'CMP Verdi',
            accept_cmp: 'Godta CMP verdi',
            accept_cmp_desc: 'Oppdater GRC slik at den samsvarer med CMP - verdien CMP blir den nye GRC-verdien.',
            keep_grc: 'Behold GRC-verdien',
            keep_grc_desc: 'Lukk denne konflikten uten å endre GRC – gi eieren CMP beskjed om å justere.',
            resolve_applied: 'Konflikt løst. GRC-formålet er oppdatert for å samsvare med CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Denne beredskapsrapporten er rådgivende. Den godkjenner, setter sammen, materialiserer, leverer eller sender ikke dokumentpakken.',
            run_label: 'Kjør pre-flight',
            running: 'Kjører pre-flight …',
            assemble_label: 'Automonter pakke',
            assembling: 'Montering …',
            verdict_label: 'Dommen før flyreisen',
            tokens_label: '≈ {count} tokens',
            remediation_label: 'Utbedring',
            no_gaps: 'Ingen hull før flygning oppdaget. Arkivet er klart til å monteres.',
            override_label: 'Overstyr blokkavgjørelse – montering vil bli revisjonsstemplet.',
            accept_label: 'Godta rapporten',
            reject_label: 'Avvis rapport',
            assembled_label: 'Innleveringspakke satt sammen',
            package_id_label: 'Pakke-ID',
            package_filename_label: 'Filnavn',
            package_hash_label: 'Pakkehash',
            evidence_count_label: '{count} beviselementer samlet',
            error_generic: 'Kunne ikke kjøre pre-flight eller sette sammen pakken. Prøv på nytt eller kontroller arkiveringsstatusen.',
        },
        errors: {
            fetch: 'Kunne ikke laste assessments.',
            save: 'Kunne ikke lagre assessment.',
            fetch_gap_report_failed: 'Kunne ikke hente mangelrapport.',
            fetch_transfers_failed: 'Kunne ikke hente transfers.',
            save_transfer_failed: 'Kunne ikke lagre overføring.',
            submit_review_failed: 'Kunne ikke sende inn til gjennomgang.',
            process_decision_failed: 'Kunne ikke behandle beslutning.',
            deprecate_failed: 'Kunne ikke utfase.',
            evaluate_risk_failed: 'Kunne ikke evaluere risiko.',
            fetch_safeguards_failed: 'Kunne ikke hente sikkerhetstiltak.',
            save_safeguard_failed: 'Kunne ikke lagre sikkerhetstiltak.',
            update_safeguard_status_failed: 'Kunne ikke oppdatere sikkerhetsstatus.',
        },
        nav: {
            dpias: 'DPIA-er',
            transfers: 'Overføringsvurderinger',
            dashboard: 'Kontrollpanel',
            gap_report: 'Avviksrapport',
        },
        risk_level_critical: 'Kritisk risiko',
        risk_level_high: 'Høy risiko',
        risk_level_medium: 'Middels risiko',
        risk_level_low: 'Lav risiko',
        severity_critical: 'Kritisk alvorlighetsgrad',
        severity_high: 'Høy alvorlighetsgrad',
        severity_medium: 'Middels alvorlighetsgrad',
        severity_low: 'Lav alvorlighetsgrad',
        overdue: 'Forfalt',
        framework_filter_label: 'Rammeverk',
        aria_filter_by_framework: 'Filtrer etter rammeverk',
        dashboard: {
            title: 'Vurderingskontrollpanel',
            page_title: 'Vurderingsdashboard',
            announce_loaded: 'Vurderingsdashboard lastet',
            announce_chart_view: 'Viser diagramvisning',
            announce_table_view: 'Viser datatabellvisning',
            aria_metrics_table: 'Vurderingsdashboard-målinger',
            metric_col: 'Metrikk',
            value_col: 'Verdi',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Overføring',
            risk_prefix: 'Risiko',
            aria_dpias_by_status: 'DPIAer etter status',
            aria_transfers_by_status: 'Overføringer etter status',
            aria_top_risks: 'Topprisiko',
            aria_top_risks_table: 'Topprisiko på tvers av DPIAer',
            overdue_dpias_attention: '{count} forfalte DPIA-vurderinger krever oppmerksomhet',
            no_overdue_dpias: 'Ingen forfalte DPIA-vurderinger',
            overdue_transfers_attention: '{count} forfalte overføringsvurderinger krever oppmerksomhet',
            no_overdue_transfers: 'Ingen forfalte overføringsvurderinger',
            dpias_overview: 'DPIA-oversikt',
            transfers_overview: 'Oversikt over overføringsvurderinger',
            overdue_items: 'Forfalte elementer',
            overdue_dpias: 'Forfalte DPIA-er',
            overdue_transfers: 'Forfalte overføringsvurderinger',
            count: 'Antall',
            no_data: 'Ingen data tilgjengelige.',
            no_risks: 'Ingen risks funnet.',
            no_dashboard_data: 'Ingen dashboard data tilgjengelige.',
            fetch_error: 'Kunne ikke hente dashboarddata. Vennligst prøv igjen.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DPIAer',
            announce_submitted_for_review: 'DPIA sendt til gjennomgang',
            announce_deprecated: 'DPIA avviklet',
            announce_risks_loaded: '{count} risiko(er) lastet',
            announce_mitigations_loaded: '{count} tiltak lastet',
            announce_risk_deleted: 'Risiko slettet',
            announce_mitigation_status_updated: 'Tiltaksstatus oppdatert',
            aria_filter: 'Filtrer DPIAer',
            aria_filter_by_status: 'Filtrer etter status',
            aria_filter_by_lead: 'Filtrer etter vurderingsleder',
            aria_table: 'Personvernkonsekvensvurderinger',
            announce_found: '{count} DPIA(er) funnet',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Start',
            mitigation_mark_implemented: 'Merk implementert',
            mitigation_verify: 'Verifiser',
            title: 'Personvernkonsekvensvurderinger (DPIA)',
            new: 'Ny DPIA',
            edit: 'Rediger DPIA',
            dpia_title: 'Tittel',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Aktivitet',
            risk_score: 'Risikoscore',
            risks: 'Risikoer',
            mitigations: 'Tiltak',
            add_risk: 'Legg til risiko',
            add_mitigation: 'Legg til tiltak',
            necessity: 'Nødvendighet / Forholdsmessighet',
            likelihood: 'Sannsynlighet',
            impact: 'Konsekvens',
            severity_label: 'Alvorlighetsgrad',
            purpose: 'Formål',
            consultation_notes: 'Konsultasjonsnotater',
            dpo_opinion: 'DPO-vurdering',
            processing_activity_ids: 'Behandlingsaktivitets-ID-er (kommaseparert)',
            system_ids: 'System-IDer (kommaseparert)',
            vendor_ids: 'Leverandør-IDer (kommaseparert)',
            data_types: 'Datatyper (kommaseparert)',
            data_types_short: 'Data Typer',
            data_subjects: 'Registrerede (kommaseparert)',
            assessment_lead_id: 'Vurdering Leder ID',
            assessment_lead: 'Vurdering Leder',
            dpo_id: 'DPO ID',
            reviewer_ids: 'Gennemgåer-ID-er (kommaseparert)',
            approval_mode: 'Godkjenning Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Kræv DPO-godkendelse',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Gjennomgang Dato',
            submit_for_review: 'Send inn for Gennemgang',
            approve_reject: 'Godkjenn/Avvis',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Utfas',
            deprecated: 'Utfaset',
            hide_risks: 'Skjul risikoer',
            hide_mitigations: 'Skjul tiltak',
            comments: 'Kommentarer',
            submit_decision: 'Send beslutning',
            category_label: 'Kategori',
            category_data_breach: 'Data Brudd',
            category_unauthorized_access: 'Uautorisert Tilgang',
            category_function_creep: 'Funksjonsutglidning',
            category_re_identification: 'Re-identifisering',
            category_discrimination: 'Diskriminering',
            category_surveillance: 'Overvåking',
            category_other: 'Annet',
            affected_rights: 'Berørte rettigheder (kommaseparert)',
            no_risks: 'Ingen risks funnet.',
            no_mitigations: 'Ingen mitigations funnet.',
            no_dpias: 'Ingen dpias funnet.',
            mitigation_type: 'Type',
            mitigation_technical: 'Teknisk',
            mitigation_organizational: 'Organisatorisk',
            mitigation_contractual: 'Kontraktsmessig',
            risk_ids: 'Risiko-IDer (kommaseparert)',
            responsible_party: 'Ansvarlig Part',
            responsible_party_placeholder: 'Eller skriv inn navn manuelt',
            affected_rights_placeholder: 'Valgte rettigheter vises her',
            affected_rights_freetext_placeholder: 'Skriv inn berørte rettigheter (kommaseparert)',
            responsible: 'Ansvarlig',
            target_date: 'Mål Dato',
            created: 'Opprettet',
            fetch_error: 'Kunne ikke hente DPIAs. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre DPIA. Vennligst prøv igjen.',
            submit_review_error: 'Kunne ikke sende inn DPIA for review. Vennligst prøv igjen.',
            decision_error: 'Kunne ikke behandle beslutning. Vennligst prøv igjen.',
            deprecate_error: 'Kunne ikke utfase DPIA. Vennligst prøv igjen.',
            fetch_risks_error: 'Kunne ikke hente risks. Vennligst prøv igjen.',
            save_risk_error: 'Kunne ikke lagre risiko. Vennligst prøv igjen.',
            delete_risk_error: 'Kunne ikke slette risiko. Vennligst prøv igjen.',
            fetch_mitigations_error: 'Kunne ikke hente mitigations. Vennligst prøv igjen.',
            save_mitigation_error: 'Kunne ikke lagre mitigation. Vennligst prøv igjen.',
            update_mitigation_status_error: 'Kunne ikke oppdatere tiltaksstatus. Vennligst prøv igjen.',
            link_form_template: 'Link forskriftsskjemamal',
            link_form_template_placeholder: 'Velg en DPIA skjemamal (valgfritt)',
            link_form_template_none: 'Ingen',
            linked_form: 'Koblet skjema',
            linked_form_creating: 'Oppretter koblet skjema ...',
            linked_form_error: 'Kunne ikke opprette koblet skjemaforekomst. DPIA ble opprettet uten et koblet skjema.',
            view_linked_form: 'Se skjema',
            announce_created_with_form: 'DPIA opprettet med koblet skjemaforekomst',
            creation_mode: 'DPIA Opprettingsmodus',
            mode_standard: 'Standard DPIA',
            mode_template: 'Bruk forskriftsskjemamal',
            select_form_template: 'Velg Skjemamal',
            create_and_fill_form: 'Opprett og fyll ut skjema',
            template_sections_title: 'Malseksjoner',
            field_singular: 'felt',
            field_plural: 'felt',
            no_sections: 'Finner ingen seksjoner i denne malen.',
            template_mode_hint: 'Etter opprettelsen vil du bli omdirigert til å fylle ut det fullstendige forskriftsskjemaet.',
            linked_dossier_filings: 'Tilknyttede dossierarkiveringer',
            no_linked_dossier_filings: 'Ingen dossierarkiveringer tilknyttet denne DPIA-en.',
            view_dossier_filing: 'Vis arkivering',
            hide_dossier_filings: 'Skjul dossierarkiveringer',
            supporting_evidence: 'Støttende bevis',
            hide_supporting_evidence: 'Skjul bevis',
            no_supporting_evidence: 'Ingen støttende bevis vedlagt.',
            upload_supporting_evidence: 'Last opp støttende bevis',
            view_form_responses: 'Se skjemasvar',
            regulatory_framework: 'Regulatorisk rammeverk',
            all_frameworks: 'Alle rammeverk',
            no_templates_for_market: 'Ingen maler tilgjengelig for det valgte markedet. Vennligst velg et annet marked eller opprett en mal.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Dataoverføringer',
            announce_found: '{count} overføringsvurdering(er) funnet',
            announce_submitted_for_review: 'Overføring sendt til gjennomgang',
            announce_deprecated: 'Overføring avviklet',
            announce_decision: 'Overføring {decision}',
            announce_risk_eval_completed: 'Risikovurdering fullført',
            announce_safeguard_status_updated: 'Sikkerhetsstatus oppdatert',
            announce_safeguards_loaded: '{count} sikkerhetstiltak lastet',
            aria_filter: 'Filtrer overføringsvurderinger',
            aria_filter_by_status: 'Filtrer etter status',
            aria_filter_by_mechanism: 'Filtrer etter overføringsmekanisme',
            aria_table: 'Dataoverføringsvurderinger',
            title: 'Grenseoverskridende overføringsvurderinger',
            new: 'Ny overføring',
            edit: 'Rediger overføring',
            name: 'Navn',
            from_country: 'Fra land',
            to_country: 'Til land',
            safeguards: 'Sikkerhetstiltak',
            risk_level: 'Risikonivå',
            assessment_notes: 'Vurderingsnotater',
            mechanism: 'Mekanisme',
            transfer_mechanism: 'Overføring Mekanisme',
            transfer_mechanism_detail: 'Overføring Mekanisme Detalj',
            all_mechanisms: 'Alle Mekanismer',
            mechanism_other: 'Annet',
            data_types: 'Datatyper (kommaseparert)',
            data_subjects: 'Registrerede (kommaseparert)',
            purpose: 'Formål',
            recipient_name: 'Mottakernavn',
            create_success: 'Overføringsvurdering opprettet.',
            update_success: 'Overføringsvurdering oppdatert.',
            recipient_details: 'Mottakerdetaljer',
            recipient_details_placeholder: 'Mottakerens rolle, plassering og systemer samt detaljer om videreoverføring',
            vendor_id: 'Leverandør ID',
            processing_activity_ids: 'Behandlingsaktivitets-ID-er (kommaseparert)',
            reviewer_ids: 'Gennemgåer-ID-er (kommaseparert)',
            approval_mode: 'Godkjenning Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Kræv DPO-godkendelse',
            review_date: 'Gjennomgang Dato',
            created: 'Opprettet',
            deprecated: 'Utfaset',
            submit_for_review: 'Send inn for Gennemgang',
            approve_reject: 'Godkjenn/Avvis',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Utfas',
            risk_eval: 'Risiko Evaluering',
            hide_safeguards: 'Skjul sikkerhetstiltak',
            mechanism_proof: 'Mekanismebevis',
            hide_mechanism_proof: 'Skjul mekanismebevis',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Ingen mekanisme bevis vedlagt.',
            upload_mechanism_proof: 'Last opp mekanismebevis',
            comments: 'Kommentarer',
            submit_decision: 'Send beslutning',
            data_sensitivity: 'Datafølsomhed (1-5)',
            country_risk: 'Landerisiko (1-5)',
            safeguard_adequacy: 'Sikkerhetstiltak Tilstrekkelighets (1-5)',
            volume_factor: 'Mængdefaktor (1-5)',
            evaluate: 'Evaluer',
            add_safeguard: 'Add Sikkerhetstiltak',
            responsible_party: 'Ansvarlig Part',
            responsible_party_placeholder: 'Eller skriv inn navn manuelt',
            responsible: 'Ansvarlig',
            implementation_date: 'Implementeringsdato',
            implementation: 'Implementering',
            expiry_date: 'Utløp Dato',
            expiry: 'Utløp',
            expire: 'Utløp',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Ingen sikkerhetstiltak funnet.',
            no_transfers: 'Ingen overføring vurderinger funnet.',
            fetch_error: 'Kunne ikke hente overføringsvurderinger. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre overføringsvurdering. Vennligst prøv igjen.',
            submit_review_error: 'Kunne ikke sende inn til gjennomgang. Vennligst prøv igjen.',
            decision_error: 'Kunne ikke behandle beslutning. Vennligst prøv igjen.',
            deprecate_error: 'Kunne ikke utfase overføring. Vennligst prøv igjen.',
            risk_eval_error: 'Kunne ikke evaluere risiko. Vennligst prøv igjen.',
            fetch_safeguards_error: 'Kunne ikke hente sikkerhetstiltak. Vennligst prøv igjen.',
            save_safeguard_error: 'Kunne ikke lagre sikkerhetstiltak. Vennligst prøv igjen.',
            update_safeguard_status_error: 'Kunne ikke oppdatere sikkerhetsstatus. Vennligst prøv igjen.',
            safeguard_type_encryption_in_transit: 'Kryptering under transport',
            safeguard_type_encryption_at_rest: 'Kryptering i hvile',
            safeguard_type_access_controls: 'Tilgang Kontroller',
            safeguard_type_contractual_clauses: 'Kontraktsklausuler',
            safeguard_type_data_minimization: 'Data Minimering',
            safeguard_type_pseudonymization: 'Pseudonymisering',
            safeguard_type_audit_rights: 'Revisjon Rettigheter',
            safeguard_type_breach_notification: 'Brudd Varsling',
            safeguard_type_data_localization: 'Data Lokalisering',
            safeguard_type_other: 'Annet',
            activate: 'Aktiver',
            all_statuses: 'Alle statuser',
            any: 'Any',
            mechanism_detail: 'Mekanisme Detalj',
            safeguard_types: {
                encryption_in_transit: 'Kryptering under overføring',
                encryption_at_rest: 'Kryptering i hvile',
                access_controls: 'Tilgangskontroller',
                contractual_clauses: 'Kontraktsklausuler',
                data_minimization: 'Dataminimering',
                pseudonymization: 'Pseudonymisering',
                audit_rights: 'Revisjonsrettigheter',
                breach_notification: 'Bruddvarsel',
                data_localization: 'Datalokalisering',
                other: 'Annet',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Gap-rapport for vurderinger',
            announce_loaded: 'Gap-rapport lastet. {count} gap identifisert',
            aria_activities_without_dpia: 'Aktiviteter uten DPIA-vurdering',
            aria_activities_unclassified: 'Aktiviteter med uklassifisert DPIA-krav',
            activities_unclassified_section_title: 'Aktiviteter med uklassifisert DPIA-krav',
            activities_unclassified_description: 'Disse aktivitetene er ikke gjennomgått for å avgjøre om en DPIA er juridisk påkrevd. Klassifiser hver enkelt for å fjerne signalet.',
            activities_unclassified_empty: 'Alle aktive aktiviteter er klassifisert.',
            aria_unmitigated_risks: 'Ubehandlede høye og kritiske risikoer',
            aria_transfers_without_safeguards: 'Overføringer uten tilstrekkelige sikkerhetstiltak',
            aria_expired_assessments: 'Utløpte vurderinger som krever fornyelse',
            title: 'Avviksrapport for vurderinger',
            no_dpia: 'Aktiviteter uten DPIA',
            no_assessment: 'Overføringer uten vurdering',
            unmitigated: 'DPIA-er med ubehandlede risikoer',
            activity_id: 'Aktivitet ID',
            dpia_id: 'DPIA ID',
            assessment_id: 'Vurdering ID',
            expired_assessments: 'Utløpte Vurderinger',
            no_gaps_activities: 'Ingen mangler funnet. Alle aktiviteter har DPIA-er.',
            no_unmitigated_risks: 'Ingen uutbedrede høye/kritiske risikoer funnet.',
            no_gaps_safeguards: 'Ingen mangler funnet. Alle overføringer har tilstrekkelige sikkerhetstiltak.',
            no_expired: 'Ingen utløpte vurderinger funnet.',
            no_report_data: 'Ingen mangel rapport data tilgjengelige.',
            fetch_error: 'Kunne ikke hente mangelrapport. Vennligst prøv igjen.',
            no_expired_assessments: 'Ingen utløpte vurderinger.',
            no_gaps_dpia: 'Ingen dpia gaps funnet.',
            no_data: 'Ingen data tilgjengelige.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossierinnleveringsdetalj',
            page_title_list: 'Dossierinnleveringssaker',
            announce_filings_found: '{count} innlevering(er) funnet',
            announce_results: '{count} resultater',
            aria_filter_by_status: 'Filtrer etter status',
            title: 'Dossierarkivsaker',
            nav_label: 'Dossierarkivering',
            create: 'Lag dossierarkivering',
            form_type: 'Skjematype',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referanse',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Innsendt dato',
            outcome: 'Utfall',
            parent_filing: 'Foreldre',
            no_filings: 'Fant ingen dossier.',
            fetch_error: 'Kunne ikke hente dossierinnleveringer. Vennligst prøv igjen.',
            save_error: 'Kunne ikke opprette dossierarkivering. Vennligst prøv igjen.',
            created: 'Dokumentarkiv opprettet.',
            loaded: 'Dokumentarkiv lastet.',
            not_found: 'Finner ikke dossier.',
            back_to_list: 'Tilbake til listen',
            section_assignments: 'Seksjonsoppgaver',
            assign_sections: 'Tilordne seksjoner',
            section_id: 'Seksjon',
            department: 'Avdeling',
            assignee: 'Oppdragstaker',
            deadline: 'Frist',
            no_assignments: 'Ingen seksjonsoppgaver ennå.',
            sections_assigned_msg: 'Seksjoner tildelt.',
            assign_error: 'Kunne ikke tilordne seksjoner. Vennligst prøv igjen.',
            lifecycle: 'Arkiveringslivssyklus',
            submission: 'Innleveringsdetaljer',
            submission_channel: 'Sendingskanal',
            confirmation_number: 'Bekreftelsesnummer',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Send til gjennomgang',
            submitted_for_review: 'Sendt til intern vurdering.',
            submit_review_error: 'Kunne ikke sende inn til vurdering. Vennligst prøv igjen.',
            legal_signoff: 'Juridisk avmelding',
            legal_signoff_done: 'Juridisk signering fullført.',
            legal_signoff_error: 'Kunne ikke fullføre juridisk signering. Vennligst prøv igjen.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Kunne ikke godkjenne innlevering. Vennligst prøv igjen.',
            materialisation_status_column: 'Innleveringsartefakt',
            materialisation_na: 'Ikke aktuelt',
            materialisation_pending: 'Venter',
            materialisation_success: 'Materialisert',
            materialisation_failed: 'Mislyktes',
            materialisation_banner_title: 'Innsendingsartefakt kunne ikke opprettes',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Prøv på nytt',
            materialisation_retry_count: 'Tidligere forsøk på nytt:',
            materialisation_retry_success: 'Innsendingsartefakt materialiserte vellykket.',
            materialisation_retry_announce: 'Forsøk på nytt lyktes',
            filter_materialisation_failed: 'Vis bare materialiseringsfeil',
            column_title: 'Tittel',
            column_form_type: 'Skjema',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Tilleggsforespørsel',
            supplement_requests: 'Tilleggsforespørsler',
            supplement_response: 'Tilleggssvar',
            record_supplement: 'Registrer tilleggsforespørsel',
            supplement_recorded: 'Tilleggsforespørsel registrert.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Kunne ikke registrere tilleggsforespørsel. Vennligst prøv igjen.',
            submit_supplement: 'Send inn tilleggssvar',
            supplement_submitted_msg: 'Tilleggssvar innsendt.',
            supplement_submit_error: 'Kunne ikke sende inn tilleggssvar. Vennligst prøv igjen.',
            response_status: 'Svarstatus',
            response_notes: 'Svarnotater',
            resubmit: 'Send inn på nytt',
            resubmitted_msg: 'Saksdokumentet er sendt inn på nytt.',
            resubmit_error: 'Kunne ikke sende inn dokumentet på nytt. Vennligst prøv igjen.',
            outcome_error: 'Kunne ikke registrere utfallet. Vennligst prøv igjen.',
            accepted: 'Akseptert',
            rejected: 'Avvist',
            close: 'Lukk',
            closed_msg: 'Arkiv stengt.',
            close_error: 'Kunne ikke lukke arkivet. Vennligst prøv igjen.',
            change_filing: 'Opprett endringsarkivering',
            change_filing_created: 'Endre arkivering opprettet.',
            change_filing_error: 'Kunne ikke opprette endringsarkivering. Vennligst prøv igjen.',
            change_reason: 'Grunn til endring',
            filing_lineage: 'Arkivering avstamning',
            timers: 'Aktive timere',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Tilleggsfrist',
            timer_resubmission_review: 'Frist for ny innsending',
            timer_department_input: 'Avdelingsfrist',
            days_remaining: 'dager igjen',
            days_overdue: 'dager forsinket',
            cover_letter: 'Følgebrev',
            checklist: 'Sjekkliste',
            evidence_index: 'Bevisindeks',
            status_draft: 'Utkast',
            status_internal_review: 'Intern gjennomgang',
            status_legal_signoff: 'Juridisk avmelding',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Tillegg forespurt',
            status_supplement_submitted: 'Tillegg innsendt',
            status_resubmitted: 'Sendt på nytt',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Lukket',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Utkast',
            step_internal_review: 'Intern gjennomgang',
            step_legal_signoff: 'Juridisk avmelding',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Tillegg forespurt',
            step_supplement_submitted: 'Tillegg innsendt',
            step_resubmitted: 'Sendt på nytt',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Lukket',
            linked_dpia: 'Tilknyttet DPIA',
            linked_dpia_none: 'Ingen DPIA tilknyttet denne arkiveringen.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Vis DPIA',
            linked_dpia_label: 'Denne dossierarkiveringen er tilknyttet følgende DPIA:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Innsending artefakt helse',
            page_title: 'Innlevering Artefakt Helse',
            page_description: 'Oppdag og utbedre problemer med materialisering av dossier. Listene er tilpasset organisasjonen din.',
            failed_title: 'Mislykkede materialiseringer',
            failed_empty: 'Ingen mislykkede materialiseringer – bra.',
            pending_title: 'Godkjent, men ikke realisert',
            pending_empty: 'Alle godkjente registreringer har en dokumentasjon.',
            orphan_title: 'Foreldreløse dossierer',
            orphan_description: 'Dossier opprettet av materialisering som ikke lenger refereres til i noen arkivering - vanligvis taperen av et samtidig løp. Oppført for etterforskning; opprydding er manuell.',
            orphan_empty: 'Ingen foreldreløse dossierer oppdaget.',
            materialise_button: 'Materialiseres nå',
            column_failure_reason: 'Årsak til feil',
            column_failed_at: 'Mislyktes kl',
            column_retries: 'Forsøk på nytt',
            column_approved_at: 'Godkjent kl',
            column_dossier_id: 'Dossier ID',
            column_source_filing_id: 'Kildearkivering',
            column_dossier_type: 'Type',
            column_created_at: 'Opprettet kl',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Tilgang nektet',
        access_denied: 'Du har ikke de nødvendige tillatelsene for å få tilgang til klassifiseringsmodulen. Kontakt administratoren din.',
        nav: {
            taxonomies: 'Taksonomier',
            labeling: 'Merking',
            lifecycle: 'Livssyklus',
            gaps: 'Avviksrapport',
            exports: 'Eksporter',
        },
        exports: {
            title: 'Klassifiseringseksporter',
            csv_section: 'CSV-eksporter',
            pdf_section: 'PDF-rapporter',
            inventory: 'Oversikt',
            classification_register: 'Klassifiseringsregister',
            raci: 'RACI-matrise',
            exceptions: 'Unntak',
            evidence_pack: 'Dokumentasjonspakke',
            delta_report: 'Deltarapport',
            executive_summary: 'Ledelsessammendrag',
            exporting: 'Eksporterer…',
            export_success: 'Eksport fullført.',
            export_error: 'Eksport mislyktes. Prøv igjen.',
            csv_download: 'CSV-nedlasting klar.',
            pdf_download: 'PDF-generering startet.',
            no_data: 'Ingen data tilgjengelig for eksport.',
        },
        delta: {
            title: 'Endringsdetaljer',
            added: 'Lagt til',
            removed: 'Fjernet',
            changed: 'Endret',
            added_count: 'Lagt til ({count})',
            removed_count: 'Fjernet ({count})',
            changed_count: 'Endret ({count})',
            object_id: 'Objekt-ID',
            object_type: 'Objekttype',
            taxonomy: 'Taksonomi',
            level: 'Nivå',
            previous: 'Forrige',
            current: 'Nåværende',
            no_records: 'Ingen poster i denne seksjonen.',
            empty_section: 'Denne seksjonen er tom.',
        },
        schedule: {
            title: 'Planlegg oppdagelsessyklus',
            enabled: 'Aktivert',
            interval_days: 'Intervall (dager)',
            interval_label: 'Kjør hver N. dag',
            manual_only: 'Kun manuell',
            every_n_days: 'Hver {n}. dag',
            save: 'Lagre tidsplan',
            cancel: 'Avbryt',
            schedule_updated: 'Tidsplan oppdatert.',
        },
        taxonomies: {
            title: 'Klassifiseringstaksonomier',
            new: 'Ny taksonomi',
            edit: 'Rediger taksonomi',
            name: 'Navn',
            levels: 'Klassifiseringsnivåer',
            seed_defaults: 'Initialiser standarder',
            scope: 'Omfang',
            scope_organization: 'Organisasjon',
            scope_module_specific: 'Modul Spesifikk',
            deprecate: 'Utfas',
            hide_levels: 'Skjul nivåer',
            versions: 'Versjoner',
            hide_versions: 'Skjul versjoner',
            add_level: 'Add Nivå',
            new_level: 'Ny Nivå',
            edit_level: 'Rediger Nivå',
            code: 'Kode',
            label: 'Etikett',
            color: 'Farge',
            ordinal: 'Ordenstall',
            handling_requirements: 'Håndtering Krav',
            version_history: 'Versjon Historikk',
            changes: 'Endringer',
            failed_fetch: 'Kunne ikke hente taksonomier. Vennligst prøv igjen.',
            failed_save: 'Kunne ikke lagre taksonomi. Vennligst prøv igjen.',
            failed_activate: 'Kunne ikke aktivere taksonomi. Vennligst prøv igjen.',
            failed_deprecate: 'Kunne ikke utfase taksonomi. Vennligst prøv igjen.',
            failed_seed: 'Kunne ikke initialisere standardtaksonomier. Vennligst prøv igjen.',
            failed_fetch_levels: 'Kunne ikke hente taksonomiske nivåer. Vennligst prøv igjen.',
            failed_save_level: 'Kunne ikke lagre taksonomisk nivå. Vennligst prøv igjen.',
            failed_delete_level: 'Kunne ikke slette taksonomisk nivå. Vennligst prøv igjen.',
            failed_fetch_versions: 'Kunne ikke hente taksonomiversjoner. Vennligst prøv igjen.',
            loaded_count: 'Lastet {count} taksonomier',
            defaults_seeded: 'Standard-taksonomier initialisert.',
            no_levels: 'Ingen klassifiseringsnivåer definert — legg til nivåer for å bygge klassifiseringshierarkiet.',
            no_versions: 'Ingen versjonshistorikk ennå — versjoner opprettes når en aktiv taksonomi oppdateres.',
        },
        labeling: {
            title: 'Datamerking',
            new: 'Ny merkelapp',
            edit: 'Rediger merkelapp',
            entity_type: 'Enhetstype',
            entity_id: 'Enhets-ID',
            taxonomy: 'Taksonomi',
            classification_level: 'Klassifiseringsnivå',
            applied_by: 'Påført av',
            applied_at: 'Påført',
            bulk_apply: 'Massemerking',
            bulk_remove: 'Massefjerning',
            all_taxonomies: 'Alle Taksonomier',
            system: 'System',
            dataset: 'Datasett',
            level_code_placeholder: 'Nivå kode...',
            select_taxonomy: 'Velg taksonomi...',
            select_level: 'Velg nivå...',
            rationale: 'Begrunnelse',
            review_date: 'Gjennomgang Dato',
            review: 'Gjennomgang',
            selected: 'valgt',
            select_all_labels: 'Velg alle etiketter',
            loaded_count: 'Lastet {count} etiketter',
            hide_stale_labels: 'Skjul forældede etiketter',
            show_conflicts: 'Vis konflikter',
            hide_conflicts: 'Skjul konflikter',
            label_conflicts: 'Etikett Konflikter',
            conflict_reason: 'Konflikt Årsak',
            labels: 'Etiketter',
            failed_fetch: 'Kunne ikke hente etiketter. Vennligst prøv igjen.',
            failed_fetch_taxonomies: 'Kunne ikke hente taksonomier. Vennligst prøv igjen.',
            failed_fetch_levels: 'Kunne ikke hente taksonomiske nivåer. Vennligst prøv igjen.',
            failed_save: 'Kunne ikke lagre etikett. Vennligst prøv igjen.',
            failed_review: 'Kunne ikke gjennomgå etikett. Vennligst prøv igjen.',
            failed_bulk: 'Kunne ikke massemerke. Vennligst prøv igjen.',
            failed_stale: 'Kunne ikke hente utdaterte etiketter. Vennligst prøv igjen.',
            failed_conflicts: 'Kunne ikke hente etikettkonflikter. Vennligst prøv igjen.',
            review_permission_denied: 'Du har ikke tillatelse til å gjennomgå denne etiketten.',
            review_not_found: 'Etikett ikke funnet — den kan ha blitt slettet.',
            approve_selected: 'Godkjenn valgte',
            reject_selected: 'Avvis valgte',
            approved: 'Godkjent',
            rejected: 'Avvist',
            labels_count: 'etiketter',
            failed_count: 'mislyktes',
            confirm_approve: 'Bekreft godkjenning',
            confirm_reject: 'Bekreft avvisning',
            approve_n_labels: 'Godkjenn {count} etiketter?',
            reject_n_labels: 'Avvis {count} etiketter?',
            submit_for_review: 'Send til gjennomgang',
            approve: 'Godkjenn',
            reject: 'Avvis',
            parent_system: 'Overordnet system',
            parent_system_placeholder: 'Velg overordnet system...',
            parent_system_help: 'Brukes til konfliktdeteksjon.',
            dataset_level: 'Datasettniva',
            parent_level: 'Overordnet niva',
            bulk_created: 'Opprettet {created} etikett(er)',
            bulk_skipped: '{skipped} hoppet over',
            bulk_all_skipped: 'Alle {skipped} hoppet over',
            ids_hint: 'Skriv inn IDer atskilt med komma',
            delete_confirm: 'Slett etikett for "{id}"?',
        },
        lifecycle: {
            title: 'Datalivssyklusovervåking',
            new: 'Ny oppføring',
            current_stage: 'Gjeldende fase',
            last_transition: 'Siste overgang',
            transition: 'Overgang',
            view_history: 'Vis historikk',
            stages: {
                creation: 'Opprettelse',
                collection: 'Innsamling',
                processing: 'Behandling',
                storage: 'Lagring',
                retention: 'Oppbevaring',
                disposal: 'Avhending',
                archived: 'Arkivert',
                active: 'Aktiv',
                pending_deletion: 'Ventende Sletting',
                deleted: 'Slettet',
                on_hold: 'På Sperring',
            },
            all_stages: 'Alle Faser',
            object_id: 'Objekt ID',
            object_type: 'Objekt Type',
            policy_reference: 'Retningslinje Referanse',
            retention_period_days: 'Opbevaringsperiode (dager)',
            retention_basis: 'Oppbevaring Grunnlag',
            deletion_method: 'Sletting Metode',
            deletion_secure_erase: 'Sikker Sletting',
            deletion_anonymization: 'Anonymisering',
            deletion_physical_destruction: 'Fysisk Destruksjon',
            deletion_other: 'Annet',
            responsible_party: 'Ansvarlig Part',
            responsible_user_id: 'Ansvarlig Bruker ID',
            retention_expiry: 'Oppbevaring Utløp',
            hold_reason: 'Sperring Årsak',
            hold: 'Hold tilbake',
            release: 'Frigivelse',
            hide_events: 'Skjul hendelser',
            events_timeline: 'Hendelser Tidslinje',
            add_event: 'Add Hendelse',
            event_type: 'Hendelse Type',
            create_event: 'Opprett hendelse',
            from_stage: 'Fra Fase',
            to_stage: 'Til Fase',
            actor: 'Aktør',
            reason_placeholder: 'Årsak...',
            go: 'Go',
            show_expired_retentions: 'Vis udløbne opbevaringer',
            hide_expired_retentions: 'Skjul udløbne opbevaringer',
            expired_retentions_alert: 'Alarm for udløbne opbevaringer',
            failed_fetch: 'Kunne ikke hente livssyklusposter. Vennligst prøv igjen.',
            failed_save: 'Kunne ikke lagre livssykluspost. Vennligst prøv igjen.',
            failed_transition: 'Kunne ikke overføre livssyklusfase. Vennligst prøv igjen.',
            failed_hold: 'Kunne ikke plassere sperring. Vennligst prøv igjen.',
            failed_release: 'Kunne ikke frigi sperring. Vennligst prøv igjen.',
            failed_fetch_events: 'Kunne ikke hente livssyklushendelser. Vennligst prøv igjen.',
            failed_create_event: 'Kunne ikke opprette livssyklushendelse. Vennligst prøv igjen.',
            failed_expired: 'Kunne ikke hente utløpte oppbevaringer. Vennligst prøv igjen.',
            hold_reason_prompt: 'Angi tilbageholdelsesårsak:',
            loaded_count: 'Lastet {count} livssyklusposter',
            stage_transition_for: 'Faseovergang for {id}',
            event_created: 'Opprettet',
            event_updated: 'Oppdatert',
            event_approved: 'Godkjent',
            event_deprecated: 'Utdatert',
            event_proposed: 'Foreslått',
            event_reviewed: 'Gjennomgått',
            event_archived: 'Arkivert',
            event_restored: 'Gjenopprettet',
        },
        gap_report: {
            title: 'Klassifiseringsavviksrapport',
            unlabeled: 'Umerkede data',
            missing_lifecycle: 'Manglende livssyklus',
            stale_labels: 'Utdaterte merkelapper',
            run_detection: 'Kjøring Mangel Deteksjon',
            detecting: 'Detekterer...',
            detection_complete: 'Mangelregistrering fuldført. {count} mangel(er) registreret.',
            detection_failed: 'Mangeldeteksjon mislyktes.',
            by_severity: 'Etter Alvorlighetsgrad',
            by_gap_type: 'Etter Mangel Type',
            all_gap_types: 'Alle Mangel Typer',
            conflicting_labels: 'Motstridende Etiketter',
            expired_retention: 'Utløpte Oppbevaring',
            missing_handling: 'Manglende Håndtering',
            info: 'Info',
            gap_type: 'Mangel Type',
            object_id: 'Objekt ID',
            object_type: 'Objekt Type',
            detected_at: 'Oppdaget den',
            open: 'Åpen',
            acknowledged: 'Bekreftet',
            remediated: 'Utbedret',
            accepted: 'Godtatt',
            acknowledge: 'Bekreft',
            remediate: 'Utbedre',
            accept_risk: 'Godta Risiko',
            accept_risk_prompt: 'Angi risiko aksept begrunnelse:',
            failed_fetch: 'Kunne ikke hente mangler. Vennligst prøv igjen.',
            failed_acknowledge: 'Kunne ikke bekrefte mangel. Vennligst prøv igjen.',
            failed_remediate: 'Kunne ikke utbedre mangel. Vennligst prøv igjen.',
            failed_accept_risk: 'Kunne ikke godta mangelrisiko. Vennligst prøv igjen.',
            failed_detect: 'Kunne ikke kjøre mangeldeteksjon. Vennligst prøv igjen.',
            loaded_count: 'Lastet {count} klassifiseringsmangler',
            related_object_id: 'Relatert objekt-ID',
            related_object_type: 'Relatert objekttype',
            gap_types: {
                unlabeled_asset: 'Umerket ressurs',
                missing_lifecycle: 'Manglende livssyklus',
                expired_retention: 'Utlopt oppbevaring',
                extended_pending_deletion: 'Utvidet ventende sletting',
            },
        },
        label_workflow: {
            title: 'Etikettarbeidsflyt',
            proposed: 'Foreslått',
            under_review: 'Under vurdering',
            approved: 'Godkjent',
            deprecated: 'Utgått',
            submit_review: 'Send til vurdering',
            approve: 'Godkjenn etikett',
            deprecate: 'Utgå',
            bulk_approve: 'Massegodkjenn',
            bulk_approve_success: 'Etiketter godkjent.',
            submit_success: 'Etikett sendt til vurdering.',
            approve_success: 'Etikett godkjent.',
            deprecate_success: 'Etikett utgått.',
        },
        review_tasks: {
            title: 'Vurderingsoppgaver',
            create: 'Ny vurderingsoppgave',
            system: 'System',
            owner: 'Eier',
            description: 'Beskrivelse',
            resolution: 'Løsning',
            resolve: 'Løs',
            approve: 'Godkjenn',
            modify: 'Endre',
            reject: 'Avvis',
            escalate: 'Eskaler',
            escalated: 'Eskalerte oppgaver',
            no_tasks: 'Ingen vurderingsoppgaver.',
            resolve_success: 'Vurderingsoppgave løst.',
            status_pending: 'Venter',
            status_resolved: 'Løst',
            status_escalated: 'Eskalert',
            notes: 'Notater',
            create_success: 'Gjennomgangsoppgave opprettet.',
            no_tasks_detail: 'Ingen gjennomgangsoppgaver ennå — opprett en oppgave for å tildele etikettgjennomganger til systemeiere.',
            label_id: 'Etikett-ID',
            system_id: 'System-ID',
            system_name: 'Systemnavn',
            owner_id: 'Eier-ID',
            resolution_col: 'Losning',
            notes_col: 'Notater',
        },
        exceptions: {
            title: 'Unntakskø',
            create: 'Nytt unntak',
            severity: 'Alvorlighetsgrad',
            system: 'System',
            owner: 'Eier',
            resolve: 'Løs',
            no_exceptions: 'Ingen unntak funnet.',
            resolve_success: 'Unntak løst.',
            dashboard_open: 'Åpne unntak',
            dashboard_resolved: 'Løste unntak',
            dashboard_total: 'Totalt',
            dashboard_by_severity: 'Etter alvorlighetsgrad',
            status_open: 'Åpen',
            status_resolved: 'Løst',
            status_accepted_risk: 'Akseptert risiko',
            create_success: 'Unntak opprettet.',
            no_exceptions_detail: 'Ingen unntak ennå — unntak opprettes fra alvorlige mangler som krever spesiell håndtering.',
            gap_id: 'Mangel-ID',
            system_id: 'System-ID',
            system_name: 'Systemnavn',
            owner_id: 'Eier-ID',
            resolution_notes: 'Løsningsmerknader',
            resolution_notes_placeholder: 'Beskriv hvordan dette unntaket ble løst...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Bedriftsdatakart',
            inventory: 'Dataoversikt',
            total_systems: 'Totalt antall systemer',
            total_labels: 'Totalt antall etiketter',
            approved_count: 'Godkjente etiketter',
            proposed_count: 'Foreslåtte etiketter',
            no_data: 'Ingen klassifiseringsdata tilgjengelig.',
            no_approved_labels: 'Ingen merkede objekter ennå — et objekt vises på datakartet så snart det har en klassifiseringsetikett, enten den er foreslått, under gjennomgang eller godkjent.',
            loaded_count: 'Lastet bedriftsdatakart',
        },
        discovery: {
            title: 'Oppdagelsessykluser',
            create: 'Ny syklus',
            cycle_name: 'Syklusnavn',
            run_delta: 'Kjør deltadeteksjon',
            delta_success: 'Deltadeteksjon fullført.',
            create_success: 'Oppdagelsessyklus opprettet.',
            no_cycles: 'Ingen oppdagelsessykluser.',
            status_created: 'Opprettet',
            status_scanning: 'Skanning',
            status_review: 'Gjennomgå',
            status_approved: 'Godkjent',
            status_published: 'Publisert',
            status_draft: 'Utkast',
            running: 'Kjører',
            completed: 'Fullført',
            cancelled: 'Avbrutt',
            delta_result_summary: 'Fant {added} lagt til, {removed} fjernet, {changed} endrede etiketter.',
            delta_results: 'Deltaresultater',
            view_results: 'Se resultater',
            scope_type: 'Omfang Type',
            scope_type_all: 'Alle',
            scope_type_systems: 'Systemer',
            scope_type_datasets: 'Datasett',
            scope_ids: 'Omfangs-IDer',
            scope_ids_placeholder: 'Skriv inn IDer atskilt med komma',
            cycle_explanation: 'En oppdagelsessyklus skanner datainfrastrukturen din for å identifisere, klassifisere og spore dataressurser. Konfigurer omfanget for å målrette mot spesifikke systemer eller datasett.',
            empty_state_title: 'Ingen oppdagelsessykluser ennå',
            empty_state_description: 'Lag din første oppdagelsessyklus for å begynne å skanne og klassifisere datamidlene dine.',
            empty_state_cta: 'Lag første syklus',
            validation_name_required: 'Syklusnavn er påkrevd',
            validation_scope_type_required: 'Omfangstype er påkrevd',
            validation_scope_ids_required: 'Minst én omfangs-ID kreves',
        },
        regulatory: {
            sensitivity_importance_info: 'Dataloven (lov 60/2024/QH15, krav. 18e4bfd1) krever klassifisering etter både sensitivitet og viktighet som separate dimensjoner. Opprett én taksonomi for hver dimensjon for å tilfredsstille dette kravet.',
            handling_rules_info: 'Hvert klassifiseringsnivå bør definere håndteringsregler per forespørsel. b89981ac ("Definer håndteringsregler etter klassifiseringsnivå").',
            template_internal_ids_info: 'Alle klassifiseringsmaler bruker interne IDer (f.eks. vn_data_law_data_classification_record). Ingen mal presenteres som et offisielt departementsskjema.',
            ai_risk_tiers_info: 'AI-loven (lov 134/2025/QH15, krav 3a87ad63) definerer fire risikonivåer for AI-systemer: minimal, begrenset, høy og uakseptabel. Lag en AI-risikotaksonomi med disse nivåene.',
            ai_risk_tier_minimal: 'Minimal',
            ai_risk_tier_limited: 'Begrenset',
            ai_risk_tier_high: 'Hoy',
            ai_risk_tier_unacceptable: 'Uakseptabel',
            ai_register_info: 'AI klassifiseringsposter bør kobles til en AI systemregisteroppføring (req. 0f4475d3) som fanger opp: systemnavn, eier, formål, distribusjonskontekst, risikonivå, leverandørinvolvering.',
            ai_template_internal_info: 'AI-klassifiseringsmaler bruker interne ID-er (f.eks. AI-RISK-01). Ingen offisielle AI-plassholder-ID-er (AI-HC-01 til AI-HC-08) brukes.',
            ai_audit_trail_info: 'AI klassifiseringsendringer produserer et versjonert revisjonsspor per UC-VN-06: aktør, tidsstempel, tidligere verdi, ny verdi og begrunnelse.',
            lifecycle_data_d1_info: 'Livssyklusoverganger kartlegger til Data Law-prosedyre DATA-D1 trinn: katalogisere, klassifisere, definere håndteringsregler, fange bevis, planlegge gjennomganger.',
            lifecycle_deletion_link_info: 'Et klassifisert dataelement som når slutten av levetiden, bør utløse en sletteplanpost via Deletion Orchestration-modulen.',
            lifecycle_evidence_info: 'Hver livssyklustilstandsendring (utkast, aktiv, undervurdering, arkivert, slettet) fanger opp aktør, tidsstempel og begrunnelse som en revisjonssporoppføring.',
            escalation_timers_info: 'UC-VN-10 eskaleringsfrister: 5 virkedager for eiervurdering, 48 timer for unntakseskalering, 72 timer for høyrisiko/sensitive elementer.',
            escalation_owner_review: '5 virkedager',
            escalation_exception: '48 timer',
            escalation_high_risk: '72 timer',
            gap_risk_areas_info: 'Datalovkrav. f6c3060a ("Datarisikovurdering") krever gapanalyse på tvers av 4 risikoområder: personvern, cybersikkerhet, tilgangskontroll og operasjonell risiko.',
            gap_risk_area_privacy: 'Personvern',
            gap_risk_area_cybersecurity: 'Cybersikkerhet',
            gap_risk_area_access: 'Tilgangskontroll',
            gap_risk_area_operational: 'Operasjonell',
            gap_framework_filter_info: 'Filtrer hull etter regelverk (VN_DATA_LAW eller VN_AI_LAW_2026) for å se relevante hull for hvert regelverk.',
            framework_vn_data_law: 'VN Datalov',
            framework_vn_ai_law: 'VN AI-lov 2026',
            enterprise_map_approved_info: 'Dette datakartet viser bare godkjente klassifiseringsposter per UC-VN-10 arbeidsflyttrinn 10 ("Godkjente etiketter blir gjeldende virksomhetssannhet"). Utkast og foreslåtte poster er ekskludert.',
            exports_bundle_info: 'Eksportpakker inkluderer: klassifiseringsregister, håndteringsregler, eierskap/RACI og bevispakke – samsvarende med UC-VN-10 eksportkrav og DATA-EVD-01 dokumentformat.',
        },
    },
    monitoring: {
        no_permission_title: 'Utilstrekkelige tillatelser',
        no_permission_message: 'Du har ikke tillatelse til a fa tilgang til denne overvakingsmodulen.',
        errors: {
            fetch: 'Kunne ikke laste.',
            save: 'Kunne ikke lagre.',
            fetch_programs_failed: 'Kunne ikke hente programmer.',
            fetch_kpi_failed: 'Kunne ikke hente KPI-data.',
            create_snapshot_failed: 'Kunne ikke opprette øyeblikksbilde.',
            fetch_periods_failed: 'Kunne ikke hente perioder.',
            create_period_failed: 'Kunne ikke opprette periode.',
            auto_generate_failed: 'Kunne ikke autogenerere perioder.',
            start_period_failed: 'Kunne ikke starte periode.',
            close_period_failed: 'Kunne ikke lukke periode.',
            save_program_failed: 'Kunne ikke lagre program.',
            activate_program_failed: 'Kunne ikke aktivere program.',
            pause_program_failed: 'Kunne ikke pause program.',
            complete_program_failed: 'Kunne ikke fullføre program.',
            archive_program_failed: 'Kunne ikke arkivere program.',
            fetch_ric_templates_failed: 'Kunne ikke hente RIC-maler.',
            fetch_ric_cases_failed: 'Kunne ikke hente RIC-saker.',
            create_ric_case_failed: 'Kunne ikke opprette RIC-sak.',
            transition_ric_failed: 'Kunne ikke overføre RIC-sak.',
            add_follow_up_failed: 'Kunne ikke legge til oppfølging.',
            respond_follow_up_failed: 'Kunne ikke svare på oppfølging.',
            link_evidence_failed: 'Kunne ikke koble bevis.',
            fetch_task_templates_failed: 'Kunne ikke hente oppgavemaler.',
            save_task_template_failed: 'Kunne ikke lagre oppgavemal.',
            delete_task_template_failed: 'Kunne ikke slette oppgavemal.',
            delete_framework_link_failed: 'Kunne ikke slette rammeverkskobling.',
            seed_defaults_failed: 'Kunne ikke initialisere standardverdier.',
            fetch_tasks_failed: 'Kunne ikke hente oppgaver.',
            create_task_failed: 'Kunne ikke opprette oppgave.',
            instantiate_tasks_failed: 'Kunne ikke instansiere oppgaver.',
            start_task_failed: 'Kunne ikke starte oppgave.',
            complete_task_failed: 'Kunne ikke fullføre oppgave.',
            defer_task_failed: 'Kunne ikke utsette oppgave.',
            block_task_failed: 'Kunne ikke blokkere oppgave.',
            override_complete_failed: 'Kunne ikke overstyre fullførelse.',
            assign_task_failed: 'Kunne ikke tildele oppgave.',
            update_checklist_failed: 'Kunne ikke oppdatere sjekkliste.',
        },
        nav: {
            programs: 'Programmer',
            periods: 'Perioder',
            task_templates: 'Oppgavemaler',
            tasks: 'Oppgaver',
            kpi: 'KPI-kontrollpanel',
            ric: 'Tilsynsforespørsler',
            framework_link: 'Rammeovervåking',
            authority_readiness: 'Myndighetsberedskap',
            change_review: 'Endringsgjennomgang',
            raci: 'RACI-matrise',
        },
        programs: {
            page_title: 'Overvåkingsprogrammer',
            title: 'Overvåkingsprogrammer',
            new: 'Nytt program',
            edit: 'Rediger program',
            name: 'Navn',
            scope: 'Omfang',
            frequency: 'Frekvens',
            owner: 'Eier',
            activate: 'Aktiver',
            pause: 'Sett på pause',
            resume: 'Gjenoppta',
            complete: 'Fullfør',
            archive: 'Arkiver',
            custom_frequency_days: 'Brukerdefineret frekvens (dager)',
            select_user: 'Velg bruker…',
            assign_training: 'Tildel opplæring',
            linked_forms: 'Tilknyttede skjemaer',
            linked_forms_none: 'Ingen tilknyttede skjemaer ennå',
            loaded_count: 'Lastet {0} programmer',
            scope_labels: {
                full: 'Full',
                partial: 'Delvis',
                targeted: 'Målrettet',
                consent: 'Samtykke',
                dpia: 'DPIA',
                vendors: 'Leverandører',
                classification: 'Klassifisering',
                rights_requests: 'Rettighetsforespørsler',
                data_mapping: 'Datakartlegging',
                transfers: 'Overføringer',
                security_monitoring: 'Sikkerhetsovervåking',
                authority_response: 'Myndighetssvar',
                evidence_preservation: 'Bevaring av bevis',
                localization_review: 'Lokaliseringsgjennomgang',
                ai_system_registration: 'AI Systemregistrering',
                ai_pre_deployment: 'AI vurdering før distribusjon',
                ai_transparency: 'AI Åpenhetssjekk',
                ai_incident_review: 'AI Gjennomgang av hendelsen',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Overvåkingsperioder',
            title: 'Gjennomgangsperioder',
            new: 'Ny periode',
            label: 'Periodeetikett',
            start_date: 'Startdato',
            end_date: 'Sluttdato',
            reviewers: 'Gjennomgangere',
            auto_generate: 'Autogenerer perioder',
            start: 'Start',
            close: 'Lukk',
            change_digest: 'Endringssammendrag',
            no_digest: 'Ingen sammendrag tilgjengelige.',
            reviewers_count: 'Antal gjennomgåere',
            select_program: 'Velg Program',
            select_program_prompt: 'Velg et program for at se perioder.',
            hide_digest: 'Skjul sammendrag',
            loaded_count: 'Lastet {0} perioder',
            periods_auto_generated: 'Perioder autogenerert',
            label_placeholder: 'f.eks. 2026-K1',
        },
        task_templates: {
            page_title: 'Oppgavemaler',
            title: 'Oppgavemaler',
            new: 'Ny mal',
            edit: 'Rediger mal',
            category: 'Kategori',
            priority: 'Prioritet',
            checklist_items: 'Sjekkliste-elementer',
            effort: 'Estimert innsats (timer)',
            pre_seeded: 'Forhåndsinnlagt',
            seed_defaults: 'Innlegg standarder',
            add_checklist: 'Legg til sjekkliste-element',
            guidance: 'Veiledningsnotater',
            all_categories: 'Alle Kategorier',
            loaded_count: 'Lastet {0} oppgavemaler',
            defaults_seeded: 'Standardoppgavemaler opprettet',
            checklist_item_label: 'Sjekklistepunkt {0}',
            remove_checklist_item: 'Fjern sjekklistepunkt {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Gjennomgangsoppgaver',
            title: 'Gjennomgangsoppgaver',
            new: 'Ny oppgave',
            assigned_to: 'Tildelt til',
            due_date: 'Forfallsdato',
            checklist: 'Sjekkliste',
            start: 'Start',
            complete: 'Fullfør',
            defer: 'Utsett',
            block: 'Blokker',
            override_complete: 'Overstyr fullføring',
            link_evidence: 'Knytt dokumentasjon',
            direct_upload_label: 'Last opp bevisfil',
            instantiate: 'Opprett fra maler',
            defer_reason: 'Begrunnelse for utsettelse',
            override_justification: 'Begrunnelse for overstyring',
            filter_assignee: 'Filtrer efter modtager',
            select_period: 'Velg Periode',
            select_program: 'Velg Program',
            select_program_period_prompt: 'Velg et program og en periode for at se oppgaver.',
            assign_placeholder: 'Tildel to...',
            loaded_count: 'Lastet {0} oppgaver',
            tasks_instantiated: 'Oppgaver opprettet fra maler',
            overdue: 'Forfalt',
            checklist_for: 'sjekkliste for',
            manage_checklist: 'Administrer sjekkliste',
            add_checklist_item: 'Legg til element',
            checklist_item_label: 'Element',
            checklist_item_description: 'Beskrivelse',
            remove_checklist_item: 'Fjern element',
            save_checklist: 'Lagre sjekkliste',
            no_checklist_items: 'Ingen sjekklisteelementer ennå.',
            confirm_delete_attachment: 'Er du sikker på at du vil slette denne bevisfilen? Dette kan ikke angres.',
        },
        kpi: {
            page_title: 'KPI-oversikt',
            title: 'KPI-kontrollpanel',
            kpi_name: 'KPI-navn',
            current_value: 'Gjeldende verdi',
            target: 'Mål',
            trends: 'Trender',
            capture_snapshot: 'Ta Øyeblikksbilde',
            select_program: 'Velg Program',
            select_program_prompt: 'Velg et program for at se KPI-er.',
            snapshot: 'Øyeblikksbilde',
            kpi_labels: {
                on_track: 'På Spor',
                at_risk: 'Ved Risiko',
                behind: 'Bak',
                rights_request_sla_rate: 'SLA-frekvens for rettighetsforespørsler',
                vendor_review_currency: 'Leverandørgjennomgang aktualitet',
                dpia_completion_rate: 'DPIA-fullføringsgrad',
                classification_coverage: 'Klassifiseringsdekning',
                consent_policy_currency: 'Samtykkepolicy aktualitet',
                security_event_detection_rate: 'Deteksjonsfrekvens for sikkerhetshendelser',
                evidence_preservation_compliance_rate: 'Samsvarsgrad for bevisbevaring',
                authority_response_timeliness: 'Myndighetsrespons aktualitet',
                localization_readiness_score: 'Score for lokaliseringsberedskap',
                ai_register_completeness: 'AI Systemregisterets fullstendighet',
                pre_deployment_gate_pass_rate: 'Gatepasseringsrate før distribusjon',
                transparency_disclosure_coverage: 'Dekning for åpenhet',
                ai_incident_readiness_score: 'AI Hendelsesberedskapspoeng',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'KPI-er for cybersikkerhetslovgivning: Deteksjonsfrekvens for sikkerhetshendelser, overholdelsesgrad for bevisoppbevaring, aktualitet for myndighetenes svar, poeng for lokaliseringsberedskap.',
            ai_law_kpi_info: 'KPI-er for AI-loven: fullstendighet i AI-systemregisteret, gatepasseringsrate før utrulling, dekning for åpenhetsopplysninger, beredskapspoeng for AI-hendelser.',
            no_data: 'Ingen data tilgjengelige.',
            snapshot_date: 'Øyeblikksbilde Dato',
            loaded_count: 'Lastet {0} KPI-øyeblikksbilder',
            snapshot_captured: 'KPI-øyeblikksbilde tatt',
            showing_chart: 'Viser diagramvisning',
            showing_table: 'Viser datatabellvisning',
            aria_current_values: 'KPI nåværende verdier',
            kpi_label: 'KPI',
            actual: 'Faktisk',
            trend: 'Trend',
            notes: 'Notater',
            trend_up: 'Stigende',
            trend_down: 'Synkende',
            trend_stable: 'Stabil',
        },
        ric: {
            page_title: 'RIC-saker',
            title: 'Tilsynsforespørsler',
            new: 'Ny tilsynssak',
            reference: 'Referanse',
            regulator: 'Tilsynsmyndighet',
            inquiry_type: 'Forespørselstype',
            deadline: 'Svarfrist',
            lead: 'Ansvarlig',
            follow_ups: 'Oppfølginger',
            add_follow_up: 'Legg til oppfølging',
            respond: 'Besvar',
            question: 'Spørsmål',
            response: 'Svar',
            asked_at: 'Spurt den',
            no_follow_ups: 'Ingen follow-ups ennå.',
            received_date: 'Mottatt dato',
            responded_at: 'Besvaret den',
            hide_follow_ups: 'Skjul Oppfølginger',
            inquiry_types: {
                general: 'Generelt',
                investigation: 'Etterforskning',
                audit: 'Revisjon',
                complaint: 'Klage',
                general_audit: 'Generell revisjon',
                rights_requests_review: 'Gjennomgang av rettighetsforespørsler',
                cross_border_transfer_review: 'Gjennomgang av grenseoverskridende overføringer',
                vendor_governance_review: 'Gjennomgang av leverandørstyring',
                consent_management_review: 'Gjennomgang av samtykkehåndtering',
                data_mapping_ropa_review: 'Gjennomgang av datakartlegging / ROPA',
                incident_readiness_review: 'Gjennomgang av hendelseberedskap',
                security_access_control_review: 'Gjennomgang av sikkerhet og tilgangskontroll',
                targeted_processing_review: 'Gjennomgang av målrettet behandling',
                bank_readiness_pack: 'Bankberedskapspakke',
            },
            link_evidence: 'Koble Bevis',
            loaded_count: 'Lastet inn {0} RIC-saker',
            to_follow_up: 'til oppfølgingsspørsmål',
            overdue: 'Forfalt',
            overdue_indicator: 'Svarfristen er overskredet',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Mottatt',
                in_triage: 'Under triage',
                evidence_gathering: 'Bevisinnsamling',
                response_drafting: 'Svarutkast',
                internal_review: 'Intern gjennomgang',
                submitted: 'Innsendt',
                follow_up: 'Oppfølging',
                closed: 'Lukket',
            },
        },
        framework_link: {
            title: 'Rammelinket overvåking',
            link_program: 'Koble program til rammeverk',
            framework_code: 'Rammekode',
            select_framework: 'Velg et rammeverk...',
            select_program: 'Velg et program...',
            readiness_score: 'Beredskapsscore',
            no_links: 'Ingen rammelinkede programmer funnet',
            seed_defaults: 'Opprett standardmaler',
            program_id: 'Program-ID',
            program: 'Program',
        },
        authority_readiness: {
            title: 'Myndighetsforespørselsberedskap',
            create_entry: 'Ny beredskapsoppføring',
            select_framework: 'Velg et rammeverk...',
            framework_code: 'Rammeverk',
            readiness_status: 'Beredskapsstatus',
            readiness_package: 'Beredskapspakke',
            run_drill: 'Kjør beredskapsøvelse',
            drill_success: 'Beredskapsøvelse registrert',
            last_drill: 'Siste øvelse',
            never_drilled: 'Aldri',
            no_readiness: 'Ingen myndighetsberedskapsdata funnet',
            entry_created: 'Beredskapsoppføring opprettet',
            ready: 'Klar',
            partial: 'Delvis',
            not_ready: 'Ikke klar',
            items: 'elementer',
        },
        change_review: {
            title: 'Regulatorisk endringsgjennomgang',
            new_review: 'Ny endringsgjennomgang',
            change_id: 'Endrings-ID',
            select_change: 'Velg en endring...',
            select_framework: 'Velg et rammeverk...',
            impact_assessment: 'Konsekvensvurdering',
            decision: 'Beslutning',
            no_action: 'Ingen handling',
            internal_update: 'Intern oppdatering',
            authority_filing: 'Myndighetsinnlevering',
            no_reviews: 'Ingen endringsgjennomganger funnet',
            auto_cycle: 'Automatisk gjennomgangssyklus',
            cadence: 'Gjennomgangsfrekvens',
            framework: 'Rammeverk',
        },
        export_csv: 'Eksporter CSV',
        exporting: 'Eksporterer …',
        export_success: 'CSV ble eksportert.',
        export_failed: 'CSV eksport mislyktes.',
        export_attestation_pack: 'Eksporter attestasjonspakke',
        staleness: {
            badge_label: '{0} foreldede varer',
            days_since: '{0} dager siden siste oppdatering',
            detail_title: 'Foreldede gjenstander',
            accept_justification: 'Godta med begrunnelse',
            justification_label: 'Begrunnelse',
            no_stale: 'Ingen foreldede varer funnet.',
            threshold_label: 'Staleness-terskel (dager)',
            threshold_min: 'Minimum 30 dager',
            threshold_max: 'Maksimalt 730 dager',
            threshold_saved: 'Staleness-terskel oppdatert.',
        },
        csv_columns: {
            name: 'Navn',
            status: 'Status',
            owner: 'Eier',
            created_at: 'Opprettet kl',
            updated_at: 'Oppdatert kl',
            scope: 'Omfang',
            frameworks: 'Rammer',
            period_label: 'Periodemerke',
            start_date: 'Startdato',
            end_date: 'Sluttdato',
            task_count: 'Oppgavetelling',
            completion_rate: 'Gjennomføringsgrad',
            title: 'Tittel',
            assigned_to: 'Tildelt til',
            due_date: 'Forfallsdato',
            priority: 'Prioritet',
            completed_at: 'Fullført kl',
            kpi_code: 'KPI Kode',
            kpi_label: 'KPI Etikett',
            target: 'Mål',
            actual: 'Faktisk',
            trend: 'Trend',
            reference: 'Referanse',
            regulator: 'Regulator',
            inquiry_type: 'Forespørselstype',
            lead: 'Bly',
            received_date: 'Mottatt dato',
            deadline: 'Frist',
            framework_code: 'Rammekode',
            readiness_status: 'Beredskapsstatus',
            package_count: 'Antall pakker',
            change_id: 'Endre ID',
            impact_assessment: 'Konsekvensutredning',
            decision: 'Beslutning',
            program: 'Program',
            readiness_score: 'Beredskapsscore',
            category: 'Kategori',
            checklist_count: 'Antall sjekklister',
            effort_hours: 'Innsats (timer)',
            version: 'Versjon',
            department: 'Avdeling',
            function_area: 'Funksjonsområde',
            role: 'Rolle',
            assignee: 'Oppdragstaker',
            frequency: 'Frekvens',
        },
        raci: {
            page_title: 'Styring RACI Matrix',
            title: 'Styring RACI Matrix',
            department: 'Avdeling',
            function_area: 'Funksjonsområde',
            responsible: 'Ansvarlig',
            accountable: 'Ansvarlig',
            consulted: 'Konsultert',
            informed: 'Informert',
            assign_role: 'Tildel RACI-rolle',
            select_program: 'Velg Program',
            select_program_prompt: 'Velg et program for å vise RACI-matrise.',
            no_assignments: 'Ingen RACI-oppdrag funnet.',
            role_updated: 'RACI-oppgaven er oppdatert.',
            role_labels: {
                R: 'Ansvarlig',
                A: 'Ansvarlig',
                C: 'Konsultert',
                I: 'Informert',
            },
            loaded_count: 'Lastet inn {0} RACI-oppdrag',
            single_accountability_warning: 'Bare én ansvarlig (A) rolle er tillatt per funksjonsområde. Å tildele en ny ansvarlig rolle vil erstatte den eksisterende.',
            cybersec_function_areas: 'Funksjonsområder for cybersikkerhetslov: Hendelsesrespons, bevisbevaring, myndighetssamarbeid, lokaliseringsgjennomgang.',
            ai_law_function_areas: 'AI Juss funksjonsområder: Systemregistrering, Pre-Deployment Assessment, Transparency, Incident Handling.',
            suggested_function_areas: 'Foreslåtte funksjonsområder',
        },
        prior_cycle: {
            link_label: 'Link til forrige syklus',
            select_prior: 'Velg forrige syklus...',
            linked: 'År over år',
            no_prior: 'Ingen tidligere sykluser tilgjengelig.',
            linked_success: 'Tidligere syklus koblet.',
        },
        digest_table: {
            module: 'Modul',
            entity_type: 'Enhetstype',
            change_type: 'Endre type',
            description: 'Beskrivelse',
            changed_at: 'Endret kl',
        },
        view_related_ric: 'Se relaterte RIC-saker',
        linked_program: 'Koblet program',
        nav_raci: 'RACI-matrise',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Utkast til svar',
            drafting: 'Utkaster …',
            results_heading: 'Utkast til svar ({count})',
            tokens_label: '≈ {count} tokens',
            counsel_label: 'Rådgivergjennomgang kreves:',
            counsel_default: 'Minst ett svar krever advokatgjennomgang før sending.',
            requires_counsel_label: 'Krever advokatgjennomgang.',
            accept_label: 'Godta utkast',
            reject_label: 'Avvis utkast',
            error_generic: 'Kunne ikke skrive utkast til svaret. Prøv igjen eller avgrens forespørselsteksten.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Tidslinje for varsel om databrudd',
            computing: 'Databehandling …',
            results_heading: 'Brudd varslingsfrister ({count})',
            tokens_label: '≈ {count} tokens',
            handoff_label: 'Forseti foreslår:',
            empty_label: 'Ingen installerte rammeverk angir en varslingsplikt for denne hendelsen.',
            mandatory_label: 'Obligatoriske felter:',
            draft_label: 'Varslingsutkast',
            remaining_label: '{count}t igjen',
            overdue_label: 'FORSIDEN innen {count}h',
            acknowledge_label: 'Bekreft tidslinjen',
            acknowledging: 'Erkjenner …',
            error_generic: 'Kunne ikke beregne tidslinjen for brudd. Prøv igjen.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Bekreftet Brudd',
            no: 'Ikke en Brudd',
            uncertain: 'Ventende',
        },
        errors: {
            fetch: 'Kunne ikke laste hendelser.',
            save: 'Kunne ikke lagre hendelse.',
            fetch_failed: 'Kunne ikke hente hendelser.',
            save_failed: 'Kunne ikke lagre hendelse.',
            transition_failed: 'Kunne ikke overføre hendelse.',
            close_failed: 'Kunne ikke lukke hendelse.',
            fetch_timeline_failed: 'Kunne ikke hente tidslinje.',
            fetch_decisions_failed: 'Kunne ikke hente beslutninger.',
            triage_failed: 'Kunne ikke oppdatere triage.',
            impact_update_failed: 'Kunne ikke oppdatere konsekvensvurdering.',
            breach_update_failed: 'Kunne ikke oppdatere bruddevaluering.',
            create_timeline_failed: 'Kunne ikke opprette tidslinjeoppføring.',
            create_decision_failed: 'Kunne ikke opprette beslutning.',
            notification_update_failed: 'Kunne ikke oppdatere varsling.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Kunne ikke hente oppgaver.',
            create_task_failed: 'Kunne ikke opprette oppgave.',
            update_task_status_failed: 'Kunne ikke oppdatere oppgavestatus.',
            update_checklist_failed: 'Kunne ikke oppdatere sjekkliste.',
            assign_task_failed: 'Kunne ikke tildele oppgave.',
            escalate_task_failed: 'Kunne ikke eskalere oppgave.',
            fetch_templates_failed: 'Kunne ikke hente maler.',
            permission_denied: 'Du har ikke tilgang til denne seksjonen. Kontakt administratoren din for a be om tilgang.',
        },
        harm_likelihood: {
            unlikely: 'Usannsynlig',
            possible: 'Mulig',
            likely: 'Sannsynlig',
            very_likely: 'Svært sannsynlig',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-post',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Tredje Part',
            automated_detection: 'Automatisert Deteksjon',
        },
        task_transition_labels: {
            start: 'Start',
            complete: 'Fullfør',
            block: 'Blokker',
            defer: 'Utsett',
            cancel: 'Avbryt',
            unblock: 'Opphev blokkering',
        },
        transition_labels: {
            start: 'Start',
            complete: 'Fullfør',
            block: 'Blokker',
            defer: 'Utsett',
        },
        nav: {
            all: 'Alle hendelser',
            tasks: 'Responsoppgaver',
            reports: 'Rapporter',
            breach_notifications: 'Bruddvarsler',
            supplements: 'Tillegg',
            remediation: 'Utbedring',
            authority_requests: 'Myndighetsforespørsler',
            disclosure_register: 'Offentliggjøringsregister',
        },
        list: {
            page_title: 'Hendelser',
            title: 'Hendelser',
            new: 'Ny hendelse',
            edit: 'Rediger hendelse',
            incident_title: 'Tittel',
            severity: 'Alvorlighetsgrad',
            reported_at: 'Rapportert',
            reported_by: 'Rapportert av',
            assigned_to: 'Tildelt til',
            view_detail: 'Vis detaljer',
            reference: 'Referanse',
            lead: 'Leder',
            reported_via: 'Rapportert Via',
            discovery_date: 'Oppdagelse Dato',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Skjul detaljer',
            close_incident: 'Lukk Hendelse',
            close_justification_prompt: 'Oppgi vennligst en begrunnelse for at lukke denne hendelse.',
            justification: 'Begrunnelse',
            lead_id: 'Leder ID',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Kontakt',
            category: 'Kategori',
            not_assessed: 'Ikke vurdert',
        },
        detail: {
            page_title: 'Hendelsesdetaljer',
            title: 'Hendelsesdetaljer',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Oversikt',
                triage: 'Triagering',
                impact: 'Konsekvensanalyse',
                breach: 'Bruddvurdering',
                timeline: 'Tidslinje',
                decisions: 'Beslutninger',
                evidence: 'Bevis',
                audit_trail: 'Revisjonssti',
            },
            evidence_upload_label: 'Legg ved bevisfil',
            evidence_uploaded: 'Bevis lastet opp',
            evidence_deleted: 'Bevis slettet',
            evidence_delete_error: 'Kunne ikke slette beviset',
            evidence_uploaded_at: 'Lastet opp',
            evidence_empty: 'Ingen bevisfiler vedlagt denne hendelsen.',
            evidence_size: 'Størrelse',
            triage_notes: 'Triagenotater',
            initial_classification: 'Første klassifisering',
            individuals_affected: 'Antall berørte personer',
            data_categories: 'Involverte datakategorier',
            systems_compromised: 'Kompromitterte systemer',
            geographic_scope: 'Geografisk omfang',
            breach_determination: 'Bruddavgjørelse',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Varsling påkrevd',
            notification_deadline: 'Varslingsfrist',
            pdpc_reportable: 'Meldepliktig til Datatilsynet',
            add_timeline: 'Legg til tidslinjehendelse',
            add_decision: 'Legg til beslutning',
            not_found: 'Hendelse ikke fundet.',
            team_members: 'Team Medlemmer',
            checklist: 'Sjekkliste',
            completed: 'Fullført',
            impact_severity_score: 'Beregnet alvorlighetsscore for konsekvens',
            records_affected: 'Poster Berørte',
            potential_consequences: 'Potensielle Konsekvenser',
            harm_likelihood: 'Skade Sannsynlighet',
            legal_basis: 'Rettslig grunnlag',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdiksjoner',
            jurisdictions_comma: 'Jurisdiksjoner (kommaseparert)',
            jurisdiction: 'Jurisdiksjon',
            authority_status: 'Myndighet Status',
            subject_status: 'Emne Status',
            authority_deadline: 'Myndighet Frist',
            subject_deadline: 'Emne Frist',
            mark_authority_done: 'Marker myndighed færdig',
            mark_subject_done: 'Marker emne færdig',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Ikke vurdert',
            event_type: 'Hendelse Type',
            timestamp: 'Tidsstempel',
            actor: 'Aktør',
            no_timeline_events: 'Ingen tidslinje hendelser ennå.',
            decision_type: 'Beslutning Type',
            decision: 'Beslutning',
            rationale: 'Begrunnelse',
            decision_made: 'Beslutning tatt',
            no_decisions: 'Ingen decisions recorded ennå.',
            no_audit_events: 'Ingen revisjonshendelser registrert ennå.',
            export_pdf: 'Eksporter PDF',
            pdf_title: 'Hendelsessammendrag',
            description: 'Beskrivelse',
            linked_records: 'Tilknyttede Poster',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Hendelsesresponsoppgaver',
            title: 'Hendelsesresponsoppgaver',
            new: 'Ny oppgave',
            edit: 'Rediger oppgave',
            task_type: 'Oppgavetype',
            assignee: 'Ansvarlig',
            due_date: 'Forfallsdato',
            dependencies: 'Avhengigheter',
            mark_complete: 'Merk som fullført',
            templates: 'Oppgavemaler',
            select_incident: 'Velg hendelse',
            all_statuses: 'Alle statuser',
            all_categories: 'Alle kategorier',
            new_response_task: 'Ny responsoppgave',
            category: 'Kategori',
            priority: 'Prioritet',
            depends_on: 'Avhenger av (kommaseparerte oppgave-ID-er)',
            checklist_items: 'Sjekkliste-elementer',
            remove: 'Fjern',
            add: 'Legg til',
            new_checklist_item: 'Nytt sjekkliste-element',
            no_templates: 'Ingen maler tilgjengelig.',
            assign_task: 'Tildel oppgave',
            assign_to: 'Tildel til',
            assign: 'Tildel',
            select_incident_prompt: 'Vennligst velg en hendelse for å se dens responsoppgaver.',
            checklist: 'Sjekkliste',
            hide_checklist: 'Skjul sjekkliste',
            escalate: 'Eskaler',
            escalations: 'Eskaleringer',
            no_checklist_items: 'Ingen sjekkliste-elementer.',
        },
        reports: {
            page_title: 'Hendelsesrapporter',
            title: 'Hendelsesrapporter',
            summary: 'Sammendragsstatistikk',
            by_severity: 'Etter alvorlighetsgrad',
            by_status: 'Etter status',
            recent: 'Nylige hendelser',
            total_incidents: 'Totalt antall hendelser',
            open: 'Åpne',
            closed: 'Lukkede',
            export_csv: 'Eksporter CSV',
            export_pdf: 'Eksporter PDF',
            export_success: 'Rapport eksportert.',
            export_error: 'Kunne ikke eksportere rapport.',
            reference: 'Referanse',
            view_report: 'Vis rapport',
        },
        statuses: {
            reported: 'Rapportert',
            triage: 'Triagering',
            contained: 'Begrenset',
            investigation: 'Etterforskning',
            remediation: 'Utbedring',
            closed: 'Lukket',
            false_positive: 'Falsk positiv',
        },
        task_statuses: {
            pending: 'Ventende',
            in_progress: 'Pågår',
            completed: 'Fullført',
            blocked: 'Blokkert',
            cancelled: 'Avbrutt',
        },
        severity: {
            low: 'Lav',
            medium: 'Middels',
            high: 'Høy',
            critical: 'Kritisk',
        },
        task_types: {
            containment: 'Begrensning',
            investigation: 'Etterforskning',
            remediation: 'Utbedring',
            communication: 'Kommunikasjon',
            documentation: 'Dokumentasjon',
            notification: 'Varsling',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Data Brudd',
            unauthorized_access: 'Uautorisert Tilgang',
            data_loss: 'Data Tap',
            system_compromise: 'System Kompromittering',
            malware: 'Skadevare',
            phishing: 'Nettsvindel',
            insider_threat: 'Innsidetrussel',
            other: 'Annet',
        },
        reported_via_options: {
            email: 'E-post',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Tredje Part',
            automated_detection: 'Automatisert Deteksjon',
        },
        harm_likelihood_options: {
            unlikely: 'Usannsynlig',
            possible: 'Mulig',
            likely: 'Sannsynlig',
            very_likely: 'Svært sannsynlig',
        },
        breach_determination_options: {
            confirmed_breach: 'Bekreftet Brudd',
            suspected_breach: 'Mistenkt Brudd',
            not_a_breach: 'Ikke en Brudd',
            pending: 'Ventende',
        },
        timeline_event_types: {
            detection: 'Deteksjon',
            escalation: 'Eskalering',
            containment: 'Begrensning',
            investigation_start: 'Etterforskning Start',
            evidence_collected: 'Bevis Innsamlet',
            notification_sent: 'Varsling Sendt',
            remediation_action: 'Utbedring Handling',
            status_change: 'Status Endrings',
            communication: 'Kommunikasjon',
            other: 'Annet',
        },
        decision_types: {
            containment: 'Begrensning',
            notification: 'Varsling',
            remediation: 'Utbedring',
            escalation: 'Eskalering',
            closure: 'Lukking',
            other: 'Annet',
        },
        task_transitions: {
            start: 'Start',
            complete: 'Fullfør',
            block: 'Blokker',
            cancel: 'Avbryt',
            unblock: 'Opphev blokkering',
        },
        announce: {
            loaded: '{count} hendelser lastet inn',
            reports_loaded: '{count} hendelsesrapporter er lastet inn',
            tasks_loaded: '{count} oppgaver lastet',
            created: 'Hendelsen ble opprettet',
            updated: 'Hendelsen ble oppdatert',
            closed: 'Hendelsen ble avsluttet',
            status_changed: 'Hendelsesstatus endret',
            triage_completed: 'Triage fullført',
            impact_updated: 'Konsekvensutredning oppdatert',
            breach_updated: 'Bruddvurdering oppdatert',
            timeline_added: 'Tidslinjehendelse lagt til',
            decision_recorded: 'Beslutning registrert',
            notification_updated: 'Varslingsstatus oppdatert',
            task_created: 'Oppgave opprettet',
            task_status_changed: 'Oppgavestatus endret',
            checklist_completed: 'Sjekklisteelementet fullført',
            checklist_unchecked: 'Sjekklisteelementet er avmerket',
            task_assigned: 'Oppgaven ble tildelt',
            task_escalated: 'Oppgaven eskalerte',
            export_initiated: 'Eksporten startet',
        },
        breach_notifications: {
            page_title: 'Bruddvarsler',
            title: 'Bruddvarsler',
            new_notification: 'Ny melding',
            no_notifications: 'Ingen bruddvarsler funnet',
            status: 'Status',
            submit_to_mps: 'Send til MPS',
            decision: 'Beslutning',
            decision_maker: 'Beslutningstaker',
            notify: 'Varsle',
            do_not_notify: 'Ikke gi beskjed',
            rationale: 'Begrunnelse',
            filing_deadline: 'Innleveringsfrist',
            overdue: 'Forfalt',
            due_soon: 'Snart forfalt',
            on_track: 'På rett spor',
            legal_review: 'Juridisk gjennomgang',
            incident_id: 'Hendelses-ID',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Vurdering',
                decision_to_notify: 'Beslutning om å varsle',
                notification_drafted: 'Melding er utarbeidet',
                legal_review: 'Juridisk gjennomgang',
                submitted_to_authority: 'Sendt til myndighet',
                supplement_requested: 'Tillegg forespurt',
                supplement_submitted: 'Tillegg innsendt',
                acknowledged: 'Erkjent',
                closed: 'Stengt',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Tilleggsforespørsler',
            title: 'Tilleggsforespørsler',
            new_supplement: 'Nytt tillegg',
            no_supplements: 'Fant ingen tilleggsforespørsler',
            request_details: 'Be om detaljer',
            response_package: 'Svarpakke',
            submit_response: 'Send inn svar',
            deadline: 'Frist',
            notification_id: 'Varslings-ID',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Skriv inn en varslings-ID for å se tillegg.',
            supplement_statuses: {
                requested: 'Forespurt',
                drafting: 'Utkast',
                submitted: 'Sendt inn',
                accepted: 'Godtatt',
                rejected: 'Avvist',
            },
        },
        remediation: {
            page_title: 'Utbedringstiltak',
            title: 'Utbedringstiltak',
            new_action: 'Ny handling',
            no_actions: 'Fant ingen utbedringshandlinger',
            description: 'Beskrivelse',
            owner: 'Eier',
            department: 'Avdeling',
            deadline: 'Frist',
            statuses: {
                planned: 'Planlagt',
                in_progress: 'Pågår',
                completed: 'Fullført',
                verified: 'Verifisert',
            },
            verify: 'Bekreft',
            incident_id: 'Hendelses-ID',
            enter_incident_id: 'Angi en hendelses-ID for å se utbedringshandlinger.',
            start: 'Start',
            complete_action: 'Fullfør',
        },
        authority_requests: {
            page_title: 'Myndighetsforespørsler',
            title: 'Myndighetsforespørsler',
            new_request: 'Ny forespørsel',
            no_requests: 'Ingen myndighetsforespørsler funnet',
            authority_name: 'Autoritetsnavn',
            request_details: 'Be om detaljer',
            response_package: 'Svarpakke',
            submission_channel: 'Sendingskanal',
            submit_response: 'Send inn svar',
            deadline: 'Frist',
            confirmation: 'Bekreftelse',
            incident_id_optional: 'Hendelses-ID (valgfritt)',
            response_context_label: 'Svarkontekst (for materialisering av innsendingsdokument)',
            response_context_none: '(hopp over materialisering)',
            response_context_supplement: 'Supplerende svar',
            response_context_information: 'Svar på informasjonsforespørsel',
            response_context_inspection: 'Svar på tilsyn',
            materialisation_status_column: 'Innsendingsdokument',
            materialisation_pending: 'Venter',
            materialisation_success: 'Materialisert',
            materialisation_failed: 'Feilet',
            materialisation_retry_button: 'Prøv igjen',
            ar_statuses: {
                received: 'Mottatt',
                verified: 'Verifisert',
                assembling: 'Montering',
                submitted: 'Innsendt',
                closed: 'Stengt',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Juridisk gjennomgang',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Sjekkliste',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Opplysningsregister',
            title: 'Opplysningsregister',
            new_entry: 'Ny oppføring',
            no_entries: 'Ingen oppføringer i opplysningsregisteret ble funnet',
            disclosure_type: 'Avsløringstype',
            disclosure_date: 'Offentliggjøringsdato',
            recipient: 'Mottaker',
            authority_name: 'Autoritetsnavn',
            channel: 'Kanal',
            content_summary: 'Innholdssammendrag',
            incident_id_optional: 'Hendelses-ID (valgfritt)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Oppsettveiviser',
            tours: 'Modulomvisninger',
            sample_data: 'Eksempeldata',
        },
        wizard: {
            title: 'Oppsettveiviser',
            welcome: 'Velkommen til AesirX ComplianceOne',
            welcome_message: 'La oss sette opp organisasjonen din. Denne veiviseren vil guide deg gjennom den innledende konfigurasjonen.',
            setup_complete: 'Oppsett fullført',
            skipped_message: 'Oppsettveiviseren ble hoppet over. Du kan kjøre den når som helst for å konfigurere organisasjonen din.',
            start_setup: 'Start oppsett',
            skip_wizard: 'Hopp over veiviser',
            go_to_admin: 'Gå til Admin',
            skip_for_now: 'Hopp over for nå',
            next_step: 'Neste trinn',
            previous_step: 'Forrige trinn',
            finish_setup: 'Fullfør oppsett',
            rerun: 'Kjør oppsettveiviseren på nytt',
            step_label: 'Trinn {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organisasjonsnavn er påkrevd.',
            validation_admin_email_required: 'Administrator-e-post er påkrevd.',
            validation_admin_email_invalid: 'Vennligst oppgi en gyldig e-postadresse.',
            validation_cmp_endpoint_required: 'CMP-endepunkt er påkrevd når CMP er aktivert.',
            steps: {
                license_activation: 'Lisensaktivering',
                org_profile: 'Organisasjonsprofil',
                module_selection: 'Modulvalg',
                cmp_integration: 'CMP-integrasjon',
                notification_preferences: 'Varsler',
                review: 'Gjennomgang og bekreftelse',
            },
            step_descriptions: {
                license_activation: 'Skriv inn lisensnøkkelen din for å aktivere pakken din og finne ut hvilke moduler som er tilgjengelige.',
                org_profile: 'Konfigurer organisasjonsnavn, jurisdiksjon, bransje, størrelse og foretrukket språk.',
                module_selection: 'Velg hvilke samsvarsmoduler som skal aktiveres for organisasjonen din.',
                cmp_integration: 'Konfigurer integrasjon med Consent Management Platform (valgfritt).',
                notification_preferences: 'Konfigurer hvordan du mottar varsler om samsvarshendelser.',
                review: 'Gjennomgå konfigurasjonen din og fullfør oppsettet.',
            },
            license_step: {
                validate_button: 'Bekreft lisens',
                skip_warning: 'Modultilgang vil være begrenset til standard pakkenivå. Du kan aktivere en lisens senere i Plattform > Lisensinnstillinger.',
            },
            module_descriptions: {
                rights_requests: 'Håndter forespørsler om tilgang til og sletting av persondata.',
                data_mapping: 'Kartlegg systemer, dataflyter og behandlingsaktiviteter.',
                governance: 'Før tilsyn med leverandører, godkjenninger, avvik og dokumentasjonsregistrene.',
                consent: 'Administrer samtykkepolicyer og formålskataloger.',
                consent_properties: 'Administrer samtykke på tvers av digitale eiendommer.',
                assessments: 'Gjennomfør konsekvensutredninger og grensekryssende dataoverføringer.',
                classification: 'Klassifiser og merk dataaktiva med livssyklussporing.',
                monitoring: 'Planlagte samsvarsgjennomganger og KPI-sporing.',
                incidents: 'Rapporter og håndter sikkerhets- og personvernhendelser.',
                connectors: 'Koble til eksterne systemer for automatisert datautveksling.',
                vendor_risk: 'Scorer leverandører, spor kontrakter og administrer due diligence.',
                deletion_orchestration: 'Koordiner datasletting på tvers av systemer og leverandører.',
                discovery: 'Oppdag og klassifiser data på tvers av infrastrukturen din.',
                incident_ops: 'Avanserte hendelsesplaner, SLA-sporing og eskaleringer.',
                program_governance: 'Modenhetsvurderinger, ledelsesrapportering og revisjonsberedskap.',
                access_accountability: 'Overvåk brukertilgang, sesjoner og begrunnelsesarbeidsflyter.',
                localization: 'Markedsspesifikke samsvarspakker og regulatoriske rammeverk.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Partner- og forhandlerarbeidsområde.',
                privacy_scanner: 'Sanntids personvernovervåking av digitale eiendommer.',
                onboarding: 'Oppsettsveileder, guidede turer og administrasjon av eksempeldata.',
                compliance_forms: 'Interaktive samsvarsmaler og dokumentgenerering.',
                audit_trail: 'Omfattende uforanderlig revisjonslogging og oppbevaring.',
                organizations: 'Multiorganisasjons- og gruppeadministrasjon.',
                task_management: 'Samlet oppgaveplanlegging, arbeidsflytmotor og kalendervisninger.',
            },
            module_categories: {
                data_privacy_rights: 'Datavern og rettigheter',
                vendor_third_party: 'Leverandør- og tredjepartsledelse',
                risk_assessment: 'Risiko og vurdering',
                operations_automation: 'Drift og automatisering',
                platform_admin: 'Plattform og administrasjon',
            },
            package_tier_label: 'Din Pakke',
            package_module_unavailable: 'Ikke inkludert i pakken din',
            select_all: 'Velg Alle',
            deselect_all: 'Opphev Alle',
            load_sample_data: 'Last inn eksempeldata etter oppsett',
            load_sample_data_description: 'Installer realistiske demodata for å utforske plattformen. Kan fjernes senere fra Onboarding > Eksempeldata.',
            language_label: 'Foretrukket Språk',
            language_description: 'Velg standardspråket for organisasjonen din.',
            admin_account_title: 'Administratorkonto',
            admin_account_created: 'Administratorkonto opprettet',
            admin_full_name: 'Fullt navn',
            admin_email: 'E-postadresse',
            admin_password: 'Passord',
            admin_confirm_password: 'Bekreft passord',
            admin_show_password: 'Vis passord',
            admin_hide_password: 'Skjul passord',
            admin_name_placeholder: 'f.eks. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minimum 8 tegn',
            admin_password_confirm_placeholder: 'Skriv inn passord på nytt',
            validation_admin_name_required: 'Administratornavn er påkrevd',
            validation_admin_password_required: 'Passord er påkrevd',
            validation_admin_password_min_length: 'Passordet må være minst 8 tegn',
            validation_passwords_mismatch: 'Passordene samsvarer ikke',
            notification_email_enabled: 'Aktiver E-postvarsler',
            notification_email_description: 'Motta e-postvarsler om oppgavetildeling, SLA-brudd og samsvarshendelser.',
            notification_digest_frequency: 'Sammendragsfrekvens',
            notification_digest_daily: 'Daglig',
            notification_digest_weekly: 'Ukentlig',
            notification_digest_none: 'Ingen sammendrag',
            notification_in_app_enabled: 'Aktiver Plattformvarsler',
            notification_in_app_description: 'Vis varselmerke og -skuff for sanntidsalarmer.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EØS',
                global: 'Globalt',
            },
            languages: {
                en: 'engelsk',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk bokmål',
                nn: 'Norsk nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Français',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Finansielle tjenester',
                healthcare: 'Helsevesen',
                technology: 'Teknologi',
                telecommunications: 'Telekommunikasjon',
                government: 'Offentlig sektor',
                education: 'Utdanning',
                manufacturing: 'Produksjon',
                retail: 'Detaljhandel',
                other: 'Annet',
            },
            placeholders: {
                org_name: 'f.eks. ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'bruker1@firma.vn, bruker2@firma.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organisasjonsnavn',
                jurisdiction: 'Jurisdiksjon',
                industry: 'Bransje',
                org_size: 'Organisasjonsstørrelse',
                language: 'Foretrukket Språk',
                admin_email: 'Administratorens e-post',
                team_emails: 'Teammedlemmers e-post (kommaseparert)',
                enable_cmp: 'Aktiver CMP-integrasjon',
                cmp_endpoint: 'CMP API-endepunkt',
                email_notifications: 'E-postvarsler',
                in_app_notifications: 'Plattformvarsler',
                digest_frequency: 'Sammendragsfrekvens',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Modulomvisninger',
            start: 'Start omvisning',
            continue_tour: 'Fortsett omvisning',
            replay: 'Spill av omvisning på nytt',
            exit: 'Avslutt omvisning',
            skip: 'Hopp over omvisning',
            next: 'Neste',
            previous: 'Forrige',
            finish: 'Fullfør omvisning',
            step_of: 'Trinn {current} av {total}',
            info: 'Ta guidede omvisninger i hver modul for å lære viktige funksjoner og arbeidsflyter. Omvisninger kan spilles av på nytt når som helst.',
            autoplay: 'Automatisk avspilling',
            interactive: 'Interaktiv',
            play_all: 'Spill alle',
            search_tours: 'Søk etter turer...',
            duration: 'Varighet',
            steps_label: '{count} trinn',
            mode: 'Modus',
            pause: 'Pause',
            resume: 'Fortsett',
            speed: 'Hastighet',
            restart: 'Start på nytt',
            reset: 'Tilbakestill',
            reset_success: 'Turfremdriften er tilbakestilt.',
            category_core_privacy: 'Kjernepersonvern',
            category_governance: 'Styring',
            category_operations: 'Drift',
            category_platform: 'Plattform',
            category_advanced: 'Avansert',
            category_data_privacy_rights: 'Datavern og rettigheter',
            category_vendor_third_party: 'Leverandør- og tredjepartsledelse',
            category_risk_assessment: 'Risiko og vurdering',
            category_operations_automation: 'Drift og automatisering',
            category_platform_admin: 'Plattform og administrasjon',
            steps_count: '{count} trinn',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Eksempeldatabehandling',
            select_modules: 'Velg moduler',
            current_status: 'Gjeldende status',
            operation_history: 'Operasjonshistorikk',
            load: 'Last inn eksempeldata',
            clear: 'Fjern eksempeldata',
            processing: 'Behandler...',
            loaded: 'Lastet',
            not_loaded: 'Ikke lastet',
            records: 'Eksempeloppføringer',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Er du sikker på at du vil fjerne alle eksempeldata for de valgte modulene?',
            confirm_clear_title: 'Fjern eksempeldata',
            confirm_load_title: 'Last inn eksempeldata',
            confirm_load: 'Er du sikker på at du vil laste inn eksempeldata for de valgte modulene?',
            action_load: 'Last inn',
            action_clear: 'Fjern',
            info: 'Last inn realistisk demodata for opplæring og demonstrasjoner. Eksempeldata er merket og kan trygt fjernes uten å påvirke ekte data.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Hjelpesenter',
            search: 'Søk',
            search_placeholder: 'Søk i hjelpeartikler...',
            clear: 'Tøm',
            all_categories: 'Alle kategorier',
            all_modules: 'Alle Moduler',
            back: 'Tilbake til hjelpesenteret',
            callout_important: 'Viktig',
            callout_tip: 'Tips',
            no_results: 'Ingen artikler funnet som samsvarer med søket ditt.',
            no_articles: 'Ingen hjelpeartikler tilgjengelig ennå. Artikler vil vises her etter hvert som innhold legges til.',
            results_for: '{count} resultat(er) for «{query}»',
            quick_links: {
                getting_started: 'Kom i gang',
                getting_started_desc: 'Installasjon, oppsett og hurtigstartveiledning.',
                user_guide: 'Brukerveiledning',
                user_guide_desc: 'Modul-for-modul bruksdokumentasjon.',
                admin_guide: 'Administrasjonsveiledning',
                admin_guide_desc: 'Konfigurasjon, vedlikehold og feilsøking.',
                faq: 'Vanlige spørsmål',
                faq_desc: 'Ofte stilte spørsmål og svar.',
            },
            search_articles_aria: 'Søk i hjelpeartikler',
            article_metadata_aria: 'Artikkelmetadata',
            faq_editor_title: 'FAQ-redigering for hjelpesenteret',
            faq_editor_description: 'Legg til, rediger og fjern spørsmål-og-svar-par i en hvilken som helst artikkel i hjelpesenteret. Redigeringsverktøyet er ennå ikke tilgjengelig; denne adressen er reservert for det.',
            faq_editor_empty: 'FAQ-redigeringen er ennå ikke tilgjengelig. Be i mellomtiden administratoren din om å legge til FAQ-seksjoner i en hjelpesenterartikkel for deg.',
            articles_list_aria: 'Hjelpeartikler',
            category_nav_aria: 'Kategorinavigasjon',
            quick_links_aria: 'Hurtiglenker til hjelpekategorier',
            returned_to_list: 'Tilbake til artikkellisten',
            opening_article: 'Åpner artikkel: {title}',
            read_article_aria: 'Les artikkel: {title}',
            pagination_nav_aria: 'Artikkelpaginering',
            error_unavailable: 'Hjelpesenteret er for øyeblikket ikke tilgjengelig. Vennligst prøv igjen senere.',
            retry: 'Prøv igjen',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'f.eks. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Lagre',
        cancel: 'Avbryt',
        system_actor: 'System',
        delete: 'Slett',
        edit: 'Rediger',
        create: 'Opprett',
        update: 'Oppdater',
        loading: 'Laster...',
        error: 'Det oppstod en feil',
        success: 'Vellykket',
        logout: 'Logg ut',
        actions: 'Handlinger',
        retrying: 'Prøver på nytt …',
        permission_denied: 'Du har ikke tilgang til denne siden.',
        status: 'Status',
        description: 'Beskrivelse',
        name: 'Navn',
        created_at: 'Opprettet',
        updated_at: 'Oppdatert',
        yes: 'Ja',
        no: 'Nei',
        confirm: 'Bekreft',
        confirm_dialog_title: 'Confirm action',
        back: 'Tilbake',
        next: 'Neste',
        previous: 'Forrige',
        search: 'Søk',
        filter: 'Filtrer',
        page_x_of_y: 'Side {page} av {total}',
        pagination_label: 'Paginering',
        view_mode_label: 'Visningsmodus',
        export: 'Eksporter',
        exporting: 'Eksporterer …',
        sidebar_navigation: 'Administrasjonssidebarnavigering',
        items_count: '{count} elementer',
        items_count_one: '{count} element',
        export_success: 'Dokumentet ble generert.',
        generate_pdf: 'Generer PDF',
        import: 'Importer',
        download: 'Last ned',
        upload: 'Last opp',
        view: 'Vis',
        close: 'Lukk',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Send inn',
        saved: 'Lagret',
        saving: 'Lagrer...',
        submitting: 'Sender...',
        created: 'Opprettet',
        all: 'Alle',
        none: 'Ingen',
        select: 'Velg',
        select_user: 'Velg bruker...',
        custom_input: 'Skriv inn manuelt',
        required: 'Påkrevd',
        optional: 'Valgfritt',
        notes: 'Notater',
        tags: 'Merkelapper',
        archive: 'Arkiver',
        clone: 'Klone',
        refresh: 'Oppdater',
        retry: 'Prøv igjen',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Tilbakestill',
        add: 'Legg til',
        remove: 'Fjern',
        removing: 'Fjerner…',
        attachment: 'Vedlegg',
        upload_file: 'Last opp fil',
        upload_file_required: 'Last opp fil (påkrevd)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Dra og slipp eller klikk for å bla gjennom',
        upload_max_size: 'Maks. størrelse: {size} MB',
        upload_accepted_types: 'Godtatt: {types}',
        upload_in_progress: 'Laster opp {name}...',
        upload_size_exceeded: 'Filstørrelsen overskrider maksimalt tillatt størrelse på {size} MB.',
        upload_type_not_accepted: 'Filtypen godtas ikke. Godtatte typer: {types}',
        upload_succeeded: 'Filen "{name}" ble lastet opp.',
        upload_failed: 'Kunne ikke laste opp filen.',
        select_entity_type_first: 'Velg en entitetstype først',
        failed_to_remove_file: 'Kunne ikke fjerne filen.',
        entity_type_label: 'Entitetstype',
        entity_label: 'Entitet',
        entity_reference: 'Entitetsreferanse',
        failed_to_load_entities: 'Kunne ikke laste inn entiteter',
        enable: 'Aktiver',
        disable: 'Deaktiver',
        no_data: 'Ingen data funnet',
        confirm_delete: 'Er du sikker på at du vil slette dette elementet?',
        confirm_action: 'Er du sikker på at du vil fortsette?',
        save_success: 'Lagret',
        save_error: 'Kunne ikke lagre',
        delete_success: 'Slettet',
        delete_error: 'Kunne ikke slette',
        hour: 'time',
        hours: 'timer',
        load_error: 'Kunne ikke laste data',
        operation_error: 'Operasjonen mislyktes',
        something_went_wrong: 'Noe gikk galt',
        go_home: 'Gå til kontrollpanelet',
        access_denied: 'Ingen tilgang',
        no_permission: 'Du har ikke tilgang til denne siden.',
        no_permission_title: 'Utilstrekkelige tillatelser',
        no_permission_message: 'Du har ikke tillatelse til å få tilgang til denne siden.',
        unknown: 'Ukjent',
        back_to_dashboard: 'Tilbake til kontrollpanelet',
        id: 'ID',
        type: 'Type',
        date: 'Dato',
        created_by: 'Opprettet av',
        updated_by: 'Oppdatert av',
        page_of: 'Side {page} av {total}',
        showing_items: 'Viser {count} elementer',
        items_per_page: 'Elementer per side',
        no_results: 'Ingen oppføringer samsvarer med filtrene dine',
        no_items: 'Ingen elementer funnet',
        sort_by: 'Sorter etter',
        ascending: 'Stigende',
        descending: 'Synkende',
        all_statuses: 'Alle statuser',
        all_types: 'Alle typer',
        all_categories: 'Alle kategorier',
        all_severities: 'Alle alvorlighetsgrader',
        date_range: 'Datoperiode',
        from_date: 'Fra',
        to_date: 'Til',
        clear_filters: 'Tøm filtre',
        apply_filters: 'Bruk filtre',
        save_changes: 'Lagre endringer',
        discard_changes: 'Forkast endringer',
        confirm_discard: 'Du har ulagrede endringer. Vil du forkaste dem?',
        copy: 'Kopier',
        duplicate: 'Dupliser',
        move: 'Flytt',
        assign: 'Tildel',
        unassign: 'Fjern tildeling',
        approve: 'Godkjenn',
        reject: 'Avvis',
        deleting: 'Sletter...',
        processing: 'Behandler...',
        loading_page: 'Laster side...',
        operation_success: 'Operasjonen ble fullført',
        operation_failed: 'Operasjonen mislyktes. Vennligst prøv igjen.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Nettverksfeil. Sjekk tilkoblingen din.',
        session_expired: 'Økten er utløpt. Vennligst logg inn igjen.',
        test: 'Test',
        pause: 'Sett på pause',
        resume: 'Gjenoppta',
        activate: 'Aktiver',
        last_sync: 'Siste synkronisering',
        health: 'Helse',
        email: 'E-post',
        role: 'Rolle',
        title: 'Tittel',
        owner: 'Eier',
        priority: 'Prioritet',
        category: 'Kategori',
        details: 'Detaljer',
        overview: 'Oversikt',
        configuration: 'Konfigurasjon',
        history: 'Historikk',
        new_item: 'Ny',
        edit_item: 'Rediger',
        select_option: 'Velg...',
        total: 'Totalt',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        enabled: 'Aktivert',
        disabled: 'Deaktivert',
        last_login: 'Siste innlogging',
        never: 'Aldri',
        password_changed: 'Passordet er endret. Du vil bli logget ut.',
        password_change_failed: 'Kunne ikke endre passord',
        passwords_mismatch: 'Passordene samsvarer ikke',
        install: 'Installer',
        install_named: 'Installer {name}',
        uninstall: 'Avinstaller',
        installed: 'Installert',
        available: 'Tilgjengelig',
        catalog: 'Katalog',
        published: 'Publisert',
        version: 'Versjon',
        market: 'Marked',
        language: 'Språk',
        framework: 'Rammeverk',
        template: 'Mal',
        connector_type: 'Koblingstype',
        polling_interval: 'Pollingsintervall (minutter)',
        new_instance: 'Ny instans',
        change_password: 'Endre passord',
        current_password: 'Gjeldende passord',
        new_password: 'Nytt passord',
        confirm_password: 'Bekreft passord',
        comments: 'Kommentarer',
        completed: 'Fullført',
        contact: 'Kontakt',
        hide: 'Skjul',
        hide_details: 'Skjul detaljer',
        label: 'Etikett',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Viser',
        page: 'Side',
        preview: 'Forhåndsvisning',
        print: 'Skriv ut',
        show: 'Vis',
        status_active: 'Aktiv',
        status_completed: 'Fullført',
        status_draft: 'Utkast',
        status_in_progress: 'I gang',
        status_pending: 'Ventende',
        view_details: 'Vis Detaljer',
        all_priorities: 'Alle prioriteringer',
        pipeline: 'Rørledning',
        all_requests: 'Alle Forespørsler',
        schedule: 'Tidsplan',
        deploy: 'Distribuer',
        rollback: 'Tilbakerulling',
        advance_stage: 'Avancer Fase',
        submit_for_approval: 'Send inn til godkendelse',
        approval_log: 'Godkjenning Logg',
        approver: 'Godkjenner',
        decision: 'Beslutning',
        decided_at: 'Besluttet At',
        rollout_stages: 'Utrulling Faser',
        approval_comment: 'Godkjenning Kommentar',
        optional_comment: 'Valgfritt kommentar...',
        impact_summary: 'Konsekvens Sammendrag',
        target_property_ids: 'Målegenskabs-ID-er (kommaseparert)',
        policy_id: 'Retningslinje ID',
        target_version: 'Mål Versjon',
        target_version_placeholder: 'f.eks. 2.0.0',
        scheduled_label: 'Planlagt',
        scheduled_start: 'Planlagt start',
        scheduled_end: 'Planlagt slutning',
        properties_count: '{count} egenskaber',
        results: 'Resultater',
        comparison_results: 'Sammenligning Resultater',
        past_comparison_reports: 'Tidligere sammenligningsrapporter',
        scanning: 'Skanner...',
        run_inconsistency_scan: 'Kjøring Inkonsistens Skanning',
        property_ids_label: 'Egenskabs-ID-er (kommaseparert, min. 2)',
        comparison_type: 'Sammenligning Type',
        golden_template_id: 'Gylden mal-ID (valgfritt)',
        leave_blank_compare: 'Lad stå tomt for at sammenligne egenskaber indbyrdes',
        add_notes_placeholder: 'Legg til dine analysenoter her...',
        covered_of_active: '{covered} av {active} aktive egenskaper dekket',
        fetch_error: 'Kunne ikke hente data. Vennligst prøv igjen.',
        run_comparison_error: 'Kunne ikke kjøre sammenligning. Vennligst prøv igjen.',
        save_notes_error: 'Kunne ikke lagre notater. Vennligst prøv igjen.',
        scan_complete: 'Inkonsistensscanning fuldført.',
        scan_error: 'Kunne ikke kjøre inkonsistensskann. Vennligst prøv igjen.',
        min_two_properties: 'Oppgi vennligst mindst 2 egenskabs-ID-er til sammenligning.',
        comma_separated: 'kommaseparert',
        language_english: 'Engelsk',
        language_vietnamese: 'Vietnamesisk',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Forfatter',
        dependencies: 'Avhengigheter',
        checksum: 'Sjekksum',
        changelog: 'Endringslogg',
        tier: 'Nivå',
        phone: 'Telefon',
        launch: 'Start',
        complete: 'Fullfør',
        back_to_list: 'Tilbake til liste',
        clear_selection: 'Tøm Valg',
        deactivate: 'Deaktiver',
        scope: 'Omfang',
        deadline: 'Frist',
        progress: 'Fremdrift',
        frequency: 'Frekvens',
        reviewer: 'Gjennomgår',
        user: 'Bruker',
        campaign: 'Kampanje',
        items: 'Elementer',
        next_run: 'Neste Kjøring',
        last_run: 'Siste Kjøring',
        overdue: 'FORSINKET',
        creating: 'Oppretter...',
        launching: 'Starter...',
        loaded: 'loaded',
        page_size: 'Sidestørrelse',
        per_page: '/ side',
        urgent: 'Haster',
        pagination: 'Paginering',
        metric: 'Metrisk',
        value: 'Verdi',
        country: 'Land',
        showing_chart_view: 'Viser diagramvisning',
        showing_table_view: 'Viser datatabellvisning',
        policies: 'Retningslinjer',
        compliant: 'Samsvarlig',
        non_compliant_status: 'Ikke-kompatibel',
        all_data: 'Alle data',
        sr_urgent: 'Haster',
        n_per_page: '{n} / side',
        select_user_placeholder: '-- Velg bruker --',
        field_required: 'Dette feltet er påkrevd',
        results_found: 'resultater funnet',
        select_placeholder: '-- Velg --',
        unknown_user: 'Ukjent bruker',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Eksporter CSV',
        not_applicable: 'I/T',
        aria_overdue_since: 'Forfalt siden {date}',
        placeholders: {
            enter_justification: 'Angi begrunnelse',
            justification: 'Begrunnelse',
            campaign_name_example: 'Q1 2026 Tilgang Gjennomgang',
            optional_description: 'Valgfritt beskrivelse',
            enter_user_id: 'Angi gjennomgåerens bruker-ID',
            enter_user_id_short: 'Angi bruker ID',
            schedule_name_example: 'Kvartalsvis tilgangsattestering',
            enter_property_id: 'Angi egenskap ID',
            enter_policy_id: 'Angi politik-ID',
            property_or_group_id: 'Egenskap eller gruppe ID',
            describe_template_purpose: 'Beskriv the mal formål...',
            step_title: 'Steg tittel...',
            detailed_instructions: 'Detaljert instruksjoner...',
            filter_by_taxonomy_id: 'Filter etter Taksonomi ID...',
            describe_classification_rule: 'Beskriv klassificeringsreglen...',
            describe_dedup_rule: 'Beskriv dedupliceringsreglen...',
            describe_import_template: 'Beskriv the importer mal...',
            reason_for_rejection: 'Årsak til afvisning...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filter etter lokalitet...',
            filter_by_event_type: 'Filter etter hendelse type',
            filter_by_actor_id: 'Filtrer efter aktør-ID',
            filter_by_market_code: 'Filter etter marked kode...',
            filter_by_framework_id: 'Filter etter rammeverk ID...',
            add_comment: 'Add en kommentar...',
            provide_rejection_reason: 'Oppgi en årsak for afvisning af denne indsendelse...',
            filter_by_jurisdiction: 'Filtrer efter jurisdiktionskode...',
            enter_report_title: 'Angi rapport tittel',
            enter_name: 'Skriv inn navn',
            enter_description: 'Skriv inn beskrivelse',
            enter_org_id: 'Angi Org ID to legg til',
            describe_evidence_gap: 'Beskriv the bevis mangel...',
            expected_evidence: 'Hvilken bevis forventes',
            entity_identifier: 'Enhet identifikator',
            remediation_steps: 'Trin til at afhjælpe denne mangel',
            user_id_or_name: 'Bruker ID eller navn',
            reporting_period_id: 'Rapporteringsperiode-ID',
            maturity_model_id: 'Modenhet modell ID',
            describe_maturity_model: 'Beskriv denne modenhedsmodel...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Oppgi en årsak for afvisning af denne anmodning',
            describe_emergency: 'Beskriv nødsituasjonen som krever forhøyet tilgang (minimum 20 tegn)',
            enter_delegate_id: 'Angi bruker ID of the deleger',
            provide_delegation_reason: 'Oppgi en årsak for denne delegering',
            client_identifier: 'Klient identifikator',
            client_secret: 'Klient hemmelighet',
            time_format: 'TT:MM',
            describe_sod_rule: 'Beskriv formålet med denne SoD-regel...',
            enter_role_id: 'Angi rolle-ID',
            explain_exception: 'Forklar hvorfor denne undtagelse er nødvendig...',
            describe_compensating_controls: 'Beskriv kompenserende kontroller...',
            full_name: 'Fullstendig navn',
            min_chars: 'Min. 12 tegn',
            enter_override_justification: 'Angi begrunnelse for at tilsidesætte denne portevaluering',
            enter_evidence_id: 'Skriv inn bevis-ID for å linke:',
            date_format: 'DD/MM/ÅÅÅÅ',
            describe_pack_contents: 'Beskriv pakkens innhold og formål',
            select_reviewers: 'Velg bedømmere...',
        },
        aria_previous_page: 'Forrige side',
        aria_next_page: 'Neste side',
        aria_results_per_page: 'Resultater per side',
        per_page_25: '25 / side',
        per_page_50: '50 / side',
        per_page_100: '100 / side',
        select_language: 'Velg språk',
        today: 'I dag',
        tomorrow: 'I morgen',
        dashboard_load_error: 'Kunne ikke laste dashbordet. Prøv igjen.',
        enum_grade: {
            red: 'Rødt',
            amber: 'Amber',
            yellow: 'Gul',
            green: 'Grønn',
        },
        enum_severity: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Lav',
        },
        enum_priority: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Lav',
        },
        enum_status: {
            pending: 'Venter',
            in_progress: 'Pågår',
            completed: 'Fullført',
            blocked: 'Blokkert',
            escalated: 'Eskalert',
            cancelled: 'Avbrutt',
            overdue: 'Forfalt',
            not_started: 'Ikke startet',
            new: 'Nytt',
            open: 'Åpne',
            closed: 'Stengt',
            acknowledged: 'Erkjent',
            resolved: 'Løst',
        },
        enum_department: {
            unassigned: 'Ikke tilordnet',
        },
        enum_form_category: {
            assessment: 'Vurdering',
            assessment_report: 'Vurderingsrapport',
            dpia: 'DPIA',
            authority_submission: 'Innlevering av myndighet',
            cross_border_transfer: 'Overføring over landegrensene',
            vendor: 'Leverandør',
            breach: 'Brudd',
            consent: 'Samtykke',
            governance: 'Styring',
            register: 'Registrer deg',
            monitoring: 'Overvåking',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Personvern',
            incident: 'Hendelse',
            org: 'Organisasjon',
            sector: 'Sektor',
            classification: 'Klassifisering',
            discovery: 'Oppdagelse',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Rettighetsforespørsler',
            assessments: 'Vurderinger',
            incidents: 'Hendelser',
            incident_ops: 'Hendelse Ops',
            monitoring: 'Overvåking',
            vendor_risk: 'Leverandørrisiko',
            governance: 'Styring',
            deletion_orchestration: 'Sletting',
            access_review: 'Tilgang gjennomgang',
            consent: 'Samtykke',
            consent_properties: 'Samtykkeegenskaper',
            forms: 'Skjemaer',
            localization: 'Lokalisering',
            program_governance: 'Programstyring',
            privacy_scanner: 'Personvernskanner',
            classification: 'Klassifisering',
            discovery: 'Oppdagelse',
            manual: 'Manuell',
            data_mapping: 'Datakartlegging',
        },
        enum_risk_level: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Lav',
        },
        enum_request_type: {
            all: 'All',
            access: 'Tilgang (DSAR)',
            deletion: 'Sletting',
            correction: 'Rettelse',
            objection: 'Innvending',
            portability: 'Bærbarhet',
            restriction: 'Begrensning',
            consent_withdrawal: 'Tilbaketrekking av samtykke',
            protective_measures: 'Beskyttelsestiltak',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'engelsk',
            vi: 'vietnamesisk',
            all: 'Begge deler',
        },
        enum_event_type: {
            case_created: 'Sak opprettet',
            status_changed: 'Status endret',
            case_assigned: 'Sak tildelt',
            note_added: 'Merknad lagt til',
            attachment_added: 'Vedlegg lagt til',
            attachment_deleted: 'Vedlegg slettet',
            reference_added: 'Referanse lagt til',
            checklist_updated: 'Sjekkliste oppdatert',
            identity_verified: 'Identitet bekreftet',
            closure_summary_added: 'Avslutningssammendrag',
            approval_granted: 'Godkjenning gitt',
            template_applied: 'Mal brukt',
            export_generated: 'Eksport generert',
            sla_warning: 'SLA Advarsel',
            sla_overdue: 'SLA Forfalt',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Eksport feilet',
        you: 'You',
        export_failed: 'Eksport mislyktes. Prøv igjen.',
        filters: 'Filtre',
        failed: 'Feilet',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Fjernet',
        draft: 'Utkast',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        pending: 'Ventende',
        in_progress: 'Pågår',
        completed: 'Fullført',
        archived: 'Arkivert',
        published: 'Publisert',
        approved: 'Godkjent',
        rejected: 'Avvist',
        cancelled: 'Avbrutt',
        blocked: 'Blokkert',
        deferred: 'Utsatt',
        not_started: 'Ikke startet',
        dismissed: 'Avvist',
        failed: 'Mislykket',
        in_review: 'I Gjennomgang',
        deprecated: 'Utfaset',
        scheduled: 'Planlagt',
        deploying: 'Distribuerer',
        deployed: 'Distribuert',
        paused: 'Pauset',
        rolled_back: 'Tilbakerullet Tilbake',
        received: 'Mottatt',
        processing: 'Behandler',
        under_assessment: 'Under vurdering',
        implemented: 'Implementert',
        pending_approval: 'Ventende Godkjenning',
        pending_deletion: 'Ventende Sletting',
        on_hold: 'På Sperring',
        deleted: 'Slettet',
        expired: 'Utløpte',
        outdated: 'Utdatert',
        drifted: 'Avviket',
        decommissioned: 'Avviklet',
        match: 'Treff',
        different: 'Forskjellig',
        missing: 'Manglende',
        confirmed: 'Bekreftet',
        revoked: 'Tilbakekalt',
        escalated: 'Eskalert',
        system: 'System',
        custom: 'Tilpasset',
        warning: 'Advarsel',
        blocking: 'Blokkerende',
        analyst: 'Analytiker',
        viewer: 'Leser',
        daily: 'Daglig',
        weekly: 'Ukentlig',
        policies: 'Retningslinjer',
        denied: 'Avslått',
        verified: 'Verifisert',
        detected: 'Oppdaget',
        ignored: 'Ignorert',
        open: 'Åpne',
        resolved: 'Løst',
        suspended: 'Suspendert',
        retired: 'Pensjonist',
        review: 'Gjennomgå',
        generated: 'Generert',
        exported: 'Eksportert',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Lav',
        medium: 'Middels',
        high: 'Høy',
        critical: 'Kritisk',
    },
    sensitivity: {
        standard: 'Standard',
        sensitive: 'Sensitiv',
        special_category: 'Særlig kategori',
        badge_sensitive: 'Sensitiv',
    },
    change_types: {
        policy_update: 'Retningslinje Oppdatering',
        new_binding: 'Ny Binding',
        remove_binding: 'Fjern Bindende',
        configuration_change: 'Konfigurasjon Endrings',
        banner_update: 'Banner Oppdater',
    },
    comparison_types: {
        full: 'Full',
        banner_only: 'Banner Kun',
        purposes_only: 'Formål Kun',
        legal_bases_only: 'Kun retsgrundlag',
        consent_settings: 'Samtykke Indstillinger',
    },
    lifecycle_stages: {
        active: 'Aktiv',
        archived: 'Arkivert',
        pending_deletion: 'Ventende Sletting',
        deleted: 'Slettet',
        on_hold: 'På Sperring',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Personoplysninger',
        consent_record: 'Samtykke Post',
        user_account: 'Bruker Konto',
        audit_log: 'Revisjon Logg',
        document: 'Dokument',
        backup: 'Sikkerhetskopi',
    },
    risk_categories: {
        data_breach: 'Data Brudd',
        unauthorized_access: 'Uautorisert Tilgang',
        data_loss: 'Data Tap',
        regulatory_non_compliance: 'Regulatorisk Manglende etterlevelse',
        third_party_risk: 'Tredjeparters Risiko',
        operational: 'Operasjonell',
        technical: 'Teknisk',
        legal: 'Juridisk',
    },
    mitigation_types: {
        technical: 'Teknisk',
        organizational: 'Organisatorisk',
        contractual: 'Kontraktsmessig',
        procedural: 'Prosedyremessig',
    },
    safeguard_types: {
        encryption_in_transit: 'Kryptering i transitt',
        encryption_at_rest: 'Kryptering i hvile',
        access_controls: 'Tilgangskontroller',
        contractual_clauses: 'Kontraktsklausuler',
        data_minimization: 'Dataminimering',
        pseudonymization: 'Pseudonymisering',
        audit_rights: 'Revisjonsrettigheter',
        breach_notification: 'Bruddvarsel',
        data_localization: 'Datalokalisering',
        other: 'Annet',
    },
    scoring_methods: {
        weighted_average: 'Vægtet gennemsnit',
        simple_average: 'Simpelt gennemsnit',
        maximum: 'Maksimum',
        manual: 'Manuell',
    },
    dpa_status: {
        not_required: 'Ikke påkrevd',
        required: 'Påkrevd',
        in_progress: 'Pågår',
        completed: 'Fullført',
        signed: 'Signert',
    },
    disposal_methods: {
        anonymize: 'Anonymiser',
        pseudonymize: 'Pseudonymiser',
        aggregate: 'Aggreger',
        delete: 'Slett',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Kontrollpanel',
        no_data: 'Ingen kontrollpaneldata tilgjengelig.',
        no_data_heading: 'Ingen aktive data ennå',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Samtykkedekning',
        ctia_filings: 'CTIA-meldinger',
        next_dpia_review: 'Neste DPIA-gjennomgang',
        next_review_none: 'Ingen planlagt',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'MPS-meldinger for hendelser',
        compliance_score: 'Samsvarsscore',
        grade: 'Karakter',
        no_score: 'Ingen samsvarsscore beregnet ennå.',
        rights_requests: 'Innsynsbegjæringer',
        open: 'Åpne',
        sla_breached: 'SLA brutt',
        sla_approaching: 'SLA utløper snart',
        closed_month: 'Lukket (måned)',
        incidents: 'Hendelser og personvernbrudd',
        active: 'Aktive',
        breaches: 'Personvernbrudd',
        overdue_tasks: 'Forfalte oppgaver',
        breach_72h_notice: 'Ved aktive brudd gjelder 72-timers forskriftsvarslingsfrist',
        recommendations: 'Anbefalinger',
        take_action: 'Ta handling',
        drift_events: 'Avvik og godkjenninger',
        open_events: 'Åpne hendelser',
        pending_approvals: 'Ventende godkjenninger',
        monitoring: 'Overvåkingsoppgaver',
        programs: 'Programmer',
        overdue: 'Forfalt',
        pending: 'Ventende',
        ric_cases: 'Tilsynssaker',
        coverage: 'Datadekning',
        systems: 'Systemer',
        activities: 'Aktiviteter',
        vendors: 'Leverandører',
        dpias: 'DPIA-er',
        open_gaps: 'åpne mangler',
        view_as_table: 'Vis som tabell',
        view_as_chart: 'Vis som diagram',
        table_metric_header: 'Beregning',
        table_value_header: 'Verdi',
        announce_chart_view: 'Viser diagramvisning',
        announce_table_view: 'Viser tabellvisning',
        section_core_metrics: 'Kjerneberegninger',
        section_task_management: 'Oppgavehandtering',
        section_compliance_insights: 'Samsvarsinnsikt',
        configure_dashboard: 'Konfigurer dashbord',
        config_col_active: 'Aktiv',
        config_col_widget: 'Widget',
        collapse_section: 'Skjul',
        expand_section: 'Utvid',
        reset_to_defaults: 'Tilbakestill til standard',
        preferences_saved: 'Dashboardinnstillinger lagret',
        preferences_reset: 'Dashboard tilbakestilt til rollestandard',
        preferences_load_error: 'Kunne ikke laste dashboardinnstillinger',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Gjennomsnittlig systempoengsum',
        factor_avg_vendor_score: 'Gjennomsnittlig leverandørscore',
        factor_processing_coverage: 'Behandlingsaktivitetsdekning',
        factor_ropa_completeness: 'RoPA fullstendighet',
        factor_drift_penalty: 'Driftsstraff',
        rec_improve_system_gov: 'Forbedre systemstyringsdokumentasjonen',
        rec_improve_vendor_gov: 'Forbedre leverandørstyringsdokumentasjonen',
        rec_document_processing: 'Dokumentbehandlingsaktiviteter for avdekkede systemer',
        rec_generate_ropa: 'Generer RoPA-poster for aktive behandlingsaktiviteter',
        rec_resolve_drift: 'Løs åpne kritiske og alvorlige drifthendelser',
        score_good: 'Bra',
        score_fair: 'Akseptabel',
        score_poor: 'Dårlig',
        score_critical: 'Kritisk',
        drag_to_reorder: 'Dra for å omorganisere',
        widget_moved: 'Widget flyttet',
        press_space_to_grab: 'Trykk mellomrom for å gripe, piltaster for å flytte, mellomrom for å slippe',
        reorder_instructions: 'Bruk dra og slipp eller tastatursnarveier for å omorganisere widgeter',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Oppgaveoversikt',
            pending: 'Venter',
            in_progress: 'Pågår',
            overdue: 'Forfalt',
            done_7d: 'Ferdig (7d)',
            by_priority: 'Etter prioritet',
            view_all_tasks: 'Vis alle oppgaver',
            upcoming_tasks: 'Kommende oppgaver (de neste 7 dagene)',
            no_upcoming: 'Ingen oppgaver forfaller de neste 7 dagene.',
            view_calendar: 'Vis kalender',
            overdue_alerts: 'Forfalt',
            no_overdue: 'Ingen forfalte oppgaver. Alt er i rute.',
            assigned_to: 'Tildelt til',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Se alle forfalt',
            notifications: 'Nylige varsler',
            unread: 'unread',
            no_notifications: 'Ingen varsler. Dere er alle fanget!',
            view_all_notifications: 'Se alle varsler',
            load_error: 'Kunne ikke laste data.',
            day_late: 'dag sent',
            days_late_plural: 'dager for sent',
            due_today: 'Har termin i dag',
            just_now: 'akkurat nå',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Statistikk på tvers av organisasjoner',
            by_organization: 'Etter organisasjon',
            overdue_across_orgs: 'forfalte oppgaver på tvers av organisasjoner',
            overdue_count_label: '{count} overdue tasks',
            total: 'Totalt',
            tasks: 'tasks',
            organization: 'Organisasjon',
            current_org: 'Nåværende organisasjon',
            all_orgs: 'Alle organisasjoner',
            all_organizations: 'Alle organisasjoner',
            filter_by_org: 'Filtrer etter organisasjon',
            cross_org: 'Cross-org',
            empty_calendar: 'Ingen oppgaver for denne perioden. Lag din første oppgave eller sjekk filtrene dine.',
            empty_my_tasks: 'Du har ingen oppgaver tildelt. Flott arbeid!',
            empty_overdue: 'Ingen forfalte oppgaver. Alt er i rute.',
            empty_notifications: 'Ingen varsler. Dere er alle fanget!',
            empty_workflows: 'Ingen arbeidsflyter er konfigurert. Bla gjennom maler for å komme i gang.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Mine Oppgaver',
            no_tasks: 'Ingen oppgaver tildelt deg.',
            load_error: 'Kunne ikke laste oppgaver.',
            overdue: 'forfalt',
            remaining: 'igjen',
            view_all: 'Vis Alle Oppgaver',
        },
        my_section_status_widget: {
            title: 'Min Seksjonsstatus',
            overall_completion: 'Totalt',
            sections_count: 'Seksjoner',
            no_sections: 'Ingen seksjoner tildelt avdelingen din.',
            load_error: 'Kunne ikke laste seksjonsstatus.',
            department_tasks_title: 'Avdelingens oppgaver',
            no_department_tasks: 'Avdelingen din har ingen oppgaver.',
            department_tasks_error: 'Kunne ikke laste avdelingens oppgaver.',
            no_department: 'Du er ikke knyttet til en avdeling.',
        },
        revision_requests_widget: {
            title: 'Revisjonsforespørsler',
            no_revisions: 'Ingen revisjonsforespørsler. Alle seksjoner er på sporet.',
            load_error: 'Kunne ikke laste revisjonsforespørsler.',
        },
        consent_summary: {
            title: 'Samtykke',
            active: 'Aktiv',
            withdrawn_30d: 'Tilbaketrukket (30d)',
            expiring_7d: 'Utløper (7d)',
        },
        transfer_assessments: {
            title: 'Overføringsvurderinger',
            open: 'Åpne',
            approved: 'Godkjent',
            gaps_flagged: 'Mellomrom flagget',
        },
        breach_notifications: {
            title: 'Bruddvarsler',
            deadline_72h: '72 timers frist',
            submitted: 'Sendt inn',
            pending: 'Venter',
        },
        authority_requests: {
            title: 'Myndighetsforespørsler',
            pending: 'Venter',
            overdue: 'Forfalt',
            responded_30d: 'Svarte (30d)',
        },
        data_subjects_widget: {
            title: 'Datasubjekter',
            total: 'Totalt',
            high_risk: 'Høy risiko',
            active_30d: 'Aktiv (30d)',
        },
        audit_activity_summary: {
            title: 'Revisjonsaktivitet',
            events_today: 'Arrangementer i dag',
            events_7d: 'Hendelser (7d)',
            unique_actors_30d: 'Unike skuespillere (30d)',
        },
        onboarding_progress: {
            title: 'Onboarding Progress',
            completion_pct: 'Komplett',
            status_label: 'Status',
            current_step: 'Nåværende trinn',
            status: {
                in_progress: 'Pågår',
                completed: 'Fullført',
                skipped: 'Hoppet over',
                not_started: 'Ikke startet',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sektoroverleggrapporter med frist',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filtrer etter rammeverk',
            all_frameworks: 'Alle rammeverk',
            filing_lifecycle: 'Innleveringslivssyklus',
            filing_pipeline: 'Innleveringspipeline',
            draft: 'Utkast',
            internal_review: 'Intern gjennomgang',
            approved: 'Godkjent',
            submitted: 'Sendt inn',
            supplement_requested: 'Tillegg forespurt',
            resubmitted: 'Sendt inn på nytt',
            accepted: 'Godtatt',
            rejected: 'Avvist',
            closed: 'Stengt',
            deadline: 'Frist',
            days_remaining: 'dager igjen',
            days_overdue: 'dager forsinket',
            no_filings: 'Ingen aktive innleveringer.',
            department_consolidation: 'Avdelingskonsolidering',
            departments_completed: 'Avdelinger fullført',
            departments_pending: 'Avdelinger ventende',
            departments_overdue: 'Avdelinger forsinkede',
            no_consolidation_cases: 'Ingen tverravdelingssaker pågår.',
            vendor_risk: 'Leverandør- og tredjepartsrisiko',
            pending_assessment: 'Ventende vurdering',
            overdue_questionnaires: 'Forsinkede spørreskjemaer',
            dpa_review_queue: 'DPA-gjennomgangskø',
            risk_distribution: 'Risikofordeling',
            total_vendors: 'Totale leverandører',
            by_risk_level: 'Etter risikonivå',
            no_vendor_data: 'Ingen leverandørrisikodata tilgjengelig.',
            discovery_summary: 'Dataoppdagelse',
            discovery_classification: 'Dataoppdagelse og klassifisering',
            active_scans: 'Aktive skanninger',
            labels_pending: 'Etiketter venter på gjennomgang',
            exceptions_unresolved: 'Unntak uløst',
            pending_review: 'Ventende gjennomgang',
            exceptions: 'Unntak',
            approved_labels: 'Godkjente etiketter',
            total_classified: 'Totalt klassifisert',
            no_discovery_data: 'Ingen oppdagelsesdata tilgjengelig.',
            ai_governance: 'KI-systemstyring',
            systems_inventory: 'Systemer i inventar',
            systems_in_inventory: 'Systemer i oversikt',
            classification_breakdown: 'Klassifiseringssammenbrudd',
            dossier_completion: 'Dossierferdigstillelse',
            by_classification: 'Etter klassifisering',
            labeling_status: 'Merkelappstatus',
            not_started: 'Ikke startet',
            no_ai_data: 'Ingen KI-styringsdata tilgjengelig.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Brudd- og hendelsesrespons',
            active_breaches: 'Aktive brudd',
            notification_status: 'Varslingsstatus',
            remediation_progress: 'Utbedringsfremgang',
            supplement_loops: 'Tilleggsløkker',
            no_breach_data: 'Ingen aktive bruddssaker.',
            audit_readiness: 'Revisjons- og vurderingsberedskap',
            assessments: 'Vurderinger',
            gap_findings: 'Mangelresultater',
            evidence_completion: 'Bevisferdigstillelse',
            export_ready: 'Klar til eksport',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Ikke klar',
            no_audit_data: 'Ingen revisjonsberedskapsdata tilgjengelig.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cybersikkerhet 2018',
            framework_label_vn_ecommerce: 'VN E-handel 2023',
            framework_label_vn_telecom: 'VN Telekommunikasjon 2023',
            framework_label_vn_ai: 'VN AI-lov 2025',
            framework_label_gdpr: 'GDPR',
            framework_label_unspecified: 'Uspesifisert',
            my_department: 'Min avdeling',
            my_tasks: 'Mine oppgaver',
            section_status: 'Seksjonsstatus',
            revision_requests: 'Revisjonsforespørsler',
            completion: 'Ferdigstillelse',
            reason: 'Årsak',
            requested_at: 'Forespurt den',
            no_tasks_assigned: 'Ingen oppgaver tildelt deg.',
            no_sections: 'Ingen seksjoner tildelt din avdeling.',
            no_revisions: 'Ingen revisjonsforespørsler.',
            load_error: 'Kunne ikke laste inn data.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instanser',
            credentials: 'Legitimasjonshvelv',
            event_bus: 'Hendelseskø',
            definitions: 'Definisjoner',
            brds: 'BRD-innsamling',
            security_reviews: 'Sikkerhetsgjennomganger',
            poc_scorecards: 'POC-resultatkort',
            automation_planning: 'Automatiseringsplanlegging',
            system_inventory: 'Systemoversikt',
            traceability_matrix: 'Sporingsmatrise',
            gap_register: 'Gap-register',
        },
        instances: {
            title: 'Koblingsinstanser',
            page_title: 'Koblinger',
            add: 'Legg til instans',
            test: 'Test tilkobling',
            sync_now: 'Synkroniser nå',
            new_instance: 'Ny Kobling Instans',
            no_instances: 'Ingen kobling instanser funnet',
            instance_count: '{count} instans(er)',
            confirm_delete: 'Slett koblinginstans "{name}"?',
            all_connector_types: 'Alle Kobling Typer',
            select_connector: 'Velg en kobling...',
            fetch_error: 'Kunne ikke hente koblingsinstanser. Vennligst prøv igjen.',
            fetch_definitions_error: 'Kunne ikke hente koblingsdefinisjoner. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette koblingsinstans',
            test_error: 'Kunne ikke teste kobling. Vennligst prøv igjen.',
            status_error: 'Kunne ikke oppdatere koblingsstatus. Vennligst prøv igjen.',
            delete_error: 'Kunne ikke slette koblingsinstans. Vennligst prøv igjen.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status oppdatert',
            reactivate: 'Aktiver på nytt',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Legitimasjonshvelv',
            page_title: 'Koblingens legitimasjon',
            add: 'Legg til legitimasjon',
            rotate: 'Roter',
            expired: 'Utløpt',
            expiring: 'Utløper snart',
            orphaned: 'Foreldreløs',
            new_credential: 'Ny Legitimasjon',
            no_credentials: 'Ingen credentials funnet',
            no_expiry: 'Ingen utløp',
            linked_connectors: 'Tilknyttede Koblinger',
            expires_at: 'Utløper den',
            value_label: 'Værdi * (kun skrivning)',
            confirm_delete_title: 'Bekreft sletting',
            confirm_delete_message: 'Slette denne legitimasjonen? Tilkoblede konnektorer mister tilgang.',
            rotate_title: 'Rotér legitimasjon',
            new_credential_value: 'Ny legitimasjonsverdi',
            fetch_error: 'Kunne ikke laste legitimasjoner. Vennligst prøv igjen.',
            health_error: 'Kunne ikke laste helse. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette legitimasjon',
            rotate_error: 'Kunne ikke rotere legitimasjon',
            delete_error: 'Kunne ikke slette legitimasjon',
        },
        event_bus: {
            title: 'Hendelseskø',
            page_title: 'Hendelsebuss',
            events: 'Hendelser',
            subscriptions: 'Abonnementer',
            dlq: 'Ubehandlet kø',
            publish: 'Publiser hendelse',
            retry: 'Prøv igjen',
            dismiss: 'Avvis',
            search_events: 'Søk hendelser...',
            filter_event_type: 'Filter etter hendelse type...',
            event_type: 'Hendelse Type',
            source_module: 'Kilde Modul',
            timestamp: 'Tidsstempel',
            delivery_status: 'Levering Status',
            correlation_id: 'Korrelasjon ID',
            tracking_id: 'Sporings-ID',
            delivery_status_hint: 'Leveringsstatus oppdateres automatisk etter hvert som hendelser behandles.',
            no_events: 'Ingen hendelser funnet',
            total_with_count: '{count} totalt',
            prev: 'Forrige',
            new_subscription: '+ Nytt abonnement',
            subscriber_type: 'Abonnent Type',
            subscriber_id: 'Abonnent ID',
            event_type_pattern: 'Hendelse Type Mønster',
            event_type_pattern_placeholder: 'f.eks. incident.*',
            filter_expression: 'Filter Uttrykk',
            filter_expression_placeholder: 'Valgfritt filter',
            subscriber_type_hint: 'Velg hvordan hendelser leveres: webhook sender HTTP-forespørsler, e-post sender varsler, kø ruter til en meldingskø.',
            subscriber_id_hint: 'Skriv inn destinasjonen for hendelser. For webhooks, bruk en URL. For e-post, bruk en adresse. For køer, bruk kønavnet.',
            event_type_pattern_hint: 'Match hendelser etter type med jokertegn. Bruk * for å matche et segment (f.eks. incident.* matcher incident.created, incident.resolved).',
            filter_expression_hint: 'Valgfritt. Filtrer hendelser ytterligere etter innhold (f.eks. severity=high).',
            subscriber_id_placeholder_webhook: 'f.eks. https://din-app.com/webhook',
            subscriber_id_placeholder_email: 'f.eks. varsler@ditt-firma.com',
            subscriber_id_placeholder_queue: 'f.eks. hendelses-ko',
            event_pattern: 'Hendelse Mønster',
            filter: 'Filtrer',
            enabled: 'Aktivert',
            delivery_stats: 'Levering Statistikk',
            no_subscriptions: 'Ingen abonnementer funnet',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} levert / {failed} mislyktes',
            confirm_delete_subscription: 'Slett dette abonnement?',
            unresolved: 'Uløst',
            resolved: 'Løst',
            event_id: 'Hendelse ID',
            subscription_id: 'Abonnement ID',
            error_code: 'Feil Kode',
            error_message: 'Feil Melding',
            attempts: 'Forsøk',
            last_failed: 'Siste mislykkede',
            no_dlq_entries: 'Ingen døde brev oppføringer funnet',
            dlq_count: '{count} post(er) i alt',
            fetch_events_error: 'Kunne ikke hente hendelser. Vennligst prøv igjen.',
            fetch_subscriptions_error: 'Kunne ikke hente abonnementer. Vennligst prøv igjen.',
            fetch_dlq_error: 'Kunne ikke hente DLQ-oppføringer. Vennligst prøv igjen.',
            create_subscription_error: 'Kunne ikke opprette abonnement',
            toggle_subscription_error: 'Kunne ikke veksle abonnement',
            delete_subscription_error: 'Kunne ikke slette abonnement',
            retry_error: 'Gjenforsøk mislyktes',
            dismiss_error: 'Avvisning mislyktes',
            event_published: 'Arrangementet er publisert',
            publish_event_error: 'Kunne ikke publisere hendelsen. Prøv igjen.',
            priority_label: 'Prioritet',
            priority_low: 'Lav',
            priority_normal: 'Normal',
            priority_high: 'Høy',
            payload_helper_text: 'Skriv inn hendelsesdata som JSON. Eksempel: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Abonnement opprettet',
            subscription_toggled: 'Abonnement slått av',
            subscription_deleted: 'Abonnement slettet',
            dlq_retried: 'DLQ-melding forsøkt på nytt',
            dlq_dismissed: 'DLQ-melding avvist',
        },
        definitions: {
            title: 'Koblingsdefinisjoner',
            page_title: 'Koblingsdefinisjoner',
            add: 'Legg til definisjon',
            new_definition: 'Ny koblingsdefinisjon',
            edit_definition: 'Rediger koblingsdefinisjon',
            no_definitions: 'Fant ingen koblingsdefinisjoner',
            definition_count: '{count} definisjon(er)',
            all_categories: 'Alle kategorier',
            select_category: 'Velg en kategori...',
            capabilities: 'Evner',
            capabilities_hint: 'Muligheter (kommaseparert)',
            capabilities_placeholder: 'f.eks. lese, skrive, synkronisere',
            created: 'Koblingsdefinisjon opprettet',
            updated: 'Koblingsdefinisjonen er oppdatert',
            fetch_error: 'Kunne ikke hente koblingsdefinisjoner. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette koblingsdefinisjon',
            update_error: 'Kunne ikke oppdatere koblingsdefinisjon',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD-innsamling',
            page_title: 'BRD-registrering',
            create_brd: 'Opprett BRD',
            brd_title: 'BRD-tittel',
            description: 'Beskrivelse',
            requirements: 'Krav',
            status: 'Status',
            stakeholders: 'Interessenter',
            connector_label: 'Kobling',
            select_connector: 'Velg en kobling...',
            approval: 'Godkjenning',
            no_brds_found: 'Ingen BRD-er funnet',
            decision_records: 'Beslutningsregistreringer',
            create_decision: 'Opprett beslutning',
            alternatives_considered: 'Vurderte alternativer',
            alternative_name: 'Alternativnavn',
            reason_rejected: 'Grunn til avvisning',
            add_alternative: 'Legg til alternativ',
            remove_alternative: 'Fjern alternativ {name}',
            alternatives: 'Alternativer',
            rationale: 'Begrunnelse',
            selected_connector: 'Valgt kobling',
            comma_separated_names: 'Kommaseparerte navn',
            brd_created: 'BRD opprettet',
            decision_record_created: 'Beslutningspost opprettet',
            no_decision_records_found: 'Ingen beslutningsposter funnet',
            aria_brds_table: 'BRDer',
            aria_decision_records_table: 'Beslutningsposter',
            aria_approve: 'Godkjenn {name}',
            aria_archive: 'Arkiver {name}',
            brd_title_help: 'Et kort, lesbart navn for denne BRD - f.eks. "Jira-billettintegrering", "Okta SSO-utrulling".',
            brd_title_placeholder: 'f.eks. Jira billettintegrering',
            description_help: 'Forretningsproblemet løser denne koblingen. Hvilket utfall forventer du når det er live?',
            description_placeholder: 'f.eks. Automatiser oppretting av hendelsesbilletter fra overvåkingsvarsler, slik at vakttelefon blir søkt innen 60-tallet.',
            connector_help: 'Valgfritt. Velg en kobling som denne BRD er for - la den stå tom hvis koblingen ikke er valgt ennå.',
            requirements_help: 'Kommaseparert liste over funksjoner du må ha — f.eks. "OAuth2-pålogging, webhook-levering, revisjonseksport".',
            requirements_placeholder: 'OAuth2-pålogging, webhook-levering, revisjonseksport',
            stakeholders_help: 'Kommaseparerte navn eller e-poster til personer som må melde seg av.',
            selected_connector_help: 'Velg koblingen denne avgjørelsen gjelder.',
            rationale_help: 'Hvorfor denne kontakten ble valgt. Dekk de avgjørende faktorene slik at en fremtidig leser forstår oppfordringen.',
            rationale_placeholder: 'f.eks. Valgte Jira fordi den allerede inneholder billetthistorikken vår og teamet er trent på den.',
            alternatives_considered_help: 'Andre koblinger evaluert og hvorfor de ikke ble valgt. Legger til forsvarlighet for revisjon.',
            alternative_name_placeholder: 'f.eks. ServiceNå',
            reason_rejected_placeholder: 'f.eks. Koster 3× høyere, ingen fordel for vår brukssituasjon',
        },
        security_reviews: {
            title: 'Sikkerhetsgjennomganger',
            page_title: 'Sikkerhetsgjennomganger',
            create_review: 'Opprett gjennomgang',
            connector_name: 'Koblingsnavn',
            connector_label: 'Kobling',
            select_connector: 'Velg en kobling...',
            reviewer: 'Anmelder',
            select_reviewer: 'Velg en anmelder...',
            findings: 'Funn (ett per linje)',
            risk_level: 'Risikonivå',
            risk_level_placeholder: 'f.eks. høy, middels, lav',
            notes: 'Notater',
            review_status: 'Gjennomgangsstatus',
            checklist: 'Sjekkliste',
            privacy_impact: 'Personvernkonsekvens',
            approval_gate: 'Godkjenningsport',
            no_reviews_found: 'Ingen gjennomganger funnet',
            review_created: 'Sikkerhetsgjennomgang opprettet',
            create_control_mapping: 'Opprett kontrollkobling',
            control_mapping_created: 'Kontrollkobling opprettet',
            connector_id_label: 'Koblings-ID',
            control_id_label: 'Kontroll-ID',
            control_name_label: 'Kontrollnavn',
            aria_approve: 'Godkjenn {name}',
            aria_reject: 'Avvis {name}',
            scan_imported: 'Skanning importert',
            import_scan: 'Importer skanning',
            scan_format: 'Skanningsformat',
            scan_data: 'Skanningsdata',
            import_results: 'Importresultater',
            recommendations: 'Anbefalinger',
            auto_populated_from: 'Automatisk utfylt fra {tool}',
            manual_entry: 'Manuell inntasting',
            automation_type: 'Automatiseringstype',
            select_automation_type: 'Velg automatiseringstype...',
            automation_full: 'Full — kobling automatiserer denne kontrollen fullstendig',
            automation_partial: 'Delvis — kobling bidrar med bevis; mennesker handler fortsatt',
            automation_monitoring: 'Kun overvåking — koblingen observerer, håndhever ikke',
            control_id_help: 'Bruk koden fra samsvarsskjemaet ditt (f.eks. ISO-27001-A.12.1, SOC2-CC6.1 eller din interne kontroll-ID).',
            control_name_help: 'Kort menneskelest navn (f.eks. "Nettverkstilgangsbegrensning").',
            description_label: 'Beskrivelse',
            load_sarif_example: 'Last inn SARIF eksempel',
            load_json_example: 'Last inn tilpasset JSON eksempel',
            scan_format_help: 'SARIF er OASIS-standarden som sendes ut av de fleste skannere (Snyk, CodeQL, Semgrep). Bruk tilpasset JSON for verktøy som ikke sender ut SARIF.',
            scan_data_help: 'Lim inn hele skanningen, last opp en .sarif/.json-fil eller klikk på en Last inn eksempel-knapp for å se formen.',
            upload_file_label: 'Last opp .sarif- eller .json-fil',
            invalid_scan_json: 'Kunne ikke analysere skannedata som JSON. Se etter syntaksfeil nær det uthevede tegnet.',
            save_as_review: 'Lagre som sikkerhetsgjennomgang',
            discard_preview: 'Forkast forhåndsvisningen',
            preview_hint: 'Dette er en forhåndsvisning — ingenting er lagret ennå. Klikk på "Lagre som sikkerhetsgjennomgang" for å opprette en ventende gjennomgang forhåndsutfylt med disse verdiene.',
            reviewer_required_for_save: 'Velg en anmelder for å lagre denne skanningen som en sikkerhetsgjennomgang.',
        },
        poc_scorecards: {
            title: 'POC-resultatkort',
            page_title: 'POC-resultatkort',
            create_scorecard: 'Opprett resultatkort',
            connector_label: 'Kobling',
            select_connector: 'Velg en kobling...',
            vendor_name: 'Leverandørnavn',
            recommendation: 'Anbefaling',
            recommendation_placeholder: 'f.eks. Fortsett, Ikke fortsett, Trenger videre evaluering',
            criteria: 'Evalueringskriterier',
            criterion_name: 'Kriterienavn',
            score: 'Poeng',
            max_score: 'Maks poeng',
            notes: 'Notater',
            add_criterion: 'Legg til kriterium',
            remove_criterion: 'Fjern kriterium {name}',
            total_score: 'Totalpoeng',
            api_quality: 'API-kvalitet',
            reliability: 'Pålitelighet',
            support_quality: 'Støttekvalitet',
            cost_score: 'Kostnadspoeng',
            overall_score: 'Totalpoeng',
            evidence: 'Dokumentasjon',
            no_scorecards_found: 'Ingen resultatkort funnet',
            scorecard_created: 'POC-resultatkort opprettet',
            connector_help: 'Velg kontakten denne POC-evalueringen er for.',
            vendor_name_help: 'Leverandøren eller produktet som evalueres – f.eks. "Okta", "Datadog", "Snøfnugg".',
            vendor_name_placeholder: 'f.eks. Okta, Datadog, Snowflake',
            recommendation_help: 'Din siste samtale etter evalueringen. Hold det kort og bestemt.',
            criteria_help: 'Poeng leverandøren på hvert evalueringskriterium. Legg til én rad per kriterium (API kvalitet, pålitelighet, støtte, kostnad osv.).',
            criterion_name_help: 'Hva du scorer — f.eks. "API Kvalitet", "Plitelighet", "Støtte", "Kostnad".',
            criterion_name_placeholder: 'f.eks. API Kvalitet',
            score_help: 'Poeng gitt for dette kriteriet. Må ikke overstige Max Score.',
            max_score_help: 'Toppscore tilgjengelig for dette kriteriet. Bruk samme skala på tvers av rader slik at totaler er sammenlignbare.',
            notes_help: 'Valgfritt. Legg til bevis, forbehold eller lenker - alt som forklarer poengsummen.',
        },
        common_labels: {
            aria_filter_instances: 'Filtrer koblingsforekomster',
            aria_filter_definitions: 'Filtrer koblingsdefinisjoner',
            aria_filter_credentials: 'Filtrer legitimasjon',
            aria_filter_events: 'Filtrer hendelser',
            aria_filter_dlq: 'Filtrer feilmeldingskø',
            aria_instance_summary: 'Koblingsforekomstoversikt',
            aria_active_connectors: 'Aktive koblinger',
            aria_error_connectors: 'Koblinger med feil',
            aria_credential_health: 'Helseoversikt for legitimasjon',
            aria_items_per_page: 'Elementer per side',
            show_password: 'Vis passord',
            hide_password: 'Skjul passord',
            payload_json_label: 'Nyttelast (JSON)',
            payload_invalid_json: 'Nyttelasten må være gyldig JSON',
            placeholder_event_type: 'f.eks. incident.created',
            placeholder_source_module: 'f.eks. monitoring',
            placeholder_version: 'f.eks. 1.0',
        },
        statuses: {
            configuring: 'Konfigurerer',
            testing: 'Testing',
            active: 'Aktiv',
            paused: 'Pause',
            error: 'Feil',
            disabled: 'Deaktivert',
            draft: 'Utkast',
            approved: 'Godkjent',
            archived: 'Arkivert',
            pending: 'Venter',
            rejected: 'Avvist',
        },
        system_types: {
            application: 'Applikasjon',
            database: 'Database',
            service: 'Service',
            infrastructure: 'Infrastruktur',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'On-Premise',
            cloud: 'Sky',
            hybrid: 'Hybrid',
            managed: 'Administrert',
        },
        system_statuses: {
            active: 'Aktiv',
            decommissioned: 'Avviklet',
            planned: 'Planlagt',
            under_review: 'Under gjennomgang',
        },
        automation_statuses: {
            planning: 'Planlegging',
            in_progress: 'Pågår',
            completed: 'Fullført',
            on_hold: 'På vent',
        },
        gap_types: {
            functional: 'Funksjonell',
            technical: 'Teknisk',
            security: 'Sikkerhet',
            compliance: 'Overholdelse',
            process: 'Prosess',
        },
        severity_labels: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Lav',
        },
        gap_statuses: {
            open: 'Åpen',
            in_progress: 'Pågår',
            mitigated: 'Mitigert',
            closed: 'Lukket',
            accepted: 'Akseptert',
        },
        verification_statuses: {
            not_started: 'Ikke startet',
            in_progress: 'Pågår',
            verified: 'Verifisert',
            failed: 'Feilet',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Intern',
            email: 'E-post',
            queue: 'Kø',
        },
        credential_types: {
            password: 'Passord',
            api_key: 'API-nøkkel',
            oauth2_client: 'OAuth2-klient',
            certificate: 'Sertifikat',
            token: 'Token',
            ssh_key: 'SSH-nøkkel',
            imap_credentials: 'IMAP-legitimasjon',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Sky',
            security: 'Sikkerhet',
            itsm: 'ITSM',
            custom: 'Egendefinert',
        },
        delivery_statuses: {
            pending: 'Ventende',
            delivered: 'Levert',
            partial: 'Delvis',
            failed: 'Feilet',
        },
        automation_planning: {
            title: 'Automatiseringsplanlegging',
            page_title: 'Automatiseringsplanlegging',
            program_name: 'Programnavn',
            program_status: 'Status',
            description: 'Beskrivelse',
            linked_artifacts: 'Tilknyttede artefakter',
            create_program: 'Opprett program',
            edit_program: 'Rediger program',
            no_programs_found: 'Ingen programmer funnet',
            program_created: 'Program opprettet',
            program_updated: 'Program oppdatert',
            target_date: 'Måldato',
            owner: 'Eier',
            select_status: 'Velg status...',
            aria_programs_table: 'Automatiseringsprogrammer',
            aria_edit: 'Rediger {name}',
            export_programs: 'Eksporter programmer',
            title_label: 'Programtittel',
            title_help: 'Et kort, lesbart navn på automatiseringsprogrammet – f.eks. "Q3-hendelse-til-Jira-utrulling", "PDPL automatisering av tilgangsforespørsel".',
            title_placeholder: 'f.eks. Q3-hendelse-til-Jira-utrulling',
            description_help: 'Hva dette programmet dekker og resultatet du forventer når det er live. Valgfritt, men anbefalt.',
            description_placeholder: 'f.eks. Kablet overvåkingsvarsler til Jira, slik at vakttelefon blir søkt innen 60-årene.',
            owner_label: 'Programeier',
            owner_help: 'Personen som er ansvarlig for dette programmet. De vil motta varsler om fremdrift og porter.',
            select_owner: 'Velg en eier...',
            frameworks_in_scope: 'Rammer i omfang',
            frameworks_in_scope_help: 'Samsvarsrammeverk dette programmet støtter. Hjelper revisjonssporet med å vise hvilke regelverk automatiseringen er styrt av.',
            select_frameworks: 'Velg rammer...',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_completed: 'Fullført',
            status_archived: 'Arkivert',
            change_status: 'Endre status',
            status_updated: 'Status oppdatert',
            aria_change_status: 'Endre status for {name}',
        },
        system_inventory: {
            title: 'Systemoversikt',
            page_title: 'Systemoversikt',
            system_name: 'Systemnavn',
            system_type: 'Systemtype',
            hosting: 'Hosting',
            status: 'Status',
            description: 'Beskrivelse',
            create_system: 'Opprett system',
            edit_system: 'Rediger system',
            no_systems_found: 'Ingen systemer funnet',
            system_created: 'System opprettet',
            system_updated: 'System oppdatert',
            select_type: 'Velg type...',
            select_hosting: 'Velg hosting...',
            select_status: 'Velg status...',
            owner: 'Eier',
            department: 'Avdeling',
            data_categories: 'Datakategorier',
            data_categories_placeholder: 'Kommaseparert, f.eks. PII, økonomi, helse',
            jurisdiction: 'Jurisdiksjon',
            jurisdiction_placeholder: 'f.eks. EU, USA, Globalt',
            interfaces: 'Grensesnitt',
            interfaces_placeholder: 'Kommaseparert, f.eks. REST API, SFTP, JDBC',
            owner_placeholder: 'Velg en ansvarlig eier...',
            department_placeholder: 'Velg den ansvarlige organisasjonsenheten...',
            system_name_hint: 'Bruk det forretningsanerkjente system- eller applikasjonsnavnet.',
            system_type_hint: 'Klassifiser systemet slik at rapportering kan gruppere lignende eiendeler.',
            hosting_hint: 'Identifiser hvor systemet kjører for bosted, sikkerhet og leverandørvurdering.',
            status_hint: 'Angi livssyklustilstanden som brukes av anmeldelser, eksport og revisjonsbevis.',
            owner_hint: 'Velg den aktive brukeren som er ansvarlig for systemstyring og oppfølgingsreiser.',
            department_hint: 'Bruk organisasjonsenheten som er ansvarlig for å drifte eller styre dette systemet.',
            data_categories_hint: 'List opp dataklassene som behandles av dette systemet, separert med komma.',
            jurisdiction_hint: 'Registrer den viktigste juridiske eller vertsjurisdiksjonen for overholdelseskartlegging.',
            interfaces_hint: 'List opp tilkoblings- eller integrasjonsveier som brukes til å utveksle data.',
            description_hint: 'Oppsummer hva systemet gjør og hvorfor det betyr noe for datakartet.',
            owner_department_required: 'Eier og avdeling er påkrevd.',
            aria_systems_table: 'Systemoversikt',
            aria_edit: 'Rediger {name}',
            export_systems: 'Eksporter systemer',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Sporingsmatrise',
            page_title: 'Sporingsmatrise',
            brd_selector: 'Velg BRD',
            requirement: 'Krav',
            linked_artifact: 'Tilknyttet artefakt',
            verification_status: 'Verifiseringsstatus',
            verified_by: 'Verifisert av',
            create_entry: 'Opprett oppføring',
            edit_entry: 'Rediger oppføring',
            no_entries_found: 'Ingen oppføringer funnet',
            entry_created: 'Oppføring opprettet',
            entry_updated: 'Oppføring oppdatert',
            select_brd: 'Velg en BRD...',
            select_status: 'Velg status...',
            aria_matrix_table: 'Sporingsmatrise',
            aria_edit: 'Rediger oppføring for {name}',
            export_matrix: 'Eksporter matrise',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Krav-ID',
        },
        gap_register: {
            title: 'Gap-register',
            page_title: 'Gap-register',
            gap_title: 'Gap-tittel',
            gap_type: 'Gap-type',
            severity: 'Alvorlighetsgrad',
            status: 'Status',
            description: 'Beskrivelse',
            remediation_plan: 'Utbedringsplan',
            assigned_to: 'Tildelt til',
            due_date: 'Forfallsdato',
            create_gap: 'Opprett gap',
            edit_gap: 'Rediger gap',
            no_gaps_found: 'Ingen gap funnet',
            gap_created: 'Gap opprettet',
            gap_updated: 'Gap oppdatert',
            select_type: 'Velg type...',
            select_severity: 'Velg alvorlighetsgrad...',
            select_status: 'Velg status...',
            aria_gaps_table: 'Gap-register',
            aria_edit: 'Rediger {name}',
            export_gaps: 'Eksporter gap',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Rammeverk-kobling',
            framework_linkage_placeholder: 'f.eks. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Rammerksomfang',
            framework_scope_placeholder: 'f.eks. GDPR, PCI-DSS',
            procedure_linkage: 'Prosedyrekobling',
            procedure_linkage_placeholder: 'f.eks. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Regulatorisk kilde',
            regulatory_source_placeholder: 'f.eks. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Kontrollmapping',
            control_mappings_section: 'Kontroller kartlegginger',
            no_control_mappings: 'Ingen kontrolltilordninger ennå. Klikk "Create Control Mapping" for å legge til en.',
            automation_type_col: 'Automatisering',
        },
        gap_register_extra: {
            framework_filter: 'Rammeverk',
            framework_id: 'Rammeverks-ID',
            framework_id_placeholder: 'f.eks. GDPR, ISO 27001',
            all_frameworks: 'Alle rammeverk',
        },
        poc_scorecards_extra: {
            criteria_count: 'Kriterier',
        },
        export_labels: {
            export_brds: 'Eksporter BRD-er',
            export_decision_records: 'Eksporter beslutningsregistre',
            export_security_reviews: 'Eksporter sikkerhetsgjennomganger',
            export_poc_scorecards: 'Eksporter POC-poengkort',
            export_instances: 'Eksporter forekomster',
            export_definitions: 'Eksporter definisjoner',
            export_credentials: 'Eksporter legitimasjon',
            export_events: 'Eksporter hendelser',
            exporting: 'Eksporterer...',
            export_success: 'Eksport fullført',
            export_error: 'Eksport mislyktes',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Trekk ut forpliktelser',
            extracting: 'Trekker ut …',
            results_heading: 'Utdragede forpliktelser ({count})',
            tokens_label: '≈ {count} tokens',
            no_obligations: 'Ingen forpliktelsesmønstre oppdaget i denne kontraktsteksten.',
            col_class: 'Klasse',
            col_summary: 'Sammendrag (redigerbar)',
            col_excerpt: 'Klausulutdrag',
            col_due_date: 'Forfallsdato (redigerbar)',
            col_priority: 'Prioritet',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Oppsigelsestid',
            class_audit: 'Revisjonsrett',
            class_insurance: 'Forsikring',
            class_sla: 'Svar SLA',
            class_breach: 'Varsling om brudd',
            class_renewal: 'Fornyelsesvindu',
            citations_label: 'Sitater',
            accept_hint: 'Accept vil opprette én oppgave per forpliktelse (tak 8) med prioritet + forfallsdato vist.',
            accept_label: 'Godta forpliktelser',
            reject_label: 'Avvis rapport',
            resolve_summary: 'Oppløsning: {status} · {tasks} oppgave(r) opprettet',
            error_generic: 'Kunne ikke trekke ut forpliktelser. Prøv igjen eller sjekk kontraktsteksten.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Utkast til begrunnelse',
            drafting: 'Utarbeide begrunnelse …',
            results_heading: 'AI-utkastet resonnement ({risk_level})',
            tokens_label: '≈ {count} tokens',
            narrative_label: 'Begrunnelsesfortelling',
            controls_heading: 'Kompenserende kontroller',
            no_controls: 'Ingen kompenserende kontroller foreslått.',
            citations_label: 'Sitater',
            accept_label: 'Godta resonnement',
            reject_label: 'Avvis utkast',
            accept_hint: 'Accept registrerer resonnementet om det vedvarende utkastet for IAT-avstamning. Narrative +-kontrollene oppdaterer ikke akseptoppføringen automatisk – operatørlime tilbake er nødvendig.',
            resolve_summary: 'Oppløsning: {status}',
            error_generic: 'Kunne ikke utarbeide begrunnelse. Prøv på nytt eller kontroller statusen for risikoaksept.',
        },
        redline_assistant: {
            run_label: 'Redline med bibliotek',
            running: 'Kjører redline...',
            results_heading: 'Redline-forslag ({count})',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'Ingen standardklausulhull oppdaget.',
            jurisdiction_label: 'Jurisdiksjon',
            col_kind: 'Snill',
            col_severity: 'Alvorlighetsgrad',
            col_title: 'Forslag',
            col_excerpt: 'Matchende utdrag',
            col_apply: 'Søk',
            kind_missing: 'Mangler standard',
            kind_weaker: 'Svakere enn standard',
            kind_jurisdiction: 'Jurisdiksjonsoverlegg',
            proposed_label: 'Foreslått tekst',
            citations_label: 'Sitater',
            accept_label: 'Godta valgt',
            reject_label: 'Avvis rapport',
            partial_label: 'Bruk utvalg',
            accept_hint: 'Bruk utvalg registrerer de valgte forslagene på utkastet for IAT-avstamning. Ingen automatisk redigering brukes på kontraktsteksten – operatøren kjører rødlinjen.',
            resolve_summary: 'Oppløsning: {status} · {count} forslag er brukt',
            error_generic: 'Kunne ikke produsere en rød linje. Prøv på nytt eller sjekk kontraktstatusen.',
            clause_library_link: 'Administrer klausulbibliotek',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Klausul bibliotek',
            add_label: 'Legg til klausul',
            edit_label: 'Rediger',
            delete_label: 'Slett',
            slug_label: 'Snegl',
            title_label: 'Tittel',
            body_label: 'Kropp',
            jurisdiction_label: 'Jurisdiksjon',
            tags_label: 'Tagger (kommaseparert)',
            must_contain_label: 'Må inneholde markører (kommaseparert)',
            severity_label: 'Alvorlighet når det mangler',
            no_clauses: 'Ingen klausuler ennå. Legg til en for å starte redline-assistenten.',
            save_label: 'Lagre',
            cancel_label: 'Avbryt',
            load_error: 'Kunne ikke laste inn klausulbiblioteket.',
            save_error: 'Kunne ikke lagre klausulen.',
            delete_error: 'Kunne ikke slette klausulen.',
            delete_confirm: 'Vil du slette klausulen "{slug}"? Dette kan ikke angres.',
        },
        subprocessor_impact: {
            assess_label: 'Vurder innvirkning',
            assessing: 'Vurderer virkningen …',
            results_heading: 'Påvirkning av underprosessor ({severity})',
            tokens_label: '≈ {count} tokens',
            risk_delta_label: 'Risiko delta',
            notification_heading: 'Kundevarsling',
            subject_label: 'Emne',
            deadline_label: 'Meldingsfrist',
            body_label: 'Meldingsorgan',
            required_fields_label: 'Felt i varselet (art.28(2))',
            mitigation_heading: 'Reduserende trinn',
            citations_label: 'Sitater',
            accept_label: 'Godta vurdering',
            reject_label: 'Avslå vurdering',
            accept_hint: 'Accept vil opprette en kundevarslingsoppgave med SLA fristen som forfallsdato.',
            resolve_summary: 'Oppløsning: {status}{task}',
            error_generic: 'Kunne ikke vurdere denne underbehandlerendringen. Prøv på nytt eller sjekk endringsposten.',
        },
        onboarding_evidence: {
            collect_label: 'Kjør AI bevisinnsamling',
            collecting: 'Samler bevis...',
            results_heading: 'Bevisinnsamling ({tier})',
            tokens_label: '≈ {count} tokens',
            findings_heading: 'Eksisterende bevis',
            missing_heading: 'Manglende gjenstander',
            no_findings: 'Ingen eksisterende bevis oppdaget.',
            no_missing: 'Ingen nødvendige gjenstander mangler for dette risikonivået.',
            next_step_label: 'Neste trinn',
            class_dpa: 'Databehandleravtale',
            class_soc2: 'SOC 2-rapport',
            class_iso27001: 'ISO 27001 sertifikat',
            class_insurance: 'Cyberforsikring',
            class_incident_history: 'Tidligere hendelse',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Godta rapporten',
            reject_label: 'Avvis rapport',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Kunne ikke kjøre AI bevisinnsamling. Prøv på nytt eller sjekk statusen for ombordstigningssaken.',
        },
        no_permission_title: 'Utilstrekkelige tillatelser',
        no_permission_message: 'Du har ikke tillatelse til å få tilgang til leverandørens risikostyring.',
        unknown_vendor: 'Ukjent leverandør',
        nav: {
            dashboard: 'Risikooversikt',
            scoring: 'Risikoscoring',
            questionnaires: 'Spørreskjemaer',
            contracts: 'Kontrakter',
            gates: 'Godkjenningsporter',
            reviews: 'Leverandørvurderinger',
            signals: 'Risikosignaler',
            reassessments: 'Revurderinger',
            portfolio: 'Risikoportefølje',
            acceptances: 'Risikoaksept',
            review_routing: 'Gjennomgangsruting',
            onboarding_cases: 'Onboarding-saker',
            sub_processor_register: 'Register over underdatabehandlere',
        },
        dashboard: {
            title: 'Leverandørrisikooversikt',
            page_title: 'Leverandørrisiko Dashboard',
            loading: 'Laster dashboarddata...',
            risk_tier_header: 'Risikonivå',
            count_header: 'Telle',
            percentage_header: 'Prosentandel',
            sections_nav: 'Leverandørrisikoseksjoner',
            urgent_expiry: 'Haster: utløper innen 14 dager',
            no_previous_tier: 'Ingen tidligere nivå',
            view_as_chart: 'Vis som diagram',
            view_as_table: 'Vis som tabell',
            showing_chart: 'Viser diagramvisning',
            showing_table: 'Viser datatabellvisning',
            load_error: 'Kunne ikke laste leverandørrisikodashboard',
            risk_distribution: 'Risikofordelingsoversigt',
            recent_tier_changes: 'Nylige Nivå Endringer',
            no_tier_changes: 'Ingen nylige nivåendringer.',
            review_kpis: 'Gjennomgang KPI-er',
            total_vendors_reviewed: 'Totalt leverandører gjennomgått',
            reviews_by_outcome: 'Gjennomganger etter Resultat',
            contract_coverage: 'Kontrakt Dekning',
            total_vendors: 'Totalt Leverandører',
            with_active_dpa: 'Med Aktive DPA',
            without_dpa: 'Uten DPA',
            dpa_coverage: 'DPA Dekning',
            active_contracts_by_type: 'Aktive Kontrakter etter Type',
            expiring_contracts: 'Utløpende kontrakter (neste 90 dager)',
            no_expiring_contracts: 'Ingen kontrakter utløper innen de neste 90 dagene.',
            vendor: 'Leverandør',
            previous_tier: 'Forrige Nivå',
            current_tier: 'Nåværende Nivå',
            changed_at: 'Endret den',
            expires_at: 'Utløper den',
            days_remaining: 'Dager igjen',
            days: 'dager',
            vendors_label: 'leverandører',
            no_data: 'Ingen data',
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Lav',
            expiring_acceptances_heading: 'Utløpende risikogodkjenninger',
            expiring_30d_label: 'Utløper om 30 dager',
            expiring_60d_label: 'Utløper om 60 dager',
            expiring_90d_label: 'Utløper om 90 dager',
        },
        scoring: {
            title: 'Risikoscoring',
            page_title: 'Leverandørrisikoscore',
            filter_scores: 'Filtrer risikopoeng',
            filter_by_tier: 'Filtrer etter risikonivå',
            scores_pagination: 'Paginering av risikopoeng',
            config_dimensions: 'Konfigurasjon av poengdimensjoner',
            scores_loaded: '{count} risikopoeng lastet',
            weight_must_equal_100: 'Totalvekt må tilsvare 100 (gjeldende: {current})',
            out_of_10: 'av 10, vurdert til {tier}',
            dimension_name: 'Dimensjon {index} navn',
            dimension_weight: '{name} vekt (prosent)',
            must_equal_100: 'må tilsvare 100',
            config_title: 'Poengberegning Konfigurasjon',
            scores_title: 'Leverandør Risiko Poeng',
            new_score_title: 'Poeng en Leverandør',
            new: 'Ny Poeng',
            vendor_id: 'Leverandør ID',
            risk_tier: 'Risiko Nivå',
            score: 'Poeng',
            scored_at: 'Scoret At',
            no_scores: 'Ingen risiko poeng funnet.',
            all_tiers: 'Alle Nivåer',
            load_config_error: 'Kunne ikke laste risikoscoringskonfigurasjon',
            update_config: 'Oppdater poengkonfigurasjon',
            update_config_error: 'Kunne ikke oppdatere risikoscoringskonfigurasjon',
            load_scores_error: 'Kunne ikke laste leverandørrisikopoeng',
            calculate_error: 'Kunne ikke beregne leverandørrisikoscore',
            weight: 'vekt',
            score_range: 'Poeng (1-5)',
            method: 'Metode',
        },
        questionnaires: {
            title: 'Spørreskjemaer',
            page_title: 'Spørreskjemaer fra leverandører',
            filter_questionnaires: 'Spørreskjemafiltre',
            questionnaire_views: 'Spørreskjemavisninger',
            templates_list: 'Liste over spørreskjemamaler',
            instances_list: 'Liste over forekomster av spørreskjema',
            filter_by_status: 'Filtrer etter spørreskjemastatus',
            templates_per_page: 'Maler per side',
            instances_per_page: 'Forekomster per side',
            select_template: 'Velg spørreskjemamal',
            template_category: 'Malkategori',
            templates_tab: 'Maler',
            instances_tab: 'Instanser',
            new: 'Ny Spørreskjema',
            new_template: 'Ny mal',
            new_instance: 'Ny instans',
            vendor_id: 'Leverandør ID',
            template_id: 'Mal ID',
            assigned_to: 'Tildelt Til',
            due_date: 'Due Dato',
            responses: 'Svar',
            risk_score: 'Ferdigstillelse',
            no_questionnaires: 'Ingen spørreskjemaer funnet.',
            load_templates_error: 'Kunne ikke laste spørreskjemamaler',
            load_instances_error: 'Kunne ikke laste spørreskjemaer',
            create_template_error: 'Kunne ikke opprette spørreskjemamal',
            initiate_error: 'Kunne ikke starte spørreskjema',
            submit_error: 'Kunne ikke sende inn spørreskjema',
            review_error: 'Kunne ikke gjennomgå spørreskjema',
            resubmit: 'Send inn igjen',
            approve_error: 'Kunne ikke godkjenne spørreskjema',
            reject_error: 'Kunne ikke avvise spørreskjema',
            resubmit_error: 'Kunne ikke sende inn spørreskjema på nytt',
            templates_loaded: '{count} spørreskjemamaler lastet inn',
            instances_loaded: '{count} spørreskjemaforekomster lastet',
            fill_responses_cta: 'Fyll ut svar',
            fill_responses_title: 'Fyll ut spørreundersøkelsessvar',
            template_missing: 'Mal ikke funnet.',
            responses_saved: 'Svar lagret.',
            load_template_error: 'Kunne ikke laste inn mal.',
            version_history: 'Versjonshistorikk',
            download_previous_version: 'Last ned tidligere versjon',
            trigger: 'Utløser',
            template_label: 'Mal',
            reviewer: 'Anmelder',
            reviewer_required: 'En korrekturleser må tildeles for {trigger} spørreskjemaer (separasjon av oppgaver).',
            select_option: 'Velg et alternativ',
            multi_choice_hint: 'Velg alt som passer',
            category_initial_onboarding: 'Første ombordstigning',
            category_annual_review: 'Årlig gjennomgang',
            category_incident_triggered: 'Hendelse utløst',
            category_contract_renewal: 'Kontraktsfornyelse',
            template_editor: {
                page_title: 'Rediger spørreskjemamal',
                preview_page_title: 'Forhåndsvis spørreskjemamal',
                edit_structure: 'Rediger struktur',
                preview: 'Forhåndsvisning',
                actions: 'Handlinger',
                back_to_list: 'Tilbake til maler',
                load_error: 'Kunne ikke laste inn malen',
                save_error: 'Kunne ikke lagre malstrukturen',
                saved: 'Malstrukturen er lagret.',
                sections_heading: 'Seksjoner',
                questions_heading: 'Spørsmål',
                add_section: 'Legg til seksjon',
                edit_section: 'Rediger seksjon',
                delete_section: 'Slett seksjon',
                move_section_up: 'Flytt delen opp',
                move_section_down: 'Flytt delen ned',
                confirm_delete_section: 'Vil du slette delen «{title}»? Alle spørsmålene vil bli fjernet.',
                section_title: 'Seksjonstittel',
                section_description: 'Seksjonsbeskrivelse',
                section_id: 'Seksjons-ID',
                section_id_hint: 'Automatisk generert med mindre du angir en eksplisitt.',
                no_sections: 'Ingen seksjoner ennå. Legg til en seksjon for å begynne.',
                no_questions: 'Ingen spørsmål i denne delen ennå.',
                add_question: 'Legg til spørsmål',
                edit_question: 'Rediger spørsmål',
                delete_question: 'Slett spørsmålet',
                move_question_up: 'Flytt spørsmålet opp',
                move_question_down: 'Flytt spørsmålet ned',
                confirm_delete_question: 'Vil du slette spørsmålet "{text}"?',
                question_text: 'Spørsmålstekst',
                question_type: 'Spørsmålstype',
                question_id: 'Spørsmåls-ID',
                evidence_required: 'Bevis kreves',
                risk_weight: 'Risikovekt',
                scoring_map: 'Scoringskart',
                scoring_map_json_hint: 'JSON objektkartlegger svarverdier til numeriske poeng.',
                scoring_map_invalid_json: 'Poengkartet må være gyldig JSON.',
                options_heading: 'Svaralternativer',
                add_option: 'Legg til alternativ',
                remove_option: 'Fjern alternativet',
                option_label: 'Etikett',
                option_value: 'Verdi',
                option_risk_weight: 'Risikovekt',
                min_options_error: 'Valgspørsmål må ha minst 2 alternativer.',
                option_label_required: 'Hvert alternativ trenger en etikett.',
                option_value_required: 'Hvert alternativ trenger en verdi.',
                option_value_unique: 'Alternativverdier må være unike.',
                branching_rules_heading: 'Forgreningsregler',
                branching_rules_hint: 'Vis eller skjul dette spørsmålet basert på svaret på et tidligere spørsmål.',
                add_branching_rule: 'Legg til forgreningsregel',
                remove_branching_rule: 'Fjern forgreningsregelen',
                branching_condition_question: 'Hvis spørsmål',
                branching_condition_value: 'har verdi',
                branching_action: 'deretter',
                branching_action_show: 'vis dette spørsmålet',
                branching_action_hide: 'skjul dette spørsmålet',
                branching_select_condition_question: 'Velg tidligere spørsmål',
                branching_select_condition_value: 'Velg verdi',
                save: 'Lagre struktur',
                saving: 'Lagrer...',
                cancel: 'Avbryt',
                close: 'Lukk',
                question_text_required: 'Spørsmålstekst er påkrevd.',
                section_title_required: 'Seksjonstittel er påkrevd.',
                type_single_choice: 'Enkeltvalg',
                type_multi_choice: 'Flervalg',
                type_free_text: 'Fri tekst',
                type_numeric: 'Numerisk',
                type_date: 'Dato',
                type_file_upload: 'Filopplasting',
                type_yes_no: 'Ja / Nei',
                preview_heading: 'Forhåndsvisning av mal',
                preview_subtitle: 'Skrivebeskyttet forhåndsvisning av hvordan spørreskjemaet ser ut for en leverandør.',
                preview_no_content: 'Denne malen har ingen seksjoner eller spørsmål ennå.',
                preview_branching_notice: 'Forgreningsreglene blir evaluert under utfylling; forhåndsvisning viser hvert spørsmål.',
            },
            review_modal: {
                title: 'Gjennomgå spørreskjemasvarene',
                open_cta: 'Gjennomgå',
                loading: 'Laster inn spørreskjema...',
                template_missing: 'Malen ble ikke funnet.',
                load_error: 'Kunne ikke laste inn spørreskjemaet for gjennomgang.',
                annotation_heading: 'Merknader',
                annotation_placeholder: 'Legg til en anmelderkommentar for dette spørsmålet...',
                annotation_status_label: 'Merknadsstatus',
                annotation_status_acceptable: 'Akseptabelt',
                annotation_status_concern: 'Bekymring',
                annotation_status_requires_action: 'Krever handling',
                review_comments_label: 'Generelle vurderingskommentarer',
                review_comments_placeholder: 'Oppsummer vurderingsfunnene dine...',
                justification_label: 'Begrunnelse for vedtak',
                justification_placeholder: 'Forklar begrunnelsen for avgjørelsen din...',
                reject_requires_comments: 'Å avvise et spørreskjema krever gjennomgangskommentarer.',
                decision_heading: 'Beslutning',
                decision: {
                    approved: 'Godkjenne',
                    rejected: 'Avvis',
                    additional_info_requested: 'Be om ytterligere informasjon',
                },
                submit: 'Send inn anmeldelse',
                submitting: 'Sender inn anmeldelse...',
                cancel: 'Avbryt',
                submit_success: 'Anmeldelse sendt inn.',
                submit_error: 'Kunne ikke sende inn anmeldelse.',
                separation_of_duties_error: 'Du kan ikke se gjennom et spørreskjema du har fått i oppdrag å fylle ut. Separasjon av oppgaver krever en annen anmelder.',
                decision_required: 'Velg en vurderingsbeslutning.',
                announce_opened: 'Gjennomgangsmodal åpnet.',
                announce_submitted: 'Anmeldelse sendt inn.',
            },
            instance_detail: {
                submitted_at: 'Sendt inn',
                reviewed_at: 'Anmeldt',
                reviewer: 'Anmelder',
                review_decision: 'Beslutning',
                review_comments: 'Gjennomgå kommentarer',
                decision_approved: 'Godkjent',
                decision_rejected: 'Avvist',
                decision_additional_info_requested: 'Mer info etterspurt',
                decision_pending: 'Venter på vurdering',
                expand_comments: 'Vis fullstendige kommentarer',
                collapse_comments: 'Skjul hele kommentarer',
                no_reviewer: 'Ingen anmelder tildelt',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Utkast',
                in_progress: 'Pågår',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Godkjent',
                rejected: 'Avvist',
                expired: 'Utløpte',
            },
            export: {
                portfolio_label: 'Eksportportefølje',
                instance_label: 'Eksporter',
                portfolio_success: 'Porteføljeeksport klar.',
                instance_success: 'Spørreskjemaeksport klar.',
                portfolio_error: 'Kunne ikke eksportere spørreskjemaporteføljen.',
                instance_error: 'Kunne ikke eksportere spørreskjemaet.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Noen spørsmål er skjult av forgreningsregler.',
                cycle_warning: 'En syklus ble oppdaget i forgreningsregler. Berørte spørsmål er som standard synlige.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Kontrakter',
            page_title: 'Leverandørkontrakter',
            new: 'Ny Kontrakt',
            vendor_id: 'Leverandør ID',
            filter_contracts: 'Filtrer kontrakter',
            filter_by_document_type: 'Filtrer etter dokumenttype',
            filter_by_status: 'Filtrer etter kontraktstatus',
            per_page: 'Kontrakter per side',
            expired_notice: 'Denne kontrakten er utløpt',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Dokument Type',
            expiry_date: 'Utløp Dato',
            no_contracts: 'Ingen kontrakter funnet.',
            all_document_types: 'Alle Dokument Typer',
            load_error: 'Kunne ikke laste kontrakter',
            create_error: 'Kunne ikke opprette kontrakt',
            delete_error: 'Kunne ikke slette kontrakt',
            update_error: 'Kunne ikke oppdatere status',
            confirm_delete: 'Slett kontrakt "{title}"?',
            renewal_date: 'Fornyelse Dato',
            effective_date: 'Gjeldende Dato',
            parties: 'Parter',
            summary: 'Sammendrag',
            n_contracts: '{count} kontrakter',
            n_contract: '{count} kontrakt',
            contracts_loaded: '{count} kontrakter lastet',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Sikkerhetstillegg',
            doc_type_scc_annex: 'SCC-vedlegg',
            doc_type_sub_processor_list: 'Underdatabehandlerliste',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Annet',
            status_draft: 'Utkast',
            status_under_negotiation: 'Under forhandling',
            status_pending_signature: 'Venter på signatur',
            status_active: 'Aktiv',
            status_expired: 'Utløpt',
            status_terminated: 'Avsluttet',
            status_superseded: 'Erstattet',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Godkjenningsporter',
            page_title: 'Portevalueringer',
            new: 'Ny Port Evaluering',
            vendor_id: 'Leverandør ID',
            gate_name: 'Port Navn',
            outcome: 'Resultat',
            evaluator: 'Evaluerer',
            no_gates: 'Ingen port evalueringer funnet.',
            load_error: 'Kunne ikke laste portevalueringer',
            evaluate_error: 'Kunne ikke evaluere leverandørport',
            detail_error: 'Kunne ikke hente evalueringsdetalj',
            override_error: 'Kunne ikke overstyre portevaluering',
            criteria_results: 'Kriterier Resultater',
            evaluate_form: 'Vurder leverandørporten',
            per_page: 'Evalueringer per side',
            passed: 'Bestått',
            failed: 'Mislykket',
            override: 'Overstyr',
            override_justification: 'Overstyring Begrunnelse',
            override_placeholder: 'Angi begrunnelse for at tilsidesætte denne portevaluering',
            approval_id: 'Godkjenning ID',
            overall_passed: 'Bestått',
            overall_failed: 'Ikke bestått',
            overall_overridden: 'Overstyrt',
            evaluations_loaded: '{count} portvurderinger lastet',
            create_approval_cta: 'Opprett godkjenning',
        },
        reviews: {
            title: 'Leverandørvurderinger',
            page_title: 'Leverandøranmeldelser',
            new: 'Ny Gjennomgang',
            vendor_id: 'Leverandør ID',
            filter_by_outcome: 'Filtrer etter vurderingsresultat',
            per_page: 'Anmeldelser per side',
            kpi_summary: 'Se gjennom KPI sammendraget',
            review_outcome_label: 'Gjennomgå resultatet',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Rediger vurderingsresultatet',
            reviews_loaded: '{count} leverandøranmeldelser lastet inn',
            reviewer: 'Gjennomgår',
            review_type: 'Gjennomgang Type',
            outcome: 'Resultat',
            findings: 'Funn',
            no_reviews: 'Ingen leverandør gjennomganger funnet.',
            load_kpis_error: 'Kunne ikke hente leverandørgjennomgangs-KPI-er',
            load_error: 'Kunne ikke hente leverandørgjennomganger',
            update_error: 'Kunne ikke oppdatere leverandøranmeldelsen',
            create_error: 'Kunne ikke opprette leverandørgjennomgang',
            total_reviewed: 'Totalt leverandører gjennomgått',
            total_reviews: 'Totalt Gjennomganger',
            satisfactory: 'Tilfredsstillende',
            concerns_identified: 'Bekymringer identifisert',
            remediation_required: 'Utbedring Påkrevd',
            escalated: 'Eskalert',
            program_id: 'Program-ID',
            period_id: 'Periode ID',
            task_id: 'Oppgave ID',
            recommendations: 'Anbefalinger',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Risikosignaler',
            page_title: 'Risikosignaler',
            new_signal: 'Nytt risikosignal',
            signal_type: 'Signaltype',
            severity: 'Alvorlighetsgrad',
            description: 'Beskrivelse',
            source: 'Kilde',
            vendor_id: 'Leverandør-ID',
            certification_expiry: 'Sertifiseringsutløp',
            breach_notification: 'Bruddvarsel',
            financial_instability: 'Finansiell ustabilitet',
            compliance_change: 'Samsvarsendring',
            custom: 'Egendefinert',
            active: 'Aktiv',
            acknowledged: 'Bekreftet',
            resolved: 'Løst',
            no_signals: 'Ingen risikosignaler funnet',
            source_external_feed: 'Ekstern kilde',
            source_manual: 'Manuell',
            source_integration: 'Integrasjon',
            source_regulatory: 'Regulatorisk',
            source_media: 'Media',
            trigger_reassessment_prompt: 'Dette signalet er høyt alvorlighetsnivå. Starte leverandørrevurdering nå?',
            reassessment_triggered: 'Revurdering startet.',
            reassessment_trigger_error: 'Kunne ikke starte revurdering.',
        },
        reassessments: {
            title: 'Revurderingsoppgaver',
            page_title: 'Revurderingsoppgaver',
            trigger: 'Utløs revurdering',
            trigger_success: 'Revurdering utløst',
            reason: 'Årsak',
            deadline: 'Tidsfrist',
            assigned_to: 'Tildelt til',
            vendor_id: 'Leverandør-ID',
            outcome: 'Resultat',
            new_risk_level: 'Nytt risikonivå',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Fullfør revurdering',
            complete_success: 'Revurdering fullført',
            pending: 'Venter',
            in_progress: 'Pågår',
            completed: 'Fullført',
            cancelled: 'Avbrutt',
            no_tasks: 'Ingen revurderingsoppgaver funnet',
            risk_trend: 'Risikotrend',
            notes: 'Notater',
        },
        portfolio: {
            title: 'Risikoportefølje',
            page_title: 'Risikoportefølje',
            dashboard: 'Porteføljeoversikt',
            vendor_id: 'Leverandør-ID',
            total_vendors: 'Totalt antall leverandører',
            average_score: 'Gjennomsnittlig risikoscore',
            high_risk_count: 'Høyrisikoleverandører',
            trend_direction: 'Trendretning',
            improved: 'Forbedret',
            stable: 'Stabil',
            degraded: 'Forverret',
            vendor_trend: 'Leverandørtrend',
            outstanding_questionnaires: 'Utestående spørreskjemaer',
            overdue_assessments: 'Forfalte vurderinger',
            no_outstanding: 'Ingen utestående spørreskjemaer',
            no_overdue: 'Ingen forfalte vurderinger',
            due_date: 'Forfallsdato',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Risikoakseptregister',
            page_title: 'Risikoakseptregister',
            new_acceptance: 'Ny risikoaksept',
            vendor_id: 'Leverandør-ID',
            risk_level: 'Risikonivå',
            conditions: 'Betingelser',
            justification: 'Begrunnelse',
            expiry_date: 'Utløpsdato',
            create_success: 'Risikoaksept opprettet',
            review_condition: 'Gjennomgå betingelse',
            condition_met: 'Betingelse oppfylt',
            condition_not_met: 'Betingelse ikke oppfylt',
            review_success: 'Betingelse gjennomgått',
            export_register: 'Eksporter register',
            active: 'Aktiv',
            expired: 'Utløpt',
            revoked: 'Tilbakekalt',
            no_acceptances: 'Ingen risikoaksepter funnet',
            met_count: '{met}/{total} møtt',
        },
        review_routing: {
            title: 'Gjennomgangsruting',
            page_title: 'Gjennomgå ruting',
            route_to_reviewers: 'Send til gjennomgangere',
            route_success: 'Sendt til gjennomgangere',
            vendor_id: 'Leverandør-ID',
            reviewer_id: 'Gjennomganger-ID',
            reviewer_role: 'Gjennomgangerrolle',
            questionnaire_id: 'Spørreskjema-ID',
            review_status: 'Gjennomgangsstatus',
            reviewers_label: 'Anmeldere (user_id, rolle per linje)',
            pending: 'Venter',
            in_review: 'Under gjennomgang',
            completed: 'Fullført',
            no_routing: 'Ingen gjennomgangsruting funnet',
            primary_reviewer: 'Primær gjennomganger',
            secondary_reviewer: 'Sekundær gjennomganger',
            approver: 'Godkjenner',
        },
        onboarding: {
            title: 'Leverandør-onboarding-saker',
            page_title: 'Leverandør-onboarding-saker',
            new_case: 'Ny onboarding-sak',
            case_detail: 'Onboarding-sak',
            open_mps_dossier_filing: 'Åpne MPS-innsending',
            dossier_filing_description: 'Oppretter en ny MPS-innsending forhåndsutfylt fra denne onboarding-saken. Innsendingen kobles tilbake til saken.',
            dossier_filing_dpia_id: 'DPIA-ID',
            dossier_filing_dpia_id_placeholder: 'f.eks. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Innsendingstittel',
            dossier_filing_form_type: 'Skjematype',
            dossier_filing_submit: 'Opprett innsending',
            dossier_filing_validation_error: 'DPIA-ID og tittel kreves.',
            dossier_filing_created: 'MPS-innsending opprettet',
            dossier_filing_error: 'Kunne ikke opprette MPS-innsending.',
            linked_questionnaire_due_date: 'Forfallsdato',
            linked_questionnaire_assignee: 'Tildelt',
            linked_questionnaire_submitted_at: 'Sendt inn',
            score_last_scored: 'Sist scoret',
            score_dimensions: 'Dimensjoner',
            linked_contract_document_type: 'Dokumenttype',
            linked_contract_effective_date: 'Virkningsdato',
            linked_contract_expiry_date: 'Utløpsdato',
            linked_sub_processor_countries: 'Behandlingsland',
            linked_sub_processor_last_assessment: 'Siste vurdering',
            linked_sub_processor_next_assessment: 'Neste vurdering',
            case_id: 'Sak-ID',
            vendor: 'Leverandør',
            case_type: 'Sakstype',
            service_description: 'Tjenestebeskrivelse',
            data_categories: 'Datakategorier',
            systems_accessed: 'Tilgangte systemer',
            jurisdiction: 'Jurisdiksjon',
            hosting_model: 'Vertsmodell',
            controller_role: 'Behandlingsansvarlig/Databehandler-rolle',
            status_pipeline: 'Statuspipeline',
            tabs_label: 'Onboarding-saksseksjoner',
            per_page: 'Saker per side',
            filter_cases: 'Filtrer onboarding-saker',
            cases_count: 'saker',
            cases_loaded: 'onboarding-saker lastet',
            no_cases: 'Ingen onboarding-saker funnet.',
            vendor_lookup_placeholder: 'Skriv inn leverandør-ID',
            data_categories_placeholder: 'f.eks., persondata, finans, helse',
            systems_placeholder: 'f.eks., CRM, ERP, HR-system',
            status_open: 'Åpen',
            status_in_progress: 'Pågår',
            status_awaiting_approval: 'Venter på godkjenning',
            status_approved: 'Godkjent',
            status_remediation_required: 'Utbedring påkrevd',
            status_closed: 'Lukket',
            type_new_vendor: 'Ny leverandør',
            type_service_change: 'Tjenesteendring',
            type_renewal: 'Fornyelse',
            type_reassessment: 'Revurdering',
            tab_overview: 'Oversikt',
            tab_due_diligence: 'Aktsomhet',
            tab_security_assessment: 'Sikkerhetsvurdering',
            tab_contractual_controls: 'Kontraktuelle kontroller',
            tab_sub_processors: 'Underdatabehandlere',
            tab_approval_gates: 'Godkjenningsporter',
            tab_evidence: 'Dokumentasjon',
            tab_placeholder: 'Innholdet i denne seksjonen fylles ut under onboarding-prosessen.',
            completion_not_started: 'Ikke startet',
            completion_in_progress: 'Pågår',
            completion_completed: 'Fullført',
            completion_requiring_attention: 'Krever oppmerksomhet',
            gate_name: 'Portnavn',
            approver: 'Godkjenner',
            decision: 'Beslutning',
            comments: 'Kommentarer',
            decide: 'Ta beslutning',
            decision_approved: 'Godkjent',
            decision_rejected: 'Avvist',
            decision_deferred: 'Utsatt',
            decision_pending: 'Pending',
            no_approvals: 'Ingen godkjenningsporter konfigurert for denne saken.',
            approval_success: 'Godkjenningsbeslutning registrert',
            approval_error: 'Kunne ikke registrere godkjenningsbeslutning',
            transition_status: 'Endre status',
            confirm_transition: 'Endre sak fra "{from}" til "{to}"?',
            transition_success: 'Saksstatus endret',
            transition_error: 'Kunne ikke endre saksstatus',
            create_success: 'Onboarding-sak opprettet',
            create_error: 'Kunne ikke opprette onboarding-sak',
            load_error: 'Kunne ikke laste onboarding-saker',
            detail_error: 'Kunne ikke laste saksdetaljer',
            export: 'Eksporter',
            export_error: 'Kunne ikke eksportere onboarding-sak',
            evidence_tab_description: 'Last opp og eksporter bevis for onboardingssaken.',
            upload_evidence_label: 'Last opp bevis',
            attached_evidence_heading: 'Vedlagte bevis',
            no_evidence_attached: 'Ingen bevis vedlagt ennå.',
            export_bundle_button: 'Eksporter bevispakke',
            load_attachments_error: 'Kunne ikke laste inn bevis.',
            linked_questionnaires_heading: 'Lenkede spørreskjemaer',
            no_linked_questionnaires: 'Ingen lenkede spørreskjemaer.',
            linked_scores_heading: 'Lenkede risikovurderinger',
            no_linked_scores: 'Ingen lenkede vurderinger.',
            latest_score_label: 'Nyeste vurdering',
            linked_contracts_heading: 'Lenkede kontrakter',
            no_linked_contracts: 'Ingen lenkede kontrakter.',
            linked_sub_processors_heading: 'Lenkede underleverandører',
            no_linked_sub_processors: 'Ingen lenkede underleverandører.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Merk som fullført',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Register over underdatabehandlere',
            page_title: 'Register over underdatabehandlere',
            new_record: 'Ny underdatabehandler',
            edit_record: 'Rediger underdatabehandler',
            onboarding_case: 'Onboarding-sak',
            services: 'Leverte tjenester',
            data_categories: 'Datakategorier',
            processing_countries: 'Behandlingsland',
            dpa_chain_status: 'DPA-kjedestatus',
            certifications: 'Sertifiseringer',
            last_assessment: 'Siste vurdering',
            next_assessment: 'Neste vurdering',
            change_history: 'Endringshistorikk',
            history: 'Historikk',
            changed_by: 'Endret av',
            comma_separated: 'Kommaseparerte verdier',
            filter_records: 'Filtrer oppføringer',
            filter_by_dpa: 'Filtrer etter DPA-kjedestatus',
            all_dpa_statuses: 'Alle DPA-statuser',
            per_page: 'Oppføringer per side',
            records_count: 'underdatabehandlere',
            records_loaded: 'oppføringer lastet',
            no_records: 'Ingen underdatabehandlere funnet.',
            no_history: 'Ingen endringshistorikk tilgjengelig.',
            dpa_active: 'Aktiv',
            dpa_pending: 'Ventende',
            dpa_expired: 'Utløpt',
            dpa_not_required: 'Ikke påkrevd',
            create_success: 'Underdatabehandler opprettet',
            create_error: 'Kunne ikke opprette underdatabehandler',
            update_success: 'Underdatabehandler oppdatert',
            update_error: 'Kunne ikke oppdatere underdatabehandler',
            load_error: 'Kunne ikke laste registeret',
            history_error: 'Kunne ikke laste endringshistorikk',
            export_csv: 'Eksporter CSV',
            export_json: 'Eksporter JSON',
            export_error: 'Kunne ikke eksportere data',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orkestreringsoversikt',
            runs: 'Orkestreringskjøringer',
            tasks: 'Utførelsesoppgaver',
            templates: 'Sjekklistemaler',
            vendor_requests: 'Leverandørforespørsler',
            proof_chain: 'Beviskjede',
            dsr_integration: 'DSR-integrasjon',
            vendor_verification: 'Leverandørverifisering',
            completeness: 'Fullstendighetsoversikt',
        },
        dashboard: {
            title: 'Orkestreringsoversikt',
            loading: 'Laster dashboarddata...',
            active_runs: 'Aktive Kjøringer',
            completion_rate: 'Ferdigstillelse Rate',
            avg_completion_time: 'Gj.sn. Ferdigstillelse Tid',
            overdue_tasks: 'Forsinkede Oppgaver',
            vendor_compliance: 'Leverandør Etterlevelse',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Nylige Kjøringer',
            no_recent_runs: 'Ingen recent orchestration runs.',
            run_id: 'Kjøring ID',
            ticket_id: 'Sak ID',
            progress: 'Fremdrift',
            systems: 'Systemer',
            vendors: 'Leverandører',
            load_error: 'Kunne ikke laste inn orkestreringsdashbordet. Vennligst prøv igjen.',
            aria_table: 'Nylige orkestreringsløp',
        },
        runs: {
            title: 'Orkestreringskjøringer',
            new: 'Ny Kjøring',
            run_id: 'Kjøring ID',
            ticket_id: 'Sak ID',
            progress: 'Fremdrift',
            systems: 'Systemer',
            vendors: 'Leverandører',
            data_subject_ref: 'Data Emne Ref',
            data_categories: 'Data Kategorier',
            enable_proof_chain: 'Aktiver Bevis Kjede',
            start: 'Start',
            started: 'Løpet startet',
            mark_ready: 'Merk klar',
            marked_ready: 'Kjør merket som klar',
            complete: 'Komplett',
            complete_exceptions: 'Komplett med unntak',
            pause: 'Pause',
            resume: 'Fortsett',
            cancel: 'Avbryt',
            no_runs: 'Ingen orchestration runs funnet.',
            load_error: 'Kunne ikke laste inn orkestreringskjøringer. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette orkestreringskjøring',
            aria_filter_status: 'Filtrer etter kjørestatus',
            aria_table: 'Orkestrering kjører',
            placeholder_ticket_id: 'f.eks. TKT-000123',
            placeholder_data_subject_ref: 'f.eks. DS-REF-001',
            placeholder_data_categories: 'f.eks. personal_data, økonomisk, helse',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Utførelsesoppgaver',
            task_id: 'Oppgave ID',
            run_id: 'Kjøring ID',
            system_name: 'System Navn',
            assigned_to: 'Tildelt Til',
            sla_deadline: 'SLA Frist',
            progress: 'Fremdrift',
            enter_run_id: 'Angi Kjøring ID to filtrer...',
            select_run: 'Velg en kjøring for å se oppgaver',
            unassigned: 'Ikke tildelt',
            overdue: 'Forsinket',
            assign_task: 'Tildel Oppgave',
            assignee_id: 'Tilordnet-ID',
            update_status: 'Oppdatering Status',
            new_status: 'Ny Status',
            no_transitions: 'Denne oppgaven er i en endelig tilstand og kan ikke endre status.',
            select_status_placeholder: 'Velg en ny status',
            skip_justification: 'Begrunnelse for hopp over',
            failure_reason: 'Årsak til feil',
            no_tasks: 'Ingen utførelse oppgaver funnet.',
            new_task: 'Ny oppgave',
            system_id: 'System-ID',
            data_classification: 'Dataklassifisering',
            load_error: 'Kunne ikke laste execution tasks. Prøv igjen.',
            assign_error: 'Kunne ikke assign task',
            status_error: 'Kunne ikke oppdatere oppgavestatus',
            no_tasks_for_run: 'Denne slettekjøringen har ingen utførelsesoppgaver ennå.',
            aria_filter_run_id: 'Filtrer etter kjøre-ID',
            aria_filter_status: 'Filtrer etter oppgavestatus',
            aria_table: 'Utførelsesoppgaver',
            view_steps: 'Vis trinn',
            steps_modal_title: 'Utførelsestrinn',
            step_title: 'Trinn',
            step_status: 'Trinnstatus',
            step_notes: 'Notater',
            step_evidence: 'Bevisfiler',
            step_evidence_required: 'Bevis kreves for dette trinnet',
            step_evidence_missing: 'Ingen bevis lastet opp ennå',
            step_save: 'Lagre trinn',
            step_saved: 'Trinn oppdatert',
            step_save_error: 'Kunne ikke oppdatere trinnet',
            create_success: 'Utførelsesoppgave opprettet',
            create_error: 'Kunne ikke opprette utførelsesoppgave',
            step_upload_label: 'Legg ved bevis',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Sjekklistemaler',
            new: 'Ny Mal',
            template_id: 'Mal ID',
            system_type: 'Systemtype',
            est_duration: 'Ansl.. Varighet',
            steps: 'Steg',
            action_type: 'Handling Type',
            instructions: 'Instruksjoner',
            mandatory: 'Obligatorisk',
            add_step: 'Add Steg',
            step_n: 'Steg',
            no_templates: 'Ingen sjekkliste maler funnet.',
            all_system_types: 'Alle System Typer',
            load_error: 'Kunne ikke laste inn sjekklistemaler. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette mal for sjekkliste',
            aria_filter_system_type: 'Filtrer etter systemtype',
            aria_filter_status: 'Filtrer etter malstatus',
            aria_table: 'Sjekklistemaler',
            placeholder_name: 'f.eks. sjekkliste for sletting av database',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Malen ble oppdatert',
            create_success_msg: 'Malen ble opprettet',
            update_error: 'Kunne ikke oppdatere malen',
            activate_success: 'Malen ble aktivert',
            activate_error: 'Kunne ikke aktivere malen',
            archive_success: 'Malen ble arkivert',
            archive_error: 'Kunne ikke arkivere malen',
            clone_success: 'Malen ble klonet',
            clone_error: 'Kunne ikke klone malen',
        },
        vendor_requests: {
            title: 'Leverandør Sletting Forespørsler',
            new: 'Ny Forespørsel',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Forespørsel ID',
            vendor_name: 'Leverandør Navn',
            vendor_id: 'Leverandør ID',
            data_subject_ref: 'Data Emne Ref',
            data_categories: 'Data Kategorier',
            legal_basis: 'Rettslig grunnlag',
            sla_days: 'SLA Dager',
            requested_completion: 'Anmodet færdiggørelse',
            communications: 'Kommunikasjoner',
            total_requests: 'Totalt Forespørsler',
            no_requests: 'Ingen leverandør sletting forespørsler funnet.',
            filter_vendor_id: 'Filter etter Leverandør ID...',
            change_status: 'Endre Status',
            new_status: 'Ny Status',
            actions: 'Handlinger',
            load_error: 'Kunne ikke laste inn forespørsler om leverandørsletting. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette forespørsel om leverandørsletting',
            dashboard_error: 'Kunne ikke laste inn kontrollpanelet for leverandørsletting. Vennligst prøv igjen.',
            aria_filter_status: 'Filtrer etter forespørselsstatus',
            aria_filter_vendor_id: 'Filtrer etter leverandør-ID',
            aria_table: 'Forespørsler om sletting fra leverandører',
            placeholder_vendor_id: 'f.eks. VND-001',
            placeholder_vendor_name: 'f.eks. Acme Analytics',
            placeholder_data_subject_ref: 'f.eks. DS-REF-001',
            placeholder_data_categories: 'f.eks. personal_data, analyser',
            placeholder_legal_basis: 'f.eks. PDPL artikkel 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Bevis Kjede Leser',
            enter_run_id: 'Angi Kjøring ID...',
            load_chain: 'Last Kjede',
            verify_chain: 'Verifiser Kjede',
            verifying: 'Verifiserer...',
            chain_valid: 'Kæden er gyldig',
            chain_invalid: 'Kjede er brutt',
            chain_length: 'Kjede Lengde',
            broken_at_index: 'Brutt ved indeks',
            no_proof_records: 'Ingen proof records found for this run.',
            proof_id: 'Bevis ID',
            entity_type: 'Enhet Type',
            entity_id: 'Enhet ID',
            event_hash: 'Hendelse Hash',
            previous_hash: 'Forrige Hash',
            attestation: 'Attestering',
            attestation_actor: 'Utført av',
            attestation_signed_at: 'Signert',
            attestation_signature: 'Signatur',
            attestation_evidence_refs: 'Bevisreferanser',
            run_field_label: 'Slettekjøring',
            select_run: 'Velg en slettekjøring…',
            aria_select_run: 'Slettekjøring for oppslag i beviskjeden',
            chain_broken_here: 'Kæde brudt her',
            load_error: 'Kunne ikke laste den sikre kjeden. Vennligst prøv igjen.',
            verify_error: 'Kunne ikke bekrefte beviskjeden. Vennligst prøv igjen.',
            aria_run_id: 'Kjør ID for bevis kjedeoppslag',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Endre status',
            request_type: 'Forespørselstype',
            data_subject_hint: 'Identifikator for den registrerte hvis data slettes (f.eks. e-post eller kunde-ID). Samsvarer vanligvis med forespørreren på den valgte DSR-saken.',
            title: 'DSR-slettingsintegrasjon',
            create_run: 'Opprett DSR-slettingskjøring',
            dsr_case_id: 'DSR-saks-ID',
            data_subject_id: 'Registrertes ID',
            systems: 'Målsystemer',
            route_to_department: 'Send til avdeling',
            confirm_deletion: 'Bekreft sletting',
            no_runs: 'Ingen DSR-slettingskjøringer funnet',
            status: 'Slettingsstatus',
            deadline: 'Frist',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Leverandørslettingsverifisering',
            create_followup: 'Opprett oppfølging',
            deadline: 'Bekreftelsesfrist',
            escalation_contact: 'Eskaleringskontakt',
            escalate: 'Eskaler',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Ingen leverandøroppfølginger funnet',
            vendor_request: 'Leverandørforespørsel',
            vendor: 'Leverandør',
            unknown_vendor: 'Ukjent leverandør',
            overdue_badge: 'Forfalt',
            mark_confirmed: 'Merk som bekreftet',
            mark_confirmed_for: 'Merk sletting som bekreftet for {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Leverandørsletting bekreftet',
            confirmation_failed: 'Kunne ikke bekrefte leverandørsletting',
            select_request: 'Velg en leverandørforespørsel…',
            escalation_contact_placeholder: 'f.eks. dpo@vendor.example',
            create_success: 'Leverandøroppfølging opprettet',
            create_failed: 'Kunne ikke opprette leverandøroppfølging',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Leverandørsletting ble eskalert',
            escalation_failed: 'Kunne ikke eskalere leverandørsletting',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Slettingsfullstendighet',
            check_conflicts: 'Kontroller oppbevaringskonflikter',
            no_conflicts: 'Ingen oppbevaringskonflikter funnet',
            conflict_resolution: 'Konfliktløsning',
            extend_retention: 'Forleng oppbevaring',
            proceed_deletion: 'Fortsett med sletting',
            escalate_conflict: 'Eskaler',
            resolve: 'Løs konflikt',
            data_subject_id: 'Datasubjekt-ID',
            placeholder_data_subject_id: 'Angi datasubjekt-ID',
            data_subject_hint: 'Identifikator for personen hvis sletting du verifiserer (f.eks. e-post, kunde-ID eller DSR-referanse). Velg et kjent subjekt eller skriv inn ett.',
            guidance: 'Skriv inn eller velg en datasubjekt-ID, velg eventuelt systemer, og kjør kontrollen for å se sletteomfang og oppbevaringskonflikter.',
            completion: 'Slettegrad',
            pending_items: 'Ventende elementer',
            system: 'System',
            policy: 'Retningslinje',
            retention_end: 'Oppbevaringsslutt',
            completed_items: 'Fullførte Elementer',
            last_review_label: 'Siste anmeldelse',
            days_ago: 'dager siden',
            review_overdue: 'Gjennomgangssyklusen er overskredet. Ny gjennomgang kreves.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Venter',
            planning: 'Planlegging',
            ready: 'Klar',
            in_progress: 'Pågår',
            paused: 'Pauset',
            completed: 'Fullført',
            completed_with_exceptions: 'Fullført med Unntak',
            cancelled: 'Avbrutt',
        },
        task_status_labels: {
            pending: 'Ventende',
            assigned: 'Tildelt',
            in_progress: 'Pågår',
            awaiting_verification: 'Avventer Verifikasjon',
            verified: 'Verifisert',
            failed: 'Feilet',
            skipped: 'Hoppet Over',
        },
        system_type_labels: {
            database: 'Database',
            saas_application: 'SaaS-applikasjon',
            file_storage: 'Fillagring',
            email_system: 'E-postsystem',
            analytics_platform: 'Analyseplattform',
            backup_system: 'Backupsystem',
            vendor_managed: 'Leverandørstyrt',
            custom: 'Tilpasset',
        },
        template_status_labels: {
            draft: 'Utkast',
            active: 'Aktiv',
        },
        action_type_labels: {
            manual_check: 'Manuell Kontroll',
            api_call: 'API-kall',
            sql_query: 'SQL-spørring',
            script_execution: 'Skriptkjøring',
            file_deletion: 'Filsletting',
            verification: 'Verifikasjon',
            notification: 'Varsling',
            approval: 'Godkjenning',
        },
        request_status_labels: {
            draft: 'Utkast',
            sent: 'Sendt',
            acknowledged: 'Bekreftet',
            in_progress: 'Pågår',
            completed: 'Fullført',
            overdue: 'Forfalt',
            escalated: 'Eskalert',
            disputed: 'Bestridt',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiering',
            data_deletion: 'Datasletting',
            completion: 'Fullføring',
            verification: 'Verifikasjon',
            rollback: 'Tilbakerulling',
        },
        count_labels: {
            one_template: '1 mal',
            n_templates: '{n} maler',
            one_run: '1 kjøring',
            n_runs: '{n} kjøringer',
            one_task: '1 oppgave',
            n_tasks: '{n} oppgaver',
            one_request: '1 forespørsel',
            n_requests: '{n} forespørsler',
        },
        aria: {
            completion_rate: 'Fullføringsgrad: {pct}%',
            vendor_compliance: 'Leverandøroverholdelse: {pct}%',
            run_progress: 'Kjøringsfremgang: {pct}%',
            run_progress_systems: 'Fremgang: {completed} av {total} systemer behandlet',
            chain_broken: 'Kjede brutt ved denne posten',
            chain_valid: 'Kjede gyldig ved denne posten',
            chain_unknown: 'Kjedestatus ukjent',
            escalate_vendor: 'Eskaler {id}',
            resolve_conflict: 'Løs konflikt for {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Fullstendigheten av slettingen må vurderes minst hver 365. dag. Oppføringer som overskrider denne terskelen blir flagget for obligatorisk ny gjennomgang for å opprettholde regelmessig samsvar.',
            runs_data_d1_lifecycle_info: 'Orkestrasjonskjøringstilstander kartlegger til DATA-D1 livssyklus: planlegging = katalog, klar = klassifiser, in_progress = bruk regler, fullført = bevis generert, completed_with_exceptions = gjennomgang nødvendig. Hver overgang registreres i prøvekjeden.',
            vendor_record_linkage_info: 'Hver forespørsel om leverandørsletting er knyttet til leverandørposten via vendor_id. Fullføringsbekreftelse krever kryptografisk bevis fra beviskjeden før forespørselen kan merkes som fullført.',
            templates_internal_classification_info: 'Alle sjekklistemaler for sletting er klassifisert som interne organisasjonsdokumenter. De har ikke offisielle ID-er for forskriftsskjemaer og skal ikke sendes inn som forskriftsdokumenter.',
            proof_chain_evidence_info: 'Hver bevispost fanger opp datasubjektet/kategorien, tidsstempelet, aktøren, juridisk grunnlag og en SHA-256-hash. Sammen produserer disse feltene kryptografisk slettebevis som tilfredsstiller regulatoriske revisjonskrav.',
            retention_data_law_info: 'Oppbevaringsplaner på dette dashbordet er avledet fra datalovklassifiseringen som er tildelt hver datakategori. Når en klassifisering endres, oppdateres tilknyttede oppbevaringsregler automatisk for å sikre regulatorisk tilpasning.',
            dsr_erasure_route_info: 'DSR-slettekjøringer er knyttet til den opprinnelige Rights Request-saken. Den regulatoriske fristen (typisk 30 dager i henhold til GDPR art. 17) spores automatisk. Forfalte saker flagges for eskalering.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Oppdagelsesoversikt',
            scans: 'Oppdagelsesskanninger',
            staging_area: 'Mellomlagringsområde',
            dedup_rules: 'Dedupliseringsregler',
            classification_rules: 'Klassifiseringsregler',
            suggestions: 'Forslagskø',
            analytics: 'Forslagsanalyse',
            import_templates: 'Importmaler',
            connectors: 'Tilkoblingsstyring',
            discovery_cycles: 'Oppdagelsessykluser',
            owner_review: 'Eiergjennomgang',
            classification_lineage: 'Klassifiseringshistorikk',
        },
        dashboard: {
            title: 'Oppdagelsesoversikt',
            loading: 'Laster dashboarddata...',
            total_scans: 'Totalt Skanninger',
            active_scans: 'Aktive Skanninger',
            completed_30d: 'Fullførte (30d)',
            failed_30d: 'Mislykkede (30 d)',
            systems_discovered: 'Systemer oppdaget',
            pending_staged_records: 'Ventende staged poster',
            recent_scans: 'Nylige Skanninger',
            no_recent_scans: 'Ingen nylige skanninger.',
            scan_id: 'Skanning ID',
            discovered: 'Oppdaget',
            view_all_scans: 'Vis Alle Skanninger',
            staging_area: 'Oppsamlingsområde',
            suggestion_queue: 'Forslag Kø',
            records_reviewed: 'Gjennomgåtte poster',
            records_approved: 'Godkjente poster',
            exceptions_pending: 'Ventende unntak',
            view_as_chart: 'Vis som diagram',
            view_as_table: 'Vis som tabell',
        },
        scans: {
            title: 'Oppdagelsesskanninger',
            new: 'Ny Skanning',
            scan_id: 'Skanning ID',
            scan_type: 'Skanning Type',
            progress: 'Fremdrift',
            discovered: 'Oppdaget',
            no_scans: 'Ingen oppdagelse skanninger funnet.',
            all_scan_types: 'Alle Skanning Typer',
            source_config: 'Kilde Konfigurasjon (JSON)',
            invalid_json: 'Ugyldig JSON in kilde konfiguration',
            load_error: 'Kunne ikke laste oppdagelsesskann. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette oppdagelsesskan',
            change_status: 'Endre status',
            new_status: 'Ny status',
            actions: 'Handlinger',
            type_endpoint: 'Endepunkt',
            type_spreadsheet_import: 'Regnearkimport',
            type_cmdb_sync: 'CMDB-synkronisering',
            type_cloud_sync: 'Skysynkronisering',
            status_pending: 'Venter',
            status_running: 'Kjører',
            status_completed: 'Fullført',
            status_failed: 'Mislykket',
            status_cancelled: 'Avbrutt',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: 'Denne skannetypen er ennå ikke tilgjengelig. Bare endepunktskanninger kan opprettes.',
            adapter_not_implemented_warning: 'Ennå ikke tilgjengelig. Skanninger av denne typen kan ikke opprettes før støtte for dem er lansert.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Mellomlagringsområde',
            record_id: 'Post ID',
            source_scan: 'Kilde Skanning',
            entity_type: 'Enhet Type',
            suggested_name: 'Foreslått Navn',
            confidence: 'Tillit',
            no_records: 'Ingen staged records funnet.',
            promote: 'Forfrem',
            discard: 'Forkast',
            review: 'Gjennomgang',
            merge: 'Slå sammen',
            merge_title: 'Slå sammen post',
            merge_target_id: 'Slå sammen Mål ID',
            reject_reason_placeholder: 'Årsak til afvisning...',
            promotion_status: 'Forfremmelse Status',
            dedup_status: 'Deduplisering Status',
            all_source_types: 'Alle Kilde Typer',
            all_promotion_statuses: 'Alle Forfremmelse Statuser',
            all_dedup_statuses: 'Alle Deduplisering Statuser',
            load_error: 'Kunne ikke laste mellomlagrede poster. Vennligst prøv igjen.',
            promote_error: 'Kunne ikke forfremme post',
            reject_error: 'Kunne ikke avvise post',
            merge_error: 'Kunne ikke slå sammen post',
            review_error: 'Kunne ikke markere post til gjennomgang',
            bulk_promote_error: 'Kunne ikke masseforfremme records',
            bulk_reject_error: 'Kunne ikke masseavvise records',
            service_type_web_server: 'Webserver',
            service_type_database: 'Database',
            service_type_api_gateway: 'API-gateway',
            service_type_storage: 'Lagring',
            service_type_authentication: 'Autentisering',
            service_type_analytics: 'Analyse',
            service_type_unknown: 'Ukjent',
            source_type_endpoint_scan: 'Endepunktskanning',
            source_type_spreadsheet: 'Regneark',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Skyinventar',
            promotion_status_new: 'Ny',
            promotion_status_under_review: 'Under gjennomgang',
            promotion_status_promoted: 'Forfremmet',
            promotion_status_rejected: 'Avvist',
            promotion_status_merged: 'Slått sammen',
            dedup_status_unmatched: 'Ingen treff',
            dedup_status_potential_match: 'Mulig treff',
            dedup_status_exact_match: 'Eksakt treff',
            dedup_status_resolved: 'Løst',
            confirm_bulk_reject: 'Er du sikker på at du vil avvise de valgte postene?',
            scan_batch_id: 'Batch-ID',
            promote_to_registry_title: 'Oppgrader til dataregister',
            promote_modal_instructions: 'Oppretter et dataregisterelement med collection_method: observert og herkomstkobling til denne oppdagelsesposten. Elementet starter i utkaststatus for gjennomgang.',
            promote_label_category: 'Kategori',
            promote_label_name_override: 'Navn (valgfri overstyring)',
            placeholder_name_override: 'Standardinnstillingen til oppdagelsespostens navn/vertsnavn',
            promote_label_sensitivity: 'Foreslått følsomhet',
            inherit_from_category: 'Arv fra kategori',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Dedupliseringsregler',
            new: 'Ny Regel',
            rule_name: 'Regel Navn',
            match_fields: 'Samsvarsfelt',
            threshold: 'Terskel',
            no_rules: 'Ingen deduplisering regler funnet.',
            seed_defaults: 'Initialiser standarder',
            match_type: 'Samsvarstype',
            similarity_threshold: 'Likhet Terskel',
            field_name: 'Felt Navn',
            load_error: 'Kunne ikke laste dedupliseringsregler. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette dedupliseringsregel',
            toggle_error: 'Kunne ikke veksle dedupliseringsregel',
            seed_error: 'Kunne ikke initialisere standardverdier',
            priority: 'Prioritet',
            enabled_status: 'Aktivert',
            disabled_status: 'Deaktivert',
            default_badge: 'Standard',
            enable: 'Aktiver',
            disable: 'Deaktiver',
            match_type_exact: 'Eksakt',
            match_type_fuzzy: 'Omtrentlig',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP-område',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Klassifiseringsregler',
            new: 'Ny Regel',
            rule_name: 'Regel Navn',
            pattern: 'Mønster',
            classification: 'Klassifisering',
            no_rules: 'Ingen classification rules funnet.',
            seed_defaults: 'Initialiser standarder',
            run_rules_now: 'Kjør regler nå',
            run_rules_success: 'Genererte {count} nye klassifiseringsforslag(er)',
            run_rules_no_results: 'Ingen nye forslag generert – alle samsvarende regler har allerede ventende forslag',
            run_rules_error: 'Kunne ikke kjøre klassifiseringsregler. Vennligst prøv igjen.',
            taxonomy_id: 'Taksonomi ID',
            level_id: 'Nivå ID',
            condition_logic: 'Betingelse Logikk',
            weight: 'Vekt',
            conditions: 'Betingelser',
            condition_field: 'Felt',
            condition_operator: 'Operatør',
            condition_value: 'Verdi',
            effectiveness: 'Effektivitet',
            match_count: 'Treff',
            approval_count: 'Godkjenninger',
            rejection_count: 'Avvisninger',
            filter_taxonomy: 'Filter etter Taksonomi ID...',
            load_error: 'Kunne ikke laste klassifiseringsregler. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette klassifiseringsregel',
            seed_error: 'Kunne ikke initialisere standardverdier',
            toggle_error: 'Kunne ikke veksle regel. Vennligst prøv igjen.',
            priority: 'Prioritet',
            enabled_status: 'Aktivert',
            disabled_status: 'Deaktivert',
            enable: 'Aktiver',
            disable: 'Deaktiver',
            logic_and: 'OG',
            logic_or: 'ELLER',
            operator_equals: 'Er lik',
            operator_contains: 'Inneholder',
            operator_matches_regex: 'Matcher Regex',
            operator_in_list: 'I listen',
            operator_not_equals: 'Er ikke lik',
            operator_not_contains: 'Inneholder ikke',
            enabled_label: 'Aktivert',
            disabled_label: 'Deaktivert',
            taxonomy_label: 'Taksonomi',
            operator_label: 'Operatør',
            value_label: 'Verdi',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Forslagskø',
            entity_type: 'Enhet Type',
            suggested_name: 'Foreslått Navn',
            source: 'Kilde',
            confidence: 'Tillit',
            accept: 'Godta',
            reject: 'Avvis',
            no_suggestions: 'Ingen forslag i kø.',
            modify_approve: 'Endre & Godkjenn',
            delegate: 'Deleger',
            delegate_user_id: 'Deleger Bruker ID',
            new_level_id: 'Ny Nivå ID',
            reject_reason_placeholder: 'Årsak til afvisning...',
            current_level: 'Nåværende Nivå',
            suggested_level: 'Foreslått Nivå',
            filter_taxonomy: 'Filter etter Taksonomi ID...',
            all_confidence: 'Alle Tillit Bånd',
            load_error: 'Kunne ikke laste forslag. Vennligst prøv igjen.',
            approve_error: 'Kunne ikke godkjenne forslag',
            reject_error: 'Kunne ikke avvise forslag',
            modify_error: 'Kunne ikke endre og godkjenne forslag',
            delegate_error: 'Kunne ikke delegere forslag',
            bulk_approve_error: 'Kunne ikke massegodkjenne forslag',
            bulk_reject_error: 'Kunne ikke masseavvise forslag',
            suggestion_type_new_label: 'Ny etikett',
            suggestion_type_validation: 'Validering',
            suggestion_type_reclassification: 'Omklassifisering',
            status_pending: 'Venter',
            status_approved: 'Godkjent',
            status_rejected: 'Avvist',
            status_superseded: 'Erstattet',
            confidence_high: 'Høy',
            confidence_medium: 'Middels',
            confidence_low: 'Lav',
            confirm_bulk_reject: 'Er du sikker på at du vil avvise de valgte forslagene?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Alle Taksonomier',
        },
        analytics: {
            title: 'Forslagsanalyse',
            total_suggestions: 'Totalt Forslag',
            accepted: 'Godtatt',
            rejected: 'Avvist',
            pending: 'Ventende',
            acceptance_rate: 'Aksept Rate',
            by_entity_type: 'Etter Enhet Type',
            by_source: 'Etter Kilde',
            avg_decision_time: 'Gj.sn. Beslutning Tid',
            rule_effectiveness: 'Regel Effektivitet',
            by_status: 'Etter Status',
            by_confidence: 'Etter Tillit Bånd',
            load_error: 'Kunne ikke laste forslagsanalyse. Vennligst prøv igjen.',
            confidence_high: 'Høy',
            confidence_medium: 'Middels',
            confidence_low: 'Lav',
            by_confidence_band: 'Etter konfidensnivå',
            approval_rate: 'Godkjenningsrate',
            by_label_state: 'Etter etikettstatus',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Importmaler',
            new: 'Ny Mal',
            template_name: 'Mal Navn',
            file_format: 'Fil Format',
            column_mappings: 'Kolonne Kartlegginger',
            no_templates: 'Ingen import maler funnet.',
            source_column: 'Kilde Kolonne',
            target_field: 'Mål Felt',
            transform: 'Transformer',
            source_column_label: 'Kildekolonne',
            target_field_label: 'Målfelt',
            transform_label: 'Forvandle',
            confirm_delete: 'Er du sikker på at du vil slette denne importmalen?',
            load_error: 'Kunne ikke laste importmaler. Vennligst prøv igjen.',
            save_error: 'Kunne ikke lagre importmal',
            delete_error: 'Kunne ikke slette importmal',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Tilkoblingsstyring',
            new: 'Ny Kobling',
            connector_name: 'Kobling Navn',
            connector_type: 'Type',
            last_sync: 'Siste Synkronisering',
            no_connectors: 'Ingen koblinger funnet.',
            cmdb_syncs: 'CMDB Synkroniseringer',
            cloud_syncs: 'Sky Synkroniseringer',
            new_sync: 'Ny Synkronisering',
            no_syncs: 'Ingen synkroniseringer funnet.',
            cmdb_error: 'Kunne ikke laste CMDB-skann. Vennligst prøv igjen.',
            cloud_error: 'Kunne ikke laste skyskann. Vennligst prøv igjen.',
            trigger_error: 'Kunne ikke utløse synkronisering',
            trigger_sync_error: 'Kunne ikke trigger {type}',
            cmdb_section_title: 'CMDB-synkroniseringer',
            cloud_section_title: 'Sky-synkroniseringer',
            column_connector: 'Kobling',
            action_start: 'Start',
            confirm_cancel_sync: 'Avbryt denne synkroniseringen?',
            start_error: 'Kunne ikke starte synkronisering',
            cancel_error: 'Kunne ikke avbryte synkronisering',
            type_endpoint: 'Endepunkt-skanning',
            type_spreadsheet_import: 'Regneark-import',
            type_cmdb_sync: 'CMDB-synkronisering',
            type_cloud_sync: 'Sky-synkronisering',
        },
        discovery_cycles: {
            title: 'Oppdagelsessykluser',
            new_cycle: 'Ny oppdagelsessyklus',
            cycle_name: 'Syklusnavn',
            scope_type: 'Omfangstype',
            scope_ids: 'Omfangs-ID',
            cadence_days: 'Frekvens (dager)',
            status: 'Status',
            no_cycles: 'Ingen oppdagelsessykluser funnet',
            compare: 'Sammenlign sykluser',
            delta_report: 'Endringsrapport',
            new_records: 'Nye poster',
            changed_records: 'Endrede poster',
            removed_records: 'Fjernede poster',
            schedule: 'Planlegg skanning',
            scope_organization: 'Organisasjon',
            scope_department: 'Avdeling',
            scope_system: 'System',
            scope_systems: 'Systemer',
            bulk_actions: 'Massehandlinger',
            selected: '{count} valgt',
            bulk_create: 'Masseopprett',
            bulk_assign_scope: 'Massetildel omfang',
            bulk_advance: 'Massefremmgang',
            confirm_bulk_action: 'Bekreft massehandling',
            bulk_confirm_message: 'Er du sikker på at du vil utføre denne handlingen?',
            cycles_affected: '{count} sykluser vil bli påvirket',
            select_all: 'Velg alle',
            select_cycle: 'Velg syklus {name}',
            from_template: 'fra mal',
            bulk_results: 'Masseresultater',
            failed_label: 'mislyktes',
            template_id: 'Mal-ID',
            count: 'Antall',
            advance_to: 'Målstatus',
            bulk_success: 'Masseoperasjon fullført',
        },
        owner_review: {
            title: 'Eiergjennomgangsoppgaver',
            assign_owner: 'Tilordne til eier',
            owner_id: 'Eier',
            decision: 'Beslutning',
            approve: 'Godkjenn',
            modify: 'Endre',
            reject: 'Avvis',
            escalate: 'Eskalere',
            no_tasks: 'Ingen gjennomgangsoppgaver funnet',
            bulk_resolve: 'Masseløsning',
            exceptions: 'Unntaksposter',
            no_exceptions: 'Ingen unntaksposter funnet',
            record_id_label: 'Registrer ID',
            selected: 'valgt',
            notes: 'Notater',
            select_all: 'Velg Alle',
            select: 'Velg',
            confirm_resolve: 'Bekreft',
        },
        classification_lineage: {
            title: 'Klassifiseringshistorikk',
            link_suggestion: 'Koble til foreslått etikett',
            suggestion_id: 'Forslag',
            proposed_label_id: 'Foreslått etikett',
            no_lineage: 'Ingen historikkdata funnet',
            full_lineage: 'Fullstendig historikk',
            record_id_label: 'Registrer ID',
            staged_record_label: 'Iscenesatt rekord',
            enter_staged_record_id: 'Angi trinnvis post-ID',
            scan_id_label: 'Skann ID',
            select_record: 'Velg en post',
            select_suggestion: 'Velg et forslag',
            select_label: 'Velg en etikett',
        },
        common_labels: {
            n_rules: '{n} regler',
            one_rule: '1 regel',
            n_scans: '{n} skanner',
            one_scan: '1 skanning',
            n_syncs: '{n} synkroniseres',
            one_sync: '1 synkronisering',
            n_records: '{n} poster',
            one_record: '1 rekord',
            n_templates: '{n} maler',
            one_template: '1 mal',
            n_suggestions: '{n} forslag',
            one_suggestion: '1 forslag',
            n_selected: '{n} valgt',
            dashboard_loaded: 'Discovery-dashbordet er lastet inn',
            n_scans_loaded: '{n} skanninger er lastet',
            n_staged_records_loaded: '{n} iscenesatte poster lastet',
            confirm_delete_import_template: 'Er du sikker på at du vil slette denne importmalen?',
            invalid_json_source_config: 'Ugyldig JSON i source_config',
            scope_organization: 'Organisasjon',
            scope_department: 'Avdeling',
            scope_system: 'System',
            aria_filter_scans: 'Filterskanninger',
            aria_filter_staged_records: 'Filtrer iscenesatte poster',
            aria_filter_suggestions: 'Filtre',
            aria_filter_by_taxonomy_id: 'Filtrer etter taksonomi-ID',
            aria_filter_by_enabled_status: 'Filtrer etter aktivert status',
            aria_filter_by_source_type: 'Filtrer etter kildetype',
            aria_filter_by_promotion_status: 'Filtrer etter kampanjestatus',
            aria_filter_by_dedup_status: 'Filtrer etter dedup-status',
            aria_filter_by_scan_type: 'Filtrer etter skannetype',
            aria_filter_by_status: 'Filtrer etter status',
            aria_filter_by_confidence: 'Filtrer etter konfidensbånd',
            aria_select_all_records: 'Velg alle poster',
            aria_select_record: 'Velg post {id}',
            aria_select_all_suggestions: 'Velg alle forslag',
            aria_select_suggestion: 'Velg forslag {id}',
            aria_promote_record: 'Markedsfør rekord {id}',
            aria_discard_record: 'Forkast posten {id}',
            aria_merge_record: 'Slå sammen posten {id}',
            aria_review_record: 'Gjennomgå posten {id}',
            aria_accept_suggestion: 'Godta forslaget {id}',
            aria_reject_record: 'Avvis post',
            aria_merge_record_dialog: 'Slå sammen post',
            aria_create_new_scan: 'Opprett ny skanning',
            aria_sync_progress: 'Synkroniseringsfremgang: {processed} av {total} elementer behandlet',
            aria_scan_progress: 'Skannefremgang: {processed} av {total} elementer behandlet',
            aria_confidence: 'Tillit: {pct} %',
            aria_effectiveness: 'Effektivitet: {pct}',
            aria_promote_n_records: 'Markedsfør {n} valgte poster',
            aria_discard_n_records: 'Forkast {n} valgte poster',
            placeholder_pii_detection_rule: 'f.eks. PII-deteksjonsregel',
            placeholder_tax_id: 'f.eks. TAX-001',
            placeholder_lvl_id: 'f.eks. LVL-001',
            placeholder_service_type: 'f.eks. service_type',
            placeholder_database: 'f.eks. database',
            placeholder_hostname_match: 'f.eks. vertsnavn eksakt samsvar',
            placeholder_hostname: 'f.eks. vertsnavn',
            placeholder_hr_systems_csv: 'f.eks. HR Systems CSV import',
            placeholder_server_name: 'f.eks. servernavn',
            placeholder_hostname_field: 'f.eks. vertsnavn',
            placeholder_lowercase: 'f.eks. små bokstaver',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'f.eks. STG-000123',
            placeholder_lvl_002: 'f.eks. LVL-002',
            placeholder_usr_id: 'f.eks. USR-000123',
            decision_for: 'Avgjørelse for {id}',
        },
        exports: {
            title: 'Eksporter',
            inventory: 'Dataoversikt',
            classification_register: 'Klassifiseringsregister',
            raci: 'RACI-matrise',
            exceptions: 'Unntaksrapport',
            evidence_pack: 'Bevissamling',
            delta_report: 'Endringsrapport',
            executive_summary: 'Ledelsessammendrag',
            export_button: 'Eksporter',
            downloading: 'Laster ned...',
            download_complete: 'Nedlasting fullført.',
            download_error: 'Kunne ikke laste ned eksport. Prøv igjen.',
        },
        flags: {
            title: 'Nedstrøms markeringer',
            pending: 'Ventende markeringer',
            cross_border_review_needed: 'Grensekryssende gjennomgang nødvendig',
            sharing_disclosure_needed: 'Delingsoffentliggjøring nødvendig',
            dpia_review_needed: 'DPIA-gjennomgang nødvendig',
            acknowledge: 'Bekreft',
            resolve: 'Løs',
            no_flags: 'Ingen nedstrøms markeringer.',
        },
        delta: {
            title: 'Endringsoversikt',
            new_count: '{n} nye',
            removed_count: '{n} fjernede',
            changed_count: '{n} endrede',
            re_review_triggered: 'Ny gjennomgang utløst',
            create_re_review: 'Opprett ny gjennomgang',
            recent_alerts: 'Siste endringsvarsler',
            no_alerts: 'Ingen siste endringsvarsler.',
            threshold_exceeded: 'Endringsgrense overskredet (>10 %)',
        },
        regulatory: {
            dashboard_governance_info: 'Dette dashbordet viser styringsstatus på syklusnivå: systemer skannet, poster iscenesatt, poster gjennomgått, poster godkjent og unntak som venter. Dette samsvarer med suksesskriteriene for ledelsen: "direkte dashbord for hvem som har oppdatert beholdningen, klassifiseringen og delingsloggene" (P-VN-05).',
            export_artifacts_info: 'Discovery-eksporter inkluderer alle nødvendige artefakttyper per UC-VN-10 og DATA-EVD-01: godkjent databeholdning, klassifiseringsregister, eierskap/RACI-kartlegging, unntakskø, sammendrag, bevispakke og deltarapport.',
            scan_inventory_pipeline_info: 'Hver fullført skanning produserer poster som er kompatible med malen vn_data_law_data_inventory_register (krav 88dd9877). Oppdagede dataressurser fyller automatisk ut datalagerregisteret for å tilfredsstille datalovens forpliktelse til å opprettholde et digitalt datalager.',
            connector_linkage_info: 'Hver koblingskonfigurasjon må inkludere: systemtype (f.eks. CMDB, skyplattform), datalagerreferanse (database, fillager, SaaS) og forretningsprosesskobling (UC-VN-10 påkrevde støtteposter). Disse feltene sikrer at oppdagede eiendeler kan spores til deres kildesystem og forretningskontekst.',
            owner_routing_info: 'Gjennomgangsoppgaver rutes til riktig avdeling eller applikasjonseier basert på system-/dataeierskap (UC-VN-10 trinn 5). Ruting bruker systemeierskapsregisteret, ikke ad-hoc tildeling.',
            owner_actions_audit_info: 'Eiere kan godkjenne, endre, avvise eller eskalere hver klassifisering (UC-VN-10 trinn 6). Hver handling fanger opp aktør, tidsstempel og årsak, og produserer styringsbevis for revisjonsbevis i henhold til krav f12fc090 (Årlig styringsattest). Revisjonssporet viser hvem som har vurdert, hva som ble endret og når.',
            escalation_timers_info: 'Eskaleringstidtakere: frist for eiervurdering er 5 virkedager; eskalering av unntak er 48 timer (UC-VN-10 tidtakere). Uløste elementer eskalerer automatisk når fristen utløper. Tidtakere kan konfigureres i henhold til organisasjonens retningslinjer.',
            original_suggestion_preserved_info: 'Det originale maskin-/systemklassifiseringsforslaget er bevart ved siden av hver eieroverstyring (UC-VN-10 beviskrav). Det opprinnelige forslaget blir aldri overskrevet og forblir synlig for revisjonsformål.',
            dedup_lineage_info: 'Dedupliseringsregler forhindrer dupliserte beholdningsoppføringer når det samme dataelementet oppdages på tvers av flere skanninger eller koblinger. De siste skannemetadataene blir bevart som den primære posten, samtidig som de opprettholder avstamningsreferanser til alle tidligere funn for sporbarhet av revisjon.',
            label_states_info: 'Analytics skiller 4 etiketttilstander for styringsgjennomgang: maskinforeslått (venter), eiergodkjent, eiermodifisert og avvist. Disse tilstandene gjør det mulig for revisorer å vurdere automatiseringsnøyaktighet og eierengasjement.',
            import_pipeline_info: 'Importmaler må produsere poster som er kompatible med oppdagelsesrørledningen. Importerte poster går inn i fasering med de samme metadatakravene som skannede poster: kildesystemidentifikasjon, batch-ID og tidsstempel. Kolonnetilordninger bør målrettes mot felt definert i vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'Klassifiseringsavstamning sporer hele banen: skanning/import -> iscenesettelse -> eieranmeldelse -> godkjent etikett -> registeroppføring (UC-VN-10 trinn 1-10). Hvert trinn viser registrerings-ID, skuespiller, tidsstempel og tilstandsovergang for fullstendig sporbarhet.',
            staging_workflow_info: 'Iscenesatte poster skilles visuelt fra godkjente beholdningsposter ved deres kampanjestatusmerke (UC-VN-10 trinn 3). Hver post beholder herkomstmetadata: kildesystem, skannebatch-ID og oppdagelsestidsstempel for full revisjonssporbarhet.',
            suggestion_data_d1_info: 'Forslag stemmer overens med datalovens klassifiseringsprosedyre DATA-D1: (1) katalogisere dataressurser, (2) klassifisere etter sensitivitetsnivå, (3) definere håndteringsregler. Hvert forslag inkluderer følsomhets- og viktighetsdimensjoner per krav 18e4bfd1.',
            downstream_flags_info: 'Elementer som er flagget for deling, grenseoverskridende overføring eller høyrisikobehandling utløser tilknyttede nedstrømsarbeidsflyter: vurdering av grenseoverskridende overføring, vurdering av risikodeling og DPIA gjennomgang (UC-VN-10 trinn 8).',
            delta_detection_info: 'Oppdagelsessykluser støtter deltadeteksjon per UC-VN-10 trinn 11: hver syklus viser hvilke poster som er nye, endret eller fjernet siden sist godkjente grunnlinje. Materialendringer (>10 % delta) utløser automatisk arbeidsflyter for ny gjennomgang.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Kunne ikke laste.',
            save: 'Kunne ikke lagre.',
            fetch_escalation_log_failed: 'Kunne ikke hente eskaleringslogg.',
            fetch_activations_failed: 'Kunne ikke hente aktiveringer.',
            deactivate_failed: 'Kunne ikke deaktivere.',
            complete_activation_failed: 'Kunne ikke fullføre activation.',
            fetch_playbook_templates_failed: 'Kunne ikke hente spillebokmaler.',
            create_playbook_template_failed: 'Kunne ikke opprette spillebokmal.',
            clone_template_failed: 'Kunne ikke klone mal.',
            update_status_failed: 'Kunne ikke oppdatere status.',
            deprecate_template_failed: 'Kunne ikke utfase mal.',
            seed_defaults_failed: 'Kunne ikke initialisere standardverdier.',
            fetch_regulator_exports_failed: 'Kunne ikke hente tilsynseksporter.',
            create_regulator_export_failed: 'Kunne ikke opprette tilsynseksport.',
            update_submission_status_failed: 'Kunne ikke oppdatere innsendingsstatus.',
            fetch_response_metrics_failed: 'Kunne ikke hente svarsmetrikker.',
            fetch_sla_dashboard_failed: 'Kunne ikke hente SLA-dashboard.',
            fetch_sla_definitions_failed: 'Kunne ikke hente SLA-definisjoner.',
            create_sla_definition_failed: 'Kunne ikke opprette SLA-definisjon.',
            seed_sla_defaults_failed: 'Kunne ikke initialisere SLA-standarder.',
            fetch_vendor_coordination_failed: 'Kunne ikke hente leverandørkoordinering.',
            fetch_vendor_detail_failed: 'Kunne ikke hente leverandørdetalj.',
            create_vendor_coordination_failed: 'Kunne ikke opprette leverandørkoordinering.',
            add_communication_failed: 'Kunne ikke legge til kommunikasjon.',
            add_info_request_failed: 'Kunne ikke legge til infoforespørsel.',
            add_action_request_failed: 'Kunne ikke legge til handlingsforespørsel.',
        },
        nav: {
            playbooks: 'Spillebokmaler',
            sla_definitions: 'SLA-definisjoner',
            sla_dashboard: 'SLA-oversikt',
            escalation_log: 'Eskaleringslogg',
            regulator_exports: 'Myndighetseksporter',
            deadlines: 'Varsling Frister',
            vendor_coordination: 'Leverandørkoordinering',
            metrics: 'Svarmålinger',
            playbook_links: 'Spilleboklenker',
            regulatory_sla: 'Regulatorisk SLA',
            coordination_dashboard: 'Koordinering',
            evidence_chain: 'Beviskjede',
            post_review: 'Etterhendelsesgjennomgang',
        },
        playbooks: {
            page_title: 'Playbook maler',
            activations_page_title: 'Playbook-aktiveringer',
            title: 'Spillebokmaler',
            new: 'Ny Mal',
            seed_defaults: 'Initialiser standarder',
            scenario_type: 'Scenariotype',
            severity_applicability: 'Alvorlighetsgrad Anvendelighet',
            all_scenario_types: 'Alle Scenario Typer',
            new_playbook: 'Ny Spillebok Mal',
            clone: 'Klon',
            clone_template: 'Klon Mal',
            template_name: 'Mal navn',
            deprecate: 'Utfas',
            confirm_deprecate: 'Utfas mal "{name}"?',
            create_template: 'Opprett Mal',
            update_status: 'Oppdatering Status',
            no_templates: 'Ingen spillebok maler funnet',
            deactivation_reason_placeholder: 'Årsak til deaktivering...',
            description_placeholder: 'Spillebok beskrivelse...',
            name_placeholder: 'Spillebok navn...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA-definisjoner',
            title: 'SLA-definisjoner',
            new: 'Ny SLA-definition',
            sla_name: 'SLA Navn',
            target_hours: 'Mål Timer',
            escalation_after: 'Eskalering etter',
            applies_to: 'Gjelder for',
            no_definitions: 'Ingen sla definitions funnet.',
            target_value_placeholder: 'Mål verdi',
            name_placeholder: 'SLA navn...',
            threshold_hours_label: 'Terskeltimer',
            severity_label: 'Alvorlighetsgrad',
            all_types: 'Alle typer',
            all_severities: 'Alle alvorlighetsgrader',
            default_label: 'Standard',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Samsvarlig',
            page_title: 'SLA-oversikt',
            title: 'SLA-oversikt',
            loading: 'Laster SLA-data...',
            compliance_rate: 'Etterlevelse Rate',
            breached: 'Brutt',
            at_risk: 'Ved Risiko',
            on_track: 'På Spor',
            by_severity: 'Etter Alvorlighetsgrad',
            total_records: 'Totalt antall poster',
            met: 'Oppfylt',
            sla_records: 'SLA-poster',
            sla_reference: 'SLA-referanse',
            record_id: 'Post-ID',
            metric_type: 'Måletype',
            severity: 'Alvorlighet',
            elapsed: 'Forløpt',
            clock_start: 'Klokkestart',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Eskaleringslogg',
            title: 'Eskaleringslogg',
            incident_id: 'Hendelse ID',
            escalated_by: 'Eskalerte Etter',
            escalated_to: 'Eskalerte Til',
            reason: 'Årsak',
            escalated_at: 'Eskalerte Ved',
            no_escalations: 'Ingen eskaleringer funnet.',
            filter_incident_id: 'Filter etter Hendelse ID',
            escalation_id: 'Eskalerings-ID',
            level: 'Nivå',
            level_range: 'Nivå (L1–L4)',
            create_escalation: 'Opprett eskalering',
            incident_id_placeholder: 'f.eks. INC-001',
            target_role_placeholder: 'f.eks. dpo, incident_manager',
            select_placeholder: '— velg —',
        },
        regulator_exports: {
            page_title: 'Tilsynseksporter',
            title: 'Myndighetseksporter',
            new: 'Ny Eksport',
            incident_id: 'Hendelse ID',
            regulator: 'Tilsynsmyndighet',
            export_format: 'Format',
            exported_at: 'Eksportert den',
            no_exports: 'Ingen regulator exports funnet.',
            enter_incident_id: 'Angi Hendelse ID',
            export_id: 'Eksport-ID',
            watermark: 'Vannmerke',
            submission_status: 'Innsendingsstatus',
            all_jurisdictions: 'Alle jurisdiksjoner',
            no_transitions: 'Ingen ytterligere overganger tilgjengelig fra',
            select_new_status: '— velg ny status —',
            sections_included: 'Inkluderte seksjoner',
            form_classification: 'Klassifisering',
            submission_channel: 'Kanal',
        },
        deadlines: {
            title: 'Varsling Frister',
            incident_id: 'Hendelse ID',
            deadline: 'Frist',
            regulator: 'Tilsynsmyndighet',
            remaining: 'Gjenstående',
            no_deadlines: 'Ingen varsling frister funnet.',
        },
        vendor_coordination: {
            page_title: 'Leverandørkoordinering',
            title: 'Leverandørkoordinering',
            new: 'Ny Koordinering',
            incident_id: 'Hendelse ID',
            vendor_name: 'Leverandør Navn',
            contact: 'Kontakt',
            coordination_status: 'Koordinering Status',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Ingen leverandør koordinering elementer funnet.',
            assessment_placeholder: 'Indledende vurdering fra leverandør...',
            coordination_id: 'Koordinasjons-ID',
            communications: 'Kommunikasjon',
            info_requests: 'Infoforespørsler',
            action_requests: 'Handlingsforespørsler',
            direction: 'Retning',
            channel_label: 'Kanal',
            no_transitions: 'Ingen flere overganger tilgjengelig.',
            vendor_ref_number: 'Referansenummer',
            report_date: 'Rapportdato',
            contact_email: 'E-post',
            contact_phone: 'Telefon',
            channel_email: 'E-post',
            channel_meeting: 'Møte',
            channel_phone: 'Telefon',
            channel_portal: 'Portal',
            incident_id_placeholder: 'Filter etter Hendelse ID...',
            priority_critical: 'Kritisk',
            priority_high: 'Høy',
            priority_low: 'Low',
            priority_medium: 'Middels',
            vendor_id_placeholder: 'Filter etter Leverandør ID...',
            description_placeholder: 'Beskrivelse...',
            filter_vendor_id: 'Filter etter Leverandør ID',
            inbound: 'Innkommende',
            outbound: 'Utgående',
            summary_placeholder: 'Sammendrag...',
            priority_label: 'Prioritet',
            due_date_label: 'Forfallsdato',
            info_request_desc: 'Beskrivelse av informasjonsforespørsel',
            action_request_desc: 'Beskrivelse av handlingsforespørsel',
            content_label: 'Innhold',
            select_incident: 'Velg hendelse',
            select_vendor: 'Velg leverandør',
            discovery_date: 'Oppdagelse Dato',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Aktør',
        },
        metrics: {
            page_title: 'Svarmålinger',
            title: 'Svarmålinger',
            loading: 'Laster metrikdata...',
            avg_response_time: 'Gj.sn. Svar Tid',
            avg_resolution_time: 'Gj.sn. Løsning Tid',
            total_incidents: 'Totalt Hendelser',
            by_severity: 'Etter Alvorlighetsgrad',
            by_status: 'Etter Status',
            trend: 'Trend',
            start_sla_clock: 'Start SLA-klokke',
            sla_records: 'SLA Rekorder',
            no_sla_records: 'Ingen SLA-poster ennå.',
            record_id: 'Registrer ID',
            severity: 'Alvorlighetsgrad',
            progress: 'Fremgang',
            incident_id_required: 'Hendelses-ID er påkrevd',
            sla_definition_required: 'SLA-definisjon er påkrevd',
            metric_type_required: 'Måletype er påkrevd',
            threshold_required: 'Terskeltimer er påkrevd',
            sla_clock_started: 'SLA-klokke startet',
            sla_definition: 'SLA-definisjon',
            no_definitions: 'Ingen SLA-definisjoner funnet.',
            select_definition: 'Velg SLA-definisjon...',
            select_metric_type: 'Velg måletype...',
            select_severity: 'Velg alvorlighetsgrad...',
            threshold_hours: 'Terskel (timer)',
            warning_threshold_pct: 'Advarselsterskel (%)',
            start_clock: 'Start klokke',
            starting: 'Starter...',
            metric_label: 'Måling',
            value_label: 'Verdi',
            incident_id: 'Hendelses-ID',
            threshold_placeholder: 'f.eks. 24',
            metric_types: {
                triage_time: 'Triagetid',
                containment_time: 'Begrensingstid',
                notification_time: 'Varslingstid',
                resolution_time: 'Løsningstid',
            },
            severities: {
                critical: 'Kritisk',
                high: 'Høy',
                medium: 'Middels',
                low: 'Low',
            },
            sla_reference: 'SLA-referanse',
        },
        playbook_links: {
            page_title: 'Playbooklenker',
            title: 'Spillebok-varslingslenker',
            new_link: 'Koble spillebok til varsling',
            activation_id: 'Aktiverings-ID',
            notification_id: 'Varslings-ID',
            select_activation: 'Velg en aktivering...',
            select_notification: 'Velg en varsling...',
            no_links: 'Ingen spilleboklenker funnet',
            seed_pdpl: 'Legg til malkoblinger for spillebøker',
            created: 'Playbook-koblingen er opprettet',
            seeded: 'Malkoblinger for spillebøker er lagt til',
            create_failed: 'Kunne ikke opprette playbook-link',
            seed_failed: 'Kunne ikke legge til malkoblinger for spillebøker',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Ingen installert rammeverk regulerer hendelseshåndtering, så det er ingen frister å legge til.',
                declared_but_none_active: 'Et rammeverk som regulerer hendelseshåndtering er installert, men ikke trådt i kraft.',
                no_declared_deadlines: 'De installerte rammeverkene for hendelseshåndtering angir ingen varslingsfrister.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Frist',
            deadline_units: {
                hours: '{count} timer',
                working_days: '{count} virkedager',
                calendar_days: '{count} kalenderdager',
                days: '{count} dager',
                weeks: '{count} uker',
                months: '{count} måneder',
                years: '{count} år',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Regulatoriske SLA-tilordninger',
            title: 'Regulatoriske SLA-tilordninger',
            new_mapping: 'Ny SLA-tilordning',
            sla_definition: 'SLA-definisjon',
            select_sla_definition: 'Velg en SLA-definisjon...',
            framework_code: 'Rammeverkkode',
            deadline_type: 'Fristtype',
            deadline_hours: 'Frist i timer',
            seed_pdpl: 'Legg til tilordninger fra installerte frister',
            no_mappings: 'Ingen regulatoriske SLA-tilordninger funnet',
            dashboard: 'Regulatorisk SLA-oversikt',
            deadline_types: {
                initial_notification: 'Innledende melding',
                supplement_response: 'Supplerende svar',
                authority_response: 'Myndighetssvar',
                remediation_report: 'Utbedringsrapport',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Lovreferanse',
        },
        coordination_dashboard: {
            page_title: 'Koordineringsoversikt',
            title: 'Koordineringsoversikt',
            incident_id: 'Hendelse-ID',
            select_incident: 'Velg en hendelse...',
            team_role: 'Teamrolle',
            members: 'Medlemmer',
            tasks: 'Oppgaver',
            sla_status: 'SLA-status',
            no_teams: 'Ingen koordineringsteam funnet',
            new_team: 'Nytt koordineringsteam',
            enter_incident_id: 'Skriv inn en hendelses-ID for å se koordineringsteam.',
            member_ids_placeholder: 'bruker1, bruker2',
            edit_team: 'Rediger team',
            task_count: 'Oppgavetelling',
            tasks_completed: 'Fullført',
            team_roles: {
                security: 'Sikkerhet',
                legal: 'Juridisk',
                it: 'IT',
                management: 'Ledelse',
                department_owner: 'Avdelingseier',
                external: 'Ekstern',
            },
        },
        evidence_chain: {
            page_title: 'Beviskjede',
            title: 'Beviskjede',
            new_entry: 'Ny bevisoppføring',
            select_incident: 'Velg en hendelse...',
            actor: 'Aktør',
            select_actor: 'Velg en aktør...',
            evidence_id: 'Bevis-ID',
            action: 'Handling',
            recipient: 'Mottaker',
            no_entries: 'Ingen beviskjedeoppføringer funnet',
            enter_incident_id: 'Skriv inn en hendelses-ID for å se beviskjede.',
            evidence_id_placeholder: 'f.eks. EVD-2026-0001',
            evidence_id_help: 'En unik referanse til bevismaterialet – en loggfil, et diskbilde eller et skjermbilde. Et forslag er forhåndsutfylt; rediger det så det samsvarer med beviskatalogen din.',
            select_incident_first: 'Velg en hendelse ovenfor før du legger til en bevisoppføring.',
            for_incident: 'For hendelse',
            actions: {
                collected: 'Samlet',
                shared: 'Delt',
                exported: 'Eksportert',
                archived: 'Arkivert',
            },
        },
        post_review: {
            page_title: 'Etterhendelsesgjennomgang',
            title: 'Etterhendelsesgjennomgang',
            new_review: 'Ny gjennomgang',
            select_incident: 'Velg en hendelse...',
            lessons_learned: 'Erfaringer',
            recommendations: 'Anbefalinger',
            improvement_actions: 'Forbedringstiltak',
            submit_for_review: 'Send til gjennomgang',
            aria_submit_review: 'Send gjennomgang {name} til gjennomgang',
            aria_sign_off: 'Godkjenn gjennomgang {name}',
            sign_off: 'Godkjenning',
            no_reviews: 'Ingen etterhendelsesgjennomganger funnet',
            enter_incident_id: 'Skriv inn en hendelses-ID for å se etterhendelsesgjennomgang.',
            one_per_line: 'Én per linje',
            statuses: {
                draft: 'Utkast',
                in_review: 'Under gjennomgang',
                signed_off: 'Godkjent',
                closed: 'Lukket',
            },
        },
        activations: {
            title: 'Playbook-aktiveringer',
            activation_id: 'Aktiverings-ID',
            phases: 'Faser',
            activated_at: 'Aktivert kl',
            activated_by: 'Aktivert av',
            deactivate: 'Deaktiver',
            complete: 'Fullfør',
            deactivation_reason: 'Årsak til deaktivering',
            evidence_button: 'Bevis',
            evidence_modal_title: 'Aktiveringsbevis',
            no_evidence: 'Ingen bevis knyttet til denne aktiveringen.',
            upload_evidence_label: 'Last opp aktiveringsbevis',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Gå meg gjennom hull',
            running: 'Gågaper...',
            results_heading: 'Forbedring veikart ({count} gap{plural})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Ingen hull oppdaget – alle domener ved eller over målet.',
            col_priority: 'Prioritet',
            col_domain: 'Domene',
            col_levels: 'Gjeldende → Mål',
            col_severity: 'Alvorlighetsgrad',
            col_actions: 'Utbedringstiltak',
            citations_label: 'Sitater',
            accept_label: 'Godta veikart',
            reject_label: 'Avvis utkast',
            export_label: 'Eksporter DOCX',
            exported_label: 'Veikart eksportert',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Oppløsning: {status}',
            resolve_status_accepted: 'Akseptert',
            resolve_status_rejected: 'Avvist',
            error_generic: 'Kunne ikke kjøre gap-gjennomgang. Prøv igjen eller kontroller vurderingsstatusen.',
        },
        policy_gap: {
            run_label: 'Oppdag politiske hull',
            running: 'Oppdager hull …',
            results_heading: 'Politikkhull ({count})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Ingen dekningshull oppdaget.',
            col_severity: 'Alvorlighetsgrad',
            col_kind: 'Snill',
            col_clause: 'Klausul / krav',
            kind_missing: 'Manglende klausul',
            kind_weak: 'Svak dekning',
            kind_stale: 'Foreldet (forsinket anmeldelse)',
            remediation_label: 'Foreslått utbedring',
            citations_label: 'Sitater',
            accept_label: 'Godta gap-rapporten',
            reject_label: 'Avvis rapport',
            export_label: 'Eksporter DOCX',
            accept_hint: 'Godta registrerer gap-rapporten for det vedvarende utkastet. Bruk Eksporter DOCX for å generere en policyrevisjonsrapport; operatøren driver selve policyredigeringen.',
            resolve_summary: 'Oppløsning: {status}',
            error_generic: 'Kunne ikke oppdage politiske hull. Prøv på nytt eller kontroller dokumentstatusen.',
        },
        remediation_plan_generator: {
            run_label: 'Generer plan fra hull',
            running: 'Genererer plan …',
            results_heading: 'Utbedringsplan ({phase_count} fase{plural})',
            tokens_label: '≈ {count} tokens',
            phase_label: 'Fase {index}',
            window_label: 'Vindu',
            owner_label: 'Eierrolle',
            evidence_label: 'Bevistyper kreves',
            tasks_label: 'Oppgaver',
            no_phases: 'Ingen faser produsert — input gap-sett kan være tomt.',
            citations_label: 'Sitater',
            accept_label: 'Godta planen',
            reject_label: 'Avvis plan',
            export_label: 'Eksporter DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Oppløsning: {status} · {tasks} oppgave(r) opprettet',
            error_generic: 'Kunne ikke generere utbedringsplan. Prøv igjen eller sjekk gap-set-inngangen.',
            source_picker_label: 'Kilde',
            source_all: 'Alle',
            source_consent: 'Analyse av samtykkegap',
            source_policy: 'Oppdagelse av politiske gap',
            source_vendor: 'Leverandør onboarding',
            load_upstream_label: 'Last fra oppstrøms',
            loading_upstream: 'Laster inn …',
            upstream_count: '{count} gap(er) lastet',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Modenhetsmodeller',
            maturity_assessments: 'Modenhetsvurderinger',
            trend_dashboard: 'Trendoversikt',
            reporting_periods: 'Rapporteringsperioder',
            executive_summaries: 'Ledelsessammendrag',
            audit_readiness: 'Revisjonsberedskap',
            evidence_gaps: 'Dokumentasjonsmangler',
            program_health: 'Programhelse',
            obligation_mappings: 'Forpliktelsestilordninger',
            governance_documents: 'Styringsdokumenter',
            remediation_plans: 'Utbedringsplaner',
            governance_model: 'Styringsmodell',
            document_drafting: 'Dokumentutkast',
            document_issuance: 'Dokumentutstedelse',
            acknowledgment_tracking: 'Kvitteringssporing',
            change_log: 'Endringslogg',
            review_cadence: 'Gjennomgangskadanse',
        },
        maturity_models: {
            title: 'Modenhetsmodeller',
            new: 'Ny Modell',
            seed_defaults: 'Initialiser standarder',
            domains: 'Domener',
            activate: 'Aktiver',
            no_models: 'Ingen modenhet modeller funnet',
            new_maturity_model: 'Ny Modenhet Modell',
            create_model: 'Opprett modell',
            load_error: 'Kunne ikke laste modenhetmodeller. Prøv igjen.',
            create_error: 'Kunne ikke opprette modenhetmodell',
            status_error: 'Kunne ikke oppdatere modellstatus',
            seed_error: 'Kunne ikke generere standardverdier',
            aria_seed_defaults: 'Generer standard modenhetmodeller',
            aria_create_new: 'Opprett ny modenhetmodell',
            loading_sr: 'Laster modenhetmodeller',
            model_name_placeholder: 'f.eks., Personvern-modenhetmodell',
            status_changed: 'Modellstatus endret til {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Modenhetsvurderinger',
            new: 'Ny Vurdering',
            model: 'Modell',
            assessor: 'Vurderer',
            overall_score: 'Samlet Poeng',
            overall_score_help: 'En vektet modenhetsvurdering fra 0–5 som beregnes automatisk når en vurdering fullføres. Øk den ved å gi hvert domene høyere poeng under evalueringen, og deretter føre vurderingen gjennom Send til gjennomgang → Fullfør.',
            assessed_at: 'Vurdert den',
            no_assessments: 'Ingen modenhet vurderinger funnet.',
            load_error: 'Kunne ikke laste modenhetsvurderinger. Prøv igjen.',
            create_error: 'Kunne ikke opprette vurdering',
            start_error: 'Kunne ikke starte vurdering',
            archive_error: 'Kunne ikke arkivere vurdering',
            eval_error: 'Kunne ikke laste vurdering for evaluering',
            eval_min_score: 'Vurder minst ett domene',
            eval_save_error: 'Kunne ikke lagre domenevurderinger',
            review_error: 'Kunne ikke sende til gjennomgang',
            finalize_error: 'Kunne ikke fullføre vurdering',
            start: 'Start',
            start_confirm_title: 'Start vurdering',
            start_confirm_message: 'Er du sikker på at du vil starte denne vurderingen?',
            archive_confirm_title: 'Arkiver vurdering',
            archive_confirm_message: 'Er du sikker på at du vil arkivere denne vurderingen?',
            evaluate_domains: 'Evaluer',
            evaluate_domains_title: 'Evaluer domener',
            submit_for_review: 'Send til gjennomgang',
            finalize: 'Fullfør',
            archive: 'Arkiver',
            all_reviewers: 'Alle gjennomgåere må godkjenne',
            any_reviewer: 'Enhver gjennomgåer kan godkjenne',
            select_level: 'Velg modenhetsnivå...',
            findings_placeholder: 'Funn, notater, bevisreferanser...',
            no_domains: 'Ingen domener funnet for denne modellen.',
        },
        trend_dashboard: {
            title: 'Trendoversikt',
            loading: 'Laster tendensdata...',
            score_trend: 'Poeng Trend',
            domain_comparison: 'Domene Sammenligning',
            period: 'Periode',
            load_error: 'Kunne ikke laste trendsammendrag. Prøv igjen.',
            metrics_load_error: 'Kunne ikke laste trendmålinger. Prøv igjen.',
            module: 'Modul',
            metric: 'Måling',
            value: 'Verdi',
            trend: 'Trend',
            aria_filters: 'Filtre for trendmålinger',
            showing_chart: 'Viser diagramvisning',
            showing_table: 'Viser datatabellvisning',
            create_metric: 'Opprett måling',
            data_points: 'Datapunkter',
            trend_direction: 'Trendretning',
            unit: 'Enhet',
        },
        reporting_periods: {
            title: 'Rapporteringsperioder',
            new: 'Ny Periode',
            period_name: 'Periode Navn',
            start_date: 'Start Dato',
            end_date: 'End Dato',
            no_periods: 'Ingen reporting periods funnet.',
            load_error: 'Kunne ikke laste rapporteringsperioder. Prøv igjen.',
            create_error: 'Kunne ikke opprette rapporteringsperiode',
            auto_generate_error: 'Kunne ikke autogenerere rapporteringsperioder',
            status_error: 'Kunne ikke oppdatere periodestatus',
            start_collecting: 'Start innsamling',
            mark_as_draft: 'Merk som utkast',
            submit_for_review: 'Send til gjennomgang',
            publish: 'Publiser',
            auto_generate: 'Autogenerer rapporteringsperioder',
            period_label_placeholder: 'f.eks., K1 2026',
            status_updated: 'Periodestatus oppdatert',
        },
        executive_summaries: {
            title: 'Ledelsessammendrag',
            new: 'Ny Sammendrag',
            period: 'Periode',
            generated_at: 'Generert Ved',
            generated_by: 'Generert Etter',
            no_summaries: 'Ingen ledelses sammendrag funnet.',
            generate: 'Generer',
            load_error: 'Kunne ikke laste ledelsesoppsummeringer. Prøv igjen.',
            generate_error: 'Kunne ikke generere ledelsesoppsummering',
            save_error: 'Kunne ikke lagre ledelsesoppsummering',
        },
        audit_readiness: {
            title: 'Revisjonsberedskap',
            loading: 'Laster revisjonsberedskabsdata...',
            overall_readiness: 'Samlet Beredskap',
            by_domain: 'Etter Domene',
            evidence_coverage: 'Bevis Dekning',
            gaps: 'Mangler',
            load_error: 'Kunne ikke laste siste beredskap. Prøv igjen.',
            history_error: 'Kunne ikke laste beredskapshistorikk. Prøv igjen.',
            calculate_error: 'Kunne ikke beregne revisjonsberedskap',
            aria_recalculate: 'Beregn revisjonsberedskapspoeng på nytt',
            aria_current_readiness: 'Nåværende revisjonsberedskap',
            aria_history: 'Historikk for beredskapspoeng',
            recalculated: 'Revisjonsberedskap beregnet på nytt',
            loading_sr: 'Laster revisjonsberedskapsdata',
        },
        evidence_gaps: {
            title: 'Dokumentasjonsmangler',
            domain: 'Domene',
            control: 'Kontroll',
            gap_type: 'Mangel Type',
            severity: 'Alvorlighetsgrad',
            no_gaps: 'Ingen bevis mangler funnet.',
            load_error: 'Kunne ikke laste bevishull. Prøv igjen.',
            stats_error: 'Kunne ikke laste hullstatistikk. Prøv igjen.',
            create_error: 'Kunne ikke opprette bevishull',
            update_error: 'Kunne ikke oppdatere bevishull',
            status_error: 'Kunne ikke oppdatere status',
            aria_create_new: 'Opprett nytt bevishull',
            aria_statistics: 'Statistikk over bevishull',
            loading_sr: 'Laster bevishull',
            status_updated: 'Hullstatus oppdatert',
            entity_type_placeholder: 'f.eks., leverandør, system',
            all_modules: 'Alle domener',
            all_severities: 'Alle alvorlighetsgrader',
        },
        program_health: {
            title: 'Programhelse',
            loading: 'Laster programsundhedsdata...',
            overall_health: 'Samlet Helse',
            by_module: 'Etter Modul',
            risk_areas: 'Risikoområder',
            recommendations: 'Anbefalinger',
            load_error: 'Kunne ikke laste programhelsedata. Prøv igjen.',
            aria_key_metrics: 'Nøkkelmålinger for programhelse',
            aria_module_scorecard: 'Modulresultatkortoppdeling',
            loading_sr: 'Laster programhelsedata',
            trending_up: 'Stigende trend',
            trending_down: 'Synkende trend',
            trending_stable: 'Stabil',
            data_loaded: 'Programhelsedata lastet',
        },
        obligation_mappings: {
            title: 'Forpliktelsestilordninger',
            create_mapping: 'Opprett forpliktelsestilordning',
            framework: 'Rammeverk',
            select_framework: 'Velg et rammeverk...',
            obligation: 'Forpliktelse',
            obligation_ref: 'Forpliktelsesreferanse',
            obligation_description: 'Forpliktelsesbeskrivelse',
            role: 'Rolle',
            assigned_role: 'Tildelt rolle',
            select_role: 'Velg en rolle...',
            assigned_department: 'Tildelt avdeling',
            select_department: 'Velg en avdeling...',
            control_ids: 'Kontroll-IDer',
            control_ids_placeholder: 'f.eks. A.5.1, CC6.1',
            control_ids_hint: 'Kommaseparerte kontrollreferansekoder.',
            raci_assignments: 'RACI-tildelinger',
            create_raci: 'Opprett RACI-tildeling',
            activity: 'Aktivitet',
            activity_name: 'Aktivitetsnavn',
            activity_description: 'Aktivitetsbeskrivelse',
            responsible: 'Ansvarlig',
            select_users: 'Velg brukere...',
            accountable: 'Regnskapspliktig',
            select_user: 'Velg en bruker...',
            consulted: 'Konsultert',
            informed: 'Informert',
            review_frequency: 'Gjennomgangsfrekvens',
            frequency_monthly: 'Månedlig',
            frequency_quarterly: 'Kvartalsvis',
            frequency_semi_annual: 'Halvårlig',
            frequency_annual: 'Årlig',
            no_mappings_found: 'Ingen forpliktelsestilordninger funnet.',
            no_raci_found: 'Ingen RACI-tildelinger funnet.',
            mapping_created: 'Forpliktelsestilordning opprettet',
            mapping_updated: 'Forpliktelsestilordning oppdatert',
            mapping_deleted: 'Forpliktelsestilordning slettet',
            raci_created: 'RACI-tilordning opprettet',
            raci_updated: 'RACI-tildeling oppdatert',
            raci_deleted: 'RACI-tildeling slettet',
            aria_edit_mapping: 'Rediger tilordning {name}',
            aria_delete_mapping: 'Slett tilordning {name}',
            aria_edit_raci: 'Rediger RACI {name}',
            aria_delete_raci: 'Slett RACI {name}',
            confirm_delete_mapping: 'Slette denne forpliktelsestilordningen?',
            confirm_delete_raci: 'Slette denne RACI-tildelingen?',
            table_view: 'Tabellvisning',
            matrix_view: 'Matrisvisning',
            print_export: 'Skriv ut / Eksporter',
            filter_framework: 'Filtrer etter rammeverk',
            no_matrix_data: 'Ingen RACI-tildelinger å vise i matrisvisning.',
            aria_print_export: 'Skriv ut eller eksporter RACI-matrise',
        },
        governance_documents: {
            title: 'Styringsdokumenter',
            create_document: 'Opprett styringsdokument',
            document_title: 'Dokumenttittel',
            document_type: 'Dokumenttype',
            content_url: 'Innholds-URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Rammekoder',
            select_frameworks: 'Velg rammeverk...',
            effective_date: 'Ikrafttredelsesdato',
            review_date: 'Gjennomgangsdato',
            status: 'Status',
            version: 'Versjon',
            acknowledgments: 'Bekreftelser',
            acknowledge: 'Bekreft',
            acknowledged_by: 'Bekreftet av',
            department: 'Avdeling',
            no_documents_found: 'Ingen styringsdokumenter funnet.',
            draft: 'Utkast',
            review: 'Gjennomgang',
            approved: 'Godkjent',
            issued: 'Utstedt',
            superseded: 'Erstattet',
            document_created: 'Dokument opprettet',
            document_acknowledged: 'Dokument bekreftet',
            policy: 'Retningslinje',
            procedure: 'Prosedyre',
            standard: 'Standard',
            guideline: 'Retningslinje',
        },
        governance_model: {
            title: 'Styringsmodell',
            create_model: 'Styringsmodell',
            edit_model: 'Styringsmodell',
            model_name: 'Model',
            description: 'Beskrivelse',
            governance_type: 'Type',
            type_centralized: 'Sentralisert',
            type_federated: 'Forbundet',
            type_hybrid: 'Hybrid',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Månedlig',
            freq_quarterly: 'Kvartalsvis',
            freq_semi_annual: 'Halvårlig',
            freq_annual: 'Årlig',
            committees: 'komiteer',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandat',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Omfang',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Aktivitet',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'Du trenger regulatory_framework:lesetillatelsen for å velge rammer. Be en administrator om å gi det.',
            select_members: 'Valgkomiteens medlemmer',
            select_from_role: 'Velg delegerende rolle',
            select_to_role: 'Velg mottakerrolle',
            mandate_markdown_hint: 'Markdown støttes: **fet**, lister, lenker.',
            scope_tab_obligation: 'Forpliktelse',
            scope_tab_framework: 'Rammeverk',
            scope_tab_free_text: 'Fri tekst',
            select_obligation: 'Velg en forpliktelse',
            select_framework: 'Velg et rammeverk',
            aria_remove_committee: 'Fjern komité {n}',
            aria_remove_delegation_rule: 'Fjern delegeringsregel {n}',
            no_frameworks_available: 'Ingen rammeverk tilgjengelig. Legg til regelverk først.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Dokumentutkast',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            drafter: 'Tegner',
            deadline: 'Frist',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Tilordne',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Anmelder',
            select_reviewer: '...',
            comments: 'Kommentarer',
            review_action: 'Action',
            approve: 'Godkjenne',
            request_changes: 'Be om endringer',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Dokumentutstedelse',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            version: 'Versjon',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Kvitteringssporing',
            document_title: 'Dokument',
            acknowledged_count: 'Erkjent',
            pending_count: 'Venter',
            percentage: '%',
            details: 'Detaljer',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Bruker',
            department: 'Avdeling',
            acknowledged_at: 'Date',
            status: 'Status',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Endringslogg',
            date: 'Dato',
            document: 'Dokument',
            change_type: 'Type',
            actor: 'Skuespiller',
            summary: 'Sammendrag',
            export_csv: 'Eksporter CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Gjennomgangskadanse',
            configure_cadence: 'Gjennomgangskadanse',
            edit_cadence: 'Gjennomgangskadanse',
            document: 'Dokument',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Status',
            overdue: 'Forfalt',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Utbedringsplaner',
            create_plan: 'Opprett utbedringsplan',
            gap_id: 'Avviksreferanse',
            select_gap: 'Velg et avvik...',
            plan_title: 'Tittel',
            description: 'Beskrivelse',
            priority: 'Prioritet',
            priority_critical: 'Kritisk',
            priority_high: 'Høy',
            priority_medium: 'Middels',
            priority_low: 'Lav',
            assigned_to: 'Tildelt til',
            select_user: 'Velg en bruker...',
            target_date: 'Måldato',
            framework_code: 'Rammeverk',
            select_framework: 'Velg et rammeverk...',
            gap_finding: 'Avviksfunn',
            action_items: 'Handlingspunkter',
            owner: 'Eier',
            deadline: 'Frist',
            planned: 'Planlagt',
            in_progress: 'Pågående',
            completed: 'Fullført',
            verified: 'Verifisert',
            no_plans_found: 'Ingen utbedringsplaner funnet.',
            evidence: 'Dokumentasjon',
            plan_created: 'Utbedringsplan opprettet',
            action_items_hint: 'Handlingspunkter (ett per linje)',
        },
        statuses: {
            identified: 'Identifisert',
            assigned: 'Tildelt',
            in_remediation: 'I utbedring',
            resolved: 'Løst',
            accepted_risk: 'Akseptert risiko',
            planned: 'Planlagt',
            in_progress: 'Pågår',
            under_review: 'Under vurdering',
            finalized: 'Finalized',
            archived: 'Arkivert',
            draft: 'Utkast',
            active: 'Aktiv',
            upcoming: 'Kommende',
            collecting: 'Collecting',
            published: 'Publisert',
            review: 'Gjennomgå',
            approved: 'Godkjent',
            issued: 'Utstedt',
            superseded: 'Superseded',
            completed: 'Fullført',
            verified: 'Verifisert',
        },
        severities: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Low',
        },
        priorities: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Low',
        },
        gap_types: {
            missing: 'Mangler',
            expired: 'Utløpt',
            insufficient: 'Utilstrekkelig',
        },
        document_types: {
            policy: 'Politikk',
            procedure: 'Prosedyre',
            standard: 'Standard',
            guideline: 'Retningslinje',
            template: 'Mal (intern)',
        },
        maturity_levels: {
            level_1: 'Nivå 1 – Innledende',
            level_2: 'Nivå 2 — Utvikle',
            level_3: 'Nivå 3 — Definert',
            level_4: 'Nivå 4 — Administrert',
            level_5: 'Nivå 5 — Optimalisering',
        },
        grades: {
            excellent: 'Utmerket',
            good: 'Bra',
            fair: 'Rettferdig',
            poor: 'Dårlig',
            critical: 'Kritisk',
            needs_improvement: 'Trenger forbedring',
            not_assessed: 'Ikke vurdert',
        },
        modules: {
            data_mapping: 'Datakartlegging',
            consent: 'Samtykke',
            governance: 'Styring',
            incidents: 'Hendelser',
            monitoring: 'Overvåking',
            classification: 'Klassifisering',
            vendor_risk: 'Leverandørrisiko',
            discovery: 'Oppdagelse',
            deletion: 'Sletting',
        },
        cadences: {
            monthly: 'Månedlig',
            quarterly: 'Kvartalsvis',
            semi_annual: 'Halvårlig',
            annual: 'Årlig',
        },
        section_labels: {
            opening: 'Åpning',
            key_metrics: 'Nøkkelberegninger',
            highlights: 'Høydepunkter',
            concerns: 'Bekymringer',
            recommendations: 'Anbefalinger',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} gap',
        count_gaps_plural: '{count} hull',
        count_summaries: '{count} sammendrag',
        count_summaries_plural: '{count} sammendrag',
        count_assessments: '{count} vurdering',
        count_assessments_plural: '{count} vurderinger',
        count_models: '{count} modell',
        count_models_plural: '{count} modeller',
        count_periods: '{count} periode',
        count_periods_plural: '{count} perioder',
        count_metrics: '{count} beregning',
        count_metrics_plural: '{count} beregninger',
        unknown_user: 'Ukjent bruker',
        unknown_gap: 'Ukjent gap',
        no_permission_title: 'Utilstrekkelige tillatelser',
        no_permission_message: 'Du har ikke tillatelse til å få tilgang til denne siden.',
        ac_vn_frameworks_title: 'VN Framework Dekning',
        ac_vn_frameworks_body: 'Modenhetsvurderinger dekker alle 6 vietnamesiske regulatoriske rammeverk: PDPL, datalov, telekomlov, cybersikkerhetslov, AI lov og e-handelslov – hver med rammespesifikke vurderingskriterier.',
        ac_controller_processor_title: 'Kontroller vs. prosessor',
        ac_controller_processor_body: 'Forpliktelseskartlegginger skiller mellom behandlingsansvarlig og databehandleransvar. Tildel roller ved hjelp av RACI-matrisen for å sikre at hver forpliktelse har en tydelig eier, enten organisasjonen fungerer som kontroller eller prosessor.',
        ac_versioned_evidence_title: 'Versjonert bevissti',
        ac_versioned_evidence_body: 'Hver statusovergang (utkast, gjennomgang, godkjent, utstedt) oppretter en tidsstemplet, versjonert post som fungerer som kontrollerbart bevis. Endringsloggen fanger opp hele dokumentets livssyklushistorikk.',
        ac_remediation_gap_link_title: 'Koblet til Assessment Gaps',
        ac_remediation_gap_link_body: 'Hver utbedringsplan kobler direkte til et identifisert vurderingshull. Tildel en eier og sett en målfrist for å spore oppløsningsfremdriften fra gap til lukking.',
        ac_annual_attestation_title: 'Årlig attestasjonssyklus',
        ac_annual_attestation_body: 'Rapporteringsperioder samsvarer med det årlige attestasjonskravet for vietnamesisk datalov. Bruk "Årlig" tråkkfrekvens (365-dagers syklus) for å generere attestasjonstilpassede perioder automatisk.',
        ac_evidence_per_obligation_title: 'Bevis per rammeforpliktelse',
        ac_evidence_per_obligation_body: 'Bevishull identifiserer manglende, utløpte eller utilstrekkelige bevis for hver rammeforpliktelse. Filtrer etter domene for å se hvilke regulatoriske krav som mangler støttende bevis.',
        ac_cross_framework_title: 'Fullstendighet på tvers av rammeverk',
        ac_cross_framework_body: 'Revisjonsberedskap gir et fullstendig syn på tvers av rammeverk. Komponentpoeng reflekterer dekning på tvers av alle kartlagte rammeverk, og fremhever områder som trenger oppmerksomhet før en revisjon.',
        ac_cadence_default_title: 'Standard gjennomgangskadens',
        ac_cadence_default_body: 'Standard gjennomgangstråkk er 180 dager. Forsinkede anmeldelser blir automatisk flagget. Juster frekvensen per dokument etter behov – alternativene varierer fra 30 til 365 dager.',
        ac_templates_internal_title: 'Malklassifisering',
        ac_templates_internal_body: 'Maler er klassifisert som interne dokumenter. Bruk dokumenttypen "Mal" for å merke gjenbrukbare styringsartefakter med kun intern distribusjonsomfang.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Ventende skrivinger',
        subtitle: 'Eksterne Claude-klienter foreslår skrivinger her. Godkjenn eller avslå hver oppføring — Forseti utfører kun etter administrators godkjenning.',
        no_pending: 'Ingen ventende skrivinger.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Verktøy',
        col_preview: 'Forhåndsvisning',
        col_key: 'API-tasten',
        col_requested: 'Forespurt',
        col_expires: 'Utløper',
        col_status: 'Status',
        status_pending: 'Venter',
        status_approved: 'Godkjent',
        status_declined: 'Avslått',
        status_expired: 'Utløpt',
        status_executed: 'Utført',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Godkjenn',
        decline_label: 'Avslå',
        refresh_label: 'Oppdater',
        error_generic: 'Kunne ikke laste ventende skrivinger.',
        confirm_approve: 'Godkjenning vil utføre verktøyet mot organisasjonen din med tillatelsene til API key-innehaveren. Fortsette?',
        confirm_decline: 'Avslå denne ventende skrivingen? Dette kan ikke angres.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Ventende skrivinger utløper 24 timer etter opprettelse. Den timesvise GC-rensingen avslår utløpte oppføringer automatisk.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Handlinger',
    },
    audit_walkthrough: {
        title: 'Forseti — Generator for revisjonsgjennomgang',
        subtitle: 'Generer en strukturert gjennomgang for en revisjonsoppstart: åpningsspørsmål per kontroll, forventet dokumentasjon, oppfølgingsspørsmål, mulige hull.',
        audit_picker_label: 'Revisjon',
        audit_picker_hint: 'Velg en planlagt revisjon for å generere en gjennomgang.',
        run_label: 'Generer gjennomgang',
        running: 'Genererer…',
        results_heading: 'Gjennomgang — {count} kontroll(er)',
        tokens_label: '≈ {count} tokens',
        no_controls: 'Ingen kontroller i omfanget. Legg til kontroller på revisjonsoppføringen.',
        col_control: 'Kontroll',
        col_question: 'Åpningsspørsmål',
        col_evidence: 'Dokumentasjon',
        col_gaps: 'Mulige hull',
        expected_evidence_label: 'Forventet dokumentasjon',
        followup_probes_label: 'Oppfølgingsspørsmål',
        accept_label: 'Godta gjennomgang',
        reject_label: 'Avvis',
        export_label: 'Eksporter DOCX',
        resolve_summary: 'Resultat: {status}',
        resolve_status_accepted: 'Akseptert',
        resolve_status_rejected: 'Avvist',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Kunne ikke generere gjennomgangen. Prøv igjen eller sjekk revisjonsstatusen.',
    },
    privilege_creep: {
        title: 'Forseti — Oppdagelse av rettighetsutvidelse',
        subtitle: 'Periodisk skanning av rolletildelinger. Funn er rådgivende — Forseti tilbakekaller aldri automatisk.',
        run_label: 'Skann nå',
        running: 'Skanner…',
        findings_heading: '{count} funn',
        no_findings: 'Ingen rettighetsutvidelse oppdaget.',
        col_user: 'Bruker',
        col_job_function: 'Jobbfunksjon',
        col_severity: 'Alvorlighetsgrad',
        col_unexpected: 'Uventede tillatelser',
        col_remediation: 'Foreslått tiltak',
        col_status: 'Status',
        severity_critical: 'Kritisk',
        severity_high: 'Høy',
        severity_medium: 'Middels',
        severity_low: 'Lav',
        status_open: 'Åpen',
        status_dismissed: 'Avvist',
        status_remediated: 'Utbedret',
        dismiss_label: 'Avvis',
        remediate_label: 'Merk som utbedret',
        note_label: 'Notat (valgfritt)',
        error_generic: 'Kunne ikke skanne rettighetsutvidelse. Prøv igjen senere.',
        advisory_banner: 'Disse funnene er rådgivende. Forseti tilbakekaller aldri tillatelser automatisk — gjennomgå med rolleeieren før du handler.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Automatisk kartlegging av samsvarsregel',
            subtitle: 'Forseti går gjennom en installert forskriftspakke og foreslår nye eller oppdaterte samsvarsregler basert på kildeartiklene.',
            pack_picker_label: 'Forskriftspakke',
            pack_picker_hint: 'Velg en installert pakke å kartlegge.',
            run_label: 'Kart fra pakke',
            running: 'Kartlegging …',
            results_heading: '{count} forslag',
            tokens_label: '≈ {count} tokens fra din månedlige bøtte',
            no_suggestions: 'Ingen forslag – pakkens innhold kan allerede være fullstendig dekket av eksisterende regler.',
            kind_new: 'Ny regel',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Ingen endring nødvendig',
            col_kind: 'Snill',
            col_title: 'Foreslått tittel',
            col_existing: 'Eksisterende regel',
            col_source: 'Kilde',
            proposed_body_label: 'Foreslått organ',
            source_quote_label: 'Kilde sitat',
            accept_selected: 'Godta valgt',
            reject_all: 'Avvis alle',
            accept_all: 'Godta alle',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Kunne ikke kjøre regeltilordning. Prøv igjen eller sjekk pakkekoden.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Sesjonsoversikt',
            access_log: 'Tilgangslogg',
            justification_review: 'Begrunnelsesgjennomgang',
            investigations: 'Undersøkelser',
            pattern_analysis: 'Mønsteranalyse',
            anchoring_status: 'Forankringsstatus',
            configuration: 'Konfigurasjon',
            access_policies: 'Tilgangspolicyer',
            review_campaigns: 'Gjennomgangskampanjer',
            evidence_packages: 'Bevispakker',
        },
        session_dashboard: {
            title: 'Sesjonsoversikt',
            loading: 'Laster oversiktsdata...',
            total_sessions: 'Økter totalt',
            active_sessions: 'Aktive økter',
            avg_duration: 'Gj.sn. varighet',
            peak_day_sessions: 'Travleste dag',
            auth_method_breakdown: 'Fordeling etter autentiseringsmetode',
            geo_country_breakdown: 'Geografisk landefordeling',
            recent_session_events: 'Nylige økthendelser',
            user_id: 'Bruker-ID',
            session_id: 'Økt-ID',
            ip_address: 'IP-adresse',
            auth_method: 'Autentiseringsmetode',
            started_at: 'Startet den',
            event_type: 'Hendelsestype',
            all_event_types: 'Alle hendelsestyper',
            filter_by_user: 'Filtrer etter bruker',
            no_active_sessions: 'Ingen aktive økter',
            no_session_events: 'Ingen økthendelser funnet',
            revoke: 'Tilbakekall',
            revoke_session: 'Tilbakekall økt',
            aria_revoke_session: 'Tilbakekall økt {user}',
            revoke_reason: 'Årsak',
            revoke_placeholder: 'Oppgi en årsak for tilbakekalling av denne økten...',
            session_label: 'Økt:',
            load_failed: 'Kunne ikke laste øktoversikten. Vennligst prøv igjen.',
            events_load_failed: 'Kunne ikke laste økthendelser. Vennligst prøv igjen.',
            revoke_failed: 'Kunne ikke tilbakekalle økt',
            session_events_loaded: '{n} økthendelser lastet',
            event_types: {
                session_started: 'Økt startet',
                session_refreshed: 'Økten er oppdatert',
                session_expired: 'Økt utløpt',
                session_revoked: 'Sesjon tilbakekalt',
                session_terminated: 'Økt avsluttet',
            },
        },
        access_log: {
            title: 'Tilgangslogg',
            user_id: 'Bruker ID',
            resource: 'Ressurs',
            action: 'Handling',
            ip_address: 'IP-adresse',
            timestamp: 'Tidsstempel',
            no_logs: 'Ingen tilgang logg oppføringer funnet.',
            filter_by_user: 'Filter etter Bruker ID...',
            load_failed: 'Kunne ikke laste tilgangslogg. Vennligst prøv igjen.',
            export_success: 'Eksport startet',
            export_failed: 'Kunne ikke eksportere tilgangslogg',
            record_count: 'post',
            record_count_plural: 'poster',
            unclassified: 'Ikke klassifisert',
            classification_missing: 'Klassifisering mangler',
            classification_level: 'Klassifiseringsnivå',
            response_code: 'Svarkode',
            access_surface: 'Tilgangskanal',
            api_key: 'API-nøkkel',
            classification_levels: {
                public: 'Offentlig',
                internal: 'Intern',
                confidential: 'Konfidensielt',
                restricted: 'Begrenset',
                unclassified: 'Uklassifisert',
            },
        },
        justification_review: {
            title: 'Begrunnelsesgjennomgang',
            user_id: 'Bruker ID',
            resource: 'Ressurs',
            justification: 'Begrunnelse',
            submitted_at: 'Innsendt At',
            reviewer: 'Gjennomgår',
            no_reviews: 'Ingen begrunnelse gjennomganger funnet.',
            load_failed: 'Kunne ikke laste begrunnelser. Vennligst prøv igjen.',
            review_failed: 'Kunne ikke gjennomgå begrunnelse',
            decline_failed: 'Kunne ikke avslå tilgang',
            justification_count: 'begrunnelse',
            justification_count_plural: 'begrunnelser',
            reviewed: 'Gjennomgått',
            concerning: 'Bekymringsfullt',
            escalated: 'Eskalert',
            link_investigation_placeholder: 'Koble til en etterforskning (valgfritt)',
            review_queue: 'Gjennomgang Kø',
            access_id_label: 'Tilgangs-ID',
            access_id_placeholder: 'ID for tilgangsloggoppføring',
            resource_type_label: 'Ressurstype',
            resource_type_placeholder: 'f.eks. document, database',
            resource_id_label: 'Ressurs-ID',
            resource_id_placeholder: 'Ressursidentifikator',
            reason_placeholder: 'Årsak til å få tilgang til denne ressursen...',
            create_failed: 'Kunne ikke create justification',
            unclassified: 'Ikke klassifisert',
            review_statuses: {
                pending: 'Venter',
                reviewed: 'Anmeldt',
                concerning: 'Angående',
                escalated: 'Eskalert',
            },
            reason_codes: {
                business_need: 'Forretningsbehov',
                legal_obligation: 'Juridisk forpliktelse',
                data_subject_request: 'Forespørsel om datasubjekt',
                audit_requirement: 'Revisjonskrav',
                incident_response: 'Hendelsesrespons',
                research: 'Forskning',
                administrative: 'Administrativt',
                other: 'Annet',
            },
            classification_level: 'Klassifiseringsnivå',
            review_decision: 'Gjennomgå vedtak',
        },
        investigations: {
            title: 'Etterforskning Arbeidsområde',
            new: 'Ny Etterforskning',
            subject: 'Emne',
            investigator: 'Etterforsker',
            opened_at: 'Åpnet den',
            findings: 'Funn',
            no_investigations: 'Ingen etterforskninger funnet.',
            load_failed: 'Kunne ikke laste etterforskninger. Vennligst prøv igjen.',
            create_failed: 'Kunne ikke opprette etterforskning',
            timeline_add_failed: 'Kunne ikke legge til tidslinjeoppføring',
            link_access_failed: 'Kunne ikke koble tilgangs-ID-er',
            link_justification_failed: 'Kunne ikke koble begrunnel-ses-ID-er',
            close_failed: 'Kunne ikke lukke etterforskning',
            export_success: 'Eksport startet',
            export_failed: 'Kunne ikke eksportere etterforskning',
            refresh_failed: 'Kunne ikke oppdatere etterforskningsdetalj. Vennligst prøv igjen.',
            update_failed: 'Kunne ikke oppdatere etterforskning',
            investigation_count: 'etterforskning',
            investigation_count_plural: 'etterforskninger',
            title_placeholder: 'Etterforskning tittel',
            description_placeholder: 'Detaljert beskrivelse av etterforskning...',
            assignee_placeholder: 'Bruker-ID på modtager (valgfritt)',
            timeline_action_placeholder: 'f.eks. Gennemgåede tilgangslogge',
            timeline_note_placeholder: 'Valgfritt notat',
            access_ids_placeholder: 'tilgang-id-1, tilgang-id-2',
            justification_ids_placeholder: 'begrunnelse-id-1, begrunnelse-id-2',
            findings_placeholder: 'Oppsummer etterforskningns fund...',
            by_actor: 'by',
            close_investigation: 'Lukk Etterforskning',
            add_timeline_failed: 'Kunne ikke legge til tidslinjeoppføring.',
            fetch_failed: 'Kunne ikke hente etterforskninger.',
            change_status: 'Endre etterforskningsstatus',
            details: 'Etterforskningsdetaljer',
            link_evidence: 'Koble bevis',
            link_justifications: 'Koble begrunnelser',
            access_ids_label: 'Tilgangs-IDer',
            justification_ids_label: 'Begrunnelse-IDer',
            access_ids_link_placeholder: 'tilgang-id-1, tilgang-id-2',
            justification_ids_link_placeholder: 'begrunnelse-id-1, begrunnelse-id-2',
            investigations_loaded: '{n} investigations lastet',
            status_changed_to: 'Status endret til {status}',
            add_timeline_entry: 'Legg til tidslinjeoppføring',
            add_access_ids: 'Legg til tilgangs-IDer',
            add_justification_ids: 'Legg til begrunnelse-IDer',
            investigation_types: {
                anomaly_review: 'Anomali gjennomgang',
                complaint_driven: 'Klagedrevet',
                scheduled_review: 'Planlagt gjennomgang',
                whistleblower_report: 'Varslerrapport',
            },
            severity_labels: {
                low: 'Lavt',
                medium: 'Middels',
                high: 'Høy',
                critical: 'Kritisk',
            },
            status_labels: {
                open: 'Åpne',
                in_progress: 'Pågår',
                pending_review: 'Venter på gjennomgang',
                closed_substantiated: 'Stengt (begrunnet)',
                closed_unsubstantiated: 'Lukket (ubegrunnet)',
                closed_inconclusive: 'Stengt (ufullstendig)',
            },
            disposition_labels: {
                substantiated: 'Begrunnet',
                unsubstantiated: 'Udokumentert',
                inconclusive: 'Inkonklusive',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Mønsteranalyse',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Laster mønsterdata...',
            anomalies_detected: 'Avvik oppdaget',
            patterns: 'Mønstre',
            by_user: 'Etter Bruker',
            by_resource: 'Etter Ressurs',
            time_range: 'Tid Område',
            load_failed: 'Kunne ikke analysere mønstre. Vennligst prøv igjen.',
            top_resources: 'Topp 10',
            access_by_hour: 'Tilgang etter Time av Dag',
            access_by_day: 'Tilgang etter Dag av Uke',
            fetch_failed: 'Kunne ikke hente mønsteranalyse.',
            analysis_loaded: 'Mønsteranalyse lastet for {user}, {n} sesjoner funnet',
            date_format_placeholder: 'DD/MM/ÅÅÅÅ',
            access_by_hour_heading: 'Tilgang etter tid på dagen',
            access_by_day_heading: 'Tilgang etter ukedag',
            by_classification: 'Etter klassifiseringsnivå',
            top_accessed_resources: 'Mest besøkte ressurser',
            user_access_summary: 'Brukertilgangsoversikt',
            days: {
                sunday: 'Søndag',
                monday: 'Mandag',
                tuesday: 'Tirsdag',
                wednesday: 'Onsdag',
                thursday: 'Torsdag',
                friday: 'Fredag',
                saturday: 'Lørdag',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Forankringsstatus',
            loading: 'Laster forankringsdata...',
            anchored: 'Forankret',
            pending: 'Ventende',
            failed: 'Mislykket',
            last_anchor: 'Siste Forankring',
            no_records: 'Ingen anchoring records funnet.',
            status_load_failed: 'Kunne ikke laste forankringsstatus. Vennligst prøv igjen.',
            proofs_load_failed: 'Kunne ikke laste tilgangsbevis. Vennligst prøv igjen.',
            verify_failed: 'Kunne ikke verifisere beviskjede. Vennligst prøv igjen.',
            anchor_failed: 'Kunne ikke forankre tilgangsbevis',
            batch_anchor_failed: 'Kunne ikke batchforankre tilgangsbevis',
            proof_count: 'bevis',
            proof_count_plural: 'bevis',
            anchor_single: 'Ankre enkelt',
            anchor_batch: 'Forankring Batch',
            access_id_placeholder: 'Angi tilgangs-ID til forankring',
            batch_ids_placeholder: 'tilgangs-id-1, tilgangs-id-2, tilgangs-id-3',
            verify_chain: 'Verifiser Kjede',
            chain_breaks: 'Kjede Brudd',
            total_checked: 'Totalt sjekket',
            anchor_placeholder: 'Angi tilgangs-ID til forankring',
            fetch_failed: 'Kunne ikke hente forankringsstatus.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Kunne ikke hente bevis.',
            access_id: 'Tilgangs-ID',
            chain_sequence: 'Kjedesekvens',
            proof_hash_label: 'Bevishash',
            batch_id: 'Batch-ID',
            proofs_loaded: '{n} access proofs lastet',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Venter',
                confirmed: 'Bekreftet',
                failed: 'Mislyktes',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Kjede',
                blockchain_anchor: 'Blockchain-anker',
                zkp_stub: 'ZKP Stubb',
            },
        },
        configuration: {
            title: 'Konfigurasjon',
            session_settings: 'Økt Innstillinger',
            log_retention: 'Logopbevaring (dager)',
            anomaly_threshold: 'Avvik Terskel',
            anchoring_enabled: 'Ankring aktivert',
            save_config: 'Lagre konfigurasjon',
            load_failed: 'Kunne ikke laste konfigurasjon. Vennligst prøv igjen.',
            save_success: 'Konfigurasjon lagret',
            save_failed: 'Kunne ikke lagre konfigurasjon. Vennligst prøv igjen.',
            justification_rules: 'Begrunnelse Regler',
            reason_codes: 'Årsak Koder',
            anchoring_settings: 'Ankringsinnstillinger',
            anchoring_method: 'Ankringsmetode',
            anchoring_frequency: 'Ankringsfrekvens',
            batch_interval: 'Batchintervall (minutter)',
            eligibility_criteria: 'Berettigelse Kriterier',
            add_criterion_placeholder: 'Legg til kvalifikasjonskriterie...',
            retention_settings: 'Oppbevaring Innstillinger',
            standard_retention: 'Standard opbevaring (dager)',
            sensitive_retention: 'Følsom opbevaring (dager)',
            working_hours: 'Arbeids Timer',
            work_start: 'Start Tid',
            work_end: 'End Tid',
            timezone: 'Tidssone',
            timezone_placeholder: 'f.eks. Asia/Ho_Chi_Minh',
            working_days: 'Arbeids Dager',
            concurrent_sessions: 'Samtidige Økt Grenser',
            concurrent_threshold: 'Samtidige Økt Terskel',
            classification_level: 'Klassifiseringsnivå',
            prompt_mode: 'Prompttilstand',
            grace_period: 'Frist Periode (s)',
            code_placeholder: 'kode',
            is_custom: 'Tilpasset',
            label_placeholder: 'Etikett',
            description_placeholder: 'Beskrivelse',
            custom: 'Tilpasset',
            fetch_failed: 'Kunne ikke hente konfigurasjon.',
            classification_levels: {
                public: 'Offentlig',
                internal: 'Intern',
                confidential: 'Konfidensielt',
                restricted: 'Begrenset',
                unclassified: 'Uklassifisert',
            },
            prompt_modes: {
                reason_only: 'Bare grunn',
                reason_text: 'Årsak + tekst',
                reason_text_supervisor: 'Årsak + tekst + veileder',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Kjede',
                blockchain_anchor: 'Blockchain-anker',
                zkp_stub: 'ZKP Stubb',
            },
            anchoring_frequencies: {
                realtime: 'Sanntid',
                batched: 'Batched',
                on_demand: 'På forespørsel',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Tilgangspolicyer',
            create_policy: 'Opprett tilgangspolicy',
            policy_name: 'Policynavn',
            framework_code: 'Rammeverk',
            select_framework: 'Velg et rammeverk...',
            review_frequency: 'Gjennomgangsfrekvens',
            scope: 'Omfang',
            no_policies_found: 'Ingen tilgangspolicyer funnet.',
            policy_created: 'Tilgangspolicy opprettet',
            frequency_labels: {
                monthly: 'Månedlig',
                quarterly: 'Kvartalsvis',
                semi_annual: 'Halvårlig',
                annual: 'Årlig',
            },
        },
        review_campaigns: {
            title: 'Tilgangsgjennomgangskampanjer',
            create_campaign: 'Opprett gjennomgangskampanje',
            reviewer: 'Gjennomgår',
            due_date: 'Forfallsdato',
            certify: 'Sertifiser',
            no_campaigns_found: 'Ingen tilgangsgjennomgangskampanjer funnet.',
            status_open: 'Åpen',
            status_in_review: 'Under gjennomgang',
            status_completed: 'Fullført',
            campaign_created: 'Vurderingskampanje opprettet',
            review_certified: 'Tilgangsgjennomgang sertifisert',
            certify_campaign: 'Sertifiser kampanje {id}',
            policy_id: 'Policy-ID',
            select_policy: 'Velg en policy...',
            reviewer_ids_label: 'Bedømmere',
            select_reviewers: 'Velg bedømmere...',
            status_labels: {
                open: 'Åpen',
                in_review: 'Under vurdering',
                in_progress: 'Pågår',
                cancelled: 'Avbrutt',
                completed: 'Fullført',
            },
        },
        evidence_packages: {
            title: 'Bevispakker',
            generate_package: 'Generer bevispakke',
            export_type: 'Eksporttype',
            package_type: 'Pakketype',
            framework_scope: 'Rammeverkssomfang',
            period: 'Periode',
            generated_by: 'Generert av',
            generated_at: 'Generert den',
            no_packages_found: 'Ingen bevispakker funnet.',
            package_generated: 'Bevisningspakke generert',
            export_type_full: 'Full eksport',
            export_type_summary: 'Sammendrag',
            export_type_certification_only: 'Kun sertifisering',
            package_type_access_review: 'Tilgangsgjennomgang',
            package_type_investigation: 'Etterforskning',
            package_type_certification: 'Sertifisering',
            package_type_authority_request: 'Myndighetsforespørsel',
            period_start: 'Periodestart',
            period_end: 'Periodeslutt',
            select_frameworks: 'Velg rammeverk...',
            completeness_col: 'Fullstendighet',
            status_col: 'Status',
            generated_by_col: 'Generert av',
        },
        evidence_package_detail: {
            title: 'Bevisningspakkedetaljer',
            not_found: 'Bevisningspakke ikke funnet.',
            package_metadata: 'Pakkemetadata',
            status: 'Status',
            item_count: 'Antall elementer',
            disposition: 'Disposisjon',
            closed_at: 'Lukket',
            completeness_dashboard: 'Fullstendighetsoversikt',
            no_completeness_data: 'Ingen fullstendighetsdata tilgjengelig.',
            controls_covered: 'kontroller dekket',
            missing: 'Mangler',
            sign_off_tracker: 'Godkjenningssporing',
            stage_framework_owner: 'Rammeverkeier',
            stage_legal_compliance: 'Jus og etterlevelse',
            stage_internal_audit: 'Intern revisjon',
            approved: 'Godkjent',
            returned: 'Returnert',
            pending: 'Venter',
            approve: 'Godkjenn',
            return_btn: 'Returner',
            sign_off_submitted: 'Godkjenning sendt',
            artifacts: 'Artefakter',
            no_artifacts: 'Ingen artefakter funnet.',
            template_id: 'Mal-ID',
            description: 'Beskrivelse',
            classification: 'Klassifisering',
            evidence_type: 'Bevistype',
            framework_section: 'Rammeverkseksjon',
            history: 'Godkjenningshistorikk',
            expand: 'Utvid',
            collapse: 'Skjul',
            action: 'Handling',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            comment: 'Kommentar',
            close_case: 'Lukk sak',
            confirm_close: 'Bekreft lukking',
            closure_notes: 'Lukkingsnotater',
            disposition_compliant: 'Samsvarende',
            disposition_non_compliant: 'Ikke-samsvarende',
            disposition_partial: 'Delvis',
            case_closed: 'Bevisningspakke lukket',
            export_package: 'Eksporter pakke',
            export_complete: 'Bevisningspakke eksportert',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Oversikt',
            properties: 'Egenskaper',
            groups: 'Grupper',
            bindings: 'Bindinger',
            change_requests: 'Endringsforespørsler',
            comparison: 'Sammenligning',
            inconsistencies: 'Avvik',
            snapshot_schedules: 'Øyeblikkbildeplaner',
            golden_templates: 'Gullmaler',
            evidence_packs: 'Dokumentasjonspakker',
            audit: 'Revisjon',
        },
        select_property: 'Velg eiendom…',
        select_policy: 'Velg retningslinje…',
        select_properties: 'Velg eiendommer…',
        select_target_properties: 'Velg målegenskaper…',
        select_owner: 'Velg eier…',
        select_technical_contact: 'Velg teknisk kontakt…',
        property_statuses: {
            active: 'Aktiv',
            inactive: 'Inaktiv',
            pending: 'Ventende',
            decommissioned: 'Avviklet',
        },
        dashboard: {
            title: 'Tverrgående egenskapsoversikt',
            loading: 'Laster dashboarddata...',
            total_properties: 'Totalt Egenskaper',
            active_properties: 'Aktive Egenskaper',
            snapshot_coverage: 'Snapshot-dekning',
            properties_covered: 'egenskaper dekket',
            total_bindings: 'Totalt antall bindinger',
            deployed_bindings: 'Utplassert',
            pending_bindings: 'Venter på utplassering',
            deployment_progress: 'Utplasseringsfremdrift',
            on_target: 'På mål',
            below_target: 'Under mål',
            target_label: 'Mål',
            total_groups: 'Totalt Grupper',
            pending_changes: 'Ventende Endringer',
            inconsistencies: 'Inkonsistenser',
            recent_activity: 'Nylige Aktivitet',
            no_recent_activity: 'Ingen nylige aktivitet.',
            fetch_error: 'Kunne ikke laste dashboard. Vennligst prøv igjen.',
        },
        properties: {
            title: 'Egenskapsregister',
            new: 'Ny Egenskap',
            edit: 'Rediger Egenskap',
            property_name: 'Navn',
            property_type: 'Type',
            identifier: 'Identifikator',
            environment: 'Miljø',
            owner_id: 'Eier ID',
            technical_contact_id: 'Teknisk Kontakt ID',
            decommission: 'Avvikle',
            confirm_decommission: 'Er du sikker på at du vil avvikle denne egenskapen?',
            no_properties: 'Ingen egenskaper funnet.',
            all_environments: 'Alle Miljøer',
            all_types: 'Alle Typer',
            cmp_instance_id: 'CMP Forekomst-ID',
            deployment_date: 'Implementeringsdato',
            identifier_placeholder: 'f.eks. www.example.com eller com.example.app',
            owner_name: 'Eier',
            technical_contact_name: 'Teknisk kontakt',
            sensitive_data_categories: 'Sensitive datakategorier',
            sensitive_data_categories_placeholder: 'Velg sensitive datakategorier',
            requires_enhanced_review: 'Krever forbedret gjennomgang',
            sensitive_review_status: 'Gjennomgang av sensitive data',
            sensitive_review_pending: 'Venter på gjennomgang',
            sensitive_review_approved: 'Godkjent',
            sensitive_review_rejected: 'Avvist',
            sensitive_review_submit: 'Send inn anmeldelse',
            sensitive_review_approve: 'Godkjenne',
            sensitive_review_reject: 'Avvis',
            sensitive_review_notes: 'Gjennomgå notater',
            sensitive_review_notes_placeholder: 'Skriv inn anmeldelsesnotater...',
            record_withdrawal: 'Rekorduttak',
            withdrawal_subject_id: 'Emne-ID',
            withdrawal_consent_id: 'ID for samtykke',
            withdrawal_reason: 'Grunn',
            withdrawal_downstream_systems: 'Nedstrøms systemer',
            withdrawal_downstream_placeholder: 'system-1, system-2',
            withdrawal_success: 'Uttaket ble forplantet.',
            withdrawal_error: 'Kunne ikke spre tilbaketrekning.',
            withdrawal_data_subject_id: 'Datasubjekt-ID',
            withdrawal_data_subject_id_placeholder: 'f.eks. usr_abc123',
            withdrawal_consent_record_id: 'ID for samtykkepost',
            withdrawal_consent_record_id_placeholder: 'f.eks. consent_xyz789',
            withdrawal_purposes: 'Tilbaketrukket formål',
            withdrawal_purposes_placeholder: 'Velg formål for å trekke tilbake samtykke for...',
            withdrawal_withdrawn_at: 'Uttaksdato',
            withdrawal_tasks_created: 'Oppgaver opprettet',
            withdrawal_affected_bindings: 'Berørte bindinger',
            withdrawal_affected_activities: 'Berørte behandlingsaktiviteter',
            withdrawal_history: 'Uttakshistorikk',
        },
        groups: {
            title: 'Egenskapsgrupper',
            new: 'Ny Gruppe',
            group_name: 'Gruppe Navn',
            member_count: 'Medlemmer',
            no_groups: 'Ingen egenskap grupper funnet.',
            policies: 'Retningslinjer',
            confirm_delete: 'Er du sikker på at du vil slette denne gruppen?',
        },
        bindings: {
            title: 'Bindingsmatrise',
            property: 'Egenskap',
            purpose: 'Formål',
            legal_basis: 'Rettslig grunnlag',
            bound: 'Bundet',
            no_bindings: 'Ingen bindings funnet.',
            bind_policy: 'Bind policy',
            bulk_bind: 'Bulkbind',
            bind_policy_to_property: 'Bind policy til eiendom',
            bulk_bind_policy: 'Retningslinjer for massebinding',
            unbound: 'Ubundet',
            policy_id: 'Retningslinje-ID',
            version_placeholder: 'f.eks. 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Binding',
            property_name: 'Egenskapsnavn',
            policy_name: 'Policynavn',
            bindings_list: 'Bindingsliste',
            binding_actions: {
                start_deploy: 'Start utrulling',
                mark_deployed: 'Merk som utrullet',
                rollback: 'Rull tilbake',
            },
        },
        change_requests: {
            title: 'Endringsforespørselsprosess',
            new: 'Ny Endrings Forespørsel',
            requester: 'Søker',
            change_type: 'Endrings Type',
            property: 'Egenskap',
            submitted_at: 'Innsendt At',
            no_requests: 'Ingen endrings forespørsler funnet.',
            fetch_pipeline_error: 'Kunne ikke hente pipeline. Vennligst prøv igjen.',
            fetch_list_error: 'Kunne ikke hente endringsforespørsler. Vennligst prøv igjen.',
            create_error: 'Kunne ikke opprette endringsforespørsel. Vennligst prøv igjen.',
            action_error: 'Kunne ikke utføre handling. Vennligst prøv igjen.',
            approver_name: 'Godkjenner',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Alle',
            view_my_properties: 'Mine Egenskaper',
            stage_requests: '{stage}-forespørsler',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Sammenligningsarbeidsområde',
            select_properties: 'Velg egenskaber til sammenligning',
            compare: 'Sammenlign',
            no_comparison: 'Velg mindst to egenskaber til sammenligning.',
            fetch_error: 'Kunne ikke hente sammenligningsrapporter. Vennligst prøv igjen.',
            run_error: 'Kunne ikke kjøre sammenligning. Vennligst prøv igjen.',
            save_notes_error: 'Kunne ikke lagre notater. Vennligst prøv igjen.',
            scan_complete: 'Inkonsistensscanning fuldført.',
            scan_error: 'Kunne ikke kjøre inkonsistensskann. Vennligst prøv igjen.',
            min_two_properties: 'Oppgi vennligst mindst 2 egenskabs-ID-er til sammenligning.',
            view_inconsistencies: 'Vis inkonsistenser',
            click_to_select: 'Klikk for å velge to eller flere egenskaper',
            no_properties_available: 'Ingen egenskaper tilgjengelige',
            no_matching_properties: 'Ingen samsvarende egenskaper',
            select_at_least_two_props: 'Velg minst to egenskaper fra rullegardinmenyen ovenfor.',
            no_differences_found: 'Ingen forskjeller funnet mellom de valgte egenskapene.',
            found_inconsistencies: 'Fant {count} inkonsistenser på tvers av {properties} egenskaper og {bindings} bindinger.',
        },
        inconsistencies: {
            title: 'Avviksliste',
            property: 'Egenskap',
            field: 'Felt',
            expected: 'Forventet',
            actual: 'Faktisk',
            severity: 'Alvorlighetsgrad',
            no_inconsistencies: 'Ingen inkonsistenser funnet.',
            trend: 'Inkonsekvenstrend',
            trend_chart_label: 'Inkonsekvenstrenddiagram som viser tellinger over tid',
            acknowledge: 'Bekreft',
            resolve: 'Løs',
            suppress: 'Undertrykk',
            cr_id_placeholder: 'CR ID',
            view_all: 'Alle',
            view_my_properties: 'Mine Egenskaper',
            view_dpo_review: 'DPO Gjennomgangskø',
            change_request_id_required: 'Endre forespørsels-ID er nødvendig for å løse denne inkonsekvensen.',
        },
        snapshot_schedules: {
            title: 'Øyeblikkbildeplaner',
            new: 'Ny tidsplan',
            schedule_name: 'Navn på tidsplan',
            frequency: 'Frekvens',
            next_run: 'Neste Kjøring',
            last_run: 'Siste Kjøring',
            no_schedules: 'Ingen øyeblikksbilde tidsplaner funnet.',
            target_type: 'Måltype',
            target_id: 'Mål-ID',
            custom_frequency_days: 'Egendefinert frekvens (dager)',
            time_of_day: 'Tid på dagen',
            time: 'Tid',
            retention_days: 'Oppbevaring (dager)',
            execute_now: 'Utfør nå',
            placeholder_days: 'f.eks. 10',
            target_name: 'Målnavn',
        },
        golden_templates: {
            title: 'Gullmaler',
            new: 'Ny Mal',
            template_name: 'Mal Navn',
            property_type: 'Egenskap Type',
            no_templates: 'Ingen gyllen maler funnet.',
            applicable_group_ids: 'Gjeldende gruppe-ID-er (kommaseparert)',
            applicable_groups: 'Gjeldende grupper',
            purposes: 'Formål',
            legal_bases: 'Juridiske grunnlag',
            banner_config: 'Bannerkonfig',
            consent_settings: 'Samtykkeinnstillinger',
            group_ids_placeholder: 'gruppe-id-1, gruppe-id-2',
            apply_to_property: 'Bruk på Egenskap',
            apply_to_group: 'Bruk på Gruppe',
            apply_success: 'Mal brukt vellykket.',
            applications: 'Brukshistorikk',
            no_applications: 'Ingen malanvendelser ennå.',
            no_configuration_details: 'Malen har ingen konfigurasjonsdetaljer. Rediger malen for å legge til formål, rettslige grunnlag, bannerinnstillinger eller samtykkeinnstillinger.',
        },
        rollouts: {
            title: 'Malutrullinger',
            rollout_status: 'Utrullingsstatus',
            confirmed: 'Bekreftet',
            pending: 'Ventende',
            failed: 'Mislykket',
            confirm: 'Bekreft Egenskap',
            confirm_success: 'Egenskap bekreftet i utrulling.',
            can_close: 'Alle egenskaper bekreftet — utrulling fullført',
            cannot_close: 'Venter på egenskapsbekreftelser',
            no_rollouts: 'Ingen utrullinger funnet.',
            in_progress: 'Pågår',
            completed: 'Fullført',
            cancelled: 'Avbrutt',
            reject: 'Avvis Egenskap',
            reject_success: 'Egenskap avvist fra utrulling.',
            confirm_all: 'Bekreft Alle',
            reject_all: 'Avvis Alle',
            confirm_label: 'Bekreft utrulling for',
            reject_label: 'Avvis utrulling for',
            confirm_all_label: 'Bekreft Alle Ventende Utrullinger',
            reject_all_label: 'Avvis Alle Ventende Utrullinger',
            no_properties_in_rollout: 'Denne utrullingen har ingen egenskaper.',
        },
        analytics: {
            title: 'Egenskapsanalyse',
            opt_in_rate: 'Opt-in-rate',
            opt_out_rate: 'Opt-out-rate',
            withdrawal_rate: 'Tilbaketrekningsrate',
            total_consents: 'Totale samtykker',
            total_withdrawals: 'Totale tilbaketrekninger',
            compliance_score: 'Samsvarsscore',
            non_compliant: 'Ikke-samsvarende egenskaper',
            no_non_compliant: 'Alle egenskaper er i samsvar.',
            threshold: 'Samsvarsterskel',
            property_id: 'Eiendoms-ID',
            compliant: 'Samsvarlig',
            non_compliant_status: 'Ikke-kompatibel',
            compliance_help_summary: 'Hvordan beregnes samsvarspoeng, og hvordan fikser jeg det?',
            compliance_help_intro: 'En eiendom er flagget som ikke-kompatibel når dens poengsum er under 80 %. Poengsummen beregnes som:',
            compliance_help_with_template_label: 'Hvis en gylden mal brukes:',
            compliance_help_with_template_text: '% av konfigurasjonsnøkler (formål, legal_bases, banner_config, consent_settings) som samsvarer nøyaktig med malen.',
            compliance_help_no_template_label: 'Hvis ingen gylden mal er brukt:',
            compliance_help_no_template_text: '% av bindinger (samtykketyper tilordnet eiendommen) med deployment_status = distribuert. En egenskap uten bindinger er som standard 100 %.',
            compliance_help_fix_intro: 'For å fjerne en egenskap fra denne listen, gjør ett av følgende:',
            compliance_help_fix_deploy_label: 'Utplasser bindingene',
            compliance_help_fix_deploy_text: 'åpne siden Binding Matrix og sett hver bindings distribusjonsstatus til distribuert.',
            compliance_help_fix_template_label: 'Bruk en gylden mal',
            compliance_help_fix_template_text: 'bruk Bruk på gruppe på malen, og bekreft deretter utrullingen for denne egenskapen. Sørg for at egenskapens konfigurasjon samsvarer med malen.',
            compliance_help_fix_decommission_label: 'Ta ut eiendommen',
            compliance_help_fix_decommission_text: 'bruk Decommission-knappen i egenskapsdetaljmodusen hvis den ikke lenger er i bruk.',
            compliance_help_fix_no_bindings_label: 'Fjern alle bindinger',
            compliance_help_fix_no_bindings_text: 'hvis en eiendom ikke har noen mal og ingen bindinger, får den 100 % som standard.',
        },
        evidence_packs: {
            title: 'Dokumentasjonspakker',
            new: 'Ny Pakke',
            pack_name: 'Pakke Navn',
            properties: 'Egenskaper',
            generated_at: 'Generert Ved',
            no_packs: 'Ingen bevis pakker funnet.',
            generate: 'Generer',
            content_type_consent_records: 'Samtykkeposter',
            content_type_policy_versions: 'Retningslinje Versjoner',
            content_type_binding_history: 'Binding Historikk',
            content_type_change_requests: 'Endrings Forespørsler',
            content_type_inconsistency_reports: 'Inkonsistens Rapporter',
            content_type_snapshot_data: 'Øyeblikksbilde Data',
            content_type_audit_trail: 'Revisjon Spor',
        },
        audit: {
            title: 'Revisjon av samtykkeegenskaper',
            event_type: 'Hendelse Type',
            entity_type: 'Enhet Type',
            entity_id: 'Enhet ID',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            no_events: 'Ingen revisjon hendelser funnet.',
            entity_type_property: 'Egenskap',
            entity_type_group: 'Gruppe',
            entity_type_binding: 'Binding',
            entity_type_change_request: 'Endringsforespørsel',
            entity_type_golden_template: 'Gyllen mal',
            entity_type_snapshot_schedule: 'Øyeblikksplan',
            entity_type_inconsistency: 'Inkonsistens',
            entity_type_evidence_pack: 'Bevispakke',
            action_created: 'Opprettet',
            action_updated: 'Oppdatert',
            action_deleted: 'Slettet',
            action_decommissioned: 'Deaktivert',
            action_status_changed: 'Status endret',
            action_submitted: 'Innsendt',
            action_approved: 'Godkjent',
            action_rejected: 'Avvist',
            action_deployed: 'Implementert',
            action_rolled_back: 'Rullet tilbake',
            action_paused: 'Satt på pause',
            action_resumed: 'Gjenopptatt',
            action_cancelled: 'Avbrutt',
            action_acknowledged: 'Bekreftet',
            action_resolved: 'Løst',
            action_suppressed: 'Undertrykt',
            action_executed: 'Utført',
        },
        property_types: {
            website: 'Nettsted',
            mobile_app: 'Mobilapp',
            ott: 'OTT',
            iot_device: 'IoT-enhet',
            kiosk: 'Kiosk',
            api_service: 'API-tjeneste',
            sdk: 'SDK',
        },
        environments: {
            production: 'Produksjon',
            staging: 'Iscensettelse',
            development: 'Utvikling',
            testing: 'Testing',
        },
        group_types: {
            brand: 'Merke',
            region: 'Region',
            product_line: 'Produktlinje',
            business_unit: 'Forretningsenhet',
            custom: 'Tilpasset',
        },
        target_types: {
            property: 'Egenskap',
            group: 'Gruppe',
            all: 'Alle',
        },
        frequencies: {
            hourly: 'Hver time',
            daily: 'Daglig',
            weekly: 'Ukentlig',
            monthly: 'Månedlig',
            custom: 'Tilpasset',
        },
        binding_statuses: {
            current: 'Gjeldende',
            outdated: 'Utdatert',
            pending: 'Ventende',
            deploying: 'Implementerer',
            rolled_back: 'Rullet tilbake',
        },
        sensitive_categories: {
            health: 'Helsedata',
            biometric: 'Biometriske data',
            genetic: 'Genetiske data',
            racial_ethnic: 'Rase/etnisk opprinnelse',
            political_opinions: 'Politiske meninger',
            religious_beliefs: 'Religiøs tro',
            trade_union: 'Fagforeningsmedlemskap',
            sexual_orientation: 'Seksuell legning',
            criminal_records: 'Kriminelle journaler',
            financial: 'Finansielle data',
            children: 'Barnas data',
            geolocation: 'Nøyaktig geolokalisering',
        },
        export_csv: 'Eksporter CSV',
        exporting: 'Eksporterer...',
        page_x_of_y: 'Side {page} av {total}',
        regulatory: {
            capture_method_guidance: 'PDPL krever samtykkebevis for å matche innsamlingskanalen. Eiendomstyper kartlegger metoder for innhenting av samtykke: nettsted/OTT = bannerbasert samtykke, mobilapp/SDK = samtykkeflyt i appen, kiosk = fysisk/samtykkefangst på skjermen, API tjeneste = programmatisk samtykke, IoT-enhet = samtykkemekanisme på enhetsnivå.',
            dashboard_metrics_info: 'Dette dashbordet gir DPO én enkelt visning av samtykkestilling på tvers av alle digitale eiendommer. Nøkkelberegninger inkluderer: bindingsprosent (distribuert kontra totale bindinger), overholdelse av maler via inkonsistenswidgeten, ventende endringsforespørsler som krever gjennomgang, og eiendomsstatusoversikt som viser egenskaper med aktive, inaktive eller utrangerte tilstander. Eiendommer med sensitive datakategorier spores via eiendomsregisteret.',
            change_request_impact_info: 'Endringsforespørsler som påvirker samtykkeegenskaper må dokumentere regulatorisk innvirkning: hvilke PDPL-krav som påvirkes, om endringen påvirker sensitiv databehandling (art. 2.4), og om endringen krever nytt samtykke fra registrerte. Konsekvenssammendraget i hver endringsforespørsel beskriver PDPL forskriftsreferanser for revisjonsbevis.',
            outdated_binding_warning: 'Bindinger med «utdatert»-status indikerer at en egenskap kjører en policyversjon som ikke er gjeldende. Disse egenskapene gjenspeiler kanskje ikke de siste PDPL kravene eller oppdateringene av organisasjonspolicyer. Gjennomgå og oppdater utdaterte bindinger umiddelbart for å opprettholde PDPL samsvar på tvers av alle samtykkeegenskaper.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Oversikt',
            market_packs: 'Markedspakker',
            language_packs: 'Språkpakker',
            frameworks: 'Regelverk',
            mappings: 'Modultilknytninger',
            submissions: 'Pakkeinnsendelser',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Oversettelsesforhåndsvisning',
            audit: 'Revisjonsspor',
        },
        dashboard: {
            title: 'Lokaliseringsoversikt',
            loading: 'Laster dashboarddata...',
            active_market_packs: 'Aktive Marked Pakker',
            published_packs_available: 'Udgivne markedspakker tilgjengelige',
            language_packs: 'Språk Pakker',
            total_language_packs: 'Sprogpakker registreret i alt',
            active_regulatory_frameworks: 'Aktive Regulatoriske Rammeverk',
            frameworks_active: 'Rammeverk med aktive status',
            pending_pack_submissions: 'Ventende Pakke Innsendinger',
            submissions_awaiting_review: 'Send innelser afventer gjennomgang',
            recent_audit_events: 'Nylige Revisjon Hendelser',
            no_recent_audit: 'Ingen nylige revisjon hendelser.',
            event_type: 'Hendelse Type',
            entity_type: 'Enhet Type',
            entity_id: 'Enhet ID',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
        },
        market_packs: {
            title: 'Markedspakkekatalog',
            new: 'Ny Marked Pakke',
            pack_name: 'Pakke Navn',
            market: 'Marked',
            locale: 'Lokalitet',
            no_packs: 'Ingen marked pakker funnet.',
        },
        language_packs: {
            title: 'Språkpakker',
            new: 'Ny Språk Pakke',
            language: 'Språk',
            locale_code: 'Lokalitet Kode',
            completeness: 'Fullstendighet',
            completeness_detail: 'Fullstendighetsdetalj',
            overall_completeness: 'Samlet fullstendighet',
            total_keys: 'Totalnøkler',
            translated_keys: 'Oversatte nøkler',
            missing_keys: 'Manglende nøkler',
            namespace_breakdown: 'Navneområdeoppdeling',
            namespace: 'Navnerom',
            translated: 'Oversatt',
            no_packs: 'Ingen språk pakker funnet.',
            install_language_pack: 'Installer språkpakke',
            available_packs: 'Tilgjengelige språkpakker',
            install: 'Installer',
            all_installed: 'Alle språkpakker er allerede installert.',
            install_success: 'Språkpakke installert.',
            market: 'Marked',
            featured: 'Fremhevet',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Regelverk',
            new: 'Ny Rammeverk',
            framework_name: 'Rammeverk Navn',
            jurisdiction: 'Jurisdiksjon',
            effective_date: 'Gjeldende Dato',
            no_frameworks: 'Ingen regulatoriske rammeverk funnet.',
            view_requirements: 'Vis krav',
            hide_requirements: 'Skjul krav',
            show_legacy: 'Vis eldre rammeverk',
            effective_until: 'Gyldig til',
            effective_on: 'Gyldig fra',
            replaced_by: 'Erstattet av',
            replaces_label: 'Erstatter',
            status_active: 'Aktiv',
            status_draft: 'Utkast',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Eldre',
            status_upcoming: 'Kommende',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Overvåkningsliste',
            watchlist_empty: 'Ingen utkast eller kommende rammer',
            watchlist_empty_desc: 'Rammer med status "utkast" eller "kommende" vil vises her for overvåking.',
            expected_effective: 'Forventet effektiv',
            effective_date_tbd: 'Ikrafttredelsesdato: TBD',
            activate: 'Aktiver',
            ai_law_title: 'Vietnam AI lov',
            ai_risk_classification: 'AI Risikoklassifisering',
            ai_system_dossier: 'AI Systemdokumentasjon',
            ai_labeling_checklist: 'AI Merking og avsløring',
            ai_incident_report: 'AI Hendelsesrapport',
            ai_impact_assessment: 'AI Konsekvensanalyse',
            ai_sandbox_application: 'Sandkasseapplikasjon',
            ai_sandbox_monitoring: 'Sandkasseovervåking',
            ai_sandbox_completion: 'Sandkasse ferdigstillelse',
            ai_submission_tracker: 'Innsending Tracker',
            ai_evidence_capture: 'Bevisfangst',
            risk_level_minimal: 'Minimal risiko',
            risk_level_limited: 'Begrenset risiko',
            risk_level_high: 'Høy risiko',
            risk_level_unacceptable: 'Uakseptabel risiko',
            submission_status_draft: 'Utkast',
            submission_status_submitted: 'Sendt inn',
            submission_status_returned: 'Returnert for endring',
            submission_status_accepted: 'Godtatt',
            submission_status_rejected: 'Avvist',
            submission_status_closed: 'Stengt',
            ecommerce_law_title: 'Vietnam e-handelslov',
            ecommerce_platform_classification: 'Plattformklassifisering',
            ecommerce_seller_register: 'Selgerregister',
            ecommerce_complaint_register: 'Klageregister',
            ecommerce_takedown_register: 'Fjerningsregister',
            ecommerce_submission_tracking: 'Innsendingssporing',
            ecommerce_dossier_checklist: 'Dossiersjekkliste',
            ecommerce_cross_border: 'Overholdelse av grenser',
            ecommerce_trustmark: 'Trustmark Program',
            ecommerce_notification: 'Varsling',
            ecommerce_registration: 'Registrering',
            ecommerce_termination: 'Oppsigelse',
            ecommerce_reporting_readiness: 'Rapporteringsberedskap',
            ecommerce_evidence_capture: 'Bevisfangst',
            submission_status_approved: 'Godkjent',
            cybersec_law_2025_title: 'Vietnam Cybersecurity Law (2025)',
            cybersec_system_classification: 'Systemklassifisering',
            cybersec_risk_assessment: 'Risikovurdering',
            cybersec_incident_report: 'Hendelsesrapport',
            cybersec_authority_cooperation: 'Myndighetssamarbeid',
            cybersec_data_localization: 'Datalokalisering',
            cybersec_vendor_security: 'Leverandørsikkerhet',
            cybersec_submission_tracker: 'Innsending Tracker',
            cybersec_evidence_capture: 'Bevisfangst',
            cybersec_dossier_checklist: 'Dossiersjekkliste',
            cybersec_classification_general: 'Generelt',
            cybersec_classification_important: 'Viktig',
            cybersec_classification_critical: 'Kritisk',
            data_law_title: 'Vietnam datalov',
            data_inventory: 'Datainventar',
            data_classification: 'Dataklassifisering',
            data_sharing: 'Datadeling',
            data_governance: 'Datastyring',
            data_submission_tracker: 'Innsending Tracker',
            data_evidence_capture: 'Bevisfangst',
            data_dossier_checklist: 'Dossiersjekkliste',
            telecom_law_title: 'Vietnam telekomlov',
            telecom_authority_request: 'Myndighetsforespørsel',
            telecom_disclosure: 'Avsløring',
            telecom_confidentiality: 'Konfidensialitet',
            telecom_submission_tracker: 'Innsending Tracker',
            telecom_evidence_capture: 'Bevisfangst',
            telecom_dossier_checklist: 'Dossiersjekkliste',
            pdpl_law_title: 'Vietnam lov om beskyttelse av personopplysninger',
            pdpl_breach_notification: 'Bruddvarsel',
            pdpl_dpo_notification: 'DPO Melding om avtale',
            pdpl_data_subject_notification: 'Varsling av registrerte',
            pdpl_submission_tracker: 'Innsending Tracker',
            pdpl_evidence_capture: 'Bevisfangst',
            pdpl_dossier_checklist: 'Dossiersjekkliste',
            pdpl_authority_filing: 'Myndighetsarkivering',
            pdpl_mops_filing: 'MoPS-arkivering',
        },
        mappings: {
            title: 'Modultilknytninger',
            new: 'Ny Kartlegging',
            module: 'Modul',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Samtykke',
            module_dpia: 'DPIA',
            module_incident: 'Hendelse',
            module_rights_request: 'Rettighetsforespørsel',
            module_data_mapping: 'Datakartlegging',
            module_classification: 'Klassifisering',
            module_monitoring: 'Overvåking',
            module_governance: 'Styring',
            module_vendor: 'Leverandør',
            module_risk: 'Risiko',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Marked Pakke',
            mapping_status: 'Kartlegging Status',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Ingen modul kartlegginger funnet.',
            incomplete_entries_warning: '{count} kartlegging(er) skjult på grunn av manglende rammeverk-ID, krav-ID eller modul.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Pakkeinnsendelser',
            new: 'Ny Innsending',
            submitter: 'Innsender',
            pack: 'Pakke',
            submitted_at: 'Innsendt At',
            no_submissions: 'Ingen pakke innsendinger funnet.',
            installed_sector_overlays_title: 'Sektoroverlegg ({count})',
            installed_sector_overlays_aria: 'Installerte sektoroverlegg',
            installed_col_name: 'Navn',
            installed_col_code: 'Kode',
            installed_col_sector: 'Sektor',
            installed_col_mode: 'Modus',
            installed_col_status: 'Status',
            installed_refresh: 'Oppdater',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Oversettelsesforhåndsvisning',
            select_locale: 'Velg språk',
            select_module: 'Velg Modul',
            key: 'Key',
            value: 'Verdi',
            no_translations: 'Ingen oversettelser funnet.',
        },
        audit: {
            title: 'Lokalisering Revisjon Spor',
            event_type: 'Hendelse Type',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Enhet Type',
            entity_id: 'Enhet ID',
            entity_name: 'Enhetsnavn',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            payload: 'Nyttelast',
            collapse_payload: 'Skjul nyttelastdetaljer',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Utvid nyttelastdetaljer',
            collapse: 'Skjul',
            expand: 'Utvid',
            all_entity_types: 'Alle enhetstyper',
            no_events: 'Ingen revisjon hendelser funnet.',
        },
        market_packs_detail: {
            installed_at: 'Installert den',
            pack_details_label: 'Pakkedetaljer',
            market_code: 'Markedskode',
            supported_locales: 'Støttede lokaliteter',
            min_platform_version: 'Min. plattformversjon',
            pack_information: 'Pakkeinformasjon',
            pack_contents: 'Pakkeinnhold',
            included_frameworks: 'Inkluderte rammeverk',
            pack_artifacts: 'Pakkeartefakter',
            no_artifacts: 'Ingen artefakter funnet for denne pakken',
            pack_not_found: 'Pakke ikke funnet',
        },
        mappings_form: {
            framework_id: 'Rammeverk-ID',
            requirement_id: 'Krav-ID',
            module_label: 'Modul',
            mapping_type: 'Koblingstype',
            configuration_json: 'Konfigurasjon (JSON)',
            conflict_resolution: 'Konfliktløsning',
            is_override: 'Er overstyring',
            all_modules: 'Alle moduler',
            all_mapping_types: 'Alle koblingstyper',
        },
        submissions_detail: {
            submission_id: 'Innsendings-ID',
            validation: 'Validering',
            reviewers: 'Bedømmere',
            start_review: 'Start vurdering',
            hide_comments: 'Skjul kommentarer',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Vurderingskommentarer',
            loading_comments: 'Laster kommentarer...',
            no_comments: 'Ingen kommentarer ennå.',
            add_comment: 'Legg til kommentar',
            reject_submission: 'Avvis innsending',
            reason_for_rejection: 'Årsak til avvisning',
            reviewer_ids: 'Bedømmer-IDer (kommaseparert)',
            submit_new_pack: 'Send inn ny pakke',
            pack_type: 'Pakketype',
            market_code: 'Markedskode',
            target_locales: 'Mållokaliteter',
            pack_file: 'Pakkefil',
            pack_format_info: 'Pakkeformat: Markedspakker bør inkludere lokalitetsspesifikke JSON-filer med navneområde/nøkkeloversettelser. Språkpakker bør gi oversettelser for de angitte målstedene. Se dokumentasjon for detaljert skjema.',
            accepted_formats: 'Godkjente formater: .json eller .zip. JSON-filer skal inneholde et flatt eller navnedelt objekt med oversettelsesnøkkelverdipar. ZIP-filer bør inneholde én eller flere JSON lokalitetsfiler.',
            comma_separated_locales: 'Kommaseparerte lokalkoder for denne språkpakken',
            confirm_approval: 'Bekreft godkjenning',
            approve_message: 'Godkjenn denne pakkeinnsendingen?',
            submission_label: 'Innsending',
            all_pack_types: 'Alle pakketyper',
            passed: 'bestått',
            failed_label: 'mislykket',
            market_code_required: 'Markedskode er påkrevd',
            version_required: 'Versjon er påkrevd',
            pack_type_regulatory: 'Regulatorisk',
            framework_code: 'Rammekode',
            jurisdiction_code: 'Jurisdiksjonskode',
            pack_source: 'Kilde',
            pack_source_manual: 'Manuell',
            pack_version: 'Pakkeversjon',
            install_regulatory_pack: 'Installer regulatorisk pakke',
            regulatory_pack_desc: 'Last opp en regulatorisk rammepakke (.zip)',
            framework_required: 'Rammeverkkode er påkrevd',
            jurisdiction_required: 'Jurisdiksjonskode er påkrevd',
        },
        catalog_detail: {
            packs_installed_of: 'av {total} pakker installert',
            install_all: 'Installer alle',
            installing: 'Installerer...',
            removing: 'Fjerner...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Krever',
            tab_catalog: 'Katalog',
            tab_installed: 'Installert',
            details: 'Detaljer',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Kildelokalitet',
            target_locale: 'Mållokalitet',
            locales_must_differ: 'Kilde- og mållokalitet må være forskjellige.',
            all_keys_translated: 'Alle nøkler oversatt',
            keys_missing: '{count} nøkkel(er) mangler',
            show_missing_only: 'Vis kun manglende',
            namespace: 'Navnerom',
            source: 'Kilde',
            target: 'Mål',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Rammeverk',
            tab_watchlist: 'Overvåkningsliste',
            authority: 'Myndighet',
            requirements_count: 'Krav',
            all_types: 'Alle typer',
            collapse_all: 'Skjul alle',
            expand_all: 'Utvid alle',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtrert til',
            clear_filter: 'Fjern filter',
            no_requirements: 'Ingen krav funnet for dette rammeverket.',
            requirements_label: 'Krav',
            status_updated: 'Status oppdatert',
            overview_tab: 'Oversikt',
            forms_tab: 'Skjemaer',
            procedures_tab: 'Prosedyrer',
            fines_risks_tab: 'Bøter og risikoer',
            deadlines_tab: 'Frister',
            guidance_tab: 'Veiledning',
            guidance_empty: 'Ingen veiledningsinnhold for dette rammeverket.',
            guidance_loading: 'Laster veiledning …',
            guidance_section: 'Veiledningsseksjon',
            guidance_view: 'Se veiledning',
            guidance_map_to_workflow: 'Kart til arbeidsflyt',
            guidance_link_evidence: 'Koblingsbevis',
            guidance_filter_type: 'Type',
            guidance_filter_category: 'Kategori',
            guidance_filter_module: 'Modul',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Søkeveiledning',
            guidance_filter_all: 'Alle',
            guidance_table_reference: 'Referanse',
            guidance_table_name: 'Navn på veiledning',
            guidance_table_type: 'Type',
            guidance_table_category: 'Kategori',
            guidance_table_effective_date: 'Ikrafttredelsesdato',
            guidance_table_modules: 'Relaterte moduler',
            guidance_table_actions: 'Handlinger',
            guidance_reference_only_banner: 'Bare referanse – gjelder for {{sector}}-sektoren, men ikke for organisasjonen din.',
            guidance_open_official_source: 'Åpne offisiell kilde',
            guidance_no_detail: 'Ingen ytterligere detaljer tilgjengelig for denne veiledningen.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Relatert veiledning',
            deadlines_empty: 'Ingen regulatoriske frister funnet for dette rammeverket.',
            deadlines_sla_type: 'SLA-type',
            deadlines_value: 'Frist',
            deadlines_unit: 'Enhet',
            deadlines_extension: 'Forlengelse',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Juridisk referanse',
            framework_stack: 'Rammeverkstruktur',
            layer_parent_law: 'Hovedlov',
            layer_implementing_decree: 'Gjennomføringsforskrift',
            layer_enforcement_decree: 'Håndhevelsesforskrift',
            layer_admin_procedures: 'Administrative prosedyrer',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Underordnede instrumenter',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Aktiv',
            status_draft: 'Utkast',
            draft_enforcement_badge: 'Utkast — ennå ikke vedtatt',
            effective_date_draft: 'Utkast — ennå ikke trådt i kraft',
            effective_date: 'Ikrafttredelsesdato',
            forms_table_id: 'Skjema-ID',
            forms_table_name: 'Skjemanavn',
            forms_table_source: 'Kilde',
            forms_table_category: 'Kategori',
            forms_table_type: 'Type',
            fill_form: 'Fyll ut skjema',
            view_reference: 'Se referanse',
            forms_empty: 'Ingen skjemaer funnet for dette rammeverket.',
            forms_loading: 'Laster skjemaer...',
            filter_by_source_layer: 'Filtrer etter kildelag',
            all_layers: 'Alle lag',
            procedures_id: 'Prosedyre-ID',
            procedures_name: 'Prosedyre',
            procedures_trigger: 'Utløser',
            procedures_steps: 'Trinn',
            procedures_outputs: 'Resultater',
            procedures_related_forms: 'Relaterte skjemaer',
            procedures_submission_channels: 'Innsendingskanaler',
            procedures_empty: 'Ingen prosedyrer funnet for dette rammeverket.',
            fines_area: 'Overtredelsesområde',
            fines_articles: 'Artikler',
            fines_individual: 'Bot (enkeltperson)',
            fines_organization: 'Bot (organisasjon)',
            fines_revenue: 'Inntektseskalering',
            fines_deadlines: 'Frister',
            fines_remediation: 'Utbedringstiltak',
            fines_related: 'Relaterte prosedyrer',
            fines_empty: 'Ingen håndhevelsesdata tilgjengelig for dette rammeverket.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Risikotype',
            fines_risk_triggers: 'Hva som utløser det',
            fines_risk_only_note: 'Disse oppføringene beskriver samsvars- og driftsrisiko, ikke bøter. Der bøter gjelder, er de fastsatt i det overordnede rammeverket.',
            fines_enforcement_pending: 'Håndhevelsesrammeverket er ikke publisert ennå. Sanksjonsdata vil bli lagt til når gjennomføringsinstrumentet er kunngjort.',
            view_details: 'Vis detaljer',
            back_to_list: 'Tilbake til listen',
            description: 'Beskrivelse',
            jurisdiction: 'Jurisdiksjon',
            lineage: 'Opphav',
            replaces: 'Erstatter',
            replaced_by: 'Erstattet av',
            requirements: 'Krav',
            tab_navigation: 'Rammeverkdetalj-faner',
            forms_table_actions: 'Handlinger',
            create_form: 'Opprett skjema',
            mark_promulgated: 'Merk som kunngjort',
            status_promulgated: 'Kunngjort',
            promulgated_success: 'Lagstatus oppdatert til kunngjort',
            transition_review: 'Overgangsgjennomgang',
            transition_review_desc: 'Dette rammeverket har en ventende overgangsgjennomgang for vedtakelse av etterfølgerlov.',
            transition_review_by: 'Gjennomgangsfrist',
            transition_review_status: 'Gjennomgangsstatus',
            transition_review_notes: 'Notater',
            transition_review_pending: 'Ventende',
            transition_review_in_progress: 'Under arbeid',
            transition_review_completed: 'Fullført',
            transition_review_successor: 'Etterfølgende rammeverk',
            transition_review_lead_time: 'Forberedelsestid',
            transition_review_lead_time_days: '{days} dager før ikrafttredelsesdato',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Skjemaovergangsgjennomganger',
            transition_review_forms_desc: 'Gjennomgå hver operativ mal for relevans under etterfølgende rammeverk.',
            transition_review_outcome: 'Resultat',
            transition_review_outcome_carry_forward: 'Videreføres',
            transition_review_outcome_carry_forward_revised: 'Videreføres (revidert)',
            transition_review_outcome_retire: 'Utgår',
            transition_review_outcome_split: 'Deles',
            transition_review_outcome_awaiting_decree: 'Avventer forskrift',
            transition_review_no_forms: 'Ingen skjemaovergangsgjennomganger konfigurert.',
            transition_review_update_success: 'Overgangsgjennomgang oppdatert',
            transition_review_operational_note: 'Dette er prosessstøttende operative maler, ikke lovpålagte skjematall. Juridisk tilordning forblir under det gamle rammeverket til etterfølgerloven trer i kraft.',
            rights_tab: 'Rettigheter',
            rights_empty: 'Ingen registrerte rettigheter definert for dette rammeverket.',
            rights_code: 'Kode',
            rights_name: 'Høyre',
            rights_legal_basis: 'Juridisk grunnlag',
            rights_elaborating_articles: 'Utdybende artikler',
            rights_description: 'Beskrivelse',
            requirements_reference: 'Referanse',
            requirements_mandatory: 'Obligatorisk',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Kilde',
            category_legal_basis: 'Juridisk grunnlag',
            category_data_subject_rights: 'Datasubjektets rettigheter',
            category_notification: 'Varsling',
            category_assessment: 'Vurdering',
            category_governance: 'Styring',
            category_record_keeping: 'Journalføring',
            category_cross_border: 'Grenseoverskridende',
            category_technical_measures: 'Tekniske tiltak',
            category_incident_reporting: 'Hendelsesrapportering',
            category_risk_management: 'Risikostyring',
            category_supply_chain: 'Forsyningskjede',
            category_certification: 'Sertifisering',
            child_frameworks: '{count} barneramme',
            child_frameworks_plural: '{count} underordnede rammer',
            replaces_prefix: 'Erstatter:',
            replaced_by_prefix: 'Erstattet av:',
            responsible_prefix: 'Ansvarlig:',
            watchlist_empty_hint: 'Rammer med status "utkast" eller "kommende" vil vises her for overvåking.',
            framework_count: '{count} rammeverk',
            framework_count_plural: '{count} rammer',
            hiding_legacy: '(skjuler {count} arv)',
            label_VN_PDPD_DECREE_13: 'PDPD-dekret 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Lov 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Dekret 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Utkast til håndhevelse 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Dataloven',
            label_VN_DATA_LAW_DECREE_165_2025: 'Datalovvedtak 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Databørsutkast 2026',
            label_VN_TELECOM_LAW_2023: 'Telekommunikasjonsloven 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Teledekret 163/2024',
            label_VN_CYBERSEC_LAW: 'Lov om nettsikkerhet (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec-dekret 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Lov om nettsikkerhet 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'E-handelslov 122/2025',
            label_VN_AI_LAW_2026: 'AI Lov 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Selskapet',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Autoritet',
            sla_type_rights_request_ack: 'Bekreftelse av rettigheter',
            'sla_type_Access control review': 'Gjennomgang av tilgangskontroll',
            'sla_type_Annual governance attestation': 'Årlig styringsattest',
            'sla_type_Audit review cycle': 'Revisjonsgjennomgangssyklus',
            'sla_type_Authority request response': 'Myndighetsforespørselssvar',
            'sla_type_Change notification processing': 'Endre varslingsbehandling',
            'sla_type_Complaint acknowledgment': 'Bekreftelse av klage',
            'sla_type_Complaint resolution': 'Løsning av klage',
            'sla_type_Confidentiality policy review': 'Gjennomgang av konfidensialitetspolicy',
            'sla_type_Cybersecurity incident full report': 'Hele rapporten om cybersikkerhetshendelsen',
            'sla_type_Cybersecurity incident initial report': 'Innledende rapport om cybersikkerhetshendelser',
            'sla_type_Data inventory review': 'Gjennomgang av datalager',
            'sla_type_Disclosure log review cycle': 'Gjennomgangssyklus for avsløringslogg',
            'sla_type_Incident classification decision': 'Beslutning om klassifisering av hendelser',
            'sla_type_Information system classification review': 'Gjennomgang av klassifisering av informasjonssystem',
            'sla_type_Notification processing (sales website/app)': 'Varslingsbehandling (salgsnettsted/app)',
            'sla_type_Pen test cycle': 'Penn testsyklus',
            'sla_type_Registration processing (service platform)': 'Registreringsbehandling (tjenesteplattform)',
            'sla_type_Risk assessment cycle': 'Risikovurderingssyklus',
            'sla_type_Takedown decision (after review)': 'Avgjørelse om fjerning (etter gjennomgang)',
            'sla_type_Termination notice (public)': 'Oppsigelsesvarsel (offentlig)',
            sla_type_ai_impact_assessment_completion: 'AI konsekvensutredning fullført',
            sla_type_ai_sandbox_application_review: 'AI gjennomgang av sandkasseapplikasjoner',
            sla_type_ai_serious_incident_report: 'AI rapport om alvorlig hendelse',
            sla_type_ai_system_registration: 'AI systemregistrering',
            sla_type_authority_request_response: 'Myndighetsforespørselssvar',
            sla_type_certificate_issuance_review: 'Gjennomgang av sertifikatutstedelse',
            sla_type_complaint_handling_approach_notice: 'Tilnærmingsvarsel for klagebehandling',
            sla_type_complaint_incomplete_info_request: 'Ufullstendig forespørsel om klage',
            sla_type_data_governance_attestation: 'Attestering av datastyring',
            sla_type_dsr_acknowledgment: 'DSR-anerkjennelse',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Trekk tilbake / begrense / gjøre innsigelse',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Tredjepart',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Tilgang / korriger / gi',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Tredjepart',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Sletting',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Sletting, tredjepart',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Beskyttelsestiltak',
            sla_type_telecom_authority_request_response: 'Svar fra telemyndighetene',
            sla_type_rights_request_completion: 'Rettighetsforespørsel om fullføring',
            sla_type_incident_authority_notification: 'Melding fra hendelsesmyndighet',
            sla_type_incident_subject_notification: 'Varsel om hendelsesemne',
            sla_type_dpia_completion: 'DPIA fullføring',
            sla_type_data_retention_review: 'Gjennomgang av datalagring',
            sla_type_authority_filing_review: 'Myndighetsarkivgjennomgang',
            sla_type_authority_supplement_response: 'Myndighetstilleggsvar',
            sla_type_authority_resubmission: 'Gjeninnsending av myndigheten',
            sla_type_authority_filing_supplement_response: 'Myndighetsarkiveringstilleggssvar',
            sla_type_authority_filing_resubmission: 'Myndighetsarkivering på nytt',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'virkedager',
            unit_calendar_days: 'kalenderdager',
            unit_hours: 'timer',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Samtykke',
            form_category_privacy: 'Personvern',
            form_category_breach: 'Brudd',
            form_category_dpia: 'DPIA',
            form_category_register: 'Registrer deg',
            form_category_monitoring: 'Overvåking',
            form_category_vendor: 'Leverandør',
            form_category_org: 'Organisatorisk',
            form_category_sector: 'Sektor',
            form_category_data_mapping: 'Datakartlegging',
            form_category_governance: 'Styring',
            form_category_incident: 'Hendelse',
            form_category_authority_submission: 'Innlevering av myndighet',
            form_category_notice: 'Legg merke til',
            form_category_assessment_report: 'Vurderingsrapport',
            form_category_request: 'Forespørsel',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Arkivering',
            module_consent: 'Samtykke',
            module_rights_request: 'Rettighetsforespørsel',
            module_classification: 'Klassifisering',
            module_dpia: 'DPIA',
            module_governance: 'Styring',
            module_monitoring: 'Overvåking',
            module_register: 'Registrer deg',
            module_risk: 'Risiko',
            module_data_mapping: 'Datakartlegging',
            module_incident: 'Hendelse',
            module_assessment: 'Vurdering',
            module_vendor: 'Leverandør',
            module_policy: 'Politikk',
            module_audit: 'Revisjon',
            module_training: 'Trening',
            module_discovery: 'Oppdagelse',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Beregningsveiledning',
            enum_guidance_type_implementation_guidance: 'Implementeringsveiledning',
            enum_guidance_type_interpretive_note: 'Fortolkende notat',
            enum_guidance_type_legal_guidance: 'Juridisk veiledning',
            enum_guidance_type_procedural_guidance: 'Prosedyreveiledning',
            enum_guidance_type_sector_guidance: 'Sektorveiledning',
            enum_guidance_type_technical_methodology: 'Teknisk metodikk',
            enum_guidance_category_annual_reporting: 'Årsrapportering',
            enum_guidance_category_authority_cooperation: 'Myndighetssamarbeid',
            enum_guidance_category_authority_coordination: 'Myndighetskoordinering',
            enum_guidance_category_business_continuity: 'Forretningskontinuitet',
            enum_guidance_category_capital_adequacy: 'Kapitaldekning',
            enum_guidance_category_complaint_handling: 'Klagebehandling',
            enum_guidance_category_consent: 'Samtykke',
            enum_guidance_category_consent_management: 'Samtykkebehandling',
            enum_guidance_category_consumer_protection: 'Forbrukerbeskyttelse',
            enum_guidance_category_control_environment: 'Kontrollmiljø',
            enum_guidance_category_cross_border: 'Grenseoverskridende',
            enum_guidance_category_cross_border_transfers: 'Overføringer over landegrensene',
            enum_guidance_category_data_classification: 'Dataklassifisering',
            enum_guidance_category_data_localization: 'Datalokalisering',
            enum_guidance_category_data_protection_impact_assessment: 'Konsekvensvurdering for databeskyttelse',
            enum_guidance_category_direct_marketing: 'Direkte markedsføring',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Styring',
            enum_guidance_category_ict_risk_management: 'IKT Risikostyring',
            enum_guidance_category_incident_management: 'Hendelseshåndtering',
            enum_guidance_category_internal_audit: 'Internrevisjon',
            enum_guidance_category_isms_documentation: 'ISMS-dokumentasjon',
            enum_guidance_category_lawful_basis: 'Lovlig grunnlag',
            enum_guidance_category_licensing: 'Lisensering',
            enum_guidance_category_local_presence: 'Lokal tilstedeværelse',
            enum_guidance_category_logging_and_retention: 'Logging og oppbevaring',
            enum_guidance_category_logical_and_physical_access: 'Logisk og fysisk tilgang',
            enum_guidance_category_operational_resilience_testing: 'Operasjonell motstandstesting',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Plattformregistrering',
            enum_guidance_category_quality_of_service: 'Kvalitet på tjenesten',
            enum_guidance_category_risk_classification: 'Risikoklassifisering',
            enum_guidance_category_risk_management: 'Risikostyring',
            enum_guidance_category_scope_definition: 'Definisjon av omfang',
            enum_guidance_category_sectoral_localisation: 'Sektorlokalisering',
            enum_guidance_category_subscriber_protection: 'Abonnentbeskyttelse',
            enum_guidance_category_supply_chain: 'Forsyningskjede',
            enum_guidance_category_system_classification: 'Systemklassifisering',
            enum_guidance_category_system_operations: 'Systemoperasjoner',
            enum_guidance_category_telecommunications_data: 'Telekommunikasjonsdata',
            enum_guidance_category_third_party_risk: 'Tredjepartsrisiko',
            enum_guidance_category_transitional_provisions: 'Overgangsbestemmelser',
            enum_guidance_category_vulnerability_management: 'Sårbarhetshåndtering',
            enum_guidance_status_active: 'Aktiv',
            enum_guidance_status_draft: 'Utkast',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Tilgangsadministrasjon',
            enum_module_ai_governance: 'AI Styring',
            enum_module_asset_management: 'Asset Management',
            enum_module_audit_trail: 'Revisjonssti',
            enum_module_breach_notification: 'Bruddvarsel',
            enum_module_capital_adequacy: 'Kapitaldekning',
            enum_module_classification: 'Klassifisering',
            enum_module_consent: 'Samtykke',
            enum_module_consent_management: 'Samtykkebehandling',
            enum_module_cross_border_transfers: 'Overføringer over landegrensene',
            enum_module_data_localization: 'Datalokalisering',
            enum_module_data_mapping: 'Datakartlegging',
            enum_module_data_protection: 'Databeskyttelse',
            enum_module_data_subject_rights: 'Datasubjektets rettigheter',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Kryptering',
            enum_module_governance: 'Styring',
            enum_module_incident: 'Hendelse',
            enum_module_incident_management: 'Hendelseshåndtering',
            enum_module_internal_audit: 'Internrevisjon',
            enum_module_isms_documentation: 'ISMS-dokumentasjon',
            enum_module_isms_governance: 'ISMS-styring',
            enum_module_logging_and_monitoring: 'Logging og overvåking',
            enum_module_monitoring: 'Overvåking',
            enum_module_policy_management: 'Policy Management',
            enum_module_risk: 'Risiko',
            enum_module_risk_management: 'Risikostyring',
            enum_module_training: 'Trening',
            enum_module_vendor: 'Leverandør',
            enum_module_vendor_management: 'Leverandøradministrasjon',
            enum_module_vendor_risk: 'Leverandørrisiko',
            enum_module_vulnerability_management: 'Sårbarhetshåndtering',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Tilgang',
            category_availability: 'Tilgjengelighet',
            category_change_management: 'Endringsledelse',
            category_classification: 'Klassifisering',
            category_communication_and_information: 'Kommunikasjon og informasjon',
            category_compliance: 'Overholdelse',
            category_confidentiality: 'Konfidensialitet',
            category_control_activities: 'Kontrollaktiviteter',
            category_control_environment: 'Kontrollmiljø',
            category_incident: 'Hendelse',
            category_internal_audit: 'Internrevisjon',
            category_internal_control: 'Internkontroll',
            category_logical_and_physical_access: 'Logisk og fysisk tilgang',
            category_mis_data: 'Ledelsesinformasjonssystemdata',
            category_monitoring: 'Overvåking',
            category_monitoring_activities: 'Overvåkingsaktiviteter',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Personvern',
            category_processing_integrity: 'Behandlingsintegritet',
            category_reporting: 'Rapportering',
            category_risk_assessment: 'Risikovurdering',
            category_risk_mitigation: 'Risikoreduserende',
            category_sector_applicability: 'Sektoranvendelse',
            category_system_operations: 'Systemoperasjoner',
            category_training: 'Trening',
            category_vendor: 'Leverandør',
            guidance_related_forms_advisory: 'Noen skjemaer har relatert veiledning — se i',
            guidance_related_procedures_advisory: 'Noen prosedyrer har relatert veiledning - se i',
            guidance_related_requirements_advisory: 'Noen krav har relatert veiledning - se i',
        },
        channels: {
            title: 'Innsendingskanaler og frister',
            channels: 'Innsendingskanaler',
            add_channel: 'Legg til kanal',
            channel_type: 'Type',
            name: 'Navn',
            instructions: 'Instruksjoner',
            active: 'Aktiv',
            no_channels: 'Ingen innsendingskanaler konfigurert',
            portal: 'Portal',
            in_person: 'Personlig',
            postal: 'Post',
            email: 'E-post',
            add: 'Legg til',
            deadlines: 'Regulatoriske frister',
            add_deadline: 'Legg til frist',
            label: 'Etikett',
            days: 'Dager',
            day_type: 'Dagtype',
            calendar_days: 'Kalenderdager',
            working_days: 'Arbeidsdager',
            no_deadlines: 'Ingen frister konfigurert',
            description: 'Beskrivelse',
            failed_load: 'Kunne ikke load channel data',
            channel_added: 'Kanal lagt til',
            channel_added_announce: 'Innsendingskanal lagt til',
            failed_add_channel: 'Kunne ikke add channel',
            deadline_added: 'Frist lagt til',
            deadline_added_announce: 'Regulatorisk frist lagt til',
            failed_add_deadline: 'Kunne ikke add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Rammeverkkobling',
            modules: 'Tilknyttede moduler',
            add_module: 'Legg til modul',
            module_code: 'Modulkode',
            required: 'Påkrevd',
            link: 'Koble',
            no_modules: 'Ingen moduler koblet',
            notes: 'Notater',
            personas: 'Tilknyttede personaer',
            add_persona: 'Legg til persona',
            persona_id: 'Persona-ID',
            role_description: 'Rollebeskrivelse',
            no_personas: 'Ingen personaer koblet',
            failed_load: 'Kunne ikke load framework links',
            module_linked: 'Modul koblet',
            module_linked_announce: 'Modul koblet',
            failed_link_module: 'Kunne ikke link module',
            persona_linked: 'Persona koblet',
            persona_linked_announce: 'Persona koblet',
            failed_link_persona: 'Kunne ikke link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Venter',
                assessed: 'Vurdert',
                applied: 'Anvendt',
                dismissed: 'Avvist',
            },
            title: 'Regulatoriske oppdateringer og rammeverk-dashboard',
            page_description: 'Dekning i korte trekk for alle installerte regulatoriske rammeverk, samt produktets endringslogg for regulatoriske oppdateringer: endringer, ikrafttredelsesdatoer og konsekvensvurderinger.',
            coverage_dashboard: 'Dashboard for rammeverkdekning',
            no_frameworks: 'Ingen rammeverk funnet',
            framework: 'Rammeverk',
            modules: 'Moduler',
            templates: 'Maler',
            forms: 'Skjemaer',
            channels: 'Kanaler',
            deadlines: 'Frister',
            pending: 'Ventende oppdateringer',
            updates: 'Regulatoriske oppdateringer',
            create_update: 'Opprett oppdatering',
            framework_id: 'Rammeverk-ID',
            update_title: 'Tittel',
            description: 'Beskrivelse',
            effective_date: 'Ikrafttredelsesdato',
            amendment_ref: 'Endringsreferanse',
            create: 'Opprett',
            no_updates: 'Ingen regulatoriske oppdateringer',
            status: 'Status',
            impact: 'Påvirkning',
            change_summary: 'Endringsoversikt',
            assess: 'Vurdér',
            failed_load: 'Kunne ikke load data',
            update_created: 'Oppdatering opprettet',
            update_created_announce: 'Regulatorisk oppdatering opprettet',
            failed_create: 'Kunne ikke create update',
            update_assessed: 'Oppdatering vurdert',
            failed_assess: 'Kunne ikke assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Relaterte rammeverk',
            panel_intro: 'Hvordan dette rammeverket henger sammen med andre. Et overlegg kan gjelde under flere overordnede rammeverk samtidig; hvert vises nøyaktig én gang.',
            outgoing_heading: 'Dette rammeverket gjelder for',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Gjelder under dette rammeverket',
            outgoing_caption: 'Rammeverk som dette rammeverket peker på',
            incoming_caption: 'Rammeverk som hører under dette rammeverket',
            col_framework: 'Rammeverk',
            col_relationship: 'Relasjon',
            col_scope: 'Kilde',
            scope_global: 'Fra pakkeinnhold',
            scope_org: 'Organisasjonsspesifikk',
            load_failed: 'Kunne ikke laste relaterte rammeverk',
            relationship_kinds: {
                parent: 'Overordnet rammeverk',
                applies_to: 'Gjelder for',
                cross_reference: 'Kryssreferanse',
                supersedes: 'Erstatter',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Bank',
                insurance: 'Forsikring',
                healthcare: 'Helse',
                education: 'Education',
                ict_critical: 'Kritisk IKT',
                telecom: 'Telekommunikasjon',
                energy: 'Energi',
                transport: 'Transport',
                digital_services: 'Digitale tjenester',
                digital_technology_industry: 'Digital teknologiindustri',
                manufacturing: 'Manufacturing',
                public_administration: 'Offentlig forvaltning',
                payments: 'Betalinger',
                publishing: 'Forlagsvirksomhet',
                crypto_assets: 'Kryptoeiendeler',
                other: 'Annet',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sektor',
            direct_scope_label: 'Direkte rettslig omfang',
            chip_direct: 'Direkte anvendelig',
            chip_reference: 'Kun til referanse',
            banner_direct_copy: 'Direkte anvendelig for organisasjonen din. Sektorrapporter og frister er aktive.',
            banner_reference_copy: 'Kun til referanse — organisasjonen din er ikke klassifisert i denne sektoren.',
            edit_mode_button: 'Rediger modus',
            edit_form_title: 'Endre installasjonsmodus',
            mode_direct_label: 'Direkte — rettslig anvendelig',
            mode_direct_help: 'Planlegger sektorfrister og widgeter i dashbordet.',
            mode_reference_label: 'Kun til referanse',
            mode_reference_help: 'Kan leses, men uten frister eller widgeter.',
            reason_note_label: 'Begrunnelse (valgfritt)',
            reason_note_placeholder: 'f.eks. Bankdatterselskap kjøpt opp',
            cancel: 'Avbryt',
            confirm: 'Bekreft',
            confirming: 'Lagrer…',
            save: 'Lagre',
            saving: 'Lagrer…',
            toast_mode_changed: 'Modus for sektoroverlegg oppdatert.',
            toast_installed: 'Sektoroverlegg installert.',
            toast_sectors_saved: 'Egenklassifisering av sektorer lagret.',
            install_modal_title: 'Installer sektoroverlegg',
            install_modal_intro: 'Sektoroverlegg er valgfrie regulatoriske pakker avgrenset til én bransje.',
            install_modal_footer: 'Du kan endre installasjonsmodus senere fra detaljsiden for rammeverket.',
            install_button: 'Installer',
            installed_label: 'Installert',
            uninstall_button: 'Avinstaller',
            uninstall_confirm: 'Bekreft avinstallering',
            uninstall_cancel: 'Avbryt',
            suggested_label: 'Foreslått',
            catalog_page_title: 'Sektoroverlegg',
            catalog_page_intro: 'Valgfrie regulatoriske pakker avgrenset til én bransjesektor.',
            catalog_empty_title: 'Ingen sektoroverlegg tilgjengelige',
            catalog_empty_description: 'Sektoroverleggspakker som er publisert på plattformen din, vises her.',
            authority_label: 'Myndighet',
            org_sectors_title: 'Bransjesektorer',
            org_sectors_intro: 'Velg bransjesektorene organisasjonen din opererer i.',
            widget_title: 'Sektoroverleggrapporter med frist',
            widget_aria_label: 'Sektoroverleggrapporter med frist',
            widget_empty: 'Ingen kommende frister for sektoroverlegg.',
            audit_event_mode_changed: 'Installasjonsmodus for sektoroverlegg endret',
            audit_event_installed: 'Sektoroverlegg installert',
            view_details_button: 'Vis detaljer →',
            banner_aria_label: 'Status for sektoroverlegg',
            toast_load_failed: 'Kunne ikke laste sektoroverlegg',
            toast_install_failed: 'Kunne ikke installere sektoroverlegg',
            toast_uninstall_confirm_required: 'Skriv "{packCode}" for å bekrefte avinstallering.',
            toast_uninstalled: '{packCode} ble avinstallert.',
            toast_uninstall_failed: 'Kunne ikke avinstallere sektoroverlegg',
            uninstall_warning_destructive_label: 'Destruktiv handling.',
            uninstall_warning_body: 'Avinstallering sletter rammeverksposten og alle lagrede regulatoriske frister knyttet til {frameworkCode} fra denne organisasjonen.',
            uninstall_warning_type_to_confirm: 'Skriv {packCode} for å bekrefte.',
            uninstall_in_progress: 'Avinstallerer…',
            widget_view_all: 'Vis alle sektoroverlegg →',
            widget_due_in_days: 'Forfaller om {days} d',
            widget_overdue_days: 'Forfalt med {days} d',
            widget_due_today: 'Forfaller i dag',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Faseinndelte forpliktelser',
            panel_aria_label: 'Faseinndelte forpliktelser',
            panel_intro: 'Noen bestemmelser i dette rammeverket trer i kraft senere enn selve rammeverket.',
            phased_effective_date_label: 'Fasevis ikrafttredelse:',
            status_pending: 'Avventer',
            status_active: 'Aktiv',
            status_early: 'Aktiv (tatt i bruk tidlig)',
            notice_window_label: 'Varslingsfrist til myndighet:',
            days_label: 'dager',
            recipient_label: 'Mottaker:',
            early_implement_button: 'Merk som tatt i bruk tidlig',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Merk faseinndelt forpliktelse som tatt i bruk tidlig',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'Gjelder fra',
            reason_label: 'Begrunnelse (valgfritt)',
            reason_placeholder: 'f.eks. Styret godkjente tidlig innføring 2026-09-01',
            confirm: 'Bekreft',
            toast_early_implemented: 'Tidlig innføring registrert.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Faseinndelt forpliktelse merket som tatt i bruk tidlig',
            widget_rolling_window_label: 'Fasevise aktiveringer innen 90 dager',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Oversikt',
            partner_orgs: 'Partnerorganisasjoner',
            service_tiers: 'Tjenestenivåer',
            deliverable_templates: 'Leveransemaler',
            deliverables: 'Leveranser',
            brand_profiles: 'Merkeprofiler',
            support_tickets: 'Støttesaker',
            workflow_templates: 'Arbeidsflytmaler',
            workflows: 'Arbeidsflyter',
            escalation: 'Eskalering',
            reports: 'Rapporter',
            audit: 'Revisjonsspor',
        },
        dashboard: {
            title: 'Partneroversikt',
            loading: 'Laster dashboarddata...',
            total_managed_tenants: 'Totalt Administrerte Leietakere',
            avg_health_score: 'Gj.sn. Helse Poeng',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Åpne Saker',
            upcoming_deliverables: 'Kommende Leveranser',
            escalated_tickets: 'Eskalerte Saker',
            managed_tenants: 'Administrerte Leietakere',
            partner_tier: 'Partner Nivå',
            managed_tenants_count: 'Administrerte Leietakere Antall',
            onboarding_date: 'Onboarding Dato',
            view_details: 'Vis Detaljer',
            no_partner_orgs: 'Ingen partnerorganisasjoner funnet.',
        },
        partner_orgs: {
            title: 'Partnerorganisasjoner',
            new: 'Ny Partner Org.',
            contact_name: 'Kontakt Navn',
            contact_email: 'Kontakt E-post',
            partner_tier: 'Partner Nivå',
            managed_orgs: 'Administrerte Org.',
            onboarding_date: 'Onboarding Dato',
            no_orgs: 'Ingen partnerorganisasjoner funnet.',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Kontakt Telefon',
            metadata_json: 'Metadata (JSON)',
            change_status: 'Endrings Status',
            new_status: 'Ny Status',
            invalid_json_metadata: 'Ugyldig JSON i metadatafelt',
            suspended: 'Suspendert',
            offboarded: 'Avregistrert',
            all_tiers: 'Alle Nivåer',
            silver: 'Sølv',
            gold: 'Gull',
            platinum: 'Platina',
            status_offboarded: 'Avregistrert',
            status_suspended: 'Suspendert',
            tier_gold: 'Gull',
            tier_platinum: 'Platina',
            tier_silver: 'Sølv',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Tjenestenivåer',
            new: 'Ny Nivå',
            tier_name: 'Nivå Navn',
            monthly_fee: 'Månedlig Fee',
            features: 'Funksjoner',
            no_tiers: 'Ingen service tiers funnet.',
            tier_code: 'Nivå Kode',
            response_time_hrs: 'Svartid (timer)',
            resolution_time_hrs: 'Løsningstid (timer)',
            review_frequency: 'Gjennomgang Frekvens',
            sla_response_time: 'SLA-svartid (timer)',
            sla_resolution_time: 'SLA-løsningstid (timer)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Sammenligner...',
            compare: 'Sammenlign',
            comparison: 'Tjenestenivåsammenligning',
            attribute: 'Attributt',
            deprecate: 'Utfas',
            deprecated: 'Utfaset',
            select_compare: 'Velg mindst 2 niveauer til sammenligning',
            comparison_title: 'Tjenestenivåsammenligning',
            status_deprecated: 'Utfaset',
            activate: 'Aktiver',
            resolution_time: 'Løsning Tid',
            response_time: 'Svar Tid',
            select_at_least_two: 'Velg mindst 2 niveauer',
        },
        deliverable_templates: {
            title: 'Leveransemaler',
            new: 'Ny Mal',
            template_name: 'Mal Navn',
            deliverable_type: 'Leveranse Type',
            no_templates: 'Ingen leveranse maler funnet.',
            frequency: 'Frekvens',
            est_effort_hrs: 'Ansl. indsats (timer)',
            estimated_effort: 'Estimeret indsats (timer)',
            associated_module: 'Tilknyttet Modul',
            linked_tiers_count: 'Tilknyttede Nivåer Antall',
            evidence_requirements: 'Bevisskrav (kommaseparert)',
            tier_ids: 'Niveau-ID-er (kommaseparert)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Én Tid',
            frequency_monthly: 'Månedlig',
            frequency_quarterly: 'Kvartalsvis',
            frequency_annually: 'Årlig',
            type_report: 'Rapport',
            type_review: 'Gjennomgang',
            type_assessment: 'Vurdering',
            type_meeting: 'Møte',
            type_configuration: 'Konfigurasjon',
            freq_one_time: 'Én Tid',
            freq_monthly: 'Månedlig',
            freq_quarterly: 'Kvartalsvis',
            freq_annually: 'Årlig',
            status_deprecated: 'Utfaset',
        },
        deliverables: {
            title: 'Leveranser',
            new: 'Ny Leveranse',
            partner: 'Partner',
            deliverable_type: 'Leveranse Type',
            due_date: 'Due Dato',
            no_deliverables: 'Ingen leveranser funnet.',
            assigned_to: 'Tildelt Til',
            completion_date: 'Ferdigstillelse Dato',
            update_status: 'Oppdatering Status',
            hide_details: 'Skjul detaljer',
            view_details: 'Vis Detaljer',
            evidence_ids: 'Bevis ID-er',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Klient Godkjenning',
            bulk_update: 'Masse Oppdater',
            bulk_update_status: 'Masse Oppdater Status',
            generate_schedule: 'Generer Planlegg',
            generate_deliverable_schedule: 'Generer Leverance Planlegg',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisasjon ID',
            service_tier_id: 'Tjeneste Nivå ID',
            filter_assigned_to: 'Filter etter Tildelt Til',
            select_status: 'Velg status...',
            new_status: 'Ny Status',
            upcoming: 'Kommende',
            overdue: 'Forsinket',
            status_deferred: 'Utsatt',
            status_overdue: 'Forsinket',
            status_upcoming: 'Kommende',
        },
        brand_profiles: {
            title: 'Merkeprofiler',
            new: 'Ny Brand Profil',
            partner: 'Partner',
            brand_name: 'Merkevare Navn',
            primary_color: 'Primær Farge',
            logo_url: 'Logo URL',
            no_profiles: 'Ingen brand profiles funnet.',
            secondary_color: 'Sekundær Farge',
            header_text: 'Topptekst Tekst',
            footer_text: 'Bunntekst Tekst',
            contact_email: 'Kontakt E-post',
            contact_phone: 'Kontakt Telefon',
            contact_website: 'Kontakt Nettsted',
            contact_address: 'Kontaktadresse',
            is_default: 'Er Standard',
            set_default: 'Set Standard',
            preview: 'Forhåndsvisning',
            report_header_preview: 'Rapport Topptekst Forhåndsvisning',
            report_footer_preview: 'Rapport Bunntekst Forhåndsvisning',
            preview_description: 'Dette er en forhåndsvisning av rapporthodet med de valgte merkevarefargene.',
            contact_website_short: 'Nettsted',
            logo_preview: 'Logo forhåndsvisning',
        },
        support_tickets: {
            title: 'Støttesaker',
            new: 'Ny Sak',
            partner: 'Partner',
            subject: 'Emne',
            assigned_to: 'Tildelt Til',
            no_tickets: 'Ingen støtte saker funnet.',
            category: 'Kategori',
            priority: 'Prioritet',
            escalation_tier: 'Eskalering Nivå',
            sla_status: 'SLA-status',
            update_status: 'Oppdatering Status',
            escalate: 'Eskaler',
            add_note: 'Add Notat',
            escalate_ticket: 'Eskaler Sak',
            escalate_to_tier: 'Eskaler To Nivå',
            notes_timeline: 'Notater Tidslinje',
            no_notes: 'Ingen notater ennå.',
            attachments: 'Vedlegg',
            no_attachments: 'Ingen attachments.',
            client_org_id: 'Klient Org. ID',
            content: 'Innhold',
            note_type: 'Type',
            view_detail: 'Vis Detalj',
            hide: 'Skjul',
            new_status: 'Ny Status',
            all_categories: 'Alle Kategorier',
            all_priorities: 'Alle prioriteringer',
            all_tiers: 'Alle Nivåer',
            ticket_not_found: 'Sak ikke fundet',
            ticket_id: 'Sak ID',
            client_org: 'Klient Org.',
            deescalate: 'Deeskaler',
            deescalate_ticket: 'Deeskaler Sak',
            deescalate_to_tier: 'Deeskaler To Nivå',
            add_attachment: 'Add Vedlegg',
            file: 'Fil',
            linked_entity: 'Tilknyttede Enhet',
            sla_target: 'Mål',
            breached: 'Brutt',
            remaining_hours: 'gjenstående',
            na: 'N/A',
            tickets_found: 'saker funnet',
            reason: 'Årsak',
            target: 'Mål',
            upload: 'Last opp',
        },
        workflow_templates: {
            title: 'Arbeidsflytmaler',
            new: 'Ny Mal',
            template_name: 'Mal Navn',
            steps: 'Steg',
            no_templates: 'Ingen arbeidsflyt maler funnet.',
            category: 'Kategori',
            view_steps: 'Vis Steg',
            hide_steps: 'Skjul trinn',
            step: 'Steg',
            step_title: 'Tittel',
            step_description: 'Beskrivelse',
            assignee_role: 'Tilordnet rolle',
            sla_hours: 'SLA Timer',
            evidence_required: 'Bevis Påkrevd',
            guidance_notes: 'Veiledning Notater',
            add_step: 'Add Steg',
            no_steps: 'Ingen steg definert.',
            no_steps_defined: 'Ingen steg definert.',
            templates_found: 'maler funnet',
            all_categories: 'Alle Kategorier',
        },
        workflows: {
            client_org: 'Klient Org.',
            title: 'Arbeidsflyt Instanser',
            new: 'Ny Arbeidsflyt',
            template: 'Mal',
            partner: 'Partner',
            current_step: 'Nåværende Steg',
            progress: 'Fremdrift',
            no_workflows: 'Ingen arbeidsflyt instanser funnet.',
            instances_found: 'instanser funnet',
        },
        escalation: {
            title: 'Eskalering Dashboard',
            ticket: 'Sak',
            partner: 'Partner',
            escalated_at: 'Eskalerte Ved',
            reason: 'Årsak',
            no_escalations: 'Ingen eskaleringer funnet.',
            time_in_tier: 'Tid i Nivå',
            auto_evaluate: 'Auto Evaluer',
            priority_critical: 'Kritisk',
            priority_high: 'Høy',
            priority_medium: 'Middels',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Deeskaleret fra dashboard',
            sla_breached: 'SLA brutt',
            sla_remaining: 'SLA Gjenstående: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Klient Org.',
            title: 'Rapporter',
            partner: 'Partner',
            report_type: 'Rapport Type',
            generated_at: 'Generert Ved',
            no_reports: 'Ingen rapporter funnet.',
            generate: 'Generer Rapport',
        },
        audit: {
            title: 'Partner Revisjon Spor',
            event_type: 'Hendelse Type',
            entity_type: 'Enhet Type',
            entity: 'Enhet',
            entity_id: 'Enhet ID',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            no_events: 'Ingen revisjon hendelser funnet.',
            events_found: 'hendelser funnet',
            client_org: 'Kundeorganisasjon',
            all_actors: 'Alle aktører',
            event_type_search: 'Søk hendelsestype',
            event_type_created: 'Opprettet',
            event_type_updated: 'Oppdatert',
            event_type_deleted: 'Slettet',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partner Org',
            entity_type_service_tier: 'Tjenestenivå',
            entity_type_deliverable_template: 'Leverbar mal',
            entity_type_deliverable_instance: 'Leverbar forekomst',
            entity_type_brand_profile: 'Merkevareprofil',
            entity_type_support_ticket: 'Støttebillett',
            entity_type_workflow_template: 'Arbeidsflytmal',
            entity_type_workflow_instance: 'Arbeidsflytforekomst',
            entity_type_generated_report: 'Generert rapport',
            entity_type_audit_export: 'Revisjonseksport',
        },
        no_permission_title: 'Utilstrekkelige tillatelser',
        no_permission_message: 'Du har ikke tillatelse til å få tilgang til partnerarbeidsområdet.',
        status_active: 'Aktiv',
        status_suspended: 'Suspendert',
        status_offboarded: 'Utenbords',
        status_completed: 'Fullført',
        status_cancelled: 'Kansellert',
        status_deprecated: 'Avviklet',
        status_new: 'Nytt',
        status_triaged: 'Triagert',
        status_in_progress: 'Pågår',
        status_waiting_on_client: 'Venter på klient',
        status_waiting_on_vendor: 'Venter på leverandøren',
        status_escalated: 'Eskalert',
        status_resolved: 'Løst',
        status_closed: 'Stengt',
        status_pending: 'Venter',
        status_skipped: 'Hoppet over',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Onboarding',
        category_incident_escalation: 'Eskalering av hendelsen',
        category_config_change: 'Config Change',
        category_periodic_review: 'Periodisk gjennomgang',
        category_offboarding: 'Offboarding',
        category_custom: 'Egendefinert',
        category_configuration: 'Konfigurasjon',
        category_incident: 'Hendelse',
        category_compliance_question: 'Samsvarsspørsmål',
        category_feature_request: 'Funksjonsforespørsel',
        category_bug_report: 'Feilrapport',
        priority_critical: 'Kritisk',
        priority_high: 'Høy',
        priority_medium: 'Middels',
        priority_low: 'Lavt',
        tier_silver: 'Sølv',
        tier_gold: 'Gull',
        tier_platinum: 'Platina',
        report_type_compliance_summary: 'Samsvarssammendrag',
        report_type_risk_overview: 'Risikooversikt',
        report_type_incident_report: 'Hendelsesrapport',
        report_type_consent_status: 'Samtykkestatus',
        report_type_program_maturity: 'Programforfall',
        report_type_executive_pack: 'Executive-pakke',
        note_type_internal: 'Intern',
        note_type_client_visible: 'Klient synlig',
        status_updated: 'Status oppdatert',
        deliverable_status_updated: 'Leveringsstatus ble oppdatert',
        ticket_status_updated: 'Billettstatusen ble oppdatert',
        ticket_escalated: 'Billetten ble eskalert',
        note_added: 'Merknaden ble lagt til',
        attachment_uploaded: 'Vedlegget ble lastet opp',
        ticket_deescalated: 'Billetten deeskalerte vellykket',
        auto_evaluation_completed: 'Auto-evaluering fullført',
        template_created: 'Arbeidsflytmal ble opprettet',
        template_updated: 'Arbeidsflytmalen ble oppdatert',
        brand_set_default: 'Merkevareprofilen er angitt som standard',
        brand_created: 'Merkevareprofilen ble opprettet',
        brand_updated: 'Merkevareprofilen ble oppdatert',
        tier_created: 'Tjenestenivået ble opprettet',
        tier_updated: 'Tjenestenivået ble oppdatert',
        tier_deprecated: 'Tjenestenivået ble avviklet',
        tier_activated: 'Tjenestenivået ble aktivert',
        period: 'Periode',
        format: 'Format',
        period_start: 'Periodestart',
        period_end: 'Periodeslutt',
        reports_count: '{count} rapporterer',
        reports_count_one: '{count} rapport',
        metric: 'Metrisk',
        value: 'Verdi',
        showing_chart_view: 'Viser kartvisning',
        showing_table_view: 'Viser datatabellvisning',
        evidence_ids_placeholder: 'f.eks. ev-001, ev-002',
        confirm_cancel_workflow: 'Er du sikker på at du vil avbryte denne arbeidsflyten? Denne handlingen kan ikke angres.',
        export_csv: 'Eksporter CSV',
        compliance_qualifications_title: 'Compliance Service-kvalifikasjoner',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Regulatory Output Alignment',
        regulatory_output_desc: 'Hver maltype tilordnes en regulatorisk utdatakategori: Rapporter produserer samsvarsdokumentasjon, vurderinger validerer kontrolleffektivitet, vurderinger dekker risikoevalueringer (DPIA, gap-analyse), møter med dokumentstyringsøkter. Beviskrav definerer artefaktene hver leveranse må referere til for revisjonsberedskap.',
        sla_compliance_title: 'SLA Sporing for overholdelseslevering',
        sla_compliance_desc: 'SLAer for respons og løsningstid er kontraktsmessig bindende for levering av samsvarstjenester. Gjennomgangsfrekvens avgjør hvor ofte partnersamsvarsleveranser evalueres. SLA-brudd på overholdelseskritiske billetter utløser automatisk eskalering og logges i Partner Audit Trail for regulatoriske bevis.',
        regulatory_engagement_title: 'Regulatoriske engasjementmønstre',
        regulatory_engagement_desc: 'Arbeidsflytkategorier kartlegges til regulatoriske engasjementtyper: Onboarding dekker innledende samsvarsoppsett og gapanalyse, periodisk gjennomgang håndterer tilbakevendende samsvarsvurderinger, Incident Escalation administrerer regulatoriske bruddrespons, Offboarding sikrer at forpliktelsene til oppbevaring av data blir oppfylt. Hvert trinn kan kreve bevisinnsamling for fullstendighet i revisjonssporet.',
        compliance_escalation_title: 'Samsvarskritiske eskaleringsprosedyrer',
        compliance_escalation_desc: 'L1 håndterer standard driftsproblemer. L2 dekker samsvarspåvirkende svikt (misste regulatoriske tidsfrister, kontrollhull). L3 er reservert for regulatoriske bruddscenarier som krever umiddelbar oppmerksomhet fra ledelsen. SLA brudd på L2/L3-billetter genererer revisjonssporoppføringer automatisk. Alle eskalerings- og deeskaleringshandlinger logges permanent.',
        audit_regulatory_title: 'Regulatory Evidence Trail',
        audit_regulatory_desc: 'Dette revisjonssporet gir manipulasjonssikre poster for overholdelse av regelverk. Hver partnerhandling (opprett, oppdater, slett, eskalering, statusendring) logges med aktøridentitet, tidsstempel og full nyttelastdiff. Bruk Entity Type-filtre for å isolere bevis etter samsvarsdomene. Eksporter filtrerte resultater for å produsere revisjonsklar dokumentasjon for regulatoriske inspeksjoner.',
        evidence_trail_title: 'Cross-Module Evidence Trail',
        evidence_trail_desc: 'Hvert arbeidsflyttrinn kan samle bevis-ID-er som kobler til leveranser, revisjonshendelser og samsvarsartefakter på tvers av moduler. Å gå videre med bevis-IDer skaper en verifiserbar kjede som kobler partnerarbeidsflyter til regulatorisk dokumentasjon. Fullførte arbeidsflyter med fullstendige beviskjeder tjener som bevis på levering av overholdelse.',
        evidence_ids_label: 'Bevis-IDer',
    },
    org_groups: {
        title: 'Organisasjonsgrupper',
        new: 'Opprett Gruppe',
        group_name: 'Gruppe Navn',
        member_count: 'Medlem Antall',
        root_org: 'Rot Org.',
        no_groups: 'Ingen org. grupper funnet.',
        add_organization: 'Legg til Organisasjon',
        select_organization: 'Velg en organisasjon...',
        no_available_orgs: 'Ingen tilgjengelige organisasjoner å legge til.',
        no_members: 'Ingen medlemsorganisasjoner i denne gruppen.',
        member_added: 'Organisasjon lagt til gruppen.',
        member_removed: 'Organisasjon fjernet fra gruppen.',
        already_in_group: 'Denne organisasjonen er allerede i en gruppe.',
        org_not_found: 'Organisasjon ikke funnet.',
        add_member_error: 'Kunne ikke legge til organisasjon i gruppen.',
        remove_member_error: 'Kunne ikke fjerne organisasjon fra gruppen.',
        confirm_remove_member: 'Er du sikker på at du vil fjerne "{name}" fra denne gruppen?',
        load_error: 'Kunne ikke laste org. gruppedetaljer.',
        page_title_list: 'Organisasjonsgrupper',
        page_title_detail: 'Organisasjonsgruppedetaljer',
        page_title_create: 'Opprett organisasjonsgruppe',
        page_title_settings: 'Organisasjonsgruppeinnstillinger',
        page_title_users: 'Gruppebrukere',
        page_title_reports: 'Grupperapporter',
        page_title_dashboard: 'Gruppe Dashboard',
        page_title_audit: 'Konsernrevisjon',
        page_title_org_audit: 'Organisasjonsgrupperevisjon',
        page_title_shared_vendors: 'Delte leverandører',
        page_title_shared_frameworks: 'Delte rammer',
        page_title_shared_forms: 'Delte skjemamaler',
        page_title_merged_vendors: 'Sammenslåtte leverandører',
        page_title_settings_inheritance: 'Innstillinger Arv',
        announce_loaded_groups: 'Lastet inn {count} organisasjonsgrupper',
        announce_loaded_group: 'Lastet gruppe {name}',
        announce_loaded_users: 'Lastet {count} gruppebrukere',
        announce_loaded_reports: 'Lastet {count} rapporter',
        announce_loaded_vendors: 'Lastet inn {count} delte leverandører',
        announce_loaded_frameworks: 'Lastet {count} delte rammer',
        announce_loaded_templates: 'Lastet {count} delte skjemamaler',
        announce_loaded_audit: 'Lastet inn {count} revisjonshendelser',
        announce_loaded_merged: 'Lastet inn {shared} delte og {local} lokale leverandører',
        announce_dashboard_loaded: 'Dashboard lastet.',
        announce_dashboard_refreshed: 'Dashbordet er oppdatert.',
        announce_dashboard_refresh_failed: 'Kunne ikke oppdatere oversikten.',
        announce_refreshing: 'Oppdaterer dashbordet ...',
        announce_settings_loaded: 'Innstillingsarv lastet.',
        announce_settings_saved: 'Innstillingene er lagret.',
        announce_saving_settings: 'Lagrer innstillinger...',
        announce_error_saving: 'Feil under lagring av innstillinger.',
        announce_group_dissolved: 'Gruppen oppløst.',
        announce_creating_group: 'Oppretter organisasjonsgruppe ...',
        announce_group_created: 'Organisasjonsgruppen ble opprettet.',
        announce_error_creating: 'Feil ved opprettelse av organisasjonsgruppe.',
        announce_vendor_published: 'Leverandør publisert vellykket.',
        announce_vendor_retired: 'Selger gikk av med pensjon.',
        announce_framework_published: 'Rammeverket ble publisert.',
        announce_framework_retired: 'Framework pensjonert.',
        announce_template_published: 'Skjemamal ble publisert.',
        announce_template_retired: 'Skjemamalen er trukket tilbake.',
        announce_role_assigned: 'Rollen ble tildelt.',
        announce_error_assign_role: 'Feil ved tildeling av rolle.',
        announce_role_revoked: 'Rollen er tilbakekalt.',
        announce_report_generating: 'Genererer rapport...',
        announce_report_requested: 'Rapportgenerering forespurt.',
        announce_error_report: 'Feil ved forespørsel om rapport.',
        announce_report_deleted: 'Rapport slettet.',
        announce_search_applied: 'Søk brukt.',
        announce_search_cleared: 'Søket er slettet.',
        announce_tab_selected: '{tab}-fanen er valgt',
        announce_error_add_member: 'Feil ved å legge til medlem i gruppen.',
        announce_category_reverted: '{category} tilbakestilt til arvet fra forelder.',
        announce_category_overridden: '{category} satt til lokal overstyring.',
        announce_override_saved: '{category} overstyring lagret.',
        announce_export_started: 'Eksporterer revisjon som {format}...',
        announce_export_completed: 'Eksporten som {format} fullført.',
        announce_export_failed: 'Eksporten mislyktes.',
        announce_filter_org: 'Filtrert etter organisasjon {name}',
        announce_filter_org_cleared: 'Organisasjonsfilteret er tømt',
        announce_filter_event: 'Filtrert etter hendelsestype {type}',
        announce_filter_cleared: 'Filter tømt',
        announce_download: 'Downloading {name} as {format}',
        error_load_groups: 'Failed to load org groups. Vennligst prøv igjen.',
        error_load_users: 'Failed to load group users.',
        error_load_reports: 'Failed to load group reports.',
        error_load_dashboard: 'Failed to load group dashboard.',
        error_load_settings: 'Failed to load group settings.',
        error_load_vendors: 'Failed to load merged vendors.',
        error_save_settings: 'Kunne ikke lagre innstillingene.',
        error_dissolve_group: 'Failed to dissolve group.',
        error_assign_role: 'Kunne ikke tildele rollen.',
        error_create_group: 'Failed to create org group. Vennligst prøv igjen.',
        error_request_report: 'Failed to request report.',
        error_group_name_required: 'Gruppenavn er påkrevd.',
        success_settings_saved: 'Innstillingene er lagret.',
        confirm_retire_vendor: 'Are you sure you want to retire vendor "{name}"?',
        confirm_retire_framework: 'Are you sure you want to retire framework "{name}"?',
        confirm_retire_template: 'Er du sikker på at du vil trekke tilbake skjemamalen "{name}"?',
        confirm_dissolve_group: 'Er du sikker på at du vil løse opp "{name}"? Denne handlingen kan ikke angres.',
        confirm_revoke_role: 'Er du sikker på at du vil tilbakekalle "{role}"-rollen fra "{name}"?',
        confirm_delete_report: 'Er du sikker på at du vil slette rapporten "{name}"?',
        dissolve_group: 'Løs opp gruppe',
        dissolve_warning: 'Dette vil oppløse organisasjonsgruppen permanent. Alle medlemsorganisasjoner vil bli fjernet.',
        count_groups: '{count} gruppe',
        count_groups_plural: '{count} grupper',
        count_users: '{count} bruker',
        count_users_plural: '{count} brukere med grupperoller',
        count_events: '{count} hendelse',
        count_events_plural: '{count} hendelser',
        count_reports: '{count} rapport',
        count_reports_plural: '{count} rapporterer',
        count_vendors: '{count} leverandør delt på tvers av gruppen',
        count_vendors_plural: '{count} leverandører delt på tvers av gruppen',
        count_frameworks: '{count} rammeverk delt på tvers av gruppen',
        count_frameworks_plural: '{count} rammeverk delt på tvers av gruppen',
        count_templates: '{count} mal delt på tvers av gruppen',
        count_templates_plural: '{count} maler delt på tvers av gruppen',
        count_shared_vendors: '{count} delte leverandører',
        count_local_vendors: '{count} lokale leverandører',
        merged_combined_view: 'Kombinert visning av {shared} delte og {local} lokale leverandører',
        dashboard: {
            title: 'Gruppeoversikt',
            loading: 'Laster dashboarddata...',
            total_groups: 'Totalt Grupper',
            total_members: 'Totalt Medlemmer',
            active_groups: 'Aktive Grupper',
            overall_compliance_score: 'Samlet samsvarspoeng',
            group_risk_level: 'Grupperisikonivå',
            sla_compliance: 'SLA Samsvar',
            compliance_posture: 'Overholdelsesstilling',
            risk_heatmap: 'Risiko varmekart',
            sla_performance: 'SLA Ytelse',
            recent_incidents: 'Nylige hendelser',
            recent_activity: 'Nylig aktivitet',
            refresh_label: 'Oppdater dashborddata',
            score_good: 'Bra',
            score_needs_attention: 'Trenger oppmerksomhet',
            score_critical: 'Kritisk',
            trend_up: 'Trender opp',
            trend_down: 'Trender nedover',
            trend_stable: 'Stabil',
            risk_low: 'Low',
            risk_medium: 'Middels',
            risk_high: 'Høy',
            risk_critical: 'Kritisk',
            risk_unknown: 'Ukjent',
            col_organization: 'Organisasjon',
            col_score: 'Score',
            col_trend: 'Trend',
            col_open_issues: 'Åpne problemer',
            col_critical_issues: 'Kritiske problemer',
            col_health: 'Helse',
            col_vendor_risk: 'Leverandørrisiko',
            col_data_risk: 'Datarisiko',
            col_compliance_risk: 'Samsvarsrisiko',
            col_total_cases: 'Totalt antall saker',
            col_on_time: 'På tide',
            col_breached: 'Brudd',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Gj.sn. dager',
            sr_vendor_risk: 'Leverandørrisikoscore: {score}',
            sr_data_risk: 'Datarisikoscore: {score}',
            sr_compliance_risk: 'Overholdelsesrisikoscore: {score}',
            sr_sla_breaches: 'Advarsel: {count} SLA brudd',
            sr_severity: 'Alvorlighetsgrad: {severity}',
        },
        detail: {
            title: 'Gruppedetaljer',
            members: 'Medlemmer',
            settings: 'Innstillinger',
            audit: 'Revisjon',
            sr_loading: 'Laster organisasjonsgruppedetaljer',
            sr_loading_hierarchy: 'Laster hierarki',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'medlemsorganisasjon',
            root_org_label: 'rotorganisasjon',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Gruppebrukere',
            add_user: 'Add Bruker',
            no_users: 'Ingen brukere i dette gruppe.',
            role_group_admin: 'Gruppeadministrator',
            role_group_reader: 'Gruppeleser',
            sr_loading: 'Laster gruppebrukere',
        },
        reports: {
            title: 'Grupperapporter',
            no_reports: 'Ingen rapporter funnet.',
            generate_report: 'Generer ny rapport',
            report_type_compliance: 'Samsvarssammendrag',
            report_type_risk: 'Risikooversikt',
            report_type_sla: 'SLA Ytelse',
            report_type_incident: 'Hendelsesrapport',
            report_type_executive: 'Executive Brief',
            report_type_board: 'Styrets rapport',
            status_queued: 'I kø',
            status_generating: 'Genererer',
            status_completed: 'Fullført',
            status_failed: 'Mislyktes',
            period_start: 'Periodestart',
            period_end: 'Periodeslutt',
            sr_loading: 'Laster grupperapporter',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Delte leverandører',
            no_vendors: 'Ingen delte leverandører funnet.',
            merged_vendors: 'Sammenslåtte Leverandører',
            publish_form_label: 'Publiser delt leverandør',
            sr_loading: 'Laster inn delte leverandører',
            sr_loading_merged: 'Laster sammenslåtte leverandører',
            local_vendors: 'Lokale leverandører',
            source_shared: 'Delt fra gruppen',
            source_local: 'Lokal leverandør',
            category_technology: 'Teknologi',
            category_consulting: 'Rådgivning',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Sky',
            category_data_processing: 'Databehandling',
            category_other: 'Annet',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Middels',
            risk_tier_high: 'Høy',
            risk_tier_critical: 'Kritisk',
            compliance_compliant: 'Samsvarlig',
            compliance_non_compliant: 'Ikke-kompatibel',
            compliance_pending: 'Venter',
            compliance_under_review: 'Under vurdering',
            status_active: 'Aktiv',
            status_retired: 'Pensjonist',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Rammekode',
            title: 'Delte regelverk',
            no_frameworks: 'Ingen delte rammeverk funnet.',
            publish_form_label: 'Publiser delt rammeverk',
            sr_loading: 'Laster inn delte rammer',
            status_active: 'Aktiv',
            status_retired: 'Pensjonist',
        },
        shared_forms: {
            title: 'Delte skjemamaler',
            no_forms: 'Ingen delte skjema maler funnet.',
            publish_form_label: 'Publiser delt skjemamal',
            sr_loading: 'Laster inn delte skjemamaler',
            status_active: 'Aktiv',
            status_retired: 'Pensjonist',
        },
        audit: {
            title: 'Grupperevisjon',
            consolidated_title: 'Konsolidert Revisjon Spor',
            consolidated_subtitle: 'Tværorganisatoriske revisjonshendelser for denne gruppe',
            audit_log_title: 'Gruppe Revisjon Logg',
            event_type: 'Hendelse Type',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            source_org: 'Kilde Org.',
            org: 'Org',
            summary: 'Sammendrag',
            details: 'Detaljer',
            no_events: 'Ingen revisjon hendelser funnet.',
            all_events: 'Alle Hendelser',
            all_orgs: 'Alle Org.',
            organization: 'Organisasjon',
            from: 'Fra',
            to: 'To',
            search_events: 'Søk hendelser...',
            clear: 'Tøm',
            events_24h: 'Hendelser (24h)',
            events_7d: 'Hendelser (7d)',
            active_orgs: 'Aktive Org.',
            exporting: 'Eksporterer...',
            export_csv: 'Eksport CSV',
            export_json: 'Eksport JSON',
            back_to_group: 'Tilbake to Gruppe',
            back_to_detail: 'Tilbake til detaljer',
            sr_loading: 'Laster revisjonshendelser',
            sr_loading_summary: 'Laster revisjonssammendrag',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Gruppe Innstillinger',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Innstillinger Arv',
            inheritance_subtitle: 'Konfigurer hvilke innstillingskategorier som arver fra gruppen kontra bruk av lokale overstyringer',
            sla_settings: 'SLA Innstillinger',
            calendar_settings: 'Kalender Innstillinger',
            notification_settings: 'Varsling Innstillinger',
            intake_settings: 'Inntak Innstillinger',
            cmp_integration: 'CMP Integrasjon',
            overridden: 'Overstyrt',
            inheriting: 'Arvet',
            hide_defaults: 'Skjul standardverdier',
            show_group_defaults: 'Vis gruppestandarder',
            group_defaults: 'Gruppe Standardverdier',
            inherit_from_group: 'Nedarv fra gruppe',
            use_local_override: 'Use Lokal Overstyr',
            local_override_json: 'Lokal Overstyr (JSON)',
            save_override: 'Lagre Overstyr',
            saving: 'Lagrer...',
            sr_loading: 'Laster inn innstillinger arv',
            sr_loading_settings: 'Laster inn gruppeinnstillinger',
            sr_enabled: 'Aktivert',
            sr_disabled: 'Deaktivert',
            sr_override_active: 'Lokal overstyring aktiv',
            sr_inherited: 'Arvet fra forelder',
            danger_zone: 'Faresone',
            sharing_options: 'Delingsalternativer',
            advanced_options: 'Avanserte alternativer',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Aktiv',
        status_dissolved: 'Oppløst',
        status_pending: 'Venter',
        status_suspended: 'Suspendert',
    },
    integrations: {
        nav: {
            webhooks: 'Webhooker',
            domains: 'Domener',
            export_schedules: 'Eksportplaner',
            email_log: 'E-postlogg',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Brukere',
            roles: 'Roller',
            profile: 'Min profil',
        },
        security: {
            mfa: 'MFA-innstillinger',
            sso: 'SSO-konfigurasjon',
            api_keys: 'API-nøkler',
            service_accounts: 'Tjenestekontoer',
            ip_allowlist: 'IP-godkjenningsliste',
            delegations: 'Delegeringer',
            break_glass: 'Nødtilgang',
            session_settings: 'Sesjonsinnstillinger',
            active_sessions: 'Aktive sesjoner',
            access_reviews: 'Tilgangsgjennomganger',
            my_reviews: 'Mine gjennomganger',
            role_analytics: 'Rolleanalyse',
            attestation_schedules: 'Attesteringsplaner',
            sod_rules: 'SoD-regler',
            sod_violations: 'SoD-brudd',
        },
        module_config: {
            title: 'Modulkonfigurasjon',
            subtitle: 'Aktiver eller deaktiver funksjonsmoduler for din organisasjon. Deaktiverte moduler skjules fra navigasjonen.',
            enabled: 'Aktivert',
            disabled: 'Deaktivert',
            save: 'Lagre konfigurasjon',
            saving: 'Lagrer...',
            saved: 'Konfigurasjon lagret',
            nav_label: 'Modulkonfigurasjon',
            settings: 'Innstillinger',
            webhooks: 'Webhooker',
            domains: 'Domener',
            export_schedules: 'Eksportplaner',
            email_log: 'E-postlogg',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Filer',
            configuration: 'Konfigurasjon',
            browser: 'Filleser',
            dashboard: 'Dashboard for filer',
        },
        immutable_audit_trails: {
            nav_group_label: 'Revisjon og bevis',
            nav_settings: 'Innstillinger',
            nav_status: 'Bevisstatus',
            nav_integrity_center: 'Integritetssenter',
            nav_usage: 'Bruk og kreditter',
            settings_title: 'Uforanderlige revisjonsspor — Innstillinger',
            settings_subtitle: 'Per-org-innstillinger for Immutable Audit Trails-tillegget.',
            settings_banner: 'Tillegget Immutable Audit Trails er ikke aktivert for organisasjonen din. Aktiver den her for å begynne å forankre bevis for Tier-A og Tier-B revisjonshendelser.',
            status_title: 'Uforanderlige revisjonsspor – bevisstatus',
            status_subtitle: 'Per-hendelse bevis relasjoner på tvers av moduler.',
            status_banner: 'Tillegget Immutable Audit Trails er ikke aktivert for organisasjonen din. Gå til Innstillinger for å aktivere den.',
            integrity_center_title: 'Uforanderlige revisjonsspor — Integritetssenter',
            integrity_center_subtitle: 'Bekreft bevis og eksporter integritetspakker.',
            integrity_center_banner: 'Tillegget Immutable Audit Trails er ikke aktivert for organisasjonen din. Gå til Innstillinger for å aktivere det før du kjører verifisering eller eksporterer integritetspakker.',
            usage_title: 'Uforanderlige revisjonsspor – bruk og kreditter',
            usage_subtitle: 'Kredittsaldo, pakkekjøp og brukshistorikk.',
            usage_banner: 'Aktiver uforanderlige revisjonsspor i innstillinger for å begynne å forbruke beviskreditter.',
            module_tips_placeholder: 'Tips og veiledning for denne overflaten er tilgjengelig i brukerstøtten.',
            column_label: 'Bevis',
            no_proof_aria: 'Ingen bevis registrert',
            proof_status_receipt_label: 'kvittering {id}',
            in_progress_hint: 'Pågår — oppdateres automatisk',
            status_pills: {
                pending: 'Venter',
                submitted: 'Sendt inn',
                anchored: 'Forankret',
                confirmed: 'Bekreftet',
                retrying: 'Prøver på nytt',
                paused_cap: 'Satt på pause (cap)',
                failed: 'Mislyktes',
                failed_auth: 'Godkjenning mislyktes',
            },
            status_tooltips: {
                pending: 'Venter — i kø for prøvetrykktjenesten. Vil flytte til Sendt om kort tid; ingen handling nødvendig.',
                submitted: 'Sendt inn – arrangementet ditt ble sendt til bevistjenesten. For batch-modus venter den på å bli forseglet og forankret på kjettingen. Oppdaterer automatisk; ingen handling nødvendig.',
                anchored: 'Forankret — batchen er skrevet på kjeden og venter på endelig bekreftelse. Oppdaterer automatisk; ingen handling nødvendig.',
                confirmed: 'Bekreftet – beviset er forankret på kjeden og kan verifiseres.',
                retrying: 'Prøver på nytt — en forbigående feil oppstod. Avstemmingen prøver automatisk på nytt; ingen handling nødvendig.',
                paused_cap: 'Midlertidig stoppet – den månedlige kredittgrensen er nådd. Løs inn flere kreditter eller vent til neste faktureringsmåned.',
                failed: 'Mislyktes — forankring lyktes ikke. Avstemmingen vil automatisk prøve på nytt; kontakt support hvis det vedvarer.',
                failed_auth: 'Godkjenning mislyktes – bevistjenesten avviste legitimasjonen vår. Sjekk siden Innstillinger for uforanderlige revisjonsspor.',
            },
            mode_pills: {
                direct: 'Direkte',
                batch: 'Batch',
            },
            drawer: {
                title: 'Bevisdetaljer',
                close: 'Lukk',
                section_proof: 'Bevis',
                section_batch: 'Batch-medlemskap',
                status: 'Status',
                proof_reference: 'Bevisreferanse',
                receipt_id: 'Kvitterings-ID',
                taxonomy_class: 'Taksonomi klasse',
                units_consumed: 'Forbrukte enheter',
                created_at: 'Opprettet',
                verification_ref: 'Kjedeverifiseringsreferanse',
                chain: 'Kjede',
                tx_hash: 'Tx-hash',
                block_ref: 'Blokkref',
                batch_reference: 'Batchreferanse',
                leaf_index: 'Bladindeks',
                local_event_hash: 'Lokal event-hash',
                show_inclusion: 'Vis inkluderingsbane',
                hide_inclusion: 'Skjul inkluderingsvei',
                privacy_note: 'Nyttelast er hash; ingen personlige data forlot ComplianceOne for denne hendelsen.',
                verify_now: 'Bekreft nå',
                verify_pending: 'Verifisering pågår …',
                verify_verified: 'Verifisert - lokale + kjedesidesjekker bestått.',
                verify_issue_prefix: 'Bekreftelsesproblem: ',
                verify_failed: 'Bekreftelsesanrop mislyktes.',
                verify_result_prefix: 'Bekreftelsesdom:',
                verify_pending_note: 'Bekreftelse venter: dette beviset venter fortsatt på forankring på kjedet. Prøv igjen etter at partiet er forseglet.',
                awaiting_anchor_note: 'Dette beviset er en del av en åpen batch. Verifisering blir tilgjengelig når batchen er forseglet og forankret på kjeden – vanligvis innenfor organisasjonens konfigurerte batchvindu. Statusmerket oppdateres automatisk.',
                terminal_error_note: 'Dette beviset klarte ikke å forankre. Bevisstatusavstemmingen vil prøve automatisk på nytt; manuell verifisering har ingenting å sjekke ennå.',
            },
            integrity_center: {
                section_run: 'Kjør verifisering',
                section_progress: 'Kjør fremgang',
                section_issues: 'Problemer',
                period_start: 'Periodestart',
                period_end: 'Periodeslutt',
                module_filter: 'Moduler (kommaseparert, valgfritt)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Kjør verifisering',
                export_button: 'Eksporter integritetspakke',
                export_success: 'Integrity Pack eksportert.',
                export_failed: 'Eksporten mislyktes.',
                empty_disabled: 'Aktiver uforanderlige revisjonsspor i Innstillinger for å kjøre verifiseringspass.',
                error_period_required: 'Velg en start- og sluttdato.',
                error_start_run: 'Kunne ikke starte kjøringen.',
                processed: 'Behandlet: {processed} av {total}',
                col_verdict: 'Dom',
                col_count: 'Telle',
                col_proof_link: 'Bevis link',
                col_module: 'Modul',
                col_event_type: 'Hendelsestype',
                col_expected: 'Forventet hasj',
                col_recomputed: 'Reberegnet hash',
                col_raised_at: 'Oppvokst kl',
                any_verdict: 'Enhver dom',
                no_issues: 'Ingen problemer funnet.',
            },
            settings_page: {
                load_error: 'Kunne ikke laste inn innstillingene.',
                save: 'Lagre',
                save_success: 'Innstillinger lagret.',
                save_error: 'Lagring mislyktes.',
                discard: 'Forkast endringer',
                read_only_banner: 'Du har skrivebeskyttet tilgang. Kontakt en plattformadministrator for å gjøre endringer.',
                section_status: 'Status og aktivering',
                enabled_label: 'Tillegg aktivert',
                enabled_help: 'Når den er aktivert, blir hver revisjonshendelse på nivå-A og nivå-B hashet og forankret.',
                section_mode: 'Standard prøvemodus',
                mode_direct: 'Direkte (ett anker per hendelse)',
                mode_batch: 'Batch (Merkle-root anker per gruppe)',
                mode_hybrid: 'Hybrid (per klasse registerstandard)',
                batch_window_label: 'Batch-vindu (minutter, 1–60)',
                section_taxonomy: 'Beskyttet hendelsestaksonomi',
                taxonomy_help: 'Overstyringer per hendelsesklasse. Nivå A → direkte modus, Nivå B → batchmodus, Av → ikke korrektur.',
                taxonomy_search: 'Søk overstyrer',
                taxonomy_reset: 'Tilbakestill til standardinnstillinger',
                taxonomy_event_class: 'Eventklasse',
                taxonomy_override: 'Overstyr',
                taxonomy_actions: 'Handlinger',
                taxonomy_clear: 'Klart',
                taxonomy_no_overrides: 'Ingen overstyringer – bruker registerstandarder.',
                taxonomy_add_class: 'event_class.kode',
                taxonomy_add: 'Legg til overstyring',
                section_caps: 'Caps & limits',
                cap_label: 'Månedlig tak (enheter; blank = ubegrenset)',
                warn_label: 'Advarselsterskel (enheter; må være mindre enn taket)',
                auto_pause_label: 'Sett nye prøvetrykk automatisk på pause når grensen er nådd',
                section_credentials: 'Legitimasjon',
                credential_id: 'Legitimasjons-ID',
                credential_rotated_at: 'Sist rotert',
                rotate_button: 'Roter legitimasjon',
                rotate_success: 'Legitimasjon rotert. Gammel legitimasjon gyldig i {n} minutter til.',
                rotate_failed: 'Påloggingsrotasjon mislyktes.',
                rotate_modal_title: 'Vil du rotere AesirX legitimasjon?',
                rotate_modal_body: 'Rotasjon utsteder en ny legitimasjon. Den gamle legitimasjonen forblir akseptert av AesirX i et 60-minutters frist slik at arbeidstakere kan fullføre sine nåværende jobber. Etter 60 minutter er den gamle legitimasjonen tilbakekalt.',
                rotate_modal_confirm: 'Roter nå',
                tier_a_off_modal_title: 'Vil du deaktivere korrektur for en hendelsesklasse?',
                tier_a_off_modal_body: 'Hvis du setter en hendelsesklasse til «Av», stopper prøvetrykket fra å forankre disse hendelsene. Arkitektur §6 forklarer den regulatoriske implikasjonen: Tier-A-hendelser er regulatorrelevante; Hvis du slår dem av, kan det bryte forsvarligheten av revisjonssporet.',
                tier_a_off_modal_confirm: 'Jeg forstår - bruk overstyring',
                error_warn_lt_cap: 'Advarselsterskelen må være mindre enn den månedlige grensen.',
                error_window_range: 'Batch-vinduet må være mellom 1 og 60 minutter.',
                error_run_in_progress: 'En verifiseringskjøring pågår; kan ikke deaktivere tillegget akkurat nå.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Bevismodus',
                filter_module: 'Modul',
                filter_source_collection: 'Kildesamling',
                filter_from_date: 'Fra',
                filter_to_date: 'Til',
                filter_search: 'Søk',
                any_status: 'Enhver status',
                any_mode: 'Enhver modus',
                col_module: 'Modul',
                col_event_type: 'Hendelsestype',
                col_proof_status: 'Status',
                col_proof_mode: 'Modus',
                col_created_at: 'Opprettet',
                col_units: 'Enheter',
                empty_disabled: 'Tillegget Immutable Audit Trails er ikke aktivert for organisasjonen din. Gå til Innstillinger for å aktivere den.',
                empty_enabled_no_rows: 'Ingen beviskoblinger samsvarer med gjeldende filtre.',
                export_csv: 'Eksporter CSV',
                export_xlsx: 'Eksporter XLSX',
                export_success: 'Eksporten er fullført',
                export_failed: 'Eksporten mislyktes',
            },
            usage_page: {
                section_alerts: 'Varsler',
                alert_dismiss: 'Avvis',
                alert_dismiss_aria: 'Avvis varsel {id}',
                alert_dismissed: 'Varsel avvist',
                alert_dismiss_failed: 'Kunne ikke avvise varselet',
                section_balance: 'Kredittsaldo',
                balance_label: 'Tilgjengelige studiepoeng',
                balance_hint: 'Gjenstående enheter (1 enhet = 1 beskyttet hendelsesbevis).',
                section_current_month: 'Gjeldende måned',
                consumed_label: 'Forbrukt',
                granted_label: 'Gitt',
                refunded_label: 'Refundert',
                net_label: 'Netto',
                section_history: 'Brukshistorikk',
                history_kind: 'Snill',
                history_units: 'Enheter',
                history_balance_after: 'Balanse etter',
                history_notes: 'Notater',
                history_at: 'Når',
                cap_warning_banner: 'Du har passert advarselsterskelen for denne faktureringsmåneden. Vurder å løse inn ekstra kreditter.',
                cap_paused_banner: 'Månedlig tak nådd. Bevisinnleveringer settes på pause til neste måned eller til grensen økes.',
                section_redeem: 'Løs inn kredittpakke',
                redeem_purchase_id: 'Fakturanummer',
                redeem_package: 'Pakke',
                redeem_button: 'Løs inn',
                redeem_pending: 'Løser inn …',
                redeem_success: 'Kreditter innløst',
                redeem_failed: 'Innløsning mislyktes',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'enheter',
                alert_dismissing: 'Avviser …',
                alert_kind: {
                    cap_reached: 'Månedlig tak nådd',
                    low_balance: 'Lav balanse',
                    auth_failure: 'Autentiseringsfeil',
                    merkle_mismatch: 'Merkle misforhold',
                    batch_terminal_failure: 'Batch-terminalfeil',
                    direct_terminal_failure: 'Feil på direkte innsendingsterminal',
                },
                usage_kind: {
                    consume: 'Konsumere',
                    refund: 'Refusjon',
                    grant: 'Grant',
                    adjustment: 'Justering',
                    reservation: 'Reservasjon',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Starter',
                package_growth: 'Vekst',
                package_enterprise: 'Enterprise',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Kjøp flere kreditter',
                    purchase_title: 'Kjøp flere kreditter',
                    purchase_subtitle: 'Velg en pakke. Du blir sendt til sikker Stripe-utsjekk, og fakturaen din kommer på e-post.',
                    checkout_button: 'Fortsett til utsjekk',
                    checkout_pending: 'Åpner utsjekk…',
                    cancel_button: 'Avbryt',
                    purchase_error: 'Kunne ikke starte utsjekk. Prøv igjen om litt.',
                    redeem_subtitle: 'Allerede betalt? Velg pakken du kjøpte, og skriv inn faktura-ID-en fra e-posten din for å legge til kredittene.',
                    error_not_found: 'Ingen faktura ble funnet med det nummeret. Kontroller fakturanummeret og prøv igjen.',
                    error_not_paid: 'Denne fakturaen er ennå ikke merket som betalt.',
                    error_org_mismatch: 'Denne fakturaen ble ikke utstedt til organisasjonen din.',
                    error_package_mismatch: 'Denne fakturaen samsvarer ikke med den valgte pakken.',
                    error_billing_unavailable: 'Kunne ikke nå faktureringstjenesten. Prøv igjen om litt.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Filkonfigurasjon',
            subtitle: 'Konfigurer filopplastingsgrenser, oppbevaringspolicyer og per-eier-type overstyringer for plattformvedleggsoverflaten.',
            section_global: 'Globale standardinnstillinger',
            section_global_help: 'Disse grensene gjelder for hver fil som lastes opp av brukere i organisasjonen din, med mindre en per-eier-type overstyring nedenfor endrer dem.',
            section_overrides: 'Per-eier-type overstyringer',
            section_overrides_help: 'Overstyr de globale grensene for en bestemt eiertype. Verdier per eiertype vinner alltid over den globale innstillingen.',
            max_file_size_label: 'Maks filstørrelse',
            max_file_size_hint: 'Per individuell opplasting (MiB)',
            max_total_attachments_label: 'Maks totalt vedlegg',
            max_total_attachments_hint: 'På tvers av hele organisasjonen',
            max_total_size_label: 'Maks total lagringsplass',
            max_total_size_hint: 'På tvers av hele organisasjonen (MiB)',
            allowed_mime_types_label: 'Tillatte filtyper',
            allowed_mime_types_placeholder: 'Velg MIME-typer',
            retention_days_label: 'Oppbevaringsperiode (dager)',
            retention_days_hint: 'Hvor lenge filer må oppbevares før de er kvalifisert for sletting. La stå tomt for ingen oppbevaringspolicy.',
            legal_hold_label: 'Lovlig hold',
            legal_hold_hint: 'Når den er aktivert, krever hard sletting av en fil av denne eiertypen tillatelse for overstyring av juridisk hold og eksplisitt operatørvalg.',
            save: 'Lagre endringer',
            saving: 'Lagrer …',
            reset: 'Tilbakestill',
            save_success: 'Innstillinger lagret',
            empty_state_title: 'Bruker plattformstandarder',
            empty_state_body: 'Organisasjonen din kjører for øyeblikket på plattformens standard filpolicy. Rediger en hvilken som helst verdi nedenfor for å opprette en overstyring på organisasjonsnivå.',
            read_only_banner: 'Du har skrivebeskyttet tilgang til filkonfigurasjon. Kontakt en plattformadministrator for å gjøre endringer.',
            owner_type_default_badge: 'Standard',
            owner_type_overridden_badge: 'Overstyrt',
            owner_type_label: 'Eiertype',
            owner_type_collapse: 'Kollaps',
            owner_type_expand: 'Utvid',
            override_save: 'Lagre overstyring',
            override_remove: 'Fjern overstyring',
            mime_labels: {
                'application/pdf': 'PDF dokument',
                'image/png': 'PNG-bilde',
                'image/jpeg': 'JPEG-bilde',
                'image/jpg': 'JPG-bilde',
                'application/msword': 'Word-dokument (legacy)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word-dokument',
                'application/vnd.ms-excel': 'Excel-regneark (legacy)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel-regneark',
                'text/csv': 'CSV fil',
                'text/plain': 'Ren tekst',
                'message/rfc822': 'E-postmelding',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Filleser',
            subtitle: 'Søk, filtrer og administrer hvert vedlegg som lastes opp på tvers av organisasjonen.',
            filter_owner_type: 'Eiertype',
            filter_uploader: 'Lastet opp av',
            filter_from: 'Lastet opp etter',
            filter_to: 'Lastet opp før',
            filter_size_min: 'Min størrelse (MiB)',
            filter_size_max: 'Maks størrelse (MiB)',
            filter_mime: 'Filtype',
            filter_deleted_state: 'Slettetilstand',
            filter_deleted_any: 'Alle filer',
            filter_deleted_only: 'Kun slettet',
            filter_deleted_exclude: 'Ekskluder slettet',
            filter_orphaned: 'Bare foreldreløse',
            filter_search_placeholder: 'Søk filnavn …',
            filter_apply: 'Bruk filtre',
            filter_clear: 'Tøm filtre',
            col_filename: 'Filnavn',
            col_owner_type: 'Eiertype',
            col_owner_entity: 'Eier',
            col_uploader: 'Lastet opp av',
            col_uploaded_at: 'Lastet opp',
            col_size: 'Størrelse',
            col_mime: 'Type',
            col_state: 'stat',
            col_actions: 'Handlinger',
            state_active: 'Aktiv',
            state_deleted: 'Slettet',
            state_orphaned: 'Foreldreløs',
            action_view_detail: 'Se detaljer',
            action_download: 'Last ned',
            action_ai_edits: 'AI Endringer',
            action_soft_delete: 'Myk sletting',
            action_restore: 'Gjenopprett',
            action_hard_delete: 'Vanskelig sletting',
            bulk_soft_delete: 'Myk sletting er valgt',
            bulk_export_csv: 'Eksporter CSV',
            bulk_select_all: 'Velg alle',
            reason_label: 'Grunn',
            reason_min_10_chars: 'Årsak kreves (minst 10 tegn)',
            reason_min_20_chars: 'Årsak kreves (minst 20 tegn)',
            legal_hold_override_label: 'Overstyr juridisk hold',
            legal_hold_override_warning: 'Denne filen er under juridisk oppbevaring. Hard-sletting vil bli registrert i revisjonssporet med ditt grunn- og overstyringsflagg.',
            empty_state_title: 'Ingen filer samsvarer med filtrene dine',
            empty_state_body: 'Juster filtrene for å se resultater, eller tøm dem for å se alle filene.',
            pagination_prev: 'Forrige',
            pagination_next: 'Neste',
            pagination_page: 'Side {page} av {total}',
            pagination_size_label: 'Per side',
            pagination_total: 'Totalt: {count}',
            toast_soft_deleted: 'Filen ble mykt slettet',
            toast_restored: 'Filen gjenopprettet',
            toast_hard_deleted: 'Fil slettet permanent',
            toast_bulk_deleted: '{count} fil(er) slettet',
            toast_error: 'Operasjonen mislyktes',
            confirm_soft_delete_title: 'Myk slette fil',
            confirm_restore_title: 'Gjenopprett fil',
            confirm_hard_delete_title: 'Slett filen permanent',
            confirm_bulk_soft_delete_title: 'Myk sletting av valgte filer',
            confirm_submit: 'Bekreft',
            confirm_cancel: 'Avbryt',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Fildetaljer',
            close: 'Lukk',
            meta_filename: 'Filnavn',
            meta_size: 'Størrelse',
            meta_mime: 'Type',
            meta_owner_type: 'Eiertype',
            meta_owner_entity: 'Eier enhet',
            meta_uploader: 'Lastet opp av',
            meta_uploaded_at: 'Lastet opp',
            meta_storage_path: 'Lagringsvei',
            meta_deleted_state: 'Slettetilstand',
            audit_section_title: 'Revisjonsspor',
            audit_load_more: 'Last inn flere hendelser',
            audit_empty: 'Ingen revisjonshendelser registrert for denne filen.',
            audit_config_changed: 'Konfigurasjonen endret',
            audit_soft_deleted: 'Myk-slettet',
            audit_restored: 'Gjenopprettet',
            audit_hard_deleted: 'Slettes permanent',
            audit_downloaded: 'Lastet ned',
            audit_quota_breached: 'Kvotebrudd forsøkt',
            audit_owner_type_download_denied: 'Henting avvist',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} av {actor} den {at}',
            action_download: 'Last ned',
            action_soft_delete: 'Myk sletting',
            action_restore: 'Gjenopprett',
            action_hard_delete: 'Vanskelig sletting',
            effective_settings_title: 'Effektiv politikk',
            effective_max_file_size: 'Maks filstørrelse',
            effective_retention_days: 'Oppbevaring',
            effective_legal_hold: 'Lovlig hold',
            effective_legal_hold_yes: 'Aktiv',
            effective_legal_hold_no: 'Ingen',
        },
        files_dashboard: {
            title: 'Dashboard for filer',
            subtitle: 'Lagringsbruk, vekst og foreldreløse filer på tvers av organisasjonen.',
            kpi_total_files: 'Totalt antall filer',
            kpi_total_size: 'Total lagring',
            kpi_near_quota: 'Kvotebruk',
            kpi_orphaned: 'Foreldreløse filer',
            chart_by_owner_type: 'Filer etter eiertype',
            chart_growth_30d: '30-dagers vekst',
            toggle_count: 'Etter telling',
            toggle_size: 'Etter størrelse',
            orphaned_panel_title: 'Foreldreløse filer (topp 20)',
            orphaned_view_all: 'Se alle foreldreløse filer',
            orphaned_empty: 'Ingen foreldreløse filer funnet.',
            growth_empty: 'Ingen opplastingsaktivitet de siste 30 dagene.',
            retention_n_days: '{days} dag(er)',
            retention_none: 'Ingen politikk',
        },
    },
    category_labels: {
        'user-guide': 'Brukerveiledning',
        'admin-guide': 'Administrasjonsveiledning',
        'getting-started': 'Komme i gang',
        faq: 'FAQ',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Taster',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Formål',
        preferences: 'Preferanser',
        onboarding: 'Onboarding',
        'first-steps': 'Første trinn',
        login: 'Logg inn',
        navigation: 'Navigasjon',
        users: 'Brukere',
        user: 'Bruker',
        roles: 'Roller',
        role: 'Rolle',
        'access-control': 'Tilgangskontroll',
        'access-management': 'Tilgangsadministrasjon',
        permissions: 'Tillatelser',
        permission: 'Tillatelse',
        consent: 'Samtykke',
        'consent-management': 'Samtykkebehandling',
        privacy: 'Personvern',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Revisjon',
        auditing: 'Revisjon',
        breach: 'Brudd',
        incident: 'Hendelse',
        incidents: 'Hendelser',
        notification: 'Varsling',
        notifications: 'Varsler',
        reporting: 'Rapportering',
        risk: 'Risiko',
        vendor: 'Leverandør',
        vendors: 'Leverandører',
        'vendor-risk': 'Leverandørrisiko',
        assessment: 'Vurdering',
        assessments: 'Vurderinger',
        dpia: 'DPIA',
        'cross-border': 'Grenseoverskridende',
        transfer: 'Overføring',
        transfers: 'Overføringer',
        deletion: 'Sletting',
        retention: 'Oppbevaring',
        'data-mapping': 'Datakartlegging',
        discovery: 'Oppdagelse',
        classification: 'Klassifisering',
        workflow: 'Arbeidsflyt',
        workflows: 'Arbeidsflyter',
        template: 'Mal',
        templates: 'Maler',
        catalog: 'Katalog',
        monitoring: 'Overvåking',
        tasks: 'Oppgaver',
        task: 'Oppgave',
        'task-management': 'Oppgavestyring',
        organizations: 'Organisasjoner',
        org: 'Organisasjon',
        governance: 'Styring',
        compliance: 'Overholdelse',
        forms: 'Skjemaer',
        form: 'Skjema',
        connectors: 'Koblinger',
        connector: 'Kobling',
        integration: 'Integrasjon',
        integrations: 'Integrasjoner',
        security: 'Sikkerhet',
        encryption: 'Kryptering',
        backup: 'Sikkerhetskopiering',
        recovery: 'Gjenoppretting',
        maintenance: 'Vedlikehold',
        settings: 'Innstillinger',
        configuration: 'Konfigurasjon',
        sla: 'SLA',
        escalation: 'Eskalering',
        remediation: 'Utbedring',
        evidence: 'Bevis',
        'case-management': 'Saksbehandling',
        profile: 'Profil',
        delegation: 'Delegering',
        'service-accounts': 'Tjenestekontoer',
        license: 'Lisens',
        'license-management': 'Lisensbehandling',
        plan: 'Planlegg',
        plans: 'Planer',
        dashboard: 'Dashbord',
        'web-consent': 'Websamtykke',
        'partner-marketplace': 'Partner Marketplace',
        partner: 'Partner',
        partners: 'Partnere',
        deliverables: 'Leveranser',
        'service-tiers': 'Tjenestenivåer',
        'support-tickets': 'Støttebilletter',
        'audit-trail': 'Revisjonssti',
        'first-time-setup': 'Førstegangsoppsett',
        setup: 'Oppsett',
        quickstart: 'Rask start',
        tour: 'Omvisning',
        tours: 'Turer',
        'sample-data': 'Eksempeldata',
        'data-subject': 'Datasubjekt',
        'data-subjects': 'Datasubjekter',
        pseudonymous: 'Pseudonym',
        identified: 'Identifisert',
        'help-center': 'Hjelpesenter',
        article: 'Artikkel',
        articles: 'Artikler',
        search: 'Søk',
        'platform-settings': 'Plattforminnstillinger',
        'platform-config': 'Plattformkonfigurasjon',
        'module-config': 'Modulkonfigurasjon',
        'module-configuration': 'Modulkonfigurasjon',
        'rights-requests': 'Rettighetsforespørsler',
        'access-accountability': 'Få tilgang til ansvarlighet',
        'data-discovery': 'Dataoppdagelse',
        'incident-ops': 'Hendelsesoperasjoner',
        'deletion-orchestration': 'Sletting Orkestrering',
        'compliance-forms': 'Overholdelsesskjemaer',
        'program-governance': 'Programstyring',
        localization: 'Lokalisering',
        'privacy-scanner': 'Personvernskanner',
        'consent-properties': 'Samtykkeegenskaper',
        acceptances: 'Aksepter',
        access: 'Tilgang',
        'access-review': 'Tilgang gjennomgang',
        'access-reviews': 'Få tilgang til anmeldelser',
        accountability: 'Ansvarlighet',
        acknowledgment: 'Anerkjennelse',
        'action-plans': 'Handlingsplaner',
        activation: 'Aktivering',
        activations: 'Aktiveringer',
        'active-response': 'Aktiv respons',
        activity: 'Aktivitet',
        adequacy: 'Tilstrekkelighet',
        'admin-guide': 'Administrasjonsveiledning',
        aggregation: 'Aggregasjon',
        agreements: 'Avtaler',
        ai: 'AI',
        alerts: 'Varsler',
        analytics: 'Analytics',
        anchoring: 'Forankring',
        approval: 'Godkjenning',
        approvals: 'Godkjenninger',
        apps: 'Apper',
        assets: 'Eiendeler',
        assignments: 'Oppdrag',
        attestation: 'Attestasjon',
        'audit-log': 'Revisjonslogg',
        'audit-pack': 'Revisjonspakke',
        authentication: 'Autentisering',
        authority: 'Autoritet',
        'auto-play': 'Automatisk avspilling',
        automated: 'Automatisert',
        automation: 'Automatisering',
        baseline: 'Grunnlinje',
        batches: 'Batcher',
        bidirectional: 'Toveis',
        bilingual: 'Tospråklig',
        bindings: 'Bindinger',
        blockchain: 'Blockchain',
        board: 'Styre',
        brand: 'Merke',
        brds: 'BRD-er',
        'break-glass': 'Break-Glass',
        builder: 'Byggmester',
        bulk: 'Bulk',
        'bulk-sync': 'Bulk Sync',
        bundles: 'Bunter',
        calendar: 'Kalender',
        campaigns: 'Kampanjer',
        cases: 'Saker',
        categories: 'Kategorier',
        category: 'Kategori',
        certification: 'Sertifisering',
        chain: 'Kjede',
        change: 'Endre',
        'change-awareness': 'Endre bevissthet',
        'change-log': 'Endre logg',
        'change-management': 'Endringsledelse',
        'change-requests': 'Endringsforespørsler',
        'cmp-validation': 'CMP Validering',
        collection: 'Samling',
        comparison: 'Sammenligning',
        completeness: 'Fullstendighet',
        completion: 'Fullføring',
        'compliance-posture': 'Overholdelsesstilling',
        'compliance-proof': 'Samsvarsbevis',
        'compliance-reviews': 'Overholdelsesanmeldelser',
        config: 'Konfig',
        confirmation: 'Bekreftelse',
        conflicts: 'Konflikter',
        consolidation: 'Konsolidering',
        continuous: 'Kontinuerlig',
        contracts: 'Kontrakter',
        controls: 'Kontroller',
        cookies: 'Informasjonskapsler',
        cooperation: 'Samarbeid',
        coordination: 'Koordinering',
        'corporate-governance': 'Corporate Governance',
        'corrective-actions': 'Korrigerende handlinger',
        coverage: 'Dekning',
        'credential-vault': 'Credential Vault',
        credentials: 'Legitimasjon',
        cycles: 'Sykluser',
        dashboards: 'Dashboards',
        'data-assets': 'Dataressurser',
        'data-flows': 'Dataflyter',
        'data-sensitivity': 'Datasensitivitet',
        'data-steward': 'Dataansvarlig',
        deadlines: 'Tidsfrister',
        'decree-356': 'Dekret 356',
        dedup: 'Dedup',
        definitions: 'Definisjoner',
        deliverable: 'Leveres',
        departments: 'Avdelinger',
        'dependency-order': 'Avhengighetsrekkefølge',
        deployment: 'Utplassering',
        detection: 'Deteksjon',
        diff: 'Forskj',
        'digital-properties': 'Digitale egenskaper',
        dimensions: 'Dimensjoner',
        disable: 'Deaktiver',
        'disaster-recovery': 'Katastrofegjenoppretting',
        disclosure: 'Avsløring',
        documentation: 'Dokumentasjon',
        documents: 'Dokumenter',
        domains: 'Domener',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Driftsdeteksjon',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Nødsituasjon',
        enable: 'Aktiver',
        'enterprise-map': 'Bedriftskart',
        erasure: 'Sletting',
        evaluation: 'Evaluering',
        'event-bus': 'Eventbuss',
        exceptions: 'Unntak',
        execution: 'Utførelse',
        executive: 'Executive',
        exercises: 'Øvelser',
        export: 'Eksporter',
        exports: 'Eksport',
        filing: 'Arkivering',
        filter: 'Filter',
        filtering: 'Filtrering',
        findings: 'Funn',
        flows: 'Strømmer',
        'follow-up': 'Oppfølging',
        forensic: 'Rettsmedisinsk',
        forensics: 'Forensics',
        'form-filler': 'Skjemautfyller',
        framework: 'Rammeverk',
        frameworks: 'Rammer',
        'gap-analysis': 'Gap analyse',
        'gap-report': 'Gap-rapport',
        gaps: 'Gap',
        gates: 'Porter',
        'getting-started': 'Komme i gang',
        'golden-templates': 'Gylne maler',
        grc: 'GRC',
        'group-management': 'Konsernledelse',
        groups: 'Grupper',
        'guided-tours': 'Guidede turer',
        guides: 'Guider',
        handling: 'Håndtering',
        'hands-on': 'Hands-On',
        health: 'Helse',
        help: 'Hjelp',
        hierarchy: 'Hierarki',
        history: 'Historie',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identitetsleverandør',
        implementation: 'Implementering',
        import: 'Importer',
        improvement: 'Forbedring',
        'incident-response': 'Hendelsesrespons',
        inconsistencies: 'Inkonsekvenser',
        instances: 'Forekomster',
        intake: 'Inntak',
        'intake-key': 'Inntaksnøkkel',
        interactive: 'Interaktiv',
        international: 'Internasjonal',
        inventory: 'Inventar',
        investigation: 'Etterforskning',
        investigations: 'Undersøkelser',
        'ip-allowlist': 'IP-tillatelsesliste',
        issues: 'Problemer',
        justification: 'Begrunnelse',
        justifications: 'Begrunnelser',
        kanban: 'Kanban',
        keyboard: 'Tastatur',
        keys: 'Nøkler',
        'knowledge-base': 'Kunnskapsbase',
        koncernstyring: 'Corporate Governance',
        kpi: 'KPI',
        labeling: 'Merking',
        language: 'Språk',
        'language-packs': 'Språkpakker',
        languages: 'Språk',
        lawfulness: 'Lovlighet',
        layout: 'Oppsett',
        'legal-basis': 'Juridisk grunnlag',
        'lessons-learned': 'Lærdom',
        levels: 'Nivåer',
        library: 'Bibliotek',
        lifecycle: 'Livssyklus',
        lineage: 'Avstamning',
        links: 'Linker',
        listings: 'Oppføringer',
        live: 'Live',
        locale: 'Språk',
        log: 'Log',
        logging: 'Logging',
        'managed-service': 'Administrert tjeneste',
        mapping: 'Kartlegging',
        mappings: 'Kartlegginger',
        'market-packs': 'Markedspakker',
        marketplace: 'Markedsplass',
        maturity: 'Modenhet',
        metrics: 'Beregninger',
        models: 'Modeller',
        module: 'Modul',
        modules: 'Moduler',
        mps: 'MPS',
        'multi-factor': 'Multifaktor',
        'multi-market': 'Multimarked',
        'multi-org': 'Multi-Org',
        'multi-property': 'Multi-Property',
        'my-tasks': 'Mine oppgaver',
        obligations: 'Forpliktelser',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orkestrering',
        'org-groups': 'Organisasjonsgrupper',
        organization: 'Organisasjon',
        overdue: 'Forfalt',
        overview: 'Oversikt',
        'owner-review': 'Eieranmeldelse',
        package: 'Pakke',
        packages: 'Pakker',
        packs: 'Pakker',
        pagination: 'Paginering',
        'parent-child': 'Foreldre-barn',
        patterns: 'Mønstre',
        performance: 'Ytelse',
        periodic: 'Periodisk',
        periods: 'Perioder',
        personal: 'Personlig',
        'personal-data': 'Personopplysninger',
        personas: 'Personas',
        planning: 'Planlegging',
        platform: 'Plattform',
        playbooks: 'Playbooks',
        poc: 'PoC',
        'point-in-time': 'Tidspunkt',
        policies: 'Retningslinjer',
        portfolio: 'Portefølje',
        'post-incident': 'Post-hendelse',
        'power-user': 'Power User',
        practice: 'Øv',
        pricing: 'Prissetting',
        'privacy-impact': 'Innvirkning på personvernet',
        procedures: 'Prosedyrer',
        processes: 'Prosesser',
        processing: 'Behandling',
        'processing-activities': 'Behandlingsaktiviteter',
        procurement: 'Innkjøp',
        productivity: 'Produktivitet',
        profiles: 'Profiler',
        programs: 'Programmer',
        progress: 'Fremgang',
        proof: 'Bevis',
        propagation: 'Formering',
        properties: 'Egenskaper',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Offentlig form',
        quality: 'Kvalitet',
        questionnaires: 'Spørreskjemaer',
        'quick-links': 'Hurtigkoblinger',
        'quick-start': 'Rask start',
        'quick-starts': 'Rask start',
        raci: 'RACI',
        readiness: 'Beredskap',
        'real-time': 'Sanntid',
        reassessments: 'Revurderinger',
        reconciliation: 'Forsoning',
        record: 'Ta opp',
        records: 'Rekorder',
        recurring: 'Tilbakevendende',
        register: 'Registrer deg',
        registry: 'Register',
        regulations: 'Forskrifter',
        regulator: 'Regulator',
        regulatory: 'Regulatorisk',
        'regulatory-frameworks': 'Regulatoriske rammer',
        remap: 'Omkart',
        reminders: 'Påminnelser',
        reports: 'Rapporter',
        requests: 'Forespørsler',
        requirements: 'Krav',
        response: 'Svar',
        'response-plans': 'Responsplaner',
        'response-team': 'Responsteam',
        'response-templates': 'Svarmaler',
        'response-time': 'Responstid',
        responses: 'Svar',
        responsibility: 'Ansvar',
        results: 'Resultater',
        review: 'Gjennomgå',
        reviews: 'Anmeldelser',
        'right-to-delete': 'Rett til å slette',
        rights: 'Rettigheter',
        'risk-tiers': 'Risikonivåer',
        rollouts: 'Utrullinger',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Ruting',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Regler',
        runs: 'Løper',
        saml: 'SAML',
        scan: 'Skann',
        scanning: 'Skanning',
        scc: 'SCC',
        schedule: 'Tidsplan',
        scheduled: 'Planlagt',
        schedules: 'Tidsplaner',
        scheduling: 'Planlegging',
        scores: 'Scorer',
        scoring: 'Scoring',
        scripts: 'Skript',
        'security-reviews': 'Sikkerhetsvurderinger',
        'sensitive-data': 'Sensitive data',
        sensitivity: 'Følsomhet',
        'separation-of-duties': 'Separasjon av oppgaver',
        service: 'Service',
        sessions: 'Økter',
        'setup-wizard': 'Oppsettveiviser',
        severity: 'Alvorlighetsgrad',
        'shared-governance': 'Delt styring',
        'shared-settings': 'Delte innstillinger',
        shortcuts: 'Snarveier',
        sidebar: 'Sidefelt',
        signals: 'Signaler',
        'single-sign-on': 'Enkel pålogging',
        snapshots: 'Øyeblikksbilder',
        sod: 'SoD',
        sorting: 'Sortering',
        sources: 'Kilder',
        staging: 'Iscenesettelse',
        standards: 'Standarder',
        status: 'Status',
        structure: 'Struktur',
        'sub-processor': 'Underprosessor',
        'sub-processors': 'Underprosessorer',
        submissions: 'Innleveringer',
        subprocessors: 'Underbehandlere',
        subscription: 'Abonnement',
        suggestions: 'Forslag',
        summaries: 'Sammendrag',
        supplements: 'Kosttilskudd',
        support: 'Støtte',
        sync: 'Synkroniser',
        system: 'System',
        systems: 'Systemer',
        tables: 'Tabeller',
        tagging: 'Tagging',
        'tamper-proof': 'Inngrepssikker',
        taxonomies: 'Taksonomier',
        taxonomy: 'Taksonomi',
        tenants: 'Leietakere',
        termination: 'Oppsigelse',
        'third-party': 'Tredjepart',
        'ticket-sync': 'Billettsynkronisering',
        tickets: 'Billetter',
        tiers: 'Nivåer',
        tokens: 'Tokens',
        totp: 'TOTP',
        traceability: 'Sporbarhet',
        tracking: 'Sporing',
        trail: 'Sti',
        training: 'Trening',
        translation: 'Oversettelse',
        translations: 'Oversettelser',
        transparency: 'Åpenhet',
        trends: 'Trender',
        unified: 'Samlet',
        updates: 'Oppdateringer',
        validation: 'Validering',
        vault: 'Hvelv',
        'vendor-requests': 'Leverandørforespørsler',
        verification: 'Verifikasjon',
        versioning: 'Versjonskontroll',
        versions: 'Versjoner',
        vietnam: 'Vietnam',
        violations: 'Krenkelser',
        visualization: 'Visualisering',
        waivers: 'Fraskrivelser',
        'web-records': 'Nettposter',
        website: 'Nettsted',
        websites: 'Nettsteder',
        weights: 'Vekter',
        'white-label': 'White-Label',
        withdrawal: 'Uttak',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Innsynsbegjæringer',
        data_mapping: 'Datakartlegging',
        governance: 'Styring',
        consent: 'Samtykkestyring',
        consent_properties: 'Samtykkeegenskaper',
        assessments: 'DPIA og vurderinger',
        classification: 'Dataklassifisering',
        monitoring: 'Overvåkingsprogrammer',
        program: 'Overvåkingsprogram',
        incidents: 'Hendelseshåndtering',
        connectors: 'Konnektorer',
        vendor_risk: 'Leverandørrisiko',
        deletion_orchestration: 'Sletteorkestrering',
        discovery: 'Dataoppdagelse',
        incident_ops: 'Hendelsesdrift',
        program_governance: 'Programstyring',
        access_accountability: 'Tilgangsansvar',
        localization: 'Lokalisering',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Partnerarbeidsområde',
        privacy_scanner: 'Personvernskanner',
        onboarding: 'Onboarding',
        compliance_forms: 'Samsvarsskjemaer',
        audit_trail: 'Revisjonslogg',
        data_subjects: 'Registrerte',
        organizations: 'Organisasjoner',
        task_management: 'Oppgavestyring',
        deletion_orch: 'Sletteorkestrering',
        forms: 'Samsvarskjemaer',
        partner_marketplace: 'Partnermarkedsplass',
        dashboard: 'Oversikt',
        platform_settings: 'Plattforminnstillinger',
        help_center: 'Hjelpesenter',
    },
    module_descriptions: {
        rights_requests: 'Administrer forespørsler om registrertes rettigheter, inkludert innsyn, sletting og dataportabilitet. Spor forespørselens livssyklus, tildel saksbehandlere og overhold regulatoriske frister.',
        data_mapping: 'Kartlegg dataflyter på tvers av organisasjonen. Spor behandlingsaktiviteter, datakategorier, formål, oppbevaringspolicyer og systemintegrasjoner.',
        governance: 'Tilsyn og dokumentasjonen bak det. Administrer leverandørregisteret og livssyklusen til det, godkjenn beslutningene denne modulen styrer, følg med på etterlevelsesavvik, og før registrene over tillit og identitet, regulatoriske artefakter, betalinger og eKYC samt plattformdokumentasjon.',
        consent: 'Administrer innhenting, lagring og tilbaketrekking av samtykke. Spor samtykkeregistreringer, formål og integrasjon med samtykkeplattformer.',
        consent_properties: 'Konfigurer samtykkeegenskaper, grupper og gylne maler. Definer gjenbrukbare samtykkekonfigurasjoner for konsistent utrulling.',
        assessments: 'Gjennomfør personvernkonsekvensvurderinger (DPIA) og risikovurderinger for personvern. Evaluer behandlingsaktiviteter for etterlevelsesrisiko.',
        classification: 'Klassifiser dataressurser etter sensitivitetsnivå. Bruk taksonomi-etiketter, spor livssyklusposter og identifiser klassifiseringsgap.',
        monitoring: 'Sett opp overvåkingsprogrammer for å spore etterlevelsesmetrikker, nøkkelrisikoindikatorer og kontrolleffektivitet over tid.',
        incidents: 'Rapporter og håndter personvernhendelser og datainnbrudd. Spor etterforskning, varsling og utbedringsarbeidsflyter.',
        connectors: 'Konfigurer datakildekoblinger og integrasjoner. Koble til eksterne systemer for automatisert dataoppdagelse og overvåking.',
        vendor_risk: 'Vurder og overvåk tredjepartsleverandørrisiko. Scorer leverandører, spor risikoreduksjon og administrer leverandørrisikokonfigurasjoner.',
        deletion_orchestration: 'Orkestrerer datasletting på tvers av systemer. Spor slettingsforespørsler, verifiser bevisskjeder og sikre fullstendig fjerning av data.',
        discovery: 'Oppdag personopplysninger på tvers av tilkoblede systemer. Skann datakilder for å bygge en omfattende dataoversikt.',
        incident_ops: 'Operasjonell hendelseshåndtering med spillbøker, eskaleringsarbeidsflyter og SLA-sporing for hendelsesløsning.',
        program_governance: 'Spor den overordnede modenheten til personvernprogrammet, etterlevelsesscorer, bevisgap og lederrapportering på tvers av organisasjonen.',
        access_accountability: 'Undersøk og revider brukertilgangsmønstre. Spor tilgangskonfigurasjoner, overvåk mistenkelig aktivitet og håndhev ansvarlighet.',
        localization: 'Installer og administrer markedslokalisringspakker. Legg til regulatoriske rammeverk, språkpakker og regionspesifikt etterlevelsesinnhold.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Samarbeid med eksterne partnere. Del etterlevelsesdata, spor partnerforpliktelser og administrer partnerrelasjoner.',
        privacy_scanner: 'Skann nettsteder for personvernetterlevelsesproblem. Overvåk informasjonskapselbruk, sporingsskript og tilstedeværelse av personvernpolicy i sanntid.',
        onboarding: 'Veiled nye organisasjoner gjennom plattformoppsett. Trinnvis innføringsveiviser for innledende konfigurasjon og dataimport.',
        compliance_forms: 'Opprett og administrer samsvarsskjemaer fra en malkatalog. Fyll ut regulatoriske skjemaer, spor innsendinger og generer rapporter.',
        audit_trail: 'Se en komplett revisjonslogg over alle systemhandlinger. Spor hvem som gjorde hva, når og hvor på tvers av alle moduler.',
        organizations: 'Administrer organisasjoner og flerleierinnstillinger. Konfigurer organisasjonsprofiler, hierarkier og relasjoner på tvers av organisasjoner.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Forklar uregelmessigheter',
            explaining: 'Forklarer …',
            results_heading: 'Anomaliforklaringer ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} elementer',
            remediation_label: 'Utbedringstrinn',
            items_detail_label: 'Berørte varer',
            accept_label: 'Godta forklaring',
            reject_label: 'Avvis forklaring',
            error_generic: 'Kunne ikke forklare uregelmessighetene. Prøv igjen eller kontroller at skanningen er fullført.',
        },
        cookie_insight: {
            insight_label: 'Informasjonskapsel og sporing AI innsikt',
            producing: 'Produserer innsikt …',
            results_heading: 'Informasjonskapsler og sporingsinnsikt ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} elementer',
            classification_label: 'Klassifisering',
            severity_label: 'Alvorlighetsgrad',
            banner_copy_label: 'Foreslått bannerkopi',
            retention_label: 'Oppbevaringsråd',
            citations_label: 'Sitater',
            kind_cookie: 'Cookie',
            kind_tracker: 'Tracker',
            class_necessary: 'Nødvendig',
            class_analytics: 'Analytics',
            class_marketing: 'Markedsføring',
            class_social: 'Sosialt',
            accept_label: 'Godta innsikt',
            reject_label: 'Avvis innsikt',
            no_items: 'Ingen informasjonskapsler eller sporere ble oppdaget i denne skanningen.',
            error_generic: 'Kunne ikke produsere informasjonskapselinnsikten. Prøv igjen eller kontroller at skanningen er fullført.',
        },
        monitoring: {
            title: 'Personvernovervåking',
            new_scan: 'Ny skanning',
            scan_url: 'URL å skanne',
            scan_frequency: 'Skanningsfrekvens',
            last_scan: 'Siste skanning',
            score: 'Personvernscore',
            issues: 'Funn',
            history: 'Skanningshistorikk',
            settings: 'Overvåkingsinnstillinger',
        },
        real_time: {
            title: 'Sanntidsskanner',
            scan_now: 'Skann nå',
            scanning: 'Skanner...',
            results: 'Skanningsresultater',
            no_results: 'Ingen skanningsresultater ennå.',
            cookies: 'Informasjonskapsler',
            trackers: 'Sporere',
            consent_banners: 'Samtykkebannere',
            third_party: 'Tredjepartsskript',
            archive: 'Resultatarkiv',
            settings: 'Skannerinnstillinger',
        },
        dashboard: {
            page_title: 'Dashboard for personvernovervåking',
            privacy_monitoring: 'Personvernovervåking',
            plan_label: '{package} plan',
            expires_label: 'utløper {date}',
            settings: 'Innstillinger',
            refresh: 'Oppdater',
            refreshing_dashboard: 'Forfriskende overvåkingsdashbord',
            refresh_aria: 'Oppdater overvåkingsdashbordet',
            not_configured_title: 'Personvernovervåking ikke konfigurert',
            not_configured_desc: 'Koble til AesirX-tjenesten for personvernovervåking for å begynne å spore nettstedets samsvar.',
            configure_monitoring: 'Konfigurer overvåking',
            license_expired_title: 'Lisens utløpt',
            license_expired_desc: 'Din AesirX lisens for personvernovervåking har utløpt. Vennligst forny den for å unngå tjenesteavbrudd.',
            compliance_score: 'Samsvarspoeng',
            cookies_detected: 'Informasjonskapsler oppdaget',
            trackers_label: 'Trackers',
            open_issues: 'Åpne problemer',
            score_good: 'Bra',
            score_needs_improvement: 'Trenger forbedring',
            score_critical: 'Kritisk',
            action_required: 'Handling kreves',
            cookies_sr: 'informasjonskapsler oppdaget',
            trackers_sr: 'sporere funnet',
            open_issues_sr: 'åpne problemer',
            action_required_sr: 'handling nødvendig',
            metric_label: 'Metrisk',
            value_label: 'Verdi',
            status_label: 'Status',
            aria_metrics: 'Beregninger for personvernovervåking',
            plan_limit_warning: 'Skanner URL: {current}/{max} – du har nådd plangrensen.',
            manage_domains: 'Administrer domener eller oppgrader planen din.',
            monitored_domains: 'Overvåkede domener',
            domain_added: 'Domene {domain} lagt til',
            domain_removed: 'Domenet {domain} fjernet',
            failed_add_domain: 'Kunne ikke legge til domene',
            failed_remove_domain: 'Kunne ikke fjerne domenet',
            failed_load: 'Kunne ikke laste inn overvåkingsdashbordet',
            new_domain_aria: 'Nytt domene URL',
            add_domain: 'Legg til domene',
            adding_domain: 'Legger til …',
            no_domains: 'Ingen overvåkede domener er konfigurert ennå.',
            aria_domains_table: 'Overvåkede domener',
            col_domain: 'Domene',
            col_compliance_score: 'Samsvarspoeng',
            col_status: 'Status',
            col_cookies: 'Informasjonskapsler',
            col_trackers: 'Trackers',
            col_issues: 'Problemer',
            col_last_scan: 'Siste skanning',
            col_action: 'Handling',
            compliance_status_sr: 'overholdelsesstatus',
            showing_chart: 'Viser kartvisning',
            showing_table: 'Viser datatabellvisning',
            loading_sr: 'Laster overvåkingsdashbord',
            col_framework_tags: 'Rammemerker',
            col_last_monitored: 'Sist overvåket',
            framework_tags_sr: 'gjeldende regelverk',
            last_monitored_sr: 'tidsstempel for kontinuerlig overvåking',
            audit_trail_heading: 'Revisjonssti',
            audit_trail_info: 'Hver overvåkingsskanning er tidsstemplet og logget. Endringer i samsvar på domenenivå produserer en uforanderlig revisjonspost som kan eksporteres for regulatorisk gjennomgang.',
            cybersecurity_law_info_heading: 'Deteksjon av lov om nettsikkerhet',
            cybersecurity_law_info: 'Overvåkede domener sjekkes automatisk mot lovkravene for nettsikkerhet (f.eks. Vietnam Cybersecurity Law, NIS2-direktivet). Rammebrikker angir hvilket regelverk som gjelder. Problemstillinger er kategorisert etter regelverk for målrettet utbedring.',
        },
        scan: {
            page_title: 'Sanntidsskanning',
            title: 'Personvernskanner i sanntid',
            settings: 'Innstillinger',
            results_archive: 'Resultatarkiv',
            launch_scan: 'Start Scan',
            target_url: 'Mål URL',
            protocol_aria: 'Protokoll',
            url_required: 'URL kreves',
            url_whitespace: 'Fjern mellomrom fra URL',
            url_invalid: 'Skriv inn et gyldig domene (f.eks. example.com)',
            add_subpages: 'Legg til undersider',
            add_subpages_aria: 'Legg til undersider, {count} lagt til',
            start_scan: 'Start skanning',
            launching: 'Starter …',
            scan_launched: 'Skanningen ble startet',
            scan_failed: 'Kunne ikke starte skanning',
            scan_progress: 'Skannefremdrift',
            scanning_in_progress: 'Skanning pågår …',
            scan_id: 'Skann ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Fremgang',
            pages_label: 'Sider',
            refresh_status: 'Oppdater status',
            new_scan: 'Ny skanning',
            scan_completed: 'Skanning fullført, resultater tilgjengelig',
            failed_check_status: 'Kunne ikke sjekke skannestatus',
            scan_results: 'Skanneresultater',
            main_page: 'Hovedside',
            page_n: 'Side {n}',
            result_pages_nav: 'Resultatsider',
            page_url: 'Side URL',
            risk_score: 'Risikopoeng',
            risk_score_sr: 'risikoscore',
            cookies_label: 'Informasjonskapsler',
            cookies_sr: 'informasjonskapsler oppdaget',
            scripts_label: 'Skript',
            scripts_sr: 'skript oppdaget',
            issues_label: 'Problemer',
            issues_sr: 'problemer funnet',
            issues_found: 'Problemer funnet',
            aria_issues_table: 'Problemer funnet under skanning',
            col_severity: 'Alvorlighetsgrad',
            col_category: 'Kategori',
            col_title: 'Tittel',
            col_remediation: 'Utbedring',
            severity_sr: 'severity',
            scan_result_metrics: 'Skann resultatberegninger',
            view_full_detail: 'Se full detalj',
            col_consent_category: 'Samtykkekategori',
            consent_category_sr: 'samtykkekategorisering',
            compliance_evidence_heading: 'Samsvarsbevis',
            compliance_evidence_info: 'Skannefunn knyttes automatisk til samsvarssaker. Hver sak kan spores til en samtykkekategori (essensiell, funksjonell, analytisk, markedsføring) og produserer revisjonsklare bevis for regulatorisk gjennomgang.',
            rule_config_heading: 'Regelkonfigurasjon',
            rule_config_info: 'Skanneregler kan konfigureres i henhold til regelverket. Kontroll av informasjonskapsler, skriptdeteksjon og leverandøranalyse kan aktiveres eller deaktiveres via Innstillinger. Egendefinerte regler kan defineres for å matche organisasjonens overholdelseskrav.',
            case_linkage_heading: 'Samsvarssakskobling',
            case_linkage_info: 'Problemer funnet under sanntidsskanninger kan knyttes til samsvarssaker for sporing og utbedring. Naviger til siden med detaljer om skanneresultat for å se sammenhenger.',
            subpages_modal_title: 'Legg til undersider',
            subpages_modal_desc: 'Legg til opptil 5 underside-URLer for å skanne sammen med hoveddomenet.',
            subpage_n: 'Underside {n}',
        },
        result_detail: {
            page_title: 'Skanneresultatdetaljer',
            title: 'Skanneresultat',
            back_to_archive: 'Tilbake til Arkiv',
            failed_load: 'Kunne ikke laste inn skanneresultatet',
            loading_sr: 'Laster inn skanneresultat …',
            scan_id: 'Skann ID',
            target_url: 'Mål URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Startet',
            completed: 'Fullført',
            pages_scanned: 'Sider skannet',
            https_label: 'HTTPS',
            highest_risk_score: 'Høyeste risikopoeng',
            most_cookies: 'De fleste informasjonskapsler',
            most_vendors: 'De fleste tredjepartsleverandører',
            most_issues: 'De fleste problemer',
            across_pages: 'på tvers av sidene',
            highest_values_aria: 'Høyeste verdier på alle sider',
            scanned_pages_nav: 'Skannede sider',
            main_page: 'Hovedside',
            page_n: 'Side {n}',
            page_url: 'Side URL',
            risk_score: 'Risikopoeng',
            overall_risk_sr: 'samlet risikoscore',
            cookies_label: 'Informasjonskapsler',
            cookies_sr: 'informasjonskapsler oppdaget',
            vendors_label: 'Tredjepartsleverandører',
            vendors_sr: 'tredjepartsleverandører oppdaget',
            issues_label: 'Problemer',
            issues_sr: 'problemer funnet',
            risk_breakdown_aria: 'Fordeling av risikopoeng',
            issues_found: 'Problemer funnet',
            aria_issues_table: 'Problemer funnet under skanning',
            col_severity: 'Alvorlighetsgrad',
            col_category: 'Kategori',
            col_title: 'Tittel',
            col_remediation: 'Utbedring',
            severity_sr: 'severity',
            cookies_heading: 'Informasjonskapsler ({count})',
            aria_cookies_table: 'Informasjonskapsler oppdaget',
            col_name: 'Navn',
            col_domain: 'Domene',
            col_path: 'Sti',
            col_secure: 'Sikker',
            col_httponly: 'Bare Http',
            col_samesite: 'SammeSite',
            vendors_heading: 'Tredjepartsleverandører ({count})',
            aria_vendors_table: 'Tredjepartsleverandører oppdaget',
            col_hostname: 'Vertsnavn',
            scripts_heading: 'Skript / Beacons ({count})',
            aria_scripts_table: 'Skript og beacons oppdaget',
            col_url: 'URL',
            col_type: 'Type',
            links_heading: 'Linker',
            first_party: 'Førstepart',
            third_party: 'Tredjepart',
            col_risk_category: 'Risikokategori',
            col_regulatory_framework: 'Regulatorisk rammeverk',
            col_actionable_detail: 'Handlingsbar detalj',
            risk_category_sr: 'regulatorisk risikokategori',
            risk_category_mapping_heading: 'Risikokategorikartlegging',
            risk_category_mapping_info: 'Hvert skanningsfunn er kartlagt til en regulatorisk risikokategori (datainnsamling, grenseoverskridende overføring, samtykkehåndtering, dataoppbevaring, sikkerhetskontroller). Denne kartleggingen muliggjør målrettet utbedring prioritert etter regulatorisk påvirkning.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Skanneresultater er knyttet til samsvarssaker for ende-til-ende-sporing. Problemer kan eskaleres direkte fra skanneresultater til arbeidsflyten for samsvarssaker.',
        },
        results_archive: {
            page_title: 'Skanningsresultatarkiv',
            title: 'Skanningsresultatarkiv',
            back_to_scanner: 'Tilbake til skanner',
            failed_load: 'Kunne ikke laste inn skanneresultater',
            results_loaded: 'Skanneresultater lastet, {count} poster',
            total_label: 'Totalt: {count}',
            aria_table: 'Skann resultatarkiv',
            col_scan_date: 'Skanningsdato',
            col_target_url: 'Mål URL',
            col_duration: 'Varighet',
            col_issues_found: 'Problemer funnet',
            col_risk_score: 'Risikopoeng',
            col_status: 'Status',
            col_actions: 'Handlinger',
            scan_status_sr: 'skannestatus',
            view_result: 'Visning',
            no_results: 'Fant ingen skanneresultater.',
            loading_sr: 'Laster inn skanneresultater',
            pagination_aria: 'Paginering av skanneresultater',
            col_risk_category: 'Risikokategori',
            risk_category_sr: 'regulatorisk risikokategori',
            archive_retention_heading: 'Arkivoppbevaring',
            archive_retention_info: 'Skanneresultater beholdes i varigheten som er spesifisert i organisasjonens retningslinjer for dataoppbevaring. Arkiverte resultater inkluderer full skanningskontekst (rammemerker, risikokategorier, utbedringsveiledning) og kan eksporteres som CSV for regulatorisk bevis.',
            export_context_info: 'Eksporterte CSV filer inkluderer regulatorisk kontekst: risikokategori, gjeldende rammeverk og handlingsbare utbedringsdetaljer for hvert funn.',
        },
        scanner_settings: {
            page_title: 'Skannerinnstillinger',
            title: 'Skannerinnstillinger',
            back_to_scanner: 'Tilbake til skanner',
            failed_load: 'Kunne ikke laste inn skannerkonfigurasjonen',
            endpoint_url: 'Endepunkt URL',
            save_success: 'Skannerkonfigurasjonen er lagret.',
            save_failed: 'Kunne ikke lagre skannerkonfigurasjonen',
            test_connection: 'Test tilkobling',
            testing: 'Tester...',
            saving: 'Lagrer...',
            loading_sr: 'Laster skannerinnstillinger',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Innstillinger for personvernovervåking',
            title: 'Overvåkingsinnstillinger',
            back_to_dashboard: 'Tilbake til Dashboard',
            failed_load: 'Kunne ikke laste inn overvåkingskonfigurasjonen',
            credentials_heading: 'AesirX Legitimasjon',
            connected_as: 'Tilkoblet som {username}',
            credentials_desc: 'Skriv inn din AesirX kontolegitimasjon for å koble til personvernovervåking. Finn medlems-ID-en din på profilsiden for AesirX-kontoen din.',
            client_id: 'Klient-ID',
            client_secret: 'Klienthemmelighet',
            username: 'AesirX Brukernavn (e-post)',
            password: 'AesirX Passord',
            placeholder_keep: '(la stå tomt for å beholde eksisterende)',
            credential_form_aria: 'AesirX legitimasjonsskjema',
            test_connection: 'Test tilkobling',
            testing: 'Tester …',
            test_aria: 'Test AesirX-tilkoblingen',
            testing_aria: 'Tester tilkobling',
            save_configuration: 'Lagre konfigurasjon',
            saving: 'Lagrer …',
            save_aria: 'Lagre legitimasjon',
            saving_aria: 'Lagrer legitimasjon',
            credentials_saved: 'AesirX påloggingsinformasjon er lagret.',
            credentials_saving: 'AesirX påloggingsinformasjon lagret. Laster inn konfigurasjonen på nytt …',
            credentials_save_failed: 'Kunne ikke lagre legitimasjonen',
            client_id_required: 'Klient-ID og brukernavn kreves.',
            all_fields_required: 'Alle legitimasjonsfelter er obligatoriske.',
            email_heading: 'E-postvarsler',
            receive_emails: 'Motta e-poster med skannerapporter',
            email_toggle_aria: 'Slå av/på skannerapport-e-poster',
            only_on_change: 'Bare e-post når risikostatus endres',
            only_on_change_aria: 'Slå av/på e-post kun ved endring av risikostatus',
            emails_enabled: 'Skannrapport e-poster aktivert',
            emails_disabled: 'Skannrapport-e-poster er deaktivert',
            only_change_on: 'Sender kun e-post om risikoendringer',
            only_change_off: 'Sender e-post ved hver skanning',
            email_failed: 'Kunne ikke oppdatere e-postinnstillingen',
            email_settings_aria: 'Innstillinger for e-postvarsling',
            loading_sr: 'Laster overvåkingsinnstillinger',
        },
        entity_types: {
            user: 'Bruker',
            case: 'Sak',
            incident: 'Hendelse',
            assessment: 'Vurdering',
            vendor: 'Leverandør',
            consent: 'Samtykke',
            policy: 'Politikk',
            data_map: 'Datakart',
        },
        actions: {
            create: 'Opprett',
            update: 'Oppdater',
            delete: 'Slett',
            view: 'Visning',
            export: 'Eksporter',
            status_change: 'Statusendring',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Skjemakontrollpanel',
            total_templates: 'Totalt antall maler',
            installed: 'Installerte',
            available: 'Tilgjengelige',
            by_market: 'Etter marked',
            back_dashboard: 'Tilbake til dashbord',
            browse_templates: 'Bla gjennom maler',
            catalog_coverage: 'Katalog Dekning',
            quick_start: 'Rask Start',
            recent_instances: 'Nylige Instanser',
            total_instances: 'Totalt Instanser',
            fetch_failed: 'Kunne ikke hente dashboarddata.',
            no_instances: 'Ingen instanser funnet.',
            form_metrics: 'Oversikt over skjemametrikker',
            templates_total: 'maler totalt',
            instances_total: 'forekomster totalt',
            installed_of_available: '{installed} av {available} installert',
            templates_in_category: 'maler i {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Skjemamaler',
            new: 'Ny mal',
            edit: 'Rediger mal',
            name: 'Malnavn',
            form_type: 'Skjematype',
            market: 'Marked',
            language: 'Språk',
            schema: 'Skjemaskjema',
            preview: 'Forhåndsvisning',
            duplicate: 'Dupliser mal',
            grid_view: 'Rutenett Vis',
            list_view: 'Liste Vis',
            fetch_failed: 'Kunne ikke hente maler.',
            filter_templates: 'Filtrer maler',
            version_history: 'Versjonshistorikk',
            no_version_history: 'Ingen versjonshistorikk — dette er den opprinnelige versjonen.',
            form_id: 'Skjema-ID',
            source_instrument: 'Kildeinstrument',
            issuer: 'Utsteder',
            all_frameworks: 'Alle rammeverk',
            view_mode: 'Visningsmodus',
            classification: 'Klassifisering',
        },
        catalog: {
            title: 'Skjemakatalog',
            install: 'Installer',
            uninstall: 'Avinstaller',
            open_form: 'Open form',
            install_all: 'Installer alle',
            installed_tab: 'Installerte',
            catalog_tab: 'Katalog',
            search_placeholder: 'Søk i maler...',
            no_templates: 'Ingen maler funnet.',
            template_count: '{count} maler',
            all_markets: 'Alle Markeder',
            sections: 'Seksjoner',
            all_categories: 'Alle Kategorier',
            category: 'Kategori',
            category_install_failed: 'Kunne ikke installere kategorimaler.',
            category_install_success: 'Kategori maler installerte.',
            fetch_failed: 'Kunne ikke hente maler.',
            fetch_installed_failed: 'Kunne ikke hente installerte maler.',
            fields: 'Felter',
            install_failed: 'Kunne ikke installere mal.',
            install_success: 'Mal installeret.',
            installing: 'Installerer...',
            removing: 'Fjerner...',
            uninstall_confirm: 'Er du sikker på at du vil avinstallere denne malen?',
            uninstall_failed: 'Kunne ikke avinstallere mal.',
            uninstall_success: 'Mal afinstalleret.',
            installation_summary: 'Installasjonsoversikt',
            filter_catalog: 'Filtrer katalog',
            all_frameworks: 'Alle rammeverk',
            templates_installed_count: '{count} mal(er) installert',
            all_already_installed: 'Alle maler i denne kategorien er allerede installert',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Skjema-ID',
            issuer: 'Utsteder',
            catalog_tabs: 'Skjemakatalogfaner',
        },
        filler: {
            back_templates: 'Tilbake til maler',
            no_sections: 'Ingen seksjoner funnet.',
            draft_created: 'Utkast opprettet.',
            draft_saved: 'Utkast lagret.',
            fetch_template_failed: 'Kunne ikke hente mal.',
            save_draft: 'Lagre Utkast',
            save_draft_failed: 'Kunne ikke lagre utkast.',
            saving: 'Lagrer...',
            select_option: 'Velg an alternativ',
            submit: 'Send inn',
            submit_failed: 'Kunne ikke sende inn.',
            submitting: 'Sender inn...',
            title: 'Skjemaveiviser',
            form_id_label: 'Skjema-ID',
            schema_label: 'Skjema',
            form_number_label: 'Form',
            framework_label: 'Rammeverk',
            issuer_label: 'Utsteder',
            source_label: 'Kilde',
            market_label: 'Marked',
            section_of: 'Seksjon {current} av {total}',
            form_navigation: 'Skjemanavigasjon',
            form_progress: 'Skjemafremdrift: Seksjon {current} av {total}',
            removed_item: 'Element {index} fjernet fra {field}',
            added_item: 'Nytt element lagt til i {field}',
            go_to_previous: 'Gå til forrige seksjon, seksjon {current} av {total}',
            go_to_next: 'Gå til neste seksjon, seksjon {current} av {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Innleveringsstatus',
            advance: 'Fremme innleveringsstatus...',
            not_started: 'Ikke startet',
            drafting: 'Utkast',
            internal_review: 'Intern gjennomgang',
            approved_for_submission: 'Godkjent for innsending',
            submitted_to_authority: 'Sendt til myndighet',
            supplement_requested: 'Tillegg etterspurt',
            supplement_in_progress: 'Tillegg pågår',
            resubmitted: 'Sendt på nytt',
            accepted: 'Godtatt',
            rejected: 'Avvist',
            closed: 'Lukket',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Godkjent av',
            form_answers: 'Skjema Svar',
            column_header: 'Form',
            no_answers: 'Ingen svar funnet.',
            view_outputs: 'Vis Utdata',
            approve: 'Godkjenn',
            approve_failed: 'Kunne ikke godkjenne.',
            approving: 'Godkjenner...',
            detail_title: 'Instans Detalj',
            fetch_failed: 'Kunne ikke hente instanser.',
            generate_failed: 'Kunne ikke generere utdata.',
            generate_output: 'Generer Utdata',
            generating: 'Genererer...',
            submitted_at: 'Innsendt At',
            instance_metadata: 'Skjemaforekomstmetadata',
            filing_status_steps: 'Arkiveringsstatussteg',
            current_status: 'nåværende status',
            filing_status_update_failed: 'Kunne ikke oppdatere innleveringsstatus',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Tilbake to Instans',
            no_outputs_msg: 'Ingen utdata har blitt generert ennå.',
            no_outputs_title: 'Ingen utdata',
            output_name: 'Utdata Navn',
            download: 'Last ned',
            download_failed: 'Kunne ikke laste ned.',
            fetch_failed: 'Kunne ikke hente utdata.',
            format: 'Format',
            generated_at: 'Generert Ved',
            generated_by: 'Generert Etter',
            size: 'Størrelse',
            title: 'Skjema Utdata',
            print: 'Skriv ut',
            print_outputs: 'Skriv ut skjemaresultater',
            printing: 'Skriver ut skjemaresultater',
            pdf_preview: 'PDF-forhåndsvisning',
            downloading: 'Laster ned {name}',
            preview_in_new_window: 'Forhåndsvis {name} ({format}) i nytt vindu',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Omfang og anvendelse',
            risk: 'Risikovurdering og kontrollkartlegging',
            policy: 'Organisatoriske sikkerhetspolicyer',
            localization: 'Datalokalisering og lokal tilstedeværelse',
            vendor: 'Tredjeparter og forsyningskjede',
            authority: 'Håndtering av myndighetsforespørsler',
            incident: 'Hendelseshåndtering og bevissikring',
            training: 'Opplæring og beredskap',
        },
        market_finalization_domains: {
            data_law: 'Datalov — Styring og Operasjonelle Kontroller',
            pdpl_2025: 'PDPL 2025 — Personvernstyring og Registrertes Rettigheter',
            decree_356: 'Dekret 356/2025 — Leverandør- og Grenseoverskridende Kontroller',
            telecom: 'Ekomlov — Konfidensialitet og Myndighetsforespørsler',
        },
        nav: {
            filing_case_links: 'Sakskoblinger',
            section_routing: 'Seksjonsruting',
            submission_packages: 'Innsendingspakker',
        },
        filing_case_links: {
            title: 'Sakskoblinger',
            link_form: 'Koble skjema til innleveringssak',
            filing_case_type: 'Innleveringssaktype',
            form_instance: 'Skjemainstans',
            select_form_instance: 'Velg en skjemainstans...',
            select_filing_case: 'Velg en innleveringssak...',
            no_links_found: 'Ingen sakskoblinger funnet.',
            linked: 'Koblet',
            filing_case_id: 'Arkiveringssak-ID',
            link_created: 'Arkiveringssak-kobling opprettet',
            link_deleted: 'Sakskobling slettet',
            aria_delete_link: 'Slett kobling {name}',
            case_type_dpia_dossier: 'DPIA-dossier',
            case_type_cross_border_filing: 'Grensekryssende innlevering',
            case_type_breach_notification: 'Bruddvarsel',
            no_instances_guidance: 'Ingen skjemainstanser ennå. Opprett en fra Skjemamaler.',
            no_filings_guidance: 'Ingen innleveringssaker ennå. Opprett en fra Vurderinger.',
        },
        section_routing: {
            title: 'Seksjonsruting',
            assign_section: 'Tildel seksjon',
            department: 'Avdeling',
            select_department: 'Velg en avdeling...',
            assignee: 'Tildelt til',
            select_assignee: 'Velg en tildelt...',
            section: 'Seksjon',
            select_section: 'Velg en seksjon...',
            select_form_instance: 'Velg en skjemainstans...',
            no_assignments_found: 'Ingen seksjonstildelinger funnet.',
            select_instance_prompt: 'Velg en skjemainstans for å se seksjonstildelinger.',
            complete_section: 'Fullfør seksjon',
            status_pending: 'Venter',
            status_in_progress: 'Pågår',
            status_completed: 'Fullført',
            status_reviewed: 'Gjennomgått',
            form_instance_id: 'Skjemaforekomst-ID',
            assignment_created: 'Seksjonstildeling opprettet',
            assignment_deleted: 'Seksjonstildeling slettet',
            status_updated: 'Seksjonsstatus oppdatert',
            aria_delete_assignment: 'Slett tildeling {name}',
            deadline: 'Frist',
            deadline_help: 'Valgfritt. Tildelinger etter denne datoen vises som forfalt.',
            overdue: 'Forfalt',
            days_remaining: 'dager igjen',
            approval_requested: 'Sendt til gjennomgang',
            form_approved: 'Skjema godkjent',
            form_rejected: 'Skjema avvist',
            no_departments_guidance: 'Ingen avdelinger konfigurert. Sett opp i Organisasjonsinnstillinger.',
            no_sections_guidance: 'Ingen seksjoner definert i malen. Sørg for at skjemamalen har seksjoner.',
            select_instance_first: 'Velg en skjemainstans først for å laste tilgjengelige seksjoner.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Innsendingspakker',
            assemble_package: 'Sett sammen innsendingspakke',
            dossier: 'Dossier',
            select_dossier: 'Velg en dossier...',
            package_label: 'Pakkeetikett',
            cover_letter: 'Følgebrev',
            checklist: 'Sjekkliste',
            evidence_index: 'Bevisindeks',
            completeness: 'Fullstendighet',
            incomplete_warning: 'Kan ikke sette sammen: skjemaet har ufullstendige seksjoner.',
            no_packages_found: 'Ingen innsendingspakker funnet.',
            version: 'Versjon',
            package_assembled: 'Innsendingspakke sammenstilt',
            package_deleted: 'Innsendingspakke slettet',
            aria_delete_package: 'Slett pakke {name}',
            status_draft: 'Utkast',
            status_complete: 'Fullstendig',
            status_submitted: 'Innsendt',
            status_accepted: 'Godkjent',
            status_rejected: 'Avvist',
            package_updated: 'Innsendingspakke oppdatert',
            document_generated: 'Dokument generert',
            documents: 'Dokumenter',
            generate: 'Generer',
            preview: 'Forhåndsvisning',
            download: 'Last ned',
            download_all: 'Last ned alle',
            no_dossiers_guidance: 'Ingen dossiers ennå. Opprett en fra Vurderinger.',
            package_plan_help: 'Inkluder i pakkeplanen (automatisk dokumentgenerering tilgjengelig):',
            cover_letter_help: 'Offisielt følgebrev adressert til mottakende myndighet',
            checklist_help: 'Samsvarsjekkliste som verifiserer at alle innleveringskrav er oppfylt',
            evidence_index_help: 'Indeks over støttedokumentasjon referert i innleveringen',
            completeness_skipped: 'Fullstendighetskontroll hoppet over — ingen skjemainstans knyttet til dette dossier.',
            completeness_unavailable: 'Fullstendighetskontroll ikke tilgjengelig — fortsetter med pakkesammensetning.',
            duplicate_package_error: 'Det finnes allerede en innleveringspakke for dette dossieret.',
            doc_cover_letter: 'Følgebrev',
            doc_checklist: 'Sjekkliste',
            doc_evidence_index: 'Bevisindeks',
            export_csv: 'Eksporter CSV',
            export_success: 'Lenker til arkiveringssaker eksportert vellykket',
            no_assignments_guidance: 'Ingen seksjonstildelinger ennå. Tildel seksjoner til avdelinger og teammedlemmer for å starte ruting.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Velg en mal',
            hint: 'Velg en regulatorisk skjemamal eller begynn med et tomt skjema.',
            market_filter: 'Filtrer etter marked',
            framework_filter: 'Filtrer etter rammeverk',
            all_markets: 'Alle markeder',
            all_frameworks: 'Alle rammeverk',
            search_placeholder: 'Søk etter maler...',
            blank_form: 'Tomt / generisk skjema',
            blank_form_desc: 'Opprett uten mal — fyll ut felt manuelt.',
            no_templates: 'Ingen maler funnet for dette filteret.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Velg',
            link_error: 'Malkobling mislyktes — du kan koble en mal senere fra Redigeringsvisningen.',
            internal_badge: 'Intern mal',
            legacy_badge: 'Utdatert',
            draft_badge: 'Utkast',
            upcoming_badge: 'Kommende',
            hidden_badge: 'Skjult',
            show_hidden_toggle: 'Vis skjulte / utdaterte maler',
            show_hidden_toggle_hint: 'Administratorvisning: inkluderer maler merket som internal_only, hidden, legacy eller draft.',
        },
        cover_letter_modal: {
            title: 'Følgebrev: forhåndsvisning og redigering',
            iframe_title: 'Forhåndsvisning av følgebrev',
            stage_label: 'Trinn',
            stage_initial: 'Første innsending',
            stage_supplement: 'Supplerende svar',
            stage_resubmission: 'Ny innsending',
            subject_label: 'Emne',
            body_label: 'Hovedavsnitt',
            notes_label: 'Ekstra notater',
            refresh_preview: 'Oppdater forhåndsvisning',
            confirm: 'Bekreft og lagre',
            regenerate: 'Regenerer',
            snapshot_saved: 'Følgebrev lagret',
        },
        related_panel: {
            title: 'Relaterte forskriftsskjemaer',
            open_form: 'Fyll ut skjema',
            no_forms: 'Ingen relaterte skjemaer funnet.',
            hint_vn: 'VN-spesifikke forskriftsskjemaer tilgjengelige — velg et skjema for å åpne samsvarsveiledningen.',
            internal_badge: 'Intern',
            official_badge: 'Offisiell',
            loading: 'Laster relaterte skjemaer...',
            error: 'Kunne ikke laste relaterte skjemaer.',
            form_id: 'Skjema-ID',
            form_name: 'Skjemanavn',
            framework: 'Rammeverk',
        },
        view_form: 'Vis skjema',
        edit_form: 'Rediger skjema',
        save_draft: 'Lagre utkast',
        module_settings: 'Modulinnstillinger',
        validation_errors: 'Vennligst rett valideringsfeilene',
        custom_name_help: 'Valgfritt — standard er malnavnet hvis tomt',
        select_placeholder: 'Velg...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Forrige',
        next_section: 'Neste',
        loading: 'Laster...',
        array_add: 'Legg til',
        array_remove: 'Fjern',
        array_item_label: '{label} element {index}',
        array_add_item: 'Legg til element i {label}',
        array_remove_item: 'Fjern element {index} fra {label}',
        legacy_banner: 'Denne posten ble opprettet før enhetlige skjemaer. Noen data kan avvike mellom enheten og skjemainstansen.',
        field_required: '{label} er påkrevd',
        section_announcement: 'Del {current} av {total}: {title}',
        sections_nav_label: 'Skjemaseksjoner',
        no_sections: 'Ingen malseksjoner tilgjengelig.',
        section_label_tpl: 'Seksjon {index}: {title}',
        field_shown_announcement: '{field} vises nå.',
        field_required_announcement: '{field} er nå påkrevd.',
        submit_blocked_hint_one: '1 påkrevd felt mangler fortsatt et svar.',
        submit_blocked_hint_many: '{count} påkrevde felter mangler fortsatt et svar.',
        module_settings_auto_applied: 'Modulspesifikke innstillinger brukes automatisk.',
        operation_failed: 'Operasjonen mislyktes',
        failed_to_load_form: 'Kunne ikke laste inn skjemaet',
        failed_to_save_form: 'Kunne ikke lagre skjemaet',
        failed_to_submit_form: 'Kunne ikke sende inn skjemaet',
        failed_to_load_template: 'Kunne ikke laste inn malen',
        failed_to_save_draft: 'Kunne ikke lagre utkastet',
        failed_to_create: 'Kunne ikke opprette',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Pakkekatalog',
        install: 'Installer pakke',
        uninstall: 'Avinstaller pakke',
        install_batch: 'Installer gruppe',
        installed_tab: 'Installerte',
        catalog_tab: 'Katalog',
        pack_details: 'Pakkedetaljer',
        frameworks: 'Rammeverk',
        requirements: 'Krav',
        artifacts: 'Artefakter',
        language_pack: 'Språkpakke',
        dependencies: 'Avhengigheter',
        no_packs: 'Ingen pakker funnet.',
    },
    platform_pages: {
        users: {
            title: 'Brukere',
            new: 'Ny bruker',
            invite: 'Inviter bruker',
            edit: 'Rediger bruker',
            name: 'Navn',
            email: 'E-post',
            role: 'Rolle',
            status: 'Status',
            identity_type: 'Identitetstype',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Siste innlogging',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Deaktiver',
            reactivate: 'Reaktiver',
            unlock: 'Lås opp',
            reset_password: 'Tilbakestill passord',
            remove: 'Fjern',
            remove_confirm: 'Fjern "{name}"? Denne handlingen er permanent, og brukeren mister all tilgang. Oppføringen beholdes for revisjonsformål.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Roller',
            new: 'Ny rolle',
            edit: 'Rediger rolle',
            name: 'Rollenavn',
            description: 'Beskrivelse',
            permissions: 'Tillatelser',
            users_count: 'Brukere',
            detail: 'Rolledetaljer',
            all_tiers: 'Alle nivåer',
            tier: 'Nivå',
            type_system: 'System',
            type_custom: 'Egendefinert',
            role_name_placeholder: 'f.eks. regional compliance-leder',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Min profil',
            name: 'Navn',
            display_name: 'Visningsnavn',
            email: 'E-post',
            phone: 'Telefon',
            department: 'Avdeling',
            job_title: 'Stillingstittel',
            language: 'Språk',
            role: 'Rolle',
            persona: 'Oppdaget persona',
            last_login: 'Siste innlogging',
            save: 'Lagre endringer',
            save_success: 'Profilen er oppdatert',
            module_tips_section: 'Modultips',
            tips_toggle: 'Vis regulatoriske tips i moduler',
            dismissed_tips: 'Avviste tips',
            reset_dismissed: 'Tilbakestill alle avviste tips',
            notification_prefs: 'Varslingsinnstillinger',
            notification_prefs_link: 'Administrer varslingsinnstillinger →',
            change_password: 'Endre passord',
            current_password: 'Gjeldende passord',
            new_password: 'Nytt passord',
            confirm_password: 'Bekreft passord',
            accessibility: 'Tilgjengelighet',
            high_contrast: 'Hoykontrast-modus',
            high_contrast_desc: 'Ok kontrasten for bedre lesbarhet. Oppfyller WCAG 2.1 AAA.',
            passwords_no_match: 'Passordene stemmer ikke overens',
            password_changed_logout: 'Passord endret. Du blir logget ut.',
            failed_update: 'Kunne ikke oppdatere profil',
            failed_change_password: 'Kunne ikke endre passord',
            none: 'Ingen',
            never: 'Aldri',
            password_requirements: 'Min. 12 tegn, stor bokstav, liten bokstav, siffer, spesialtegn',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA-innstillinger',
            mfa_enable: 'Aktiver MFA',
            mfa_disable: 'Deaktiver MFA',
            mfa_setup: 'Skann denne QR-koden med autentiseringsappen din',
            sso_title: 'SSO-konfigurasjon',
            sso_provider: 'Leverandør',
            sso_client_id: 'Klient-ID',
            sso_redirect: 'Omdirigerings-URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API-nøkler',
            api_keys_new: 'Ny API-nøkkel',
            api_keys_name: 'Nøkkelnavn',
            api_keys_last_used: 'Sist brukt',
            api_keys_expires: 'Utløper',
            api_keys_revoke: 'Tilbakekall',
            service_accounts_title: 'Tjenestekontoer',
            ip_allowlist_title: 'IP-godkjenningsliste',
            ip_add: 'Legg til IP-adresse',
            ip_address: 'IP-adresse',
            ip_description: 'Beskrivelse',
            delegations_title: 'Delegeringer',
            delegations_new: 'Ny delegering',
            delegations_from: 'Fra bruker',
            delegations_to: 'Til bruker',
            delegations_expires: 'Utløper',
            break_glass_title: 'Nødtilgang',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Be om tilgang',
            break_glass_reason: 'Begrunnelse for tilgang',
            break_glass_duration: 'Varighet',
            session_settings_title: 'Sesjonsinnstillinger',
            eviction_policy_section: 'Utkastelsespolicy',
            session_timeout: 'Sesjonstidsavbrudd (minutter)',
            session_max_concurrent: 'Maks samtidige sesjoner',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Aktive sesjoner',
            active_sessions_device: 'Enhet',
            active_sessions_ip: 'IP-adresse',
            active_sessions_started: 'Startet',
            active_sessions_revoke: 'Tilbakekall sesjon',
            active_sessions_confirm_terminate: 'Er du sikker på at du vil avslutte denne økten?',
            active_sessions_confirm_terminate_current: 'Dette er din nåværende økt. Hvis du avslutter det, vil du logge deg ut umiddelbart. Fortsette?',
            active_sessions_confirm_terminate_all: 'Er du sikker på at du vil avslutte alle andre økter? Bare din nåværende økt vil forbli aktiv.',
            active_sessions_terminated: 'Økten ble avsluttet.',
            active_sessions_all_terminated: 'Alle andre økter er avsluttet.',
            active_sessions_terminate_all: 'Avslutt alle andre økter',
            active_sessions_last_activity: 'Siste aktivitet',
            active_sessions_current: 'Nåværende økt',
            active_sessions_revoke_selected: 'Tilbakekall valgte',
            active_sessions_confirm_terminate_selected: 'Er du sikker på at du vil avslutte de valgte øktene?',
            active_sessions_selected_terminated: 'Valgte økter avsluttet.',
            ip_allowlist_desc: 'Administrer tillatte IP-adresser for API tilgang.',
            ip_no_entries: 'Ingen IP-adresser er konfigurert.',
            session_settings_desc: 'Konfigurer tidsavbrudd for økter og grenser for samtidige økter.',
            session_settings_save_policy: 'Lagre sesjonspolicy',
            session_settings_saved: 'Øktinnstillingene er oppdatert.',
            session_settings_idle_timeout: 'Inaktiv tidsavbrudd (minutter)',
            session_settings_absolute_timeout: 'Absolutt tidsavbrudd (minutter)',
            session_settings_help_idle: 'Økten vil utløpe etter denne perioden med inaktivitet.',
            session_settings_help_absolute: 'Økten vil utløpe etter denne totale varigheten uavhengig av aktivitet.',
            session_settings_help_max: 'Maksimalt antall samtidige økter per bruker.',
            sso_note: 'Endringer i SSO-konfigurasjonen kan kreve at brukere autentiserer seg på nytt.',
            service_accounts_warning: 'Denne API-tasten vil bare vises én gang. Kopier det nå og oppbevar det sikkert.',
            service_accounts_new: 'Ny tjenestekonto',
            service_accounts_name: 'Kontonavn',
            service_accounts_role: 'Rolle',
            service_accounts_created: 'Opprettet',
            service_accounts_desc: 'Administrer maskin-til-maskin-tjenestekontoer for API integrasjoner.',
            break_glass_warning: 'Break-glass-tilgang revideres og logges. Bruk kun for ekte nødstilfeller.',
            break_glass_min_chars: '{count} / {min} minimumstegn',
            break_glass_permission_count: '{count} tillatelse(r) er valgt',
            break_glass_submitted: 'Forespørsel om knust glass er sendt inn. Venter på godkjenning.',
            break_glass_approved: 'Forespørsel om knust glass godkjent.',
            break_glass_denied: 'Forespørsel om glassknusing avvist.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Bekreft aktivering',
            break_glass_pending: 'Ventende forespørsler',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Gjenstående tid',
            break_glass_expired: 'Utløpt',
            break_glass_approved_denied_by: 'Godkjent/Nekt av',
            delegations_desc: 'Administrer midlertidige tillatelsesdelegeringer mellom brukere.',
            delegations_my: 'Mine delegasjoner',
            delegations_to_me: 'Delegert til meg',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Alle tillatelser',
            delegations_scope_specific: 'Spesifikke tillatelser',
            delegations_created: 'Delegasjonen ble opprettet.',
            delegations_revoked: 'Delegasjonen tilbakekalt.',
            delegations_confirm_revoke: 'Er du sikker på at du vil tilbakekalle denne delegeringen?',
            delegations_confirm_revoke_title: 'Bekreft tilbakekall',
            delegations_permission_count: '{count} tillatelse(r)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Status',
            api_keys_prefix: 'Nøkkelprefiks',
            api_keys_permissions_count: 'Tillatelser',
            api_keys_ip_count: 'IP-begrensninger',
            api_keys_created: 'Opprettet',
            api_keys_confirm_revoke_title: 'Bekreft tilbakekall',
            api_keys_confirm_revoke_message: 'Er du sikker på at du vil tilbakekalle API-tasten "{name}"? Denne handlingen kan ikke angres.',
            api_keys_confirm_rotate_title: 'Bekreft rotasjon',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API nøkkel opprettet.',
            api_keys_revoked_success: 'API-tasten "{name}" er tilbakekalt.',
            api_keys_rotated_success: 'API-tasten "{name}" har blitt rotert. Kopier den nye nøkkelen nedenfor.',
            api_keys_load_error: 'Kunne ikke laste inn API-nøkler.',
            api_keys_create_error: 'Kunne ikke opprette API nøkkel.',
            api_keys_revoke_error: 'Kunne ikke tilbakekalle API-nøkkelen.',
            api_keys_rotate_error: 'Kunne ikke rotere API-tasten.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minutter',
            break_glass_duration_30: '30 minutter',
            break_glass_duration_60: '60 minutter (1t)',
            break_glass_duration_120: '120 minutter (2t)',
            break_glass_duration_240: '240 minutter (4t)',
            sod_rules_title: 'Regler for pliktdeling',
            sod_violations_report: 'Rapport om brudd',
            sod_create_rule: 'Opprett regel',
            sod_col_name: 'Navn',
            sod_col_description: 'Beskrivelse',
            sod_col_conflict_count: 'Antall konflikter',
            sod_col_severity: 'Alvorlighetsgrad',
            sod_col_status: 'Status',
            sod_col_actions: 'Handlinger',
            sod_no_rules: 'Ingen SoD-regler funnet',
            sod_severity_warning: 'Advarsel',
            sod_severity_blocking: 'Blokkering',
            sod_active: 'Aktiv',
            sod_inactive: 'Inaktiv',
            sod_edit: 'Rediger',
            sod_exceptions: 'Unntak',
            sod_deactivate: 'Deaktiver',
            sod_confirm_deactivate_title: 'Bekreft deaktivering',
            sod_confirm_deactivate_message: 'Vil du deaktivere regelen «{name}»? Det vil ikke lenger flagge brudd.',
            sod_deactivate_error: 'Kunne ikke deaktivere regelen',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Tilgangsgjennomganger',
            campaigns: 'Gjennomgangskampanjer',
            new_campaign: 'Ny kampanje',
            campaign_name: 'Kampanjenavn',
            scope: 'Omfang',
            reviewers: 'Gjennomgangere',
            due_date: 'Forfallsdato',
            progress: 'Fremdrift',
            my_reviews: 'Mine gjennomganger',
            approve: 'Godkjenn',
            revoke: 'Tilbakekall',
            analytics: 'Rolleanalyse',
            analytics_title: 'Rolleanalyse',
            permission_usage: 'Tillatelsesbruk',
            role_coverage: 'Rolledekning',
            campaign_detail: 'Kampanjedetaljer',
            campaign_description: 'Beskrivelse',
            campaign_status: 'Status',
            campaign_deadline: 'Frist',
            campaign_scope_type: 'Omfang Type',
            campaign_completion: 'Fullføring',
            campaign_total_items: 'Totalt antall varer',
            campaign_pending_items: 'Venter',
            campaign_confirmed_items: 'Bekreftet',
            campaign_revoked_items: 'Opphevet',
            campaign_escalated_items: 'Eskalert',
            campaign_launch: 'Start kampanje',
            campaign_cancel: 'Avbryt kampanje',
            campaign_confirm_launch: 'Er du sikker på at du vil lansere denne kampanjen?',
            campaign_confirm_cancel: 'Er du sikker på at du vil kansellere denne kampanjen?',
            campaign_launched: 'Kampanjen ble lansert.',
            campaign_cancelled: 'Kampanjen kansellert.',
            review_items: 'Gjennomgå elementer',
            review_item_user: 'Bruker',
            review_item_role: 'Rolle',
            review_item_reviewer: 'Anmelder',
            review_item_decision: 'Beslutning',
            review_item_justify: 'Begrunnelse',
            review_item_confirm: 'Bekreft',
            review_item_revoke: 'Oppheve',
            review_item_escalate: 'Eskalere',
            all_decisions: 'Alle vedtak',
            bulk_action: 'Bulk handling',
            bulk_confirm: 'Massebekreft',
            bulk_revoke: 'Bulk Revoke',
            selected_count: '{count} valgt',
            justify_required: 'Begrunnelse er nødvendig',
            scope_all_users: 'Alle brukere',
            scope_role_owner: 'Rolleeier',
            scope_department: 'Avdeling',
            back_to_campaigns: 'Tilbake til Kampanjer',
            recommendations: 'Anbefalinger',
            refreshing: 'Oppdaterer...',
            decide: 'Bestem deg',
            decision_by: 'Vedtak av',
            loading_items: 'Laster inn elementer ...',
            loading_review_items: 'Laster inn anmeldelseselementer ...',
            campaign_not_found: 'Fant ikke kampanjen',
            loading_campaign: 'Laster inn kampanjedetaljer ...',
            campaign_completed: 'Kampanjen fullført.',
            confirm_cancel_title: 'Bekreft Avbryt',
            justify_bulk_confirm: 'Begrunnelse for massebekreftelse:',
            justify_bulk_revoke: 'Begrunnelse for masseopphevelse:',
            items_selected: '{count} element(er) er valgt',
            justify_bulk_required: 'Begrunnelse er nødvendig for bulkhandlinger',
            create_campaign_title: 'Opprett tilgangsvurderingskampanje',
            name_required: 'Navn er påkrevd',
            scope_type_required: 'Omfangstype er påkrevd',
            reviewer_strategy_required: 'Anmelderstrategi er nødvendig',
            deadline_required: 'Frist er påkrevd',
            reviewer_id_required: 'Anmelder-ID kreves for spesifikk brukerstrategi',
            invalid_json: 'Ugyldig JSON',
            scope_filter_label: 'Omfangsfilter (JSON)',
            reviewer_strategy_label: 'Anmelderstrategi',
            reviewer_id_label: 'Anmelder-ID',
            deadline_label: 'Frist',
            create_campaign_btn: 'Opprett kampanje',
            scope_type_label: 'Omfang Type',
            scope_by_role: 'Etter rolle',
            scope_by_department: 'Etter avdeling',
            scope_custom: 'Egendefinert',
            reviewer_manager: 'Manager',
            reviewer_specific_user: 'Spesifikk bruker',
            enter_json_filter: 'Skriv inn JSON filter',
            hint_label: 'Hint:',
            role_usage: 'Rollebruk',
            over_privileged_users: 'Overprivilegerte brukere',
            role_name_col: 'Rollenavn',
            assigned_users: 'Tilordnede brukere',
            active_users_30d: 'Aktive brukere (30d)',
            total_perms: 'Total Perms',
            used_perms: 'Brukte Perms',
            unused_perms: 'Ubrukte Perms',
            no_data_available: 'Ingen data tilgjengelig',
            roles_col: 'Roller',
            total_col: 'Totalt',
            used_col: 'Brukt',
            unused_pct: 'Ubrukt %',
            current_count: 'Nåværende telling',
            recommended_count: 'Anbefalt antall',
            rationale: 'Begrunnelse',
            loading_analytics: 'Laster inn statistikk ...',
            loading_role_analytics: 'Laster inn rolleanalysedata …',
            computed_at: 'Beregnet ved:',
            form_errors: 'Skjemaet har {count} feil(er). Rett dem opp.',
            scope_filter_invalid_json: 'Omfangsfilteret inneholder ugyldig JSON',
            scope_hint_all_users: 'Ingen filter nødvendig - alle brukere vil bli inkludert.',
            review_type_label: 'Gjennomgangstype',
            review_type_required: 'Gjennomgangstype er påkrevd',
            reviewer_ids_label: 'Revisor-ID-er',
            reviewer_ids_hint: 'Skriv inn bruker-ID-er atskilt med komma',
            reviewer_ids_required: 'Minst én revisor-ID er påkrevd',
            start_date_label: 'Startdato',
            start_date_required: 'Startdato er påkrevd',
            end_date_label: 'Sluttdato',
            end_date_required: 'Sluttdato er påkrevd',
            review_type_user_access: 'Brukertilgang',
            review_type_role_permissions: 'Rolletillatelser',
            review_type_service_accounts: 'Tjenestekontoer',
            review_type_privileged_access: 'Privilegert tilgang',
            review_type_application_access: 'Applikasjonstilgang',
            auto_revoke_label: 'Auto-tilbakekall ved avvisning',
            campaigns_title: 'Få tilgang til gjennomgangskampanjer',
            create_campaign_label: '+ Opprett kampanje',
            no_campaigns_found: 'Fant ingen kampanjer',
            loading_campaigns: 'Laster inn kampanjer ...',
            no_reviews_assigned: 'Ingen anmeldelser er tildelt deg',
            loading_reviews: 'Laster anmeldelser ...',
            review_decision_title: 'Gjennomgå vedtak',
            justification_label: 'Begrunnelse',
            announce_loaded_review_items: 'Lastet inn {count} anmeldelsesartikler',
            announce_loaded_campaigns: 'Lastet inn {count} kampanjer',
            announce_decision_submitted: 'Vedtak «{decision}» sendt inn for vurdering',
            announce_decision_submitted_for: 'Avgjørelse «{decision}» sendt inn for {name}',
            announce_bulk_decision: 'Masse {decision} brukt på {count} varer',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Attesteringsplaner',
            new: 'Ny plan',
            edit: 'Rediger plan',
            create_title: 'Opprett attesteringsplan',
            edit_title: 'Rediger attesteringsplan',
            schedule_history: 'Planhistorikk',
            next_run_date: 'Neste kjøringsdato',
            schedule_name: 'Plannavn',
            frequency: 'Frekvens',
            next_run: 'Neste kjøring',
            last_run: 'Siste kjøring',
            history: 'Attesteringshistorikk',
            description: 'Beskrivelse',
            scope: 'Omfang',
            status: 'Status',
            created_at: 'Opprettet',
            updated_at: 'Oppdatert',
            trigger_now: 'Utløs nå',
            back_to_schedules: 'Tilbake til tidsplaner',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Tidsplanen ble utløst.',
            deleted: 'Tidsplan slettet.',
            saved: 'Tidsplanen er lagret.',
            campaigns_generated: 'Kampanjer generert',
            run_date: 'Kjøringsdato',
            run_status: 'Kjør status',
            create_schedule: 'Opprett tidsplan',
            view_history: 'Vis historikk',
            deactivate: 'Deaktiver',
            overdue: 'OVERDUE',
            overdue_sr: 'Tidsplanen er forsinket',
            confirm_trigger_title: 'Bekreft handling',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Bekreft deaktivering',
            confirm_deactivate_message: 'Er du sikker på at du vil deaktivere denne tidsplanen?',
            triggered_success: 'Tidsplanen ble utløst.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Tidsplan deaktivert.',
            name_label: 'Navn *',
            frequency_label: 'Frekvens *',
            scope_type_label: 'Omfangstype *',
            scope_filter_label: 'Omfangsfilter (JSON)',
            reviewer_strategy_label: 'Anmelderstrategi *',
            reviewer_id_label: 'Anmelder-ID *',
            frequency_quarterly: 'Kvartalsvis',
            frequency_semi_annual: 'Halvårlig',
            frequency_annual: 'Årlig',
            scope_all_users: 'Alle brukere',
            scope_by_role: 'Etter rolle',
            scope_by_department: 'Etter avdeling',
            scope_custom: 'Egendefinert',
            reviewer_manager: 'Manager',
            reviewer_role_owner: 'Rolleeier',
            reviewer_specific_user: 'Spesifikk bruker',
            computed_next_run_date: 'Beregnet neste kjøringsdato',
            current_scheduled_date: 'Gjeldende planlagt dato',
            recomputed_frequency_changed: '(beregnet på nytt -- frekvens endret)',
            estimated_days_hint: 'Estimert basert på {days} dager fra i dag',
            validation_name_required: 'Navn er påkrevd',
            validation_frequency_required: 'Frekvens er nødvendig',
            validation_scope_type_required: 'Omfangstype er påkrevd',
            validation_reviewer_strategy_required: 'Anmelderstrategi er nødvendig',
            validation_reviewer_id_required: 'Anmelder-ID kreves for spesifikk brukerstrategi',
            validation_invalid_json: 'Ugyldig JSON',
            validation_scope_filter_invalid_json: 'Omfangsfilteret inneholder ugyldig JSON',
            form_errors: 'Skjemaet har {count} feil(er). Rett dem opp.',
            create_schedule_btn: 'Opprett tidsplan',
            schedule_history_title: 'Tidsplan historikk',
            campaign_name_col: 'Kampanjenavn',
            items_col: 'Varer',
            no_campaigns_yet: 'Ingen kampanjer er generert ennå',
            loading_history: 'Laster inn loggen ...',
            loading_schedule: 'Laster tidsplan...',
            total_campaigns: 'Totalt antall kampanjer: {count}',
            results_per_page: 'Resultater per side',
            page_size_option: '{size} / side',
        },
        sod: {
            title: 'Funksjonsdelingsregler',
            new: 'Ny regel',
            edit: 'Rediger regel',
            create_title: 'Opprett SoD-regel',
            edit_title: 'Rediger SoD-regel',
            rule_name: 'Regelnavn',
            conflicting_permissions: 'Motstridende tillatelser',
            permission_a: 'Tillatelse A',
            permission_b: 'Tillatelse B',
            violations: 'SoD-brudd',
            violation_user: 'Bruker',
            violation_rule: 'Brutt regel',
            violation_date: 'Oppdaget',
            exceptions: 'SoD-unntak',
            exception_reason: 'Unntaksbegrunnelse',
            exception_approver: 'Godkjent av',
            exception_status: 'Unntaksstatus',
            has_exception: 'Har unntak',
            no_exception: 'Ingen unntak',
            grant_exception: 'Grant Unntak',
            granting: 'Innvilger...',
            grant_new_exception: 'Gi nytt unntak',
            exceptions_for: 'Unntak for: {name}',
            back_to_rules: 'Tilbake til Regler',
            no_violations: 'Ingen brudd funnet',
            no_exceptions_found: 'Ingen unntak funnet for denne regelen',
            all_exception_statuses: 'Alle unntaksstatuser',
            user_id_label: 'Bruker-ID',
            justification_label: 'Begrunnelse',
            compensating_controls_label: 'Kompenserende kontroller',
            expires_at_label: 'Utløper kl',
            granting_exception_for: 'Gir unntak for regel: {name}',
            severity: 'Alvorlighetsgrad',
            severity_warning: 'Advarsel',
            severity_blocking: 'Blokkering',
            violation_count: '{count} brudd(er)',
            exception_count: '{count} unntak(er)',
            rule_description: 'Beskrivelse',
            confirm_delete: 'Er du sikker på at du vil slette denne regelen?',
            deleted: 'Regel slettet.',
            framework_code: 'Regelverk',
            name_label: 'Navn *',
            severity_label: 'Alvorlighetsgrad *',
            conflict_pairs_legend: 'Konflikttillatelsespar',
            conflict_pairs_hint: 'Hvert par definerer to tillatelser som ikke skal innehas av samme bruker.',
            placeholder_permission_a: '-- Tillatelse A --',
            placeholder_permission_b: '-- Tillatelse B --',
            permission_a_for_pair: 'Tillatelse A for par {index}',
            permission_b_for_pair: 'Tillatelse B for paret {index}',
            conflict_pair_label: 'Konfliktpar {index}',
            remove_conflict_pair: 'Fjern konfliktpar {index}',
            add_pair: '+ Legg til par',
            placeholder_name: 'f.eks. Betalingsgodkjenningsseparasjon',
            create_rule: 'Opprett regel',
            loading_rule: 'Laster inn SoD-regel ...',
            failed_load_rule: 'Kunne ikke laste inn regelen.',
            failed_create_rule: 'Kunne ikke opprette SoD-regel.',
            failed_update_rule: 'Kunne ikke oppdatere SoD-regelen.',
            validation_name_required: 'Navn er påkrevd.',
            validation_pair_required: 'Minst ett konflikttillatelsespar kreves.',
            validation_pair_both_required: 'Par {index}: Begge tillatelsene må velges.',
            validation_pair_must_differ: 'Par {index}: Tillatelse A og Tillatelse B må være forskjellige.',
            added_conflict_pair: 'Lagt til konfliktpar {index}',
            removed_conflict_pair: 'Fjernet konfliktpar {index}',
            announce_loaded_violations: 'Lastet {count} brudd',
            announce_loaded_exceptions: 'Lastet inn {count} unntak',
            announce_loaded_rules: 'Lastet {count} SoD-regler',
            announce_loading_exceptions: 'Laster inn unntak …',
            regulatory_info: {
                sod_context: 'SoD-regler håndhever det regulatoriske rolleskillet som kreves av PDPL. Brudd knyttet til en framework_code kan inkluderes i rammeverksspesifikke bevispakker (UC-VN-09).',
                violation_export_context: 'SoD-bruddeksporter inkluderer framework_code, motstridende roller, alvorlighetsgrad, opprettet av og registreringstidspunkt for evidenspakker (P-VN-10).',
                exception_context: 'SoD-unntak produserer revisjonsdokumentasjon: godkjenner, begrunnelse, kompenserende kontroller og utløpsdato. Utløpte unntak forblir synlige (UC-VN-09 steg 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API-nøkkeltelemetri utgjør konto­dokumentasjon etter VN_CYBERSEC_LAW_2025 avsnitt B.5. Hver nøkkelpost inneholder last_used_at, ip_allowlist, permissions, status og expires_at.',
            session_context: 'Øktstyring håndhever tilgangskontroller i tråd med konfidensialitetskravene i telekomloven (avsnitt B.4). Økthendelser oppretter poster i Audit Trail.',
            break_glass_context: 'Nødtilgangsprosedyrer produserer regulatorisk begrunnelsesdokumentasjon: identiteten til den som ber om tilgang, begrunnelse, tillatelser, varighet, identiteten til godkjenneren og tidsstempler for godkjenning eller avslag.',
            license_context: 'Lisensinnstillingene styrer hvilke moduler som er tilgjengelige per nivå gjennom PACKAGE_DEFINITIONS. Rammeverksspesifikke moduler krever riktig lisensnivå. Nivåendringer oppretter poster i Audit Trail.',
            module_config_context: 'Modulkonfigurasjon støtter aktivering styrt av rammeverk. Hvis du deaktiverer en modul som kreves av et aktivt regulatorisk rammeverk, kan det svekke fullstendigheten i dokumentasjonen.',
            notification_context: 'Varsler som er kritiske for etterlevelse, som frister for bruddvarsling og forfallsdatoer for attestasjoner, merkes med compliance_critical og kan ikke undertrykkes fullt ut.',
            delegation_context: 'Styring av delegering produserer dokumentasjon på revisjonsnivå: delegator_id, delegate_id, tillatelsesomfang, start- og sluttdatoer samt status. Utløpte delegeringer forblir synlige.',
            access_control_context: 'Konfigurasjonen av tilgangskontroll oppfyller kravene i cybersikkerhetsloven (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, tjenestekontoer og SSO-endringer spores i Audit Trail.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP-integrasjon',
            status: 'Tilkoblingsstatus',
            endpoint: 'CMP-endepunkt',
            last_sync: 'Siste synkronisering',
            configure: 'Konfigurer',
        },
        purpose_mapping: {
            title: 'Formålskobling',
            cmp_purpose: 'CMP-formål',
            grc_purpose: 'GRC-formål',
            mapping_status: 'Koblingsstatus',
            auto_map: 'Automatisk kobling',
            new_mapping: 'Ny kobling',
        },
        webhook: {
            title: 'Webhook-konfigurasjon',
            webhook_url: 'Webhook-URL',
            secret: 'Webhook-hemmelighet',
            events: 'Hendelsestyper',
            test_webhook: 'Test webhook',
        },
        bulk_sync: {
            title: 'Massesynkronisering',
            sync_now: 'Synkroniser nå',
            last_run: 'Siste kjøring',
            records_synced: 'Synkroniserte oppføringer',
            sync_history: 'Synkroniseringshistorikk',
            syncing: 'Synkroniserer...',
        },
        sync_settings: {
            sync_failures: 'Synkronisering Feil',
            sync_secret_warning: 'Synkroniseringshemmelighet (lagre denne nå — den vises ikke igjen):',
            push_description: 'Konfigurer GRC Suite til å sende saksstatusoppdateringer til AesirX CMP.',
            technical_description: 'Denne konfigurasjonen kontrollerer hvordan GRC Suite sender saksstatusoppdateringer til AesirX CMP.',
            direction_label: 'Retning',
            direction_value: 'GRC Suite → AesirX CMP (utgående)',
            auth_label: 'Autentisering',
            auth_value: 'HMAC-SHA256-signatur i X-GRC-Signature-header',
            retry_label: 'Gjenforsøk Retningslinje',
            retry_value: '3 gjenforsøk med eksponentiell ventetid (30s, 2min, 10min)',
            inbound_label: 'Innkommende Konfigurasjon',
            inbound_value: 'Innkommende webhook (CMP → GRC) er konfigurert i',
            cmp_webhook_settings: 'CMP Webhook Innstillinger',
            page_title: 'CMP Synkroniseringsinnstillinger',
            outbound_sync_configured: 'Utgående synkronisering konfigurert.',
            configure_sync_failed: 'Kunne ikke konfigurere synkronisering',
            configuration_updated: 'Konfigurasjon oppdatert.',
            update_configuration_failed: 'Kunne ikke oppdatere konfigurasjon',
            connection_test_failed: 'Tilkoblingstest mislyktes',
            test_result_message: 'Test sendt til {endpoint}. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Ny → Under gjennomgang (tildelt)',
                under_review_to_identity_verification: 'Under gjennomgang → Identitetsverifisering',
                under_review_to_in_progress: 'Under gjennomgang → I gang',
                in_progress_to_completed: 'I gang → Fullført',
                in_progress_to_denied: 'I gang → Avvist',
                any_to_closed: 'Alle → Lukket',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhooker',
            new: 'Ny webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Hendelser',
            secret: 'Hemmelighet',
            active: 'Aktiv',
            test: 'Test',
            create_failed: 'Kunne ikke opprette webhook.',
            create_success: 'Webhook opprettet.',
            deactivate_failed: 'Kunne ikke deaktivere webhook.',
            deactivate_success: 'Webhook deaktivert.',
            fetch_failed: 'Kunne ikke hente webhooks.',
            send_test_failed: 'Kunne ikke sende test.',
            test_failed: 'Webhook-test mislyktes.',
            test_success: 'Webhook-test fullført.',
            edit_title: 'Rediger webhook',
            announce_loaded: 'Webhooker lastet',
            update_success: 'Webhook oppdatert.',
            update_failed: 'Kunne ikke oppdatere webhook.',
            activate_success: 'Webhook aktivert.',
            activate_failed: 'Kunne ikke aktivere webhook.',
        },
        domains: {
            title: 'Domener',
            new: 'Nytt domene',
            domain_name: 'Domenenavn',
            verified: 'Verifisert',
            verify: 'Verifiser',
            add_failed: 'Kunne ikke legge til domene.',
            add_success: 'Domene lagt til.',
            domain_placeholder: 'f.eks. grc.example.com',
            fetch_failed: 'Kunne ikke hente domener.',
            remove_failed: 'Kunne ikke fjerne domene.',
            remove_success: 'Domene fjernet.',
            txt_record: 'TXT Post',
            verify_dns_failed: 'DNS-verifisering mislyktes.',
            verify_failed: 'Kunne ikke verifisere domene.',
            verify_success: 'Domene verifisert.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Eksportplaner',
            new: 'Ny plan',
            schedule_name: 'Navn',
            frequency: 'Frekvens',
            format: 'Format',
            last_run: 'Siste kjøring',
            next_run: 'Neste kjøring',
            create_failed: 'Kunne ikke opprette tidsplan.',
            create_success: 'Tidsplan opprettet.',
            day_of_month: 'Dag i måneden',
            day_of_week: 'Dag av Uke',
            deactivate_failed: 'Kunne ikke deaktivere tidsplan.',
            deactivate_success: 'Tidsplan deaktivert.',
            fetch_failed: 'Kunne ikke hente tidsplaner.',
            freq_daily: 'Daglig',
            freq_monthly: 'Månedlig',
            freq_weekly: 'Ukentlig',
            recipients: 'Mottakere',
            time_utc: 'Tid (UTC)',
            trigger_failed: 'Kunne ikke utløse eksport.',
            trigger_success: 'Eksport udløst.',
            type_audit_trail: 'Revisjon Spor',
            type_compliance_report: 'Etterlevelse Rapport',
            type_custom: 'Tilpasset',
            type_incident_report: 'Hendelse Rapport',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'E-postlogg',
            recipient: 'Mottaker',
            subject: 'Emne',
            sent_at: 'Sendt',
            status: 'Status',
            resend: 'Send på nytt',
            fetch_failed: 'Kunne ikke hente e-postlogger.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisasjoner',
        create: 'Opprett Organisasjon',
        edit: 'Rediger Organisasjon',
        name: 'Navn',
        slug: 'Snegl',
        timezone: 'Tidssone',
        locale: 'Standard Lokalitet',
        parent: 'Overordnet Organisasjon',
        group: 'Gruppe',
        org_group: 'Organisasjonsgruppe',
        no_group: '(Ingen - ikke i en gruppe)',
        created: 'Opprettet',
        no_parent: '(Ingen - frittstående)',
        no_orgs: 'Ingen organisasjoner funnet',
        load_error: 'Kunne ikke laste organisasjoner',
        save_error: 'Kunne ikke lagre organisasjon',
        created_msg: 'Organisasjon opprettet',
        updated: 'Organisasjon oppdatert',
        deactivate: 'Deaktiver',
        deactivated: 'Organisasjon deaktivert',
        confirm_deactivate: 'Er du sikker på at du vil deaktivere denne organisasjonen?',
        loaded_results: 'Organizations lastet, {count} resultater',
        form_loaded: 'Organization-skjema lastet',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Engelsk (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Alle rammeverk',
        assigned_at: 'Tildelt den',
    },
    intake_configs: {
        title: 'Offentlig Inntak Konfigurasjoner',
        create: 'Opprett Konfiguration',
        edit: 'Rediger Inntaks Konfiguration',
        label_col: 'Etikett',
        key: 'Inntak Nøkkel',
        key_hint: 'Offentlig URL vil være: /rights-request?key=',
        mapped_org: 'Mål Organisasjon',
        status: 'Status',
        enabled: 'Aktivert',
        disabled: 'Deaktivert',
        request_types: 'Aktivert Forespørsel Typer',
        language: 'Standard Språk',
        public_url: 'Offentlig URL',
        no_configs: 'Ingen inntakskonfigurasjoner funnet. Opprett en for å aktivere offentlige rettighetsforespørselsskjemaer.',
        load_error: 'Kunne ikke laste inntakskonfigurasjoner',
        save_error: 'Kunne ikke lagre',
        delete_error: 'Kunne ikke slette',
        created_msg: 'Konfigurasjon opprettet',
        updated: 'Konfigurasjon oppdatert',
        deleted: 'Indtagskonfigurasjon slettet',
        confirm_delete: 'Slett denne indtagskonfigurasjon?',
        select_types: 'Velg mindst én anmodningstype',
        select_org: 'Velg a mål organisasjon',
        routing_mode: 'Rutemodus',
        single_org: 'Enkelt Organisasjon',
        org_group_mode: 'Organisasjonsgruppe',
        org_group: 'Organisasjonsgruppe',
        select_group: 'Velg en organisasjonsgruppe...',
        group_root_hint: 'Settes automatisk til gruppens rotorganisasjon når gruppen velges.',
        loaded_results: 'Intake configurations lastet, {count} resultater',
        form_loaded: 'Intake configuration-skjema lastet',
    },
    frequency: {
        weekly: 'Ukentlig',
        monthly: 'Månedlig',
        quarterly: 'Kvartalsvis',
        semi_annually: 'Halvårlig',
        annually: 'Årlig',
        custom: 'Tilpasset',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Kalender',
            all_tasks: 'Alle oppgaver',
            my_tasks: 'Mine oppgaver',
            overdue: 'Forsinkede',
            board: 'Tavle',
            workflows: 'Arbeidsflyter',
        },
        list: {
            title: 'Alle oppgaver',
            my_title: 'Mine oppgaver',
            overdue_title: 'Forsinkede oppgaver',
            new_task: 'Opprett oppgave',
            no_tasks: 'Ingen oppgaver funnet.',
            empty_state: 'Ingen oppgaver funnet. Oppgaver genereres fra modularbeidsflyter som vurderinger, hendelser og gjennomgangskampanjer.',
            total: 'Totale oppgaver',
            search_placeholder: 'Søk oppgaver...',
            bulk_actions: 'Massehandlinger',
            change_status: 'Endre status',
            change_priority: 'Endre prioritet',
            assign: 'Tildel',
            sr_my_tasks_count: '{count} oppgaver tildelt deg',
            sr_days_overdue: '{count} dager forsinket',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Oppgavedetaljer',
            description: 'Beskrivelse',
            workflow_progress: 'Arbeidsflytframgang',
            checklist: 'Sjekkliste',
            dependencies: 'Avhengigheter',
            comments: 'Kommentarer',
            activity_timeline: 'Aktivitetstidslinje',
            assignees: 'Tildelte',
            add_assignee: 'Legg til tildelt',
            remove_assignee: 'Fjern tildelt',
            due_date: 'Forfallsdato',
            start_date: 'Startdato',
            sla_status: 'SLA-status',
            sla_on_track: 'I rute',
            sla_at_risk: 'I fare',
            sla_breached: 'Brutt',
            tags: 'Tagger',
            source_entity: 'Kildeenhet',
            view_source: 'Vis kilde',
            add_dependency: 'Legg til avhengighet',
            remove_dependency: 'Fjern avhengighet',
            write_comment: 'Skriv en kommentar...',
            post_comment: 'Send',
            edit_comment: 'Rediger',
            delete_comment: 'Slett',
            add_checklist_item: 'Legg til element',
            no_dependencies: 'Ingen avhengigheter',
            no_comments: 'Ingen kommentarer ennå',
            blocks: 'Blokkerer',
            blocked_by: 'Blokkert av',
            related: 'Relatert',
            working_days_remain: 'arbeidsdager igjen',
            created_by: 'Opprettet av',
            completed_at: 'Fullført den',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Ingen tildelte',
            add_assignee_placeholder: 'Skriv inn bruker-ID...',
            edited: 'redigert',
            timeline: 'Aktivitetstidslinje',
            no_timeline: 'Ingen aktivitet ennå',
            timeline_reason: 'Reason',
            no_workflow: 'Ingen arbeidsflyt tildelt',
            add_dependency_placeholder: 'Skriv inn oppgave-ID...',
            sr_status_updated: 'Status oppdatert til {status}',
            sr_priority_updated: 'Prioritet oppdatert til {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Kjør som arbeidsflyt',
            convert_modal_title: 'Konverter oppgave til arbeidsflytkjøring',
            convert_workflow_label: 'Arbeidsflyt',
            convert_workflow_placeholder: 'Velg en arbeidsflyt...',
            convert_attachments_label: 'Vedlegg',
            convert_attachments_empty: 'Ingen vedlegg til denne oppgaven.',
            convert_persona_label: 'Personaoverstyring (valgfritt)',
            convert_submit: 'Start arbeidsflytkjøring',
            convert_starting: 'Starter arbeidsflytkjøring …',
            convert_started: 'Arbeidsflytkjøring startet — åpningskjøring...',
            convert_disabled_in_flight: 'En arbeidsflyt som kjøres fra denne oppgaven er allerede i gang.',
            linked_workflow_chip: 'Fra {workflow_title}',
            linked_chat_chip: 'Fra AI Chat',
            linked_more: '+{count} til',
        },
        calendar: {
            title: 'Oppgavekalender',
            page_title: 'Kalender',
            create_task: 'Opprett oppgave',
            month: 'Måned',
            week: 'Uke',
            day: 'Dag',
            today: 'I dag',
            more_tasks: '+{count} til',
            no_tasks_day: 'Ingen oppgaver denne dagen',
            all_day: 'Hele dagen',
            quick_create_tooltip: 'Opprett oppgave for denne dagen',
            view_changed: 'Kalendervisning endret til {view}',
            tasks_in_view: '{count} oppgaver i visning',
            task_in_view: '{count} oppgave i sikte',
            filter_my_tasks: 'Mine oppgaver',
            filter_overdue: 'Forfalt',
            filter_high_priority: 'Høy prioritet',
            filter_all: 'Alle',
        },
        filters: {
            assignee: 'Tildelt',
            me: 'Meg',
            role: 'Rolle',
            status: 'Status',
            priority: 'Prioritet',
            task_type: 'Oppgavetype',
            source_module: 'Kildemodul',
            organization: 'Organisasjon',
            sla_breached_only: 'Vis kun SLA-brudd',
            clear_filters: 'Fjern filtre',
            apply_filters: 'Bruk',
            source_label: 'Kilde',
            source_manual: 'Manuell',
            source_module_kind: 'Modulgenerert',
            source_chat: 'Fra AI Chat',
            source_workflow_run: 'Fra AI arbeidsflytkjøring',
        },
        status: {
            pending: 'Ventende',
            in_progress: 'Pågår',
            blocked: 'Blokkert',
            completed: 'Fullført',
            overdue: 'Forsinkede',
            escalated: 'Eskalert',
            cancelled: 'Avbrutt',
        },
        priority: {
            critical: 'Kritisk',
            high: 'Høy',
            medium: 'Middels',
            low: 'Lav',
        },
        task_types: {
            manual_task: 'Manuell oppgave',
            dsr_case: 'DSR-sak',
            dsr_review: 'DSR-gjennomgang',
            dsr_escalation: 'DSR-eskalering',
            dpia_review: 'DPIA-gjennomgang',
            dpia_approval: 'DPIA-godkjenning',
            transfer_review: 'Overføringsgjennomgang',
            incident_task: 'Hendelsesoppgave',
            incident_notification: 'Hendelsesvarsling',
            playbook_step: 'Spilleboktrinn',
            regulator_deadline: 'Myndighetsfrist',
            monitoring_task: 'Overvåkingsoppgave',
            kpi_alert: 'KPI-varsel',
            vendor_review: 'Leverandørgjennomgang',
            vendor_questionnaire: 'Leverandørspørreskjema',
            governance_review: 'Styringsgjennomgang',
            drift_alert: 'Avviksvarsel',
            governance_approval: 'Styringsgodkjenning',
            deletion_task: 'Sletteoppgave',
            deletion_verification: 'Sletteverifisering',
            access_review: 'Tilgangsgjennomgang',
            sod_alert: 'SoD-varsel',
            consent_workflow: 'Samtykkearbeidsflyt',
            consent_alert: 'Samtykkevarsel',
            property_review: 'Egenskapsgjennomgang',
            inconsistency_alert: 'Inkonsekvensvarsel',
            form_review: 'Skjemagjennomgang',
            dossier_deadline: 'Dossierfrist',
            submission_task: 'Innsendingsoppgave',
            translation_review: 'Oversettelsesgjennomgang',
            governance_task: 'Styringsoppgave',
            reporting_deadline: 'Rapporteringsfrist',
            scanner_alert: 'Skannervarsel',
            classification_review: 'Klassifiseringsgjennomgang',
            lifecycle_task: 'Livssyklusoppgave',
            discovery_review: 'Oppdagelsesgjennomgang',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Opplæringstildeling',
        task_type_groups: {
            training: 'Opplæring',
            manual: 'Manuell',
            dsr: 'DSR / Rettighetsforespørsler',
            assessments: 'Vurderinger',
            incidents: 'Hendelser',
            incident_ops: 'Hendelsesdrift',
            monitoring: 'Overvåking',
            vendor_risk: 'Leverandørrisiko',
            governance: 'Styring',
            deletion: 'Sletting',
            access_review: 'Tilgangsgjennomgang',
            consent: 'Samtykke',
            consent_properties: 'Samtykkeegenskaper',
            forms: 'Skjemaer',
            localization: 'Lokalisering',
            program_governance: 'Programstyring',
            privacy_scanner: 'Personvernskanner',
            classification: 'Klassifisering',
            discovery: 'Oppdagelse',
            forseti: 'Forseti',
        },
        create: {
            title: 'Opprett oppgave',
            task_title: 'Tittel',
            task_description: 'Beskrivelse',
            task_type: 'Oppgavetype',
            task_type_label: 'Oppgavetype',
            task_type_placeholder: 'Velg en oppgavetype',
            priority: 'Prioritet',
            assignees: 'Tildelte',
            assignee_search_placeholder: 'Søk brukere...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Fjern {name}',
            due_date: 'Forfallsdato',
            start_date: 'Startdato',
            checklist: 'Sjekkliste',
            tags: 'Tagger',
            add_checklist_item: 'Legg til sjekklisteelement',
            validation: {
                title_required: 'Tittel er påkrevd',
                title_max_length: 'Tittel må være 500 tegn eller færre',
                description_max_length: 'Beskrivelse må være 5000 tegn eller færre',
                due_date_before_start: 'Forfallsdato må være etter startdato',
                max_reminders: 'Maksimalt 5 påminnelser per oppgave',
            },
            reminders: {
                title: 'Påminnelser',
                add: 'Legg til påminnelse',
                at_due_date: 'Ved forfallsdato',
                minutes_before: '{count} minutter før',
                hours_before: '{count} timer før',
                days_before: '{count} dager før',
                weeks_before: '{count} uker før',
                custom: 'Tilpasset',
                channel_platform: 'Plattform',
                channel_email: 'E-post',
                channel_both: 'Begge',
                offset_label: 'Påminnelse {n}: tidspunkt',
                channel_label: 'Påminnelse {n}: kanal',
                remove_label: 'Fjern påminnelse {n}',
            },
            workflow: {
                label: 'Arbeidsflyt',
                none: 'Ingen (manuell statusadministrasjon)',
                steps_count: '{count} trinn',
                preview_label: 'Arbeidsflyttrinn',
            },
        },
        errors: {
            fetch_failed: 'Kunne ikke laste oppgaver.',
            save_failed: 'Kunne ikke lagre oppgave.',
            delete_failed: 'Kunne ikke slette oppgave.',
            status_change_failed: 'Kunne ikke endre oppgavestatus.',
            assign_failed: 'Kunne ikke tildele oppgave.',
            comment_failed: 'Kunne ikke lagre kommentar.',
            checklist_failed: 'Kunne ikke oppdatere sjekkliste.',
            dependency_failed: 'Kunne ikke oppdatere avhengighet.',
            workflow_failed: 'Kunne ikke lagre arbeidsflyt.',
            workflow_activate_failed: 'Kunne ikke aktivere arbeidsflyt.',
            workflow_delete_failed: 'Kunne ikke slette arbeidsflyt.',
            template_install_failed: 'Kunne ikke installere mal.',
            search_failed: 'Kunne ikke søke oppgaver.',
            convert_failed: 'Kunne ikke starte arbeidsflytkjøringen.',
            convert_quota_exhausted: 'Token bøtte lav. Fyll på før du kjører på nytt.',
        },
        dependency_types: {
            blocks: 'Blokkerer',
            blocked_by: 'Blokkert av',
            related: 'Relatert',
        },
        workflow: {
            current_step: 'Nåværende trinn',
            transition: 'Overgang',
            history: 'Trinnhistorikk',
            no_workflow: 'Ingen arbeidsflyt tildelt',
            detail_title: 'Arbeidsflytdetaljer',
            list_title: 'Arbeidsflytdefinisjoner',
            create_workflow: 'Opprett arbeidsflyt',
            browse_templates: 'Bla i maler',
            template_catalog_title: 'Arbeidsflytmalkatalog',
            name: 'Navn',
            description: 'Beskrivelse',
            task_types: 'Oppgavetyper',
            steps: 'Trinn',
            version: 'Versjon',
            created: 'Opprettet',
            actions: 'Handlinger',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_archived: 'Arkivert',
            activate: 'Aktiver',
            archive: 'Arkiver',
            save: 'Lagre',
            edit: 'Rediger',
            delete: 'Slett',
            clone: 'Klon',
            install: 'Installer',
            installed: 'Installert',
            system: 'System',
            general_settings: 'Generelle innstillinger',
            default_assignment: 'Standardtildeling',
            default_sla: 'Standard SLA',
            workflow_steps: 'Arbeidsflyttrinn',
            add_step: 'Legg til trinn',
            step_name: 'Trinnnavn',
            step_status: 'Statusverdi',
            assignment_strategy: 'Tildelingsstrategi',
            assignment_role: 'Tildelingsrolle',
            sla_hours: 'SLA-timer',
            sla_calendar_type: 'Kalendertype',
            working_days: 'Arbeidsdager',
            calendar_days: 'Kalenderdager',
            is_terminal: 'Terminaltrinn',
            required_fields: 'Påkrevde felt',
            checklist_template: 'Sjekklistemal',
            transitions: 'Overganger',
            add_transition: 'Legg til overgang',
            from_step: 'Fra',
            to_step: 'Til',
            permission: 'Tillatelse',
            sla_action: 'SLA-handling',
            notify: 'Varsle',
            escalation_rules: 'Eskaleringsregler',
            add_rule: 'Legg til regel',
            trigger: 'Utløser',
            threshold: 'Terskel',
            action: 'Handling',
            target_role: 'Målrolle',
            trigger_sla_threshold: 'SLA-terskel',
            trigger_sla_breach: 'SLA-brudd',
            trigger_blocked_duration: 'Blokkert varighet',
            trigger_manual: 'Manuell',
            action_notify: 'Varsle',
            action_escalate: 'Eskaler nivå',
            action_reassign: 'Tildel på nytt',
            sla_start: 'Start',
            sla_pause: 'Pause',
            sla_resume: 'Gjenoppta',
            sla_stop: 'Stopp',
            strategy_manual: 'Manuell',
            strategy_role_based: 'Rollebasert',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Minst belastet',
            strategy_creator: 'Oppretter',
            confirm_delete: 'Er du sikker på at du vil slette denne arbeidsflyten?',
            confirm_archive: 'Er du sikker på at du vil arkivere denne arbeidsflyten?',
            cannot_delete_active: 'Aktive arbeidsflyter kan ikke slettes. Arkiver først.',
            validation_errors: 'Valideringsfeil i arbeidsflyt',
            step_count: '{count} trinn',
            no_workflows: 'Ingen arbeidsflyter funnet.',
            no_templates: 'Ingen maler tilgjengelig.',
            sr_templates_count: '{count} maler tilgjengelig',
            template_installed: 'Mal installert.',
            activate_success: 'Arbeidsflyt aktivert.',
            archive_success: 'Arbeidsflyt arkivert.',
            save_success: 'Arbeidsflyt lagret.',
            delete_success: 'Arbeidsflyt slettet.',
            applicable_task_types: 'Aktuelle oppgavetyper',
            steps_column: 'Trinn',
            status_value_hint: 'Arbeidsflytens tilstandsnøkkel som overganger bruker. Små bokstaver, tall og understreker.',
            from_status: 'Fra-status',
            to_status: 'Til-status',
            required_permissions: 'Nødvendige tillatelser',
            transition_required_fields: 'Obligatoriske felt (overgang)',
            auto_assign: 'Automatisk tildeling',
            auto_assign_role: 'Rolle for automatisk tildeling',
            threshold_percent: 'Terskel (%)',
            blocked_hours: 'Blokkerte timer',
            notify_roles: 'Varsle roller',
            escalate_to_role: 'Eskaler til rolle',
            notification_template: 'Varselmal',
            action_escalate_tier: 'Eskaler nivå',
            create_modal_title: 'Opprett arbeidsflyt',
            create_modal_description: 'Definer trinn, overganger og eskaleringsregler. Arbeidsflyten opprettes som utkast.',
            create_modal_footer_note: 'Opprettes som utkast. Aktiver den når grafen er gyldig.',
            validation_name_required: 'Navn på arbeidsflyt er påkrevd.',
            validation_task_types_required: 'Velg minst én aktuell oppgavetype.',
            validation_min_one_step: 'Legg til minst ett trinn.',
            validation_min_one_transition: 'Legg til minst én overgang.',
            validation_step_name_required: 'Trinnnavn er påkrevd.',
            validation_status_value_required: 'Statusverdi er påkrevd.',
            validation_status_value_duplicate: 'Statusverdien må være unik på tvers av trinn.',
            validation_transition_endpoints_required: 'Velg både en fra-status og en til-status.',
            validation_transition_unknown_status: 'Denne overgangen refererer til en status som ingen trinn definerer.',
            validation_sla_positive: 'SLA-timer må være større enn null. La stå tomt for ingen SLA.',
            validation_threshold_percent_range: 'Terskelen må være mellom 0 og 100.',
            validation_blocked_hours_positive: 'Blokkerte timer må være større enn null.',
            warning_no_terminal_step: 'Ingen sluttrinn. Aktivering vil mislykkes til ett trinn er merket som slutt.',
            warning_step_no_outgoing: 'Ikke-sluttrinn uten utgående overganger: {statuses}',
            warning_unreachable_steps: 'Trinn som ikke kan nås fra første trinn: {statuses}',
            orphaned_transitions_warning: '{count} overganger refererer til et slettet trinn.',
            remove_orphaned_transitions: 'Fjern foreldreløse overganger',
            transitions_renamed_announcement: '{count} overganger oppdatert til den nye statusverdien.',
            add_checklist_item: 'Legg til sjekklisteelement',
            checklist_item_label: 'Sjekklisteelement',
        },
        columns: {
            priority: 'Prioritet',
            title: 'Tittel',
            status: 'Status',
            type: 'Type',
            assignees: 'Tildelt(e)',
            due_date: 'Forfallsdato',
            source: 'Kilde',
            actions: 'Handlinger',
        },
        groups: {
            overdue: 'Forsinkede',
            due_today: 'Forfaller i dag',
            due_this_week: 'Forfaller denne uken',
            due_later: 'Forfaller senere',
            no_due_date: 'Ingen forfallsdato',
            closed: 'Fullført eller avbrutt',
        },
        board: {
            title: 'Oppgavetavle',
            drag_hint: 'Dra oppgaver mellom kolonner for å endre status',
            empty_column: 'Ingen oppgaver',
            keyboard_hint: 'Tastatur: piltaster for å navigere, mellomrom for å plukke opp en oppgave, pil venstre/høyre for å velge kolonne, Enter for å slippe, Escape for å avbryte.',
            sr_moving: 'Flytter {title}. Over {column}. Trykk Enter for å slippe, Escape for å avbryte.',
            sr_tasks_shown: '{count} oppgaver vist',
            sr_picked_up: '{title} plukket opp',
            sr_returned: '{title} returnerte til {column}',
            sr_dropped: '{title} sluppet i {column}, posisjon {position} av {total}',
            sr_move_cancelled: 'Flytting avbrutt',
            sr_over_column: 'Over {column}',
            sr_moved_to: '{title} flyttet til {column}',
            invalid_transition: 'Kan ikke flytte oppgave fra {from} til {to}.',
            terminal_status: 'Denne oppgaven har en avsluttet status og kan ikke flyttes.',
            invalid_drop_target: 'Ugyldig slippmal',
        },
        team: {
            title: 'Teamarbeidsbelastning',
            assignee: 'Tildelt',
            active: 'Aktiv',
            overdue: 'Forsinkede',
            blocked: 'Blokkert',
            completed_7d: 'Fullført (7d)',
            load: 'Belastning',
            load_high: 'Høy',
            load_medium: 'Middels',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Avhengighetsgraf',
            list_view: 'Listevisning',
            graph_view: 'Grafvisning',
            zoom_in: 'Zoom inn',
            zoom_out: 'Zoom ut',
            reset_zoom: 'Tilbakestill',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Ingen avhengigheter å visualisere.',
        },
        add_dependency_modal: {
            title: 'Legg til avhengighet',
            search_placeholder: 'Søk i oppgaver etter tittel eller ID...',
            no_results: 'Ingen oppgaver funnet.',
            this_blocks: 'Denne oppgaven blokkerer valgt',
            this_blocked_by: 'Denne oppgaven er blokkert av valgt',
            related: 'Relatert',
            max_dependencies: 'Maks 10 avhengigheter per oppgave.',
            circular_error: 'Kan ikke legge til: vil skape en sirkulær avhengighet.',
        },
        conflict_detection: {
            title: 'Konfliktdeteksjon',
            unresolved: 'uløst',
            no_conflicts: 'Ingen konflikter oppdaget',
            section: 'Seksjon',
            field: 'Felt',
            dept_a: 'Avdeling A',
            value_a: 'Verdi A',
            dept_b: 'Avdeling B',
            value_b: 'Verdi B',
            severity: 'Alvorlighetsgrad',
            status: 'Status',
            resolved: 'Løst',
            open: 'Åpen',
            resolve: 'Løs',
            load_failed: 'Kunne ikke load conflicts',
            resolve_success: 'Konflikt løst',
            resolve_failed: 'Kunne ikke resolve conflict',
            severity_critical: 'Kritisk',
            severity_warning: 'Advarsel',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Konsolideringsfremdrift',
            add_child: 'Legg til underoppgave',
            child_task_id: 'Underoppgave-ID',
            department_id: 'Avdelings-ID',
            link: 'Koble',
            overall_completion: 'Samlet fullføring',
            departments_complete: 'Avdelinger fullført',
            overdue: 'Forfalt',
            ready: 'Klar',
            not_ready: 'Ikke klar',
            master_status: 'Overordnet status',
            department_breakdown: 'Avdelingsfordeling',
            department: 'Avdeling',
            status: 'Status',
            completion: 'Fullføring',
            due_date: 'Forfallsdato',
            overdue_flag: 'Forfalt',
            child_tasks: 'Underoppgaver',
            task: 'Oppgave',
            load_failed: 'Kunne ikke load consolidation data',
            child_linked: 'Underoppgave koblet',
            link_failed: 'Kunne ikke link child task',
            confirm_unlink: 'Er du sikker på at du vil fjerne tilknytningen til denne underordnede oppgaven?',
            child_unlinked: 'Underordnet oppgave frakoblet',
            unlink_failed: 'Kunne ikke koble fra underordnet oppgave',
            unlink: 'Fjern tilknytningen',
            status_completed: 'Fullført',
            status_in_progress: 'Pågår',
            status_pending: 'Venter',
            select_child_task: 'Velg underoppgave',
            select_department: 'Velg avdeling',
        },
        section_assignments: {
            title: 'Seksjonstildelinger',
            assign_section: 'Tildel seksjon',
            section_ref: 'Seksjonsref.',
            section_label: 'Seksjonsetikett',
            department_id: 'Avdelings-ID',
            department: 'Avdeling',
            assignee_id: 'Tildeltes-ID',
            assignee: 'Tildelt',
            mandatory: 'Obligatorisk',
            assign: 'Tildel',
            no_assignments: 'Ingen seksjonstildelinger',
            status: 'Status',
            completion: 'Fullføring',
            mark_complete: 'Fullfør',
            load_failed: 'Kunne ikke load section assignments',
            assign_success: 'Seksjon tildelt',
            assign_failed: 'Kunne ikke assign section',
            status_updated: 'Status oppdatert',
            status_update_failed: 'Kunne ikke oppdatere status',
            section_ref_placeholder: 'f.eks. 3.2',
            status_completed: 'Fullført',
            status_in_progress: 'Pågår',
            status_pending: 'Venter',
            select_department: 'Velg avdeling',
            select_assignee: 'Velg tildelt',
        },
        overdue_columns: {
            days_overdue: 'Dager forsinket',
        },
        regulatory_info: {
            deadline_context: 'Ved opprettelse av DPIA-oppgaver bor forfallsdatoer samsvare med PDPL-innleveringsfrister: avdelingsinput = 10 arbeidsdager. Cybersikkerhets- og telekomfrister gjelder ogsa.',
            overdue_deadline_context: 'Regulatoriske frister fra pakkedata: PDPL avdelingsinput = 10 arbeidsdager, tilleggssvar = 15 arbeidsdager, ny innsending = 10 arbeidsdager. SLA-brudd markeres visuelt.',
            section_assignment_context: 'DPIA fleravdelingsarbeidsflyt (PDPL-P1): DPO tildeler dossierseksjoner til avdelinger. Avdelingsinputfrist: 10 arbeidsdager.',
            template_catalog_context: 'Systemmaler dekker rammeverksspesifikke prosesser: PDPL DPIA, datalovattestering, cybersikkerhetsrespons, telekommyndighetsforesporselr og AI-registrering. Systemmaler kan ikke redigeres.',
            workflow_instantiation_context: 'Installerte maler kan instantieres til aktive arbeidsflyter. Oppgaver opprettes automatisk med beregnede forfallsdatoer.',
        },
        overdue: {
            days_overdue: 'dager forsinket',
        },
    },
    notifications: {
        bell: {
            title: 'Varsler',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Varsler',
            mark_all_read: 'Merk alle som lest',
            no_notifications: 'Ingen varsler',
            filter: {
                all: 'All',
                unread: 'Ulest',
                tasks: 'Oppgaver',
                approvals: 'Godkjenninger',
                alerts: 'Varsler',
            },
        },
        preferences: {
            title: 'Varslingsinnstillinger',
            description: 'Konfigurer hvordan du mottar varsler',
            category: {
                label: 'Kategori',
                task_assigned: 'Oppgave tildelt',
                task_sla_approaching: 'SLA Nærmer seg',
                task_overdue: 'Oppgave forfalt',
                task_completed: 'Oppgave fullført',
                task_escalated: 'Oppgave eskalert',
                workflow_transition: 'Arbeidsflytovergang',
                approval_requested: 'Godkjenning forespurt',
                comment_added: 'Kommentar lagt til',
                weekly_digest: 'Ukentlig sammendrag',
                sla_breach_report: 'SLA Rapport om brudd',
                system_alert: 'Systemvarsel',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plattform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Stille timer',
                start: 'Start',
                end: 'End',
                timezone: 'Tidssone',
            },
            digest: {
                title: 'Digest Frequency',
                daily: 'Daglig',
                weekly: 'Ukentlig',
                none: 'Ingen',
            },
            watched: {
                title: 'Så på Entities',
                empty: 'Ingen overvåkede enheter',
            },
            save: 'Lagre innstillinger',
            saving: 'Lagrer...',
            saved_success: 'Preferansene er lagret',
            load_error: 'Kunne ikke laste inn lagrede preferanser. Viser standardinnstillinger — endringer lagres når du klikker på Lagre.',
            enabled: 'Aktivert',
            disabled: 'Deaktivert',
            unwatch: 'Fjern se',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Administrasjonskø for varsler',
            description: 'Overvåk leveringsstatus for e-post og prøv mislykkede meldinger på nytt',
            load_error: 'Kunne ikke laste e-postkødata',
            retry_error: 'Kunne ikke prøve e-post på nytt',
            filter_status: 'Filtrer etter status',
            all_statuses: 'Alle',
            refresh: 'Oppdater',
            status_queued: 'I kø',
            status_sent: 'Sendt',
            status_failed: 'Mislykket',
            status_bounced: 'Avvist',
            status_sending: 'Sending',
            col_recipient: 'Mottaker',
            col_category: 'Kategori',
            col_template: 'Mal',
            col_status: 'Status',
            col_retries: 'Forsøk',
            col_created: 'Opprettet',
            col_sent: 'Sendt den',
            col_actions: 'Handlinger',
            no_items: 'Ingen elementer i e-postkøen funnet',
            retry: 'Prøv på nytt',
            retrying: 'Prøver på nytt...',
            prev: 'Forrige',
            next: 'Neste',
            access_denied_title: 'Administratortilgang kreves',
            access_denied: 'Denne siden er kun tilgjengelig for administratorer. Kontakt administratoren din hvis du trenger tilgang.',
            announce_queue_loaded: 'E-postkø lastet, {count} elementer',
            announce_processed: 'Behandlet {count} e-post(er)',
            process_now: 'Behandle nå',
            processing_ellipsis: 'Behandler …',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI Assist',
            activate: 'Aktiver',
            deactivate: 'Deaktiver',
            connected: 'Tilkoblet',
            disconnected: 'Koblet fra',
            disconnected_hint: "Frakoblet — kjør 'npm run relay' for å koble til Claude CLI",
            open_panel: 'Åpne UI Assist-panelet',
            history: 'Historie',
            settings: 'Innstillinger',
        },
        prompt: {
            placeholder: 'Beskriv endringen du vil gjøre...',
            send: 'Send',
            cancel: 'Avbryt',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Behandler …',
            complete: 'Komplett',
            error: 'Feil',
            abort: 'Avbryt',
            abort_confirm: 'Er du sikker på at du vil abortere?',
            aborted_hint: 'Økten ble avbrutt — du kan fortsette herfra.',
            undo: 'Angre',
            redo: 'Gjenta',
            retry: 'Prøv på nytt',
            dismiss: 'Avvis',
        },
        label: {
            component: 'Komponent',
            element: 'Element',
            file: 'Fil',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Ingen meldinger ennå. Velg elementer og send en melding for å komme i gang.',
        },
        panel: {
            tab_conversation: 'Chat',
            tab_tasks: 'Oppgaver',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sesjoner',
            tab_lobby: 'Lobbyen',
            follow_up: 'Følg opp',
            followup_placeholder: 'Still et oppfølgingsspørsmål...',
            interrupt_placeholder: 'Skriv for å avbryte og omdirigere…',
            send_interrupt: 'Send & Avbryt',
            will_interrupt: 'Avbryter det gjeldende svaret og sender dette i stedet',
            expand: 'Utvid til fullskjerm',
            collapse: 'Skjul til sidepanel',
            no_sessions: 'Ingen tidligere sesjoner. Velg elementer og send en melding for å begynne.',
            session_active: 'Aktiv',
            session_ended: 'Avsluttet',
            session_messages: '{count} meldinger',
            switch_session: 'Bytt til denne sesjonen',
            load_session: 'Last inn økthistorikk',
            session_restored: 'Sesjon gjenopprettet fra historien',
            archive_session: 'Arkivøkt',
            archive_confirm: 'Vil du arkivere denne økten? Sesjonsfilen vil bli slettet.',
            new_session: 'Ny økt',
            new_session_desc: 'Bruk Aktiver for å velge et UI-element og starte en ny økt',
            start_new_session: 'Start ny økt',
            no_active_session_chat: 'Start en ny økt eller bruk Aktiver for å velge et UI-element på siden for å begynne.',
            no_active_session_tasks: 'Start en ny økt eller velg en eksisterende økt for å opprette oppgaver.',
            no_active_session_sprint: 'Start en ny økt eller velg en eksisterende økt for å planlegge en sprint.',
            previous_sessions: 'Tidligere økter',
        },
        tasks: {
            none_found: 'Finner ingen oppgaver i svaret. Be Claude foreslå oppgaver.',
            selected: 'selected',
            create_selected: 'Lag oppgaver',
            execute_tasks: 'Utfør oppgaver',
            creating: 'Oppretter …',
            created: 'opprettede oppgaver',
            create_error: 'Kunne ikke opprette oppgaven',
            plan_sprint: 'Planlegg sprint',
            priority_critical_desc: 'Blokkerende — fiks umiddelbart',
            priority_high_desc: 'Viktig — løs i denne sprinten',
            priority_medium_desc: 'Normal — planlegg for kommende sprint',
            priority_low_desc: 'Hyggelig å ha — backlog-kandidat',
            add_custom: 'Legg til oppgave',
            add_title_placeholder: 'Oppgavetittel…',
            add_desc_placeholder: 'Beskrivelse (valgfritt)',
            add_save: 'Legg til',
            add_cancel: 'Avbryt',
        },
        sprint: {
            title: 'Sprintplanlegger',
            assigned: 'assigned',
            new_placeholder: 'Sprintnavn (f.eks. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Etterslep',
            move_to: 'Flytt til...',
            empty: 'Opprett en sprintgruppe og tildel oppgaver til den.',
            create_all: 'Opprett alle oppgaver',
            remove: 'Fjern',
            drop_here: 'Tildel oppgaver fra backlog nedenfor',
            add_task: 'Legg til oppgave fra backlog...',
        },
        feature_disabled: 'UI Assist er ikke aktivert',
        permission_denied: 'Du har ikke tillatelse til å bruke UI Assist',
    },
    license: {
        title: 'Lisens og pakke',
        current_license: 'Gjeldende lisens',
        license_key: 'Lisensnøkkel',
        activate: 'Aktiver lisens',
        deactivate: 'Deaktiver lisens',
        validate_now: 'Bekreft nå',
        status: 'Lisensstatus',
        active: 'Aktiv',
        expired: 'Utløpt',
        suspended: 'Suspendert',
        invalid: 'Ugyldig',
        pending: 'Venter',
        no_license: 'Ingen lisens',
        expires_at: 'Utløper',
        last_validated: 'Sist validert',
        domain: 'Lisensiert domene',
        activation_success: 'Lisens aktivert.',
        activation_failed: 'Lisensaktivering mislyktes.',
        domain_mismatch: 'Lisensdomenet samsvarer ikke med gjeldende domene.',
        deactivation_success: 'Lisens deaktivert.',
        validation_success: 'Lisensen er validert.',
        validation_failed: 'Lisensvalidering mislyktes.',
        enter_key: 'Skriv inn lisensnøkkelen din',
        confirm_deactivate: 'Er du sikker på at du vil deaktivere denne lisensen? Modulbegrensninger vil gjelde basert på standardnivået.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Pakke',
        module_column: 'Modul',
        current_tier: 'Gjeldende pakke',
        light: 'Lys',
        standard: 'Standard',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Endre pakke',
        upgrade: 'Oppgrader',
        downgrade: 'Nedgrader',
        target_tier: 'Målpakke',
        new_license_key: 'Lisensnøkkel for målpakke',
        confirm_change: 'Bekreft pakkeendring',
        change_success: 'Pakken ble endret.',
        change_failed: 'Pakkebytte mislyktes.',
        modules_included: 'Moduler inkludert',
        modules_restricted: 'Ikke tilgjengelig i gjeldende pakke',
        upgrade_to_enable: 'Oppgrader for å aktivere denne modulen.',
        module_availability: 'Modultilgjengelighet',
        comparison: 'Pakkesammenligning',
        power_users: 'Superbrukere',
        unlimited: 'Ubegrenset',
        impact_warning: 'Nedgradering vil deaktivere følgende moduler:',
        invalid_license: 'Den oppgitte lisensnøkkelen er ikke gyldig for denne pakken.',
        tier_mismatch: 'Lisensnøkkelen er for et annet pakkenivå.',
    },
    board_report: {
        title: 'Styrerapport om Samsvar',
        subtitle: 'Sammendrag av samsvar på tvers av alle moduler',
        date_from: 'Fra dato',
        date_to: 'Til dato',
        language: 'Rapportspråk',
        generate: 'Generer rapport',
        generating: 'Genererer rapport...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Last ned PDF',
        close: 'Lukk rapport',
        open_board_report: 'Generer styrerapport',
        close_board_report: 'Lukk styrerapport',
        report_period: 'Rapportperiode',
        generated_at: 'Generert den',
        generated_by: 'Generert av',
        organization: 'Organisasjon',
        section_rights_requests: 'Rettighetsforespørsler',
        section_vendors: 'Leverandører',
        section_dpias: 'DPIA-er',
        section_consent: 'Samtykkepolicyer',
        section_data_mapping: 'Datakartlegging',
        section_incidents: 'Hendelser',
        section_classification: 'Dataklassifisering',
        section_governance: 'Styring',
        open: 'Åpen',
        closed: 'Lukket',
        sla_compliance: 'SLA-samsvar',
        total: 'Totalt',
        expired_dpa: 'Utløpt DPA',
        high_risk: 'Høy risiko',
        completed: 'Fullført',
        overdue: 'Forsinket',
        policies_active: 'Aktive policyer',
        coverage: 'Dekning',
        systems: 'Systemer',
        completeness: 'Fullstendighet',
        avg_resolution_days: 'Gjs. løsningstid (dager)',
        labeled: 'Merket',
        unlabeled_systems: 'Umerkede systemer',
        compliance_score: 'Samsvarspoeng',
        drift_events_open: 'Åpne avvik',
        no_data: 'Ingen data tilgjengelig for den valgte perioden.',
        error_date_range: 'Velg både start- og sluttdato.',
        table_category: 'Kategori',
        table_metric: 'Metrikk',
        table_value: 'Verdi',
        showing_chart_view: 'Viser diagramvisning',
        showing_table_view: 'Viser datatabellvisning',
        error_generate: 'Styrerapport kunne ikke genereres.',
        playbook_evidence_title: 'Bevis for playbook-utførelse',
        playbook_evidence_description: 'Utførelsesaktivitet per playbook og vedlagte bevisfiler for rapportperioden.',
        playbook_name: 'Playbook',
        playbook_execution_count: 'Utførelser',
        playbook_steps_total: 'Trinn',
        playbook_steps_required: 'Bevis kreves',
        playbook_steps_attached: 'Bevis vedlagt',
        playbook_coverage_pct: 'Dekning',
        playbook_evidence_files: 'Bevisfiler',
        playbook_no_evidence: 'Ingen playbook-utførelser i denne perioden.',
        playbook_download_file: 'Last ned fil',
    },
    framework_module_links: {
        title: 'Rammetilknytning',
        modules: 'Tilknyttede moduler',
        add_module: 'Legg til modul',
        module_code: 'Modulkode',
        required: 'Påkrevd',
        notes: 'Notater',
        link: 'Kobling',
        no_modules: 'Ingen moduler tilknyttet',
        personas: 'Tilknyttede personas',
        add_persona: 'Legg til persona',
        persona_id: 'Persona-id',
        role_description: 'Rollbeskrivelse',
        no_personas: 'Ingen personas tilknyttet',
    },
    framework_channels: {
        title: 'Innleveringskanaler & Frister',
        channels: 'Innleveringskanaler',
        add_channel: 'Legg til kanal',
        channel_type: 'Type',
        name: 'Navn',
        instructions: 'Instruksjoner',
        active: 'Aktiv',
        no_channels: 'Ingen innleveringskanaler konfigurert',
        portal: 'Portal',
        in_person: 'Personlig',
        postal: 'Post',
        email: 'E-post',
        add: 'Legg til',
        deadlines: 'Regelverksfrister',
        add_deadline: 'Legg til frist',
        label: 'Etikett',
        days: 'Dager',
        day_type: 'Dagtype',
        calendar_days: 'Kalenderdager',
        working_days: 'Arbeidsdager',
        description: 'Beskrivelse',
        no_deadlines: 'Ingen frister konfigurert',
    },
    regulatory_updates: {
        title: 'Regelverksoppdateringer & Ramme-dashboard',
        coverage_dashboard: 'Ramme-dekning-dashboard',
        no_frameworks: 'Ingen rammeverk funnet',
        framework: 'Rammeverk',
        status: 'Status',
        modules: 'Moduler',
        templates: 'Maler',
        forms: 'Skjemaer',
        channels: 'Kanaler',
        deadlines: 'Frister',
        pending: 'Ventende oppdateringer',
        updates: 'Regelverksoppdateringer',
        create_update: 'Opprett oppdatering',
        framework_id: 'Rammeverk-id',
        update_title: 'Tittel',
        description: 'Beskrivelse',
        effective_date: 'Ikrafttredelsesdato',
        create: 'Opprett',
        no_updates: 'Ingen regelverksoppdateringer',
        impact: 'Påvirkning',
        assess: 'Vurder',
    },
    module_tips: nbModuleTips,
    framework_rights: {
        be_informed: 'Rett til å bli informert',
        consent: 'Rett til samtykke',
        refuse_consent: 'Rett til å nekte samtykke',
        withdraw_consent: 'Rett til å trekke tilbake samtykke',
        access: 'Rett til innsyn',
        rectification: 'Rett til retting',
        data_provision: 'Rett til datautlevering',
        deletion: 'Rett til sletting',
        restriction_of_processing: 'Rett til begrensning av behandling',
        object_to_processing: 'Rett til å protestere mot behandling',
        complaint: 'Rett til å klage',
        denunciation: 'Rett til anmeldelse',
        lawsuit: 'Rett til å reise søksmål',
        claim_damages: 'Rett til erstatning',
        request_protection_measures: 'Rett til å be om beskyttelsestiltak',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Direktiv 2002/58/EF (e-personverndirektivet)',
            description: 'Overordnet direktiv for nasjonal transponering av ePersonvern. Dekker informasjonskapsler, personvern for elektronisk kommunikasjon, direkte markedsføring. Hvert medlemsland må innføre i nasjonal lovgivning.',
        },
        EU_GDPR: {
            display_name: 'Generell databeskyttelsesforordning (EU) 2016/679',
            description: 'General Data Protection Regulation (GDPR) er en EU-forordning om databeskyttelse og personvern. Den gjelder direkte i alle EU/EØS-medlemsland.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Direktiv (EU) 2022/2555 (NIS2)',
            description: 'Overordnet direktiv for nasjonale NIS2 transposisjoner. Dekker cybersikkerhetsrisikostyring, hendelsesrapportering, forsyningskjedesikkerhet for viktige og viktige enheter.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnam AI lov (134/2025/QH15) – Gjelder 1. mars 2026',
            description: 'Vietnam AI lov (Luật Trí tuệ nhân tạo 134/2025/QH15) som styrer utvikling, distribusjon og bruk av kunstig intelligens-systemer. Etablerer risikoklassifisering, transparens og merkeplikter, krav til hendelsesrapportering, konsekvensvurderingsforpliktelser og en kontrollert testmekanisme (sandbox) for AI aktiviteter i Vietnam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnam Cybersecurity Law (24/2018-epoken)',
            description: 'Vietnams lov om cybersikkerhet (lov nr. 24/2018/QH14) som etablerer krav til cybersikkerhet, datalokalisering og hendelsesrapportering. Aktiv inntil erstattet av lov 116/2025/QH15 med virkning fra 2026-07-01.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Dekret 53 – Implementering av lov om nettsikkerhet',
            description: 'Dekret 53/2022/ND-CP som implementerer cybersikkerhetsloven, som dekker fastsettelse innenfor omfanget, utløsere for datalokalisering, krav til lokal tilstedeværelse og arbeidsflyter for myndighetssamarbeid.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Lov om nettsikkerhet (116/2025/QH15)',
            description: 'Vietnams konsoliderte lov om cybersikkerhet (lov 116/2025/QH15), som erstatter lov 24/2018/QH14, med virkning fra 1. juli 2026. Konsoliderer omfanget av dekret 53/2022/NĐ-CP (se VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Vietnam datalov',
            description: 'Vietnams datalov (lov 60/2024/QH15) som etablerer omfattende datastyringskrav, inkludert databeholdning, klassifisering, delingsstyring og grenseoverskridende dataoverføringsstyring. Overlapper med VN_PDPL_LAW_2025 (personopplysningsaspekter) og VN_CYBERSEC_LAW_2025 (datalokalisering/klassifisering).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Dekret 165/2025/NĐ-CPĐ-CP (datalovimplementering)',
            description: 'Dekret 165/2025/NĐ-CP som implementerer Vietnams datalov (lov 60/2024/QH15). Tilbyr offisielle skjemaer for konsekvensvurderinger for grenseoverskridende dataoverføring, årlig risikovurderingsrapportering og nasjonal databasetilgang. Definerer 11 offisielle Mẫu số-former.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Utkast til dekret om databørser 2026',
            description: 'Utkast til dekret om drift av datautvekslingsplattformer (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regulerer plattformklassifisering, deltakerberettigelse, kontroller for dataprodukt- og tjenesteoppføringer, sandkassetesting, transaksjonsarbeidsflyt, elektroniske kontrakter, sertifikater for eiendomsrettigheter over data og håndtering av klager/tvister. For tiden i offentlig høring; ennå ikke kunngjort. Siterer lov om data 60/2024/QH15, lov om beskyttelse av personopplysninger 91/2025/QH15, lov om nettsikkerhet 116/2025/QH15, og lov om elektroniske transaksjoner 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Lov om elektronisk handel (122/2025/QH15)',
            description: 'Vietnam lov om elektronisk handel (Luật Thương mại điện tử 122/2025/QH15) som etablerer et enhetlig nasjonalt regime for elektronisk handel, som dekker innenlands og grenseoverskridende plattformoperasjoner, plattformstyring og ansvarlighet, forbrukerbeskyttelse, selger og tilknyttede selskaper, fjerning av innhold, håndtering av innhold, klage og nedlasting av innhold, klage og nedlasting. statlig styringssystem for e-handel.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Dekret 13/2023/NĐ-CP',
            description: 'Dekret 13/2023/ND-CP, det operative instrumentet for PDPL oppgaver, inkludert dossierarkivering, styringsartefakter, grenseoverskridende mekanikk og administrative prosedyrer for MoPS.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Personvernlov (91/2025/QH15)',
            description: 'Vietnams lov om beskyttelse av personopplysninger (lov 91/2025/QH15) som etablerer et omfattende rammeverk for beskyttelse av personopplysninger, gjeldende 1. januar 2026. Dekker samsvarstemaer A–J: lovlige behandlingsbaser, samtykke til sensitive data, rettigheter til registrerte personer (tilgang/retting/sletting/begrensning/innsigelse/portabilitet), og datasubjektets rettigheter. DPIA for behandling med høy risiko, styring av grenseoverskridende overføringer, DPO betegnelse, registreringer av behandlingsaktiviteter, kontraktsmessige forpliktelser for behandler. Myndighetsarkiveringsforpliktelser via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Kryssreferanser: VN_PDPL_DECREE_356_2025 (gjennomføringsdekret), VN_CYBERSEC_LAW_2025 (cybersikkerhet overlapper), VN_DATA_LAW (datastyring).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Dekret 356/2025/ND-CP (PDPL Implementering)',
            description: 'Dekret 356/2025/ND-CP som implementerer personopplysningsloven (lov 91/2025/QH15). Dekker dossierarkivering, styringsartefakter, grenseoverskridende mekanikk og sertifisering av tjenesteleverandører. Vedleggsskjema inventar: Phụ lục I–VIII (konsekvensvurdering, grenseoverskridende overføring, sertifisering). MoPS administrative prosedyrer via VN_PDPL_ADMIN_PROCEDURES_778_2026 (vedtak 778/QĐ-BCA-A05). Arbeidsflyt for sertifisering: søknad → utstedelse → endringsforespørsel → ny utstedelse → tilbakekall. Kryssreferanser: VN_PDPL_LAW_2025 (overordnet lov), VN_CYBERSEC_LAW_2025 (datalokalisering), VN_DATA_LAW (datastyring overlapper).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Administrative prosedyrer (vedtak 778/QĐ-BCA-A05)',
            description: 'Avgjørelse 778/QĐ-BCA-A05 (februar 2026) — Administrative prosedyrer for beskyttelse av personopplysninger under A05 (MPS). Definerer prosedyrer for innsending, behandling og resultatutstedelse, inkludert DLCN-HC-skjemaer og lovbestemte skjemaer.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Utkast til håndhevingsdekret 2026',
            description: 'Utkast til håndhevings- og sanksjonsdekret for brudd på personopplysningsloven (lov 91/2025/QH15). Definerer straffeområder, skjerpende/formildende faktorer og håndhevelsesprosedyrer. For øyeblikket i utkaststatus og venter på kunngjøring.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnam telekommunikasjonslov 2023',
            description: 'Vietnam Telecommunications Law 2023 (Luật Viễn thông), et sektoroverlegg for telekommunikasjons-/ISP-/plattformselskaper med forpliktelser om konfidensialitet, avsløringskontroll og myndighetshåndtering. Kryssreferanser: VN_CYBERSEC_LAW_2025 (informasjonssystemsikkerhet), VN_PDPL_LAW_2025 (brukerdatabehandling), VN_DATA_LAW (datastyring).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Dekret 163/2024/NĐ-CPĐ-CP (Telecom Law Implementation)',
            description: 'Dekret 163/2024/NĐ-CP som implementerer telekommunikasjonsloven (lov 24/2023/QH15). Gir offisielle skjemaer for varsling av telekomtjenester, lisensiering, registrering og utviklingsplaner for datasenter. Definerer 7 offisielle Mẫu số-former.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Klager, tvister og risikohåndtering',
            description: 'Plattformoperatører må sørge for klageinntak, tvistehåndteringsarbeidsflyt med bevisbevaring, risikoidentifikasjon og tidlige varslingskontroller, og periodisk gjennomgang av risikoer for personvern, cybersikkerhet og tilgangsadministrasjon.',
        },
        vn_dp_draft_e: {
            title: 'Transaksjonsarbeidsflyt og sertifikat for eiendomsrett over data',
            description: 'Transaksjoner krever kontoregistrering, noteringsgodkjenning, elektronisk kontrakt, betalingsbekreftelse, leveringsregistrering og utstedelse av et sertifikat for eiendomsrett til data der det er aktuelt. Transaksjonshistorikk og sporbarhet skal oppbevares i et transaksjonsregister.',
        },
        vn_dp_draft_d: {
            title: 'Sandkasse og testmiljøkontroller',
            description: 'Testmiljøer må være isolert, tilgangsbegrenset, sikkerhetsovervåket og gjennomgått før utdata trekkes ut. Ingen kildedata kan trekkes ut fra testmiljøet, og stenging/opprydding av testøkter skal registreres.',
        },
        vn_dp_draft_c: {
            title: 'Datakontroller for produkt- og tjenesteoppføringer',
            description: 'Oppføringer må støttes av bevis fra lovlig kilde og eierskap/bruksrett, tekniske valideringsregistreringer, kvalitetsavsløringer og oppføringer for gjennomgang og godkjenning på plattformsiden. Forbudte data må filtreres før oppføring.',
        },
        vn_dp_draft_b: {
            title: 'Deltakerberettigelse og onboarding',
            description: 'Plattformoperatører må verifisere juridisk status, juridisk kapasitet og betingede forretningskrav for selgere og kjøpere, registrere transaksjons- og betalingskontoer og opprettholde et deltakerregister.',
        },
        vn_dp_draft_a: {
            title: 'Dataplattformklassifisering og driftsmodell',
            description: 'Plattformoperatører må etablere en klassifisering av plattformtype (nasjonal dataplattform vs andre dataplattformer), definere operatørens berettigelse og ansvar, og dokumentere priser, gebyrer, driftsregler og teknisk styring.',
        },
        d165_r5: {
            title: 'Informasjonstilgang til nasjonal database',
            description: 'Forespørsler om å trekke ut/bruke informasjon fra den nasjonale digitale datadatabasen bruker foreskrevne skjemaer (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Årlig risikovurderingsrapportering',
            description: 'Årlige risikovurderingsrapporter må innleveres ved bruk av foreskrevne skjemaer (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Oppsigelse av grenseoverskridende overføring',
            description: 'Oppsigelse av grenseoverskridende overføring skal varsles ved bruk av fastsatte skjemaer (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Varsling om vurderingsresultat',
            description: 'Myndigheten utsteder kunngjøringer om vurderingsresultater ved å bruke foreskrevet skjema (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Innlevering av konsekvensanalyse for grenseoverskridende dataoverføring',
            description: 'Organisasjoner må sende inn dokumentasjon for grenseoverskridende dataoverføring/behandling av konsekvensvurderinger ved å bruke foreskrevne skjemaer (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Samtykke for informasjonskapsler og lignende teknologier',
            description: 'Lagring eller tilgang til informasjon på et terminalutstyr er kun tillatt med forhåndssamtykke, med unntak av teknisk nødvendig lagring.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Konfidensialitet av kommunikasjon',
            description: 'Medlemsstatene skal sikre konfidensialiteten til kommunikasjon og relaterte trafikkdata ved hjelp av et offentlig kommunikasjonsnett og offentlig tilgjengelige elektroniske kommunikasjonstjenester.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Samtykke for direkte markedsføring (opt-in)',
            description: 'Bruk av elektroniske kommunikasjonssystemer for direkte markedsføring krever forhåndssamtykke fra abonnenten.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Myk opt-in for eksisterende kunder',
            description: 'Direkte markedsføring til eksisterende kunder for lignende produkter/tjenester er tillatt uten samtykke dersom opt-out er gitt.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Bruddvarsling for teleleverandører',
            description: 'Tilbydere av offentlig tilgjengelige elektroniske kommunikasjonstjenester skal varsle vedkommende nasjonale myndighet om brudd på personopplysninger.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Rett til å motsette seg direkte markedsføring',
            description: 'Abonnenter skal gis mulighet til å motsette seg å motta direkte markedsføringsmeldinger når som helst og gratis.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Oppbevaring og sletting av trafikkdata',
            description: 'Trafikkdata knyttet til abonnenter og brukere må slettes eller gjøres anonyme når de ikke lenger er nødvendige for overføring, bortsett fra fakturering eller samtrafikkbetalinger.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Betingelser for behandling av stedsdata',
            description: 'Andre stedsdata enn trafikkdata kan kun behandles når de er gjort anonyme eller med samtykke fra brukeren, i den utstrekning og varighet det er nødvendig.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Behandlingens lovlighet – samtykke',
            description: 'Behandling skal bare være lovlig dersom den registrerte har gitt samtykke til behandling av sine personopplysninger for ett eller flere spesifikke formål.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Behandlingens lovlighet — kontrakt',
            description: 'Behandling er nødvendig for å oppfylle en kontrakt som den registrerte er part i.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Behandlingens lovlighet — juridisk forpliktelse',
            description: 'Behandling er nødvendig for å overholde en rettslig forpliktelse som den behandlingsansvarlige er underlagt.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Behandlingens lovlighet — vitale interesser',
            description: 'Behandling er nødvendig for å beskytte de vitale interessene til den registrerte eller en annen fysisk person.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Behandlingens lovlighet — allmenn interesse',
            description: 'Behandling er nødvendig for å utføre en oppgave som utføres i allmennhetens interesse eller ved utøvelse av offentlig myndighet.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Behandlingens lovlighet — legitime interesser',
            description: 'Behandling er nødvendig for formålet med de legitime interessene som forfølges av den behandlingsansvarlige eller en tredjepart.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Behandling av spesielle kategorier (sensitive data)',
            description: 'Behandling av spesielle kategorier av personopplysninger (raseopprinnelse, politiske meninger, helse, etc.) er forbudt med mindre spesifikke vilkår i art. 9(2) er oppfylt.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Vilkår for samtykke',
            description: 'Kontrolløren må vise at samtykke ble gitt. Samtykke må være fritt gitt, spesifikt, informert og utvetydig. Tilbaketrekking må være like enkelt som å gi samtykke.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Barns samtykke — digitale tjenester',
            description: 'Behandling av personopplysninger om et barn under 16 år (eller lavere alder fastsatt av medlemsland, ikke under 13) krever samtykke fra foreldre for informasjonssamfunnstjenester.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Transparent informasjon og kommunikasjon',
            description: 'Behandlingsansvarlig skal gi informasjon knyttet til behandlingen i en kortfattet, transparent, forståelig og lett tilgjengelig form.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Informasjon for direkte innsamling',
            description: 'Der det samles inn personopplysninger fra den registrerte, skal behandlingsansvarlig gi spesifisert informasjon på innsamlingstidspunktet.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Informasjon for indirekte innsamling',
            description: 'Der det ikke er innhentet personopplysninger fra den registrerte, skal behandlingsansvarlig gi spesifisert informasjon innen rimelig tid.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Rett til innsyn',
            description: 'Den registrerte skal ha rett til å få bekreftelse på behandlingen og tilgang til personopplysningene og tilleggsopplysninger.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Rett til retting',
            description: 'Den registrerte skal ha rett til å få retting av unøyaktige personopplysninger uten ugrunnet opphold.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Rett til sletting (rett til å bli glemt)',
            description: 'Den registrerte skal ha rett til å få slettet personopplysninger uten ugrunnet opphold der spesifiserte grunner gjør seg gjeldende.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Rett til begrensning av behandlingen',
            description: 'Den registrerte skal ha rett til å få begrensning av behandlingen der nærmere angitte vilkår gjelder.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Meldeplikt — retting/sletting/begrensning',
            description: 'Behandlingsansvarlig skal kommunisere enhver retting, sletting eller begrensning til hver mottaker som data ble utlevert til.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Rett til dataportabilitet',
            description: 'Den registrerte skal ha rett til å motta sine personopplysninger i et strukturert, vanlig brukt, maskinlesbart format.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Rett til å protestere',
            description: 'Den registrerte skal ha rett til å motsette seg behandling basert på allmenne interesser eller legitime interesser, herunder profilering.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automatisert individuell beslutningstaking, inkludert profilering',
            description: 'Den registrerte skal ha rett til ikke å bli gjenstand for en avgjørelse basert utelukkende på automatisert behandling som gir rettslige eller tilsvarende betydelige virkninger.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Bruddmelding til tilsynsmyndigheten (72t)',
            description: 'Ved brudd på personopplysninger skal den behandlingsansvarlige varsle tilsynsmyndigheten innen 72 timer etter at han ble kjent med det, med mindre det er usannsynlig at det medfører risiko.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Bruddvarsling til registrerte (høy risiko)',
            description: 'Når et brudd sannsynligvis vil medføre en høy risiko for rettigheter og friheter, skal den behandlingsansvarlige kommunisere bruddet til den registrerte uten unødig opphold.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Svar på forespørsler fra registrerte (30 dager)',
            description: 'Behandlingsansvarlig skal svare på forespørsler fra registrerte uten unødig forsinkelse og innen én måned etter mottak, som kan forlenges med ytterligere to måneder for komplekse forespørsler.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Konsekvensvurdering av databeskyttelse',
            description: 'Der behandling sannsynligvis vil resultere i en høy risiko for rettigheter og friheter, skal den behandlingsansvarlige utføre en DPIA før behandlingen.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Forutgående konsultasjon med tilsynsmyndighet',
            description: 'Behandlingsansvarlig skal rådføre seg med tilsynsmyndigheten der en DPIA indikerer at behandling vil resultere i høy risiko uten avbøtende tiltak.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA obligatorisk behandlingsliste',
            description: 'DPIA kreves for systematisk evaluering/profilering, storskala behandling av spesielle kategorier, og systematisk overvåking av offentlige arealer.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Registrering av behandlingsaktiviteter (kontrollør)',
            description: 'Hver behandlingsansvarlig skal føre en oversikt over behandlingsaktiviteter under dens ansvar, inkludert formål, kategorier, mottakere, overføringer og oppbevaring.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Registrering av behandlingsaktiviteter (behandler)',
            description: 'Hver databehandler skal føre en oversikt over alle kategorier av behandlingsaktiviteter utført på vegne av en behandlingsansvarlig.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Behandlingssikkerhet',
            description: 'Behandlingsansvarlig og databehandler skal iverksette passende tekniske og organisatoriske tiltak for å sikre sikkerhet tilpasset risikoen.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Databeskyttelse ved design og som standard',
            description: 'Behandlingsansvarlig skal iverksette hensiktsmessige tiltak for å sikre at kun personopplysninger som er nødvendige for hvert spesifikke formål behandles (dataminimering).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Utpeking av personvernombud',
            description: 'Behandlingsansvarlig og databehandler skal utpeke en DPO der behandlingen utføres av offentlig myndighet, innebærer regelmessig og systematisk overvåking, eller storskala behandling av spesielle kategorier.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Generelt prinsipp for overføringer',
            description: 'Enhver overføring av personopplysninger til et tredjeland skal bare skje dersom vilkårene i kapittel V er oppfylt.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Overføringer basert på tilstrekkelighetsbeslutning',
            description: 'En overføring kan finne sted der Kommisjonen har besluttet at tredjelandet sikrer et tilstrekkelig beskyttelsesnivå.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Overføringer underlagt passende sikkerhetstiltak',
            description: 'I fravær av en beslutning om tilstrekkelighet, kan overføringer finne sted der hensiktsmessige garantier er gitt, inkludert SCC eller BCR.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standard kontraktsmessige klausuler',
            description: 'Standard kontraktsbestemmelser vedtatt av Kommisjonen eller av en tilsynsmyndighet gir passende garantier for internasjonale overføringer.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Bindende bedriftsregler',
            description: 'Bindende selskapsregler godkjent av en kompetent tilsynsmyndighet gir hensiktsmessige garantier for konserninterne internasjonale overføringer.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Unntak for spesifikke situasjoner',
            description: 'I mangel av tilstrekkelighet eller garantier, kan overføringer finne sted basert på uttrykkelig samtykke, kontraktsnødvendighet, allmenne interesser, rettskrav, vitale interesser eller offentlig register.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Lederorgansansvar og opplæring',
            description: 'Ledelsesorganer for viktige og viktige enheter må godkjenne risikostyringstiltak for nettsikkerhet og gjennomgå regelmessig opplæring.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Risikostyringstiltak for cybersikkerhet',
            description: 'Essensielle og viktige enheter skal iverksette passende og forholdsmessige tekniske, operasjonelle og organisatoriske tiltak for å håndtere cybersikkerhetsrisiko.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Retningslinjer og prosedyrer for hendelseshåndtering',
            description: 'Enheter skal implementere retningslinjer for hendelseshåndtering, inkludert deteksjon, analyse, inneslutning og responsprosedyrer.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Forretningskontinuitet og krisehåndtering',
            description: 'Enheter skal implementere forretningskontinuitetstiltak, inkludert sikkerhetskopiering, katastrofegjenoppretting og krisehåndtering.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Sikkerhet i forsyningskjeden',
            description: 'Enheter skal behandle sikkerhetsrelaterte aspekter ved forhold til direkte leverandører og tjenesteleverandører, inkludert risikovurderinger i forsyningskjeden.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Sikkerhet i nettverk og informasjonssystemer',
            description: 'Enheter skal implementere sikkerhet ved anskaffelse, utvikling og vedlikehold av nettverk og informasjonssystem, inkludert sårbarhetshåndtering.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Sårbarhetshåndtering og avsløring',
            description: 'Enheter skal implementere retningslinjer og prosedyrer for koordinert avsløring og håndtering av sårbarheter.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Effektivitet av vurdering av cybersikkerhetsrisiko',
            description: 'Enheter skal implementere retningslinjer og prosedyrer for å vurdere effektiviteten til risikostyringstiltak for nettsikkerhet.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Politikk for kryptografi og kryptering',
            description: 'Enheter skal implementere retningslinjer og prosedyrer angående bruk av kryptografi og, der det er hensiktsmessig, kryptering.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR-sikkerhet, tilgangskontroll, kapitalforvaltning',
            description: 'Enheter skal implementere sikkerhet for menneskelige ressurser, retningslinjer for tilgangskontroll og aktivaforvaltning.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Multi-faktor autentisering',
            description: 'Enheter skal bruke multifaktorautentisering eller kontinuerlig autentiseringsløsninger, sikret tale/video/tekstkommunikasjon og sikrede nødkommunikasjonssystemer.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Tidlig advarsel til CSIRT (24 timer)',
            description: 'Enheter skal sende inn en tidlig advarsel til den kompetente CSIRT innen 24 timer etter at de ble oppmerksomme på en betydelig hendelse.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Hendelsesvarsling til CSIRT (72 timer)',
            description: 'Enheter skal sende inn en hendelsesmelding til den kompetente CSIRT innen 72 timer etter at de ble oppmerksomme på en betydelig hendelse.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Sluttrapport til CSIRT (1 måned)',
            description: 'Enheter skal sende inn en sluttrapport til den kompetente CSIRT senest én måned etter hendelsesmeldingen.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Anvendelsesbestemmelse for AI aktiviteter',
            description: 'Bestem om organisasjonen utfører AI aktiviteter underlagt Vietnam AI-loven og dokumenter anvendelsesbeslutningen.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI systemrisikoklassifisering etter risikonivå',
            description: 'Klassifiser hvert AI system etter risikonivå (minimalt, begrenset, høyt, uakseptabelt) ved å bruke den veiledede klassifiseringsmetoden.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI Vedlikehold av systemregisteroppføring',
            description: 'Oppretthold et register over AI systemer med systemnavn, eier, formål, distribusjonskontekst, risikonivå og leverandørinvolvering.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI Systemdokumentasjon med artefaktsjekkliste og bevis',
            description: 'Bygg og vedlikehold et systemdossier med nødvendig sjekkliste for artefakter, bevisbibliotek og versjonshistorikk.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Åpenhetsforpliktelser for AI systemoperatører',
            description: 'Implementer åpenhetstiltak for AI-systemer, inkludert brukervarsling, avsløring av AI involvering og systemdokumentasjon.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI utdatamerking, vannmerking og metadatasignering',
            description: 'Implementer merking og vannmerking for AI-generert innhold, inkludert metadatasignering der det er aktuelt.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Offentliggjøringskrav for AI-generert innhold',
            description: 'Fortell brukere når innhold er AI-generert og gi brukere mekanismer for å identifisere AI involvering.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Forklaringsoppgaver og plikter til brukervennlig åpenhet',
            description: 'Gi forklarlige utdata for AI beslutninger som påvirker enkeltpersoner, og oppretthold dokumentasjon for brukervendt åpenhet.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Menneskelige tilsynsforpliktelser for AI-systemer med høy risiko',
            description: 'Sørg for at menneskelige tilsynsmekanismer er på plass for AI-systemer med høy risiko, inkludert eskalerings- og overstyringsfunksjoner.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI hendelsesovervåking og deteksjon',
            description: 'Implementer overvåkingsfunksjoner for å oppdage AI systemhendelser, funksjonsfeil og uønskede utfall.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Alvorlig AI hendelse som rapporteres til myndigheten',
            description: 'Rapporter alvorlige AI hendelser til vedkommende myndighet innen den foreskrevne tidslinjen (72 timer).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI tidslinjeoverholdelse av hendelsesvarsling',
            description: 'Overhold varslingstidslinjene for AI hendelser, inkludert innledende varsling, oppdateringer og avslutningsrapporter.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI konsekvensvurdering for høyrisikosystemer',
            description: 'Gjennomfør konsekvensvurderinger for høyrisiko-AI-systemer som dekker risikoer, avbøtende tiltak, menneskelig tilsyn og overvåkingsplaner.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Kontrollert testing / fastsettelse av kvalifisering for sandkasse',
            description: 'Bestem kvalifisering for kontrollert testing (sandkasse) deltakelse og dokumenter kvalifikasjonsvurderingen.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Sandbox-søknadsinnsending og godkjenningssporing',
            description: 'Send inn sandkassesøknader til den kompetente myndigheten og spor godkjenningsstatus gjennom innsendingslivssyklusen.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandkasse overvåking sjekkpunkter og ferdigstillelse/utgang',
            description: 'Gjennomfør periodiske overvåkingssjekkpunkter under sandkassedeltakelse og utarbeide fullførings-/utgangsrapporter.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Merkefritak/unntaksdokumentasjon med begrunnelse',
            description: 'Dokumenter eventuelle merkingsunntak eller unntak med begrunnelse og støttende bevis.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Datalokalisering for vietnamesiske brukere',
            description: 'Data fra vietnamesiske brukere om tjenester som tilbys i Vietnam må lagres innenfor Vietnams territorium.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Rapportering av cybersikkerhetshendelser',
            description: 'Cybersikkerhetshendelser må rapporteres til kompetente myndigheter i tide.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Klassifisering av informasjonssystem',
            description: 'Informasjonssystemer må klassifiseres etter viktighetsnivå og passende sikkerhetstiltak skal iverksettes.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Risikovurdering av cybersikkerhet',
            description: 'Det må gjennomføres regelmessige risikovurderinger av cybersikkerhet for informasjonssystemer.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Bestemmelse innenfor omfanget (beslutningstre)',
            description: 'Organisasjoner må avgjøre om de er innenfor virkeområdet for dekret 53 ved å bruke det foreskrevne beslutningstreet.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Datalokaliseringsvurdering og beredskap',
            description: 'Organisasjoner må vurdere krav til datalokalisering og vise beredskap.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Sjekkliste for beredskap for lagring og oppbevaring',
            description: 'En sjekkliste for beredskap for lagring og oppbevaring må fylles ut for samsvar med datalokalisering.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Lokal tilstedeværelseskrav vurdering',
            description: 'Organisasjoner må vurdere om lokale tilstedeværelseskrav gjelder og planlegge deretter.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Myndighetsforespørsel om mottak og verifisering',
            description: 'Myndighetsforespørsler må mottas, verifiseres og behandles i henhold til foreskrevne prosedyrer.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Myndigheten ber om svar innen fristen',
            description: 'Svar på myndighetsforespørsler skal gis innen fastsatt frist.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Logg for håndtering av myndighetsforespørsel',
            description: 'Det skal føres en logg over alle myndighetsforespørselshåndteringsaktiviteter.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Anvendbarhet og fastsettelse innenfor omfanget (konsolidert)',
            description: 'Organisasjoner må avgjøre om de er innenfor virkeområdet under den konsoliderte cybersikkerhetsloven, som dekker både den gamle loven 24/2018 og dekret 53/2022.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Informasjonssystemklassifisering etter viktighetsnivå',
            description: 'Informasjonssystemer skal klassifiseres etter viktighetsnivå (generelt, viktig, kritisk) med tilsvarende sikkerhetsforpliktelser.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Styringsstruktur for cybersikkerhet og ansvarlig personell',
            description: 'Organisasjoner må etablere styringsstrukturer for cybersikkerhet og utpeke ansvarlig personell.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Periodisk vurdering av cybersikkerhetsrisiko',
            description: 'Det skal gjennomføres regelmessige risikovurderinger av nettsikkerhet for alle klassifiserte informasjonssystemer.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Sikkerhetstiltak per informasjonssystemklassifisering',
            description: 'Tekniske sikkerhetstiltak må iverksettes hensiktsmessig for hvert informasjonssystemklassifiseringsnivå.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Adgangskontroll og identitetshåndtering',
            description: 'Det skal implementeres tilgangskontroll og identitetsstyringstiltak for informasjonssystemer.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Logging, overvåking og revisjonsspor',
            description: 'Informasjonssystemer må opprettholde logging, overvåking og revisjonsspor.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Sårbarhetshåndtering og oppdatering',
            description: 'Sårbarhetshåndtering og oppdateringsprosesser må etableres og vedlikeholdes.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Datalokalisering for vietnamesiske brukerdata',
            description: 'Data fra vietnamesiske brukere om tjenester som tilbys i Vietnam må lagres innenfor Vietnams territorium.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Lokal tilstedeværelseskrav for utenlandske tjenesteleverandører',
            description: 'Utenlandske tjenesteleverandører som opererer i Vietnam må opprettholde lokal tilstedeværelse som foreskrevet.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Lokal lagringsarkitektur og grenseoverskridende flytdokumentasjon',
            description: 'Lokal lagringsarkitektur må dokumenteres og grenseoverskridende datastrømmer må kartlegges og kontrolleres.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Deteksjon og respons på cybersikkerhetshendelser',
            description: 'Organisasjoner må ha evner til å oppdage og reagere på cybersikkerhetshendelser.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Obligatorisk hendelsesrapportering til kompetent myndighet',
            description: 'Kvalifiserte cybersikkerhetshendelser må rapporteres til MPS/MIC innen foreskrevne tidslinjer.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Overholdelse av tidslinje for hendelsesvarsling',
            description: 'Hendelsesvarsler må overholde foreskrevne tidslinjekrav (innledende rapport innen 24 timer, fullstendig rapport innen 72 timer).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Myndighetsforespørselsbehandling og samarbeid',
            description: 'Myndighetsforespørsler må behandles raskt og samarbeid gis i henhold til foreskrevne prosedyrer.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Myndighetsbegjæringssaksregister og utleveringslogg',
            description: 'Alle myndighetsforespørsler og avsløringer skal loggføres i et saksregister med fullstendig revisjonsspor.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Leverandørs og tredjeparts sikkerhetsvurdering',
            description: 'Tredjepartsleverandører med tilgang til informasjonssystemer må gjennomgå sikkerhetsvurdering.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Kontroller for leverandørdatatilgang og hendelsesvarsling',
            description: 'Leverandørdatatilgang må dokumenteres og kontraktsmessige forpliktelser om varsling av hendelser skal etableres.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Sikkerhetsbevissthetstrening og øvelser',
            description: 'Regelmessig bevisstgjøringstrening for nettsikkerhet og bordøvelser må gjennomføres.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Periodisk pennetest og sikkerhetsrevisjon',
            description: 'Periodiske penetrasjonstesting og sikkerhetsrevisjoner skal utføres på klassifiserte informasjonssystemer.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Opprettholde digital databeholdning',
            description: 'Organisasjoner må opprettholde en omfattende digital databeholdning som katalogiserer alle dataressurser.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Dataklassifisering etter sensitivitet og viktighet',
            description: 'Data må klassifiseres i henhold til sensitivitets- og viktighetsnivåer som definert av dataloven.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Definer håndteringsregler etter klassifiseringsnivå',
            description: 'Det må etableres håndteringsregler for hvert dataklassifiseringsnivå.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Spor datadeling og avsløring med godkjenninger',
            description: 'Alle datadelings- og avsløringsaktiviteter må spores med passende godkjenninger.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Grenseoverskridende dataoverføringsstyring',
            description: 'Grenseoverskridende dataoverføringer må være i samsvar med styringskrav i henhold til dataloven.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Opprettholde styringspolitikk og ansvar',
            description: 'Organisasjoner må opprettholde retningslinjer for datastyring og tildele styringsansvar.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Årlig styringsattest',
            description: 'En årlig styringsattest må fylles ut for å bekrefte løpende overholdelse.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Datarisikovurdering',
            description: 'En datarisikovurdering må utføres for å identifisere og redusere risikoer for datastyring.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Plattformoperatørens ansvar og styringsforpliktelser',
            description: 'Etabler ansvarlighetsstrukturer for plattformoperatører, inkludert styringspolitikk, overvåkingsansvar og håndhevelsesforpliktelser.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Plattformregler publisering og åpenhet av informasjon',
            description: 'Publiser plattformregler, handelsbetingelser og operasjonelle avsløringer på en transparent og tilgjengelig måte.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'E-handel salgsnettsted/app-melding til MOIT',
            description: 'Send melding til MOIT for e-handelssalgsnettsteder og -applikasjoner via Online.gov.vn eller angitte kanaler.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'E-handelstjenesteplattform/markedsplassregistrering hos MOIT',
            description: 'Registrer e-handelstjenesteplattformer og markedsplasser med MOIT, inkludert innsending av dossier og godkjenningssporing.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Vedlikehold av selgerregister (innenlandsk og utenlandsk)',
            description: 'Oppretthold et register over alle selgere på plattformen, inkludert innenlandske og utenlandske selgere med onboarding, verifisering og livssyklussporing.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream selgerregister og samsvarskontroller',
            description: 'Oppretthold et register over livestreamselgere med identitetsbekreftelse, aktivitetssporing og samsvarskontroller.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate- og markedsføringspartnerregister',
            description: 'Opprettholde et register over tilknyttede markedsførere og henvisningspartnere med kontraktsstatus, retningslinjer for innhold og bekreftelse på samsvar.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Produktliste og håndhevelse av forbudte/betingede varer',
            description: 'Implementer produktoppføringskontroller, inkludert forbudte og betingede varescreening, overvåking og håndhevingshandlinger.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Forbrukerklager registreres med inntak, handlinger og revisjonsspor',
            description: 'Oppretthold et klageregister med inntaksregistreringer, undersøkelseshandlinger, løsningsresultater og fullstendig revisjonsspor.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Prosedyre for klagebehandling og tidsfrister for løsning',
            description: 'Etabler klagebehandlingsprosedyrer med definerte tidslinjer for bekreftelse, etterforskning og løsning.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Moderering av innhold, overvåking og håndhevelse av fjerning',
            description: 'Implementer prosedyrer for moderering, overvåking og fjerning av innhold for ulovlig, forbudt eller krenkende innhold.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Fjerning og håndhevingshandlinger registreres med bevisbevaring',
            description: 'Opprettholde et register over fjernings- og håndhevingshandlinger med beslutningsregister, bevisoppbevaring og selgervarslinger.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Plattformpolicy og avsløringsregister (versjoner, publiseringsdatoer, aksept)',
            description: 'Oppretthold et register over plattformpolicyer og avsløringer med versjonshistorikk, publiseringsdatoer og akseptbevis.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Forbrukerinformasjonsplikter - nøyaktig produktinformasjon og anti-bedrag',
            description: 'Sørg for nøyaktig, tydelig produkt- og tjenesteinformasjon og implementer tiltak for å forhindre villedende praksis på plattformen.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Driftsforhold for utenlandsk plattform og registrering/varsling',
            description: 'Overhold driftsvilkårene for utenlandske plattformer som betjener vietnamesiske brukere, inkludert registrerings- eller varslingskrav.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Lokal representasjon eller autorisasjon for utenlandske plattformer',
            description: 'Utpek en lokal representant eller autorisert enhet i Vietnam for utenlandske plattformer som kreves av loven.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Samordning av selger og klagebehandling på tvers av landegrensene',
            description: 'Koordinere grenseoverskridende selgerhåndtering og klageløsning inkludert eskalering og bevishåndtering.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Statlig samhandling og rapporteringsberedskap for e-handelsstyringssystem',
            description: 'Forbered deg på samhandling med det statlige e-handelsstyringssystemet, inkludert datafeltkartlegging og rapporteringsevne.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Melding om vesentlig endring etter godkjenning',
            description: 'Send inn varsler for vesentlige endringer i plattformdetaljer, retningslinjer eller operasjoner etter første godkjenning.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Oppsigelse og deaktiveringsvarsel',
            description: 'Send inn varsler om oppsigelse eller deaktivering ved opphør av e-handel, inkludert offentlig kunngjøring og håndtering av forbrukerbeskyttelse.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Trustmark og troverdighetsvurderingsprogram kvalifisering og vedlikehold',
            description: 'Vurder kvalifisering for trustmerke- og troverdighetsvurderingsprogrammer og opprettholde samsvarsbevis for pågående deltakelse.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Samtykke til behandling av personopplysninger',
            description: 'Behandling av personopplysninger krever samtykke fra den registrerte med mindre unntak gjelder.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Rett til innsyn',
            description: 'Registrerte har rett til innsyn i sine personopplysninger som den behandlingsansvarlige har.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Rett til retting',
            description: 'Registrerte har rett til å be om retting av unøyaktige personopplysninger.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Rett til sletting',
            description: 'Registrerte har rett til å be om sletting av sine personopplysninger.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Rett til å begrense behandlingen',
            description: 'Registrerte har rett til å be om begrensning av behandlingen av sine personopplysninger.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Rett til å protestere',
            description: 'Registrerte har rett til å protestere mot behandlingen av deres personopplysninger.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Rett til å trekke tilbake samtykke',
            description: 'Registrerte har rett til å trekke tilbake tidligere gitt samtykke når som helst.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Melding om brudd til myndigheten (72 timer)',
            description: 'Behandlingsansvarlig må varsle departementet for offentlig sikkerhet innen 72 timer etter oppdagelse av et brudd på personopplysninger.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Bruddvarsling til registrerte',
            description: 'Behandlingsansvarlig skal varsle berørte registrerte om et brudd på personopplysninger uten ugrunnet opphold.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA for høyrisikobehandling',
            description: 'En konsekvensutredning for databeskyttelse må utføres for behandlingsaktiviteter som sannsynligvis vil resultere i høy risiko.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Grenseoverskridende overføringsvurdering',
            description: 'En konsekvensutredning må gjennomføres før overføring av personopplysninger utenfor Vietnam.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Registrering av behandlingsaktiviteter',
            description: 'Behandlingsansvarlige må føre en journal over behandlingsaktiviteter (RoPA) som dokumenterer alle behandlingsaktiviteter.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Databehandleravtaleregister',
            description: 'Det skal føres journal over databehandleravtaler med behandlere og underbehandlere.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Beskyttelse av grenseoverskridende overføringer',
            description: 'Overføring av personopplysninger utenfor Vietnam krever passende sikkerhetstiltak og dokumenterte vurderinger.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Bekreftelse innen 2 virkedager',
            description: 'Registrerte forespørsler må bekreftes innen 2 virkedager etter mottak.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Ferdigstillelse innen 15 virkedager',
            description: 'Registrerte forespørsler må fullføres innen 15 virkedager, med en mulig forlengelse på 5 virkedager.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'Innlevering av MoPS konsekvensvurderingsmapp',
            description: 'Organisasjoner som utfører høyrisikobehandling må sende inn en konsekvensutredningssak til departementet for offentlig sikkerhet ved å bruke den foreskrevne administrative prosedyren.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'MoPS arkivering av grenseoverskridende overføringer',
            description: 'Organisasjoner som overfører personopplysninger utenfor Vietnam, må sende inn en konsekvensvurderingsdossier for grenseoverskridende overføring til departementet for offentlig sikkerhet.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Behandler sertifisering av tjenesteleverandør',
            description: 'Organisasjoner som yter personopplysningsbehandling som en tjeneste må innhente et tjenestebehandlingssertifikat fra departementet for offentlig sikkerhet.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Behandling av konsekvensutredningsdossier når triggere er oppfylt',
            description: 'En saksbehandlingssak må innleveres når foreskrevne triggere er oppfylt.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Dokumentasjon for grenseoverskridende overføring',
            description: 'En grenseoverskridende overføringskonsekvensdossier må utarbeides og arkiveres for internasjonale dataoverføringer.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Utpeking av personvernombud',
            description: 'En personvernombud må utpekes slik dekret 13 krever.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Styringsartefakter (policyer, roller, prosedyrer)',
            description: 'Styringsartefakter, inkludert retningslinjer, roller og prosedyrer, må opprettholdes.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Fullstendighet i bevispakken og uforanderlig revisjonsspor',
            description: 'Bevispakker må være komplette og et uforanderlig revisjonsspor må opprettholdes.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Støtte for arbeidsflyt for arkivering',
            description: 'Arbeidsflyter for innlevering av sak må støttes for konsekvensutredning og grenseoverskridende sakssaker.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Behandler kontraktsmessige krav',
            description: 'Kontraktskrav til databehandlere skal etableres og overvåkes.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Årlig etterlevelsesgjennomgang',
            description: 'En årlig overholdelsesgjennomgang må gjennomføres for å verifisere løpende etterlevelse.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Myndighetsarkivering av innsendingsposter',
            description: 'Registreringer over alle innleveringer fra myndighetene må opprettholdes.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Lovlig grunnlag for behandling av personopplysninger',
            description: 'Behandling av personopplysninger må være basert på lovlige grunnlag som definert av PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Registrerte rettigheter (tilgang, retting, sletting, begrense, gjenstand, portabilitet)',
            description: 'Registrerte har omfattende rettigheter, inkludert tilgang, retting, sletting, begrensning, innsigelse og portabilitet.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Bruddmelding til myndighet',
            description: 'Behandlingsansvarlige må varsle vedkommende myndighet om brudd på personopplysninger innen den fastsatte tidsrammen.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Bruddvarsling til registrerte',
            description: 'Behandlingsansvarlige skal varsle berørte registrerte om brudd på personopplysninger.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Konsekvensvurdering av databeskyttelse',
            description: 'En konsekvensutredning for databeskyttelse må gjennomføres for behandlingsaktiviteter med høy risiko.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Styring av grenseoverskridende overføringer',
            description: 'Grenseoverskridende overføringer av personopplysninger må være i samsvar med styringskrav.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Utpeking av personvernombud',
            description: 'Organisasjoner må utpeke en personvernansvarlig etter behov.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Registrering av behandlingsaktiviteter',
            description: 'Organisasjoner må føre oversikt over alle behandlingsaktiviteter.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Behandler kontraktsmessige forpliktelser',
            description: 'Kontraktsforpliktelser må etableres med databehandlere.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Innlevering av konsekvensanalyser',
            description: 'Dokumenter for konsekvensutredning må innleveres til vedkommende myndighet som foreskrevet.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Dokumentasjon for grenseoverskridende overføring',
            description: 'En grenseoverskridende overføringskonsekvensdossier må utarbeides og arkiveres for internasjonale dataoverføringer.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO avtale og varsling',
            description: 'Det skal oppnevnes en personvernombud og myndigheten varsles.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Retningslinjer og prosedyrer for styring',
            description: 'Retningslinjer og prosedyrer for styring må etableres og vedlikeholdes.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Bevispakke og uforanderlig revisjonsspor',
            description: 'Bevispakker må være komplette og et uforanderlig revisjonsspor må opprettholdes.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Innleveringssaks livssyklus',
            description: 'Livssykluser for innlevering av sak må administreres gjennom foreskrevne stadier.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Behandler kontraktsmessige krav',
            description: 'Kontraktskrav til databehandlere skal etableres og overvåkes.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Tjenestebehandlingsleverandørsertifisering',
            description: 'Organisasjoner som leverer databehandlingstjenester må få sertifisering.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Myndighetsarkivering av innsendingsposter',
            description: 'Registreringer over alle innleveringer fra myndighetene må opprettholdes.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Årlig etterlevelsesgjennomgang',
            description: 'En årlig overholdelsesgjennomgang må gjennomføres for å verifisere løpende etterlevelse.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Datasikkerhetstekniske tiltak',
            description: 'Tekniske tiltak for datasikkerhet skal implementeres og vedlikeholdes.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Dekret 356 offisielle krav til Mẫu số',
            description: 'De 13 offisielle Mẫu số administrative prosedyreskjemaene (Mẫu số 01a–10) foreskrevet ved dekret 356/2025/NĐ-CPĐ-CP må brukes for de tilsvarende innleveringene til departementet for offentlig sikkerhet. Phụ lục I–VIII-elementene er interne datastrukturkartlegginger, ikke offisielle reguleringsskjemaer.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA innsending av dossier til A05',
            description: 'Organisasjoner må sende inn DPIA-dossierer til A05/MPS for behandlingsaktiviteter som involverer sensitive personopplysninger eller storskala behandling.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Innsending av sakspapir for grenseoverskridende overføring',
            description: 'Organisasjoner må sende inn dokumentasjon for grenseoverskridende dataoverføring til A05/MPS for gjennomgang og godkjenning.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Behandler søknad om tjenestesertifikat',
            description: 'Leverandører av databehandlingstjenester må søke om sertifikater fra A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Varsel om behandling av personopplysninger',
            description: 'Behandlingsansvarlige må varsle A05/MPS om behandling av personopplysninger ved bruk av foreskrevne skjemaer (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Årlig situasjonsrapportering',
            description: 'Behandlingsansvarlige må levere årlige situasjonsrapporter for behandling av personopplysninger (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Brukerinformasjon konfidensialitet som standard',
            description: 'Brukerinformasjon må behandles som konfidensiell som standard.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minimum organisatoriske og tekniske beskyttelseskontroller',
            description: 'Minimum organisatoriske og tekniske vernekontroller må implementeres.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Avsløring kun med riktig grunnlag',
            description: 'Utlevering av brukerinformasjon er kun tillatt med et forsvarlig juridisk grunnlag.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Obligatorisk avsløringslogging og revisjonerbarhet',
            description: 'Alle avsløringer må logges og revideres.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Intern godkjenningsarbeidsflyt for avsløringer',
            description: 'Det må etableres en intern godkjenningsarbeidsflyt for alle avsløringer.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Behandling av svar fra myndighetsforespørsel',
            description: 'Myndighetsforespørsler skal håndteres og besvares i henhold til foreskrevne prosedyrer.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'Den registrerte har rett til å bli informert om behandling av personopplysninger.',
        consent: 'Den registrerte har rett til å samtykke til behandling av sine personopplysninger.',
        refuse_consent: 'Den registrerte har rett til å ikke samtykke til behandling av sine personopplysninger.',
        withdraw_consent: 'Den registrerte har rett til å be om tilbaketrekking av tidligere gitt samtykke.',
        access: 'Den registrerte har rett til å se og få tilgang til sine personopplysninger som den behandlingsansvarlige har.',
        rectification: 'Den registrerte har rett til å korrigere eller be om retting av unøyaktige personopplysninger.',
        data_provision: 'Den registrerte har rett til å be om utlevering av sine personopplysninger (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'Den registrerte har rett til å be om sletting eller destruksjon av personopplysninger, med forbehold om lovens unntak.',
        restriction_of_processing: 'Den registrerte har rett til å be om begrensning av behandlingen av sine personopplysninger.',
        object_to_processing: 'Den registrerte har rett til å sende en forespørsel som protesterer mot behandlingen av deres personopplysninger.',
        complaint: 'Den registrerte har rett til å sende inn en klage (khiếu nại) angående behandling av personopplysninger.',
        denunciation: 'Den registrerte har rett til å gjøre en oppsigelse (tố cáo) angående brudd på personopplysningsbeskyttelsen.',
        lawsuit: 'Den registrerte har rett til å reise rettslige skritt (khởi kiện) for brudd på deres databeskyttelsesrettigheter.',
        claim_damages: 'Den registrerte har rett til å kreve erstatning for skade forårsaket av brudd på personopplysningsbeskyttelsen.',
        request_protection_measures: 'Den registrerte har rett til å be kompetente myndigheter eller relevante organisasjoner/enkeltpersoner om å iverksette tiltak for å beskytte sine personopplysninger.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Risikostyring og overvåking (utkast)',
            steps: {
                '1': 'Identifiser risikokategorier',
                '2': 'Oppretthold tidlig varslingskontroller',
                '3': 'Gjennomgå risikoer for innsamling, lagring, behandling, deling og transaksjoner',
                '4': 'Registrer risikoer for personvern, cybersikkerhet og tilgangsadministrasjon',
                '5': 'Utløs utbedringshandlinger',
                '6': 'Ta vare på anmeldelsesloggen',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Klager og tvister (utkast)',
            steps: {
                '1': 'Motta klage/tvistinntak',
                '2': 'Sjekk fullstendigheten av nødvendig informasjon; be om tillegg innen 24 timer hvis ufullstendig',
                '3': 'Innen 3 virkedager etter å ha mottatt en fullstendig forespørsel, varsle partene om håndteringstilnærming og forventet tidslinje',
                '4': 'Be om svar og støttedokumenter',
                '5': 'Vurder tvistefakta og plattformansvar',
                '6': 'Rekordoppløsningsresultat',
                '7': 'Ta vare på bevis og avslutt sak',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Levering og sertifikat for eiendomsrett til data (utkast)',
            steps: {
                '1': 'Levere produkt/tjeneste etter kontraktsmetode',
                '2': 'Registrer leveringshendelse og tidspunkt',
                '3': 'Generer sertifikat for eiendomsrett over data',
                '4': 'Registrer transaksjonsfullføring',
                '5': 'Aktiver senere oppslag/verifisering der det er nødvendig',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transaksjonsinitiering og kontrahering (utkast)',
            steps: {
                '1': 'Gjennomgå oppføring og evalueringsresultat',
                '2': 'Start arbeidsflyt for kjøp, forhandlinger eller auksjon',
                '3': 'Generer elektronisk kontrakt',
                '4': 'Sikre begge parters aksept',
                '5': 'Registrer fullføring av betaling',
                '6': 'Godkjenne levering',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Tilgang til sandkasse/testmiljø (utkast)',
            steps: {
                '1': 'Send inn sandkasseforespørsel',
                '2': 'Definer omfang, tillatte data, tidsperiode og behandlingstillatelser',
                '3': 'Tilveiebringe isolert teknisk miljø',
                '4': 'Overvåk sikkerhet og tilgang under testing',
                '5': 'Gjennomgå utdata før ekstraksjon',
                '6': 'Godkjenn uttak kun der det er tillatt',
                '7': 'Lukk testøkt og ryd opp i miljøet',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Dataprodukt-/tjenesteoppføring (utkast)',
            steps: {
                '1': 'Opprett oppføringsapplikasjon',
                '2': 'Fang kilde og eierskap / bruksrett bevis',
                '3': 'Registrer tekniske egenskaper, format, API detaljer og begrensninger',
                '4': 'Avsløringer av rekordkvalitet',
                '5': 'Send inn oppføringsdossier til plattformgjennomgang',
                '6': 'Plattform utfører fullstendighet, lovlighet og tekniske kontroller',
                '7': 'Godkjenne eller avslå oppføringen',
                '8': 'Hvis godkjent, publiser oppføringen med vedlagt evalueringsresultat',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Deltaker onboarding (utkast)',
            steps: {
                '1': 'Bestem deltakertype: selger eller kjøper',
                '2': 'Bekreft juridisk status og rettslig kapasitet',
                '3': 'Bekreft tilleggsbetingelser hvis betingede forretningslinjer gjelder',
                '4': 'Registrer transaksjonskonto',
                '5': 'Koble til betalingskonto',
                '6': 'Godkjenne plattformdeltakelse',
                '7': 'Lagre deltakerregistrering og revisjonsspor',
            },
        },
        AI_A1: {
            name: 'AI Systemregistrering/varsling',
            steps: {
                '1': 'Anvendelseskontroll og AI systemklassifisering',
                '2': 'Bygg registrerings-/varslingsdossier',
                '3': 'Intern godkjenning',
                '4': 'Send inn via kanal',
                '5': 'Endringssløyfe – hvis returnert, oppdater og send inn på nytt',
                '6': 'Lukk og lagre bekreftelse',
            },
        },
        AI_A2: {
            name: 'AI Arbeidsflyt for risikoklassifisering',
            steps: {
                '1': 'Opprett AI systemregisteroppføring',
                '2': 'Kjør klassifiseringsveiviseren',
                '3': 'Legg ved bevis',
                '4': 'Godkjenne klassifisering',
                '5': 'Koble utdata til systemdossier',
            },
        },
        AI_A3: {
            name: 'Overholdelse av merking og avsløring',
            steps: {
                '1': 'Identifiser merkingsforpliktelser etter system og brukstilfelle',
                '2': 'Implementer merkingsmerknader',
                '3': 'Ta bevis – skjermbilder, UI-kopi, tekniske metadata',
                '4': 'Håndtere unntak og registrere begrunnelse',
                '5': 'Godkjenne og versjon',
            },
        },
        AI_A4: {
            name: 'Rapportering av alvorlige hendelser',
            steps: {
                '1': 'Opprett AI hendelsespost',
                '2': 'Klassifiser alvorlighetsgrad',
                '3': 'Utfør inneslutningshandlinger og bevisfangst',
                '4': 'Generer maleksport for alvorlig hendelsesrapport',
                '5': 'Send til autoritet via kanal',
                '6': 'Spor myndighetssvar og stenging',
            },
        },
        AI_A5: {
            name: 'AI Konsekvensanalyse',
            steps: {
                '1': 'Opprett vurderingspost',
                '2': 'Fyll inn nødvendige seksjoner',
                '3': 'Legg ved bevis – dataflyter, modelldokumentasjon, kontroller',
                '4': 'Intern godkjenning',
                '5': 'Eksporter rapport og lagre i dossier',
            },
        },
        AI_A6: {
            name: 'Kontrollert testing / Sandbox-deltakelse',
            steps: {
                '1': 'Fullfør kvalifikasjons- og beredskapsjournal',
                '2': 'Forbered søknad og eksport',
                '3': 'Send inn via kanal',
                '4': 'Spor godkjenning',
                '5': 'Utfør overvåkingssjekkpunkter og logger',
                '6': 'Fullfør ferdigstillelsesrapport og avslutningspakke',
            },
        },
        CYBERSEC_C1: {
            name: 'Sikkerhetsvurdering av kritisk informasjonssystem',
            steps: {
                '1': 'Klassifisere informasjonssystemer i henhold til nasjonale cybersikkerhetskrav',
                '2': 'Gjennomføre sikkerhetsvurdering og risikoevaluering for klassifiserte systemer',
                '3': 'Implementer nødvendige sikkerhetstiltak og basiskontroller',
                '4': 'Dokumenter bevis på samsvar og planlegg periodiske gjennomganger',
            },
        },
        CYBERSEC_C2: {
            name: 'Varsling om cybersikkerhetshendelser',
            steps: {
                '1': 'Oppdag, klassifiser og vurder cybersikkerhetshendelsen',
                '2': 'Utfør umiddelbare reaksjonshandlinger og bevisbevaring',
                '3': 'Rapportere til myndighet og delta i nasjonal koordinering av hendelse-respons',
                '4': 'Gjennomfør gjennomgang etter hendelsen og dokumenter erfaringer',
            },
        },
        CYBERSEC_C3: {
            name: 'Verifikasjon av overholdelse av datalokalisering',
            steps: {
                '1': 'Vurder om datalokaliseringsutløsere gjelder basert på tjenestetype og datakategorier',
                '2': 'Kontroller at lokal lagringsarkitektur og grenseoverskridende flytkontroller er på plass',
                '3': 'Vurder krav til lokal tilstedeværelse (filial / representasjonskontor) der det er aktuelt',
                '4': 'Dokumenter samsvarsbevis og vedlikehold for myndighetsgjennomgang',
            },
        },
        CYBERSEC_C4: {
            name: 'Myndighetssamarbeidsforespørselssvar',
            steps: {
                '1': 'Motta og verifisere legitimiteten og omfanget av myndighetsforespørselen',
                '2': 'Samle og klargjør forespurt informasjon eller utfør nødvendige handlinger',
                '3': 'Send svar til myndigheten innen den nødvendige tidslinjen',
                '4': 'Logg avsløringen og lukk saken',
            },
        },
        CYBERSEC_C5: {
            name: 'Verifisering av kvalifisering for cybersikkerhetspersonell',
            steps: {
                '1': 'Vurder leverandørens sikkerhetsstilling ved å bruke rammeverket for vurdering av nettsikkerhet',
                '2': 'Bekreft leverandørdatatilgangsomfanget og registrer i leverandørdatatilgangsloggen',
                '3': 'Gjennomgå varslingsklausulen for leverandørhendelser og kontraktsfestede krav til cybersikkerhet',
                '4': 'Planlegg periodiske leverandørsikkerhetsgjennomganger og overvåk overholdelse',
            },
        },
        DATA_D1: {
            name: 'Datainventar og klassifiseringsoppsett',
            steps: {
                '1': 'Katalisere alle dataressurser med typer og kilder i datalagerregisteret',
                '2': 'Klassifiser hver eiendel etter følsomhetsnivå',
                '3': 'Definer håndteringsregler per klassifiseringsnivå',
                '4': 'Ta bevis – skjermbilder, arkitekturdiagrammer, tilgangskontroller',
                '5': 'Planlegg periodiske gjennomganger',
            },
        },
        DATA_D2: {
            name: 'Datadelingsstyring',
            steps: {
                '1': 'Evaluer nye delingsordninger via risikovurdering for datadeling',
                '2': 'Fullfør arbeidsflyt for intern godkjenning',
                '3': 'Registrer delingshendelse i datadeling/avsløringslogg',
                '4': 'Ta bevis – godkjenningsdokumenter, kontraktsdokumenter',
                '5': 'Periodisk gjennomgang av delingsordninger',
            },
        },
        DATA_D3: {
            name: 'Grenseoverskridende dataoverføringsvurdering',
            steps: {
                '1': 'Identifiser grenseoverskridende overføringer i databeholdning',
                '2': 'Gjennomfør overføringsrisikovurdering',
                '3': 'Bruk sikkerhetstiltak og styringstiltak',
                '4': 'Dokument i grenseoverskridende del av styringsattest',
                '5': 'Eksportvurderingspakke for revisjon',
            },
        },
        DATA_D4: {
            name: 'Årlig styringsattest',
            steps: {
                '1': 'Gjennomgå governance RACI matrise',
                '2': 'Bekreft policystatus (styring, klassifisering, deling, oppbevaring)',
                '3': 'Komplett attestasjonspakke',
                '4': 'Få intern godkjenning',
                '5': 'Eksportere og arkivere attestering',
            },
        },
        DATA_D5: {
            name: 'Håndtering av datahendelser',
            steps: {
                '1': 'Klassifiser hendelsen ved hjelp av beslutningsprotokoll',
                '2': 'Utfør hendelsesrespons per spillebok',
                '3': 'Ta bevis – logger, skjermbilder, kommunikasjon',
                '4': 'Utfør gjennomgang etter hendelsen',
                '5': 'Hvis myndighetsvarsling kreves: opprett innsendingssak og send inn til MIC',
                '6': 'Lukk og arkiver',
            },
        },
        ECOM_P1: {
            name: 'Varsling (salgsnettsted/app)',
            steps: {
                '1': 'Bestem klassifisering: salgsside/app vs markedsplass/tjenesteplattform',
                '2': 'Opprett intern dossieroppføring med firmaidentitet, informasjon om nettsted/app og nødvendige avsløringer',
                '3': 'Generer bevispakke – skjermbilder av avsløringer, policy-URLer, versjonshistorikk',
                '4': 'Send inn via Online.gov.vn – last opp dokumenter, registrer tidsstempel og bekreftelse',
                '5': 'Spor behandling – hvis den returneres for endring, registrer årsaker og send inn på nytt',
                '6': 'Lukk — butikkbekreftelse, koblingsbevis og revisjonsspor',
            },
        },
        ECOM_P2: {
            name: 'Registrering (tjenesteplattformer / markedsplasser)',
            steps: {
                '1': 'Bestem plattformmodell og forpliktelser - markedsplass vs blandet vs sosial handel',
                '2': 'Bygg registreringsdossier – søknadsskjema, tjenesteordning, vilkår og betingelser, plattformregler',
                '3': 'Interne godkjenninger – tildel juridisk/samsvarseier, godkjenn for innsending',
                '4': 'Send inn via Online.gov.vn – last opp dossier, registrer tidsstempel',
                '5': 'Behandlingssløyfe - hvis returnert for endring, oppdater og send inn på nytt',
                '6': 'Godkjenning og operasjonalisering — butikkbekreftelse, vedlikeholde løpende samsvarsregistre',
            },
        },
        ECOM_P3: {
            name: 'Oppdater/endre varsling',
            steps: {
                '1': 'Identifiser endringsutløser — materiell endring i dossierelementer',
                '2': 'Generer endringspakke — endringssammendrag, oppdaterte dokumenter, bevisoppdateringer',
                '3': 'Send inn oppdatering og noter innsendingsstatus',
                '4': 'Spor behandling og lukk',
            },
        },
        ECOM_P4: {
            name: 'Oppsigelse / deaktivering',
            steps: {
                '1': 'Forbered oppsigelsespakke - opphørsdato, årsak, håndteringsplan for forbrukerbeskyttelse, bevis på offentlig varsel',
                '2': 'Send oppsigelse – registrer tidsstempel og bekreftelse',
                '3': 'Lukk — lagre endelig status og bevis',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / Troverdighetsvurderingsprogram',
            steps: {
                '1': 'Kvalifikasjons- og beredskapsvurdering',
                '2': 'Forbered dokumentasjon ved å bruke offisielt skjema',
                '3': 'Send inn og spor',
                '4': 'Oppretthold løpende samsvarsbevis',
            },
        },
        PDPL_P1: {
            name: 'DPIA Innlevering til MPS',
            steps: {
                '1': 'Fullfør DPIA ved å bruke dekret 356 skjemavei (Mẫu số 02a/02b)',
                '2': 'Samle omslagsdokumenter og støttebevis',
                '5': 'Ta vare på dossier og bevis for innsending for revisjonsspor',
            },
        },
        PDPL_P2: {
            name: 'Søknad om grenseoverskridende overføring',
            steps: {
                '1': 'Fullfør dokumentasjon for grenseoverskridende overføring ved hjelp av dekret 356-skjemaer (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Søknad om sertifikat for databehandlingstjeneste',
            steps: {
                '1': 'Forbered sertifiseringssøknadsmapper i henhold til dekret 356 krav',
                '2': 'Send inn søknad til MPS med nødvendige støttedokumenter',
                '3': 'Svar på MPS vurderingskommentarer og forespørsler om tillegg',
            },
        },
        PDPL_P4: {
            name: 'Behandling av forespørsel om datasubjektrettigheter',
            steps: {
                '1': 'Motta og logg forespørsel; bekrefte identiteten til den registrerte innen 2 virkedager',
                '2': 'Vurder forespørselstype og bestem gjeldende frist (10/15/20 dager etter type)',
                '3': 'Utfør oppfyllelseshandlinger på tvers av relevante systemer og avdelinger',
                '4': 'Forbered og send svar til den registrerte med utfall og begrunnelse',
                '5': 'Avslutt sak og oppbevar bevis for revisjon; dokumentere eventuelle avslagsbegrunnelser',
            },
        },
        PDPL_P5: {
            name: 'Bruddvarsel til MPS',
            steps: {
                '1': 'Oppdag og vurder bruddet; avgjør om meldeplikten gjelder, og finn gjeldende meldefrist fra de installerte fristpostene',
                '2': 'Forbered varslingspakke i henhold til PDPL krav',
                '3': 'Send meldingen til MPS innen meldefristen som følger av de installerte fristpostene, regnet fra oppdagelsen',
                '4': 'Svar på MPS oppfølgingsforespørsler; sende inn tillegg og koordineringsbevis',
            },
        },
        TELECOM_T1: {
            name: 'Håndtering av myndighetsforespørsel',
            steps: {
                '1': 'Motta og logg myndighetsforespørselen via inntakspost',
                '2': 'Bekreft myndighetsidentitet, juridisk grunnlag og forespørselsomfang',
                '3': 'Gjennomfør intern juridisk gjennomgang og innhent godkjenning fra ledelsen',
                '4': 'Forbered svarpakke med samlet bevis',
                '5': 'Utkast og godkjenne svarbrev',
                '6': 'Opprett innsendingssak og send inn svar via kanal',
                '7': 'Loggavsløring og spormyndighetsbekreftelse',
                '8': 'Lukk saken og oppbevar bevis for revisjonsspor',
            },
        },
        TELECOM_T2: {
            name: 'Disclosure Management',
            steps: {
                '1': 'Motta forespørsel om offentliggjøring (brukersamtykke, juridisk forpliktelse eller myndighetsforespørsel)',
                '2': 'Fullfør arbeidsflyt for godkjenning av avsløring',
                '3': 'Utfør avsløring og registrer i loggregister',
                '4': 'Fang bevis - godkjenningsposter, kommunikasjonsposter',
                '5': 'Periodisk gjennomgang av avsløringslogg',
            },
        },
        TELECOM_T3: {
            name: 'Konfidensialitetspolicy og oppsett av tilgangskontroll',
            steps: {
                '1': 'Utforme og godkjenne retningslinjer for konfidensialitet',
                '2': 'Utforme og godkjenne retningslinjer for tilgangskontroll og logging',
                '3': 'Dokumentbehandlingsaktiviteter i brukerinformasjonsbehandlingsregister',
                '4': 'Fang bevis – policydokumenter, implementeringsbevis',
                '5': 'Planlegg periodiske gjennomganger',
            },
        },
        TELECOM_T4: {
            name: 'Telekom hendelsesvurdering',
            steps: {
                '1': 'Klassifisere og vurdere hendelsen',
                '2': 'Bestem varslingskrav',
                '3': 'Hvis myndighetsvarsel kreves: opprette innsendingssak',
                '4': 'Fange og ta vare på bevis',
                '5': 'Oppløsning og lukking',
            },
        },
        TELECOM_T5: {
            name: 'Håndtering av oppbevaring og sletting',
            steps: {
                '1': 'Definer oppbevaring og slettingsplan per datakategori',
                '2': 'Implementer sletteprosedyrer',
                '3': 'Ta bevis — sletting av poster',
                '4': 'Periodisk gjennomgang av timeplaner',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registrering / Varsling / Administrativ beredskap',
            'ECOM-FINE-B': 'Klager og forbrukerbeskyttelse',
            'ECOM-FINE-C': 'Overvåking og fjerningshåndhevelse',
            'ECOM-FINE-D': 'Grenseoverskridende operasjoner',
            'ECOM-FINE-E': 'Plattformregler og åpenhet',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Håndtering av myndighetsforespørselsfeil',
            'TELECOM-FINE-B': 'Konfidensialitet og uautorisert avsløring',
            'TELECOM-FINE-C': 'Tilgangskontroll og loggingsfeil',
            'TELECOM-FINE-D': 'Oppbevaring og slettingsbrudd',
            'TELECOM-FINE-E': 'Feil ved vurdering av telekomhendelser',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Opptil 100 millioner VND',
                fine_org: 'Opptil 200 millioner VND',
                deadlines: 'Umiddelbart ved oppdagelse',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Opptil 100 millioner VND',
                fine_org: 'Opptil 200 millioner VND',
                deadlines: 'Løpende forpliktelse',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Opptil 100 millioner VND',
                fine_org: 'Opptil 200 millioner VND',
                deadlines: 'Løpende forpliktelse',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Opptil 100 millioner VND',
                fine_org: 'Opptil 200 millioner VND',
                deadlines: 'Løpende forpliktelse',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Opptil 100 millioner VND',
                fine_org: 'Opptil 200 millioner VND',
                deadlines: 'Som angitt i myndighetsanmodningen',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Opptil 80 millioner VND',
                fine_org: 'Opptil 160 millioner VND',
                deadlines: 'Løpende forpliktelse',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Opptil 80 millioner VND',
                fine_org: 'Opptil 160 millioner VND',
                deadlines: 'Løpende forpliktelse',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Opptil 80 millioner VND',
                fine_org: 'Opptil 160 millioner VND',
                deadlines: 'Løpende forpliktelse',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Opptil 80 millioner VND',
                fine_org: 'Opptil 160 millioner VND',
                deadlines: 'Som spesifisert per klagebehandlingsforpliktelser',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Opptil 80 millioner VND',
                fine_org: 'Opptil 160 millioner VND',
                deadlines: 'Før du starter e-handel',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Løpende drift av plattformen',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformoperatør / risiko',
                '2': 'Plattformoperatør / Sikkerhet',
                '3': 'Plattformoperatør / risiko',
                '4': 'Plattformoperatør / Sikkerhet',
                '5': 'Plattformoperatør',
                '6': 'Overholdelse',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'En deltaker sender inn en klage eller tvisteforespørsel',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformoperatør',
                '2': 'Plattformoperatør',
                '3': 'Plattformoperatør',
                '4': 'Plattformoperatør',
                '5': 'Plattformoperatør / juridisk',
                '6': 'Plattformoperatør',
                '7': 'Overholdelse',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Levering er gjennomført etter at kontrakt og betalingsbetingelser er oppfylt',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Selger / Plattformoperatør',
                '2': 'Plattformoperatør',
                '3': 'Plattformoperatør',
                '4': 'Plattformoperatør',
                '5': 'Plattformoperatør',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'En kjøper initierer en transaksjon for data, produkt eller tjeneste',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Kjøper',
                '2': 'Kjøper / Plattformoperatør',
                '3': 'Plattformoperatør',
                '4': 'Kjøper/selger',
                '5': 'Plattformoperatør / Betaling',
                '6': 'Plattformoperatør',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'En deltaker ønsker å evaluere dataprodukter/tjenester i det isolerte miljøet',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Deltaker',
                '2': 'Plattformoperatør/deltaker',
                '3': 'Plattformoperatør / IT',
                '4': 'Plattformoperatør / Sikkerhet',
                '5': 'Plattformoperatør',
                '6': 'Plattformoperatør',
                '7': 'Plattformoperatør / IT',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'En selger søker å føre opp data, produkter eller datatjenester for handel',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Selger',
                '2': 'Selger / Juridisk',
                '3': 'Selger / IT',
                '4': 'Selger',
                '5': 'Selger',
                '6': 'Plattformoperatør',
                '7': 'Plattformoperatør',
                '8': 'Plattformoperatør',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'En selger eller kjøper blir med i dataplattformen',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformoperatør',
                '2': 'Plattformoperatør / juridisk',
                '3': 'Plattformoperatør / juridisk',
                '4': 'Plattformoperatør',
                '5': 'Plattformoperatør',
                '6': 'Plattformoperatør',
                '7': 'Overholdelse',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Oppstart av personopplysningsbehandling som krever konsekvensutredning',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Personvernteam',
                '2': 'Personvernteam',
                '3': 'Juridisk / Samsvar',
                '4': 'DPO / Personvernteam',
                '5': 'Overholdelsesteam',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Hensikt om å overføre personopplysninger utenfor Vietnam',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Personvernteam',
                '2': 'Juridisk / Samsvar',
                '3': 'DPO / Personvernteam',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organisasjon som leverer databehandlingstjenester som krever sertifisering',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Juridisk / Samsvar',
                '2': 'Juridisk / Samsvar',
                '3': 'DPO / Personvernteam',
                '4': 'Overholdelsesteam',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Mottak av en forespørsel om registrerte rettigheter',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Personvernteam / kundeservice',
                '2': 'DPO / Personvernteam',
                '3': 'Avdelingsledere / IT',
                '4': 'DPO / Personvernteam',
                '5': 'Overholdelsesteam',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Oppdagelse av brudd eller brudd på personopplysningsbeskyttelsen',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Incident Response Team / DPO',
                '2': 'DPO / Personvernteam',
                '3': 'Juridisk / Samsvar',
                '4': 'DPO / Incident Response Team',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Dekret 13 Art. 24',
        'MoPS Admin Procedures': 'MoPS Admin Prosedyrer',
        'Annual cycle': 'Årlig syklus',
        'Decree 13/2023/NĐ-CP Art. 24': 'Dekret 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Utkast til dekret om dataplattformer 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Lov 122/2025/QH15 Kap. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Lov 122/2025/QH15 Kap. IV (forbrukerbeskyttelse)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Lov 122/2025/QH15 – Online.gov.vn operasjonelle prosedyre',
        'Law 134/2025/QH15 Article 12': 'Lov 134/2025/QH15 Artikkel 12',
        'Law 134/2025/QH15 Article 21': 'Lov 134/2025/QH15 Artikkel 21',
        'Law 134/2025/QH15 Article 27': 'Lov 134/2025/QH15 Artikkel 27',
        'Law 134/2025/QH15 Articles 9–10': 'Lov 134/2025/QH15 Artikkel 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Lov 24/2023/QH15 – artikkelkartlegging venter',
        'Law 60/2024/QH15 (internal control target)': 'Lov 60/2024/QH15 (internkontrollmål)',
        'Law 91/2025/QH15 Art. 21': 'Lov 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Lov 91/2025/QH15 Art. 23',
        'Policy-defined': 'Policy-definert',
        Quarterly: 'Kvartalsvis',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 administrativ prosedyre',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'internkontrollmål (lov 116/2025/QH15 Art. 24(2)(b), 41(5) – eksakt tidslinje i påvente av implementeringsinstrument)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'internkontrollmål (lov 116/2025/QH15 art. 41(3) – eksakt tidslinje i påvente av implementeringsinstrument)',
        'source verification pending': 'kildebekreftelse venter',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad hoc-rapportering fra internrevisjon til SBV (lag 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig ICAAP-rapport (Phụ lục III) (lag 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig Phụ lục I (lag 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig Phụ lục IV (lag 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig risikostyringsrapport (Phụ lục II) (lag 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Ansvar for eiendelsforvaltning (lag 2, tema C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN — Styrets / medlemsrådets tilsyn (lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Styreutvalg (lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Styrevedtak og protokoller (lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Kontrollprosedyrer for filialer (lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliancevurdering av interne regler (lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliancefunksjon (lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Oppfølging av compliance-tiltak (lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Konsentrasjonsrisiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Interessekonflikt (lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Kontrollkultur (lag 2, tema A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kredittrisiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegert myndighet (lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Varsel om tidlig innføring (lag 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Administrerende direktørs ansvar (lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Renterisiko i bankboken (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Instruks for internrevisjon (lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internrevisjonsoppdrag (tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Kommunikasjon av funn fra internrevisjon (lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Funn fra internrevisjon og korrigerende tiltak (tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Oppfølging av internrevisjon (tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Styring av internrevisjon',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internrevisjonens uavhengighet (lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Plan for internrevisjon (lag 2/tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Rapportering fra internrevisjon til SBV (lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Styring av internkontrollsystemet (lag 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Intern rapporteringsprosedyre (lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register over interne rapporter (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Overvåking av lovendringer (lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Juridisk risiko og omdømmerisiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Eskalering ved brudd på grenser (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Likviditetsrisiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Årlig gjennomgang av MIS (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Sikkerhetskopiering og kontinuitet for MIS (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Eskaleringsmekanismer for MIS (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Pålitelighet og konfidensialitet for MIS (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risikodatakilder for MIS (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Ledelsesinformasjonssystem (lag 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Markedsrisiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Register over vesentlige risikoer (lag 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Modellrisiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Gjennomgang av nye produkter (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operasjonell risiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Beredskapsplanlegging for outsourcing (lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcingskontrakter (lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Kontroller for outsourcing',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Aktsomhetsvurdering for outsourcing (lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Reserveleverandør ved outsourcing (lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Vesentlighetsvurdering for outsourcing (lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Oppfølging av outsourcing (lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Tekniske referanser i Phụ lục V/VI (lag 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policyregister (lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Profesjonsetikk (lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risikoappetitt (lag 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Koordinering av risikoutvalg (lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Styring av risikodata (lag 3, tema E/F) — fasevis ikrafttredelse 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Gjennomgang av risikogrenser (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Rammeverk for risikogrenser (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikostyringsfunksjon (lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikometodikk og modeller (lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikorapportering (lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Rapporteringsfrekvens for risiko (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — Innsending av utbedringstiltak til SBV (lag 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — Innsendingskanaler til SBV (lag 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sektoranvendelse (lag 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Oppgaveadskillelse (lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Oppfølging fra toppledelsen (lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategisk risiko (lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stresstesting (lag 3, tema E) — fasevis ikrafttredelse 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Kontrollkomité (lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register over tilsynsfunn (lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Roller og eskalering i de tre forsvarslinjene (lag 2, tema B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — De tre forsvarslinjene (lag 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Eskalering av brudd (lag 2, tema D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (nasjonalforsamlingen)',
        'Ministry of Public Security / A05 Department': 'Departementet for offentlig sikkerhet / A05-avdelingen',
        'Ministry of Public Security': 'Departementet for offentlig sikkerhet',
        'Ministry of Information and Communications': 'Informasjons- og kommunikasjonsdepartementet',
        'Government of Vietnam': 'Regjeringen i Vietnam',
        'European Data Protection Board (EDPB)': 'European Data Protection Board (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / Nasjonale CSIRTer',
        'State Bank of Vietnam': 'State Bank of Vietnam',
    },
    tour_titles: {
        access_accountability: 'Få tilgang til ansvarlighetstur',
        assessments: 'Vurderingstur',
        audit_trail: 'Audit Trail Tour',
        classification: 'Klassifiseringstur',
        compliance_forms: 'Omvisning i samsvarsskjemaer',
        connectors: 'Connectors Tour',
        consent_properties: 'Omvisning i samtykkeegenskaper',
        consent: 'Samtykkestur',
        dashboard: 'Dashboard tur',
        data_mapping: 'Datakartleggingstur',
        data_subjects: 'Omvisning for dataemner',
        deletion_orchestration: 'Sletting Orchestration Tour',
        discovery: 'Oppdagelsestur',
        governance: 'Governance Tour',
        help_center: 'Omvisning i hjelpesenteret',
        incident_ops: 'Incident Operations Tour',
        incidents: 'Hendelsestur',
        localization: 'Lokaliseringstur',
        monitoring: 'Overvåkingstur',
        onboarding: 'Onboarding Tour',
        organizations: 'Organisasjonstur',
        partner_workspace: 'Partner arbeidsomvisning',
        platform_settings: 'Omvisning i plattforminnstillinger',
        privacy_scanner: 'Privacy Scanner Tour',
        program_governance: 'Program Governance Tour',
        rights_requests: 'Rettighetsforespørsler Tour',
        task_management: 'Task Management Tour',
        vendor_risk: 'Leverandørrisikoomvisning',
    },
    tour_descriptions: {
        access_accountability: 'Som internrevisjonsleder (P-VN-10), gjennomgå tilgangsbevis, bruk av revisjonstillatelser og undersøk unormale mønstre for å demonstrere GDPR artikkel 5(1)(f) integritet og konfidensialitet.',
        assessments: 'Som Enterprise DPO (P-VN-01), gjennomføre DPIAer etter dekret 356 Mau so 02a, identifisere samsvarshull og vurdere risikoer for grenseoverskridende overføringer under PDPL.',
        audit_trail: 'Som Internal Audit Lead (P-VN-10), spør etter bevis på tvers av moduler, sett sammen revisjonspakker og eksporter manipulasjonssikre logger for regulatoriske innsendinger.',
        classification: 'Som Data Steward (P-VN-05), definer dataklassifiseringstaksonomier, tildel sensitivitetsetiketter, håndhev oppbevaringspolicyer og løs dekningshull som kreves for PDPL.',
        compliance_forms: 'Som Enterprise DPO (P-VN-01), bla gjennom, fyll ut og send inn offisielle vietnamesiske reguleringsskjemaer inkludert Phu luc I, Phu luc II og Mau so maler for PDP-komitéinnleveringer.',
        connectors: 'Som IT Systems Lead (P-VN-05), fange opp forretningskrav for integrasjoner, kartlegge koblinger til samsvarsdataflyter og verifiser POC-beredskap før du går live.',
        consent_properties: 'Som Enterprise DPO (P-VN-01), konfigurer CMP-distribusjon: digital eiendomsregistrering, gylne maler for konsistente PDPL samtykkebannere og bindende matriser som kobler egenskaper til behandlingsaktiviteter.',
        consent: 'Som Enterprise DPO (P-VN-01), administrer samtykkepolitikkens livssyklus: definer innsamlingsformål, håndhev PDPL tilbaketrekkingsrettigheter, og integrer med din samtykkeadministrasjonsplattform.',
        dashboard: 'Som Enterprise DPO (P-VN-01), orienter deg rundt kommandosenteret for samsvarsstilling som viser PDPL tidsfrister, brudd på SLAer og KPIer for revisjonsberedskap.',
        data_mapping: 'Som Data Steward (P-VN-05), bygg ROPA-inventaret ditt, kartlegg grenseoverskridende datastrømmer og vedlikehold behandlingsaktivitetsregisteret som kreves av PDPL.',
        data_subjects: 'Som Enterprise DPO eller Education Compliance Lead, administrer enhetlige datasubjektprofiler som kobler sammen samtykkeposter, rettighetsforespørsler og tidslinjeaktivitet på tvers av plattformen.',
        deletion_orchestration: 'Som DPO (P-VN-01), verifiser sletting av data fra ende til ende på tvers av interne systemer og tredjepartsleverandører, med kryptografiske beviskjeder for GDPR artikkel 17-rett til sletting.',
        discovery: 'Som Data Steward (P-VN-05), kjør automatiserte skanninger for å oppdage personopplysninger på tvers av tilkoblede systemer, gjennomgå trinnvise funn og mate resultater inn i klassifiseringsmodulen for PDPL-merking.',
        governance: 'Som leverandørrisikobehandler (P-VN-08), vedlikehold leverandørregisteret, spor underprosessorkjeder, vurder risikoer for grenseoverskridende overføringer og håndhev styringshåndbøker under PDPL.',
        help_center: 'For alle personas -- DPOer, revisorer, IT-ledere og compliance managers -- finn svar, fremgangsmåteveiledninger og modulspesifikk dokumentasjon for å støtte dine daglige compliance-arbeidsflyter.',
        incident_ops: 'Som Incident Commander (P-VN-09), aktiver responsspillebøker, spor SLA-overholdelse mot det 72-timers PDPL varslingsvinduet, administrer eskaleringer og koordiner leverandørrespons.',
        incidents: 'Som hendelsessjef (P-VN-09), oppdage og rapporter datainnbrudd med 72-timers PDPL varslingsforpliktelser til PDP-komiteen, triage etter alvorlighetsgrad, og bygg revisjonsgraderte hendelsesdossierer.',
        localization: 'Som Enterprise DPO (P-VN-01), administrer livssyklusen for det regulatoriske rammeverket: installer vietnamesiske markedspakker, aktiver PDPL og dekret 13-rammeverk, kartlegg krav til plattformmoduler og administrer oversettelser.',
        monitoring: 'Som Enterprise DPO (P-VN-01), etablere pågående overvåkingsprogrammer, spor regulatoriske endringer, mål KPIer og administrer regulatoriske interaksjonssaker under PDPL.',
        onboarding: 'Som Compliance Trainer (P-VN-02), lever opplæring via guidede turer, last inn realistiske eksempeldata for praktiske øvelser, og kjør oppsettsveiviseren for hurtigstartscenarier.',
        organizations: 'Som DPO (P-VN-01), konfigurer overholdelsesstrukturer for flere organisasjoner, etablere avdelingshierarkier og rute datasubjektforespørsler på tvers av bedriftsgruppen din.',
        partner_workspace: 'Som Partner Account Manager, ombord partnerorganisasjoner, konfigurere tjenestenivåer, spore overholdelsesleveranser og administrere partnermarkedsplassen.',
        platform_settings: 'Som plattformadministrator, administrer all operasjonell konfigurasjon -- identitet, sikkerhet, integrasjoner og modultilgang som underbygger samsvarsprogrammet ditt.',
        privacy_scanner: 'Som personvernanalytiker (P-VN-06), kjør kontinuerlige overvåkingsskanninger for å oppdage brudd på informasjonskapslers samtykke, uautoriserte sporere og policyhull – og generer bevis for ePrivacy og GDPR-overholdelse.',
        program_governance: 'Naviger mellom modenhetsmodeller, vurderinger, bevisgap, ledersammendrag og mer. Hver undermodul bidrar til et helhetlig syn på din interne programhelse.',
        rights_requests: 'Som Enterprise DPO (P-VN-01), administrere forespørsler om registrerte rettigheter under PDPL med 15-dagers lovpålagte frister, identitetsverifisering og revisjonsgradsløsningsspor.',
        task_management: 'Rut etterlevelsesoppgaver på tvers av personas, spor SLA tidsfrister og automatiser arbeidsflyter som holder teamet ditt ansvarlig.',
        vendor_risk: 'Som Vendor Risk Manager (P-VN-08) kan du score tredjepartsrisiko, håndheve gatevurderinger og utløse revurderinger – forskjellig fra Program Governance, som sporer din interne modenhet.',
    },
    consolidation: {
        title: 'Konsolideringssaker',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Opprett konsolideringssak',
        case_count: 'case',
        created: 'Konsolideringssak opprettet',
        create: 'Opprett sak',
        creating: 'Oppretter...',
        cancel: 'Avbryt',
        no_cases: 'Ingen konsolideringssaker funnet.',
        all_statuses: 'Alle statuser',
        all_purposes: 'Alle formål',
        filter_status: 'Filtrer etter status',
        filter_purpose: 'Filtrer etter formål',
        col_title: 'Tittel',
        col_purpose: 'Formål',
        col_status: 'Status',
        col_progress: 'Fremgang',
        col_deadline: 'Frist',
        col_department: 'Avdeling',
        col_version: 'Versjon',
        col_evidence: 'Bevis',
        sections_accepted: 'accepted',
        prev: 'Forrige',
        next: 'Neste',
        page: 'Side',
        prev_page: 'Forrige side',
        next_page: 'Neste side',
        field_title: 'Tittel',
        field_purpose: 'Formål',
        field_framework: 'Rammekode',
        field_deadline: 'Frist',
        field_reviewer: 'Reviewer-bruker-ID',
        title_placeholder: 'Skriv inn sakstittel',
        framework_placeholder: 'f.eks. PDPD-2023',
        reviewer_placeholder: 'Skriv inn anmelder-bruker-ID',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Arkivering',
            cross_border_filing: 'Grenseoverskridende arkivering',
            annual_governance_attestation: 'Årlig styringsattest',
            breach_package: 'Bruddpakke',
            audit_inspection_pack: 'Revisjon Inspeksjonspakke',
        },
        statuses: {
            draft: 'Utkast',
            collecting: 'Collecting',
            consolidating: 'Konsoliderer',
            review: 'Gjennomgå',
            corrections: 'Rettelser',
            pending_approval: 'Venter på godkjenning',
            approved: 'Godkjent',
            completed: 'Fullført',
            closed: 'Stengt',
            rejected: 'Avvist',
        },
        section_statuses: {
            pending: 'Venter',
            in_progress: 'Pågår',
            submitted: 'Sendt inn',
            accepted: 'Godtatt',
            needs_correction: 'Trenger korrigering',
            corrected: 'Rettet',
        },
        flag_types: {
            missing_section: 'Manglende seksjon',
            conflicting_entry: 'Motstridende oppføring',
            stale_entry: 'Foreldet inngang',
            missing_evidence: 'Manglende bevis',
            incomplete_data: 'Ufullstendige data',
            duplicate_entry: 'Duplisert oppføring',
        },
        flag_severities: {
            critical: 'Kritisk',
            warning: 'Advarsel',
            info: 'Info',
        },
        sections_title: 'Seksjoner',
        no_sections: 'Ingen seksjoner er tildelt ennå.',
        flags_title: 'Flagg',
        no_flags: 'Ingen flagg oppdaget.',
        open: 'open',
        approval_chain: 'Godkjenningskjede',
        pending: 'Venter',
        audit_trail: 'Revisjonssti',
        no_audit: 'Ingen revisjonshendelser.',
        auto_assign: 'Auto-tilordne seksjoner',
        start_collection: 'Start samlingen',
        review_case: 'Gjennomgå sak',
        generate_export: 'Generer eksport',
        review_title: 'Konsolideringsgjennomgang',
        advance_approval: 'Gå videre til godkjenning',
        back_to_case: 'Tilbake til sak',
        accept: 'Godta',
        return_section: 'Retur',
        return_comment: 'Returner kommentar...',
        resolve: 'Løs',
        dismiss: 'Avvis',
        resolution_notes: 'Merknader om oppløsning...',
        notes: 'Notater',
        section_accepted: 'Seksjon akseptert',
        section_returned: 'Seksjon returnert for retting',
        advanced_to_approval: 'Saken avventer godkjenning',
        section_data: 'Seksjonsdata',
        save_progress: 'Lagre fremdrift',
        submit_section: 'Send inn seksjon',
        submitting: 'Sender inn...',
        resubmit: 'Send inn på nytt',
        progress_saved: 'Fremdriften er lagret',
        section_submitted: 'Seksjon innsendt',
        correction_required: 'Rettelse kreves',
        evidence_title: 'Bevis',
        no_evidence: 'Ingen bevis vedlagt.',
        add_evidence: 'Legg til bevis',
        evidence_added: 'Bevis lagt til',
        evidence_files: 'bevisfiler',
        uploaded_by: 'lastet opp av',
        comments_title: 'Kommentarer',
        no_comments: 'Ingen kommentarer ennå.',
        comment_placeholder: 'Legg til en kommentar...',
        add_comment: 'Legg til kommentar',
        comment_added: 'Kommentar lagt til',
        view_data: 'Se innsendte data',
        export_title: 'Eksport av dossier',
        export_generated: 'Eksportpakke generert',
        regenerate: 'Regenerer',
        regenerating: 'Regenererer...',
        generated_at: 'Generert kl',
        artifacts_title: 'Eksporter artefakter',
        artifact_type: 'Artefakt',
        file_name: 'Filnavn',
        file_size: 'Størrelse',
        generated: 'Generert',
        download: 'Last ned',
        artifacts: {
            master_report: 'Hovedrapport',
            evidence_bundle: 'Bevispakke (ZIP)',
            contributor_lineage: 'Bidragsyter avstamning',
            conflict_resolution: 'Konfliktløsning',
            approval_history: 'Godkjenningshistorikk',
        },
        official_forms: 'Offisielle skjemaer',
        forms_description: 'Følgende offisielle skjemaer kan genereres fra de konsoliderte dataene. Offisielle skjemaer er atskilt fra interne arbeidspapirer.',
        announce_loaded_cases: 'Lastet inn {count} konsolideringssaker',
        announce_case_detail_loaded: 'Saksdetaljene er lastet inn',
        announce_case_transitioned: 'Saken er overført til {status}',
        announce_flag_resolved: 'Flagg løst',
        announce_flag_dismissed: 'Flagg avvist',
        case_detail_loaded: 'Saksdetaljene er lastet inn',
        sections_auto_assigned: 'Seksjoner automatisk tildelt fra avdelingshierarki',
        errors: {
            load_failed: 'Kunne ikke laste inn data. Vennligst prøv igjen.',
            create_failed: 'Kunne ikke opprette sak. Vennligst prøv igjen.',
            required_fields: 'Fyll ut alle obligatoriske felt.',
            not_found: 'Ikke funnet.',
            comment_required: 'Kommentar er nødvendig når du returnerer en seksjon.',
            notes_required: 'Merknader kreves når du løser eller avviser et flagg.',
        },
    },
    rights_requests: {
        import: {
            title: 'Importer saker',
            nav_title: 'Masseimport',
            description: 'Last opp en CSV-fil for å masseimportere rettighetsforespørselssaker.',
            select_file: 'Velg CSV-fil',
            file_help: 'CSV med overskrifter som samsvarer med saksfelt.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Filen er for stor. Maksimal størrelse er 2MB.',
            preview: 'Forhåndsvisning',
            columns_detected: 'Kolonner oppdaget: {count}',
            column_mapping: 'Kolonnetilordning',
            import_button: 'Importer',
            import_complete: 'Import fullført',
            import_error: 'Import mislyktes',
            result: 'Importresultat',
            total: 'Totalt',
            imported: 'Importert',
            skipped: 'Hoppet over',
            failed: 'Feilet',
            errors: 'Feil',
            error_row: 'Rad',
            error_field: 'Felt',
            error_message: 'Melding',
            history: 'Importhistorikk',
            no_history: 'Ingen importhistorikk.',
            history_file: 'Filnavn',
            history_date: 'Dato',
        },
        batch: {
            nav_title: 'Batch prosess',
            page_title: 'DSR batchprosess',
            page_subtitle: 'Last opp en CSV av ventende DSR-er. Forseti utarbeider et bekreftelsessvar per forespørsel og setter en verifiseringsoppgave i kø per rad. DPO anmeldelser + godtar hver før sending.',
            upload_heading: 'Last opp',
            csv_hint: 'Påkrevde kolonner: request_id, requester_email, request_type. Valgfritt: frist (ISO-8601), merknader.',
            rows_heading: '{count} rader analysert',
            submit_label: 'Kjør batch',
            submitting: 'Utkaster …',
            col_request_id: 'Forespørsels-ID',
            col_email: 'Forespørsels e-post',
            col_type: 'Type',
            col_status: 'Status',
            col_actions: 'Handlinger',
            status_queued: 'I kø',
            status_drafted: 'Utkastet',
            status_failed: 'Mislyktes',
            status_accepted: 'Godtatt',
            status_rejected: 'Avvist',
            status_parsing_error: 'Parse feil',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Godta',
            reject: 'Avvis',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Vurderingsutløsere',
        subtitle: 'Konfigurer automatisert vurderingsoppretting og DPO varsler for personvernarbeidsflyter.',
        auto_create_dpia_label: 'Opprett DPIA automatisk når behandlingsaktivitet merket som DPIA kreves',
        auto_notify_new_purpose_label: 'Gi automatisk melding til DPO når nytt formål opprettes med sensitive datakategorier',
        auto_notify_cross_border_label: 'Automatisk varsling DPO når grenseoverskridende overføring legges til behandlingsaktivitet',
        default_dpo_label: 'Standard DPO',
        default_dpo_placeholder: 'Velg standard DPO...',
        notification_channels_label: 'Varslingskanaler',
        channel_in_app: 'I appen',
        channel_email: 'Email',
        save_success: 'Innstillinger for vurderingsutløser lagret.',
        save_error: 'Kunne ikke lagre innstillingene for vurderingsutløser.',
        fetch_error: 'Kunne ikke laste inn innstillingene for vurderingsutløser.',
        fetch_users_error: 'Kunne ikke laste inn brukere.',
        announce_loaded: 'Innstillinger for vurderingstrigger er lastet inn',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Linket',
        dpia_required_no_dpia: 'DPIA Påkrevd – ikke opprettet',
        create_dpia: 'Opprett DPIA',
        create_dpia_success: 'DPIA opprettet og knyttet til aktivitet.',
        create_dpia_error: 'Kunne ikke opprette DPIA for aktivitet.',
        view_dpia: 'Se DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Registrerte personers profiler',
        nav_title: 'Registrerte personer',
        nav: {
            profiles: 'Profiler',
        },
        create: 'Opprett profil',
        edit: 'Rediger profil',
        delete: 'Slett profil',
        delete_confirm: 'Er du sikker på at du vil slette denne datasubjektprofilen?',
        display_name: 'Vist navn',
        email: 'Email',
        phone: 'Telefon',
        employee_id: 'Ansatt ID',
        student_id: 'Student-ID',
        notes: 'Notater',
        external_refs: 'Eksterne referanser',
        cases_count: 'Saker',
        workflows_count: 'Samtykkeposter',
        last_activity: 'Siste aktivitet',
        timeline: 'Tidslinje',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Ingen registrerte profiler funnet.',
        search_placeholder: 'Søk etter navn, e-post, telefon eller ID...',
        create_success: 'Registreringsprofil opprettet.',
        create_error: 'Kunne ikke opprette den registrerte profilen.',
        update_success: 'Registreringsprofilen er oppdatert.',
        update_error: 'Kunne ikke oppdatere den registrerte profilen.',
        delete_success: 'Registreringsprofilen er slettet.',
        delete_error: 'Kunne ikke slette den registrerte profilen.',
        link_case_success: 'Sak knyttet til profil.',
        link_case_error: 'Kunne ikke koble sak til profil.',
        link_workflow_success: 'Arbeidsflyt knyttet til profil.',
        link_workflow_error: 'Kunne ikke koble arbeidsflyten til profilen.',
        load_error: 'Kunne ikke laste inn registrerte profiler.',
        detail_load_error: 'Kunne ikke laste inn den registrerte profilen.',
        timeline_load_error: 'Kunne ikke laste inn tidslinjen.',
        tab_cases: 'DSR-saker',
        tab_consent: 'Samtykkeposter',
        tab_timeline: 'Tidslinje',
        tab_consent_workflows: 'Samtykke arbeidsflyter',
        tab_web_consent: 'CMP Samtykke',
        web_consent_empty: 'Ingen CMP samtykkeposter funnet for dette emnet.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Kunne ikke laste inn CMP samtykkeposter.',
        col_policy_version: 'Policyversjon',
        col_versions: 'Versjoner',
        col_consent_version: 'Samtykke',
        col_cookie_declaration_version: 'Cookie-erklæring',
        col_purposes: 'Formål',
        purpose_granted: 'Gitt',
        purpose_denied: 'Nektet',
        granted_domains: 'Gitt',
        denied_domains: 'Nektet',
        col_action: 'Handling',
        col_timestamp: 'Tidsstempel',
        col_domain: 'Domene',
        no_cases: 'Ingen koblede saker.',
        no_workflows: 'Ingen tilknyttede samtykkearbeidsflyter.',
        no_timeline: 'Ingen tidslinjehendelser.',
        case_id: 'Saks-ID',
        case_status: 'Status',
        case_type: 'Forespørselstype',
        case_created: 'Opprettet',
        workflow_id: 'Arbeidsflyt-ID',
        workflow_type: 'Type',
        workflow_status: 'Status',
        workflow_created: 'Opprettet',
        timeline_date: 'Dato',
        timeline_event: 'Hendelse',
        timeline_details: 'Detaljer',
        timeline_time: 'Tid',
        timeline_type: 'Type',
        timeline_summary: 'Sammendrag',
        type_case: 'DSR sak',
        type_consent_workflow: 'Arbeidsflyt for samtykke',
        profile_info: 'Profilinformasjon',
        announce_loaded: 'Registreringsprofiler er lastet inn',
        announce_detail_loaded: 'Registreringsprofilen er lastet inn',
        select_case: '— Velg en sak —',
        select_workflow: '— Velg en arbeidsflyt —',
        no_cases_hint: 'Koble saker for å se dem her.',
        no_workflows_hint: 'Koble samtykkeprosesser for å se dem her.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} systemer · {flows} flyter · {crossBorder} grenseoverskridende · {highRisk} høy/kritisk risiko',
            switch_to_list: 'Bytt til listevisning',
            switch_to_list_aria: 'Bytt til tabellvisning',
            svg_aria: 'Dataflytkart: {systems} systemer, {flows} flyter, {crossBorder} grenseoverskridende',
            selected_system: 'Valgt system',
            drill_hint: 'Åpne listefanen for å redigere de koblede flytene, DPIAene og leverandørtilordningene for dette systemet.',
        },
    },
    packages: {
        light: {
            name: 'Lys',
            description: 'Viktige personvernsamsvarsverktøy for små team',
        },
        standard: {
            name: 'Standard',
            description: 'Fullt utstyrt samsvarsplattform for voksende organisasjoner',
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Samsvar på bedriftsnivå med multi-org-støtte og dedikerte tjenester',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Leverandørinnstillinger',
            subtitle: 'Konfigurer Claude-leverandøren for Forseti AI juridisk rådgiver.',
            healthHeading: 'Leverandørhelse',
            statusLabel: 'Status',
            statusOk: 'OK',
            statusFailed: 'Utilgjengelig',
            providerLabel: 'Leverandør',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI stafett',
            providerAesirManaged: 'Aesir-administrerte tokens',
            lastCheckedLabel: 'Sist sjekket',
            reasonLabel: 'Grunn',
            refreshButton: 'Oppdater',
            apiKeyHeading: 'Claude API nøkkel',
            apiKeyHint: 'Ved å angi en API-tast aktiveres Claude API-modus. La være uaktivert for å bruke det lokale CLI-reléet (utvikler-/selvvertsmodus).',
            apiKeyLabel: 'API-tasten',
            apiKeyMinLength: 'Minimum 32 tegn. Kryptert i hvile; aldri vist igjen.',
            apiKeyTooShort: 'API-tasten må være på minst 32 tegn.',
            apiKeySaved: 'API nøkkel lagret. Forfriskende helse...',
            apiKeyCleared: 'API-tasten er slettet. Forseti bruker nå det lokale CLI-reléet.',
            setKeyButton: 'Lagre API-tasten',
            clearKeyButton: 'Fjern API-tasten (bruk CLI-relé)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Bruk',
            recentHeading: 'Nylig aktivitet',
            historyHeading: 'Siste 12 perioder',
            noActivity: 'Ingen aktivitet ennå denne perioden.',
            featureLabel: 'Funksjon',
            whenLabel: 'Når',
            periodLabel: 'Periode',
            byFeatureHeading: 'Denne måneden etter funksjon',
            byFeatureEmpty: 'Ingen funksjonsaktivitet ennå denne perioden.',
            featureColumn: 'Funksjon',
            featureUnitsColumn: 'Enheter',
            featureRunsColumn: 'Løper',
            byUserHeading: 'Denne måneden etter bruker (administratorvisning)',
            byUserEmpty: 'Ingen aktivitet per bruker ennå denne perioden.',
            userColumn: 'Bruker',
            reconciliationLinkLabel: 'Se rapport for avstemmingsavvik',
            reconciliationLinkHint: 'Kun Aesir-intern administrator.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chat',
                workflow: 'Arbeidsflytkjøring',
                tabular: 'Tabellutvinning',
                redline: 'Redline anmeldelse',
                title: 'Samtaletittel',
                unknown: 'Annet',
            },
        },
        reconciliation: {
            title: 'Forseti-kvoteavstemming',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Tilbake til Forseti-bruk',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Denne siden er begrenset til Aesir-interne administratorer. Hvis du mener at du bør ha tilgang, kontakt din AesirX-representant.',
        },
        projects: {
            title: 'Prosjekter',
            subtitle: 'Arbeidsområder for dokumenter, samtaler, arbeidsflyter og anmeldelser.',
            modeLabel: 'Visning',
            tabMine: 'Mine prosjekter',
            tabShared: 'Delt med meg',
            searchLabel: 'Søk etter prosjekter',
            searchPlaceholder: 'Søk...',
            newButton: 'Nytt prosjekt',
            colName: 'Navn',
            colVisibility: 'Synlighet',
            colDocs: 'Dokumenter',
            colMembers: 'Medlemmer',
            colUpdated: 'Oppdatert',
            visibilityOwn: 'Kun eie',
            visibilityShared: 'Delt',
            emptyMine: 'Ingen prosjekter ennå. Opprett en for å komme i gang.',
            emptyShared: 'Ingen prosjekter har blitt delt med deg.',
            pageHint: 'Viser {shown} av {total}.',
            createTitle: 'Nytt prosjekt',
            createSubmit: 'Lag prosjekt',
            fieldName: 'Navn',
            fieldDescription: 'Beskrivelse (valgfritt)',
            fieldCmNumber: 'Saks-/saksnummer (valgfritt)',
            fieldVisibility: 'Synlighet',
            errorNameRequired: 'Navn er påkrevd.',
            errorSelectorRequired: 'Velg en bruker eller organisasjonsgruppe du vil legge til.',
            renameButton: 'Gi nytt navn',
            renamePrompt: 'Gi nytt navn til prosjektet',
            deleteButton: 'Slett',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Vil du slette denne mappen? Dokumenter inne vil forbli i prosjektet, bare uarkivert.',
            removeMemberConfirm: 'Vil du fjerne dette medlemmet?',
            foldersHeading: 'Mapper',
            foldersEmpty: 'Ingen mapper ennå.',
            addFolderButton: 'Ny mappe',
            addFolderTitle: 'Ny mappe',
            documentsHeading: 'Dokumenter',
            documentsEmpty: 'Ingen dokumenter vedlagt ennå.',
            colFilename: 'Filnavn',
            colAdded: 'Lagt til',
            unattachButton: 'Løsne',
            membersHeading: 'Medlemmer',
            membersEmpty: 'Kun eier.',
            addMemberButton: 'Legg til medlem',
            addMemberTitle: 'Legg til medlem',
            addMemberSubmit: 'Legg til medlem',
            colMember: 'Medlem',
            colRole: 'Rolle',
            selectorTypeLabel: 'Legg til etter',
            selectorUser: 'Bruker-ID',
            selectorGroup: 'Organisasjonsgruppe-ID',
            fieldUserId: 'Bruker',
            fieldGroupId: 'Organisasjonsgruppe',
            fieldRole: 'Rolle',
            roleAdmin: 'Admin',
            roleEditor: 'Redaktør',
            roleViewer: 'Seer',
            notFound: 'Finner ikke prosjektet.',
            selectUserPlaceholder: 'Velg en bruker...',
            selectGroupPlaceholder: 'Velg en organisasjonsgruppe...',
            noUsersAvailable: 'Ingen brukere tilgjengelig.',
            noGroupsAvailable: 'Ingen tilgjengelige organisasjonsgrupper.',
            loadFailed: 'Kunne ikke laste inn alternativer.',
            deleteProjectTitle: 'Vil du slette prosjektet?',
            deleteFolderTitle: 'Vil du slette mappen?',
            removeMemberTitle: 'Vil du fjerne medlem?',
            removeMemberConfirmGeneric: 'Vil du fjerne dette medlemmet fra prosjektet?',
            addDocumentButton: 'Legg til dokument',
            addDocumentTitle: 'Legg til dokument i prosjekt',
            fieldDocument: 'Velg dokument',
            selectDocPlaceholder: 'Søk etter et dokument...',
            ownerLabel: 'Systemeier',
        },
        chat: {
            sidebarLabel: 'Chatter',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Din AI juridiske og overholdelsesrådgiver – basert på leietakerdataene dine og de installerte reguleringspakkene dine.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Velg en persona',
            startChatButton: 'Start en ny chat',
            startingChat: 'Starter chat …',
            recentHeading: 'Nylige chatter',
            recentEmpty: 'Ingen chatter ennå - start en ovenfor.',
            untitledChat: 'Ny chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Samtale',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'Send en melding',
            composeLabel: 'Melding Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Forhåndsvalgt persona',
            changePersona: 'Bytt persona',
            hidePersonaOptions: 'Skjul personavalg',
            starterHeading: 'Start med en av disse handlingene',
            renameButton: 'Gi nytt navn',
            renamePrompt: 'Gi nytt navn til chat',
            deleteButton: 'Slett',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat ikke funnet.',
            emptyChat: 'Ingen meldinger ennå – si hei for å starte en samtale.',
            roleUser: 'Du',
            roleAssistant: 'Forseti',
            roleSystem: 'System',
            thinkingLabel: 'Tenker',
            toolCallPlaceholder: 'Verktøykall: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Dokumentredigeringer foreslått (V{version}).',
            disclaimer: 'Forseti støtter dommen din – den erstatter ikke autorisert advokat.',
            sendButton: 'Send',
            sending: 'Sender …',
            composePlaceholder: 'Spør Forseti...',
            composingPlaceholder: 'Forseti svarer...',
            streamingPlaceholder: 'Forseti skriver...',
            promptInjectionTitle: 'Forseti droppet noen kontrolltokens.',
            promptInjectionBody: 'Strippede mønstre: {patterns}. Forseti vil bare se meldingsteksten din.',
            rateLimitTitle: 'Du sender meldinger for fort.',
            rateLimitRetryAfter: 'Prøv igjen om {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti er usikker her.',
            counselReviewBody: 'Bekreft med autorisert advokat før du handler på dette svaret.',
            legalConclusionRefused: 'Forseti nektet å avgi en juridisk konklusjon uten begrunnelse. Prøv search_regulatory først eller eskaler til råd.',
            toolCallDone: 'Ferdig.',
            toolCallArgsLabel: 'Argumenter',
            toolCallResultLabel: 'Resultat',
            toolResultLogged: 'Verktøyresultat fanget i revisjonslogg.',
            toolResultAria: 'verktøyresultat',
            documentEditAria: 'dokumentredigering',
            createTaskFromMessage: 'Lag oppgave fra dette',
            createTaskHeading: 'Opprett oppgave fra denne meldingen',
            createTaskTitleLabel: 'Tittel',
            createTaskTitleRequired: 'Tittel er påkrevd.',
            createTaskDescriptionLabel: 'Beskrivelse',
            createTaskAssigneeLabel: 'Oppdragstaker',
            createTaskAssigneePlaceholder: 'Velg mottaker...',
            createTaskAssigneeNone: '— ikke tildelt —',
            createTaskDueDateLabel: 'Forfallsdato',
            createTaskWorkflowLabel: 'Koblet arbeidsflyt (valgfritt)',
            createTaskWorkflowPlaceholder: 'Velg en arbeidsflyt...',
            createTaskWorkflowNone: '— ingen —',
            renameModalSubmit: 'Gi nytt navn',
            renameModalSaving: 'Lagrer …',
            deleteConfirmTitle: 'Vil du slette chatten?',
            createTaskSubmit: 'Lag oppgave',
            createTaskSubmitting: 'Oppretter …',
            cancelButton: 'Avbryt',
            autoRouteHintMessage: 'Dette spørsmålet er mer i {persona} sitt styrehus.',
            autoRouteSwitchButton: 'Bytt til {persona}',
            autoRouteSwitching: 'Bytter …',
            handoffSuggestionMessage: 'Eller åpne en ny chat med {persona} som fører dette spørsmålet videre.',
            handoffSuggestionButton: 'Start ny chat med {persona}',
            handoffSuggestionWorking: 'Åpner …',
            handoffSuggestionDismiss: 'Ikke nå',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance-spesialist',
                'forseti-legal': 'Juridisk rådgiver',
                'forseti-dpo': 'Personvernrådgiver',
                'forseti-risk': 'Risikoanalytiker',
                'forseti-auditor': 'Revisorblikk',
            },
            exportButton: 'Eksporter PDF',
            exportRunning: 'Eksporterer…',
            exportSuccess: 'Eksportert: {filename} (åpne filnettleseren).',
            exportOpenFile: 'Åpne i filnettleseren',
            exportDownload: 'Last ned PDF',
            citation: {
                verifyBadge: '[VERIFY]',
                verifyAria: 'sitatet kunne ikke bekreftes – gjennomgå før du stoler på',
                popoverAria: 'Kilde for {label}',
                openSource: 'Åpen kildekode-side',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-generert hendelse fra Forseti revisjonsspor.',
        },
        rowAction: {
            ask: 'Spør Forseti',
            tooltipOpen: 'Spør Forseti om dette {recordType}',
            tooltipLocked: 'Denne {recordType} er låst — Forseti kan svare på spørsmål, men kan ikke gjøre endringer.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — om denne posten',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Sammendrag',
            suggestedPersona: 'Foreslått persona',
            askForseti: 'Spør Forseti',
            runWorkflow: 'Kjør en arbeidsflyt',
            viewLineage: 'Se revisjonslinje',
            createPdplEvidencePack: 'Opprett PDPL-dokumentasjonspakke',
            prepareBreachDraft: 'Forbered varslingsutkast',
            primaryActions: {
                projectSummary: 'Oppsummer prosjektdokument',
                supplementSummary: 'Oppsummer tilleggsforespørsel',
                authoritySummary: 'Oppsummer myndighetsforespørsel',
                rightsRequestResponse: 'Utkast til DSR-svar',
                dpiaQuickReview: 'Kjør rask DPIA-gjennomgang',
                taskControlWalkthrough: 'Gå gjennom kontrollbevis',
                vendorRiskNarrative: 'Generer leverandørrisiko-narrativ',
                incidentLetter: 'Utkast til PDPL-bruddbrev',
                ropaSufficiency: 'Kjør RoPA-tilstrekkelighetssjekk',
                vendorDpaReview: 'Kjør leverandør-DPA-gjennomgang',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti utarbeider bare utkast til det tilknyttede Mẫu số 08. Det avgjør ikke om varsling kreves, og sender ikke inn eller videresender skjemaet.',
            breachDraftReady: 'Utkast til Mẫu số 08 klart for gjennomgang',
            breachDraftReviewBeforeSelecting: 'Gjennomgå før valg',
            workflowHeading: 'Relevante arbeidsflyter',
            workflowLoading: 'Laster relevante arbeidsflyter…',
            workflowEmpty: 'Fant ingen egnet arbeidsflyt. Opprett en veiledet arbeidsflyt for denne posten.',
            workflowError: 'Kunne ikke laste relevante arbeidsflyter.',
            selectWorkflow: 'Velg {title}',
            workflowSelected: 'Arbeidsflyten er valgt og klar til å kjøres for denne posten.',
            workflowRunSelected: 'Kjør valgt arbeidsflyt',
            workflowStarting: 'Starter arbeidsflyt…',
            workflowRunError: 'Kunne ikke starte denne arbeidsflyten.',
            workflowCreateGuided: 'Opprett en veiledet arbeidsflyt for denne posten',
            lineageHeading: 'Revisjonsspor for denne posten',
            lineageLoading: 'Laster revisjonsspor…',
            lineageEmpty: 'Det er ikke registrert noen Forseti-sporhendelser for denne posten.',
            lineageError: 'Kunne ikke laste revisjonsspor.',
            retry: 'Prøv igjen',
            chatContextLoading: 'Laster kontekst for posten…',
            chatGuidance: {
                general: 'Forseti kan oppsummere posten, identifisere manglende informasjon og foreslå gjennomgangstrinn.',
                privacy: 'Forseti kan gjennomgå personvernopplysninger, kontrollere forpliktelser i installerte regelverk og hjelpe med å lage et utkast for menneskelig gjennomgang.',
                compliance: 'Forseti kan identifisere etterlevelsesmangler, forankre krav i installerte kilder og foreslå ansvarlige neste trinn.',
                legal: 'Forseti kan oppsummere posten, finne underbygget kildemateriale og hjelpe med å lage et utkast for juridisk gjennomgang.',
                risk: 'Forseti kan vurdere registrerte risikoer, markere manglende fakta og foreslå forholdsmessige gjennomgangstrinn.',
                audit: 'Forseti kan undersøke mangler i bevisgrunnlaget, knytte posten til installerte kontroller og foreslå revisjonsklare oppfølginger.',
            },
            starterPrompts: {
                identifyGaps: 'Hvilken informasjon eller hvilket bevisgrunnlag mangler i denne posten?',
                reviewFrameworks: 'Hvilke forpliktelser i de installerte regelverkene våre bør jeg gjennomgå for denne posten?',
                prepareDraft: 'Hjelp meg med å lage et utkast som bare bygger på denne posten og underbygde installerte kilder.',
                nextSteps: 'Hvilke gjennomgangstrinn bør jeg ta videre?',
                summarizeRecord: 'Oppsummer posten og marker punkter som krever menneskelig gjennomgang.',
                assessRisk: 'Vurder de registrerte risikoene, identifiser manglende fakta og foreslå gjennomgangstrinn.',
            },
            disclaimer: 'Forseti støtter dommen din – den erstatter ikke autorisert advokat.',
            recordType: {
                project: 'Prosjekt',
                dpia: 'DPIA',
                dossier_filing: 'Dossierarkivering',
                transfer_assessment: 'Overføring over landegrensene',
                vendor: 'Leverandør',
                vendor_contract: 'Leverandørkontrakt',
                vendor_acceptance: 'Leverandørens risikoaksept',
                incident: 'Hendelse',
                incident_op: 'Hendelsesoperasjon',
                breach_notification: 'Bruddvarsel',
                authority_request: 'Myndighetsforespørsel',
                supplement_request: 'Tilleggsforespørsel',
                rights_request: 'Rettighetsforespørsel',
                contract: 'Kontrakt',
                task: 'Oppgave',
                data_mapping_activity: 'RoPA-aktivitet',
                compliance_rule: 'Samsvarsregel',
                program: 'Program',
                governance_document: 'Styringsdokument',
                remediation_plan: 'Saneringsplan',
                cross_border_filing: 'Grenseoverskridende arkivering',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Be Forseti utarbeide et utkast til dette',
            drafting: 'Utkaster …',
            hint_pick_activity: 'Velg en koblet behandlingsaktivitet for å aktivere Forseti-utkast.',
            draft_origin_label: 'Forseti-utkast — gjennomgang per felt',
            tokens_label: '≈ {count} tokens fra din månedlige bøtte',
            accept_selected: 'Godta valgt',
            reject_all: 'Avvis utkast',
            field_checkbox_label: 'Bruk Forseti-utkast for {field}',
            field_labels: {
                description: 'Beskrivelse',
                purpose: 'Formål',
                data_subjects: 'Datasubjekter',
                data_types: 'Datatyper',
                necessity_justification: 'Nødvendighet og proporsjonalitet',
                consultation_notes: 'Høringsnotater',
                dpo_opinion: 'DPO mening',
                system_ids: 'Koblede systemer',
                vendor_ids: 'Tilknyttede leverandører',
            },
            error_generic: 'Forseti kunne ikke utarbeide denne DPIA. Prøv igjen eller avgrens aktivitetsposten.',
            variant_legend: 'Rammeramme',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Ingen installerte rammeverk er tilgjengelige',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Vær oppmerksom: {count} konflikter på tvers av rammeverk gjelder for denne varianten. Se gjennom før du godtar.',
        },
        formDraft: {
            button_label: 'Be Forseti om å utarbeide dette skjemaet',
            drafting: 'Utarbeider…',
            draft_origin_label: 'Forseti-utkast — gjennomgå hvert felt',
            tokens_label: '≈ {count} tokens fra den månedlige kvoten din',
            accept_selected: 'Godta valgte',
            reject_all: 'Avvis utkast',
            field_checkbox_label: 'Bruk Forseti-utkast for {field}',
            excluded_note: '{count} felt kan ikke utarbeides (referanser, vedlegg eller skrivebeskyttet).',
            error_generic: 'Forseti kunne ikke utarbeide dette skjemaet. Prøv igjen.',
            empty_draft: 'Forseti laget ingen feltutkast for dette skjemaet.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Be Forseti om å utarbeide denne TIA',
            drafting: 'Utkaster TIA …',
            hint_pick_destination: 'Angi destinasjonslandet på overføringsposten før Forseti kan draft.',
            draft_origin_label: 'Forseti TIA-utkast — gjennomgang per felt',
            tokens_label: '≈ {count} tokens fra din månedlige bøtte',
            accept_selected: 'Godta valgt',
            reject_all: 'Avvis utkast',
            field_checkbox_label: 'Bruk Forseti-utkast for {field}',
            error_generic: 'Forseti kunne ikke utarbeide denne TIA. Prøv på nytt eller avgrens overføringsposten.',
            usage_details: 'Detaljer om KI-bruk',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Anbefalte oppdateringer av oppføring',
            mechanism_recommendation: 'Overføringsmekanisme: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Angi en gjennomgangsfrist før du oppdaterer denne sensitive overføringen.',
            processing_activity_warning: 'Koble til minst én behandlingsaktivitet / ROPA-oppføring før du fullfører TIA-en.',
            go_to_field: 'Gå til felt',
            adequacy_label: 'Destinasjonsregime',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Overgangs-/EØS-snarvei',
            adequacy_status_unknown: 'Ukjent – angi destinasjon',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Vær oppmerksom: {count} konflikt(er) på tvers av rammeverk gjelder for dette overføringsregimet. Se gjennom før du godtar.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Manglende påkrevde opplysninger',
            missing_facts_hint: 'Forseti merket opplysninger som ikke finnes i posten. Felter som avhenger av dem, er ikke forhåndsvalgt — oppgi opplysningene eller godta feltet bevisst.',
            risk_label: 'Kalibrert risiko',
            risk_low: 'Lav',
            risk_medium: 'Middels',
            risk_high: 'Høy',
            jurisdiction_label: 'Jurisdiksjonsutløsere',
            jurisdiction_none_installed: 'Ingen installert rammeverk dekker denne vurderingen — ingen rettslig ordning ble vurdert. Installer en relevant regelverkspakke før du stoler på de rettslige konklusjonene i dette utkastet.',
            jurisdiction_none_assessed: 'Forseti returnerte ingen jurisdiksjonsvurdering. Ingenting ble vurdert — dette er ikke en konklusjon om at ingen ordning gjelder.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Laster navnene på de vurderte ordningene…',
            trigger_applicable: 'Gjelder',
            trigger_not_applicable: 'Ikke utløst',
            trigger_unknown: 'Ukjent — opplysning mangler',
            recipient_role_label: 'Mottakerens rolle',
            recipient_role_controller: 'Behandlingsansvarlig',
            recipient_role_processor: 'Databehandler',
            recipient_role_joint_controller: 'Felles behandlingsansvarlig',
            recipient_role_unknown: 'Ukjent — ikke oppgitt',
            opt_in_hint: 'Juridisk konklusjon — velg kun hvis du godtar den med tilgjengelige fakta.',
        },
        mcp: {
            pageTitle: 'MCP-koblingsnøkler',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Utsted ny nøkkel',
            issueModalTitle: 'Utsted MCP-nøkkelen API',
            labelField: 'Etikett',
            labelHint: 'En kort beskrivelse (f.eks. "AI kapasitetslaboratorium – regulatorisk forskning").',
            permittedToolsField: 'Tillatte verktøy',
            expiresAtField: 'Utløper (valgfritt)',
            expiresAtHint: 'La stå tomt for aldri utløper.',
            submit: 'Utsted nøkkel',
            cancel: 'Avbryt',
            keyDisplayedOnce: 'Kopier denne nøkkelen nå - du vil IKKE kunne se den igjen. Lagre det i din hemmelige manager (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'Jeg kopierte det - lukk',
            colLabel: 'Etikett',
            colPrefix: 'Prefiks',
            colStatus: 'Status',
            colTools: 'Verktøy',
            colCreatedAt: 'Opprettet',
            colLastUsed: 'Sist brukt',
            colCalls: 'Samtaler',
            colActions: 'Handlinger',
            statusActive: 'Aktiv',
            statusRevoked: 'Opphevet',
            statusExpired: 'Utløpt',
            revokeAction: 'Oppheve',
            rotateAction: 'Roter',
            confirmRevokeTitle: 'Vil du tilbakekalle denne MCP-nøkkelen?',
            confirmRevokeBody: 'Eksterne Claude-klienter som bruker denne nøkkelen vil miste tilgangen umiddelbart. Dette kan ikke angres.',
            confirmRevoke: 'Opphev nøkkel',
            rotatedTitle: 'Nøkkel rotert',
            rotatedBody: 'Den nye klartekstnøkkelen er nedenfor. Den gamle nøkkelen fortsetter å virke i 5 minutter, så alle anrop under flyet avsluttes.',
            emptyState: 'Ingen MCP-nøkler er utstedt ennå.',
            errorIssue: 'Kunne ikke utstede MCP-nøkkelen.',
            errorList: 'Kunne ikke laste inn MCP-nøkler.',
            errorRevoke: 'Kunne ikke trekke tilbake nøkkelen.',
            errorRotate: 'Kunne ikke rotere nøkkelen.',
            neverUsed: '—',
            neverExpires: 'Aldri',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forseti minne',
            pageSubtitle: 'Bla gjennom og kurater den organisasjonsspesifikke konteksten Forseti har samlet på tvers av samtaler og beslutninger. Kurserte oppføringer låses mot nattlig oppbevaring og forblir tilgjengelige for alle personer.',
            filterCategory: 'Kategori',
            filterAllCategories: 'Alle kategorier',
            totalCount: '{count} oppføringer',
            colAnchor: 'Anker',
            colCategory: 'Kategori',
            colSource: 'Kilde',
            colContent: 'Innhold',
            colCreatedAt: 'Opprettet',
            colActions: 'Handlinger',
            editButton: 'Rediger',
            deleteButton: 'Slett',
            promoteButton: 'Fremme til kuratert',
            emptyState: 'Ingen minner ennå - Forseti vil samle kontekst mens du chatter.',
            categories: {
                framework_interpretation: 'Rammetolkning',
                process_preference: 'Prosesspreferanse',
                escalation_threshold: 'Eskaleringsterskel',
                decision_context: 'Beslutningskontekst',
                glossary_term: 'Ordlistebegrep',
                auto_inferred: 'Automatisk utledet',
            },
            sources: {
                admin_curated: 'Kuratert',
                user_explicit: 'Eksplisitt',
                auto: 'Utledet',
            },
            deleteConfirmTitle: 'Vil du slette dette minnet?',
            deleteConfirmBody: 'Sletting vil fjerne dette ankeret fra hver fremtidig Forseti-chatoppstart. Anker: {anchor}',
            deleteConfirmLabel: 'Slett',
            deleteCancelLabel: 'Avbryt',
            editModalTitle: 'Rediger minne',
            editAnchorLabel: 'Anker',
            editCategoryLabel: 'Kategori',
            editContentLabel: 'Innhold',
            editContentCount: '{n}/{max} tegn',
            editEmptyError: 'Innholdet kan ikke være tomt.',
            editTooLongError: 'Innholdet må være ≤1000 tegn.',
            editAutoPromoteHint: 'Lagring av dette automatisk utledede minnet vil merke det som brukerbekreftet og fjerne det fra oppbevaringssveipet.',
            editCancelButton: 'Avbryt',
            editSaveButton: 'Lagre',
            editSaving: 'Lagrer …',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Revisjonsslekt',
            subtitle: 'Spor hver AI-genererte artefakt som er knyttet til en post. Chatter, verktøyanrop, oppgaver, bevisutkast og dokumentversjoner.',
            filterForm: 'Filtre',
            recordType: 'Record type',
            recordId: 'Registrer ID',
            dateStart: 'Fra',
            dateEnd: 'Til',
            search: 'Søk',
            loading: 'Laster inn …',
            exportPdf: 'Eksporter PDF',
            exportCsv: 'Eksporter CSV',
            nodesHeading: 'Avstamningsnoder',
            colType: 'Type',
            colLabel: 'Etikett',
            colOccurredAt: 'Oppstod kl',
            colActor: 'Skuespiller',
            colId: 'ID',
            noResults: 'Ingen rådgiveraktivitet for denne posten.',
            draftBadge: 'AI-generert utkast',
            prev: 'Forrige',
            next: 'Neste',
            viewToggleTable: 'Vis som tabell',
            viewToggleGraph: 'Vis som graf',
            graphAriaLabel: 'Linjegraf (visuelt). Bruk tabellvisningen for hjelpemidler.',
            graphLegendDraft: 'Utkast til AI bevis',
            graphEmpty: 'Ingen noder å tegne.',
            paginationAriaLabel: 'Paginering',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Eksport mislyktes: {message}',
            truncatedNotice: 'Denne sporbarheten er ikke fullstendig. Plattformen leser høyst et fast antall rader fra hver kilde, og grensen ble nådd for: {sources}. Rader utover en grense, hvis det finnes noen, vises ikke. Avgrens datoperioden for å få dem med.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Prosjekt',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Leverandør',
                incident: 'Hendelse',
                incident_op: 'Hendelsesoperasjon',
                rights_request: 'Rettighetsforespørsel',
                contract: 'Kontrakt',
                task: 'Oppgave',
                data_mapping_activity: 'Datakartleggingsaktivitet',
                compliance_rule: 'Overholdelsesregel',
                program: 'Program',
            },
            nodeTypes: {
                chat: 'Chat',
                tool_call: 'Verktøykall',
                workflow_run: 'Arbeidsflytkjøring',
                task: 'Oppgave',
                evidence: 'Bevis',
                document_version: 'Dokumentversjon',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Drevet',
                hash_mismatch: 'Hash-mismatch',
                merkle_mismatch: 'Merkle misforhold',
                source_missing: 'Kilde mangler',
                not_found: 'Ikke funnet',
                chain_unreachable: 'Kjede uoppnåelig',
                service_unreachable: 'Tjenesten er utilgjengelig',
                pending: 'Venter på forankring',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-generert UTKAST',
            pendingReviewBadge: 'Venter på vurdering',
            approvedBadge: 'Godkjent',
            rejectedBadge: 'Avvist',
            moveToReviewButton: 'Merk klar for anmeldelse',
            approveButton: 'Godkjenne',
            rejectButton: 'Avvis',
            rejectReasonLabel: 'Grunn til avvisning',
            humanSignoffBanner: 'Forseti støtter dommen din – den erstatter ikke autorisert advokat. AI-generert bevis krever menneskelig godkjenning før det teller som formelt bevis.',
            pageTitle: 'AI bevisutkast',
            pageSubtitle: 'Se gjennom, godkjenn eller avvis AI-genererte bevis. Utkast må godkjennes av et menneske før de teller som formelt bevis.',
            filterAll: 'Alle handlingsdyktige',
            filterDrafts: 'Kun utkast',
            filterPending: 'Venter på vurdering',
            filterApproved: 'Nylig godkjent',
            filterRejected: 'Nylig avvist',
            colId: 'Bevis-ID',
            colStatus: 'Status',
            colSource: 'Kilde',
            colCreatedAt: 'Opprettet',
            colActions: 'Handlinger',
            empty: 'Ingen AI bevisutkast i denne statusen.',
            confirmApprove: 'Godkjenne dette AI-genererte beviset som formelt?',
            confirmReject: 'Avvis dette bevisutkastet. Dette kan ikke angres.',
            reasonRequired: 'Det kreves en avvisningsgrunn.',
            rejectModalTitle: 'Avvis utkast til bevis',
            approveModalTitle: 'Godkjenne utkast til bevis',
            operationFailed: 'Operasjonen mislyktes. Vennligst prøv igjen.',
            moveToReviewSuccess: 'Merket klar for vurdering.',
            approveSuccess: 'Godkjent.',
            rejectSuccess: 'Avvist.',
            sourceLabelWorkflowRunTitle: 'Arbeidsflyt: {title}',
            sourceLabelWorkflowRunId: 'Arbeidsflytkjøring {id}',
            sourceLabelChatTitle: 'Chat: {title}',
            sourceLabelChatId: 'Forseti chat {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Filterstatus',
        },
        workflows: {
            pageTitle: 'AI-arbeidsflyter',
            pageSubtitle: 'Lagrede AI-promptmaler som produserer oppgaver og utkast til bevis.',
            newWorkflow: 'Ny arbeidsflyt',
            newWorkflowHeading: 'Ny AI-arbeidsflyt',
            tabAll: 'Alle',
            tabBuiltin: 'Innebygd pakke',
            tabMine: 'Mine arbeidsflyter',
            tabDetail: 'Detaljer',
            tabLinkedTasks: 'Tilknyttede oppgaver',
            builtinBadge: 'Innebygd',
            builtinSubtitle: 'Innebygd arbeidsflyt.',
            userSubtitle: 'Tilpasset arbeidsflyt.',
            emptyHint: 'Ingen arbeidsflyter ennå.',
            loadTimeoutError: 'Kunne ikke laste arbeidsflyter etter 10 sekunder. Prøv igjen, eller sjekk nettleserens nettverksfane for en hengende forespørsel.',
            loadError: 'Kunne ikke laste arbeidsflyter.',
            hideError: 'Skjul mislyktes.',
            unhideError: 'Vis mislyktes.',
            retry: 'Prøv igjen',
            producesTasks: 'Produserer oppgaver',
            producesEvidence: 'Produserer utkast til bevis',
            hideButton: 'Skjul fra min liste',
            unhideButton: 'Vis igjen',
            runButton: 'Kjør',
            editButton: 'Rediger',
            shareButton: 'Del',
            backToList: 'Tilbake til listen',
            requireTitleAndPrompt: 'Tittel og prompt er påkrevd.',
            fieldTitle: 'Tittel',
            fieldDescription: 'Beskrivelse',
            fieldType: 'Type',
            fieldPersona: 'Persona-affinitet',
            fieldPractice: 'Praksis',
            fieldPrompt: 'Spørre (Markdown)',
            fieldProducesTasks: 'Produserer oppgaver',
            fieldProducesEvidence: 'Produserer utkast til bevis',
            fieldProjectId: 'Prosjekt (valgfritt)',
            fieldAttachmentIds: 'Vedlegg',
            fieldParentRecord: 'Overordnet post (valgfritt)',
            fieldPersonaOverride: 'Persona-overstyring (valgfritt)',
            typeAssistant: 'Assistent',
            typeTabular: 'Tabellform',
            personaNone: '— ingen —',
            parentNone: '— ingen —',
            parentIdPlaceholder: 'Overordnet post-ID',
            selectProjectPlaceholder: 'Velg et prosjekt…',
            selectProjectFirst: 'Velg et prosjekt for å velge vedlegg.',
            selectAttachmentsPlaceholder: 'Velg dokumenter…',
            noAttachmentsInProject: 'Dette prosjektet har ingen vedlagte dokumenter.',
            selectParentRecordPlaceholder: 'Velg en post…',
            selectParentRecordFirst: 'Velg først en posttype.',
            noRecordsForType: 'Ingen poster av denne typen er tilgjengelige.',
            shareUserSelectorLabel: 'Mottakere (brukere)',
            shareGroupSelectorLabel: 'Mottakere (organisasjonsgrupper)',
            selectUsersPlaceholder: 'Velg brukere…',
            selectOrgGroupsPlaceholder: 'Velg organisasjonsgrupper…',
            loadingOptions: 'Laster…',
            loadFailed: 'Kunne ikke laste alternativer.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Leverandør',
                incident: 'Hendelse',
                rights_request: 'Rettighetsforespørsel',
                contract: 'Kontrakt',
                data_mapping_activity: 'RoPA-aktivitet',
                compliance_rule: 'Compliance-regel',
                program: 'Program',
            },
            cancelButton: 'Avbryt',
            closeButton: 'Lukk',
            createButton: 'Opprett',
            creating: 'Oppretter…',
            saveButton: 'Lagre',
            saving: 'Lagrer…',
            shareWorkflowHeading: 'Del arbeidsflyt',
            shareIntro: 'Deler: {title}',
            shareUserIds: 'Bruker-IDer (atskilt med mellomrom eller komma)',
            shareGroupIds: 'Organisasjonsgruppe-IDer (atskilt med mellomrom eller komma)',
            shareAllowEdit: 'Tillat mottakere å redigere',
            shareRequireRecipient: 'Oppgi minst én bruker-ID eller gruppe-ID.',
            shareSuccess: 'Delt.',
            sharing: 'Deler…',
            runWorkflowHeading: 'Kjør: {title}',
            runStarting: 'Starter…',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Oppgaver: {tasks} · Utkast til bevis: {evidence} · Vedlegg: {attachments}',
            runPanelHeading: 'Arbeidsflytkjøring',
            runPanelHeadingWithTitle: 'Arbeidsflytkjøring: {title}',
            runViewTasks: 'Vis genererte oppgaver',
            runViewEvidence: 'Vis utkast til bevis',
            runResultHeading: 'Rådgiverresultat',
            runNoResult: 'Kjøringen ble ferdig, men returnerte ikke et resultat som kan gjennomgås. Ingen oppgaver, bevis eller vedlegg ble opprettet. Prøv igjen med nødvendig post eller nødvendige dokumenter valgt.',
            runCitationWarning: 'Noen sitater kunne ikke verifiseres automatisk. Gjennomgå dem før du stoler på dette resultatet.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Kunne ikke laste arbeidsflytkjøringen.',
            runStreamFailed: 'Tilkoblingen til arbeidsflytens fremdrift mislyktes.',
            runStatusValues: {
                pending: 'Venter',
                running: 'Kjører',
                completed: 'Fullført',
                succeeded: 'Lyktes',
                failed: 'Mislyktes',
                cancelled: 'Avbrutt',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Oppgave opprettet.',
            runProgressEvidenceDrafted: 'Utkast til bevis opprettet.',
            runProgressAttachmentGenerated: 'Vedlegg generert.',
            guidedWorkflowTitle: 'Veiledet arbeidsflyt for {recordType}',
            guidedWorkflowDescription: 'Opprettet for poster av typen {recordType}. Gå gjennom og tilpass arbeidsflyten før du lagrer.',
            guidedWorkflowPrompt: 'Gå gjennom den oppgitte posten av typen {recordType}, identifiser manglende fakta og foreslå underbygde neste trinn for menneskelig gjennomgang.',
            promptHeading: 'Spør',
            howItWorksHeading: 'Hva som skjer når du kjører denne arbeidsflyten',
            builtinGuidance: 'Forseti gjennomgår postene og dokumentene du velger, returnerer et resultat operatøren kan gjennomgå, og oppretter utkast til oppgaver eller bevis bare når resultatet støtter det. Ingenting godkjennes eller sendes inn automatisk.',
            editWorkflowHeading: 'Rediger arbeidsflyt',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Arbeidsflyt ikke funnet.',
            schedules: {
                tabTitle: 'Tidsplaner',
                sectionHeading: 'Planlagte kjøringer',
                empty: 'Ingen tidsplaner ennå. Opprett en for å kjøre denne arbeidsflyten automatisk.',
                addButton: 'Legg til tidsplan',
                addHeading: 'Planlegg arbeidsflyt',
                editHeading: 'Rediger tidsplan',
                fieldFrequency: 'Frekvens',
                fieldDayOfWeek: 'Ukedag',
                fieldDayOfMonth: 'Dag i måneden',
                fieldTime: 'Tid (UTC)',
                fieldTimezone: 'Tidssone',
                fieldProject: 'Prosjekt (valgfritt)',
                fieldAttachments: 'Standardvedlegg',
                fieldPersonaOverride: 'Persona-overstyring (valgfritt)',
                frequencyDaily: 'Daglig',
                frequencyWeekly: 'Ukentlig',
                frequencyMonthly: 'Månedlig',
                weekdaySun: 'Søndag',
                weekdayMon: 'Mandag',
                weekdayTue: 'Tirsdag',
                weekdayWed: 'Onsdag',
                weekdayThu: 'Torsdag',
                weekdayFri: 'Fredag',
                weekdaySat: 'Lørdag',
                statusActive: 'Aktiv',
                statusPaused: 'Pauset',
                nextRunLabel: 'Neste kjøring',
                lastRunLabel: 'Siste kjøring',
                pauseButton: 'Pause',
                resumeButton: 'Fortsett',
                deleteButton: 'Slett',
                triggerButton: 'Kjør nå',
                savingButton: 'Lagrer…',
                saveButton: 'Lagre',
                triggered: 'Kjøring startet — sjekk kjøringshistorikken.',
            },
            branching: {
                helpHeading: 'Betinget prompt-syntaks',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Konflikter på tvers av rammeverk',
            pageSubtitle: 'Utvalgt katalog over hvor de installerte reguleringspakkene dine samhandler på motstridende måter. Forseti overflater; mennesker bestemmer.',
            noInstalledPacks: 'Ingen forskriftspakker installert for denne organisasjonen – installer minst to pakker for å avdekke konflikter på tvers av rammeverk.',
            noConflicts: 'Ingen konflikter samsvarer med gjeldende filtre.',
            allAcknowledged: 'Enhver synlig konflikt har blitt erkjent. Fint arbeid.',
            columnSeverity: 'Alvorlighetsgrad',
            columnTitle: 'Tittel',
            columnFrameworks: 'Rammer',
            columnTopic: 'Emne',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'Alle rammer',
            filterTopicPlaceholder: 'Alle emner',
            filterSeverityPlaceholder: 'Alle alvorlighetsgrader',
            filterStatusPlaceholder: 'Alle statuser',
            filterStatusAcknowledged: 'Erkjent',
            filterStatusUnacknowledged: 'Ukjent',
            severityCritical: 'Kritisk',
            severityHigh: 'Høy',
            severityMedium: 'Middels',
            severityLow: 'Lavt',
            statusAcknowledged: 'Erkjent',
            statusInReview: 'I anmeldelse',
            statusResolved: 'Løst',
            statusWontFix: 'Vil ikke fikse',
            statusUnacknowledged: 'Ukjent',
            drawerSummaryHeading: 'Sammendrag',
            drawerSideAHeading: 'Side A',
            drawerSideBHeading: 'Side B',
            drawerRequirementsLabel: 'Oppgitte krav',
            drawerProvisionsLabel: 'Oppgitte bestemmelser',
            drawerSuggestedResolutionHeading: 'Foreslått oppløsning',
            drawerLegalCounselBadge: 'Juridisk gjennomgang kreves',
            drawerAckHeading: 'Anerkjennelse',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Tildelt til',
            drawerAckDueDateLabel: 'Forfallsdato',
            drawerAckNotesLabel: 'Notater',
            drawerAckButtonAcknowledge: 'Bekreft',
            drawerAckButtonUpdate: 'Oppdater',
            drawerAckButtonUnacknowledge: 'Avbekreft',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Søk i tittel eller sammendrag...',
            searchButton: 'Søk',
            searchButtonAriaLabel: 'Kjør konfliktsøk',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Eksport mislyktes: {message}',
            exportAriaLabel: 'Eksportkonflikter',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Tabulariske gjennomganger',
            pageSubtitle: 'Sammenlign N dokumenter på tvers av M attributter med begrunnede sitater.',
            newReview: 'Ny gjennomgang',
            newReviewHeading: 'Ny tabularisk gjennomgang',
            emptyHint: 'Ingen tabulariske gjennomganger ennå.',
            docsLabel: '{n} dokumenter',
            colsLabel: '{n} kolonner',
            detailSubtitle: '{docs} dokumenter · {cols} kolonner',
            addColumn: 'Legg til kolonne',
            addColumnHeading: 'Legg til kolonne',
            columnInfo: 'Vis prompt og format',
            columnInfoAria: 'Vis prompt og format for kolonnen {name}',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Returformat',
            columnInfoPresets: 'Tillatte verdier',
            columnInfoCitations: 'Kildehenvisninger',
            columnInfoCitationsRequired: 'Påkrevd',
            removeColumn: 'Fjern kolonne',
            removeColumnAria: 'Fjern kolonne {name}',
            removeColumnConfirm: 'Fjerne kolonnen "{name}"? De uttrukne cellene slettes.',
            deleteReview: 'Slett',
            deleteReviewAria: 'Slett gjennomgang {title}',
            deleteReviewConfirm: 'Slette gjennomgangen "{title}"? Dette fjerner permanent kolonnene, cellene og diskusjonene.',
            runAll: 'Kjør alle',
            running: 'Kjører…',
            cancel: 'Avbryt',
            exportXlsx: 'Eksporter Excel',
            exportCsv: 'Eksporter CSV',
            exporting: 'Eksporterer…',
            backToList: 'Tilbake til listen',
            notFound: 'Tabularisk gjennomgang ikke funnet.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Vedlegg dokumenter for å starte gjennomgangen.',
            noColumns: 'Legg til kolonner for å definere hva som skal trekkes ut.',
            tableAriaLabel: 'Uttrekkingsrutenett',
            threadsCountAria: '{n} diskusjonsinnlegg',
            startDiscussionAria: 'Start diskusjon',
            docColumn: 'Dokument',
            statusPending: 'Venter',
            statusRunning: 'Kjører…',
            statusFailed: 'Mislyktes: {msg}',
            statusCancelled: 'Avbrutt',
            citationsCount: '{n} sitat(er)',
            citationsView: 'Vis kildeutdrag',
            citationPage: 'Side {n}',
            titleRequired: 'Tittel er påkrevd.',
            fieldTitle: 'Tittel',
            fieldProjectId: 'Prosjekt (valgfritt)',
            fieldAttachmentIds: 'Vedlegg',
            fieldColumnName: 'Kolonnenavn',
            fieldColumnNameHelp: 'En kort, beskrivende etikett for denne kolonnen i uttrekksrutenettet.',
            columnNamePlaceholder: 'f.eks. Kontraktens startdato',
            fieldExtractionPrompt: 'Uttrekkingsprompt',
            fieldExtractionPromptHelp: 'Beskriv informasjonen du vil trekke ut. Vær spesifikk for å få bedre resultater.',
            columnPromptPlaceholder: 'Hva skal Forseti se etter i dokumentene?',
            fieldReturnFormat: 'Returformat',
            fieldReturnFormatHelp: 'Styrer hvordan AI-en formaterer svaret sitt (f.eks. som en dato eller et tall).',
            fieldPresets: 'Tillatte verdier (én per linje)',
            fieldPresetsPlaceholder: 'ja\nnei\ndelvis',
            fieldCitationsRequired: 'Sitat påkrevd',
            formatShortText: 'Kort tekst',
            formatLongText: 'Lang tekst',
            formatEnum: 'Enum',
            formatNumber: 'Tall',
            formatDate: 'Dato',
            formatList: 'Liste',
            columnRequireFields: 'Navn og prompt er påkrevd.',
            addColumnError: 'Kunne ikke legge til kolonne.',
            cancelButton: 'Avbryt',
            createButton: 'Opprett',
            creating: 'Oppretter…',
            addButton: 'Legg til',
            adding: 'Legger til…',
            runConfirmHeading: 'Kjør uttrekking',
            estimateBody: 'Estimert ~{units} fakturerbare enheter ({confidence} tillit).',
            starting: 'Starter…',
            confirmRun: 'Bekreft kjøring',
            charts: {
                title: 'Kolonnediagrammer',
                tabsAria: 'Diagram-kolonner',
                enumAria: 'Fordeling av enum-verdier',
                numberAria: 'Histogram over tallfordeling',
                dateAria: 'Datotidslinje',
                enumEmpty: 'Ingen fullførte celler.',
                numberEmpty: 'Ingen numeriske verdier.',
                numberExcluded: '{n} celle(r) ekskludert (tomme / ikke-numeriske / ikke fullførte).',
                dateEmpty: 'Ingen datoverdier.',
                dateExcluded: '{n} celle(r) ekskludert.',
                unsupportedFormat: 'Ikke støttet kolonneformat.',
            },
            threads: {
                drawerTitle: 'Diskusjon',
                loading: 'Laster…',
                empty: 'Ingen innlegg ennå. Start samtalen.',
                composerLabel: 'Ny kommentar',
                composerPlaceholder: 'Legg til en kommentar…',
                post: 'Send',
                posting: 'Sender…',
                edit: 'Rediger',
                editLabel: 'Rediger innlegg',
                delete: 'Slett',
                save: 'Lagre',
                cancel: 'Avbryt',
                close: 'Lukk',
                editedBadge: 'redigert',
                loadError: 'Kunne ikke laste tråden',
                postError: 'Kunne ikke sende',
                editError: 'Kunne ikke redigere',
                deleteError: 'Kunne ikke slette',
            },
            compare: {
                pageTitle: 'Sammenlign gjennomganger',
                leftReviewLabel: 'Venstre gjennomgang',
                rightReviewLabel: 'Høyre gjennomgang',
                pickReview: 'Velg en gjennomgang å sammenligne med',
                noSharedDocuments: 'Disse gjennomgangene deler ingen dokumenter — sammenligning krever minst ett felles vedlegg.',
                sharedDocumentsHeading: 'Delte dokumenter',
                leftOnlyHeading: 'Kolonner kun i venstre gjennomgang',
                rightOnlyHeading: 'Kolonner kun i høyre gjennomgang',
                differs: 'Avviker',
                identical: 'Identisk',
                emptyValue: '—',
                compareButton: 'Sammenlign med…',
                documentHeading: 'Dokument',
                errorMissingIds: 'Både venstre og høyre gjennomgangs-IDer er påkrevd.',
                errorComparisonFailed: 'Sammenligning mislyktes.',
                errorLoadFailed: 'Kunne ikke laste gjennomganger.',
                pickerLoading: 'Laster…',
                pickerNoOtherReviews: 'Ingen andre gjennomganger tilgjengelige for sammenligning.',
                pickerCancel: 'Avbryt',
            },
        },
        documentReview: {
            pageTitle: 'Dokumentgjennomgang',
            pageSubtitle: 'Godta eller avslå AI-foreslåtte endringer i dette dokumentet.',
            sidePanelLabel: 'AI røde linjer',
            sidePanelHeading: 'AI røde linjer',
            noPendingEdits: 'Ingen ventende redigeringer.',
            pendingCount: '{count} venter',
            acceptAllButton: 'Godta alle',
            rejectAllButton: 'Avvis alle',
            acceptAllTitle: 'Godta alle ventende redigeringer?',
            acceptAllConfirm: 'Dette vil godta {count} ventende endringer og opprette en eller flere nye versjoner. Fortsette?',
            editsHeading: 'Rediger',
            editsEmpty: 'Ingen endringer å vurdere.',
            versionsHeading: 'Versjonshistorikk',
            versionsEmpty: 'Ingen versjoner ennå.',
            editLabel: 'Rediger',
            editLabelWithId: 'Rediger #{id}',
            acceptButton: 'Godta',
            rejectButton: 'Avvis',
            accepting: 'Godtar …',
            rejecting: 'Avviser …',
            diffDeletedAria: 'Slettet',
            diffInsertedAria: 'Innsatt',
            statusPending: 'Venter',
            statusAccepted: 'Godtatt',
            statusRejected: 'Avvist',
            versionLabel: 'Versjon',
            versionAriaLabel: 'Versjon {n}',
            versionAriaLabelCurrent: 'Versjon {n} ({current})',
            versionCurrentTag: 'gjeldende',
            versionSourceLabel: 'Kilde',
            versionCreatedLabel: 'Opprettet',
            versionModelLabel: 'Modell',
            sourceUpload: 'Last opp',
            sourceUserUpload: 'Brukeropplasting',
            sourceAssistantEdit: 'Assistent-redigering',
            sourceUserAccept: 'Bruker godtatt redigering',
            sourceUserReject: 'Bruker avviste redigering',
            sourceGenerated: 'Generert',
            extractionExtracted: 'Tekst hentet ut',
            extractionFailed: 'Tekstuthenting mislyktes',
            extractionUnsupported: 'Teksten kunne ikke hentes ut',
            extractionPages: '{n} sider',
            extractionWords: '{n} ord',
            extractionReasonScanned: 'Dette ser ut som et skannet dokument eller et dokument med kun bilder. Forseti kan ikke lese det ennå — last opp en tekstbasert fil, eller lim inn teksten direkte.',
            extractionReasonEncrypted: 'Filen er passordbeskyttet. Fjern beskyttelsen og last den opp på nytt.',
            extractionReasonMalformed: 'Filen kunne ikke tolkes og er muligens skadet.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Filen er for stor til å behandles.',
            extractionReasonTimeout: 'Behandlingen tok for lang tid. Prøv en mindre eller enklere fil.',
            extractionReasonUnknown: 'Ingen ytterligere detaljer er tilgjengelige.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Dokumenttekst',
            textPaneTitle: 'Dokumenttekst',
            textPaneVersion: 'Versjon {n}',
            textPaneEmpty: 'Ingen tekst er tilgjengelig for denne versjonen. Skannede PDF-er og enkelte dokumenttyper har ingen tekst som kan hentes ut.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Dokumentgjennomgang',
            pageTitle: 'Dokumentgjennomgang',
            pageSubtitle: 'Dokumenter i organisasjonen din som har AI-foreslåtte redigeringer som venter på gjennomgang eller Forseti-baserte versjoner.',
            colDocument: 'Dokument',
            colLinkedRecord: 'Tilknyttet post',
            colPendingEdits: 'Ventende redigeringer',
            colLastActivity: 'Siste aktivitet',
            colOpen: 'Åpne',
            open: 'Åpne',
            empty: 'Ingen dokumenter under Forseti-gjennomgang ennå. Dokumenter kommer i denne køen når Forseti foreslår en redigering under en chat, når en AI-arbeidsflyt produserer en redline, eller når du sender et dokument til gjennomgang via knappen ovenfor.',
            loading: 'Laster…',
            loadError: 'Kunne ikke laste dokumenter under gjennomgang.',
            retryButton: 'Prøv igjen',
            noLinkedRecord: '—',
            pendingZero: 'Ingen',
            sendForReviewButton: 'Send et dokument til gjennomgang',
            sendForReviewTitle: 'Send et dokument til gjennomgang',
            sendForReviewSubtitle: 'Last opp et .txt-, .md-, .pdf- eller .docx-dokument (opptil 50 MB). Det legges til gjennomgangskøen og Forseti vil kjøre en redline-gjennomgang på det.',
            sendForReviewTabUpload: 'Last opp nytt',
            sendForReviewPickerLabel: 'Søk i vedlegg',
            sendForReviewPickerPlaceholder: 'Skriv for å søke…',
            sendForReviewPickerEmpty: 'Ingen samsvarende vedlegg. Prøv et annet søk.',
            sendForReviewSubmit: 'Send til gjennomgang',
            sendForReviewCancel: 'Avbryt',
            sendForReviewSubmitting: 'Sender…',
            sendForReviewError: 'Kunne ikke sende dokumentet til gjennomgang. Prøv igjen.',
            sendForReviewSuccess: 'Dokument sendt til gjennomgang.',
            sendForReviewAcceptedExt: 'Akseptert: {ext}.',
            sendForReviewMaxSize: 'Maks størrelse {mb} MB.',
        },
        chats: {
            exportError: 'Eksport mislyktes.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL-bevispakker',
        subtitle: 'Kundekontrollert innlevering til MoPS A05',
        nav: { emailProviders: 'E-postleverandører' },
        providers: {
            pageTitle: 'E-postleverandører',
            pageSubtitle: 'Konfigurer organisasjonens utgående e-postleverandør for PDPL-bevispakker til MoPS A05.',
            architectureRuleHeading: 'Arkitekturregel (låst)',
            architectureRuleBody: 'AesirX er ikke avsender for bevismateriale-e-poster. Hver sending bruker en leverandør du eier og kontrollerer. AesirX sender aldri på dine vegne.',
            empty: 'Ingen leverandører er konfigurert ennå.',
            createCta: 'Opprett leverandør',
            statusPendingConsent: 'Venter på samtykke',
            statusPendingConnectionCheck: 'Venter på tilkoblingstest',
            statusActive: 'Aktiv',
            statusConnectionCheckFailed: 'Tilkoblingstest mislyktes',
            statusDisabled: 'Deaktivert',
            acceptConsentCta: 'Godta samtykke',
            runConnectionCheckCta: 'Kjør tilkoblingstest',
            disableCta: 'Deaktiver',
            disableConfirm: 'Hvis du deaktiverer denne leverandøren stopper direktesendinger til en ny leverandør er konfigurert og samtykket er bekreftet på nytt. Fortsette?',
            columnDisplayName: 'Navn',
            columnFromAddress: 'Avsenderadresse',
            columnStatus: 'Status',
            columnLastCheck: 'Siste test',
            columnActions: 'Handlinger',
            createModalTitle: 'Ny SMTP-leverandør',
            createDisplayNameLabel: 'Visningsnavn',
            createFromAddressLabel: 'Avsenderadresse',
            createReplyToLabel: 'Svaradresse (valgfritt)',
            createSmtpHostLabel: 'SMTP-vert',
            createSmtpPortLabel: 'SMTP-port',
            createSmtpUsernameLabel: 'SMTP-brukernavn',
            createSmtpPasswordLabel: 'SMTP-passord',
            createSmtpEncryptionLabel: 'Kryptering',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implisitt)',
            createSmtpEncryptionNone: 'Ingen',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Kommer snart',
            createOauthDisabledHint: 'OAuth-leverandører er ennå ikke aktivert. Bruk SMTP foreløpig.',
            createSubmitCta: 'Opprett leverandør',
            createCancelCta: 'Avbryt',
            createErrorInvalidFromAddress: 'Avsenderadressen må være en gyldig e-postkonto.',
            createErrorDuplicateFromAddress: 'En annen leverandør er allerede konfigurert med denne avsenderadressen.',
            createErrorGeneric: 'Kunne ikke opprette leverandøren. Se gjennom skjemaet og prøv igjen.',
            consentModalTitle: 'Bekreft arkitekturregelen',
            consentLocaleToggleEn: 'Engelsk',
            consentLocaleToggleVi: 'Vietnamesisk',
            consentAcknowledgeCta: 'Jeg bekrefter — godta samtykke',
            consentCancelCta: 'Avbryt',
            consentErrorMismatch: 'Samtyketeksten ble endret og samsvarer ikke lenger med den kanoniske ansvarsfraskrivelsen. Åpne dialogen på nytt og send inn igjen.',
            testModalTitle: 'Tilkoblingstest',
            testRunningCta: 'Tester tilkobling…',
            testRunCta: 'Kjør tilkoblingstest',
            testRetryCta: 'Prøv tilkoblingstest på nytt',
            testSuccessHeading: 'Tilkobling vellykket',
            testSuccessBody: 'Autentisert mot {host}:{port} på {latencyMs} ms. Leverandøren er klar for direktesending.',
            testFailureHeading: 'Tilkobling mislyktes',
            testFailureBody: 'Testen kunne ikke fullføres. Se gjennom årsaken nedenfor og prøv igjen når den er rettet.',
            testFailureReasonLabel: 'Feilårsak',
            testCloseCta: 'Lukk',
            detailDrawerTitle: 'Leverandørdetaljer',
            detailDrawerHistoryHeading: 'Historikk for tilkoblingstester',
            detailDrawerConsentHeading: 'Samtykkebekreftelse',
            detailDrawerNoHistory: 'Ingen tilkoblingstester er registrert ennå.',
            detailDrawerNoConsent: 'Samtykke er ikke bekreftet ennå.',
            outcomeSuccess: 'Vellykket',
            outcomeFailure: 'Mislykket',
        },
        builder: {
            pageTitle: 'Sett sammen bevispakke',
            pageSubtitle: 'Last opp de 6 delfilene, velg pakketype og sett sammen den deterministiske 8-delers ZIP-en for innlevering.',
            packTypeLabel: 'Pakketype',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Del 01 — Følgebrev (PDF)',
            part02AuthorityForm: 'Del 02 — Myndighetsskjema (PDF)',
            part03Supporting: 'Del 03 — Støttebevis (≥1)',
            part04Safeguards: 'Del 04 — Sikkerhetstiltak',
            part04SafeguardsRequiredOtia: '(påkrevd for OTIA)',
            part05PriorCorrespondence: 'Del 05 — Tidligere korrespondanse (valgfritt)',
            part06Signature: 'Del 06 — Signatur + stempelbevis (≥1)',
            uploadPickFile: 'Velg fil',
            uploadPickFiles: 'Velg filer',
            uploadRemoveFile: 'Fjern',
            requiredMissing: 'Påkrevd del mangler',
            assembleCta: 'Sett sammen pakke',
            assembling: 'Setter sammen…',
            assembleSuccessHeading: 'Pakke satt sammen',
            assembleSuccessSummary: '{fileCount} filer · {bytes} byte · SHA-256 {shaPrefix}…',
            composeNextCta: 'Skriv e-postutkast',
            warningsHeading: 'Valideringsadvarsler',
            assembleError: 'Kunne ikke sette sammen pakken. Gå gjennom inndata og prøv igjen.',
            forsetiAssistHeading: 'Forseti AI-assistanse (valgfri)',
            forsetiAssistHint: 'Kjør PDPL Evidence Pack Assembly Advisor for å løfte frem manglende deler, utarbeide følgebrevprosa og flagge sikkerhetshull. AesirX er ikke avsenderen — AI-en gir kun råd; du godkjenner hvert artefakt.',
            forsetiAssistCta: 'Åpne Forseti arbeidsflyt-bibliotek',
            forsetiAiRunIdLabel: 'Tilknyttet Forseti AI-kjørings-id (lim inn for å registrere AI-assistanse)',
        },
        draft: {
            pageTitle: 'E-postutkast',
            pageSubtitle: 'Skriv og rediger e-postutkastet. Direkte sending kommer i en senere utgivelse.',
            composeCta: 'Skriv utkast',
            composing: 'Skriver…',
            noDraftYet: 'Ingen utkast ennå. Skriv ett for å komme i gang.',
            localeToggleLabel: 'Språk',
            localeEn: 'Engelsk',
            localeVi: 'Vietnamesisk',
            subjectLabel: 'Emne',
            bodyLabel: 'Brødtekst',
            saveCta: 'Lagre endringer',
            saving: 'Lagrer…',
            saveError: 'Kunne ikke lagre utkastet. Prøv igjen.',
            manifestSummaryLabel: 'Manifest-oversikt',
            coverLetterLabel: 'Følgebrev (PDF)',
            coverLetterDownloadCta: 'Last ned PDF',
            editHistoryHeading: 'Redigeringshistorikk',
            editHistoryEmpty: 'Ingen redigeringer ennå.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 uoffisiell kanal-melding',
            uploadSentProofCta: 'Last opp manuelt sendebevis',
            versionLabel: 'Versjon',
            statusDraft: 'Utkast',
            statusEdited: 'Redigert',
            statusFinalized: 'Fullført',
        },
        sentProof: {
            modalTitle: 'Last opp manuelt sendebevis',
            architectureLockNote: 'AesirX er ikke avsenderen. Dette beviset fanger den manuelle sendingen du gjennomførte via ditt eget e-postprogram.',
            fileLabel: 'Bevisfil (.eml, .png, .jpg)',
            envelopeFromLabel: 'Fra',
            envelopeToLabel: 'Til',
            envelopeSubjectLabel: 'Emne (ved sending)',
            envelopeSentAtLabel: 'Sendt',
            envelopeAttachmentShaLabel: 'Vedlegg SHA-256 (ved sending)',
            uploadCta: 'Last opp bevis',
            uploading: 'Laster opp…',
            uploadSuccess: 'Sendebevis lastet opp.',
            uploadErrorInvalidMime: 'Filtypen er ikke tillatt. Bruk .eml, .png, .jpg eller .jpeg.',
            uploadErrorGeneric: 'Kunne ikke laste opp beviset. Prøv igjen.',
            existingProofsHeading: 'Registrerte sendebevis',
            existingProofsEmpty: 'Ingen sendebevis registrert ennå.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Bevispakke',
            notFoundMessage: 'Pakken ble ikke funnet — den kan ha blitt fjernet eller lenken er feil.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Sak',
            packShaLabel: 'Pakke-SHA-256',
            fileCountLabel: 'Filer',
            bytesLabel: 'Størrelse',
            composeDraftCta: 'Skriv utkast',
            sendComingSoonCta: 'Direkte sending: kommer snart',
            sendComingSoonHint: 'Direkte sending er under aktiv utvikling. Bruk "Last opp manuelt sendebevis" på utkastsiden inntil det leveres.',
            sendCta: 'Send via konfigurert leverandør',
            sendDisabledNoProviderHint: 'Konfigurer først en kundekontrollert e-postleverandør. AesirX sender aldri på dine vegne.',
            timelineHeading: 'Sendehistorikk',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Send bevispakke',
            providerPickerLabel: 'Konfigurert e-postleverandør',
            providerPickerHint: 'AesirX sender aldri på dine vegne. Velg en av dine konfigurerte leverandører nedenfor.',
            providerPickerEmpty: 'Ingen aktive leverandører er konfigurert. Konfigurer en på siden E-postleverandører før du sender.',
            providerPickerEmptyCta: 'Åpne E-postleverandører',
            draftPickerLabel: 'E-postutkast',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Ingen utkast er skrevet for denne pakken ennå. Skriv ett på utkastsiden først.',
            recipientToLabel: 'Til',
            recipientToHint: 'En eller flere e-postadresser, atskilt med komma.',
            recipientCcLabel: 'Kopi (kommaseparert)',
            recipientCcHint: 'Valgfritt. Kommaseparert liste over e-postadresser.',
            recipientBccLabel: 'Blindkopi (kommaseparert)',
            recipientBccHint: 'Valgfritt. Kommaseparert liste over e-postadresser.',
            consentReaffirmHeading: 'Bekreft leverandørsamtykke på nytt',
            submitCta: 'Send nå',
            sending: 'Sender…',
            cancelCta: 'Avbryt',
            sendSuccess: 'Sending registrert. Pakken er overført via din konfigurerte leverandør.',
            sendErrorGeneric: 'Kunne ikke sende pakken. Se feilen nedenfor og prøv igjen.',
            sendErrorAlreadyFinalized: 'Dette utkastet er allerede brukt til en vellykket sending. Skriv et nytt utkast før du sender igjen.',
            sendErrorInvalidRecipients: 'En eller flere mottakeradresser er ikke gyldige e-postkasser.',
            // Timeline
            timelineEmpty: 'Ingen sendinger registrert ennå. Bruk "Send via konfigurert leverandør" over for å overføre gjennom din kundekontrollerte SMTP / Microsoft 365 / Google Workspace-leverandør.',
            timelineProviderLabel: 'Sendt via din konfigurerte leverandør',
            timelineSentAtLabel: 'Sendt',
            timelineRecipientLabel: 'Mottaker',
            timelineOutcomeSent: 'Sendt',
            timelineOutcomeFailed: 'Mislyktes',
            timelineViewDetailsCta: 'Vis detaljer',
            // Authority-response chips
            authorityChipNone: 'Venter på svar',
            authorityChipAcknowledgement: 'Mottakelse bekreftet',
            authorityChipAcceptance: 'Godkjent',
            authorityChipRejection: 'Avvist',
            authorityChipSupplementRequest: 'Forespørsel om tillegg',
            authorityChipNoResponse: 'Ingen respons',
            // Hard-copy chips
            hardCopyChipNone: 'Ingen fysisk oppfølging',
            hardCopyChipPending: 'Postlagt — venter på levering',
            hardCopyChipDelivered: 'Levert',
            // Drawer
            drawerTitle: 'Sendepost',
            drawerEnvelopeHeading: 'Konvolutt',
            drawerEnvelopeFromLabel: 'Fra',
            drawerEnvelopeToLabel: 'Til',
            drawerEnvelopeCcLabel: 'Kopi',
            drawerEnvelopeBccLabel: 'Blindkopi',
            drawerEnvelopeSubjectLabel: 'Emne',
            drawerEnvelopeMessageIdLabel: 'Melding-ID',
            drawerOutcomeHeading: 'Utfall',
            drawerSnapshotHeading: 'Øyeblikksbilde av bevisbevaringskjeden',
            drawerSnapshotPackShaLabel: 'Pakke-SHA-256 ved sending',
            drawerSnapshotDraftVersionLabel: 'Utkastversjon ved sending',
            drawerSnapshotAttachmentLabel: 'Overført ZIP',
            drawerSnapshotAttachmentDownloadCta: 'Last ned ZIP',
            drawerAuthorityResponseHeading: 'Tilsynssvar',
            drawerHardCopyHeading: 'Fysisk oppfølging',
            drawerSentByLabel: 'Sendt av',
            drawerSendIdLabel: 'Sendepost-ID',
            drawerSmtpResponseLabel: 'SMTP-respons',
            drawerFailureReasonLabel: 'Årsak til feil',
            drawerLatencyLabel: 'Forsinkelse',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Akseptert',
            drawerRejectedLabel: 'Avvist',
            drawerCloseCta: 'Lukk',
            // Authority-response panel
            authorityRecordCta: 'Registrer svar',
            authorityReclassifyCta: 'Omklassifiser',
            authorityClassificationLabel: 'Klassifisering',
            authorityReceivedAtLabel: 'Mottatt',
            authorityFreeTextLabel: 'Merknader (valgfritt)',
            authorityEvidenceLabel: 'Bevisfil (.eml / .pdf / .png / .jpg — valgfritt)',
            authorityReasonLabel: 'Begrunnelse for omklassifisering',
            authorityHistoryHeading: 'Omklassifiseringshistorikk',
            authorityHistoryEmpty: 'Ingen omklassifiseringer registrert.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Lagre svar',
            authoritySubmitting: 'Lagrer…',
            authorityErrorInvalidMime: 'Bevisfiltypen er ikke tillatt. Bruk .eml, .pdf, .png eller .jpg.',
            authorityErrorGeneric: 'Kunne ikke lagre svaret. Prøv igjen.',
            authorityRecordedAtLabel: 'Registrert',
            authorityRecordedByLabel: 'Registrert av',
            authorityEvidenceFilenameLabel: 'Bevis',
            authorityEvidenceDownloadCta: 'Last ned bevis',
            // Hard-copy panel
            hardCopyRecordCta: 'Logg fysisk oppfølging',
            hardCopyConfirmCta: 'Bekreft levering',
            hardCopyEditNotesCta: 'Rediger leveringsnotater',
            hardCopyPostedAtLabel: 'Postlagt',
            hardCopyCourierLabel: 'Budfirma',
            hardCopyTrackingLabel: 'Sporingsnummer',
            hardCopyRecipientLabel: 'Mottaker på etikett',
            hardCopyReceiptLabel: 'Kvitteringsbevis (.pdf / .png / .jpg — valgfritt)',
            hardCopyReceiptDownloadCta: 'Last ned kvittering',
            hardCopyDeliveredAtLabel: 'Levert',
            hardCopyDeliveryNotesLabel: 'Leveringsnotater (valgfritt)',
            hardCopySubmitCta: 'Lagre oppfølging',
            hardCopySubmitting: 'Lagrer…',
            hardCopyErrorInvalidMime: 'Kvitteringsfiltypen er ikke tillatt. Bruk .pdf, .png eller .jpg.',
            hardCopyErrorGeneric: 'Kunne ikke lagre oppfølgingen. Prøv igjen.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Fullførte rapporter og meldinger for saken',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=nb.js.map